// Sentry is initialized in index.html via CDN script tag
// Use window.Sentry for error tracking
const Sentry = window.Sentry || {};
const captureException = (error, context) => {
  if (Sentry.captureException) {
    Sentry.captureException(error, context);
  }
};
const setUser = (user) => {
  if (Sentry.setUser) {
    Sentry.setUser(user);
  }
};

import {
  formatCurrencyHalfKrone,
  formatPriceHalfKrone,
  roundToHalfKrone,
} from './utils/format.js';
import { formatDistance } from './utils/geo.js';
import { getFlagEmoji } from './utils/locale.js';
import {
  clearLoginSessionCookie,
  hydrateFromCookie,
  readLoginSessionCookie,
  writeLoginSessionCookie,
} from './utils/tokenStorage.js';
import {
  formatCardNumber,
  formatExpiryDate,
  stripNonDigits,
} from './utils/input.js';
import { formatDateDMY } from './utils/date.js';
import { showToast } from './utils/toast.js';
import { getErrorMessage, extractErrorFields } from './utils/errors.js';
import { isValidCardNumber, isValidExpiryDate } from './utils/validation.js';
import { highlightFieldError } from './utils/dom.js';
import { getApiConfig } from './utils/apiConfig.js';
import {
  calculateGymDistances,
  checkGeolocationPermission,
  getUserLocation,
  isGeolocationAvailable,
} from './utils/geolocation.js';
import { buildApiUrl, requestJson } from './utils/apiRequest.js';
import { sanitizeHTML } from './sanitize.js';

/**
 * Gets today's date in YYYY-MM-DD format using local time (not UTC).
 * This ensures we always send the user's local "today" date to the backend.
 * @returns {string} Today's date in YYYY-MM-DD format (e.g., "2026-01-05")
 */
function getTodayLocalDateString() {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** BRP API3 CurrencyOut.amount is always in minor units (øre). */
function extractCurrencyOutKr(currencyOut) {
  if (currencyOut == null) return 0;
  if (typeof currencyOut === 'number') {
    return roundToHalfKrone(currencyOut / 100);
  }
  if (typeof currencyOut === 'object') {
    if (typeof currencyOut.amount === 'number') {
      return roundToHalfKrone(currencyOut.amount / 100);
    }
    const nested = currencyOut.value ?? currencyOut.discount;
    if (nested != null) return extractCurrencyOutKr(nested);
  }
  return 0;
}

/** Parse coupon discount from CurrencyOut, item.coupon, or order response. */
function extractCouponDiscountKr(source) {
  if (source == null) return 0;
  if (typeof source === 'number') return extractCurrencyOutKr(source);
  if (typeof source === 'object') {
    if (source.couponDiscount != null || source.price?.couponDiscount != null) {
      return extractCurrencyOutKr(source.couponDiscount ?? source.price?.couponDiscount);
    }
    if (source.discount != null && source.name != null) {
      return extractCurrencyOutKr(source.discount);
    }
    return extractCurrencyOutKr(source);
  }
  return 0;
}

function calculateOrderCouponDiscountKr(order, subtotalForFallback = 0) {
  const fromCouponField = extractCouponDiscountKr(
    order?.couponDiscount ?? order?.price?.couponDiscount
  );
  if (fromCouponField > 0) return fromCouponField;

  const price = order?.price;
  if (price && subtotalForFallback > 0) {
    const orderTotalKr = extractCurrencyOutKr(price.amount ?? price.total ?? price.leftToPay);
    if (orderTotalKr > 0 && orderTotalKr < subtotalForFallback) {
      return roundToHalfKrone(subtotalForFallback - orderTotalKr);
    }
  }
  return 0;
}

function capCouponDiscountKr(discountAmount, subtotal) {
  if (discountAmount > subtotal && subtotal > 0) {
    console.warn('[Discount] Discount amount exceeds subtotal, capping at subtotal:', discountAmount, '->', subtotal);
    return subtotal;
  }
  return discountAmount;
}

/** Original / discount / pay-now breakdown for payment overview when a coupon is applied. */
function resolvePayNowDiscountBreakdown(basePayNowKr) {
  const base = roundToHalfKrone(basePayNowKr);
  const catalogSubtotal = roundToHalfKrone(state.totals.subtotal || 0);
  const stateDiscountKr = state.discountApplied
    ? roundToHalfKrone(state.totals.discountAmount || 0)
    : 0;
  const orderCouponKr = getOrderCouponDiscountKr();
  const orderCouponKrForLogic = orderCouponKr > 0 ? orderCouponKr : stateDiscountKr;

  if (orderCouponKrForLogic <= 0 && stateDiscountKr <= 0) {
    return { beforeDiscount: base, discountKr: 0, afterDiscount: base, hasDiscount: false };
  }

  const orderPriceKr = state.fullOrder?.price?.amount !== undefined
    ? roundToHalfKrone(extractCurrencyOutKr(state.fullOrder.price.amount))
    : null;

  let afterDiscount = base;
  let beforeDiscount = base;
  let discountKr = orderCouponKrForLogic;

  if (orderPriceKr !== null && (orderCouponKr > 0 || (stateDiscountKr > 0 && orderPriceKr < base))) {
    afterDiscount = orderPriceKr;
    const orderDerivedBefore = roundToHalfKrone(afterDiscount + orderCouponKrForLogic);
    // Keep the catalog/cart price the user saw before applying the coupon. BRP's
    // order-derived pre-discount total can differ by half-krone (e.g. 99,50 vs 99,00).
    const anchorBefore = catalogSubtotal > afterDiscount ? catalogSubtotal : orderDerivedBefore;
    beforeDiscount = anchorBefore > afterDiscount ? anchorBefore : orderDerivedBefore;
    discountKr = roundToHalfKrone(Math.max(0, beforeDiscount - afterDiscount));
  } else {
    beforeDiscount = catalogSubtotal > 0 ? catalogSubtotal : base;
    afterDiscount = roundToHalfKrone(Math.max(0, beforeDiscount - orderCouponKrForLogic));
    discountKr = roundToHalfKrone(Math.max(0, beforeDiscount - afterDiscount));
  }

  const hasDiscount = discountKr > 0 && beforeDiscount > afterDiscount;
  return { beforeDiscount, discountKr, afterDiscount, hasDiscount };
}

function getOrderCouponDiscountKr() {
  const fromOrder = roundToHalfKrone(extractCouponDiscountKr(state.fullOrder?.couponDiscount));
  if (fromOrder > 0) return fromOrder;

  const order = state.fullOrder;
  const itemGroups = [
    order?.subscriptionItems,
    order?.valueCardItems,
    order?.articleItems,
    order?.entryItems,
  ].filter(Array.isArray);

  let lineSum = 0;
  for (const items of itemGroups) {
    for (const item of items) {
      if (item?.coupon?.discount) {
        lineSum += extractCurrencyOutKr(item.coupon.discount);
      }
    }
  }
  return roundToHalfKrone(lineSum);
}

function isPunchCardCheckout() {
  return state.selectedProductType === 'punch-card' && Boolean(state.selectedProductId);
}

function ensurePaymentOverviewDomRefs() {
  if (!DOM.paymentOverview) {
    DOM.paymentOverview = document.querySelector('.payment-overview:not(.payment-overview-total-wrap)')
      || document.querySelector('.payment-overview');
  }
  if (!DOM.payNow) DOM.payNow = document.querySelector('[data-summary-field="pay-now"]');
  if (!DOM.monthlyPayment) DOM.monthlyPayment = document.querySelector('[data-summary-field="monthly-payment"]');
  if (!DOM.paymentDiscount) DOM.paymentDiscount = document.querySelector('[data-summary-field="discount-amount"]');
  if (!DOM.paymentPayNowOriginal) DOM.paymentPayNowOriginal = document.querySelector('[data-summary-field="pay-now-original"]');
  if (!DOM.firstMonthRow) DOM.firstMonthRow = document.querySelector('.payment-overview-first-month');
  if (!DOM.paymentBoundUntil) DOM.paymentBoundUntil = document.querySelector('[data-summary-field="payment-bound-until"]');
}

function applyPaymentOverviewDiscountRows(payNowBreakdown) {
  const originalRow = DOM.paymentPayNowOriginal?.closest('.payment-overview-original')
    || document.querySelector('.payment-overview-original');
  if (payNowBreakdown.hasDiscount) {
    if (originalRow) originalRow.style.display = 'flex';
    if (DOM.paymentPayNowOriginal) {
      DOM.paymentPayNowOriginal.textContent = formatCurrencyHalfKrone(payNowBreakdown.beforeDiscount);
    }
    if (DOM.paymentDiscount) {
      DOM.paymentDiscount.textContent = `-${formatCurrencyHalfKrone(payNowBreakdown.discountKr)}`;
      const discountRow = DOM.paymentDiscount.closest('.payment-overview-discount');
      if (discountRow) {
        discountRow.style.display = 'flex';
        const discountLabel = discountRow.querySelector('.payment-label');
        if (discountLabel) discountLabel.textContent = getDiscountAmountLabel(payNowBreakdown);
      }
    }
  } else {
    if (originalRow) originalRow.style.display = 'none';
    if (DOM.paymentDiscount) {
      const discountRow = DOM.paymentDiscount.closest('.payment-overview-discount');
      if (discountRow) discountRow.style.display = 'none';
    }
  }
}

function getPunchCardPayNowBaseKr() {
  const subtotal = state.totals.subtotal || 0;
  if (state.fullOrder?.price?.amount === undefined) return subtotal;

  const orderKr = roundToHalfKrone(extractCurrencyOutKr(state.fullOrder.price.amount));
  const couponKr = getOrderCouponDiscountKr();
  if (couponKr > 0) return roundToHalfKrone(orderKr + couponKr);
  if (state.discountApplied && state.totals.discountAmount > 0) {
    return roundToHalfKrone(orderKr + state.totals.discountAmount);
  }
  return orderKr > 0 ? orderKr : subtotal;
}

function updatePunchCardPaymentOverview() {
  DOM.paymentOverview.style.display = 'block';
  const totalWrap = document.querySelector('.payment-overview-total-wrap');
  if (totalWrap) totalWrap.style.display = 'none';

  const monthlyPaymentItem = DOM.monthlyPayment?.closest('.payment-overview-item');
  if (monthlyPaymentItem) monthlyPaymentItem.style.display = 'none';
  if (DOM.firstMonthRow) DOM.firstMonthRow.style.display = 'none';
  if (DOM.paymentBoundUntil) DOM.paymentBoundUntil.style.display = 'none';
  if (DOM.paymentOverview) DOM.paymentOverview.classList.remove('payment-overview--tiered-offer');

  const payNowBreakdown = resolvePayNowDiscountBreakdown(getPunchCardPayNowBaseKr());
  const payNowAmount = roundToHalfKrone(payNowBreakdown.afterDiscount);

  state.totals.payNowAmount = payNowAmount;
  state.totals.cartTotal = payNowAmount;

  applyPaymentOverviewDiscountRows(payNowBreakdown);

  if (DOM.payNow) {
    DOM.payNow.textContent = formatCurrencyHalfKrone(payNowAmount);
    const payNowRow = DOM.payNow.closest('.payment-overview-paynow-row');
    const periodElement = payNowRow?.querySelector('.payment-label-period');
    if (periodElement) periodElement.textContent = '';
  }

  const payNowIncludesEl = document.querySelector('[data-summary-field="pay-now-includes"]');
  if (payNowIncludesEl) {
    payNowIncludesEl.style.display = 'none';
    payNowIncludesEl.textContent = '';
  }

  applyFreeFlowCartUi();
}

function formatCouponPercent(percent) {
  if (!Number.isFinite(percent) || percent <= 0) return null;
  // Half-krone rounding on amounts can yield 25.125% etc. for a 25% coupon — snap to integer when close.
  const rounded = Math.abs(percent - Math.round(percent)) < 0.2
    ? Math.round(percent)
    : Math.round(percent * 10) / 10;
  return `${rounded}%`;
}

function getDiscountAmountLabel(payNowBreakdown) {
  if (!payNowBreakdown?.hasDiscount || payNowBreakdown.beforeDiscount <= 0) {
    return t('cart.discountAmount');
  }
  const percent = (payNowBreakdown.discountKr / payNowBreakdown.beforeDiscount) * 100;
  const percentText = formatCouponPercent(percent);
  if (!percentText) return t('cart.discountAmount');
  return t('cart.discountAmountWithPercent').replace('{percent}', percentText);
}

function normalizeCartProductId(id) {
  if (id == null) return null;
  if (typeof id === 'string' && id.startsWith('punch-')) {
    return parseInt(id.replace('punch-', ''), 10);
  }
  return id;
}

function findOrderItemForCartItem(item) {
  const order = state.fullOrder;
  if (!order || !item) return null;

  const productId = normalizeCartProductId(item.productId ?? item.id);
  const matchProduct = (orderItem) => {
    const pid = orderItem?.product?.id;
    return pid === productId || String(pid) === String(productId);
  };

  if (item.type === 'membership') {
    return order.subscriptionItems?.find(matchProduct) ?? null;
  }
  if (item.type === 'value-card') {
    return order.valueCardItems?.find(matchProduct) ?? null;
  }
  if (item.type === 'addon') {
    return order.articleItems?.find(matchProduct) ?? null;
  }
  return null;
}

/** Per-item coupon from BRP order line (product-specific); 0 when coupon does not apply. */
function getCartItemCouponDiscountKr(item) {
  if (!state.discountApplied) return 0;
  const orderItem = findOrderItemForCartItem(item);
  if (!orderItem?.coupon?.discount) return 0;
  return extractCurrencyOutKr(orderItem.coupon.discount);
}

function getCartItemDisplayPrices(item) {
  const originalPrice = item.amount;
  const itemDiscount = getCartItemCouponDiscountKr(item);
  const displayPrice = itemDiscount > 0
    ? Math.max(0, roundToHalfKrone(originalPrice - itemDiscount))
    : originalPrice;
  return { originalPrice, displayPrice, itemDiscount };
}

function renderCartItemPriceElement(priceEl, item) {
  if (!priceEl) return;
  const { originalPrice, displayPrice, itemDiscount } = getCartItemDisplayPrices(item);
  const roundedOriginal = roundToHalfKrone(originalPrice);
  const roundedDisplay = roundToHalfKrone(displayPrice);

  if (itemDiscount > 0 && roundedDisplay !== roundedOriginal) {
    priceEl.classList.add('cart-price-discounted');
    priceEl.innerHTML = sanitizeHTML(
      `<span class="cart-price-old">${formatPriceHalfKrone(roundedOriginal)} kr.</span><span class="cart-price-new">${formatPriceHalfKrone(roundedDisplay)} kr.</span>`
    );
    return;
  }
  priceEl.classList.remove('cart-price-discounted');
  priceEl.textContent = formatPriceHalfKrone(roundedDisplay) + ' kr.';
}

let discountApplyInFlight = false;

function beginApplyDiscountLoading(label) {
  discountApplyInFlight = true;
  if (!DOM.applyDiscountBtn) return;
  DOM.applyDiscountBtn.disabled = true;
  DOM.applyDiscountBtn.textContent = label;
}

function endApplyDiscountLoading() {
  discountApplyInFlight = false;
  syncApplyDiscountButtonState();
}

function isApplyDiscountButtonLoading() {
  return discountApplyInFlight;
}

function syncApplyDiscountButtonState() {
  if (!DOM.applyDiscountBtn || !DOM.discountInput || state.discountApplied) return;
  if (discountApplyInFlight) return;
  DOM.applyDiscountBtn.textContent = t('button.apply');
  DOM.applyDiscountBtn.disabled = DOM.discountInput.value.trim().length === 0;
}

let discountInputErrorTimer = null;

function clearDiscountInputError() {
  if (discountInputErrorTimer) {
    window.clearTimeout(discountInputErrorTimer);
    discountInputErrorTimer = null;
  }
  DOM.discountInput?.classList.remove('discount-input--error');
}

function setDiscountInputError() {
  clearDiscountInputError();
  DOM.discountInput?.classList.add('discount-input--error');
  discountInputErrorTimer = window.setTimeout(() => {
    if (!state.discountApplied) clearDiscountInputError();
  }, 5000);
}

function resolveDiscountApplyErrorMessage(errorText) {
  const text = String(errorText || '').toLowerCase();

  if (text.includes('log in') || text.includes('account') || text.includes('log ind') || text.includes('konto')) {
    return t('cart.discount.loginRequired');
  }
  if (text.includes('gym location') || text.includes('business unit')) {
    return t('cart.discount.locationRequired');
  }
  if (text.includes('coupon_not_applicable') || text.includes('not applicable')) {
    return t('cart.discount.notApplicable');
  }
  if (text.includes('coupon_not_found') || text.includes('not found') || text.includes('404')) {
    return t('cart.discount.notFound');
  }
  if (text.includes('coupon_expired') || text.includes('expired') || text.includes('udløb')) {
    return t('cart.discount.expired');
  }
  if (text.includes('coupon_already_used') || text.includes('already used')) {
    return t('cart.discount.alreadyUsed');
  }
  if (text.includes('403') || text.includes('forbidden')) {
    return t('cart.discount.forbidden');
  }
  if (text.includes('405')) {
    return t('cart.discount.methodNotSupported');
  }
  if (text.includes('400') || text.includes('invalid') || text.includes('no discount applied')) {
    return t('cart.discount.invalid');
  }

  return t('cart.discount.genericFailed');
}

function showDiscountError(message) {
  showDiscountMessage(message, 'error');
  setDiscountInputError();
  DOM.discountInput?.focus();
}

function handleDiscountInputChange() {
  clearDiscountInputError();
  const existingError = document.querySelector('.discount-message-error');
  if (existingError) clearDiscountMessage();
  syncApplyDiscountButtonState();
}

function updateDiscountFormControls() {
  const entry = DOM.discountFormEntry || DOM.discountForm?.querySelector('.discount-form-entry');
  const chip = DOM.discountAppliedChip || DOM.discountForm?.querySelector('.discount-applied-chip');
  const codeEl = DOM.discountAppliedCode || chip?.querySelector('.discount-applied-code');
  const removeLink = DOM.removeDiscountBtn || chip?.querySelector('.discount-remove-link');

  if (state.discountApplied && state.discountCode) {
    if (entry) entry.hidden = true;
    if (chip) {
      chip.hidden = false;
      if (codeEl) codeEl.textContent = state.discountCode;
    }
    if (removeLink) {
      removeLink.disabled = false;
      removeLink.textContent = t('button.removeCoupon');
    }
    return;
  }

  if (entry) entry.hidden = false;
  if (chip) chip.hidden = true;
  if (removeLink) {
    removeLink.disabled = false;
    removeLink.textContent = t('button.removeCoupon');
  }
  syncApplyDiscountButtonState();
}

function resetLocalDiscountState({ preserveInput = false } = {}) {
  state.discountCode = null;
  state.discountApplied = false;
  state.totals.discountAmount = 0;
  if (DOM.discountInput) {
    DOM.discountInput.disabled = false;
    if (!preserveInput) {
      DOM.discountInput.value = '';
    }
    DOM.discountInput.style.opacity = '';
    if (!preserveInput) {
      clearDiscountInputError();
    }
  }
  updateDiscountFormControls();
}

const debugEnabled = window.DEBUG_LOGS === true;
const originalConsoleLog = console.log.bind(console);
const originalConsoleWarn = console.warn.bind(console);
if (!debugEnabled) {
  console.log = () => {};
  console.warn = () => {};
}
const devLog = (...args) => {
  if (debugEnabled) originalConsoleLog(...args);
};
const devWarn = (...args) => {
  if (debugEnabled) originalConsoleWarn(...args);
};
const geoLogger = { log: devLog, warn: devWarn };

const LANDING_ROUTE_CONFIG = Object.freeze({
  '/freetrial': Object.freeze({
    componentName: 'LandingFreeTrial',
    labelKey: 'LandingFreeTrial',
    mode: 'single',
  }),
  '/membership-offer': Object.freeze({
    componentName: 'LandingFirstMonthFree',
    labelKey: 'LandingFirstMonthFree',
    mode: 'multi',
  }),
  '/99kr': Object.freeze({
    componentName: 'LandingFirstClimb',
    labelKey: 'firstclimb',
    mode: 'single',
  }),
});
const NON_INDEXABLE_PATHS = Object.freeze(new Set([
  '/freetrial',
  '/membership-offer',
  '/99kr',
]));

function normalizePathname(pathname) {
  const raw = String(pathname || '/').trim().toLowerCase();
  if (!raw || raw === '/') return '/';
  const withoutTrailingSlash = raw.replace(/\/+$/, '');
  return withoutTrailingSlash || '/';
}

function resolveLandingRouteConfig(pathname = window.location.pathname) {
  const normalizedPath = normalizePathname(pathname);
  return LANDING_ROUTE_CONFIG[normalizedPath] || null;
}

function isFirstClimbRoute() {
  const active = (typeof state !== 'undefined' && state?.landingRouteConfig) || resolveLandingRouteConfig();
  return active?.componentName === 'LandingFirstClimb';
}

// firstclimb is a one-per-customer offer. Every user — new or existing — can
// buy it exactly once. A customer is disqualified if they have ever held a
// product carrying the dedicated `Første Gang` label (the canonical blocking
// signal, assigned in BRP admin to this product and any future products that
// should consume the same allowance). The legacy `firstclimb` label is kept
// in the list defensively in case it lingers on historical product records.
// Stored lowercase + whitespace-collapsed; matched with the same normalization.
const FIRSTCLIMB_BLOCKING_LABELS = Object.freeze([
  'første gang',
  'firstclimb',
]);

function normalizeFirstclimbLabelName(value) {
  // NFC-normalize so single-codepoint "ø" (U+00F8) and decomposed
  // "o + combining stroke" (U+006F U+0338) compare equal — BRP sometimes
  // returns the decomposed form.
  return String(value || '').normalize('NFC').trim().toLowerCase().replace(/\s+/g, ' ');
}

function productHasFirstclimbBlockingLabel(product) {
  const labels = Array.isArray(product?.productLabels) ? product.productLabels : [];
  const matched = labels.find((label) => {
    const name = normalizeFirstclimbLabelName(label?.name);
    return FIRSTCLIMB_BLOCKING_LABELS.includes(name);
  });
  if (matched) {
    console.log('[firstclimb] product label matched blocking list:', {
      productId: product?.id,
      productName: product?.name,
      matchedLabel: matched?.name,
      allLabels: labels.map((l) => l?.name),
    });
  } else if (labels.length > 0) {
    // Help diagnose mismatches when a product *does* carry labels but none
    // are recognised — e.g. encoding drift, unexpected spelling, etc.
    console.log('[firstclimb] product carries labels but none are blocking:', {
      productId: product?.id,
      productName: product?.name,
      labels: labels.map((l) => ({ name: l?.name, normalized: normalizeFirstclimbLabelName(l?.name) })),
      blockingList: FIRSTCLIMB_BLOCKING_LABELS,
    });
  }
  return Boolean(matched);
}

async function _firstclimbAuthFetch(path) {
  const token = (typeof window.getAccessToken === 'function') ? window.getAccessToken() : null;
  if (!token) throw new Error('No access token for customer history fetch');
  const url = buildApiUrl({
    baseUrl: businessUnitsAPI?.baseUrl || null,
    useProxy: businessUnitsAPI?.useProxy ?? true,
    path,
  });
  return requestJson({
    url,
    method: 'GET',
    headers: {
      'Accept-Language': getAcceptLanguageHeader(),
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  });
}

// Pull product IDs the customer currently holds or has ever held. Errors on
// any single endpoint are swallowed — we'd rather fail open per-source than
// abort the whole check (the caller defaults to "block" if all sources fail).
async function collectFirstclimbCustomerProductIds(customerId) {
  if (!customerId) return { ids: new Set(), errors: ['no customer id'] };
  const ids = new Set();
  const errors = [];
  const safeFetch = async (path) => {
    try { return await _firstclimbAuthFetch(path); }
    catch (e) { errors.push(`${path}: ${e?.message || e}`); return null; }
  };
  const [subs, valueCards, receipts] = await Promise.all([
    safeFetch(`/api/ver3/customers/${customerId}/subscriptions?includeInactive=true`),
    safeFetch(`/api/ver3/customers/${customerId}/valuecards`),
    safeFetch(`/api/ver3/customers/${customerId}/receipts`),
  ]);
  const addRef = (ref) => {
    const id = ref?.id ?? ref?.productId ?? ref?.product?.id;
    if (id != null) ids.add(String(id));
  };
  (Array.isArray(subs) ? subs : []).forEach((s) => addRef(s?.subscriptionProduct || s?.product));
  (Array.isArray(valueCards) ? valueCards : []).forEach((vc) => addRef(vc?.valueCardProduct || vc?.validCardProduct || vc?.product));
  (Array.isArray(receipts) ? receipts : []).forEach((r) => {
    (r?.receiptItems || r?.items || []).forEach((item) => addRef(item?.product || item));
  });
  console.log('[firstclimb] customer history product IDs collected:', {
    customerId,
    ids: Array.from(ids),
    counts: {
      subscriptions: Array.isArray(subs) ? subs.length : 0,
      valueCards: Array.isArray(valueCards) ? valueCards.length : 0,
      receipts: Array.isArray(receipts) ? receipts.length : 0,
    },
    errors,
  });
  return { ids, errors };
}

// Resolve a product by id from the already-loaded catalog if possible, else fetch.
async function resolveProductForFirstclimbCheck(productId) {
  const id = String(productId);
  const fromCatalog = (state.allRawProducts || []).find((p) => String(p?.id) === id);
  if (fromCatalog) return fromCatalog;
  if (!state.selectedBusinessUnit) return null;
  // Try subscription first, then value card. Both endpoints 404 if it's the other kind.
  try { return await businessUnitsAPI.getSubscriptionById(productId, state.selectedBusinessUnit); }
  catch (_) {}
  try { return await businessUnitsAPI.getValueCardById(productId, state.selectedBusinessUnit); }
  catch (_) {}
  return null;
}

async function customerHasFirstclimbBlockingHistory(customerId) {
  const { ids, errors } = await collectFirstclimbCustomerProductIds(customerId);
  if (errors.length) console.warn('[firstclimb] history fetch had partial errors:', errors);
  if (ids.size === 0) return false; // clean history
  for (const id of ids) {
    let product = null;
    try { product = await resolveProductForFirstclimbCheck(id); } catch (_) {}
    if (product && productHasFirstclimbBlockingLabel(product)) {
      console.log('[firstclimb] blocking product detected in customer history:', { id, name: product?.name });
      return true;
    }
  }
  return false;
}

// firstclimb is a single-product flow. Once the gym is chosen we have everything
// we need to skip the product-selection step and drop the user straight into the
// form/checkout step with the value card already selected (quantity locked to 1).
async function advanceFirstClimbFromGym() {
  if (state.selectedBusinessUnit && !productsLoadPromise) {
    loadProductsFromAPI();
  }
  try { await productsLoadPromise; } catch (_) {}

  const product = (state.landingMatchedProducts || [])[0];
  if (!product) {
    devWarn('[Landing Route] firstclimb auto-select aborted — no matched product');
    state.currentStep = 2;
    showStep(2);
    updateStepIndicator();
    updateNavigationButtons();
    updateMainSubtitle();
    return;
  }

  const planId = `punch-${product.id}`;
  state.membershipPlanId = planId;
  state.selectedProductId = product.id;
  state.selectedProductType = 'punch-card';
  state.valueCardQuantities.set(planId, 1);

  // Form/checkout step (addons step 3 is always skipped in this codebase).
  state.currentStep = 4;
  showStep(4);
  updateStepIndicator();
  updateNavigationButtons();
  updateMainSubtitle();
  try { updateCartSummary(); } catch (_) {}
  scrollToTop();
  setTimeout(() => scrollToTop(), 200);
}

// firstclimb is a new-customer-only product. If BRP recognizes the email
// (or the user is already logged in), we surface a modal explaining why the
// purchase can't proceed and direct them to the regular entry options.
function blockFirstClimbExistingCustomer({ email } = {}) {
  const emailVal = (email || '').toLowerCase();
  const title = (typeof t === 'function' && t('firstclimb.existingCustomer.title'))
    || 'This offer is for new climbers only';
  const baseBody = (typeof t === 'function' && t('firstclimb.existingCustomer.body'))
    || 'We already have a profile linked to your email {email}. You can still climb — pick one of our other products.';
  // Inline the email next to "your email" so it reads naturally. If we don't
  // have an email, strip the placeholder and the leading space before it.
  const body = emailVal
    ? baseBody.replace('{email}', emailVal)
    : baseBody.replace(/\s*\{email\}/g, '');
  const ctaLabel = (typeof t === 'function' && t('firstclimb.existingCustomer.cta'))
    || 'See other entry options';
  const closeLabel = (typeof t === 'function' && t('button.close')) || 'Close';

  // Stop the checkout flow dead — disable the button so the modal isn't fighting
  // a stale click handler.
  try {
    state.checkoutInProgress = false;
    setCheckoutLoadingState && setCheckoutLoadingState(false);
  } catch (_) {}
  try {
    document.querySelectorAll('[data-action="submit-checkout"]').forEach((btn) => {
      btn.disabled = true;
      btn.style.pointerEvents = 'none';
      btn.style.opacity = '0.5';
    });
  } catch (_) {}

  // Build modal once; reuse if already in DOM.
  let modal = document.getElementById('firstclimbBlockerModal');
  if (!modal) {
    modal = document.createElement('div');
    modal.id = 'firstclimbBlockerModal';
    modal.className = 'firstclimb-modal-overlay';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-labelledby', 'firstclimbBlockerTitle');
    document.body.appendChild(modal);
  }
  modal.innerHTML = sanitizeHTML(`
    <div class="firstclimb-modal">
      <button class="firstclimb-modal-close" type="button" aria-label="${closeLabel}" data-firstclimb-modal-close>&times;</button>
      <div class="firstclimb-modal-title" id="firstclimbBlockerTitle">${title}</div>
      <p class="firstclimb-modal-body">${body}</p>
      <div class="firstclimb-modal-actions">
        <a class="firstclimb-modal-cta" href="/">${ctaLabel}</a>
      </div>
    </div>
  `);
  modal.style.display = 'flex';

  const close = () => {
    modal.style.display = 'none';
  };
  modal.querySelector('[data-firstclimb-modal-close]')?.addEventListener('click', close);
  modal.addEventListener('click', (event) => {
    if (event.target === modal) close();
  });
}

function applyRouteIndexingPolicy(pathname = window.location.pathname) {
  const normalizedPath = normalizePathname(pathname);
  const shouldNoIndex = NON_INDEXABLE_PATHS.has(normalizedPath);
  const managedMetaSelector = 'meta[data-route-indexing="managed"]';

  document.querySelectorAll(managedMetaSelector).forEach((meta) => meta.remove());
  if (!shouldNoIndex) return;

  [
    { name: 'robots', content: 'noindex, nofollow' },
    { name: 'googlebot', content: 'noindex, nofollow' },
  ].forEach(({ name, content }) => {
    const meta = document.createElement('meta');
    meta.setAttribute('name', name);
    meta.setAttribute('content', content);
    meta.setAttribute('data-route-indexing', 'managed');
    document.head.appendChild(meta);
  });
}

function applyOfferLandingCategoryCopy() {
  const activeLandingRoute = state.landingRouteConfig || resolveLandingRouteConfig();
  if (activeLandingRoute?.componentName !== 'LandingFirstMonthFree') return;
  const targetCategory = document.querySelector('[data-category="campaign"]');
  const title = targetCategory?.querySelector('.category-title');
  const subtitle = targetCategory?.querySelector('.category-subtitle');
  const description = targetCategory?.querySelector('.category-description p');
  const countdown = document.getElementById('campaignCountdown');

  if (title) {
    title.textContent = t('offer.membership.title');
    title.removeAttribute('data-i18n-key');
  }
  if (subtitle) {
    subtitle.textContent = '';
    subtitle.removeAttribute('data-i18n-key');
  }
  if (description) {
    const descriptionText = t('offer.membership.description');
    if (descriptionText.includes('<a') || descriptionText.includes('<span')) {
      description.innerHTML = sanitizeHTML(descriptionText);
    } else {
      description.textContent = descriptionText;
    }
    description.removeAttribute('data-i18n-key');
  }
  if (countdown) countdown.style.display = 'none';
  stopCampaignCountdown();
}

const REQUIRED_FIELDS = [
  'firstName',
  'lastName',
  'dateOfBirth',
  'streetAddress',
  'postalCode',
  'email',
  'countryCode',
  'password',
  'primaryGym',
];

const PARENT_REQUIRED_FIELDS = [
  'parentFullName',
  'parentDateOfBirth',
  'parentStreetAddress',
  'parentPostalCode',
  'parentEmail',
  'parentCountryCode',
  'parentPassword',
  'parentPrimaryGym',
];

const CARD_FIELDS = ['cardNumber', 'expiryDate', 'cvv', 'cardholderName'];


// API Integration Functions
class BusinessUnitsAPI {
  constructor(baseUrl = null) {
    const config = getApiConfig({ baseUrlOverride: baseUrl });
    this.baseUrl = config.baseUrl;
    this.useProxy = config.useProxy;
  }

  // Get all business units from API
  // Step 3: Fetch from /api/reference/business-units endpoint
  // Note: This endpoint uses "No Auth" according to Postman docs
  async getBusinessUnits() {
    try {
      const url = buildApiUrl({
        baseUrl: this.baseUrl,
        useProxy: this.useProxy,
        path: '/api/reference/business-units',
      });
      devLog('Fetching business units from:', url);
      
      const headers = {
        'Accept-Language': getAcceptLanguageHeader(), // Step 2: Language default
        'Content-Type': 'application/json',
        // No Authorization header needed - endpoint uses "No Auth"
      };

      const data = await requestJson({ url, headers });
      devLog('Business units API response:', data);
      devLog('Response type:', Array.isArray(data) ? 'Array' : typeof data);
      devLog('Number of items:', Array.isArray(data) ? data.length : 'N/A');

      return data;
    } catch (error) {
      console.error('Error fetching business units:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        name: error.name,
        status: error.status,
        payload: error.payload,
      });
      // Don't use fallback mock data - throw error so caller can handle it
      throw error;
    }
  }

  // Create a new business unit
  async createBusinessUnit(businessUnitData) {
    try {
      const response = await fetch(`${this.baseUrl}/api/ver3/businessunits`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(businessUnitData)
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error creating business unit:', error);
      throw error;
    }
  }

  // Update an existing business unit
  async updateBusinessUnit(id, businessUnitData) {
    try {
      const response = await fetch(`${this.baseUrl}/api/ver3/businessunits/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(businessUnitData)
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error updating business unit:', error);
      throw error;
    }
  }

  // Step 5: Get subscriptions (memberships) for a business unit
  // According to OpenAPI spec (line ~8287):
  // - subscriber parameter: "This is to determine whether the product is bookable for the subscription user"
  // - allowedToOrder field: "To determine whether the subscription product is bookable for the subscription user or not"
  // Note: When no subscriber is provided (anonymous users), allowedToOrder should reflect whether
  // the product can be booked via internet (based on backend checkbox settings)
  async getSubscriptions(businessUnitId) {
    try {
      // Build URL with business unit as query parameter
      // Note: We don't send subscriber/customer parameters for anonymous users
      // Backend should set allowedToOrder based on "kan bookes via internet" checkbox
      const cacheBuster = `&_t=${Date.now()}`;
      const queryParam = businessUnitId ? `?businessUnit=${businessUnitId}${cacheBuster}` : `?_t=${Date.now()}`;
      const url = buildApiUrl({
        baseUrl: this.baseUrl,
        useProxy: this.useProxy,
        path: `/api/products/subscriptions${queryParam}`,
      });
      devLog('Fetching subscriptions from:', url);
      
      const acceptLanguage = getAcceptLanguageHeader();
      const headers = {
        'Accept-Language': acceptLanguage,
        'Content-Type': 'application/json',
      };
      
      devLog('[API] Fetching subscriptions with Accept-Language:', acceptLanguage);

      const data = await requestJson({ url, headers });
      devLog('[API] Subscriptions response sample:', data[0] ? { id: data[0].id, name: data[0].name, description: data[0].description?.substring(0, 50), externalDescription: data[0].externalDescription?.substring(0, 50) } : 'empty');
      return data;
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
      throw error;
    }
  }

  // Step 5: Get value cards (punch cards)
  async getValueCards() {
    try {
      const cacheBuster = `?_t=${Date.now()}`;
      const url = buildApiUrl({
        baseUrl: this.baseUrl,
        useProxy: this.useProxy,
        path: `/api/products/valuecards${cacheBuster}`,
      });
      devLog('Fetching value cards from:', url);
      
      const acceptLanguage = getAcceptLanguageHeader();
      const headers = {
        'Accept-Language': acceptLanguage,
        'Content-Type': 'application/json',
      };
      
      devLog('[API] Fetching value cards with Accept-Language:', acceptLanguage);

      const data = await requestJson({ url, headers });
      devLog('[API] Value cards response sample:', data[0] ? { 
        id: data[0].id, 
        name: data[0].name, 
        description: data[0].description?.substring(0, 50) 
      } : 'empty');
      return data;
    } catch (error) {
      console.error('Error fetching value cards:', error);
      throw error;
    }
  }

  // Step 5: Get subscription additions (add-ons) for a specific membership product
  // Note: The /additions endpoint may not exist yet - this is a placeholder for when it's available
  async getSubscriptionAdditions(productId) {
    try {
      // Try the additions endpoint first (as per guide)
      const url = buildApiUrl({
        baseUrl: this.baseUrl,
        useProxy: this.useProxy,
        path: `/api/products/subscriptions/${productId}/additions`,
      });
      devLog('Fetching subscription additions from:', url);
      
      const headers = {
        'Accept-Language': getAcceptLanguageHeader(),
        'Content-Type': 'application/json',
      };

      let data;
      try {
        data = await requestJson({ url, headers });
      } catch (error) {
        // If 404, the endpoint doesn't exist yet - return empty array for now
        if (error.status === 404) {
          devWarn(`Additions endpoint not found for product ${productId}. Endpoint may not be implemented yet.`);
          return [];
        }
        throw error;
      }
      devLog('Subscription additions API response:', data);
      
      // Handle different response formats
      return Array.isArray(data) ? data : (data.data || data.items || []);
    } catch (error) {
      console.error('Error fetching subscription additions:', error);
      // Return empty array if endpoint doesn't exist - don't break the flow
      devWarn('Returning empty array for add-ons - endpoint may not be available yet');
      return [];
    }
  }

  async getSubscriptionById(productId, businessUnitId = null) {
    try {
      const cacheBuster = `_t=${Date.now()}`;
      const query = businessUnitId != null
        ? `?businessUnit=${businessUnitId}&${cacheBuster}`
        : `?${cacheBuster}`;
      const url = buildApiUrl({
        baseUrl: this.baseUrl,
        useProxy: this.useProxy,
        path: `/api/products/subscriptions/${productId}${query}`,
      });
      const headers = {
        'Accept-Language': getAcceptLanguageHeader(),
        'Content-Type': 'application/json',
      };
      return await requestJson({ url, headers });
    } catch (error) {
      console.warn('[Boost Modal] Could not load subscription details:', { productId, error });
      return null;
    }
  }

  async getValueCardById(productId, businessUnitId = null) {
    try {
      const cacheBuster = `_t=${Date.now()}`;
      const query = businessUnitId != null
        ? `?businessUnit=${businessUnitId}&${cacheBuster}`
        : `?${cacheBuster}`;
      const url = buildApiUrl({
        baseUrl: this.baseUrl,
        useProxy: this.useProxy,
        path: `/api/products/valuecards/${productId}${query}`,
      });
      const headers = {
        'Accept-Language': getAcceptLanguageHeader(),
        'Content-Type': 'application/json',
      };
      return await requestJson({ url, headers });
    } catch (error) {
      console.warn('[Boost Modal] Could not load value card details:', { productId, error });
      return null;
    }
  }

  // Step 8: Get additional catalog items - GET /api/products
  // To offer more products, fetch catalogs with GET /api/products
  async getProducts(businessUnitId = null) {
    try {
      const queryParam = businessUnitId ? `?businessUnit=${businessUnitId}` : '';
      const url = buildApiUrl({
        baseUrl: this.baseUrl,
        useProxy: this.useProxy,
        path: `/api/products${queryParam}`,
      });
      
      devLog('[Step 8] Fetching additional catalog products from:', url);
      
      const headers = {
        'Accept-Language': getAcceptLanguageHeader(),
        'Content-Type': 'application/json',
      };

      let data;
      try {
        data = await requestJson({ url, headers });
      } catch (error) {
        // If 404, the endpoint doesn't exist yet - return empty array
        if (error.status === 404) {
          devLog('[Step 8] Products endpoint not found (404) - may not be implemented yet');
          return [];
        }
        throw error;
      }

      devLog('[Step 8] Products API response:', data);
      
      // Handle different response formats
      return Array.isArray(data) ? data : (data.data || data.items || []);
    } catch (error) {
      console.error('[Step 8] Error fetching products:', error);
      // Return empty array if endpoint doesn't exist - don't break the flow
      return [];
    }
  }
}

// Step 4: Reference Data Loader API
// Fetches reference/lookup data after business unit selection
// Caches responses in client state and refreshes when business unit changes
class ReferenceDataAPI {
  constructor(baseUrl = null) {
    const config = getApiConfig({ baseUrlOverride: baseUrl });
    this.baseUrl = config.baseUrl;
    this.useProxy = config.useProxy;
    this.isDevelopment = config.isDevelopment;
  }

  // Fetch reference data (extensible for different types of reference data)
  // This is a flexible method that can be extended as needed
  async getReferenceData(type, businessUnitId = null) {
    try {
      // Build endpoint path based on type
      // Examples: 'countries', 'regions', 'currencies', 'payment-methods', etc.
      let endpoint = `/api/reference/${type}`;
      
      // Add business unit query param if provided and needed
      const queryParam = businessUnitId ? `?businessUnit=${businessUnitId}` : '';
      
      let url;
      if (this.useProxy) {
        url = `${this.baseUrl}?path=${endpoint}${queryParam}`;
      } else {
        url = `${this.baseUrl}${endpoint}${queryParam}`;
      }
      
      console.log(`[Step 4] Fetching reference data (${type}) from:`, url);
      
      const headers = {
        'Accept-Language': getAcceptLanguageHeader(),
        'Content-Type': 'application/json',
      };
      
      const response = await fetch(url, {
        method: 'GET',
        headers,
      });
      
      if (!response.ok) {
        // If 404, the endpoint doesn't exist - return null (not an error, just not available)
        if (response.status === 404) {
          console.log(`[Step 4] Reference data endpoint ${type} not found (404) - may not be implemented yet`);
          return null;
        }
        
        const errorText = await response.text();
        console.error(`[Step 4] API Error (${response.status}):`, errorText);
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }
      
      const data = await response.json();
      console.log(`[Step 4] Reference data (${type}) API response:`, data);
      return data;
    } catch (error) {
      console.error(`[Step 4] Error fetching reference data (${type}):`, error);
      // Don't throw - reference data is optional, return null instead
      return null;
    }
  }

  // Fetch all reference data types that might be needed
  // This can be extended as new reference data types are identified
  async getAllReferenceData(businessUnitId = null) {
    const referenceData = {};
    
    // List of reference data types to fetch
    // Add more types here as they become available/needed
    const referenceTypes = [
      // 'countries',      // If available
      // 'regions',         // If available
      // 'currencies',      // If available
      // 'payment-methods', // If available
      // Add more as needed
    ];
    
    // Fetch all reference data types in parallel
    const promises = referenceTypes.map(async (type) => {
      const data = await this.getReferenceData(type, businessUnitId);
      if (data !== null) {
        referenceData[type] = data;
      }
    });
    
    await Promise.all(promises);
    
    console.log('[Step 4] All reference data loaded:', referenceData);
    return referenceData;
  }

  // Lookup city by postal code
  // Endpoint: GET /api/addresses/{postalCode}
  async lookupCityByPostalCode(postalCode) {
    if (!postalCode || postalCode.trim().length === 0) {
      return null;
    }

    try {
      // Clean postal code (remove spaces, ensure it's a valid format)
      const cleanPostalCode = postalCode.trim().replace(/\s+/g, '');
      
      // Danish postal codes are 4 digits
      if (!/^\d{4}$/.test(cleanPostalCode)) {
        console.log('[PostalCode] Invalid postal code format:', cleanPostalCode);
        return null;
      }

      const endpoint = `/api/addresses/${cleanPostalCode}`;
      
      let url;
      if (this.useProxy) {
        url = `${this.baseUrl}?path=${endpoint}`;
      } else {
        // In development, use full API URL if baseUrl is empty
        const apiBase = this.baseUrl || 'https://api-join.boulders.dk';
        url = `${apiBase}${endpoint}`;
      }
      
      console.log('[PostalCode] Looking up city for postal code:', cleanPostalCode, 'from:', url);
      
      const headers = {
        'Accept-Language': getAcceptLanguageHeader(),
        'Content-Type': 'application/json',
      };
      
      const response = await fetch(url, {
        method: 'GET',
        headers,
      });
      
      if (!response.ok) {
        // If 404, endpoint doesn't exist yet - try local lookup as fallback
        if (response.status === 404) {
          console.log('[PostalCode] Address lookup endpoint not available (404) - trying local lookup...');
          
          // Try local lookup if available
          if (typeof lookupCityByPostalCode === 'function') {
            const localCity = lookupCityByPostalCode(cleanPostalCode);
            if (localCity) {
              console.log('[PostalCode] Found city in local lookup:', localCity);
              return localCity;
            }
          }
          
          // If local lookup also fails, return unavailable flag
          return { unavailable: true };
        }
        
        const errorText = await response.text();
        console.error(`[PostalCode] API Error (${response.status}):`, errorText);
        return null; // Don't throw - just return null if lookup fails
      }
      
      const data = await response.json();
      console.log('[PostalCode] Address lookup response:', data);
      
      // Extract city name from response
      // API might return: { city: "Copenhagen" } or { address: { city: "Copenhagen" } }
      const city = data?.city || data?.address?.city || data?.name || null;
      
      if (city) {
        console.log('[PostalCode] Found city:', city, 'for postal code:', cleanPostalCode);
        return city;
      }
      
      return null;
    } catch (error) {
      console.error('[PostalCode] Error looking up city by postal code:', error);
      // Network errors or CORS issues - try local lookup as fallback
      const errorMessage = error.message || String(error);
      if (errorMessage.includes('Failed to fetch') || errorMessage.includes('CORS') || errorMessage.includes('NetworkError')) {
        console.log('[PostalCode] Network/CORS error - trying local lookup...');
        
        // Try local lookup if available
        if (typeof lookupCityByPostalCode === 'function') {
          const cleanPostalCode = postalCode.trim().replace(/\s+/g, '');
          const localCity = lookupCityByPostalCode(cleanPostalCode);
          if (localCity) {
            console.log('[PostalCode] Found city in local lookup:', localCity);
            return localCity;
          }
        }
        
        return { unavailable: true };
      }
      return null; // Don't throw - just return null if lookup fails
    }
  }
}

// Step 6: Authentication API
// Handles login, token management, customer creation, and password reset
class AuthAPI {
  constructor(baseUrl = null) {
    const config = getApiConfig({ baseUrlOverride: baseUrl });
    this.baseUrl = config.baseUrl;
    this.useProxy = config.useProxy;
    this.isDevelopment = config.isDevelopment;
  }

  // Step 6: Login - Submit login credentials and store tokens
  async login(email, password, options = {}) {
    try {
      const { saveTokens = true } = options;
      const url = buildApiUrl({
        baseUrl: this.baseUrl,
        useProxy: this.useProxy,
        path: '/api/auth/login',
      });
      
      devLog('[Step 6] Logging in:', url);
      
      const headers = {
        'Accept-Language': getAcceptLanguageHeader(),
        'Content-Type': 'application/json',
      };
      
      // API expects username field (which is the email)
      let data;
      try {
        data = await requestJson({
          url,
          method: 'POST',
          headers,
          body: { username: email, password },
        });
      } catch (error) {
        const errorPayload = error?.payload;
        const payloadText = typeof errorPayload === 'string' ? errorPayload : JSON.stringify(errorPayload);
        console.error(`[Step 6] Login error (${error.status || 'unknown'}):`, payloadText || error);

        // Handle rate limit errors with better messaging
        if (error.status === 429) {
          let retryAfterSeconds = 60; // Default 1 minute (60 seconds)
          if (errorPayload && typeof errorPayload === 'object' && errorPayload.retryAfter) {
            retryAfterSeconds = parseInt(errorPayload.retryAfter, 10);
          } else if (payloadText) {
            const retryMatch = payloadText.match(/retryAfter["\s:]*(\d+)/i);
            if (retryMatch) {
              retryAfterSeconds = parseInt(retryMatch[1], 10);
            }
          }
          if (typeof window !== 'undefined') {
            window.loginCooldownUntil = Date.now() + (retryAfterSeconds * 1000);
          }
          const retryMinutes = Math.floor(retryAfterSeconds / 60);
          const retrySeconds = retryAfterSeconds % 60;
          const retryMessage = retryMinutes > 0
            ? `${retryMinutes} minute${retryMinutes !== 1 ? 's' : ''}${retrySeconds > 0 ? ` and ${retrySeconds} second${retrySeconds !== 1 ? 's' : ''}` : ''}`
            : `${retryAfterSeconds} second${retryAfterSeconds !== 1 ? 's' : ''}`;
          throw new Error(`Rate limit exceeded. Please wait ${retryMessage} before trying again. (${error.status} - ${payloadText})`);
        }

        // Handle 401 errors - preserve error structure for getErrorMessage
        if (error.status === 401 && errorPayload && typeof errorPayload === 'object') {
          if (errorPayload.error?.code === 'INVALID_CREDENTIALS' || errorPayload.error?.message) {
            throw new Error(`Login failed: ${error.status} - ${payloadText}`);
          }
        }

        throw new Error(`Login failed: ${error.status || 'unknown'} - ${payloadText || error.message}`);
      }

      devLog('[Step 6] Login response:', data);
      
      const tokenPayload = data?.data ?? data;
      const accessToken = tokenPayload.accessToken || tokenPayload.access_token;
      const refreshToken = tokenPayload.refreshToken || tokenPayload.refresh_token;
      let expiresAt = tokenPayload.expiresAt || tokenPayload.expires_at;
      const expiresIn = tokenPayload.expiresIn || tokenPayload.expires_in;
      if (!expiresAt && expiresIn) {
        const expiresInMs = Number(expiresIn) * 1000;
        expiresAt = Date.now() + (Number.isFinite(expiresInMs) ? expiresInMs : 0);
      }
      
      if (accessToken && refreshToken) {
        if (typeof window.saveTokens === 'function') {
          if (saveTokens) {
            const metadata = {
              username: tokenPayload.username || tokenPayload.userName,
              email: tokenPayload.email || email,
              roles: tokenPayload.roles || [],
              tokenType: tokenPayload.tokenType || tokenPayload.token_type,
              expiresIn: tokenPayload.expiresIn || tokenPayload.expires_in,
            };
            window.saveTokens(accessToken, refreshToken, expiresAt, metadata);
            console.log('[Step 6] ✅ Tokens saved successfully');

            // Set user context in Sentry for error tracking
            setUser({
              id: tokenPayload.sub || tokenPayload.userId,
              email: tokenPayload.email || email,
            });
          }
        } else {
          console.warn('[Step 6] saveTokens function not available - tokens not saved');
        }
      } else {
        console.warn('[Step 6] ⚠️ No tokens found in login response');
        console.warn('[Step 6] Response structure:', Object.keys(data));
        if (data.data) {
          console.warn('[Step 6] Data object structure:', Object.keys(data.data));
        }
      }
      
      return data;
    } catch (error) {
      console.error('[Step 6] Login error:', error);

      // Report login errors to Sentry (excluding rate limits and user input errors)
      if (error.status !== 429 && error.status !== 400 && error.status !== 401) {
        captureException(error, {
          tags: {
            flow: 'authentication',
            error_type: 'login_failed',
            status: error.status,
          },
        });
      }

      throw error;
    }
  }

  // Step 6: Validate token - Keep tokens fresh when app reloads
  async validateToken() {
    try {
      const url = buildApiUrl({
        baseUrl: this.baseUrl,
        useProxy: this.useProxy,
        path: '/api/auth/validate',
      });
      
      devLog('[Step 6] Validating token:', url);
      
      // Note: Authorization header will be added automatically by HttpClient
      // But since we're using fetch directly, we need to add it manually
      const accessToken = typeof window.getAccessToken === 'function' 
        ? window.getAccessToken() 
        : null;
      
      if (!accessToken) {
        throw new Error('No access token available');
      }
      
      const headers = {
        'Accept-Language': getAcceptLanguageHeader(),
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      };
      
      const data = await requestJson({
        url,
        method: 'POST',
        headers,
        body: { accessToken },
      });
      devLog('[Step 6] Token validation response:', data);
      return data;
    } catch (error) {
      console.error('[Step 6] Token validation error:', error);
      throw error;
    }
  }

  // Step 6: Refresh token - Refresh expired tokens
  async refreshToken() {
    try {
      const url = buildApiUrl({
        baseUrl: this.baseUrl,
        useProxy: this.useProxy,
        path: '/api/auth/refresh',
      });
      
      devLog('[Step 6] Refreshing token:', url);
      
      const refreshToken = typeof window.getRefreshToken === 'function' 
        ? window.getRefreshToken() 
        : null;
      
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }
      
      const headers = {
        'Accept-Language': getAcceptLanguageHeader(),
        'Content-Type': 'application/json',
      };
      
      let data;
      try {
        data = await requestJson({
          url,
          method: 'POST',
          headers,
          body: { refreshToken },
        });
      } catch (error) {
        const payloadText = typeof error.payload === 'string' ? error.payload : JSON.stringify(error.payload);
        console.error(`[Step 6] Token refresh error (${error.status || 'unknown'}):`, payloadText || error);
        const isRateLimit = error.status === 429;
        // If refresh fails for other reasons, clear tokens and return to auth step
        if (!isRateLimit && typeof window.clearTokens === 'function') {
          window.clearTokens();
        }
        throw new Error(`Token refresh failed: ${error.status || 'unknown'} - ${payloadText || error.message}`);
      }
      
      devLog('[Step 6] Token refresh response:', data);
      
      const tokenPayload = data?.data ?? data;
      const newAccessToken = tokenPayload.accessToken || tokenPayload.access_token;
      const newRefreshToken = tokenPayload.refreshToken || tokenPayload.refresh_token || refreshToken;
      let newExpiresAt = tokenPayload.expiresAt || tokenPayload.expires_at;
      const expiresIn = tokenPayload.expiresIn || tokenPayload.expires_in;
      if (!newExpiresAt && expiresIn) {
        const expiresInMs = Number(expiresIn) * 1000;
        newExpiresAt = Date.now() + (Number.isFinite(expiresInMs) ? expiresInMs : 0);
      }
      
      // Store new tokens with metadata
      if (newAccessToken && newRefreshToken && typeof window.saveTokens === 'function') {
        const metadata = {
          username: tokenPayload.username || data.username,
          email: tokenPayload.email || data.email || state.authenticatedEmail,
          roles: tokenPayload.roles || data.roles,
          tokenType: tokenPayload.tokenType || tokenPayload.token_type || data.tokenType || data.token_type,
          expiresIn: expiresIn,
        };
        window.saveTokens(newAccessToken, newRefreshToken, newExpiresAt, metadata);
        
        // Update header auth indicator after token refresh
        refreshHeaderAuthIndicator();
        
        // Dispatch event to notify React components
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new Event('auth-state-changed'));
        }
      }
      
      return tokenPayload;
    } catch (error) {
      console.error('[Step 6] Token refresh error:', error);
      throw error;
    }
  }

  // Step 6: Password reset - Offer forgotten-password flow
  // Endpoint: POST /api/ver3/auth/resetpassword
  // Base URL: https://boulders.brpsystems.com/apiserver (handled by proxy for ver3 endpoints)
  // appId is optional - if not provided, BRP will use default setting for reset link
  async resetPassword(email, appId = null) {
    try {
      const url = this.useProxy
        ? buildApiUrl({
            baseUrl: this.baseUrl,
            useProxy: this.useProxy,
            path: '/api/ver3/auth/resetpassword',
          })
        : 'https://boulders.brpsystems.com/apiserver/api/ver3/auth/resetpassword';
      
      devLog('[Step 6] Requesting password reset:', url);
      
      const headers = {
        'Accept-Language': getAcceptLanguageHeader(),
        'Content-Type': 'application/json',
      };
      
      // Build payload - appId is optional
      // If appId is provided, convert to number; otherwise omit it
      const payload = { email };
      if (appId !== null && appId !== undefined) {
        const numericAppId = typeof appId === 'string' ? parseInt(appId, 10) : appId;
        if (!isNaN(numericAppId)) {
          payload.appId = numericAppId;
        }
      }
      
      devLog('[Step 6] Password reset payload:', payload);

      let data;
      try {
        data = await requestJson({
          url,
          method: 'POST',
          headers,
          body: payload,
          expectJson: false,
        });
      } catch (error) {
        const payloadText = typeof error.payload === 'string' ? error.payload : JSON.stringify(error.payload);
        console.error(`[Step 6] Password reset error (${error.status || 'unknown'}):`, payloadText || error);
        throw new Error(`Password reset failed: ${error.status || 'unknown'} - ${payloadText || error.message}`);
      }

      devLog('[Step 6] Password reset response:', data);
      return { success: true, data };
    } catch (error) {
      console.error('[Step 6] Password reset error:', error);
      throw error;
    }
  }

  // Step 6: Create customer - For new users
  async createCustomer(customerData) {
    try {
      // Always include the active business unit
      if (!customerData.businessUnit && state.selectedBusinessUnit) {
        customerData.businessUnit = state.selectedBusinessUnit;
      }
      
      // The API expects the customer data nested under a "customer" key
      // Based on error field paths like "customer.email", "customer.firstName"
      const requestPayload = {
        customer: customerData
      };
      
      let url;
      if (this.useProxy) {
        url = `${this.baseUrl}?path=/api/customers`;
      } else {
        url = `${this.baseUrl}/api/customers`;
      }
      
      console.log('[Step 6] Creating customer:', url);
      console.log('[Step 6] Customer data being sent:', JSON.stringify(requestPayload, null, 2));
      
      const headers = {
        'Accept-Language': getAcceptLanguageHeader(),
        'Content-Type': 'application/json',
      };
      
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(requestPayload),
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`[Step 6] Create customer error (${response.status}):`, errorText);
        
        // Check for duplicate email error
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch (e) {
          // If not JSON, use the text as-is
          errorData = { message: errorText };
        }
        
        // Log the full error for debugging
        console.error('[Step 6] Full error data:', JSON.stringify(errorData, null, 2));
        console.error('[Step 6] Error code:', errorData?.errorCode);
        console.error('[Step 6] Field errors:', errorData?.fieldErrors);
        
        // Check if this is a duplicate email error
        const errorMessage = errorData?.message || errorText || '';
        const errorLower = errorMessage.toLowerCase();
        const errorCode = errorData?.errorCode || '';
        const errorCodeLower = errorCode.toLowerCase();

        // Handle validation errors for email (e.g., incomplete/invalid email)
        const validationDetails = errorData?.error?.details;
        const emailValidationDetail = Array.isArray(validationDetails)
          ? validationDetails.find(detail => {
              const field = (detail?.field || '').toLowerCase();
              return field.includes('email') || field === 'customer.email';
            })
          : null;
        if (emailValidationDetail) {
          const validationError = new Error('Please enter a valid email address.');
          validationError.status = response.status;
          validationError.isInvalidEmail = true;
          validationError.validationDetail = emailValidationDetail;
          throw validationError;
        }
        
        // Check for EMAIL_ALREADY_EXISTS error code (from API docs)
        const hasEmailExistsErrorCode = 
          errorCodeLower === 'email_already_exists' ||
          errorCodeLower === 'email_already_exists_name' ||
          errorCode === 'EMAIL_ALREADY_EXISTS' ||
          errorCode === 'EMAIL_ALREADY_EXISTS_NAME';
        
        // Check fieldErrors array for email-related errors
        const hasEmailFieldError = errorData?.fieldErrors && Array.isArray(errorData.fieldErrors) &&
          errorData.fieldErrors.some(err => {
            const field = err?.field || '';
            const fieldLower = field.toLowerCase();
            const errCode = err?.errorCode || '';
            const errCodeLower = errCode.toLowerCase();
            return (
              fieldLower.includes('email') ||
              fieldLower === 'customer.email' ||
              errCodeLower === 'email_already_exists' ||
              errCode === 'EMAIL_ALREADY_EXISTS'
            );
          });
        
        // Check for email-related error messages
        const hasEmailErrorMessage = 
          errorLower.includes('email') && (
            errorLower.includes('already exists') ||
            errorLower.includes('already registered') ||
            errorLower.includes('duplicate') ||
            errorLower.includes('taken') ||
            errorLower.includes('in use') ||
            errorLower.includes('is already') ||
            errorLower.includes('already in use')
          );
        
        // Check errors array (alternative format)
        const hasEmailInErrorsArray = errorData?.errors && Array.isArray(errorData.errors) && 
          errorData.errors.some(err => {
            const errMsg = (err?.message || err?.msg || '').toLowerCase();
            const errField = (err?.field || err?.path || '').toLowerCase();
            return (
              errField.includes('email') ||
              errField === 'customer.email' ||
              errMsg.includes('email') && (
                errMsg.includes('already exists') ||
                errMsg.includes('duplicate') ||
                errMsg.includes('taken')
              )
            );
          });
        
        const isDuplicateEmail = 
          hasEmailExistsErrorCode ||
          hasEmailFieldError ||
          hasEmailErrorMessage ||
          hasEmailInErrorsArray ||
          (response.status === 409 && errorLower.includes('email')) || // Conflict with email mention
          (response.status === 400 && hasEmailErrorMessage); // Bad Request with email error
        
        console.log('[Step 6] Duplicate email check:', {
          isDuplicateEmail,
          hasEmailExistsErrorCode,
          hasEmailFieldError,
          hasEmailErrorMessage,
          hasEmailInErrorsArray,
          status: response.status,
          errorCode,
          errorMessage: errorMessage.substring(0, 200)
        });
        
        if (isDuplicateEmail) {
          const duplicateError = new Error(`An account with this email address already exists. Please log in instead.`);
          duplicateError.status = response.status;
          duplicateError.isDuplicateEmail = true;
          duplicateError.originalError = errorData;
          throw duplicateError;
        }
        
        throw new Error(`Create customer failed: ${response.status} - ${errorText}`);
      }
      
      const data = await response.json();
      console.log('[Step 6] Create customer response:', data);
      return data;
    } catch (error) {
      console.error('[Step 6] Create customer error:', error);
      throw error;
    }
  }

  // Step 6: Get customer profile
  async getCustomer(customerId) {
    try {
      let url = buildApiUrl({
        baseUrl: this.baseUrl,
        useProxy: this.useProxy,
        path: `/api/customers/${customerId}`,
      });
      
      devLog('[Step 6] Fetching customer profile:', url);
      
      const accessToken = typeof window.getAccessToken === 'function' 
        ? window.getAccessToken() 
        : null;
      
      if (!accessToken) {
        throw new Error('No access token available');
      }
      
      const headers = {
        'Accept-Language': getAcceptLanguageHeader(),
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      };

      try {
        const data = await requestJson({ url, headers });
        devLog('[Step 6] Get customer response:', data);

        const hasProfileDetails = Boolean(
          data?.firstName ||
          data?.lastName ||
          data?.shippingAddress ||
          data?.address ||
          data?.mobilePhone ||
          data?.phone ||
          data?.phoneNumber
        );

        if (!hasProfileDetails) {
          const fallbackUrl = this.useProxy
            ? buildApiUrl({
                baseUrl: this.baseUrl,
                useProxy: this.useProxy,
                path: `/api/ver3/customers/${customerId}`,
              })
            : `https://boulders.brpsystems.com/apiserver/api/ver3/customers/${customerId}`;
          devWarn('[Step 6] Customer profile missing details. Retrying with ver3:', fallbackUrl);
          const fallbackData = await requestJson({ url: fallbackUrl, headers });
          devLog('[Step 6] Get customer response (ver3):', fallbackData);
          return fallbackData;
        }

        return data;
      } catch (error) {
        if (error.status === 404 && this.useProxy) {
          // Fallback to ver3 endpoint if legacy customer profile requires it
          const fallbackUrl = buildApiUrl({
            baseUrl: this.baseUrl,
            useProxy: this.useProxy,
            path: `/api/ver3/customers/${customerId}`,
          });
          devWarn('[Step 6] Customer profile not found on /api/customers. Retrying:', fallbackUrl);
          const data = await requestJson({ url: fallbackUrl, headers });
          devLog('[Step 6] Get customer response (ver3):', data);
          return data;
        }
        throw error;
      }
    } catch (error) {
      console.error('[Step 6] Get customer error:', error);
      throw error;
    }
  }

  // Step 6: Update customer
  async updateCustomer(customerId, customerData) {
    try {
      // Always include the active business unit
      if (!customerData.businessUnit && state.selectedBusinessUnit) {
        customerData.businessUnit = state.selectedBusinessUnit;
      }
      
      let url;
      if (this.useProxy) {
        url = `${this.baseUrl}?path=/api/customers/${customerId}`;
      } else {
        url = `${this.baseUrl}/api/customers/${customerId}`;
      }
      
      console.log('[Step 6] Updating customer:', url);
      
      const accessToken = typeof window.getAccessToken === 'function' 
        ? window.getAccessToken() 
        : null;
      
      const headers = {
        'Accept-Language': getAcceptLanguageHeader(),
        'Content-Type': 'application/json',
        ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {}),
      };
      
      const response = await fetch(url, {
        method: 'PUT',
        headers,
        body: JSON.stringify(customerData),
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`[Step 6] Update customer error (${response.status}):`, errorText);
        throw new Error(`Update customer failed: ${response.status} - ${errorText}`);
      }
      
      const data = await response.json();
      console.log('[Step 6] Update customer response:', data);
      return data;
    } catch (error) {
      console.error('[Step 6] Update customer error:', error);
      throw error;
    }
  }

  // Step 6: Link guardian/child relationship
  async linkOtherUser(customerId, otherUserId, role = 'PAYER') {
    try {
      let url;
      if (this.useProxy) {
        url = `${this.baseUrl}?path=/api/customers/${customerId}/otheruser`;
      } else {
        url = `${this.baseUrl}/api/customers/${customerId}/otheruser`;
      }
      
      console.log('[Step 6] Linking other user:', url);
      
      const accessToken = typeof window.getAccessToken === 'function' 
        ? window.getAccessToken() 
        : null;
      
      const headers = {
        'Accept-Language': getAcceptLanguageHeader(),
        'Content-Type': 'application/json',
        ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {}),
      };
      
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify({ otherUserId, role }),
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`[Step 6] Link other user error (${response.status}):`, errorText);
        throw new Error(`Link other user failed: ${response.status} - ${errorText}`);
      }
      
      const data = await response.json();
      console.log('[Step 6] Link other user response:', data);
      return data;
    } catch (error) {
      console.error('[Step 6] Link other user error:', error);
      throw error;
    }
  }
}

// Step 6: Token Management Functions
// Expose token utilities globally for AuthAPI to use
// These implement saveTokens, getAccessToken, clearTokens as per guide requirements
(function() {
  const TOKEN_STORAGE_KEY = 'boulders_auth_tokens';
  const LOGIN_SESSION_COOKIE = 'boulders_login_session';
  let tokenStore = null; // Memory-first storage

  const getCookieData = () => readLoginSessionCookie(LOGIN_SESSION_COOKIE);
  const persistCookie = (tokenData) => writeLoginSessionCookie(LOGIN_SESSION_COOKIE, tokenData);
  const clearCookie = () => clearLoginSessionCookie(LOGIN_SESSION_COOKIE);
  const loadCookieTokens = () => {
    const cookieData = getCookieData();
    if (cookieData) {
      tokenStore = cookieData;
    }
    return cookieData;
  };

  // Load tokens from sessionStorage on init
  try {
    const stored = sessionStorage.getItem(TOKEN_STORAGE_KEY);
    if (stored) {
      tokenStore = JSON.parse(stored);
    }
  } catch (error) {
    console.warn('[Step 6] Could not load tokens from sessionStorage:', error);
  }

  if (!tokenStore) {
    const cookieData = hydrateFromCookie(LOGIN_SESSION_COOKIE);
    if (cookieData) {
      tokenStore = cookieData;
    }
  }

  // Step 6: saveTokens - Persist tokens in session store and cookie
  window.saveTokens = function(accessToken, refreshToken, expiresAt, metadata = {}) {
    const tokenData = { accessToken, refreshToken, expiresAt, metadata };
    tokenStore = tokenData;
    
    try {
      sessionStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(tokenData));
    } catch (error) {
      console.warn('[Step 6] Could not save tokens to sessionStorage:', error);
    }

    persistCookie(tokenData);
    
    // Update header auth indicator when tokens are saved
    // Use setTimeout to ensure DOM is ready (in case this is called during page load)
    setTimeout(() => {
      refreshHeaderAuthIndicator();
    }, 0);
    
    // Dispatch event to notify React components
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('auth-state-changed'));
    }
  };

  // Step 6: getAccessToken - Get access token from session store/cookie
  window.getAccessToken = function() {
    if (tokenStore?.accessToken) {
      return tokenStore.accessToken;
    }
    
    try {
      const stored = sessionStorage.getItem(TOKEN_STORAGE_KEY);
      if (stored) {
        tokenStore = JSON.parse(stored);
        if (tokenStore?.accessToken) {
          return tokenStore.accessToken;
        }
      }
    } catch (error) {
      console.warn('[Step 6] Could not read tokens from sessionStorage:', error);
    }

    const cookieTokens = loadCookieTokens();
    return cookieTokens?.accessToken || null;
  };

  // getRefreshToken - Get refresh token from session store/cookie
  window.getRefreshToken = function() {
    if (tokenStore?.refreshToken) {
      return tokenStore.refreshToken;
    }
    
    try {
      const stored = sessionStorage.getItem(TOKEN_STORAGE_KEY);
      if (stored) {
        tokenStore = JSON.parse(stored);
        if (tokenStore?.refreshToken) {
          return tokenStore.refreshToken;
        }
      }
    } catch (error) {
      console.warn('[Step 6] Could not read tokens from sessionStorage:', error);
    }
    
    const cookieTokens = loadCookieTokens();
    return cookieTokens?.refreshToken || null;
  };

  window.getTokenMetadata = function() {
    if (tokenStore?.metadata) {
      return tokenStore.metadata;
    }

    try {
      const stored = sessionStorage.getItem(TOKEN_STORAGE_KEY);
      if (stored) {
        tokenStore = JSON.parse(stored);
        if (tokenStore?.metadata) {
          return tokenStore.metadata;
        }
      }
    } catch (error) {
      console.warn('[Step 6] Could not read token metadata from sessionStorage:', error);
    }

    const cookieTokens = loadCookieTokens();
    return cookieTokens?.metadata || null;
  };

  // Step 6: clearTokens - Clear session and return to auth step
  window.clearTokens = function() {
    tokenStore = null;
    
    try {
      sessionStorage.removeItem(TOKEN_STORAGE_KEY);
      sessionStorage.removeItem('boulders_checkout_customer');
      sessionStorage.removeItem('boulders_checkout_order');
    } catch (error) {
      console.warn('[Step 6] Could not clear tokens from sessionStorage:', error);
    }

    clearCookie();
    
    // Update header auth indicator when tokens are cleared
    refreshHeaderAuthIndicator();
    
    // Dispatch event to notify React components
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('auth-state-changed'));
    }
  };

  // Check if token is expired
  window.isTokenExpired = function() {
    const activeStore = tokenStore ?? getCookieData();
    if (!activeStore?.expiresAt) {
      return false;
    }
    const buffer = 5 * 60 * 1000; // 5 minute buffer
    return Date.now() >= (activeStore.expiresAt - buffer);
  };
})();

// Step 7: Order and Items API
// Handles order creation, adding items (subscriptions, value cards, articles), and order management
const orderGetInFlight = new Map();
const orderGetLastFetchedAt = new Map();
const orderGetLastResponse = new Map();
let orderGetCooldownUntil = 0;

class OrderAPI {
  constructor(baseUrl = null) {
    const config = getApiConfig({ baseUrlOverride: baseUrl });
    this.baseUrl = config.baseUrl;
    this.useProxy = config.useProxy;
    this.isDevelopment = config.isDevelopment;
  }

  // Apply coupon via api-join order endpoint (used by payment link generation)
  // Some backends do not propagate API3 coupon changes into /api/orders immediately.
  async applyDiscountCodeApiOrders(orderId, discountCode) {
    const url = buildApiUrl({
      baseUrl: this.baseUrl,
      useProxy: this.useProxy,
      path: `/api/orders/${orderId}/coupon`,
    });

    const accessToken = typeof window.getAccessToken === 'function'
      ? window.getAccessToken()
      : null;

    const headers = {
      'Accept-Language': getAcceptLanguageHeader(),
      'Content-Type': 'application/json',
      ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {}),
    };

    const payload = { couponName: discountCode };
    console.log('[Discount] Applying coupon via /api/orders coupon endpoint:', { orderId, discountCode, url });

    try {
      return await requestJson({ url, method: 'POST', headers, body: payload });
    } catch (error) {
      const payloadText = typeof error.payload === 'string'
        ? error.payload
        : JSON.stringify(error.payload);
      console.error(`[Discount] /api/orders coupon apply error (${error.status || 'unknown'}):`, payloadText || error);
      const wrapped = new Error(`Apply coupon (api/orders) failed: ${error.status || 'unknown'} - ${payloadText || error.message}`);
      wrapped.status = error.status;
      wrapped.payload = error.payload;
      throw wrapped;
    }
  }

  // Step 7: Create order - POST /api/orders
  async createOrder(orderData) {
    try {
      // Always include the active business unit
      if (!orderData.businessUnit && state.selectedBusinessUnit) {
        orderData.businessUnit = state.selectedBusinessUnit;
      }

      const url = buildApiUrl({
        baseUrl: this.baseUrl,
        useProxy: this.useProxy,
        path: '/api/orders',
      });
      
      console.log('[Step 7] Creating order:', url);
      
      const accessToken = typeof window.getAccessToken === 'function' 
        ? window.getAccessToken() 
        : null;
      
      const headers = {
        'Accept-Language': getAcceptLanguageHeader(),
        'Content-Type': 'application/json',
        ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {}),
      };
      
      let data;
      try {
        data = await requestJson({ url, method: 'POST', headers, body: orderData });
      } catch (error) {
        const payloadText = typeof error.payload === 'string'
          ? error.payload
          : JSON.stringify(error.payload);
        console.error(`[Step 7] Create order error (${error.status || 'unknown'}):`, payloadText || error);
        throw new Error(`Create order failed: ${error.status || 'unknown'} - ${payloadText || error.message}`);
      }
      console.log('[Step 7] Create order response:', data);

      const createdOrderId = data?.id ?? data?.orderId ?? data?.data?.id ?? data?.data?.orderId;
      if (createdOrderId) {
        console.log('[Step 7] Created order ID:', createdOrderId);
      } else {
        console.warn('[Step 7] Create order response did not include an order ID field');
      }
      return data;
    } catch (error) {
      console.error('[Step 7] Create order error:', error);
      throw error;
    }
  }

  // Step 7: Add subscription item (membership) - POST /api/ver3/orders/{orderId}/items/subscriptions
  // API Documentation: https://boulders.brpsystems.com/brponline/external/documentation/api3
  // @param {string} [startDateOverride] - Optional start date YYYY-MM-DD. For 15-day pass, use state.subscriptionStartDate or today.
  async addSubscriptionItem(orderId, productId, startDateOverride) {
    try {
      const url = this.useProxy
        ? buildApiUrl({
            baseUrl: this.baseUrl,
            useProxy: this.useProxy,
            path: `/api/ver3/orders/${orderId}/items/subscriptions`,
          })
        : `https://boulders.brpsystems.com/apiserver/api/ver3/orders/${orderId}/items/subscriptions`;
      
      console.log('[Step 7] Adding subscription item:', url);
      
      const accessToken = typeof window.getAccessToken === 'function' 
        ? window.getAccessToken() 
        : null;
      
      const headers = {
        'Accept-Language': getAcceptLanguageHeader(),
        'Content-Type': 'application/json',
        ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {}),
      };
      
      // API expects subscriptionProduct field, not productId
      // Extract numeric ID if productId is in format "membership-134" -> 134
      let subscriptionProductId = productId;
      if (typeof productId === 'string' && productId.includes('-')) {
        // Extract numeric part from "membership-134" -> 134
        const numericPart = productId.split('-').pop();
        subscriptionProductId = parseInt(numericPart, 10);
        if (isNaN(subscriptionProductId)) {
          throw new Error(`Invalid product ID format: ${productId}. Expected format: "membership-{number}" or numeric ID.`);
        }
      } else if (typeof productId === 'string') {
        // Try to parse if it's a string number
        subscriptionProductId = parseInt(productId, 10);
        if (isNaN(subscriptionProductId)) {
          subscriptionProductId = productId; // Fallback to original if not numeric
        }
      }
      
      if (!subscriptionProductId) {
        throw new Error(`Missing or invalid subscription product ID: ${productId}`);
      }
      
      const subscriberId = state.customerId ? Number(state.customerId) : null;
      const birthDate = getSubscriberBirthDate();
      // Set start date: use override (e.g. 15-day pass future date) or today
      // Format: YYYY-MM-DD (ISO date format)
      const hasExplicitStartDate = !!(startDateOverride && /^\d{4}-\d{2}-\d{2}$/.test(startDateOverride));
      const startDate = hasExplicitStartDate
        ? startDateOverride
        : getTodayLocalDateString();
      const includeStartDate = true;
      
      // Pricing calculation: When startDate is on day 16 or later, price includes:
      // - Rest of current month (prorated) + Full next month
      // When startDate is before day 16, price includes:
      // - Rest of current month only (prorated)
      // Frontend calculation now matches backend logic to ensure price consistency.
      
      // Referral attribution (Phase 1): if a friend landed via ?ref=<id> and
      // signs up here, BRP records the recruiter on the subscription itself
      // (see SubscriptionOut.recruitedBy in docs/brp-api3-openapi.yaml L14948).
      // getRecruitedByForPayload() applies a self-referral guard, so we
      // never send the subscriber's own ID — BRP would 400 with
      // RECRUITED_BY_CANNOT_BE_SAME_AS_THE_SUBSCRIPTION_USER otherwise.
      const recruitedBy = getRecruitedByForPayload();

      // Build payload according to OpenAPI spec:
      // Required: subscriptionProduct, birthDate
      // Optional: startDate, subscriber, externalMessage, additionTo, recruitedBy, paymentOption
      // Note: businessUnit is not in spec - may be inferred from order context
      const payload = {
        subscriptionProduct: subscriptionProductId,
        ...(includeStartDate ? { startDate } : {}),
        ...(subscriberId ? { subscriber: subscriberId } : {}),
        ...(birthDate ? { birthDate } : {}),
        ...(recruitedBy ? { recruitedBy } : {}),
        // businessUnit is not in OpenAPI spec - backend may infer from order
        // ...(state.selectedBusinessUnit ? { businessUnit: state.selectedBusinessUnit } : {}),
      };
      
      // Log pricing calculation details for productId 134 for verification
      if (subscriptionProductId === 134) {
        const dayOfMonth = new Date().getDate();
        if (dayOfMonth >= 16) {
          console.log('[Step 7] ℹ️ Adding subscription for productId 134 ("Medlemskab")');
          console.log('[Step 7] ℹ️ Day >= 16: Price will include rest of current month + full next month');
        } else {
          console.log('[Step 7] ℹ️ Adding subscription for productId 134 ("Medlemskab")');
          console.log('[Step 7] ℹ️ Day < 16: Price will include prorated rest of current month only');
        }
      }
      
      console.log('[Step 7] Adding subscription item - productId:', productId);
      console.log('[Step 7] Extracted subscriptionProductId:', subscriptionProductId);
      console.log('[Step 7] Subscription item payload:', JSON.stringify(payload, null, 2));
      
      // Log payload details for debugging and verification
      console.log('[Step 7] 🔍 Payload analysis:', {
        subscriptionProductId,
        productId,
        startDate,
        includeStartDate,
        hasSubscriber: !!subscriberId,
        subscriberId,
        hasBirthDate: !!birthDate,
        birthDate,
        businessUnit: state.selectedBusinessUnit,
        payloadKeys: Object.keys(payload),
        payloadStartDate: payload.startDate,
        payloadStart: payload.start
      });
      
      
      let data;
      try {
        data = await requestJson({ url, method: 'POST', headers, body: payload });
        // Phase 1: this is the moment the attribution actually lands at BRP,
        // so it's the only correct place to clear the local referral entry.
        // Doing it in the success-page render was too eager — it wiped the
        // ref before signup, including in `?testSuccess=true` test mode.
        if (payload.recruitedBy) {
          clearReferral('signup-attributed');
          try {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
              event: 'referral_attributed',
              referrer_id: payload.recruitedBy,
              subscription_product_id: subscriptionProductId,
            });
          } catch (_) { /* GTM optional */ }
          // Independent ledger mirror so manual reward processing doesn't
          // depend on BRP exposing `recruitedBy` in the admin UI.
          const subscriptionItem = data?.subscriptionItems?.[0] || null;
          const customer = state.authenticatedCustomer || {};
          const memberName = [customer.firstName, customer.lastName].filter(Boolean).join(' ').trim();
          logReferralAttributionToSheet({
            recruiterId: payload.recruitedBy,
            newCustomerId: state.customerId || customer.id || null,
            newCustomerEmail: state.authenticatedEmail || customer.email || null,
            newCustomerName: memberName || null,
            subscriptionId: data?.id ?? null,
            subscriptionItemId: subscriptionItem?.id ?? null,
            orderId: data?.id ?? state.orderId ?? null,
            subscriptionProductId,
            subscriptionProductName: subscriptionItem?.subscriptionProduct?.name || null,
            sourceUrl: typeof window !== 'undefined' ? window.location.href : null,
          });
          devLog('[Referral] ✅ Attributed signup to recruiter:', payload.recruitedBy);
        }
      } catch (error) {
        const errorPayload = error?.payload;
        let errorData = errorPayload;
        if (typeof errorPayload === 'string') {
          try {
            errorData = JSON.parse(errorPayload);
          } catch (e) {
            errorData = null;
          }
        }
        const errorCode = errorData?.errorCode || errorData?.code;

        // Defense-in-depth: if BRP rejects the recruiter ID (e.g. self-referral
        // slipped past our client-side guard, or the ref points to a deleted
        // customer), drop the referral and retry once without recruitedBy so
        // the signup itself isn't blocked by a tracking concern.
        if (errorCode === 'RECRUITED_BY_CANNOT_BE_SAME_AS_THE_SUBSCRIPTION_USER' && payload.recruitedBy) {
          console.warn('[Step 7] ⚠️ BRP rejected recruitedBy:', payload.recruitedBy, '— retrying without referral attribution');
          clearReferral('brp-rejected-recruitedBy');
          try {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
              event: 'referral_rejected',
              error_code: errorCode,
              ref: payload.recruitedBy,
            });
          } catch (_) { /* GTM optional */ }
          delete payload.recruitedBy;
          data = await requestJson({ url, method: 'POST', headers, body: payload });
          console.log('[Step 7] ✅ Subscription created on retry without recruitedBy');
          return data;
        }

        if (errorCode === 'PRODUCT_NOT_ALLOWED') {
          const blockedProductId = errorData?.id || subscriptionProductId;
          console.warn(`[Step 7] ⚠️ Product ${blockedProductId} is not allowed for this customer (campaign eligibility restriction)`);
          
          // Create a custom error that can be identified in catch blocks
          const restrictionError = new Error('PRODUCT_NOT_ALLOWED: This offer is not available for your account due to campaign restrictions.');
          restrictionError.isProductNotAllowed = true;
          restrictionError.productId = blockedProductId;
          restrictionError.originalError = errorPayload;
          throw restrictionError;
        }

        const payloadText = typeof errorPayload === 'string'
          ? errorPayload
          : JSON.stringify(errorPayload);
        console.error(`[Step 7] Add subscription item error (${error.status || 'unknown'}):`, payloadText || error);
        throw new Error(`Add subscription item failed: ${error.status || 'unknown'} - ${payloadText || error.message}`);
      }
      console.log('[Step 7] Add subscription item response:', data);

      // CRITICAL: Check if backend accepted startDate
      const subscriptionItem = data?.subscriptionItems?.[0];
      const responseStartDate = subscriptionItem?.initialPaymentPeriod?.start;
      const responseOrderPrice = data?.price?.amount;
      
      // Check if backend start date is more than 1 day in the future (backend ignored our startDate)
      const backendStartDateObj = responseStartDate ? new Date(responseStartDate) : null;
      const checkToday = new Date();
      checkToday.setHours(0, 0, 0, 0);
      const daysUntilStart = backendStartDateObj ? Math.ceil((backendStartDateObj - checkToday) / (1000 * 60 * 60 * 24)) : 0;
      const startDateIgnored = daysUntilStart > 1;
      
      console.log('[Step 7] 🔍 Response analysis:', {
        productId,
        subscriptionProductId,
        sentStartDate: startDate,
        responseStartDate,
        daysUntilStart,
        startDateIgnored,
        orderPrice: responseOrderPrice,
        orderPriceDKK: responseOrderPrice ? responseOrderPrice / 100 : null,
        hasInitialPaymentPeriod: !!subscriptionItem?.initialPaymentPeriod,
        initialPaymentPeriod: subscriptionItem?.initialPaymentPeriod
      });
      
      // CRITICAL: If backend ignored startDate, attempt to fix with multiple strategies
      // Only attempt when price mismatch is significant (avoid churn on rounding diffs)
      if (startDateIgnored && subscriptionItem?.id) {
        const expectedPrice = this._calculateExpectedPartialMonthPrice(subscriptionProductId, startDate);
        const orderPriceInCents = typeof responseOrderPrice === 'object'
          ? responseOrderPrice.amount
          : responseOrderPrice;
        const priceDifference = expectedPrice && Number.isFinite(orderPriceInCents)
          ? Math.abs(orderPriceInCents - expectedPrice.amountInCents)
          : null;

        if (priceDifference !== null && priceDifference <= 100) {
          console.warn('[Step 7] ⚠️ Backend ignored startDate but price difference is within rounding tolerance:', priceDifference, 'cents');
          return data;
        }

        console.warn('[Step 7] ⚠️ Backend ignored startDate - start date is', daysUntilStart, 'days in future');
        console.warn('[Step 7] ⚠️ Product ID:', subscriptionProductId);
        console.warn('[Step 7] ⚠️ Attempting to fix with multiple strategies...');

        // Try multiple strategies to fix backend pricing
        const fixedData = await this._fixBackendPricingBug(
          orderId,
          url,
          headers,
          subscriptionItem.id,
          payload,
          subscriptionProductId,
          expectedPrice,
          accessToken,
          checkToday
        );
        
        if (fixedData) {
          // CRITICAL: Update subscription item ID after pricing fix
          // The pricing fix deletes and re-adds the subscription item, creating a NEW ID
          // We must update state.subscriptionItemId with the new ID, otherwise addons will fail with ID_NOT_FOUND
          const newSubscriptionItem = fixedData?.subscriptionItems?.[0];
          if (newSubscriptionItem?.id) {
            const oldSubscriptionItemId = state.subscriptionItemId;
            state.subscriptionItemId = newSubscriptionItem.id;
            console.log('[Step 7] ✅ Updated subscription item ID after pricing fix:', {
              oldId: oldSubscriptionItemId,
              newId: state.subscriptionItemId
            });
          }
          return fixedData;
        }
        
        // If all strategies failed, log detailed error
        console.error('[Step 7] ❌ All strategies failed to fix pricing mismatch for productId:', subscriptionProductId);
        console.error('[Step 7] ❌ Order may have pricing discrepancy - verification will continue');
      }
      
      return data;
    } catch (error) {
      console.error('[Step 7] Add subscription item error:', error);
      throw error;
    }
  }

  /**
   * Delete subscription item from order. Used when user changes 15-day pass activation date.
   * @param {string|number} orderId - Order ID
   * @param {number} subscriptionItemId - Subscription item ID to delete
   * @returns {Promise<boolean>} true if deleted, false if failed
   */
  async deleteSubscriptionItem(orderId, subscriptionItemId) {
    try {
      const deleteUrl = this.useProxy
        ? buildApiUrl({
            baseUrl: this.baseUrl,
            useProxy: this.useProxy,
            path: `/api/ver3/orders/${orderId}/items/subscriptions/${subscriptionItemId}`,
          })
        : `https://boulders.brpsystems.com/apiserver/api/ver3/orders/${orderId}/items/subscriptions/${subscriptionItemId}`;

      const accessToken = typeof window.getAccessToken === 'function' ? window.getAccessToken() : null;
      await requestJson({
        url: deleteUrl,
        method: 'DELETE',
        headers: {
          'Accept-Language': getAcceptLanguageHeader(),
          ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {}),
        },
        expectJson: false,
      });
      return true;
    } catch (error) {
      if (error.status === 403) {
        console.warn('[OrderAPI] Cannot delete subscription item (403) - order may be locked');
      } else {
        console.warn('[OrderAPI] Failed to delete subscription item:', error);
      }
      return false;
    }
  }

  /**
   * Calculates the expected partial-month price for a subscription starting today.
   * Used to verify backend pricing is correct.
   * 
   * @param {number} productId - The subscription product ID
   * @param {string} startDate - Start date in YYYY-MM-DD format
   * @returns {Object|null} Expected price info { amountInCents, amountInDKK, daysRemaining, daysInMonth } or null
   */
  _calculateExpectedPartialMonthPrice(productId, startDate) {
    // Check all subscription types: campaign, membership, and 15-Day Trial Pass
    const allSubscriptions = [
      ...(state.campaignSubscriptions || []),
      ...(state.subscriptions || []),
      ...(state.dayPassSubscriptions || [])
    ];
    const membership = allSubscriptions.find(p => 
      p.id === productId || 
      p.id === Number(productId) ||
      String(p.id) === String(productId)
    );
    
    if (!membership?.priceWithInterval?.price?.amount) {
      console.warn('[Step 7] Cannot calculate expected price - product not found:', productId);
      return null;
    }
    
    const monthlyPriceInCents = membership.priceWithInterval.price.amount;
    const startDateObj = new Date(startDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Calculate days remaining in current month
    const currentMonth = today.getMonth();
    const currentYear = today.getFullYear();
    const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0);
    const daysInCurrentMonth = lastDayOfMonth.getDate();
    const dayOfMonth = today.getDate();
    const daysRemainingInMonth = daysInCurrentMonth - dayOfMonth + 1;
    
    // CRITICAL: If today is 16th or later, price should be: rest of current month + full next month
    // Otherwise: just rest of current month (prorated)
    let proratedPriceInCents;
    let includesNextMonth = false;
    
    if (dayOfMonth >= 16) {
      // Calculate: (rest of current month) + (full next month)
      const partialCurrentMonthPrice = Math.round((monthlyPriceInCents * daysRemainingInMonth) / daysInCurrentMonth);
      const fullNextMonthPrice = monthlyPriceInCents;
      proratedPriceInCents = partialCurrentMonthPrice + fullNextMonthPrice;
      includesNextMonth = true;
      console.log(`[Price Calculation] Day ${dayOfMonth} >= 16: Calculating rest of month (${daysRemainingInMonth} days) + full next month`);
    } else {
      // Calculate: just rest of current month (prorated)
      proratedPriceInCents = Math.round((monthlyPriceInCents * daysRemainingInMonth) / daysInCurrentMonth);
      console.log(`[Price Calculation] Day ${dayOfMonth} < 16: Calculating prorated price for remaining ${daysRemainingInMonth} days`);
    }
    
    return {
      amountInCents: proratedPriceInCents,
      amountInDKK: proratedPriceInCents / 100,
      daysRemaining: daysRemainingInMonth,
      daysInMonth: daysInCurrentMonth,
      monthlyPriceInCents,
      monthlyPriceInDKK: monthlyPriceInCents / 100,
      includesNextMonth // Flag to indicate if next month is included
    };
  }

  /**
   * Attempts multiple strategies to fix backend pricing bug when startDate is ignored.
   * 
   * @param {string|number} orderId - Order ID
   * @param {string} url - API endpoint URL
   * @param {Object} headers - Request headers
   * @param {number} subscriptionItemId - Subscription item ID to delete
   * @param {Object} basePayload - Base payload object
   * @param {number} productId - Subscription product ID
   * @param {Object|null} expectedPrice - Expected price info
   * @param {string|null} accessToken - Access token
   * @param {Date} today - Today's date (normalized)
   * @returns {Promise<Object|null>} Fixed order data or null if all strategies failed
   */
  async _fixBackendPricingBug(orderId, url, headers, subscriptionItemId, basePayload, productId, expectedPrice, accessToken, today) {
    // Strategy 1: Delete and re-add with same payload (sometimes backend needs fresh start)
    console.log('[Step 7] Strategy 1: Delete and re-add with same payload');
    try {
      const strategy1Result = await this._tryStrategyDeleteAndReadd(
        orderId, url, headers, subscriptionItemId, basePayload, productId, expectedPrice, accessToken, today
      );
      if (strategy1Result) return strategy1Result;
    } catch (error) {
      if (error.isOrderLocked) {
        console.warn('[Step 7] ⚠️ Order is locked (403 Forbidden) - cannot modify subscription item');
        console.warn('[Step 7] ⚠️ Skipping remaining fix strategies - order cannot be modified');
        return null; // Order is locked, can't fix
      }
      // Other error - continue to next strategy
    }
    
    // Strategy 2: Try with explicit date format (using helper function for consistency)
    console.log('[Step 7] Strategy 2: Try with explicit date format');
    try {
      const todayExplicit = getTodayLocalDateString();
      if (todayExplicit !== basePayload.startDate) {
        const payloadVariant = { ...basePayload, startDate: todayExplicit };
        const strategy2Result = await this._tryStrategyDeleteAndReadd(
          orderId, url, headers, subscriptionItemId, payloadVariant, productId, expectedPrice, accessToken, today
        );
        if (strategy2Result) return strategy2Result;
      }
    } catch (error) {
      if (error.isOrderLocked) {
        console.warn('[Step 7] ⚠️ Order is locked - skipping remaining strategies');
        return null;
      }
    }
    
    // Strategy 3: Try minimal payload (only required fields)
    console.log('[Step 7] Strategy 3: Try minimal payload');
    try {
      const minimalPayload = {
        subscriptionProduct: basePayload.subscriptionProduct,
        startDate: basePayload.startDate,
        ...(basePayload.birthDate ? { birthDate: basePayload.birthDate } : {}),
      };
      const strategy3Result = await this._tryStrategyDeleteAndReadd(
        orderId, url, headers, subscriptionItemId, minimalPayload, productId, expectedPrice, accessToken, today
      );
      if (strategy3Result) return strategy3Result;
    } catch (error) {
      if (error.isOrderLocked) {
        console.warn('[Step 7] ⚠️ Order is locked - skipping remaining strategies');
        return null;
      }
    }
    
    // Strategy 4: Try with longer wait time (backend might need more time to process)
    console.log('[Step 7] Strategy 4: Try with longer wait time');
    try {
      const strategy4Result = await this._tryStrategyDeleteAndReadd(
        orderId, url, headers, subscriptionItemId, basePayload, productId, expectedPrice, accessToken, today, 2000
      );
      if (strategy4Result) return strategy4Result;
    } catch (error) {
      if (error.isOrderLocked) {
        console.warn('[Step 7] ⚠️ Order is locked - all strategies failed');
        return null;
      }
    }
    
    return null; // All strategies failed
  }

  /**
   * Tries a strategy: delete subscription item and re-add with given payload.
   * 
   * @param {string|number} orderId - Order ID
   * @param {string} url - API endpoint URL
   * @param {Object} headers - Request headers
   * @param {number} subscriptionItemId - Subscription item ID to delete
   * @param {Object} payload - Payload to use when re-adding
   * @param {number} productId - Subscription product ID
   * @param {Object|null} expectedPrice - Expected price info
   * @param {string|null} accessToken - Access token
   * @param {Date} today - Today's date (normalized)
   * @param {number} waitTime - Wait time in ms before re-adding (default 1000)
   * @returns {Promise<Object|null>} Fixed order data or null if strategy failed
   */
  async _tryStrategyDeleteAndReadd(orderId, url, headers, subscriptionItemId, payload, productId, expectedPrice, accessToken, today, waitTime = 1000) {
    try {
      // Delete subscription item
      const deleteUrl = this.useProxy
        ? buildApiUrl({
            baseUrl: this.baseUrl,
            useProxy: this.useProxy,
            path: `/api/ver3/orders/${orderId}/items/subscriptions/${subscriptionItemId}`,
          })
        : `https://boulders.brpsystems.com/apiserver/api/ver3/orders/${orderId}/items/subscriptions/${subscriptionItemId}`;

      try {
        await requestJson({
          url: deleteUrl,
          method: 'DELETE',
          headers: {
            ...headers,
            ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {}),
          },
          expectJson: false,
        });
      } catch (error) {
        if (error.status === 403) {
          console.log('[Step 7] Cannot delete subscription item (403 Forbidden) - order may be in a state that prevents modification');
          // Throw a specific error so caller knows order is locked
          const lockedError = new Error('Order is locked (403 Forbidden)');
          lockedError.isOrderLocked = true;
          lockedError.status = 403;
          throw lockedError;
        }
        const payloadText = typeof error.payload === 'string'
          ? error.payload
          : JSON.stringify(error.payload);
        console.warn('[Step 7] Failed to delete subscription item:', error.status || 'unknown', payloadText || error);
        return null; // Can't delete, skip this strategy
      }
      
      // Wait for backend to process deletion
      await new Promise(resolve => setTimeout(resolve, waitTime));
      
      // Re-add subscription
      let retryData;
      try {
        retryData = await requestJson({ url, method: 'POST', headers, body: payload });
      } catch (error) {
        return null; // Re-add failed, skip this strategy
      }
      
      // Verify pricing is correct
      const verification = this._verifySubscriptionPricing(retryData, productId, expectedPrice, today);
      
      if (verification.isCorrect) {
        console.log('[Step 7] ✅ Strategy succeeded - pricing is correct');
        return retryData;
      }
      
      return null; // Pricing still incorrect
    } catch (error) {
      console.error('[Step 7] Strategy error:', error);
      return null;
    }
  }

  /**
   * Verifies that subscription pricing is correct.
   * 
   * @param {Object} orderData - Order data from API response
   * @param {number} productId - Subscription product ID
   * @param {Object|null} expectedPrice - Expected price info
   * @param {Date} today - Today's date (normalized to midnight)
   * @returns {Object} Verification result { isCorrect, startDateCorrect, priceCorrect, details }
   */
  _verifySubscriptionPricing(orderData, productId, expectedPrice, today) {
    const subscriptionItem = orderData?.subscriptionItems?.[0];
    const initialPaymentPeriod = subscriptionItem?.initialPaymentPeriod;
    const orderPriceAmount = orderData?.price?.amount || 0;
    const orderPriceInCents = typeof orderPriceAmount === 'object' ? orderPriceAmount.amount : orderPriceAmount;
    
    // The order price from BRP already has any coupon discount deducted, while
    // expectedPrice is calculated coupon-free. Add the discount back before
    // comparing, otherwise applying a coupon makes verification fail and the
    // UI overrides the (correct) discounted backend price.
    const rawCouponDiscount = orderData?.couponDiscount;
    const couponDiscountInCents = typeof rawCouponDiscount === 'object'
      ? (rawCouponDiscount?.amount || 0)
      : (rawCouponDiscount || 0);
    const effectiveOrderPriceInCents = orderPriceInCents + couponDiscountInCents;
    
    // Check if start date is correct (should be today or tomorrow at most)
    let daysUntilStart = 0;
    if (initialPaymentPeriod?.start) {
      const backendStartDate = new Date(initialPaymentPeriod.start);
      daysUntilStart = Math.ceil((backendStartDate - today) / (1000 * 60 * 60 * 24));
    }
    
    const startDateCorrect = daysUntilStart <= 1;
    
    // Check if price is correct
    let priceCorrect = false;
    const priceDifference = expectedPrice
      ? Math.abs(effectiveOrderPriceInCents - expectedPrice.amountInCents)
      : null;
    if (expectedPrice) {
      // Allow up to 1 DKK difference to account for backend rounding
      priceCorrect = priceDifference <= 100;
    }
    
    const isCorrect = startDateCorrect && priceCorrect;
    
    return {
      isCorrect,
      startDateCorrect,
      priceCorrect,
      daysUntilStart,
      priceDifference,
      orderPriceInCents,
      orderPriceDKK: orderPriceInCents / 100,
      expectedPriceInCents: expectedPrice?.amountInCents || null,
      expectedPriceDKK: expectedPrice?.amountInDKK || null,
      details: {
        initialPaymentPeriod,
        recurringPrice: subscriptionItem?.payRecurring?.price?.amount || 0,
        productId
      }
    };
  }

  // Step 7: Add value card item (punch card) - POST /api/ver3/orders/{orderId}/items/valuecards
  // API Documentation: https://boulders.brpsystems.com/brponline/external/documentation/api3
  async addValueCardItem(orderId, productId, quantity = 1, additionTo = null, amountInCentsFromCaller = null) {
    try {
      const url = this.useProxy
        ? buildApiUrl({
            baseUrl: this.baseUrl,
            useProxy: this.useProxy,
            path: `/api/ver3/orders/${orderId}/items/valuecards`,
          })
        : `https://boulders.brpsystems.com/apiserver/api/ver3/orders/${orderId}/items/valuecards`;
      
      console.log('[Step 7] Adding value card item:', url);
      
      const accessToken = typeof window.getAccessToken === 'function' 
        ? window.getAccessToken() 
        : null;
      
      const headers = {
        'Accept-Language': getAcceptLanguageHeader(),
        'Content-Type': 'application/json',
        ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {}),
      };
      
      // Resolve amount (price in cents). Backend may require 'amount' (FIELD_MANDATORY).
      let amount = 0;
      if (amountInCentsFromCaller != null && typeof amountInCentsFromCaller === 'number' && !Number.isNaN(amountInCentsFromCaller)) {
        amount = Math.round(amountInCentsFromCaller);
      } else {
        const allValueCards = [
          ...(state.valueCards || []),
          ...(state.campaignValueCards || []),
        ];
        const valueCardProduct = allValueCards.find(p =>
          p.id === productId || p.id === Number(productId) || String(p.id) === String(productId)
        ) || (state.allRawProducts || []).find(p =>
          (p.id === productId || p.id === Number(productId) || String(p.id) === String(productId)) &&
          (p.productType === 'VALUE_CARD' || p.productType === 'VALUECARD' || p.valueCardType !== undefined)
        );
        const priceObj = valueCardProduct?.price;
        const amountInCents = priceObj != null
          ? (typeof priceObj === 'object' && 'amount' in priceObj ? priceObj.amount : Number(priceObj))
          : (valueCardProduct?.amount != null ? valueCardProduct.amount : 0);
        amount = typeof amountInCents === 'number' && !Number.isNaN(amountInCents) ? Math.round(amountInCents) : 0;
      }
      
      // Send amount in cents (integer). Backend accepts 0 for free value cards.
      const amountNum = typeof amount === 'number' && !Number.isNaN(amount) ? Math.round(amount) : 0;
      const payload = {
        valueCardProduct: productId,
        amount: amountNum,
        ...(additionTo != null ? { additionTo } : {}),
      };
      console.log('[Step 7] Value card payload:', JSON.stringify(payload, null, 2));
      
      let data;
      try {
        data = await requestJson({ url, method: 'POST', headers, body: payload });
      } catch (error) {
        const payloadText = typeof error.payload === 'string'
          ? error.payload
          : JSON.stringify(error.payload);
        console.error(`[Step 7] Add value card item error (${error.status || 'unknown'}):`, payloadText || error);
        throw new Error(`Add value card item failed: ${error.status || 'unknown'} - ${payloadText || error.message}`);
      }
      console.log('[Step 7] Add value card item response:', data);
      return data;
    } catch (error) {
      console.error('[Step 7] Add value card item error:', error);
      throw error;
    }
  }

  // Step 7: Add article item (add-ons/extras) - POST /api/orders/{orderId}/items/articles
  async addArticleItem(orderId, productId, additionTo = null) {
    try {
      const url = buildApiUrl({
        baseUrl: this.baseUrl,
        useProxy: this.useProxy,
        path: `/api/orders/${orderId}/items/articles`,
      });
      
      console.log('[Step 7] Adding article item:', url);
      
      const accessToken = typeof window.getAccessToken === 'function' 
        ? window.getAccessToken() 
        : null;
      
      const headers = {
        'Accept-Language': getAcceptLanguageHeader(),
        'Content-Type': 'application/json',
        ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {}),
      };
      
      // API expects 'articleProduct' not 'productId' per OpenAPI spec
      // additionTo links this addon to the parent subscription item
      const payload = {
        articleProduct: productId, // API field name is 'articleProduct'
        businessUnit: state.selectedBusinessUnit, // Always include active business unit
        ...(additionTo ? { additionTo } : {}), // Link to parent subscription item if provided
      };
      
      console.log('[Step 7] Add article item payload:', payload);
      
      let data;
      try {
        data = await requestJson({ url, method: 'POST', headers, body: payload });
      } catch (error) {
        const payloadText = typeof error.payload === 'string'
          ? error.payload
          : JSON.stringify(error.payload);
        console.error(`[Step 7] Add article item error (${error.status || 'unknown'}):`, payloadText || error);
        throw new Error(`Add article item failed: ${error.status || 'unknown'} - ${payloadText || error.message}`);
      }
      console.log('[Step 7] Add article item response:', data);
      return data;
    } catch (error) {
      console.error('[Step 7] Add article item error:', error);
      throw error;
    }
  }

  // Step 7: Get order - GET /api/orders/{orderId}
  async getOrder(orderId) {
    const orderKey = String(orderId);
    const inFlight = orderGetInFlight.get(orderKey);
    if (inFlight) {
      return inFlight;
    }

    const now = Date.now();
    const isLocalDev = isLocalDevHost();
    const cachedOrder = orderGetLastResponse.get(orderKey);
    const lastFetchedAt = orderGetLastFetchedAt.get(orderKey) || 0;

    // Localhost protection: avoid hammering GET /api/orders/{id}
    if (isLocalDev && now < orderGetCooldownUntil) {
      const secondsLeft = Math.ceil((orderGetCooldownUntil - now) / 1000);
      console.log(`[Step 7] Skipping getOrder(${orderKey}) during cooldown (${secondsLeft}s remaining)`);
      if (cachedOrder) {
        return cachedOrder;
      }
    }

    // Localhost protection: reuse very recent response for rapid duplicate reads
    if (isLocalDev && cachedOrder && now - lastFetchedAt < 1500) {
      return cachedOrder;
    }

    const fetchPromise = (async () => {
      try {
      const url = buildApiUrl({
        baseUrl: this.baseUrl,
        useProxy: this.useProxy,
        path: `/api/orders/${orderId}`,
      });
      
      console.log('[Step 7] Getting order:', url);
      
      const accessToken = typeof window.getAccessToken === 'function' 
        ? window.getAccessToken() 
        : null;
      
      const headers = {
        'Accept-Language': getAcceptLanguageHeader(),
        'Content-Type': 'application/json',
        ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {}),
      };
      
      let data;
      try {
        data = await requestJson({ url, headers });
      } catch (error) {
        const payloadText = typeof error.payload === 'string'
          ? error.payload
          : JSON.stringify(error.payload);
        console.error(`[Step 7] Get order error (${error.status || 'unknown'}):`, payloadText || error);
        if (isLocalDev && isRateLimitError(error)) {
          const retryMs = getRetryDelayFromError(error);
          orderGetCooldownUntil = Date.now() + retryMs;
          console.warn(`[Step 7] getOrder rate limited. Cooling down for ${Math.ceil(retryMs / 1000)}s`);
          if (cachedOrder) {
            return cachedOrder;
          }
        }
        const wrapped = new Error(`Get order failed: ${error.status || 'unknown'} - ${payloadText || error.message}`);
        wrapped.status = error.status;
        wrapped.payload = error.payload;
        throw wrapped;
      }
      console.log('[Step 7] Get order response:', data);
      orderGetLastFetchedAt.set(orderKey, Date.now());
      orderGetLastResponse.set(orderKey, data);
      return data;
    } catch (error) {
      console.error('[Step 7] Get order error:', error);
      throw error;
    } finally {
      orderGetInFlight.delete(orderKey);
    }
    })();

    orderGetInFlight.set(orderKey, fetchPromise);
    return fetchPromise;
  }

  // Step 7: Update order - PUT /api/orders/{orderId}
  async updateOrder(orderId, orderData) {
    try {
      // Always include the active business unit
      if (!orderData.businessUnit && state.selectedBusinessUnit) {
        orderData.businessUnit = state.selectedBusinessUnit;
      }

      const url = buildApiUrl({
        baseUrl: this.baseUrl,
        useProxy: this.useProxy,
        path: `/api/orders/${orderId}`,
      });
      
      console.log('[Step 7] Updating order:', url);
      
      const accessToken = typeof window.getAccessToken === 'function' 
        ? window.getAccessToken() 
        : null;
      
      const headers = {
        'Accept-Language': getAcceptLanguageHeader(),
        'Content-Type': 'application/json',
        ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {}),
      };
      
      let data;
      try {
        data = await requestJson({ url, method: 'PUT', headers, body: orderData });
      } catch (error) {
        const payloadText = typeof error.payload === 'string'
          ? error.payload
          : JSON.stringify(error.payload);
        console.error(`[Step 7] Update order error (${error.status || 'unknown'}):`, payloadText || error);
        throw new Error(`Update order failed: ${error.status || 'unknown'} - ${payloadText || error.message}`);
      }
      console.log('[Step 7] Update order response:', data);
      return data;
    } catch (error) {
      console.error('[Step 7] Update order error:', error);
      throw error;
    }
  }

  // Apply coupon to order - POST /api/ver3/orders/{order}/coupons
  // Note: API returns 405 for PUT, using POST instead
  // Documentation: https://boulders.brpsystems.com/brponline/external/documentation/api3
  async applyDiscountCode(orderId, discountCode) {
    try {
      // ver3 endpoints use different base URL (boulders.brpsystems.com/apiserver)
      // In development we rely on a relative URL so local dev proxy can handle it.
      // In production we should normally use the Cloudflare Pages proxy (`/api-proxy`).
      let url;
      if (this.useProxy) {
        url = buildApiUrl({
          baseUrl: this.baseUrl,
          useProxy: this.useProxy,
          path: `/api/ver3/orders/${orderId}/coupons`,
        });
      } else if (this.isDevelopment) {
        url = `/api/ver3/orders/${orderId}/coupons`;
      } else {
        url = `https://boulders.brpsystems.com/apiserver/api/ver3/orders/${orderId}/coupons`;
      }
      
      console.log('[Discount] Applying coupon:', discountCode, 'to order:', orderId);
      console.log('[Discount] Endpoint:', url);
      
      const accessToken = typeof window.getAccessToken === 'function' 
        ? window.getAccessToken() 
        : null;
      
      const headers = {
        'Accept-Language': getAcceptLanguageHeader(),
        'Content-Type': 'application/json',
        ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {}),
      };
      
      // API expects couponName field (not code)
      const payload = {
        couponName: discountCode,
      };
      
      console.log('[Discount] Request payload:', JSON.stringify(payload, null, 2));
      
      let data;
      try {
        data = await requestJson({ url, method: 'POST', headers, body: payload });
      } catch (error) {
        if (error.status === 405 || error.status === 404) {
          console.log('[Discount] POST returned', error.status, '- trying alternative endpoint...');
          const altUrl = this.useProxy
            ? buildApiUrl({
                baseUrl: this.baseUrl,
                useProxy: this.useProxy,
                path: `/api/orders/${orderId}/coupon`,
              })
            : `${this.baseUrl}/api/orders/${orderId}/coupon`;
          console.log('[Discount] Trying alternative endpoint:', altUrl);
          try {
            data = await requestJson({ url: altUrl, method: 'POST', headers, body: payload });
          } catch (altError) {
            const payloadText = typeof altError.payload === 'string'
              ? altError.payload
              : JSON.stringify(altError.payload);
            console.error(`[Discount] Apply coupon error (${altError.status || 'unknown'}):`, payloadText || altError);
            throw new Error(`Apply coupon failed: ${altError.status || 'unknown'} - ${payloadText || altError.message}`);
          }
        } else {
          const payloadText = typeof error.payload === 'string'
            ? error.payload
            : JSON.stringify(error.payload);
          console.error(`[Discount] Apply coupon error (${error.status || 'unknown'}):`, payloadText || error);
          throw new Error(`Apply coupon failed: ${error.status || 'unknown'} - ${payloadText || error.message}`);
        }
      }
      console.log('[Discount] Apply coupon response:', data);

      const subtotal = state.totals.subtotal || state.totals.cartTotal || 0;
      const discountAmount = calculateOrderCouponDiscountKr(data, subtotal);
      console.log('[Discount] Final extracted discountAmount:', discountAmount);

      return {
        ...data,
        discountAmount,
      };
    } catch (error) {
      console.error('[Discount] Apply coupon error:', error);
      throw error;
    }
  }

  // Remove coupon from order - DELETE /api/ver3/orders/{order}/coupons/{couponName}
  async removeDiscountCode(orderId, discountCode) {
    const encodedCoupon = encodeURIComponent(discountCode);
    let url;
    if (this.useProxy) {
      url = buildApiUrl({
        baseUrl: this.baseUrl,
        useProxy: this.useProxy,
        path: `/api/ver3/orders/${orderId}/coupons/${encodedCoupon}`,
      });
    } else if (this.isDevelopment) {
      url = `/api/ver3/orders/${orderId}/coupons/${encodedCoupon}`;
    } else {
      url = `https://boulders.brpsystems.com/apiserver/api/ver3/orders/${orderId}/coupons/${encodedCoupon}`;
    }

    console.log('[Discount] Removing coupon:', discountCode, 'from order:', orderId);

    const accessToken = typeof window.getAccessToken === 'function'
      ? window.getAccessToken()
      : null;

    const headers = {
      'Accept-Language': getAcceptLanguageHeader(),
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    };

    try {
      return await requestJson({ url, method: 'DELETE', headers });
    } catch (error) {
      const payloadText = typeof error.payload === 'string'
        ? error.payload
        : JSON.stringify(error.payload);
      console.error(`[Discount] Remove coupon error (${error.status || 'unknown'}):`, payloadText || error);
      throw new Error(`Remove coupon failed: ${error.status || 'unknown'} - ${payloadText || error.message}`);
    }
  }
}

// Step 9: Payment API
// Handles payment link generation for checkout
class PaymentAPI {
  constructor(baseUrl = null) {
    const config = getApiConfig({ baseUrlOverride: baseUrl });
    this.baseUrl = config.baseUrl;
    this.useProxy = config.useProxy;
    this.isDevelopment = config.isDevelopment;
    
    // Correct endpoint: /api/payment/generate-link
    // Base URL: https://api-join.boulders.dk
    // Documentation: https://documenter.getpostman.com/view/6552350/2sB3Wtsz3V#75d4fd2c-d336-43a3-a48f-5808f04290ad
    this.paymentEndpoint = '/api/payment/generate-link';
  }

  // Step 9: Generate payment link - POST /api/payment/generate-link
  // Endpoint: https://api-join.boulders.dk/api/payment/generate-link
  // Documentation: https://documenter.getpostman.com/view/6552350/2sB3Wtsz3V#75d4fd2c-d336-43a3-a48f-5808f04290ad
  // Create checkout URLs after an order is ready
  // Pass the order ID, payment method, selected business unit, return URL, and optional receipt email
  async generatePaymentLink({ orderId, paymentMethod, businessUnit, returnUrl = null, receiptEmail = null }) {
    try {
      const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

      // Build return URL if not provided
      if (!returnUrl && orderId) {
        const path = window.location.pathname || '/';
        const baseUrl = getReturnUrlBase();
        returnUrl = `${baseUrl}${path}?payment=return&orderId=${orderId}`;
      }
      
      if (!returnUrl) {
        throw new Error('Return URL is required for payment link generation');
      }
      
      if (!orderId) {
        throw new Error('Order ID is required for payment link generation');
      }
      
      const url = this.useProxy
        ? buildApiUrl({
            baseUrl: this.baseUrl,
            useProxy: this.useProxy,
            path: this.paymentEndpoint,
          })
        : `https://api-join.boulders.dk${this.paymentEndpoint}`;
      
      console.log('[Step 9] ===== GENERATE PAYMENT LINK CARD REQUEST =====');
      console.log('[Step 9] Endpoint:', url);
      console.log('[Step 9] Method: POST');
      
      const accessToken = typeof window.getAccessToken === 'function' 
        ? window.getAccessToken() 
        : null;
      
      console.log('[Step 9] Access token available:', !!accessToken);
      console.log('[Step 9] Access token value:', accessToken ? `${accessToken.substring(0, 20)}...` : 'null');
      
      if (!accessToken) {
        console.error('[Step 9] ⚠️ No access token available! Payment link endpoint requires authentication.');
        console.error('[Step 9] Make sure user is logged in or token is stored in session.');
      }
      
      const headers = {
        'Accept-Language': getAcceptLanguageHeader(),
        'Content-Type': 'application/json',
        ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {}),
      };
      
      console.log('[Step 9] Headers:', headers);
      console.log('[Step 9] Has Authorization header:', !!headers.Authorization);
      
      // Step 9: Payload structure according to Postman documentation
      // Endpoint: POST /api/payment/generate-link
      // Payload: { orderId, paymentMethodId, businessUnit, returnUrl, receiptEmail (optional) }
      // API expects paymentMethodId (numeric ID), not paymentMethod (string)
      // Map payment method string to numeric ID
      let paymentMethodId = paymentMethod;
      if (typeof paymentMethod === 'string') {
        const paymentMethodMap = {
          'card': 32,
          'creditcard': 32,
          'credit_card': 32,
          'debit': 2,
          'mobilepay': 3,
          'mobile_pay': 3,
        };
        paymentMethodId = paymentMethodMap[paymentMethod.toLowerCase()] || 32;
        console.log('[Step 9] Mapped payment method:', paymentMethod, '->', paymentMethodId);
      }
      
      const businessUnitId = typeof businessUnit === 'string' ? parseInt(businessUnit, 10) || businessUnit : businessUnit;

      // receiptEmail is intentionally NOT sent in the payload.
      //
      // Empirical finding: paying customers received two identical "Kvittering"
      // emails, while 100%-discount flows (which skip this endpoint entirely)
      // received only one. The most plausible cause is that BRP's payment-link
      // backend emits its own receipt to receiptEmail when present, *and* the
      // post-finalization flow also emits a receipt to the customer record's
      // email — yielding two copies for the same purchase.
      //
      // Dropping the explicit receiptEmail leaves the customer-record email as
      // the single source of truth. If we ever need to override the recipient
      // for a specific flow, gate it behind an explicit opt-in.

      const payload = {
        orderId, // Required: ID of the order
        paymentMethodId, // Required: Payment method ID (numeric)
        returnUrl, // Required: Absolute URL to return to after payment
        ...(businessUnitId ? { businessUnit: businessUnitId } : {}),
      };
      
      console.log('[Step 9] Payment method (raw):', paymentMethod);
      console.log('[Step 9] Payment method ID (mapped):', paymentMethodId);
      
      console.log('[Step 9] Request payload:', JSON.stringify(payload, null, 2));
      console.log('[Step 9] Sending Generate Payment Link Card request...');
      
      let data;
      try {
        data = await requestJson({ url, method: 'POST', headers, body: payload });
      } catch (error) {
        const payloadText = typeof error.payload === 'string'
          ? error.payload
          : JSON.stringify(error.payload);
        console.error(`[Step 9] ❌ Generate Payment Link Card failed (${error.status || 'unknown'}):`, payloadText || error);
        
        if (error.status === 403) {
          console.error('[Step 9] ⚠️ 403 Forbidden - Possible reasons:');
          console.error('[Step 9] ⚠️ 1. Order may have pricing mismatch (price verification failed)');
          console.error('[Step 9] ⚠️ 2. Order may be in a state that prevents payment link generation');
          console.error('[Step 9] ⚠️ 3. Payment method may not be valid for this order');
          console.error('[Step 9] ⚠️ 4. Business unit may not match order');
          console.error('[Step 9] ⚠️ Order details:', {
            orderId,
            orderPrice: state.fullOrder?.price?.amount,
            orderPriceDKK: state.fullOrder?.price?.amount ? (typeof state.fullOrder.price.amount === 'object' ? state.fullOrder.price.amount.amount / 100 : state.fullOrder.price.amount / 100) : null,
            hasSubscriptionItems: !!state.fullOrder?.subscriptionItems?.length,
            subscriptionItem: state.fullOrder?.subscriptionItems?.[0],
            paymentMethodId,
            businessUnit: businessUnitId
          });
          
          const subscriptionItem = state.fullOrder?.subscriptionItems?.[0];
          if (subscriptionItem) {
            const productId = subscriptionItem?.product?.id;
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const startDateStr = getTodayLocalDateString();
            const expectedPrice = orderAPI._calculateExpectedPartialMonthPrice(productId, startDateStr);
            const verification = orderAPI._verifySubscriptionPricing(state.fullOrder, productId, expectedPrice, today);
            
            if (!verification.isCorrect && (!verification.priceDifference || verification.priceDifference > 100)) {
              console.error('[Step 9] ❌ CONFIRMED: Order has pricing mismatch!');
              console.error('[Step 9] ❌ Backend shows:', verification.orderPriceDKK, 'DKK');
              console.error('[Step 9] ❌ Expected:', verification.expectedPriceDKK || 'N/A', 'DKK');
              console.error('[Step 9] ❌ This may cause payment link generation to fail with 403');
              console.error('[Step 9] ❌ Product ID:', productId);
            }
          }
        }
        
        throw new Error(`Generate Payment Link Card failed: ${error.status || 'unknown'} - ${payloadText || error.message}`);
      }
      console.log('[Step 9] ✅ Generate Payment Link Card response:', JSON.stringify(data, null, 2));
      
      // API Response structure from backend example: { "url": "..." }
      // We only use the payment provider URL. We do NOT redirect to Assently/signature case URLs.
      const rawLink = data.url ||
                     data.data?.url ||
                     data.data?.paymentLink ||
                     data.data?.link ||
                     data.paymentLink ||
                     data.link;
      const isAssentlyUrl = (url) => typeof url === 'string' && /assently\.com/i.test(url);
      if (rawLink && isAssentlyUrl(rawLink)) {
        console.error('[Step 9] ❌ API returned Assently/signature URL instead of payment link – refusing to use it');
        console.error('[Step 9] URL was:', rawLink);
        throw new Error('Payment link API returned a signature URL (Assently). Backend should return the payment provider URL only.');
      }
      const paymentLink = rawLink;
      
      if (!paymentLink) {
        console.error('[Step 9] ❌ No payment link in response!');
        console.error('[Step 9] Response structure:', Object.keys(data));
        if (data.data) {
          console.error('[Step 9] Data object keys:', Object.keys(data.data));
        }
        
        throw new Error('Payment link not found in API response');
      }
      
      // Store in state for UI to use
      if (state) {
        state.paymentLink = paymentLink;
        state.paymentLinkGenerated = true;
      }
      console.log('[Step 9] Payment link extracted from response.url:', paymentLink);
      
      return { ...data, paymentLink: paymentLink, url: paymentLink };
    } catch (error) {
      console.error('[Step 9] Generate payment link error:', error);
      throw error;
    }
  }
}

function getSubscriberBirthDate() {
  try {
    const dateField = document.getElementById('dateOfBirth');
    if (!dateField || !dateField.value) {
      return null;
    }
    const value = dateField.value.trim();
    if (!value) {
      return null;
    }
    // Ensure format YYYY-MM-DD
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return null;
    }
    return value;
  } catch (error) {
    console.warn('[checkout] Could not read birth date field:', error);
    return null;
  }
}

// Initialize API instances
const businessUnitsAPI = new BusinessUnitsAPI();
const referenceDataAPI = new ReferenceDataAPI();
const authAPI = new AuthAPI();
const orderAPI = new OrderAPI();
const paymentAPI = new PaymentAPI();

// Step 6: Token validation on app reload
// Keep tokens fresh by calling POST /api/auth/validate when app reloads with saved credentials
async function validateTokensOnLoad() {
  const accessToken = window.getAccessToken();
  const refreshToken = window.getRefreshToken();
  
  if (!accessToken) {
    return; // No tokens to validate
  }
  
  const now = Date.now();
  if (now < tokenValidationCooldownUntil) {
    const secondsLeft = Math.ceil((tokenValidationCooldownUntil - now) / 1000);
    console.log(`[Step 6] Skipping token validation (cooldown ${secondsLeft}s remaining)`);
    return;
  }
  
  // Check if token is expired
  if (window.isTokenExpired && window.isTokenExpired()) {
    console.log('[Step 6] Access token expired, attempting refresh...');
    
    // If refresh token exists, try to refresh
    if (refreshToken) {
      try {
        await authAPI.refreshToken();
        console.log('[Step 6] Token refreshed successfully');
        // Header auth indicator will be updated by refreshToken() function
      } catch (error) {
        console.error('[Step 6] Token refresh failed, clearing session:', error);
        window.clearTokens();
        // Return to auth step would be handled by UI logic
      }
    } else {
      // No refresh token, clear session
      console.log('[Step 6] No refresh token available, clearing session');
      window.clearTokens();
    }
    return;
  }
  
  // Token not expired, validate it
  try {
    await authAPI.validateToken();
    console.log('[Step 6] Token validated successfully');
  } catch (error) {
    const isRateLimit = isRateLimitError(error);
    if (isRateLimit) {
      const retryMs = getRetryDelayFromError(error);
      tokenValidationCooldownUntil = Date.now() + retryMs;
      console.warn(`[Step 6] Token validation rate limited. Cooling down for ${Math.ceil(retryMs / 1000)}s`);
      return;
    }
    console.error('[Step 6] Token validation failed:', error);
    // If validation fails, try refresh if refresh token exists
    if (refreshToken) {
      try {
        await authAPI.refreshToken();
        console.log('[Step 6] Token refreshed after validation failure');
        // Header auth indicator will be updated by refreshToken() function
      } catch (refreshError) {
        const refreshRateLimited = isRateLimitError(refreshError);
        if (refreshRateLimited) {
          const retryMs = getRetryDelayFromError(refreshError);
          tokenValidationCooldownUntil = Date.now() + retryMs;
          console.warn(`[Step 6] Token refresh rate limited. Cooling down for ${Math.ceil(retryMs / 1000)}s`);
          return;
        }
        console.error('[Step 6] Token refresh failed, clearing session:', refreshError);
        window.clearTokens();
      }
    } else {
      window.clearTokens();
    }
  }
}

// Step 4: Load reference data after business unit selection
// Caches responses in client state and refreshes when business unit changes
async function loadReferenceData() {
  if (!state.selectedBusinessUnit) {
    console.warn('[Step 4] Cannot load reference data: No business unit selected');
    return;
  }

  try {
    console.log('[Step 4] Loading reference data for business unit:', state.selectedBusinessUnit);
    
    // Fetch all reference data types
    const referenceData = await referenceDataAPI.getAllReferenceData(state.selectedBusinessUnit);
    
    // Cache in state
    state.referenceData = referenceData;
    state.referenceDataLoaded = true;
    
    console.log('[Step 4] Reference data loaded and cached:', referenceData);
    
    // If reference data is available, it can be used throughout the app
    // For example, if countries/regions are available, they can populate dropdowns
    // This is extensible - add UI updates here as new reference data types are added
    
  } catch (error) {
    console.error('[Step 4] Failed to load reference data:', error);
    // Don't block the flow - reference data is optional
    state.referenceData = {};
    state.referenceDataLoaded = false;
  }
} 

// Helper function to check if product should be displayed based on labels
// Rules:
// 1. If product has "Hidden" label (case-insensitive) → exclude (even if it also has "Public" or "PublicCampaign")
// 2. If product has "Public" or "PublicCampaign" label (case-insensitive) → include
// 3. Otherwise (no labels or other labels) → exclude (requires explicit "Public" or "PublicCampaign" label)
function shouldDisplayProductByLabels(product, options = {}) {
  const allowedVisibilityLabels = Array.isArray(options.allowedVisibilityLabels) && options.allowedVisibilityLabels.length > 0
    ? options.allowedVisibilityLabels.map(normalizeLabelName).filter(Boolean)
    : ['public', 'publiccampaign'];

  if (!product.productLabels || !Array.isArray(product.productLabels) || product.productLabels.length === 0) {
    // No labels → exclude (requires explicit "Public" or "PublicCampaign" label)
    return false;
  }
  
  // Check for "Hidden" label first (case-insensitive)
  const hasHiddenLabel = product.productLabels.some(
    label => label.name && label.name.toLowerCase() === 'hidden'
  );
  
  if (hasHiddenLabel) {
    // "Hidden" label takes priority → exclude
    return false;
  }
  
  // Check for allowed visibility labels (case-insensitive)
  const hasVisibilityLabel = product.productLabels.some(
    label => {
      const labelName = normalizeLabelName(label?.name);
      return allowedVisibilityLabels.includes(labelName);
    }
  );
  
  if (hasVisibilityLabel) {
    // Has allowed visibility label and no "Hidden" label → include
    return true;
  }
  
  // Has labels but neither "Public", "PublicCampaign", nor "Hidden" → exclude (requires explicit display label)
  return false;
}

function normalizeLabelName(value) {
  return String(value || '').trim().toLowerCase();
}

function productHasLabel(product, labelKey) {
  const normalizedKey = normalizeLabelName(labelKey);
  if (!normalizedKey) return false;
  if (!product || !Array.isArray(product.productLabels)) return false;
  return product.productLabels.some((label) => normalizeLabelName(label?.name) === normalizedKey);
}

function getRouteMatchedProducts(products, labelKey) {
  if (!Array.isArray(products) || products.length === 0) return [];
  return products.filter((product) => productHasLabel(product, labelKey));
}

function getDisplayableRouteMatchedProducts(products, labelKey, displayOptions = {}) {
  return getRouteMatchedProducts(products, labelKey)
    .filter((product) => shouldDisplayProductByLabels(product, displayOptions));
}

function getRequiredLandingLabels(activeLandingRoute) {
  if (activeLandingRoute?.componentName === 'LandingFirstMonthFree') {
    return ['landingfirstmonthfree', 'secret'];
  }
  return null;
}

function hasRequiredLabels(product, requiredLabels) {
  if (!Array.isArray(requiredLabels) || requiredLabels.length === 0) return true;
  return requiredLabels.every((label) => productHasLabel(product, label));
}

function getProductPriceCents(product) {
  const amount = product?.priceWithInterval?.price?.amount ?? product?.price?.amount ?? product?.amount;
  if (amount == null) return 0;
  return typeof amount === 'object' && 'amount' in amount ? Number(amount.amount) : Number(amount);
}

// Step 5: Load products (subscriptions and value cards) from API
// API Endpoints (per OpenAPI documentation):
// - GET /api/ver3/products/subscriptions?businessUnit={id} (line ~8287)
//   - Filters by businessUnit query param
//   - Returns SubscriptionProductOut objects with allowedToOrder field
// - GET /api/ver3/products/valuecards?businessUnit={id} (line ~8529)
//   - Filters by businessUnit query param
//   - Returns ValueCardProductOut objects (no allowedToOrder field)
async function loadProductsFromAPI() {
  if (!state.selectedBusinessUnit) {
    console.warn('Cannot load products: No business unit selected');
    return;
  }

  const activeLandingRoute = state.landingRouteConfig || resolveLandingRouteConfig();
  const isFreeTrialLandingRoute = activeLandingRoute?.componentName === 'LandingFreeTrial';
  const isFirstMonthFreeLandingRoute = activeLandingRoute?.componentName === 'LandingFirstMonthFree';
  const isFirstClimbLandingRoute = activeLandingRoute?.componentName === 'LandingFirstClimb';
  const displayLabelOptions = isFreeTrialLandingRoute
    ? { allowedVisibilityLabels: ['secret'] }
    : isFirstMonthFreeLandingRoute
      ? { allowedVisibilityLabels: ['secret'] }
      : isFirstClimbLandingRoute
        // Accept the firstclimb label itself as a visibility token so a BRP product
        // carrying only `firstclimb` (no Public/Secret/PublicCampaign) still passes.
        ? { allowedVisibilityLabels: ['firstclimb', 'secret', 'public', 'publiccampaign'] }
        : {};

  // Deduplicate concurrent calls (preload + step transition + showStep)
  if (productsLoadPromise) {
    return productsLoadPromise;
  }

  productsLoadPromise = (async () => {
  try {
    // Fetch subscriptions and value cards in parallel
    // Backend should already filter by businessUnit query param, but we do additional
    // client-side filtering to ensure compliance with OpenAPI spec display rules
    const [subscriptionsResponse, valueCardsResponse] = await Promise.all([
      businessUnitsAPI.getSubscriptions(state.selectedBusinessUnit),
      businessUnitsAPI.getValueCards(),
    ]);

    // Handle different response formats - could be array or object with data property
    let subscriptions = Array.isArray(subscriptionsResponse) 
      ? subscriptionsResponse 
      : (subscriptionsResponse.data || subscriptionsResponse.items || []);
    
    let valueCards = Array.isArray(valueCardsResponse)
      ? valueCardsResponse
      : (valueCardsResponse.data || valueCardsResponse.items || []);

    // Store raw products before filtering (for boost product detection)
    // This ensures boost products aren't missed if they're filtered out by display rules
    state.allRawProducts = [...subscriptions, ...valueCards];

    // Filter out products that shouldn't be displayed according to OpenAPI documentation
    // Reference: docs/brp-api3-openapi.yaml - SubscriptionProductOut schema (line ~14970)
    // Reference: docs/brp-api3-openapi.yaml - ValueCardProductOut schema (line ~15461)
    
    const requiredLandingLabels = getRequiredLandingLabels(activeLandingRoute);
    const landingLabelFilterActive = Array.isArray(requiredLandingLabels) && requiredLandingLabels.length > 0;

    // Filter subscription products
    // According to OpenAPI spec (SubscriptionProductOut):
    // - allowedToOrder (boolean): "To determine whether the subscription product is bookable for the subscription user or not"
    // - businessUnits (array): "Business units where the product exists"
    const originalSubscriptionCount = subscriptions.length;
    subscriptions = subscriptions.filter((product) => {
      // Debug: Log details for products with "[invalid]" in name to understand backend behavior
      const productNameLower = product.name && typeof product.name === 'string' ? product.name.toLowerCase() : '';
      if (productNameLower.includes('[invalid]') || productNameLower.includes('invalid')) {
        console.log(`[Product Filter DEBUG] ⚠️ Found product with [invalid] in name:`, {
          id: product.id,
          name: product.name,
          nameLower: productNameLower,
          allowedToOrder: product.allowedToOrder,
          hasAllowedToOrderProperty: product.hasOwnProperty('allowedToOrder'),
          allowedToOrderType: typeof product.allowedToOrder,
          businessUnits: product.businessUnits,
          fullProduct: JSON.parse(JSON.stringify(product)) // Deep clone to avoid reference issues
        });
      }
      
      // Debug: Log details for specific product "Collaboration" to understand backend behavior
      if (product.name && typeof product.name === 'string' && 
          (product.name.includes('Collaboration') || product.name.includes('Studiepris'))) {
        const fullProductCopy = JSON.parse(JSON.stringify(product)); // Deep clone to avoid reference issues
        console.log(`[Product Filter DEBUG] 🔍 Found Collaboration/Studiepris product:`, {
          id: product.id,
          name: product.name,
          allowedToOrder: product.allowedToOrder,
          hasAllowedToOrderProperty: product.hasOwnProperty('allowedToOrder'),
          allowedToOrderType: typeof product.allowedToOrder,
          priceWithInterval: product.priceWithInterval,
          hasPrice: !!(product.priceWithInterval?.price?.amount),
          priceAmount: product.priceWithInterval?.price?.amount,
          businessUnits: product.businessUnits,
          businessUnitIds: product.businessUnits?.map(bu => bu.id),
          selectedBusinessUnit: state.selectedBusinessUnit,
          applicableCustomerTypes: product.applicableCustomerTypes,
          applicableCustomerTypesCount: product.applicableCustomerTypes?.length || 0,
          fullProduct: fullProductCopy
        });
        console.log(`[Product Filter DEBUG] 🔍 Full product object:`, fullProductCopy);
      }
      
      // Check 1: allowedToOrder field (per OpenAPI spec line ~15032)
      // If allowedToOrder field exists and is false, exclude the product
      // Note: We check hasOwnProperty to distinguish between undefined and false
      // IMPORTANT: Backend should set allowedToOrder=false for products that shouldn't be displayed,
      // but if backend doesn't do this correctly, we need to handle it defensively
      if (product.hasOwnProperty('allowedToOrder')) {
        if (product.allowedToOrder === false) {
          return false;
        }
        // If allowedToOrder is explicitly true, continue to next checks
      } else {
        // If allowedToOrder property doesn't exist, we need to check if this is intentional
        // According to OpenAPI spec, this field should exist, but if backend doesn't send it,
        // we should log a warning but not exclude the product (backend should handle filtering)
        // Debug logging will help identify if backend is sending products that shouldn't be displayed
        console.warn(`[Product Filter] Subscription product ${product.id} (${product.name}) missing 'allowedToOrder' field - backend should filter this or set allowedToOrder=false`);
      }
      
      // Check 1a: productLabels.availableFor (per OpenAPI spec line ~13397)
      // Product labels can have 'availableFor' field: 'API', 'PUBLIC', or 'PUBLIC_EXCLUDE_FROM_CLASS_FILTER'
      // If product has labels but none are 'PUBLIC', it might not be meant for public display
      // However, this is not a definitive check - products without labels might still be public
      // So we only log a warning, not exclude
      if (product.productLabels && Array.isArray(product.productLabels) && product.productLabels.length > 0) {
        const hasPublicLabel = product.productLabels.some(
          label => label.availableFor === 'PUBLIC' || label.availableFor === 'PUBLIC_EXCLUDE_FROM_CLASS_FILTER'
        );
        if (!hasPublicLabel) {
          console.warn(`[Product Filter] Subscription product ${product.id} (${product.name}) has productLabels but none are PUBLIC - might not be meant for public display`);
        }
      }
      
      // Check 1b: applicableCustomerTypes (per OpenAPI spec line ~15035)
      // If product has no applicable customer types, it might not be displayable
      // However, this could also be valid for products that don't use customer type pricing
      // So we only log a warning, not exclude
      if (product.applicableCustomerTypes && Array.isArray(product.applicableCustomerTypes) && product.applicableCustomerTypes.length === 0) {
        console.warn(`[Product Filter] Subscription product ${product.id} (${product.name}) has empty applicableCustomerTypes array`);
      }
      
      // Check 1c: priceWithInterval (per OpenAPI spec line ~15017)
      // According to OpenAPI spec, subscription products should have priceWithInterval.price.amount
      // Products without a valid price cannot be purchased and should not be displayed
      // Note: Free products should still have price.amount = 0, not missing price
      // This is a defensive check - backend should filter these, but we exclude them client-side too
      if (!product.priceWithInterval || !product.priceWithInterval.price || 
          product.priceWithInterval.price.amount === undefined || 
          product.priceWithInterval.price.amount === null) {
        return false;
      }
      
      // Check 2: businessUnits validation (per OpenAPI spec line ~14986)
      // Defensive check: ensure product is available for the selected business unit
      // Note: Backend should already filter by businessUnit query param, but this is a safety check
      if (product.businessUnits && Array.isArray(product.businessUnits) && product.businessUnits.length > 0) {
        const isAvailableForBusinessUnit = product.businessUnits.some(
          (bu) => bu.id === state.selectedBusinessUnit || bu.id === parseInt(state.selectedBusinessUnit, 10)
        );
        if (!isAvailableForBusinessUnit) {
          return false;
        }
      }
      
      // Check 3: Label-based filtering
      // Normal flow: apply visibility labels. Landing flows with explicit label filters bypass this gate.
      if (!landingLabelFilterActive && !shouldDisplayProductByLabels(product, displayLabelOptions)) {
        return false;
      }
      
      return true;
    });
    

    // Filter value card products
    // According to OpenAPI spec (ValueCardProductOut):
    // - businessUnits (array): "Business units where the product exists" (line ~15477)
    // - Note: Value card products do NOT have an 'allowedToOrder' field in the schema
    const originalValueCardCount = valueCards.length;
    valueCards = valueCards.filter((product) => {
      // Check: businessUnits validation (per OpenAPI spec line ~15477)
      // Defensive check: ensure product is available for the selected business unit
      // Note: Backend should already filter by businessUnit query param, but this is a safety check
      if (product.businessUnits && Array.isArray(product.businessUnits) && product.businessUnits.length > 0) {
        const isAvailableForBusinessUnit = product.businessUnits.some(
          (bu) => bu.id === state.selectedBusinessUnit || bu.id === parseInt(state.selectedBusinessUnit, 10)
        );
        if (!isAvailableForBusinessUnit) {
          return false;
        }
      }
      
      // Check: Label-based filtering
      // Normal flow: apply visibility labels. Landing flows with explicit label filters bypass this gate.
      if (!landingLabelFilterActive && !shouldDisplayProductByLabels(product, displayLabelOptions)) {
        return false;
      }
      
      return true;
    });
    
    if (requiredLandingLabels) {
      const beforeSubscriptionLandingFilter = subscriptions.length;
      const beforeValueCardLandingFilter = valueCards.length;
      subscriptions = subscriptions.filter((product) => hasRequiredLabels(product, requiredLandingLabels));
      valueCards = valueCards.filter((product) => hasRequiredLabels(product, requiredLandingLabels));
      console.log('[Products] Landing filter active for path:', window.location.pathname, 'labels:', requiredLandingLabels, 'subscriptions:', `${beforeSubscriptionLandingFilter} -> ${subscriptions.length}`, 'valueCards:', `${beforeValueCardLandingFilter} -> ${valueCards.length}`);
    }


    // Separate subscriptions into Campaign, Membership, and 15-Day Trial Pass categories based on labels
    // Priority: PublicCampaign > 15-Day Trial Pass > Public
    // Products with "PublicCampaign" label → Campaign category
    // Products with "15-Day Trial Pass" label → 15-Day Trial Pass category
    // Products with "Public" label (but not "PublicCampaign" or "15-Day Trial Pass") → Membership category
    const hasLabelName = (product, expected) =>
      Array.isArray(product?.productLabels) &&
      product.productLabels.some((label) =>
        String(label?.name || '').trim().toLowerCase() === expected
      );

    const has15DayPassLikeLabel = (product) =>
      Array.isArray(product?.productLabels) &&
      product.productLabels.some((label) => {
        const normalized = String(label?.name || '')
          .trim()
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '');
        return normalized === '15daypass' ||
          normalized === '15daytrialpass' ||
          normalized === '15daypassfree';
      });

    const campaignSubscriptions = subscriptions.filter(product => {
      // For landing routes with explicit label requirements, match only required labels.
      if (requiredLandingLabels) {
        return hasRequiredLabels(product, requiredLandingLabels);
      }
      // Default behavior: campaign products are identified by "PublicCampaign".
      return hasLabelName(product, 'publiccampaign');
    });
    
    const dayPassSubscriptions = subscriptions.filter(product => {
      // Check if product has "15-Day Trial Pass" label (but not "PublicCampaign")
      const has15DayPassLabel = has15DayPassLikeLabel(product);
      const hasPublicCampaignLabel = hasLabelName(product, 'publiccampaign');
      return has15DayPassLabel && !hasPublicCampaignLabel;
    });
    
    const membershipSubscriptions = subscriptions.filter(product => {
      // Check if product has "PublicCampaign" or "15-Day Trial Pass" label
      const hasPublicCampaignLabel = hasLabelName(product, 'publiccampaign');
      const has15DayPassLabel = has15DayPassLikeLabel(product);
      // If it has "PublicCampaign" or "15-Day Trial Pass" label, exclude from membership
      if (hasPublicCampaignLabel || has15DayPassLabel) {
        return false;
      }
      // Otherwise, include if it has "Public" label (already filtered by shouldDisplayProductByLabels)
      return true;
    });
    
    // Separate value cards: those with "PublicCampaign" go to Campaign category
    // Value cards with "PublicCampaign" should appear in Campaign, not in Punch Card category
    const campaignValueCards = valueCards.filter(product => {
      // Check if product has "PublicCampaign" label
      const hasPublicCampaignLabel = product.productLabels?.some(
        label => label.name && label.name.toLowerCase() === 'publiccampaign'
      );
      return hasPublicCampaignLabel;
    });
    
    // Regular value cards (without "PublicCampaign") go to Punch Card category
    const regularValueCards = valueCards.filter(product => {
      const hasPublicCampaignLabel = product.productLabels?.some(
        label => label.name && label.name.toLowerCase() === 'publiccampaign'
      );
      return !hasPublicCampaignLabel;
    });

    // Sort all product lists by price high to low (price in cents for consistent comparison)
    const byPriceHighToLow = (a, b) => getProductPriceCents(b) - getProductPriceCents(a);
    campaignSubscriptions.sort(byPriceHighToLow);
    campaignValueCards.sort(byPriceHighToLow);
    membershipSubscriptions.sort(byPriceHighToLow);
    dayPassSubscriptions.sort(byPriceHighToLow);
    regularValueCards.sort(byPriceHighToLow);

    // Enrich value card products with GET /api/products/valuecards/{id} when the list payload
    // omits unit/clip fields (authoritative counts for cart totals).
    try {
      await mergeValueCardProductDetails([campaignValueCards, regularValueCards]);
    } catch (mergeErr) {
      console.warn('[Products] Value card detail merge failed:', mergeErr);
    }

    // Store in state
    state.campaignSubscriptions = campaignSubscriptions;
    state.campaignValueCards = campaignValueCards; // Value cards for Campaign category
    state.subscriptions = membershipSubscriptions;
    state.dayPassSubscriptions = dayPassSubscriptions;
    state.valueCards = regularValueCards; // Only non-campaign value cards go to Punch Card category
    
    if (activeLandingRoute) {
      const displayCandidates = [
        ...campaignSubscriptions,
        ...membershipSubscriptions,
        ...dayPassSubscriptions,
        ...campaignValueCards,
        ...regularValueCards,
      ];
      const uniqueDisplayCandidates = displayCandidates.filter((product, index, arr) => {
        const productKind = product?.priceWithInterval?.price?.amount !== undefined ? 'subscription' : 'valuecard';
        const productId = String(product?.id ?? '');
        const identity = `${productKind}:${productId}`;
        return arr.findIndex((candidate) => {
          const candidateKind = candidate?.priceWithInterval?.price?.amount !== undefined ? 'subscription' : 'valuecard';
          const candidateId = String(candidate?.id ?? '');
          return `${candidateKind}:${candidateId}` === identity;
        }) === index;
      });
      const matched = getDisplayableRouteMatchedProducts(uniqueDisplayCandidates, activeLandingRoute.labelKey, displayLabelOptions)
        .filter((product) => hasRequiredLabels(product, requiredLandingLabels))
        .sort(byPriceHighToLow);
      state.landingMatchedProducts = activeLandingRoute.mode === 'single'
        ? matched.slice(0, 1)
        : matched;
      devLog(`[Landing Route] ${activeLandingRoute.componentName} matched products:`, state.landingMatchedProducts.length);
      if (state.landingMatchedProducts.length === 0) {
        devWarn(`[Landing Route] No products matched label '${activeLandingRoute.labelKey}' on path '${normalizePathname(window.location.pathname)}'`);
      }
    } else {
      state.landingMatchedProducts = [];
    }


    // Re-render the membership plans with API data
    renderProductsFromAPI();
    updateCartSummary();

    // Prewarm boost modal resources in the background so first open feels instant.
    prewarmBoostModalResources();
  } catch (error) {
    console.error('Failed to load products from API:', error);
    // Show error message to user
    showToast('Failed to load membership options. Please try again later.', 'error');
    throw error;
  } finally {
    productsLoadPromise = null;
  }
  })();

  return productsLoadPromise;
}

// Step 5: Render products from API data into the UI
function renderProductsFromAPI() {
  // Helper function to render a subscription product card
  const renderSubscriptionCard = (product, category) => {
    const planCard = document.createElement('div');
    planCard.className = 'plan-card';
    const productId = product.id;
    planCard.dataset.plan = `${category}-${productId}`;
    planCard.dataset.productId = productId;
    planCard.dataset.category = category;
    
    // Extract price from API structure
    const priceInCents = product.priceWithInterval?.price?.amount || 
                         product.price?.amount || 
                         product.amount || 
                         0;
    const price = priceInCents > 0 ? priceInCents / 100 : 0;
    const currency = product.priceWithInterval?.price?.currency || 
                     product.price?.currency || 
                     product.currency || 
                     'DKK';
    
    // Determine price unit: 15-day pass is one-time payment (kr), others use interval (kr/mo, kr/year)
    const is15DayPass = category === '15daypass';
    const intervalUnit = product.priceWithInterval?.interval?.unit || 'MONTH';
    const priceUnit = is15DayPass ? 'kr' : (intervalUnit === 'MONTH' ? 'kr/mo' : 
                     intervalUnit === 'YEAR' ? 'kr/year' : 'kr');

    // Campaign cards can show first-month intro price with original monthly price struck through.
    // Prefer explicit API fields if present; otherwise parse from campaign text.
    const resolveCampaignIntroPriceDisplay = (campaignProduct) => {
      const directCandidates = [
        campaignProduct?.firstCampaignPrice,
        campaignProduct?.firstMonthPrice,
        campaignProduct?.introPrice,
        campaignProduct?.campaignPrice,
        campaignProduct?.priceFirstMonth,
        campaignProduct?.price?.firstMonth?.amount,
        campaignProduct?.priceWithInterval?.firstMonth?.amount,
      ];

      for (const candidate of directCandidates) {
        if (candidate === null || candidate === undefined || candidate === '') continue;
        const numeric = Number(candidate) > 1000 ? Number(candidate) / 100 : Number(candidate);
        if (Number.isFinite(numeric) && numeric >= 0) {
          return formatPriceHalfKrone(roundToHalfKrone(numeric));
        }
      }

      const combinedText = `${campaignProduct?.name || ''} ${campaignProduct?.externalDescription || ''} ${campaignProduct?.description || ''}`;
      const normalizedText = combinedText.replace(/\u00a0/g, ' ');
      const introMatch = normalizedText.match(/(?:første\s*måned|first\s*month)[^0-9]{0,20}(\d+(?:[.,]\d{1,2})?)\s*(?:kr|dkk)?/i) ||
        normalizedText.match(/(\d+(?:[.,]\d{1,2})?)\s*(?:kr|dkk)[^a-zA-Z]{0,20}(?:første\s*måned|first\s*month)/i);
      if (!introMatch || !introMatch[1]) return null;

      const parsed = Number(introMatch[1].replace(',', '.'));
      if (!Number.isFinite(parsed) || parsed < 0) return null;
      return formatPriceHalfKrone(roundToHalfKrone(parsed));
    };

    const campaignIntroPriceDisplay = category === 'campaign' ? resolveCampaignIntroPriceDisplay(product) : null;
    const isFirstMonthCampaignPricing = category === 'campaign' && campaignIntroPriceDisplay !== null;
    const productOriginalPriceDKK = !isFirstMonthCampaignPricing
      ? getPlanCardOriginalPrice(product, price)
      : null;
    const hasDiscountedPlanPrice = !isFirstMonthCampaignPricing
      && Number.isFinite(productOriginalPriceDKK)
      && productOriginalPriceDKK > price;
    const originalPriceDisplay = isFirstMonthCampaignPricing
      ? (price > 0 ? formatPriceHalfKrone(roundToHalfKrone(price)) : '')
      : (hasDiscountedPlanPrice ? formatPriceHalfKrone(roundToHalfKrone(productOriginalPriceDKK)) : '');
    const effectiveDisplayPrice = isFirstMonthCampaignPricing ? campaignIntroPriceDisplay : (price > 0 ? formatPriceHalfKrone(roundToHalfKrone(price)) : 'FREE');
    const showPriceUnit = isFirstMonthCampaignPricing || price > 0;
    
    // Get description - use externalDescription if available, otherwise fall back to description
    // Do not show product number as fallback
    let description = '';
    if (category === 'campaign') {
      // Campaign: prefer externalDescription, but fall back to description if externalDescription is empty or single-line
      const externalDesc = product.externalDescription || '';
      const descText = product.description || '';
      // If externalDesc exists and has newlines, use it; otherwise try description
      description = (externalDesc && externalDesc.includes('\n')) ? externalDesc : 
                    (descText && descText.includes('\n')) ? descText : 
                    externalDesc || descText || '';
    } else {
      description = product.externalDescription || product.description || '';
    }
    
    // Preserve line breaks from backend - escape HTML and preserve newlines
    const descriptionHtml = description 
      ? description
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/\n/g, '<br>')
      : '';
    
    planCard.innerHTML = sanitizeHTML(`
      <div class="plan-info">
        <div class="plan-content-left">
          <div class="plan-type">${product.name || 'Membership'}</div>
          <div class="plan-price ${(isFirstMonthCampaignPricing || hasDiscountedPlanPrice) ? 'plan-price--campaign' : ''}">
            <span class="price-amount">${effectiveDisplayPrice}</span>
            ${showPriceUnit ? `<span class="price-unit">${priceUnit}</span>` : ''}
            ${originalPriceDisplay
              ? `<span class="price-original">${originalPriceDisplay} ${priceUnit}</span>`
              : ''}
          </div>
          ${descriptionHtml ? `<div class="plan-description">${descriptionHtml}</div>` : ''}
        </div>
        <div class="plan-card-actions">
          <button class="select-btn" data-action="select-plan" data-plan-id="${category}-${productId}" data-i18n-key="button.select">Select</button>
        </div>
      </div>
    `);
    
    return planCard;
  };
  
  // Helper function to render value card (punch card) products
  const renderValueCard = (product, category = 'punchcard') => {
    const planCard = document.createElement('div');
    planCard.className = 'plan-card';
    // Use numeric ID from API
    const productId = product.id;
    planCard.dataset.plan = `punch-${productId}`; // For backward compatibility
    planCard.dataset.productId = productId; // Store API product ID (numeric)
    planCard.dataset.category = category;
    
    // Extract price from API structure: price.amount is in cents/øre (e.g., 77500 = 775.00 DKK)
    const priceInCents = product.price?.amount || product.amount || 0;
    const price = priceInCents / 100; // Convert from cents to main currency unit
    planCard.dataset.price = price;
    
    // Value cards are one-time purchases, so no interval unit needed
    const description = product.description || product.productNumber || '';
    
    // Preserve line breaks from backend
    const descriptionHtml = description 
      ? description
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/\n/g, '<br>')
      : '';
    
    planCard.innerHTML = sanitizeHTML(`
      <div class="plan-info">
        <div class="plan-content-left">
          <div class="plan-type">${product.name || 'Punch Card'}</div>
          <div class="plan-price">
            <span class="price-amount">${price > 0 ? formatPriceHalfKrone(roundToHalfKrone(price)) : 'FREE'}</span>
            ${price > 0 ? '<span class="price-unit">kr</span>' : ''}
          </div>
          ${descriptionHtml ? `<div class="plan-description">${descriptionHtml}</div>` : ''}
        </div>
        <div class="plan-card-actions">
          <button class="select-btn" data-action="select-plan" data-plan-id="punch-${productId}" data-i18n-key="button.select">Select</button>
          <div class="quantity-panel">
            ${isFirstClimbRoute() ? '' : `
            <span class="quantity-panel-label" data-i18n-key="quantity.label">Choose quantity</span>
            <div class="quantity-selector">
              <button class="quantity-btn minus" data-action="decrement-quantity" data-plan-id="punch-${productId}" disabled>−</button>
              <span class="quantity-value" data-plan-id="punch-${productId}">1</span>
              <button class="quantity-btn plus" data-action="increment-quantity" data-plan-id="punch-${productId}">+</button>
            </div>`}
            <button class="continue-btn" data-action="continue-value-cards" data-plan-id="punch-${productId}" data-i18n-key="button.continue">Continue</button>
          </div>
        </div>
      </div>
    `);
    
    return planCard;
  };

  const activeLandingRoute = state.landingRouteConfig || resolveLandingRouteConfig();
  const landingMatchedProducts = Array.isArray(state.landingMatchedProducts)
    ? state.landingMatchedProducts
    : [];
  const shouldRenderLandingRoute = !!activeLandingRoute;
  const resolveLandingCategory = (product) => {
    if (product?.priceWithInterval?.price?.amount !== undefined) {
      if (hasProductLabel(product, 'publiccampaign')) return 'campaign';
      if (has15DayPassLabel(product)) return '15daypass';
      return 'membership';
    }
    return hasProductLabel(product, 'publiccampaign') ? 'campaign' : 'punchcard';
  };
  const ensureFreeTrialCategoryItem = () => {
    const categoryList = document.querySelector('.category-list');
    if (!categoryList) return null;
    let categoryItem = categoryList.querySelector('[data-category="freetrial"]');
    if (!categoryItem) {
      categoryItem = document.createElement('div');
      categoryItem.className = 'category-item expanded selected';
      categoryItem.dataset.category = 'freetrial';
      categoryItem.dataset.landingLocked = 'true';
      categoryItem.innerHTML = sanitizeHTML(`
        <div class="category-header">
          <div class="category-info">
            <div class="category-title">Aktivér 15 Dages Kort</div>
          </div>
          <i data-lucide="chevron-down" class="category-icon"></i>
        </div>
        <div class="category-content">
          <div class="category-description">
            <p>Prøv Boulders gratis i 15 dage. Fuld adgang + lejesko inkluderet.</p>
          </div>
          <div class="plans-list"></div>
        </div>
      `);
      categoryList.prepend(categoryItem);
    }
    return categoryItem;
  };
  const ensureFirstClimbCategoryItem = () => {
    const categoryList = document.querySelector('.category-list');
    if (!categoryList) return null;
    let categoryItem = categoryList.querySelector('[data-category="firstclimb"]');
    if (!categoryItem) {
      categoryItem = document.createElement('div');
      categoryItem.className = 'category-item expanded selected';
      categoryItem.dataset.category = 'firstclimb';
      categoryItem.dataset.landingLocked = 'true';
      const title = t('firstclimb.category.title') || 'Din første klatretur';
      const description = t('firstclimb.category.description') || 'En dagsbillet til 99 kr inkl. lejesko og kalk. Kun for nye klatrere.';
      categoryItem.innerHTML = sanitizeHTML(`
        <div class="category-header">
          <div class="category-info">
            <div class="category-title" data-i18n-key="firstclimb.category.title">${title}</div>
          </div>
          <i data-lucide="chevron-down" class="category-icon"></i>
        </div>
        <div class="category-content">
          <div class="category-description">
            <p data-i18n-key="firstclimb.category.description">${description}</p>
          </div>
          <div class="plans-list"></div>
        </div>
      `);
      categoryList.prepend(categoryItem);
    }
    return categoryItem;
  };
  const getLandingTargetList = () => {
    if (activeLandingRoute?.componentName === 'LandingFreeTrial') {
      const categoryItem = ensureFreeTrialCategoryItem();
      return categoryItem ? categoryItem.querySelector('.plans-list') : null;
    }
    if (activeLandingRoute?.componentName === 'LandingFirstClimb') {
      const categoryItem = ensureFirstClimbCategoryItem();
      return categoryItem ? categoryItem.querySelector('.plans-list') : null;
    }
    if (activeLandingRoute?.componentName === 'LandingFirstMonthFree') {
      return document.querySelector('[data-category="campaign"] .plans-list');
    }
    return document.querySelector('[data-category="membership"] .plans-list');
  };
  const hideNonLandingCategories = () => {
    const categoryItems = document.querySelectorAll('.category-item');
    categoryItems.forEach((item) => {
      item.style.display = 'none';
    });
    const targetCategory = activeLandingRoute?.componentName === 'LandingFreeTrial'
      ? ensureFreeTrialCategoryItem()
      : activeLandingRoute?.componentName === 'LandingFirstClimb'
        ? ensureFirstClimbCategoryItem()
        : activeLandingRoute?.componentName === 'LandingFirstMonthFree'
          ? document.querySelector('[data-category="campaign"]')
          : document.querySelector('[data-category="membership"]');
    if (targetCategory) {
      targetCategory.style.display = '';
      targetCategory.classList.add('expanded', 'selected');
    }
    if (activeLandingRoute?.componentName === 'LandingFirstMonthFree') {
      applyOfferLandingCategoryCopy();
    }
  };
  const renderLandingCards = (products) => {
    const plansList = getLandingTargetList();
    if (!plansList) return;
    const requiredLandingLabels = getRequiredLandingLabels(activeLandingRoute);
    const strictlyMatchedProducts = Array.isArray(products)
      ? products.filter((product) => hasRequiredLabels(product, requiredLandingLabels))
      : [];
    plansList.innerHTML = '';
    if (strictlyMatchedProducts.length === 0) {
      const emptyEl = document.createElement('div');
      emptyEl.className = 'no-products-message';
      emptyEl.innerHTML = sanitizeHTML(`
        <div class="no-products-content">
          <p>No offers are available for this campaign right now.</p>
        </div>
      `);
      plansList.appendChild(emptyEl);
      return;
    }
    strictlyMatchedProducts.forEach((product) => {
      const category = activeLandingRoute?.componentName === 'LandingFirstMonthFree'
        ? 'campaign'
        : resolveLandingCategory(product);
      const isSubscriptionProduct = product?.priceWithInterval?.price?.amount !== undefined;
      const card = isSubscriptionProduct
        ? renderSubscriptionCard(product, category)
        : renderValueCard(product, category === 'campaign' ? 'campaign' : 'punchcard');
      plansList.appendChild(card);
    });
  };
  const LandingFreeTrial = (products) => {
    hideNonLandingCategories();
    renderLandingCards((products || []).slice(0, 1));
    devLog('[Landing Route] Rendering LandingFreeTrial');
  };
  const LandingFirstMonthFree = (products) => {
    hideNonLandingCategories();
    renderLandingCards(products || []);
    devLog('[Landing Route] Rendering LandingFirstMonthFree');
  };
  const LandingFirstClimb = (products) => {
    hideNonLandingCategories();
    renderLandingCards((products || []).slice(0, 1));
    devLog('[Landing Route] Rendering LandingFirstClimb');
  };
  if (shouldRenderLandingRoute) {
    if (activeLandingRoute.componentName === 'LandingFreeTrial') {
      LandingFreeTrial(landingMatchedProducts);
    } else if (activeLandingRoute.componentName === 'LandingFirstMonthFree') {
      LandingFirstMonthFree(landingMatchedProducts);
    } else if (activeLandingRoute.componentName === 'LandingFirstClimb') {
      LandingFirstClimb(landingMatchedProducts);
    } else {
      devWarn('[Landing Route] Unknown component name:', activeLandingRoute.componentName);
    }

    scheduleAccessStepRebind();
    return;
  }

  const freeTrialCategoryItem = document.querySelector('.category-item[data-category="freetrial"]');
  if (freeTrialCategoryItem) {
    freeTrialCategoryItem.remove();
  }
  const firstClimbCategoryItem = document.querySelector('.category-item[data-category="firstclimb"]');
  if (firstClimbCategoryItem) {
    firstClimbCategoryItem.remove();
  }
  const categoryItems = document.querySelectorAll('.category-item');
  categoryItems.forEach((item) => {
    item.style.display = '';
    item.dataset.landingLocked = '';
  });
  
  // Render campaign subscriptions and value cards into the campaign category
  const campaignCategoryItem = document.querySelector('[data-category="campaign"]');
  const campaignPlansList = document.querySelector('[data-category="campaign"] .plans-list');
  
  if (campaignCategoryItem) {
    // Check if we have any campaign products (subscriptions or value cards)
    const hasCampaignSubscriptions = state.campaignSubscriptions && state.campaignSubscriptions.length > 0;
    const hasCampaignValueCards = state.campaignValueCards && state.campaignValueCards.length > 0;
    const hasCampaignProducts = hasCampaignSubscriptions || hasCampaignValueCards;
    
    // Hide campaign category if no products
    if (!hasCampaignProducts) {
      campaignCategoryItem.style.display = 'none';
      stopCampaignCountdown();
    } else {
      // Show category and render products
      campaignCategoryItem.style.display = '';
      startCampaignCountdown();
      if (campaignPlansList) {
        campaignPlansList.innerHTML = '';
        
        // Render campaign subscriptions first
        if (hasCampaignSubscriptions) {
          state.campaignSubscriptions.forEach((product) => {
            const planCard = renderSubscriptionCard(product, 'campaign');
            // Event listeners will be set up by setupNewAccessStep()
            campaignPlansList.appendChild(planCard);
          });
        }
        
        // Render campaign value cards
        if (hasCampaignValueCards) {
          state.campaignValueCards.forEach((product) => {
            const planCard = renderValueCard(product, 'campaign');
            // Event listeners will be set up by setupNewAccessStep()
            campaignPlansList.appendChild(planCard);
          });
        }
      }
    }
  }
  
  // Render subscriptions (memberships) into the membership category
  const membershipPlansList = document.querySelector('[data-category="membership"] .plans-list');
  if (membershipPlansList) {
    membershipPlansList.innerHTML = '';
    
    if (state.subscriptions.length > 0) {
      state.subscriptions.forEach((product) => {
        const planCard = renderSubscriptionCard(product, 'membership');
        // Event listeners will be set up by setupNewAccessStep()
        membershipPlansList.appendChild(planCard);
      });
    } else {
      // Show "No products available" message
      const noProductsMessage = document.createElement('div');
      noProductsMessage.className = 'no-products-message';
      noProductsMessage.innerHTML = sanitizeHTML(`
        <div class="no-products-content">
          <p data-i18n-key="message.noProducts.membership">No membership options available at this time.</p>
        </div>
      `);
      membershipPlansList.appendChild(noProductsMessage);
      // Update translation immediately
      const messageP = noProductsMessage.querySelector('p[data-i18n-key]');
      if (messageP) messageP.textContent = t('message.noProducts.membership');
    }
  }

  // Render 15-Day Trial Pass subscriptions into the 15daypass category
  const dayPassPlansList = document.querySelector('[data-category="15daypass"] .plans-list');
  if (dayPassPlansList) {
    dayPassPlansList.innerHTML = '';
    
    if (state.dayPassSubscriptions && state.dayPassSubscriptions.length > 0) {
      state.dayPassSubscriptions.forEach((product) => {
        const planCard = renderSubscriptionCard(product, '15daypass');
        // Event listeners will be set up by setupNewAccessStep()
        dayPassPlansList.appendChild(planCard);
      });
    } else {
      // Show "No products available" message
      const noProductsMessage = document.createElement('div');
      noProductsMessage.className = 'no-products-message';
      noProductsMessage.innerHTML = sanitizeHTML(`
        <div class="no-products-content">
          <p>No 15-Day Trial Pass options available at this time.</p>
        </div>
      `);
      dayPassPlansList.appendChild(noProductsMessage);
    }
  }

  // Render value cards (punch cards) into the punchcard category
  // Note: Only regular value cards (without "PublicCampaign") are shown here
  // Value cards with "PublicCampaign" are shown in the Campaign category
  const punchCardPlansList = document.querySelector('[data-category="punchcard"] .plans-list');
  if (punchCardPlansList) {
    punchCardPlansList.innerHTML = '';
    
    if (state.valueCards && state.valueCards.length > 0) {
      state.valueCards.forEach((product) => {
        const planCard = renderValueCard(product, 'punchcard');
        // Event listeners will be set up by setupNewAccessStep()
        punchCardPlansList.appendChild(planCard);
      });
    } else {
      // Show "No products available" message
      const noProductsMessage = document.createElement('div');
      noProductsMessage.className = 'no-products-message';
      noProductsMessage.innerHTML = sanitizeHTML(`
        <div class="no-products-content">
          <p data-i18n-key="message.noProducts.punchcard">No punch card options available at this time.</p>
        </div>
      `);
      punchCardPlansList.appendChild(noProductsMessage);
      // Update translation immediately
      const messageP = noProductsMessage.querySelector('p[data-i18n-key]');
      if (messageP) messageP.textContent = t('message.noProducts.punchcard');
    }
  }
  
  // Re-setup event listeners and translations in one batched frame to avoid visible double-render.
  scheduleAccessStepRebind();
}

// Step 5: Load add-ons when a membership is selected
async function loadSubscriptionAdditions(productId) {
  if (!productId) {
    console.warn('Cannot load additions: No product ID provided');
    return;
  }

  try {
    const response = await businessUnitsAPI.getSubscriptionAdditions(productId);
    
    // Handle different response formats
    const additions = Array.isArray(response)
      ? response
      : (response.data || response.items || []);

    state.subscriptionAdditions = additions;
    console.log(`Loaded ${additions.length} subscription additions for product ${productId}`);

    // TODO: Update add-ons UI with fetched data (Step 3 - Add-ons step)
    // This will be used in the add-ons step
  } catch (error) {
    console.error('Failed to load subscription additions:', error);
    state.subscriptionAdditions = [];
  }
}

// Store user location and gym distances
let userLocation = null;
let gymsWithDistances = [];

// Load gyms from API and update UI
async function loadGymsFromAPI() {
  const now = Date.now();
  if (isLocalDevHost() && now < gymLoadCooldownUntil) {
    const secondsLeft = Math.ceil((gymLoadCooldownUntil - now) / 1000);
    console.log(`[Step 1] Skipping gym load (cooldown ${secondsLeft}s remaining)`);
    const noResults = document.getElementById('noResults');
    if (noResults) {
      noResults.classList.remove('hidden');
      noResults.textContent = `API rate limit in local dev. Retrying in ~${secondsLeft}s...`;
    }
    return;
  }

  try {
    if (gymLoadRetryTimeoutId) {
      clearTimeout(gymLoadRetryTimeoutId);
      gymLoadRetryTimeoutId = null;
    }
    const response = await businessUnitsAPI.getBusinessUnits();
    
    // Handle different response formats - could be array or object with data property
    const gyms = Array.isArray(response) ? response : (response.data || response.items || []);
    
    devLog('Loaded gyms from API:', gyms);
    devLog(`Found ${gyms.length} business units`);
    
    // Store gyms for distance calculation
    gymsWithDistances = gyms;
    
    // Update selected gym display if we're on step 2
    if (state.currentStep === 2) {
      updateSelectedGymDisplay();
    }
    
    // Clear existing gym list
    const gymList = document.querySelector('.gym-list');
    if (gymList) {
      gymList.innerHTML = '';
    }
    
    // Show error message if no gyms found
    if (gyms.length === 0) {
      const noResults = document.getElementById('noResults');
      if (noResults) {
        noResults.classList.remove('hidden');
        noResults.textContent = 'No business units found. Please check API authentication.';
      }
      console.warn('No business units returned from API');
      return;
    }
    
    // Hide no results message if gyms are found
    const noResults = document.getElementById('noResults');
    if (noResults) {
      noResults.classList.add('hidden');
    }
    
    // Log sample gym structure to debug coordinate location
    if (gyms.length > 0) {
      devLog('[Load Gyms] Sample gym structure:', {
        name: gyms[0].name,
        address: gyms[0].address,
        hasLatLon: !!(gyms[0].address?.latitude && gyms[0].address?.longitude),
        fullGym: gyms[0]
      });
    }
    
    // If user location is available, sort by distance
    let gymsToDisplay = gyms;
    if (userLocation) {
      devLog('[Load Gyms] User location available, calculating distances...', userLocation);
      // Show loading message
      // Hide status text
      const locationStatus = document.getElementById('locationStatus');
      if (locationStatus) {
        locationStatus.style.display = 'none';
      }
      
      gymsToDisplay = await calculateGymDistances(
        gyms,
        userLocation.latitude,
        userLocation.longitude,
        userLocation.accuracy,
        { logger: geoLogger }
      );
      gymsWithDistances = gymsToDisplay;
      
      // Update selected gym display if we're on step 2
      if (state.currentStep === 2) {
        updateSelectedGymDisplay();
      }
      
      // Ensure location button is highlighted if location is active
      const locationBtn = document.getElementById('findNearestGym');
      if (locationBtn && userLocation) {
        locationBtn.classList.add('active');
      }
      
      // Log first few gyms to verify sorting
      devLog('[Load Gyms] First 3 gyms after sorting:', gymsToDisplay.slice(0, 3).map(g => ({
        name: g.name,
        distance: g.distance !== null ? `${g.distance.toFixed(2)} km` : 'N/A',
        hasCoordinates: !!(g.address?.latitude && g.address?.longitude)
      })));
    } else {
      devLog('[Load Gyms] No user location available, displaying gyms in original order');
    }
    
    // Store existing gym items and their positions for animation
    const existingItems = Array.from(gymList.querySelectorAll('.gym-item'));
    const existingPositions = new Map();
    existingItems.forEach((item, index) => {
      const gymId = item.getAttribute('data-gym-id');
      if (gymId) {
        existingPositions.set(gymId, {
          element: item,
          oldIndex: index,
          rect: item.getBoundingClientRect()
        });
      }
    });
    
    // Clear the list
    gymList.innerHTML = '';
    
    // Create gym items from API data
    const newItems = [];
    for (let i = 0; i < gymsToDisplay.length; i++) {
      const gym = gymsToDisplay[i];
      if (gym.name && gym.address) {
        // Create and display gym item
        // Mark as nearest if it's the first gym AND has a valid distance
        const isNearest = i === 0 && userLocation && gym.distance !== null && gym.distance !== undefined;
        if (isNearest) {
          devLog('[Load Gyms] Marking as nearest:', gym.name, `${gym.distance.toFixed(2)} km`);
        }
        const gymItem = createGymItem(gym, isNearest);
        const gymId = `gym-${gym.id}`;
        
        // Check if this item existed before and get its old position
        const existingData = existingPositions.get(gymId);
        if (existingData && existingData.oldIndex !== i) {
          // Item moved - add animation class
          gymItem.classList.add('reordering');
        } else if (!existingData) {
          // New item - fade in with staggered delay
          gymItem.classList.add('fade-in');
          gymItem.style.animationDelay = `${i * 0.05}s`;
        }
        
        newItems.push({ item: gymItem, index: i });
        if (gymList) {
          gymList.appendChild(gymItem);
        }
      }
    }
    
    // Animate reordering using FLIP technique
    if (existingItems.length > 0) {
      requestAnimationFrame(() => {
        newItems.forEach(({ item, index }) => {
          const gymId = item.getAttribute('data-gym-id');
          const existingData = existingPositions.get(gymId);
          
          if (existingData && existingData.oldIndex !== index) {
            // Calculate new position
            const newRect = item.getBoundingClientRect();
            const oldRect = existingData.rect;
            
            // Calculate transform
            const deltaX = oldRect.left - newRect.left;
            const deltaY = oldRect.top - newRect.top;
            
            // Apply initial transform
            item.style.transform = `translate(${deltaX}px, ${deltaY}px)`;
            item.style.transition = 'none';
            
            // Trigger reflow
            item.offsetHeight;
            
            // Animate to final position with staggered delay
            requestAnimationFrame(() => {
              item.style.transform = '';
              item.style.transition = `transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)`;
              // Add slight delay based on distance moved
              const delay = Math.min(Math.abs(index - existingData.oldIndex) * 0.03, 0.2);
              if (delay > 0) {
                item.style.transitionDelay = `${delay}s`;
              }
            });
          }
        });
      });
    }
    
    // Re-setup event listeners for new gym items
    setupGymEventListeners();
    
    // Re-render FAQ when on step 1 so opening hours table uses API gym list (and API opening hours if present)
    if (state.currentStep === 1) {
      renderFAQ();
    }
    
  } catch (error) {
    if (isLocalDevHost() && isRateLimitError(error)) {
      const retryMs = getRetryDelayFromError(error);
      gymLoadCooldownUntil = Date.now() + retryMs;
      console.warn(`[Step 1] Business units rate limited. Cooling down for ${Math.ceil(retryMs / 1000)}s`);
      const noResults = document.getElementById('noResults');
      if (noResults) {
        noResults.classList.remove('hidden');
        noResults.textContent = `API rate limit in local dev. Retrying in ~${Math.ceil(retryMs / 1000)}s...`;
      }
      if (!gymLoadRetryTimeoutId) {
        gymLoadRetryTimeoutId = setTimeout(() => {
          gymLoadRetryTimeoutId = null;
          loadGymsFromAPI().catch((retryError) => {
            console.warn('[Step 1] Auto-retry gym load failed:', retryError);
          });
        }, Math.max(retryMs + 250, 1000));
      }
      return;
    }
    console.error('Failed to load gyms from API:', error);
    
    // Show user-friendly error message
    const gymList = document.querySelector('.gym-list');
    const noResults = document.getElementById('noResults');
    if (gymList && noResults) {
      gymList.innerHTML = '';
      noResults.classList.remove('hidden');
      
      // Provide more helpful error message based on error type
      let errorMessage = 'Failed to load locations. ';
      if (error.message && error.message.includes('HTML instead of JSON')) {
        errorMessage += 'API proxy may not be configured correctly. Please contact support.';
      } else {
        errorMessage += error.message || 'Please check console for details.';
      }
      
      noResults.textContent = errorMessage;
    }
  }
}

// Load countries from API and populate country code selectors
async function loadCountriesFromAPI() {
  try {
    // Determine base URL and proxy settings
    const { baseUrl, useProxy, isDevelopment } = getApiConfig();

    // Helper to log only in development
    const devLog = (...args) => {
      if (isDevelopment) console.log(...args);
    };
    const devWarn = (...args) => {
      if (isDevelopment) console.warn(...args);
    };
    const devError = (...args) => {
      if (isDevelopment) console.error(...args);
    };
    // Build URL for countries endpoint
    // Note: /api/ver3/ endpoints use boulders.brpsystems.com/apiserver base URL
    // In development, use Vite proxy which handles this
    // In production with proxy, the proxy handles the base URL mapping
    let url;
    if (useProxy) {
      url = `${baseUrl}?path=/api/ver3/services/countries`;
    } else if (isDevelopment) {
      // In development, use Vite proxy (relative URL)
      url = '/api/ver3/services/countries';
    } else {
      // Direct API call (shouldn't happen in production, but fallback)
      url = 'https://boulders.brpsystems.com/apiserver/api/ver3/services/countries';
    }
    
    devLog('[Countries] Fetching countries from:', url);
    
    const headers = {
      'Accept-Language': getAcceptLanguageHeader(),
      'Content-Type': 'application/json',
    };
    
    const response = await fetch(url, {
      method: 'GET',
      headers,
    });
    
    devLog('[Countries] Response status:', response.status, response.statusText);
    
    if (!response.ok) {
      const errorText = await response.text();
      devError(`[Countries] Failed to load countries (${response.status}):`, errorText);
      devWarn(`[Countries] Using default +45 only.`);
      return; // Fallback to default +45
    }
    
    const countries = await response.json();
    devLog('[Countries] Loaded countries from API:', countries);
    devLog('[Countries] Number of countries:', countries?.length || 0);
    
    if (!Array.isArray(countries) || countries.length === 0) {
      devWarn('[Countries] No countries returned from API. Using default +45 only.');
      return; // Fallback to default +45
    }
    
    // Sort countries alphabetically by name
    const sortedCountries = countries.sort((a, b) => {
      const nameA = (a.name || '').toLowerCase();
      const nameB = (b.name || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
    
    // Populate both country code selectors
    const countryCodeSelect = document.getElementById('countryCode');
    const parentCountryCodeSelect = document.getElementById('parentCountryCode');
    
    devLog('[Countries] Found selectors - countryCode:', !!countryCodeSelect, 'parentCountryCode:', !!parentCountryCodeSelect);
    
    if (!countryCodeSelect && !parentCountryCodeSelect) {
      devWarn('[Countries] Neither selector found in DOM. Retrying in 500ms...');
      // Retry after a delay in case DOM isn't ready yet (only retry once)
      if (!loadCountriesFromAPI.retryAttempted) {
        loadCountriesFromAPI.retryAttempted = true;
        setTimeout(() => {
          loadCountriesFromAPI();
        }, 500);
      }
      return;
    }
    
    // Reset retry flag on successful execution
    loadCountriesFromAPI.retryAttempted = false;
    
    if (countryCodeSelect) {
      populateCountrySelector(countryCodeSelect, sortedCountries);
      devLog('[Countries] Populated countryCode selector with', countryCodeSelect.options.length, 'options');
      // Add event listener to update flag when selection changes
      countryCodeSelect.addEventListener('change', () => {
        updateCountryFlagIcon(countryCodeSelect);
      });
    } else {
      devWarn('[Countries] countryCode selector not found in DOM');
    }
    
    if (parentCountryCodeSelect) {
      populateCountrySelector(parentCountryCodeSelect, sortedCountries);
      devLog('[Countries] Populated parentCountryCode selector with', parentCountryCodeSelect.options.length, 'options');
      // Add event listener to update flag when selection changes
      parentCountryCodeSelect.addEventListener('change', () => {
        updateCountryFlagIcon(parentCountryCodeSelect);
      });
    } else {
      devWarn('[Countries] parentCountryCode selector not found in DOM');
    }
    
    devLog('[Countries] Country selectors populated successfully');
  } catch (error) {
    devWarn('[Countries] Error loading countries from API:', error);
    // Fallback to default +45 - selectors already have this option
  }
}

// Store country data mapping for flag lookups
let countryDataMap = new Map(); // Maps phoneCountryCode (as string like "+45") to {alpha2, name, phoneCountryCode}

// Helper function to populate a country selector with countries
function populateCountrySelector(selectElement, countries) {
  if (!selectElement || !Array.isArray(countries)) return;
  
  // Store current selected value
  const currentValue = selectElement.value || '+45';
  
  // Clear existing options (except keep the structure)
  selectElement.innerHTML = '';
  
  // Clear and rebuild country data map
  countryDataMap.clear();
  
  // Add countries as options
  countries.forEach(country => {
    if (country.phoneCountryCode != null) {
      const option = document.createElement('option');
      const phoneCode = `+${country.phoneCountryCode}`;
      option.value = phoneCode;
      // Format: "+45 Denmark" or "+45" if no name
      const countryName = country.name || country.alpha2 || '';
      option.textContent = countryName ? `${phoneCode} ${countryName}` : phoneCode;
      
      // Store data attribute for flag lookup
      if (country.alpha2) {
        option.dataset.alpha2 = country.alpha2;
        // Store in map for quick lookup
        countryDataMap.set(phoneCode, {
          alpha2: country.alpha2,
          name: country.name,
          phoneCountryCode: country.phoneCountryCode
        });
      }
      
      selectElement.appendChild(option);
    }
  });
  
  // Set default selection to +45 (Denmark) if available, otherwise keep current or select first
  if (selectElement.querySelector('option[value="+45"]')) {
    selectElement.value = '+45';
  } else if (selectElement.options.length > 0) {
    selectElement.value = selectElement.options[0].value;
  }
  
  // If current value exists, try to restore it
  if (currentValue && selectElement.querySelector(`option[value="${currentValue}"]`)) {
    selectElement.value = currentValue;
  }
  
  // Update flag icon for the selected value
  updateCountryFlagIcon(selectElement);
}

// Helper function to update flag icon based on selected country code
function updateCountryFlagIcon(selectElement) {
  if (!selectElement) return;
  
  const selectedValue = selectElement.value;
  const flagIcon = selectElement.closest('.country-selector')?.querySelector('.flag-icon');
  
  if (!flagIcon) return;
  
  // Find the selected option
  const selectedOption = selectElement.querySelector(`option[value="${selectedValue}"]`);
  const alpha2 = selectedOption?.dataset.alpha2 || countryDataMap.get(selectedValue)?.alpha2;
  
  if (alpha2) {
    flagIcon.textContent = getFlagEmoji(alpha2);
  } else {
    // Fallback: try to infer from phone code (common countries)
    const phoneCode = selectedValue.replace('+', '');
    const commonFlags = {
      '45': 'DK', // Denmark
      '46': 'SE', // Sweden
      '47': 'NO', // Norway
      '358': 'FI', // Finland
      '49': 'DE', // Germany
      '33': 'FR', // France
      '44': 'GB', // United Kingdom
      '1': 'US', // United States
    };
    
    const inferredAlpha2 = commonFlags[phoneCode];
    if (inferredAlpha2) {
      flagIcon.textContent = getFlagEmoji(inferredAlpha2);
    } else {
      flagIcon.textContent = '🌍'; // Default globe emoji
    }
  }
}


// Find nearest gym using geolocation
async function findNearestGym() {
  const locationBtn = document.getElementById('findNearestGym');
  const locationStatus = document.getElementById('locationStatus');
  
  if (!locationBtn || !locationStatus) return;
  
  // Check if location is already active - toggle it off
  if (userLocation && locationBtn.classList.contains('active')) {
    devLog('[Geolocation] Toggling location off, restoring default order');
    
    // Clear user location
    userLocation = null;
    gymsWithDistances = [];
    
    // Remove active state and loading
    locationBtn.classList.remove('active');
    locationBtn.classList.remove('loading');
    
    // Reload gyms in default order (without distance sorting)
    await loadGymsFromAPI();
    
    return;
  }
  
  // Check if geolocation is supported
  if (!isGeolocationAvailable()) {
    locationStatus.style.display = 'none';
    return;
  }
  
  // Update button state - show loading
  locationBtn.disabled = true;
  locationBtn.classList.add('loading');
  
  // Hide status text
  locationStatus.style.display = 'none';
  
  try {
    // Trigger geolocation immediately from user gesture, then await permission info
    const permissionPromise = checkGeolocationPermission();
    const locationPromise = getUserLocation({ logger: geoLogger });
    const [permissionStatus, location] = await Promise.all([permissionPromise, locationPromise]);
    devLog('[Geolocation] Permission status before request:', permissionStatus);
    userLocation = location;
    
    devLog('User location:', location);
    
    // Hide status text
    locationStatus.style.display = 'none';
    
    // Reload gyms with distance sorting
    await loadGymsFromAPI();
    
    // Highlight icon button (add active class)
    locationBtn.classList.add('active');
    locationBtn.classList.remove('loading');
    locationBtn.disabled = false;
    
  } catch (error) {
    // Log location errors as warnings (not errors) since they're handled gracefully
    // Location unavailable/timeout is common in testing/dev environments, especially on macOS
    if (error.type === 'unavailable') {
      console.warn('[Geolocation] Location unavailable:', error.message);
    } else if (error.type === 'timeout') {
      console.warn('[Geolocation] Location timeout (common on macOS when CoreLocation cannot determine position):', error.message);
    } else if (error.type === 'permission-denied') {
      console.warn('[Geolocation] Location permission denied:', error.message);
    } else {
      console.warn('[Geolocation] Location error:', error.message);
    }
    
    // Show helpful error message based on error type
    let errorMessage = error.message;
    let showHelp = false;
    
    if (error.type === 'permission-denied') {
      errorMessage = 'Location access was denied. Please allow location access in your browser settings and try again.';
      showHelp = true;
    } else if (error.type === 'unavailable') {
      // Provide macOS-specific troubleshooting
      const isMacOS = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      if (isMacOS) {
        devLog('%c[Geolocation Troubleshooting]', 'color: #F401F5; font-weight: bold;');
        devLog('macOS CoreLocation cannot determine your position. Try:');
        devLog('1. System Settings → Privacy & Security → Location Services (enable)');
        devLog('2. Grant location permission to your browser');
        devLog('3. Enable WiFi (needed for macOS location, even if not connected)');
        devLog('4. Reset browser location permissions');
        devLog('5. Restart your browser');
        errorMessage = 'Location unavailable on macOS. Check: System Settings → Privacy → Location Services, enable WiFi, and grant browser permission.';
      } else {
        errorMessage = 'Location information is unavailable. Please ensure location services are enabled on your device.';
      }
      showHelp = true;
    } else if (error.type === 'timeout') {
      // Timeout errors on macOS are often CoreLocation issues - be more helpful
      if (error.isMacOS) {
        errorMessage = 'Location request timed out. On macOS, this usually means CoreLocation cannot determine your position. The "Find nearest gym" feature may not be available in this environment. You can still browse all gyms normally.';
      } else {
        errorMessage = 'Location request timed out. Please check your connection and try again.';
      }
      showHelp = true;
    }
    
    // Hide status text (no error messages shown)
    locationStatus.style.display = 'none';
    
    // Remove active state and loading from button
    locationBtn.classList.remove('active');
    locationBtn.classList.remove('loading');
    locationBtn.disabled = false;
  }
}

// Create gym item element from API data
function createGymItem(gym, isNearest = false) {
  const gymItem = document.createElement('div');
  gymItem.className = 'gym-item';
  if (isNearest) {
    gymItem.classList.add('nearest-gym');
  }
  gymItem.setAttribute('data-gym-id', `gym-${gym.id}`);
  
  const address = gym.address;
  // Break address before zipcode - street on first line, zipcode + city on second
  const addressStreet = address.street;
  const addressPostalCity = `${address.postalCode} ${address.city}`;
  
  // Add distance badge if available
  const distanceBadge = gym.distance !== null && gym.distance !== undefined
    ? `<div class="gym-distance-badge">${formatDistance(gym.distance)}</div>`
    : '';
  
  // Add nearest badge if this is the nearest gym (positioned absolutely in top right)
  const nearestBadge = isNearest
    ? `<div class="nearest-badge">${t('gym.nearest')}</div>`
    : '';
  
  gymItem.innerHTML = sanitizeHTML(`
    ${nearestBadge}
    ${distanceBadge}
    <div class="gym-info">
      <div class="gym-name">${gym.name}</div>
      <div class="gym-details">
        <div class="gym-address">
          <div class="gym-address-street">${addressStreet}</div>
          <div class="gym-address-postal-city">${addressPostalCity}</div>
        </div>
      </div>
    </div>
    <div class="check-circle"></div>
  `);
  
  return gymItem;
}

// Setup event listeners for gym items
function setupGymEventListeners() {
  const gymItems = document.querySelectorAll('.gym-item');
  gymItems.forEach(item => {
    item.addEventListener('click', (e) => {
      handleGymSelection(item);
    });
  });
}


// Track pending navigation timeouts to prevent double-clicks and stale state
const pendingNavigationTimeouts = {
  gym: null,
  plan: null,
  membership: null,
  punchcard: null
};

// Language management
const SUPPORTED_LANGUAGES = {
  'da-DK': { code: 'da-DK', name: 'Dansk', flag: '🇩🇰' },
  'en-GB': { code: 'en-GB', name: 'English', flag: '🇬🇧' }
};

const DEFAULT_LANGUAGE = 'da-DK';

function getStoredLanguage() {
  try {
    const stored = localStorage.getItem('selectedLanguage');
    return stored && SUPPORTED_LANGUAGES[stored] ? stored : DEFAULT_LANGUAGE;
  } catch {
    return DEFAULT_LANGUAGE;
  }
}

function setStoredLanguage(languageCode) {
  try {
    if (SUPPORTED_LANGUAGES[languageCode]) {
      localStorage.setItem('selectedLanguage', languageCode);
      return true;
    }
  } catch {
    // Ignore localStorage errors
  }
  return false;
}

function getAcceptLanguageHeader() {
  return state.language || DEFAULT_LANGUAGE;
}

function getReturnUrlBase() {
  let override = null;
  try {
    override = window?.BOULDERS_RETURN_URL_BASE || localStorage.getItem('boulders_return_url_base');
  } catch (e) {
    // Ignore localStorage errors
  }

  if (override) {
    return String(override).replace(/\/$/, '');
  }

  const isHttps = window.location.protocol === 'https:';
  if (isHttps) {
    return window.location.origin.replace('http://', 'https://');
  }

  // Local HTTP is not accepted by payment API, default to production
  return 'https://join.boulders.dk';
}

const state = {
  currentStep: 1,
  language: getStoredLanguage(), // Current selected language
  selectedGymId: null,
  selectedBusinessUnit: null, // Step 3: Store chosen business unit for API requests
  selectedGymName: null, // Store selected gym name for display
  landingRouteConfig: null, // Active landing route config for path-specific product surfacing
  landingMatchedProducts: [], // Products matched by active landing route label
  currentAuthMode: null, // Track current auth mode (login/create)
  membershipPlanId: null,
  valueCardQuantities: new Map(),
  addonIds: new Set(),
  totals: {
    cartTotal: 0,
    membershipMonthly: 0,
    discountAmount: 0, // Discount amount applied
    subtotal: 0, // Subtotal before discount
  },
  cartItems: [],
  billingPeriod: '',
  forms: {},
  order: null, // Order summary object for confirmation view (created by buildOrderSummary)
  fullOrder: null, // Full order object from API (includes subscriptionItems, price, etc.) - used for payment overview
  orderId: null, // Step 7: Created order ID
  customerId: null, // Step 6: Created customer ID (for membership ID display)
  paymentFailed: false, // Flag to track if payment failed (prevents success page from showing)
  paymentPending: false, // Flag to track if payment is pending (prevents success page from showing)
  paymentConfirmed: false, // Flag to track if payment is confirmed (allows success page to show)
  authenticatedEmail: null,
  authenticatedCustomer: null, // Full customer profile data from API
  checkoutInProgress: false, // Flag to prevent duplicate checkout attempts
  loginInProgress: false, // Prevent duplicate login submissions
  paymentMethod: null,
  // Step 9: Payment link state
  paymentLink: null, // Generated payment link for checkout
  paymentLinkGenerated: false, // Flag indicating if payment link has been generated
  // Discount code state
  discountCode: null, // Applied discount code
  discountApplied: false, // Whether discount is currently applied
  // Email tracking to prevent duplicate account creation
  createdEmails: new Set(), // Track emails that have been used to create accounts in this session
  // Step 5: Store fetched products from API
  campaignSubscriptions: [], // Fetched campaign subscription products (with "PublicCampaign" label)
  campaignValueCards: [], // Fetched campaign value card products (with "PublicCampaign" label)
  campaignEndDate: null, // ISO string or Date for countdown; if null, uses end of current month
  subscriptions: [], // Fetched membership products (with "Public" label, excluding "PublicCampaign" and "15-Day Trial Pass")
  dayPassSubscriptions: [], // Fetched 15-Day Trial Pass products (with "15-Day Trial Pass" label)
  valueCards: [], // Fetched punch card products
  subscriptionAdditions: [], // Fetched add-ons for selected membership
  boostProducts: [], // Products with "boostProduct" label for boost modal
  selectedProductType: null, // 'membership' or 'punch-card'
  selectedProductId: null, // The actual product ID from API
  selectedAddonIds: [], // Array of selected add-on product IDs
  // Step 4: Reference data cache
  referenceData: {}, // Cached reference/lookup data (countries, regions, currencies, etc.)
  referenceDataLoaded: false, // Flag to track if reference data has been loaded
  subscriptionAttachedOrderId: null, // Tracks which order already has the membership attached
  subscriptionItemId: null, // Subscription item ID from order - used to link addons via additionTo
  // 15-day pass: activation date (null = today, or YYYY-MM-DD string for future start)
  subscriptionStartDate: null,
  checkoutConfirmAccepted: false,
  checkoutConfirmPreviousFocus: null,
  checkoutConfirmPendingAfterEdit: false,
  // Test mode for success page
  testMode: false, // Flag to enable test mode for success page (?testSuccess=true)
  testProductType: null, // Product type for test mode (membership, 15daypass, punch-card)
};

let orderCreationPromise = null;
let subscriptionAttachPromise = null;
let productsLoadPromise = null;
let tokenValidationCooldownUntil = 0;
let gymLoadCooldownUntil = 0;
let gymLoadRetryTimeoutId = null;
let customerSyncCooldownUntil = 0;
let loginCooldownUntil = 0;
let campaignCountdownIntervalId = null;
let activationDatePeriodHintTimeoutId = null;
let campaignGuardCustomerRefreshPromise = null;
let accessStepRebindRafId = null;
const subscriptionDetailPromises = new Map();
const subscriptionDetailHydratedIds = new Set();

function scheduleAccessStepRebind() {
  if (accessStepRebindRafId != null) {
    cancelAnimationFrame(accessStepRebindRafId);
  }
  accessStepRebindRafId = requestAnimationFrame(() => {
    accessStepRebindRafId = null;
    try {
      setupNewAccessStep();
      updatePageTranslations();
    } catch (e) {
      console.warn('[Step 2] setupNewAccessStep failed (scheduled):', e);
    }
  });
}

// Campaign countdown end date: set here when API doesn't send Omsætningsperiode. Use ISO string or null for "end of current month".
const CAMPAIGN_COUNTDOWN_END_DATE_FALLBACK = '2026-02-16'; // e.g. '2026-02-15T23:59:59.999' or '2026-02-15'

function getCampaignCountdownEndDate() {
  const fromState = state.campaignEndDate;
  if (fromState) {
    const d = typeof fromState === 'string' ? new Date(fromState) : fromState;
    if (!isNaN(d.getTime())) return d;
  }
  if (CAMPAIGN_COUNTDOWN_END_DATE_FALLBACK) {
    const d = new Date(CAMPAIGN_COUNTDOWN_END_DATE_FALLBACK);
    if (!isNaN(d.getTime())) return d;
  }
  const now = new Date();
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
  return end;
}

function updateCampaignCountdown() {
  const el = document.getElementById('campaignCountdown');
  const valueEl = document.getElementById('campaignCountdownValue');
  const endDate = getCampaignCountdownEndDate();
  const now = new Date();
  const diff = endDate.getTime() - now.getTime();
  const timeLeft = diff > 0;
  const parts = timeLeft ? (() => {
    const days = Math.floor(diff / (24 * 60 * 60 * 1000));
    const hours = Math.floor((diff % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
    const minutes = Math.floor((diff % (60 * 60 * 1000)) / (60 * 1000));
    const seconds = Math.floor((diff % (60 * 1000)) / 1000);
    const p = [];
    if (days > 0) p.push(days + (days === 1 ? 'd' : 'd'));
    p.push(String(hours).padStart(2, '0') + 'h', String(minutes).padStart(2, '0') + 'm', String(seconds).padStart(2, '0') + 's');
    return p.join(' ');
  })() : '';
  if (!timeLeft) {
    if (el) el.style.display = 'none';
    if (campaignCountdownIntervalId) {
      clearInterval(campaignCountdownIntervalId);
      campaignCountdownIntervalId = null;
    }
    return;
  }
  if (valueEl) valueEl.textContent = parts;
  if (el) el.style.display = '';
}

function startCampaignCountdown() {
  if (campaignCountdownIntervalId) {
    clearInterval(campaignCountdownIntervalId);
    campaignCountdownIntervalId = null;
  }
  updateCampaignCountdown();
  campaignCountdownIntervalId = setInterval(updateCampaignCountdown, 1000);
}

function stopCampaignCountdown() {
  if (campaignCountdownIntervalId) {
    clearInterval(campaignCountdownIntervalId);
    campaignCountdownIntervalId = null;
  }
  const el = document.getElementById('campaignCountdown');
  if (el) el.style.display = 'none';
}

function isUserAuthenticated() {
  return typeof window.getAccessToken === 'function' && Boolean(window.getAccessToken());
}

function getTokenMetadata() {
  if (typeof window.getTokenMetadata === 'function') {
    return window.getTokenMetadata();
  }
  return null;
}

function isLocalDevHost() {
  const host = window.location.hostname;
  return host === 'localhost' || host === '127.0.0.1';
}

async function syncAuthenticatedCustomerState(username = null, email = null) {
  const metadata = getTokenMetadata();
  const resolvedUsername = username || state.customerId || metadata?.username || metadata?.userName;
  const resolvedEmail = email || state.authenticatedEmail || metadata?.email;

  if (resolvedUsername) {
    state.customerId = String(resolvedUsername);
  }

  if (resolvedEmail) {
    state.authenticatedEmail = resolvedEmail;
  }

  // Fetch customer profile if we have customer ID and access token
  // Always fetch to ensure we have complete profile data (not just partial form data)
  if (state.customerId && isUserAuthenticated()) {
    const now = Date.now();
    if (isLocalDevHost() && now < customerSyncCooldownUntil) {
      const secondsLeft = Math.ceil((customerSyncCooldownUntil - now) / 1000);
      console.log(`[Auth] Skipping customer profile refresh (cooldown ${secondsLeft}s remaining)`);
      refreshLoginUI();
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event('auth-state-changed'));
      }
      if (state.currentStep === 4) {
        updatePaymentOverview();
      }
      return;
    }

    try {
      const customerData = await authAPI.getCustomer(state.customerId);
      state.authenticatedCustomer = customerData;
      console.log('[Auth] Customer profile loaded:', customerData);
      // Refresh UI after loading profile to show all fields
      refreshLoginUI();
    } catch (profileError) {
      if (isLocalDevHost() && isRateLimitError(profileError)) {
        const retryMs = getRetryDelayFromError(profileError);
        customerSyncCooldownUntil = Date.now() + retryMs;
        console.warn(`[Auth] Customer profile request rate limited. Cooling down for ${Math.ceil(retryMs / 1000)}s`);
      }
      console.warn('[Auth] Could not fetch customer profile:', profileError);
      // Continue even if profile fetch fails - refresh UI with whatever data we have
      refreshLoginUI();
    }
  } else {
    refreshLoginUI();
  }
  
  // Dispatch event to notify React components of auth state change
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('auth-state-changed'));
  }
  // Do not auto-create orders/items on auth sync.
  // Order creation must only happen after explicit checkout action.
  if (state.currentStep === 4) {
    updatePaymentOverview();
  }
}

const DOM = {};
const templates = {};
const TOTAL_STEPS = 5;
const buttonGlareTimeouts = new WeakMap();
const carouselResizeObservers = new WeakMap();
const carouselScrollHandlers = new WeakMap();
const carouselResizeFallbacks = new WeakMap();

// Determine whether a membership (not punch card) is currently selected
function isMembershipSelected() {
  const id = state.membershipPlanId;
  if (!id) return false;
  return !String(id).includes('punch');
}

// Interstitial Add-ons Modal (shown after selecting membership on step 2)
let addonsModal = null;
let addonLightbox = null; // full-size image overlay (z-index above modal)
function defaultAddonsImage() {
  // Simple dark gradient SVG placeholder with label
  const svg = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="800" height="1000">
  <defs>
    <linearGradient id="g" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stop-color="#0f172a"/>
      <stop offset="100%" stop-color="#0b0f1a"/>
    </linearGradient>
  </defs>
  <rect width="100%" height="100%" fill="url(#g)"/>
  <g fill="#22d3ee" opacity="0.08">
    <circle cx="120" cy="160" r="80"/>
    <circle cx="360" cy="280" r="60"/>
    <circle cx="640" cy="180" r="90"/>
    <circle cx="240" cy="520" r="70"/>
    <circle cx="560" cy="720" r="100"/>
  </g>
  <text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="#e5e7eb" font-size="36" font-family="Inter, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial">Boost your membership</text>
  <text x="50%" y="58%" dominant-baseline="middle" text-anchor="middle" fill="#94a3b8" font-size="18" font-family="Inter, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial">Add shoes, chalk and more</text>
</svg>`;
  return 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg);
}

function getProductPlaceholderImage() {
  // Placeholder image for individual product cards (consistent aspect ratio)
  const svg = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="400" height="300" viewBox="0 0 400 300">
  <defs>
    <linearGradient id="productGrad" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stop-color="#1e293b"/>
      <stop offset="100%" stop-color="#0f172a"/>
    </linearGradient>
  </defs>
  <rect width="100%" height="100%" fill="url(#productGrad)"/>
  <g fill="#64748b" opacity="0.3">
    <rect x="150" y="100" width="100" height="100" rx="8"/>
  </g>
  <text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="#64748b" font-size="14" font-family="Inter, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial">No image</text>
</svg>`;
  return 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg);
}
function ensureAddonsModal() {
  if (addonsModal) return addonsModal;
  const overlay = document.createElement('div');
  overlay.className = 'addons-overlay';
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  
  // Modal styles are defined in styles.css (no inline style injection)

  const sheet = document.createElement('div');
  sheet.className = 'addons-sheet';

  const contentWrap = document.createElement('div');
  contentWrap.className = 'addons-content';

  // Left image column (optional)
  // Image column removed per request

  const header = document.createElement('div');
  header.className = 'addons-header';
  const title = document.createElement('h3');
  title.textContent = t('addons.modal.title');
  title.className = 'addons-title';
  const closeBtn = document.createElement('button');
  closeBtn.setAttribute('aria-label', t('addons.modal.close'));
  closeBtn.setAttribute('type', 'button');
  closeBtn.textContent = '\u00D7';
  closeBtn.className = 'addons-close';
  closeBtn.addEventListener('click', () => hideAddonsModal());
  header.appendChild(title);
  header.appendChild(closeBtn);

  const grid = document.createElement('div');
  grid.className = 'addons-grid';
  grid.setAttribute('data-modal-addons-grid', '');

  const actions = document.createElement('div');
  actions.className = 'addons-actions';
  const hint = document.createElement('div');
  hint.textContent = t('addons.modal.hint');
  hint.className = 'addons-hint';
  
  // Single dynamic button
  const actionButton = document.createElement('button');
  actionButton.textContent = t('addons.modal.skip');
  actionButton.className = 'addons-action-btn addons-skip';
  actionButton.setAttribute('data-i18n-key', 'addons.modal.skip');
  actionButton.addEventListener('click', () => handleAddonAction());
  
  const rightActions = document.createElement('div');
  rightActions.className = 'addons-actions-right';
  rightActions.appendChild(actionButton);
  actions.appendChild(hint);
  actions.appendChild(rightActions);

  const contentCol = document.createElement('div');
  contentCol.className = 'addons-main';
  contentCol.appendChild(header);
  contentCol.appendChild(grid);
  contentCol.appendChild(actions);

  // Image column removed
  contentWrap.appendChild(contentCol);
  sheet.appendChild(contentWrap);
  overlay.appendChild(sheet);
  
  // Add click-outside-to-close functionality
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) {
      hideAddonsModal();
    }
  });
  
  document.body.appendChild(overlay);

  // Single lightbox for addon images (appended to body, above modal)
  if (!addonLightbox) {
    const lb = document.createElement('div');
    lb.className = 'addon-lightbox';
    lb.setAttribute('role', 'dialog');
    lb.setAttribute('aria-modal', 'true');
    lb.setAttribute('aria-label', t('addons.modal.close') || 'Close image');
    lb.innerHTML = '<button type="button" class="addon-lightbox-close" aria-label="' + (t('addons.modal.close') || 'Close') + '">×</button><img class="addon-lightbox-img" alt="" />';
    const lbImg = lb.querySelector('.addon-lightbox-img');
    const lbClose = lb.querySelector('.addon-lightbox-close');
    lbClose.addEventListener('click', (e) => { e.stopPropagation(); closeAddonLightbox(); });
    lb.addEventListener('click', () => closeAddonLightbox());
    lbImg.addEventListener('click', (e) => e.stopPropagation());
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && addonLightbox && addonLightbox.classList.contains('addon-lightbox--active')) closeAddonLightbox(); });
    document.body.appendChild(lb);
    addonLightbox = lb;
  }

  addonsModal = overlay;
  return addonsModal;
}

function openAddonLightbox(imageSrc, focusReturnEl) {
  if (!addonLightbox) ensureAddonsModal();
  const img = addonLightbox.querySelector('.addon-lightbox-img');
  const closeBtn = addonLightbox.querySelector('.addon-lightbox-close');
  if (img) img.src = imageSrc || '';
  addonLightbox._focusReturn = focusReturnEl || null;
  addonLightbox.classList.add('addon-lightbox--active');
  addonLightbox.style.display = 'flex';
  if (closeBtn) closeBtn.focus();
}

function closeAddonLightbox() {
  if (!addonLightbox) return;
  addonLightbox.classList.remove('addon-lightbox--active');
  addonLightbox.style.display = 'none';
  const ret = addonLightbox._focusReturn;
  if (ret && typeof ret.focus === 'function') ret.focus();
  addonLightbox._focusReturn = null;
}

function populateAddonsModal() {
  ensureAddonsModal();
  const grid = addonsModal.querySelector('[data-modal-addons-grid]');
  if (!grid) return;
  grid.innerHTML = '';
  const addons = Array.isArray(state.subscriptionAdditions)
    ? state.subscriptionAdditions
    : [];

  // If duplicate add-ons share the same display name (e.g. multiple "Kaffekort"),
  // only keep the cheapest option in the modal list.
  const addonsByName = new Map();
  addons.forEach((addon) => {
    const displayName = (addon?.addition?.name || addon?.name || '').trim();
    const key = displayName.toLowerCase();
    if (!key) return;
    const price = roundToHalfKrone(getAddonPrice(addon));
    const existing = addonsByName.get(key);
    if (!existing || price < existing.price) {
      addonsByName.set(key, { addon, price });
    }
  });
  const addonsToRender = addonsByName.size > 0
    ? Array.from(addonsByName.values()).map((entry) => entry.addon)
    : addons;

  if (addonsToRender.length === 0) {
    return;
  }
  if (!templates.addon) {
    // Fallback simple cards if template missing
    addonsToRender.forEach((addon) => {
      const card = document.createElement('div');
      card.className = 'plan-card addon-card';
      card.style.cursor = 'pointer';
      const addonPrice = getAddonPrice(addon);
      const addToCartBtn = document.createElement('button');
      addToCartBtn.className = 'addon-add-to-cart-btn';
      addToCartBtn.setAttribute('data-action', 'toggle-addon');
      addToCartBtn.setAttribute('data-addon-id', addon.id);
      addToCartBtn.textContent = t('button.addToCart');
      addToCartBtn.setAttribute('data-i18n-key', 'button.addToCart');
      
      card.innerHTML = sanitizeHTML(`
        <div style="font-weight:600">${addon.name}</div>
        <div>${addonPrice > 0
          ? formatPriceHalfKrone(roundToHalfKrone(addonPrice))
          : '—'} kr.</div>
      `);
      card.appendChild(addToCartBtn);
      
      // Make entire card clickable
      card.addEventListener('click', (e) => {
        // Don't trigger if clicking the button itself
        if (e.target === addToCartBtn || addToCartBtn.contains(e.target)) {
          return;
        }
        // Toggle the addon when clicking anywhere on the card
        if (addon.id) toggleAddon(addon.id, addToCartBtn);
      });
      
      grid.appendChild(card);
    });
    return;
  }
  // Use existing add-on template for consistency (list layout: main row + expandable details)
  addonsToRender.forEach((addon, index) => {
    const card = templates.addon.content.firstElementChild.cloneNode(true);
    const addToCartBtn = card.querySelector('[data-action="toggle-addon"]');
    if (addToCartBtn) addToCartBtn.dataset.addonId = addon.id;
    const thumbWrapper = card.querySelector('.addon-card-thumb') || card.querySelector('[data-action="open-lightbox"]');
    const nameEl = card.querySelector('[data-element="name"]');
    const originalPriceEl = card.querySelector('[data-element="originalPrice"]');
    const discountedPriceEl = card.querySelector('[data-element="discountedPrice"]');
    const priceBannerEl = card.querySelector('[data-element="priceBanner"]');
    const descriptionEl = card.querySelector('[data-element="description"]');
    const featuresEl = card.querySelector('[data-element="features"]');
    const imageEl = card.querySelector('[data-element="image"]');
    const imageBannerEl = card.querySelector('[data-element="imageBanner"]');
    const addonData = addon.addition || addon;
    if (nameEl) nameEl.textContent = addonData.name || addon.name || '';
    // Extract and display product image
    if (imageEl) {
      // Check if addon has assets (from API product) or imageUrl (from hardcoded)
      let imageUrl = null;
      if (addon.assets && Array.isArray(addon.assets) && addon.assets.length > 0) {
        console.log('[Addons Modal] Addon assets:', addon.name, addon.assets);
        // Look for MAIN or CENTERED asset type
        const mainAsset = addon.assets.find(asset => 
          asset.type === 'MAIN' || asset.type === 'CENTERED'
        ) || addon.assets[0]; // Fallback to first asset if no MAIN/CENTERED
        
        // AssetOut has contentUrl field (per OpenAPI spec)
        // Use contentUrl if available, otherwise fall back to constructing from reference
        imageUrl = mainAsset?.contentUrl || mainAsset?.url || null;
        
        // If no contentUrl, construct from reference ID
        if (!imageUrl && mainAsset?.reference) {
          // Construct asset URL from reference ID
          const referenceId = mainAsset.reference;
          const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
          const isCloudflarePages = window.location.hostname.includes('pages.dev') ||
                                   window.location.hostname.includes('join.boulders.dk') ||
                                   window.location.hostname === 'boulders.dk';
          
          if (isDevelopment) {
            imageUrl = `/api-proxy?path=/api/assets/${referenceId}`;
          } else if (isCloudflarePages) {
            imageUrl = `/api-proxy?path=/api/assets/${referenceId}`;
          } else {
            imageUrl = `https://api-join.boulders.dk/api/assets/${referenceId}`;
          }
          console.log('[Addons Modal] Constructed asset URL from reference:', referenceId, '->', imageUrl);
        }
      } else if (addon.imageUrl) {
        imageUrl = addon.imageUrl;
      }
      
      console.log('[Addons Modal] Image URL for', addon.name, ':', imageUrl);
      
      // Always show image element - use placeholder if no image URL
      imageEl.removeAttribute('style'); // Remove inline style that hides it
      imageEl.style.display = 'block';
      imageEl.style.visibility = 'visible';
      imageEl.style.opacity = '1';
      imageEl.style.width = '100%';
      // Image size controlled by CSS (.addons-grid .addon-card img max-height by breakpoint)
      imageEl.style.objectFit = 'cover';
      imageEl.style.borderRadius = '8px';
      imageEl.style.marginBottom = '12px';
      imageEl.style.backgroundColor = '#1e293b'; // Background color for placeholder
      imageEl.classList.add('addon-image');
      
      if (imageUrl) {
        imageEl.src = imageUrl;
        imageEl.alt = addon.name || 'Addon image';
        console.log('[Addons Modal] Image set for', addon.name, '- src:', imageEl.src);
        console.log('[Addons Modal] Image computed display:', window.getComputedStyle(imageEl).display);
      } else {
        // Use placeholder image when no image is available
        imageEl.src = getProductPlaceholderImage();
        imageEl.alt = (addon.name || 'Addon') + ' - No image available';
        console.log('[Addons Modal] Using placeholder image for', addon.name);
      }
      
      // Display media banner text above price (workaround for original/list price payload gaps).
      const addonBannerText = getAddonMediaBannerText(addon);
      if (priceBannerEl && addonBannerText) {
        priceBannerEl.textContent = addonBannerText;
        priceBannerEl.style.display = '';
        console.log('[Addons Modal] Price banner text set for', addon.name, ':', addonBannerText);
      } else if (priceBannerEl) {
        priceBannerEl.style.display = 'none';
      }
      if (imageBannerEl) imageBannerEl.style.display = 'none';
      if (thumbWrapper && imageEl) {
        thumbWrapper.addEventListener('click', (e) => {
          e.stopPropagation();
          if (imageEl.src) openAddonLightbox(imageEl.src, imageEl);
        });
      }
      imageEl.style.width = '';
      imageEl.style.marginBottom = '';
    } else {
      console.log('[Addons Modal] Image element not found for', addon.name);
    }
    
    // Use helper function for discounted/current price
    const addonPrice = getAddonPrice(addon);

    // Hide legacy strikethrough original price in addon modal cards.
    if (originalPriceEl) {
      originalPriceEl.style.display = 'none';
    }
    if (discountedPriceEl) {
      discountedPriceEl.textContent = addonPrice > 0
        ? formatPriceHalfKrone(roundToHalfKrone(addonPrice))
        : '—';
    }
    if (descriptionEl) {
      const description = addonData.description || addon.description || '';
      if (description) {
        // Preserve line breaks from backend - escape HTML and preserve newlines
        const descriptionHtml = description
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/\n/g, '<br>');
        descriptionEl.innerHTML = sanitizeHTML(descriptionHtml);
      } else {
        descriptionEl.textContent = '';
      }
    }
    const descToggle = card.querySelector('.addon-card-description-toggle');
    if (descToggle) {
      const hasDescription = (addonData.description || addon.description || '').trim().length > 0;
      descToggle.style.display = hasDescription ? '' : 'none';
      if (hasDescription) {
        const descId = 'addon-desc-' + (addon.id || index);
        if (descriptionEl) descriptionEl.id = descId;
        descToggle.setAttribute('aria-controls', descId);
        descToggle.setAttribute('aria-label', t('addons.modal.showDescription') || 'Show full description');
        descToggle.title = t('addons.modal.showDescription') || 'Show full description';
      }
      descToggle.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const expanded = card.classList.toggle('expanded');
        descToggle.setAttribute('aria-expanded', String(expanded));
        descToggle.setAttribute('aria-label', expanded ? (t('addons.modal.collapseDescription') || 'Collapse description') : (t('addons.modal.showDescription') || 'Show full description'));
        descToggle.title = expanded ? (t('addons.modal.collapseDescription') || 'Collapse description') : (t('addons.modal.showDescription') || 'Show full description');
      });
    }
    if (featuresEl) {
      featuresEl.innerHTML = '';
      if (addon.features && Array.isArray(addon.features) && addon.features.length > 0) {
        addon.features.forEach((feature) => {
          const li = document.createElement('li');
          li.textContent = feature;
          featuresEl.appendChild(li);
        });
      }
    }
    
    // Features are now directly in the card, no plan-meta container
    
    // Update button text based on initial state
    if (addToCartBtn) {
      const isSelected = state.addonIds.has(addon.id);
      addToCartBtn.textContent = isSelected ? t('button.added') : t('button.addToCart');
      addToCartBtn.setAttribute('data-i18n-key', isSelected ? 'button.added' : 'button.addToCart');
      if (isSelected) {
        card.classList.add('selected');
      }
    }
    
    const mainRow = card.querySelector('.addon-card-main');
    card.style.cursor = 'pointer';
    (mainRow || card).addEventListener('click', (e) => {
      if (e.target === addToCartBtn || addToCartBtn.contains(e.target)) return;
      if (thumbWrapper && (e.target === thumbWrapper || thumbWrapper.contains(e.target))) return;
      const descToggleEl = card.querySelector('.addon-card-description-toggle');
      if (descToggleEl && (e.target === descToggleEl || descToggleEl.contains(e.target))) return;
      if (addon.id) toggleAddon(addon.id, addToCartBtn);
    });
    
    grid.appendChild(card);
  });
}

function showAddonsModal() {
  ensureAddonsModal();
  populateAddonsModal();
  updateAddonActionButton();
  
  // Show modal - overlay dims immediately, only sheet animates
  addonsModal.style.display = 'flex';
  addonsModal.style.alignItems = 'center';
  addonsModal.style.justifyContent = 'center';
  addonsModal.style.opacity = '1'; // Overlay appears immediately (no animation)
  addonsModal.style.transition = 'opacity 0.2s ease'; // Quick fade for overlay only
  
  // Prevent scrolling behind modal
  document.body.classList.add('modal-open');
  document.body.style.overflow = 'hidden';
  document.body.style.position = 'fixed';
  document.body.style.width = '100%';
  document.body.style.height = '100%';
  
  // Animate only the modal sheet, not the overlay
  const sheet = addonsModal.querySelector('.addons-sheet');
  if (sheet) {
    sheet.style.opacity = '0';
    sheet.style.transform = 'scale(0.95)';
    sheet.style.transition = 'opacity 0.3s ease, transform 0.3s ease';
    
    requestAnimationFrame(() => {
      sheet.style.opacity = '1';
      sheet.style.transform = 'scale(1)';
    });
  }
}

function hideAddonsModal() {
  if (!addonsModal) return;
  addonsModal.style.display = 'none';
  // Clear sheet inline styles so next open uses CSS only (no legacy position/transform)
  const sheet = addonsModal.querySelector('.addons-sheet');
  if (sheet) {
    sheet.style.position = '';
    sheet.style.left = '';
    sheet.style.top = '';
    sheet.style.transform = '';
    sheet.style.opacity = '';
    sheet.style.transition = '';
  }
  document.body.classList.remove('modal-open');
  document.body.style.overflow = '';
  document.body.style.position = '';
  document.body.style.width = '';
  document.body.style.height = '';
}

function proceedAfterAddons() {
  hideAddonsModal();
  // Jump directly to Info step (step 4)
  state.currentStep = 4;
  showStep(state.currentStep);
  updateStepIndicator();
  updateNavigationButtons();
  updateMainSubtitle();
  scrollToTop();
  setTimeout(scrollToTop, 200);
}

// Populate boost modal with products that have "boostProduct" label
function populateBoostModal() {
  ensureAddonsModal();
  const grid = addonsModal.querySelector('[data-modal-addons-grid]');
  const title = addonsModal.querySelector('.addons-title');
  
  if (!grid) {
    console.error('[Boost Modal] Grid element not found!');
    return;
  }
  
  // Ensure templates are initialized
  if (!templates.addon) {
    console.warn('[Boost Modal] Templates not initialized yet, initializing...');
    templates.addon = document.getElementById('addon-card-template');
    if (!templates.addon) {
      console.error('[Boost Modal] Addon template not found in DOM!');
    }
  }
  
  // Update title for boost modal
  if (title) {
    title.textContent = t('addons.modal.boost.title');
  }
  
  grid.innerHTML = '';
  
  // Use products with "boostProduct" label.
  // If duplicate products share the same display name (e.g. two "Kaffekort"),
  // keep only the cheapest one.
  const boostProducts = state.boostProducts || [];
  const boostProductsByName = new Map();
  boostProducts.forEach((product) => {
    const displayName = (product?.name || '').trim();
    const key = displayName.toLowerCase();
    if (!key) return;
    const price = roundToHalfKrone(getAddonPrice(product));
    const existing = boostProductsByName.get(key);
    if (!existing || price < existing.price) {
      boostProductsByName.set(key, { product, price });
    }
  });
  const boostProductsToRender = boostProductsByName.size > 0
    ? Array.from(boostProductsByName.values()).map((entry) => entry.product)
    : boostProducts;
  
  console.log(`[Boost Modal] populateBoostModal: ${boostProductsToRender.length} boost products to render`);
  
  if (boostProductsToRender.length === 0) {
    // No boost products available
    console.warn('[Boost Modal] No boost products to display');
    const emptyMsg = document.createElement('div');
    emptyMsg.className = 'addons-empty';
    emptyMsg.textContent = t('addons.modal.empty');
    grid.appendChild(emptyMsg);
    return;
  }
  
  // Check if template exists
  if (!templates.addon) {
    console.warn('[Boost Modal] Addon template not found, using fallback rendering');
  }
  
  // Render each boost product
  boostProductsToRender.forEach((product, index) => {
    console.log(`[Boost Modal] Rendering boost product ${index + 1}/${boostProductsToRender.length}: ${product.name} (ID: ${product.id})`);
    if (!templates.addon) {
      // Fallback simple card if template missing
      const card = document.createElement('div');
      card.className = 'plan-card addon-card';
      card.style.cursor = 'pointer';
      
      // Extract price from product structure (similar to renderSubscriptionCard)
      const priceInCents = product.priceWithInterval?.price?.amount || 
                           product.price?.amount || 
                           product.amount || 
                           0;
      const price = priceInCents > 0 ? priceInCents / 100 : 0;
      const currency = product.priceWithInterval?.price?.currency || 
                       product.price?.currency || 
                       product.currency || 
                       'DKK';
      
      const addToCartBtn = document.createElement('button');
      addToCartBtn.className = 'addon-add-to-cart-btn';
      addToCartBtn.setAttribute('data-action', 'toggle-addon');
      addToCartBtn.setAttribute('data-addon-id', product.id);
      
      card.innerHTML = sanitizeHTML(`
        <div style="font-weight:600">${product.name || 'Boost Product'}</div>
        <div>${price > 0 ? formatPriceHalfKrone(roundToHalfKrone(price)) + ' kr.' : '—'}</div>
      `);
      card.appendChild(addToCartBtn);
      
      // Update button text based on initial state
      const isSelected = state.addonIds.has(product.id);
      addToCartBtn.textContent = isSelected ? t('button.added') : t('button.addToCart');
      addToCartBtn.setAttribute('data-i18n-key', isSelected ? 'button.added' : 'button.addToCart');
      if (isSelected) {
        card.classList.add('selected');
      }
      
      // Make entire card clickable
      card.addEventListener('click', (e) => {
        // Don't trigger if clicking the button itself
        if (e.target === addToCartBtn || addToCartBtn.contains(e.target)) {
          return;
        }
        // Toggle the addon when clicking anywhere on the card
        if (product.id) toggleAddon(product.id, addToCartBtn);
      });
      
      // Ensure card is visible
      card.style.opacity = '1';
      card.style.visibility = 'visible';
      
      grid.appendChild(card);
      return;
    }
    
    // Use existing add-on template
    const card = templates.addon.content.firstElementChild.cloneNode(true);
    const addToCartBtn = card.querySelector('[data-action="toggle-addon"]');
    if (addToCartBtn) addToCartBtn.dataset.addonId = product.id;
    
    const thumbWrapper = card.querySelector('.addon-card-thumb') || card.querySelector('[data-action="open-lightbox"]');
    const nameEl = card.querySelector('[data-element="name"]');
    const imageEl = card.querySelector('[data-element="image"]');
    const imageBannerEl = card.querySelector('[data-element="imageBanner"]');
    const originalPriceEl = card.querySelector('[data-element="originalPrice"]');
    const discountedPriceEl = card.querySelector('[data-element="discountedPrice"]');
    const priceBannerEl = card.querySelector('[data-element="priceBanner"]');
    const descriptionEl = card.querySelector('[data-element="description"]');
    const featuresEl = card.querySelector('[data-element="features"]');
    
    if (nameEl) nameEl.textContent = product.name || 'Boost Product';
    
    // Extract and display product image
    if (imageEl) {
      let imageUrl = null;
      
      if (product.assets && Array.isArray(product.assets) && product.assets.length > 0) {
        console.log('[Boost Modal] Product assets:', product.name, product.assets);
        // Look for MAIN or CENTERED asset type
        const mainAsset = product.assets.find(asset => 
          asset.type === 'MAIN' || asset.type === 'CENTERED'
        ) || product.assets[0]; // Fallback to first asset if no MAIN/CENTERED
        
        console.log('[Boost Modal] Selected asset full object:', JSON.stringify(mainAsset, null, 2));
        console.log('[Boost Modal] Asset contentUrl:', mainAsset?.contentUrl);
        console.log('[Boost Modal] Asset url:', mainAsset?.url);
        console.log('[Boost Modal] Asset reference:', mainAsset?.reference);
        
        // AssetOut has contentUrl field (per OpenAPI spec)
        // Use contentUrl if available, otherwise fall back to constructing from reference
        imageUrl = mainAsset?.contentUrl || mainAsset?.url || null;
        
        // If no contentUrl, construct from reference ID
        if (!imageUrl && mainAsset?.reference) {
          // Construct asset URL from reference ID
          const referenceId = mainAsset.reference;
          const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
          const isCloudflarePages = window.location.hostname.includes('pages.dev') ||
                                   window.location.hostname.includes('join.boulders.dk') ||
                                   window.location.hostname === 'boulders.dk';
          
          if (isDevelopment) {
            imageUrl = `/api-proxy?path=/api/assets/${referenceId}`;
          } else if (isCloudflarePages) {
            imageUrl = `/api-proxy?path=/api/assets/${referenceId}`;
          } else {
            imageUrl = `https://api-join.boulders.dk/api/assets/${referenceId}`;
          }
          console.log('[Boost Modal] Constructed asset URL from reference:', referenceId, '->', imageUrl);
        }
      }
      
      console.log('[Boost Modal] Image URL for', product.name, ':', imageUrl);
      
      // Always show image element - use placeholder if no image URL
      imageEl.removeAttribute('style'); // Remove inline style that hides it
      imageEl.style.display = 'block';
      imageEl.style.visibility = 'visible';
      imageEl.style.opacity = '1';
      imageEl.style.width = '100%';
      // Image size controlled by CSS (.addons-grid .addon-card img max-height by breakpoint)
      imageEl.style.objectFit = 'cover';
      imageEl.style.borderRadius = '8px';
      imageEl.style.marginBottom = '12px';
      imageEl.style.backgroundColor = '#1e293b'; // Background color for placeholder
      imageEl.classList.add('addon-image');
      
      if (imageUrl) {
        imageEl.src = imageUrl;
        imageEl.alt = product.name || 'Product image';
        console.log('[Boost Modal] Image set for', product.name, '- src:', imageEl.src);
      } else {
        // Use placeholder image when no image is available
        imageEl.src = getProductPlaceholderImage();
        imageEl.alt = (product.name || 'Product') + ' - No image available';
        console.log('[Boost Modal] Using placeholder image for', product.name);
      }
      
      // Display media banner text above price (used as original-price fallback too).
      const productBannerText = getAddonMediaBannerText(product);
      if (priceBannerEl && productBannerText) {
        priceBannerEl.textContent = productBannerText;
        priceBannerEl.style.display = '';
        console.log('[Boost Modal] Price banner text set for', product.name, ':', productBannerText);
      } else if (priceBannerEl) {
        priceBannerEl.style.display = 'none';
      }
      if (imageBannerEl) imageBannerEl.style.display = 'none';
      if (thumbWrapper && imageEl) {
        thumbWrapper.addEventListener('click', (e) => {
          e.stopPropagation();
          if (imageEl.src) openAddonLightbox(imageEl.src, imageEl);
        });
      }
      // List layout: thumb size from CSS (64px / 40px mobile), no large-image inline styles
      imageEl.style.width = '';
      imageEl.style.marginBottom = '';
    }
    
    // Use helper function for consistent price extraction
    const price = getAddonPrice(product);
    // Boost products come from regular catalog products (label: boostProduct).
    // Try explicit standard/list fields, then derive from discount percent.
    const originalPrice = getBoostProductOriginalPrice(product);
    console.log('[Boost Modal] Price diagnostics:', {
      productId: product.id,
      productName: product.name,
      discountedPriceDKK: price,
      originalPriceDKK: originalPrice,
      priceWithIntervalAmount: product.priceWithInterval?.price?.amount,
      priceAmount: product.price?.amount,
      discountPercent: product.discountPercent ?? product.discountPercentage ?? product.rabatProcent ?? product.discount?.percent,
    });
    
    if (originalPriceEl) {
      originalPriceEl.style.display = 'none';
    }
    
    if (discountedPriceEl) {
      // Always show price, including "0 kr" for free items
      discountedPriceEl.textContent = formatPriceHalfKrone(roundToHalfKrone(price));
    }
    
    if (descriptionEl) {
      // Get description - use externalDescription if available, otherwise fall back to description
      // Do not show product number as fallback
      const description = product.externalDescription || product.description || '';
      if (description) {
        // Preserve line breaks from backend - escape HTML and preserve newlines
        const descriptionHtml = description
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/\n/g, '<br>');
        descriptionEl.innerHTML = sanitizeHTML(descriptionHtml);
        descriptionEl.style.display = '';
      } else {
        descriptionEl.textContent = '';
        descriptionEl.style.display = 'none';
      }
    }
    const descToggleBoost = card.querySelector('.addon-card-description-toggle');
    if (descToggleBoost) {
      const descText = (product.externalDescription || product.description || '').trim();
      descToggleBoost.style.display = descText.length > 0 ? '' : 'none';
      if (descText.length > 0) {
        const descId = 'addon-desc-boost-' + (product.id || index);
        if (descriptionEl) descriptionEl.id = descId;
        descToggleBoost.setAttribute('aria-controls', descId);
        descToggleBoost.setAttribute('aria-label', t('addons.modal.showDescription') || 'Show full description');
        descToggleBoost.title = t('addons.modal.showDescription') || 'Show full description';
      }
      descToggleBoost.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const expanded = card.classList.toggle('expanded');
        descToggleBoost.setAttribute('aria-expanded', String(expanded));
        descToggleBoost.setAttribute('aria-label', expanded ? (t('addons.modal.collapseDescription') || 'Collapse description') : (t('addons.modal.showDescription') || 'Show full description'));
        descToggleBoost.title = expanded ? (t('addons.modal.collapseDescription') || 'Collapse description') : (t('addons.modal.showDescription') || 'Show full description');
      });
    }
    
    if (featuresEl && product.features && Array.isArray(product.features)) {
      featuresEl.innerHTML = '';
      product.features.forEach((feature) => {
        const li = document.createElement('li');
        li.textContent = feature;
        featuresEl.appendChild(li);
      });
    } else if (featuresEl) {
      featuresEl.innerHTML = '';
    }
    
    // Update button text based on initial state
    if (addToCartBtn) {
      const isSelected = state.addonIds.has(product.id);
      addToCartBtn.textContent = isSelected ? t('button.added') : t('button.addToCart');
      addToCartBtn.setAttribute('data-i18n-key', isSelected ? 'button.added' : 'button.addToCart');
      if (isSelected) {
        card.classList.add('selected');
      }
    }
    
    // Main row click toggles add to cart (expand button and thumbnail have their own handlers)
    card.style.cursor = 'pointer';
    const mainRow = card.querySelector('.addon-card-main');
    const cardClickTarget = mainRow || card;
    cardClickTarget.addEventListener('click', (e) => {
      if (e.target === addToCartBtn || addToCartBtn.contains(e.target)) return;
      if (thumbWrapper && (e.target === thumbWrapper || thumbWrapper.contains(e.target))) return;
      const descToggleEl = card.querySelector('.addon-card-description-toggle');
      if (descToggleEl && (e.target === descToggleEl || descToggleEl.contains(e.target))) return;
      if (product.id) toggleAddon(product.id, addToCartBtn);
    });
    
    // Ensure card is visible (fix opacity: 0 issue)
    card.style.opacity = '1';
    card.style.visibility = 'visible';
    
    grid.appendChild(card);
    console.log(`[Boost Modal] ✅ Card appended for product: ${product.name}`);
  });
  
  console.log(`[Boost Modal] ✅ Finished rendering ${boostProductsToRender.length} boost products`);
}

const BOOST_MODAL_OPEN_DELAY_MS = 50;

// Show boost modal (uses same modal infrastructure as addons)
async function showBoostModal(options = {}) {
  ensureAddonsModal();
  
  // Ensure products are loaded first
  const hasAnyProducts = (state.allRawProducts?.length || 0) > 0 || 
                         (state.subscriptions?.length || 0) > 0 || 
                         (state.campaignSubscriptions?.length || 0) > 0 ||
                         (state.dayPassSubscriptions?.length || 0) > 0 ||
                         (state.valueCards?.length || 0) > 0;
  
  if (!hasAnyProducts) {
    console.warn('[Boost Modal] No products loaded yet. Waiting for products to load...');
    // Wait a bit and try again, or load products if business unit is selected
    if (state.selectedBusinessUnit) {
      await loadProductsFromAPI();
    } else {
      console.error('[Boost Modal] Cannot load boost products: No business unit selected');
      showToast('Unable to load boost products. Please try again.', 'error');
      return;
    }
  }
  
  // If products were already loaded for the current selection, skip duplicate reload.
  if (!options.productsPreloaded) {
    await loadBoostProducts(resolveSelectedAccessProduct());
  }
  
  // Show modal first so sheet container is in layout (CSS: flex centering, no inline position)
  addonsModal.style.display = 'flex';
  addonsModal.style.alignItems = 'center';
  addonsModal.style.justifyContent = 'center';
  addonsModal.style.opacity = '1';
  addonsModal.style.transition = 'opacity 0.2s ease';

  document.body.classList.add('modal-open');
  document.body.style.overflow = 'hidden';
  document.body.style.position = 'fixed';
  document.body.style.width = '100%';
  document.body.style.height = '100%';

  const sheet = addonsModal.querySelector('.addons-sheet');
  if (sheet) {
    // Rely on CSS for positioning (no position/left/top/translate – avoids conflicts on some devices)
    sheet.style.position = '';
    sheet.style.left = '';
    sheet.style.top = '';
    sheet.style.opacity = '0';
    sheet.style.transform = 'scale(0.95)';
    sheet.style.transition = 'opacity 0.3s ease, transform 0.3s ease';
  }

  // Let sheet render/layout first, then populate cards, then animate
  requestAnimationFrame(() => {
    populateBoostModal();
    updateAddonActionButton();

    if (sheet) {
      requestAnimationFrame(() => {
        sheet.style.opacity = '1';
        sheet.style.transform = 'scale(1)';
      });
    }
  });
}

function prewarmBoostModalResources() {
  // Precreate modal DOM upfront to avoid first-open construction cost.
  ensureAddonsModal();

  if (!state.selectedBusinessUnit) return;
  if (Array.isArray(state.boostProducts) && state.boostProducts.length > 0) return;

  const productSources = [
    ...(state.allRawProducts || []),
    ...(state.subscriptions || []),
    ...(state.campaignSubscriptions || []),
    ...(state.dayPassSubscriptions || []),
    ...(state.valueCards || []),
    ...(state.campaignValueCards || [])
  ];

  // Warm the likely boost dataset using any plan that can trigger boost modal.
  const preloadPlan = productSources.find((product) => hasBoostLabel(product));
  if (!preloadPlan) return;

  loadBoostProducts(preloadPlan).catch((error) => {
    console.warn('[Boost Modal] Prewarm skipped:', error);
  });
}

// Expose a small API to set the image dynamically if desired
// Usage: window.setAddonsUpsellImage('https://.../image.jpg')
// Image API removed

function hasProductLabel(product, expectedLabelName) {
  if (!product || !Array.isArray(product.productLabels)) {
    return false;
  }
  const targetLabel = String(expectedLabelName || '').toLowerCase();
  if (!targetLabel) return false;
  return product.productLabels.some((label) => {
    const labelName = String(label?.name || '').toLowerCase();
    return labelName === targetLabel;
  });
}

function resolveSelectedAccessProduct() {
  if (!state.selectedProductId) return null;
  const selectedId = String(state.selectedProductId);
  const productPools = [
    ...(state.allRawProducts || []),
    ...(state.subscriptions || []),
    ...(state.campaignSubscriptions || []),
    ...(state.dayPassSubscriptions || []),
    ...(state.valueCards || []),
    ...(state.campaignValueCards || [])
  ];
  return productPools.find((product) => String(product?.id) === selectedId) || null;
}

// Check if a product should trigger boost modal ("boost" or "boostCampaign")
function hasBoostLabel(product) {
  return hasProductLabel(product, 'boost') || hasProductLabel(product, 'boostcampaign');
}

function has15DayPassLabel(product) {
  if (!product || !Array.isArray(product.productLabels)) {
    return false;
  }
  return product.productLabels.some((label) => {
    const normalized = String(label?.name || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '');
    return normalized === '15daypass' ||
      normalized === '15daytrialpass' ||
      normalized === '15daypassfree';
  });
}

// Load and filter products with boost labels for the selected plan
async function loadBoostProducts(selectedPlanProduct = null) {
  const boostProducts = [];
  const seenIds = new Set();
  
  const resolvedSelectedPlanProduct = selectedPlanProduct || resolveSelectedAccessProduct();
  const selectedPlanHasBoost = hasProductLabel(resolvedSelectedPlanProduct, 'boost');
  const selectedPlanHasBoostCampaign = hasProductLabel(resolvedSelectedPlanProduct, 'boostcampaign');
  
  // Scan all product sources - prioritize raw products (before filtering) to catch all boost products
  // Then also check filtered arrays in case products are in both
  const productSources = [
    ...(state.allRawProducts || []), // Raw products before filtering (most important)
    ...(state.subscriptions || []),
    ...(state.campaignSubscriptions || []),
    ...(state.dayPassSubscriptions || []),
    ...(state.valueCards || []),
    ...(state.campaignValueCards || []) // Include campaign value cards
  ];
  
  console.log(`[Boost Modal] Scanning ${productSources.length} products for boost labels`);
  console.log(`[Boost Modal] Raw products: ${state.allRawProducts?.length || 0}, Subscriptions: ${state.subscriptions?.length || 0}, Campaigns: ${state.campaignSubscriptions?.length || 0}, DayPass: ${state.dayPassSubscriptions?.length || 0}, ValueCards: ${state.valueCards?.length || 0}, CampaignValueCards: ${state.campaignValueCards?.length || 0}`);
  
  // Filter products with:
  // - boostProduct label when selected plan has boost label, or
  // - boostCampaignProduct label when selected plan has boostCampaign label.
  // If plan has both labels, include both product sets.
  productSources.forEach((product) => {
    const includeByDefaultBoost = selectedPlanHasBoost && hasProductLabel(product, 'boostproduct');
    const includeByBoostCampaign = selectedPlanHasBoostCampaign && hasProductLabel(product, 'boostcampaignproduct');
    if ((includeByDefaultBoost || includeByBoostCampaign) && product.id) {
      const productId = String(product.id);
      if (!seenIds.has(productId)) {
        seenIds.add(productId);
        boostProducts.push(product);
        console.log(`[Boost Modal] Found boost product: ${product.name} (ID: ${product.id})`);
      }
    }
  });
  
  let enrichedBoostProducts = boostProducts;
  if (boostProducts.length > 0) {
    enrichedBoostProducts = await Promise.all(boostProducts.map(async (product) => {
      const looksLikeSubscription = !!product.priceWithInterval;
      const details = looksLikeSubscription
        ? await businessUnitsAPI.getSubscriptionById(product.id, state.selectedBusinessUnit)
        : await businessUnitsAPI.getValueCardById(product.id, state.selectedBusinessUnit);
      return details ? { ...product, ...details } : product;
    }));
  }

  state.boostProducts = enrichedBoostProducts;
  console.log(`[Boost Modal] Loaded ${enrichedBoostProducts.length} boost products`);
  if (enrichedBoostProducts.length === 0) {
    console.warn('[Boost Modal] No boost products found! Check selected plan labels ("boost"/"boostCampaign") and product labels ("boostProduct"/"boostCampaignProduct").');
  }
  return enrichedBoostProducts;
}

// Hide Boost from the step indicator permanently and toggle Boost step panel by selection
function applyConditionalSteps() {
  // 1) Hide Boost in the step indicator entirely (never shown)
  const boostStep = Array.from(document.querySelectorAll('.step .step-label'))
    .find((label) => label.textContent.trim() === 'Boost')?.closest('.step');
  if (boostStep) {
    boostStep.classList.add('hidden');
    // Hide adjacent connectors so the visual line doesn't have a dangling segment
    const prevConnector = boostStep.previousElementSibling;
    if (prevConnector && prevConnector.classList.contains('step-connector')) {
      prevConnector.classList.add('hidden');
    }
    const nextConnector = boostStep.nextElementSibling;
    // Keep the connector after Boost visible to bridge Access -> Send
    if (nextConnector && nextConnector.classList.contains('step-connector')) {
      nextConnector.classList.remove('hidden');
    }
  }

  // 1b) /99kr (firstclimb): the Access step is skipped, so hide it in the indicator too.
  //     This collapses the indicator to Home Gym → Send.
  const accessStep = Array.from(document.querySelectorAll('.step .step-label'))
    .find((label) => {
      const k = label.getAttribute('data-i18n-key');
      return k === 'step.access';
    })?.closest('.step');
  if (accessStep) {
    if (isFirstClimbRoute()) {
      accessStep.classList.add('hidden');
      const prevConnector = accessStep.previousElementSibling;
      if (prevConnector && prevConnector.classList.contains('step-connector')) {
        prevConnector.classList.add('hidden');
      }
    } else {
      accessStep.classList.remove('hidden');
      const prevConnector = accessStep.previousElementSibling;
      if (prevConnector && prevConnector.classList.contains('step-connector')) {
        prevConnector.classList.remove('hidden');
      }
    }
  }

  // 2) Always hide Boost/Add-ons page (step 3) - disabled for now
  const boostPanel = document.getElementById('step-3');
  if (boostPanel) {
    boostPanel.style.display = 'none'; // Always hidden

    // If user is currently on step 3, move to the next visible step
    if (state.currentStep === 3) {
      nextStep();
    }
  }

  // Recompute indicator visuals after changes
  updateStepIndicator();
}

function init() {
  applyRouteIndexingPolicy(window.location.pathname);
  state.landingRouteConfig = resolveLandingRouteConfig(window.location.pathname);
  if (state.landingRouteConfig) {
    devLog('[Landing Route] Active config:', state.landingRouteConfig.componentName, state.landingRouteConfig.labelKey);
  }

  // firstclimb: if the user arrives already authenticated, kick off an async
  // history check. Block only if they hold/held one of the disqualifying products.
  if (state.landingRouteConfig?.componentName === 'LandingFirstClimb' && isUserAuthenticated()) {
    (async () => {
      let customerIdForCheck = state.customerId
        || getTokenMetadata()?.username
        || getTokenMetadata()?.userName;
      try {
        if (!customerIdForCheck) {
          await syncAuthenticatedCustomerState();
          customerIdForCheck = state.customerId;
        }
      } catch (_) {}
      let hasBlockingHistory = true;
      try {
        hasBlockingHistory = await customerHasFirstclimbBlockingHistory(customerIdForCheck);
      } catch (historyErr) {
        console.warn('[init] firstclimb history check failed; blocking by default:', historyErr);
        hasBlockingHistory = true;
      }
      if (hasBlockingHistory) {
        const blockedEmail = state.authenticatedEmail
          || state.authenticatedCustomer?.email
          || getTokenMetadata()?.email
          || '';
        try { window.clearTokens && window.clearTokens(); } catch (_) {}
        state.customerId = null;
        state.authenticatedCustomer = null;
        state.authenticatedEmail = null;
        try { refreshLoginUI(); } catch (_) {}
        blockFirstClimbExistingCustomer({ email: blockedEmail });
      }
    })();
  }

  // /99kr: tag the body so route-scoped CSS rules can target this flow.
  if (state.landingRouteConfig?.componentName === 'LandingFirstClimb') {
    try { document.body.classList.add('firstclimb-flow'); } catch (_) {}
  }

  // Capture ?ref=<recruiter customer ID> early so it's stored before any
  // signup steps run. Persists in localStorage with a 30-day TTL; later
  // reads via getActiveReferral() / getRecruitedByForPayload().
  captureReferralFromUrl();

  // Initialize email tracking from localStorage
  try {
    const storedEmails = JSON.parse(localStorage.getItem('boulders_created_emails') || '[]');
    storedEmails.forEach(email => {
      if (email && typeof email === 'string') {
        state.createdEmails.add(email.toLowerCase().trim());
      }
    });
    console.log('[Init] Loaded', state.createdEmails.size, 'previously created emails from localStorage');
  } catch (e) {
    console.warn('[Init] Could not load created emails from localStorage:', e);
  }
  
  // Initialize language (must be before API calls)
  initLanguageSwitcher();
  
  cacheDom();
  cacheTemplates();
  // Precreate addon/boost modal shell once so opening is snappier.
  ensureAddonsModal();
  initFAQ();
  
  // Restore privacy and terms consent from localStorage
  restoreConsentCheckboxes();
  
  renderCatalog();
  refreshCarousels();
  updateCartSummary();
  initAuthModeToggle();
  updateCheckoutButton();
  setupEventListeners();
  updateFAQVisibility(); // Initialize FAQ visibility
  // Apply conditional visibility for Boost on load
  applyConditionalSteps();
  updateStepIndicator();
  updateNavigationButtons();
  
  // Load gyms from API
  loadGymsFromAPI();
  
  // Load countries from API and populate country code selectors
  // Use setTimeout to ensure DOM is fully ready
  setTimeout(() => {
    loadCountriesFromAPI();
  }, 100);
  
  // Restore location button active state if location exists
  const locationBtn = document.getElementById('findNearestGym');
  if (locationBtn && userLocation) {
    locationBtn.classList.add('active');
  }
  
  // Cookie consent handled by Cookiebot (see index.html). Footer "Cookie Settings" triggers Cookiebot.renew().
  
  // Geolocation must be triggered by user gesture to avoid browser blocks.
  
  updateMainSubtitle();
  
  // Hide loading overlay and show main content
  hideLoadingOverlay();
}

// Hide loading overlay and show main content
function hideLoadingOverlay() {
  const loadingOverlay = document.getElementById('loadingOverlay');
  const mainContent = document.getElementById('mainContent');
  const headerContent = document.getElementById('headerContent');
  
  if (loadingOverlay) {
    // Show header and main content
    if (headerContent) {
      headerContent.style.display = '';
    }
    if (mainContent) {
      mainContent.style.display = '';
      // Update FAQ visibility after main is shown
      setTimeout(() => {
        updateFAQVisibility();
        initFAQ();
      }, 100);
    }
    
    // Hide loading overlay with fade out
    loadingOverlay.classList.add('hidden');
    
    // Remove from DOM after animation completes
    setTimeout(() => {
      if (loadingOverlay.parentNode) {
        loadingOverlay.remove();
      }
    }, 300);
  }
}


// Comprehensive translation system - using da-DK and en-GB to match API language codes
const translations = {
  'da-DK': {
    'step.homeGym': 'Hjemmehal', 'step.access': 'Adgang', 'step.boost': 'Boost', 'step.send': 'Send',
    'category.campaign': 'Medlemskabskampagne', 'category.campaign.desc': 'Begrænset medlemskampagne, udløber 8. april 2026. Køb tilbuddet inden det udløber. Kan kun bruges af personer der ikke har været medlem i Boulders de seneste 6 måneder.', 'category.campaign.subtitle': 'Start nu og spar stort! Kun 3 måneders binding. Ingen skjulte gebyrer. <a href="#" data-action="open-terms" data-terms-type="terms" onclick="event.preventDefault();">Vilkår og betingelser</a> gælder.', 'category.campaign.endsIn': 'Udløber om', 'category.membership': 'Medlemskab', 'category.membership.subtitle': 'Ubegrænset adgang i alle Boulders + loyalitetsprogram + ekstra medlemsfordele.', 'category.15daypass': '15-Dages Prøveperiode', 'category.15daypass.subtitle': 'Prøv Boulders med 15 dages ubegrænset klatring inkl. sko. Start når det passer dig.', 'category.punchcard': 'Klippekort', 'category.punchcard.subtitle': 'Klatrer du en gang imellem eller et par gange om måneden? Så er klippekortet til dig.',
    'category.membership.desc': 'Medlemskab er et løbende abonnement med automatisk fornyelse. Ingen tilmelding eller opsigelsesgebyrer. Opsigelsesvarsel er resten af måneden + 1 måned.',
    'category.15daypass.desc': 'Prøv Boulders af med 15 dages adgang til alle haller og faciliteter. Klatersko inkluderet.',
    'category.punchcard.desc': 'Hver indgang koster 1 klip, og giver adgang til alle haller og faciliteter. Genopfyld inden for 14 dage efter dit sidste klip og få 100 kr rabat i hallen. Kan konverteres til medlemskab senere.',
    'header.selectedGym': 'Valgt hal:', 'gym.headsUp': 'Hjemmehal valgt:', 'access.headsUp': 'Adgangstype valgt:',
    'main.subtitle.step1': 'Vælg din hjemmehal', 'main.subtitle.step1.secondary': 'Dette er hvor du primært træner − du får adgang til alle haller.',
    'main.subtitle.step2': 'Vælg din adgangstype', 'main.subtitle.step2.secondary': 'Vælg medlemskab hvis du klatrer mindst én gang om ugen.',
    'main.subtitle.step3': 'Vil du have pommes frites med?', 'main.subtitle.step4': 'Send',
    'button.next': 'Næste', 'button.back': 'Tilbage', 'button.continue': 'Fortsæt', 'button.skip': 'Spring over', 'button.complete': 'Færdig', 'button.edit': 'Rediger', 'button.select': 'Vælg', 'button.addToCart': 'Tilføj til kurv', 'button.added': 'Tilføjet!',
    'button.findNearest': 'Find nærmeste hal', 'button.searchGyms': 'Søg haller...', 'button.apply': 'Anvend', 'button.removeCoupon': 'Fjern', 'gym.nearest': 'Nærmeste',
    'form.email': 'E-mail*', 'form.email.placeholder': 'E-mail', 'form.password': 'Adgangskode*', 'form.password.placeholder': 'Adgangskode',
    'form.forgotPassword': 'Glemt adgangskode?', 'form.login': 'Log ind', 'form.createAccount': 'Opret konto', 'form.loggedInAs': 'Logget ind som', 'form.address': 'Adresse:',
    'form.firstName': 'Fornavn*', 'form.firstName.placeholder': 'Fornavn', 'form.lastName': 'Efternavn*', 'form.lastName.placeholder': 'Efternavn',
    'form.dateOfBirth': 'Fødselsdato*', 'form.streetAddress': 'Gade og husnummer*', 'form.streetAddress.placeholder': 'Gade og husnummer',
    'form.postalCode': 'Postnummer*', 'form.postalCode.placeholder': 'Postnummer', 'form.city': 'By', 'form.city.placeholder': 'Auto',
    'form.email.create': 'E-mail*', 'form.email.create.placeholder': 'Indtast din e-mail', 'form.country': 'Land', 'form.phoneNumber': 'Mobilnummer*',
    'form.phoneNumber.placeholder': '12345678', 'form.password.create': 'Adgangskode*', 'form.password.create.placeholder': 'Opret en adgangskode',
    'form.confirmPassword': 'Bekræft adgangskode*', 'form.confirmPassword.placeholder': 'Bekræft din adgangskode', 'form.saveAccount': 'Opret profil',
    'form.buyer': 'Køber', 'form.createProfile': 'NY PROFIL', 'form.parentGuardian': 'Forælder/Værge Information',
    'form.parentFullName': 'Fornavn og efternavn*', 'form.parentFullName.placeholder': 'Indtast dit fulde navn',
    'form.parentDateOfBirth': 'Fødselsdato*', 'form.parentStreetAddress': 'Gade og husnummer*', 'form.parentStreetAddress.placeholder': 'Indtast gade og husnummer',
    'form.parentPostalCode': 'Postnummer*', 'form.parentPostalCode.placeholder': '1234', 'form.parentCity': 'By', 'form.parentCity.placeholder': 'København',
    'form.parentEmail': 'E-mail*', 'form.parentEmail.placeholder': 'Indtast din e-mail', 'form.parentCountryCode': 'Land',
    'form.parentPhoneNumber': 'Mobilnummer*', 'form.parentPhoneNumber.placeholder': '12345678', 'form.sameAddress': 'Samme adresse og kontaktinformation',
    'form.error.firstName': 'Indtast venligst dit fornavn', 'form.error.lastName': 'Indtast venligst dit efternavn',
    'form.error.email': 'Indtast venligst en gyldig e-mailadresse',
    'form.resetPassword': 'NULSTIL ADGANGSKODE', 'form.resetPassword.desc': 'Indtast din e-mailadresse, og vi sender dig instruktioner til at nulstille din adgangskode.',
    'form.resetPassword.success': 'Nulstillingsinstruktioner er blevet sendt til din e-mail.', 'form.sendResetLink': 'SEND NULSTILLINGSLINK',
    'button.cancel': 'Annuller', 'button.close': 'Luk',
    'form.authSwitch.login': 'Log ind', 'form.authSwitch.createAccount': 'Opret konto',
    'cart.title': 'Kurv', 'cart.completeIn': 'Gennemfør inden', 'cart.offerExpiresIn': 'Tilbuddet udløber om', 'cart.timeLeft': 'Tid tilbage', 'cart.timeToComplete': 'Tid tilbage til at gennemføre:', 'cart.subtotal': 'Subtotal', 'cart.discount': 'Rabatkode', 'cart.discount.placeholder': 'Rabatkode', 'cart.discountAmount': 'Rabat', 'cart.discountAmountWithPercent': 'Rabat ({percent})', 'cart.discount.applied': 'Rabatkode anvendt!', 'cart.discount.removed': 'Rabatkode fjernet.', 'cart.discount.removeFailed': 'Kunne ikke fjerne rabatkoden. Prøv igen.', 'cart.discount.empty': 'Indtast en rabatkode', 'cart.discount.loginRequired': 'Log ind eller opret en konto for at bruge en rabatkode', 'cart.discount.locationRequired': 'Vælg en hal først', 'cart.discount.selectProduct': 'Vælg et medlemskab eller klippekort først', 'cart.discount.notFound': 'Rabatkoden blev ikke fundet. Tjek koden og prøv igen.', 'cart.discount.invalid': 'Ugyldig rabatkode. Tjek koden og prøv igen.', 'cart.discount.expired': 'Denne rabatkode er udløbet.', 'cart.discount.alreadyUsed': 'Denne rabatkode er allerede brugt.', 'cart.discount.notApplicable': 'Rabatkoden gælder ikke for denne ordre.', 'cart.discount.forbidden': 'Rabatkoden kan ikke bruges på denne ordre.', 'cart.discount.genericFailed': 'Kunne ikke anvende rabatkoden. Prøv igen.', 'cart.discount.noOrder': 'Ingen ordre fundet. Genindlæs siden og prøv igen.', 'cart.discount.methodNotSupported': 'Rabatkode kunne ikke anvendes. Kontakt support.', 'cart.discount.applying': 'Anvender…', 'cart.discount.creatingOrder': 'Opretter ordre…', 'cart.total': 'Total', 'cart.payNow': 'Betal nu', 'cart.monthlyFee': 'Månedlig pris', 'cart.firstMonth': 'Første måned', 'cart.validUntil': 'Gyldig indtil', 'cart.punch.one': '1 Klip', 'cart.punch.label': 'Klip',
    'quantity.label': 'Vælg antal',
    'activationDate.label': 'Hvornår vil du aktivere din prøveperiode?',
    'activationDate.now': 'Aktiver nu',
    'activationDate.now.desc': 'Starter med det samme',
    'activationDate.later': 'Aktiver på en bestemt dato',
    'activationDate.later.desc': 'Vælg en fremtidig startdato',
    'activationDate.pickLabel': 'Vælg aktiveringsdato',
    'activationDate.hint': 'Vælg en dato i dag eller i fremtiden.',
    'activationDate.lastDay': 'Sidste gyldige dag: {date}',
    'activationDate.change': 'Ændre',
    'activationDate.changeFailed': 'Kunne ikke ændre datoen. Gennemfør din køb eller start forfra.',
    'activationConfirm.title': 'Bekræft dit valg',
    'activationConfirm.subtitle': 'Bekræft hvornår din prøveperiode skal starte, før du går til betaling.',
    'activationConfirm.subtitle.freetrial': 'Bekræft hvornår din prøveperiode skal starte.',
    'activationConfirm.startLabel': 'Starter',
    'activationConfirm.endLabel': 'Gyldig til',
    'activationConfirm.today': 'I dag',
    'activationConfirm.continue': 'Fortsæt til betaling',
    'activationConfirm.continue.freetrial': 'Aktivér prøveperiode',
    'cart.membershipDetails': 'Medlemskabsdetaljer', 'cart.membershipNumber': 'Medlemsnummer:', 'cart.membershipActivation': 'Medlemskabet er aktiveret med automatisk fornyelse', 'cart.memberName': 'Medlemsnavn:',
    'cart.period': 'Periode', 'cart.paymentMethod': 'Vælg betalingsmetode', 'cart.paymentRedirect': 'Du vil blive omdirigeret til vores sikre betalingsudbyder for at gennemføre din betaling.',
    'cart.consent.terms': 'Jeg accepterer <a href="#" data-action="open-terms" data-terms-type="terms" onclick="event.preventDefault();">Vilkår og Betingelser</a>.*',
    'cart.consent.terms.firstclimb': 'Jeg forstår, at jeg skal underskrive <a href="#" data-action="open-terms" data-terms-type="liability-waiver" onclick="event.preventDefault();">ansvarsfraskrivelsen</a> ved indløsning af billetten.*',
    'liability.waiver.title': 'Ansvarsfraskrivelse',
    'cart.consent.privacy': 'Jeg accepterer <a href="#" data-action="open-terms" data-terms-type="privacy" onclick="event.preventDefault();">Persondatapolitik</a>.*',
    'cart.consent.marketing': 'Jeg vil gerne modtage <a href="#" data-action="open-terms" data-terms-type="email-consent" onclick="event.preventDefault();">marketing-e-mails</a>.',
    'consent.email.explainer': 'E-mail<br><br>Jeg giver samtykke til, at Boulders må sende mig e-mails og holde mig opdateret med begivenheder og tiltag, inspiration til træningen, tilbud og andre tjenester.',
    'cart.consent.sms': 'Jeg vil gerne modtage <a href="#" data-action="open-terms" data-terms-type="sms-consent" onclick="event.preventDefault();">SMS-beskeder</a>.',
    'consent.sms.explainer': 'SMS<br><br>Jeg giver samtykke til, at Boulders må sende mig SMS-beskeder og holde mig opdateret med begivenheder og tiltag, inspiration til træningen, tilbud og andre tjenester.',
    'cart.cardPayment': 'Kortbetaling', 'cart.checkout': 'Til kassen', 'cart.freePeriod': 'Gratis periode', 'cart.activateTrial': 'AKTIVÉR PRØVEPERIODE', 'cart.noCreditCardRequired': 'Intet kreditkort kræves', 'cart.faqHelp': 'Spørgsmål? Se FAQ', 'step4.completePurchase': 'Færdiggør dit køb',
    'step4.loginPrompt': 'Log ind på din eksisterende konto eller opret en ny.',
    'cart.boundUntil': 'bundet indtil', 'cart.billingPeriodConfirmed': 'Faktureringsperiode bekræftes efter køb.',
    'cart.tier.firstMonths': 'Første {months} måneder', 'cart.tier.afterMonths': 'Efter {months} måneder', 'cart.tier.introRate': 'Introduktionspris', 'cart.tier.commitment3': 'Kun 3 måneders binding', 'cart.tier.cancelMonthToMonth': 'Opsig måned for måned', 'cart.tier.savings': 'SPAR {amount} KR.',
    'cart.payNowTooltip.title': 'Sådan beregnes beløbet', 'cart.payNowTooltip.line1': 'Køb d. 1.-15.: Du betaler kun for resten af denne måned.', 'cart.payNowTooltip.line2': 'Køb d. 16. eller senere: Resten af denne måned + hele næste måned.', 'cart.payNowIncludesAddons': 'Inkluderer valgte tilvalg (+{amount})',
    'offer.membership.title': 'Medlemskabskampagne', 'offer.membership.description': 'Start nu og spar stort! Kun 3 måneders binding. Ingen skjulte gebyrer. <a href="#" data-action="open-terms" data-terms-type="terms" onclick="event.preventDefault();">Vilkår og betingelser</a> gælder.',
    'message.noProducts.membership': 'Ingen medlemskabsmuligheder tilgængelig på nuværende tidspunkt.',
    'message.noProducts.punchcard': 'Ingen klippekortmuligheder tilgængelig på nuværende tidspunkt.',
    'message.noProducts.15daypass': 'Ingen 15-dages muligheder tilgængelig på nuværende tidspunkt.',
    'confirmation.title': 'SUCCES!',
    'confirmation.message': 'Din ordre er blevet bekræftet! Du modtager en e-mail med alle detaljerne snart.',
    'confirmation.message.membership': 'Dit medlemskab er blevet bekræftet! Du modtager en e-mail med alle detaljerne snart.',
    'confirmation.message.15daypass': 'Din 15-dages prøveperiode er blevet bekræftet! Du modtager en e-mail med alle detaljerne snart.',
    'confirmation.message.punchcard': 'Dit klippekort er blevet bekræftet! Du modtager en e-mail med alle detaljerne snart.',
    'confirmation.message.firstclimb': 'Din første klatretur er klar! Du modtager en e-mail med alle detaljerne snart.',
    'confirmation.nextStep2.firstclimb': 'Din dagsbillet er klar — kig forbi hallen, når det passer dig (inden for en måned).',
    'confirmation.nextStep3.firstclimb': 'Når du kommer: oplys dit telefonnummer eller email, så aktiverer vi din billet og udleverer lejesko og kalk. Husk at du skal underskrive ansvarsfraskrivelsen.',
    'confirmation.message.generic': 'Din ordre er blevet bekræftet! Du modtager en e-mail med alle detaljerne snart.',
    'confirmation.orderDetails': 'Ordredetaljer',
    'confirmation.orderNumber': 'Ordrenummer:',
    'confirmation.date': 'Dato:',
    'confirmation.total': 'Total:',
    'confirmation.showReceipt': 'Vis detaljeret kvittering',
    'confirmation.receipt.title': 'Ordrebekræftelse',
    'confirmation.receipt.order': 'Ordre',
    'confirmation.receipt.orderOverview': 'Ordreoversigt',
    'confirmation.receipt.subtotal': 'Subtotal:',
    'confirmation.receipt.discount': 'Rabat:',
    'confirmation.receipt.total': 'TOTALT:',
    'confirmation.receipt.vatInclusive': 'inkl. moms (25%):',
    'confirmation.receipt.payments': 'Betalinger',
    'confirmation.receipt.paymentMethod': 'Betalingsmetode:',
    'confirmation.receipt.amountPaid': 'Betalt beløb:',
    'confirmation.receipt.orderNumber': 'Ordrenummer:',
    'confirmation.receipt.sellerInfo': 'Sælgers oplysninger',
    'confirmation.receipt.sellerName': 'Sælgerens navn:',
    'confirmation.receipt.address': 'Adresse:',
    'confirmation.receipt.cvr': 'CVR-nummer:',
    'confirmation.receipt.phone': 'Telefon:',
    'confirmation.receipt.email': 'E-mail:',
    'confirmation.receipt.customerInfo': 'Kundens informationer',
    'confirmation.receipt.customerName': 'Kundens navn:',
    'confirmation.receipt.valueCardsPurchased': 'Købte værdikort',
    'confirmation.receipt.close': 'LUK',
    'confirmation.primaryGym': 'Hjemmehal:',
    'confirmation.membershipType': 'Type:',
    'confirmation.monthlyPrice': 'Månedlig pris:',
    'confirmation.15daypassDetails': 'Prøveperiode detaljer',
    'confirmation.passType': 'Pastype:',
    'confirmation.validFrom': 'Gyldig fra:',
    'confirmation.validUntil': 'Gyldig til:',
    'confirmation.punchCardDetails': 'Klippekort detaljer',
    'confirmation.name': 'Navn:',
    'confirmation.cardType': 'Korttype:',
    'confirmation.quantity': 'Antal:',
    'confirmation.nextStepsTitle': 'Hvad sker der nu?',
    'confirmation.nextStep1': 'E-mail bekræftelse sendt til din indbakke',
    'confirmation.nextStep2.membership': 'Medlemskabsaktivering og automatisk fornyelse',
    'confirmation.nextStep2.15daypass': 'Dit pas er aktivt og klar til brug',
    'confirmation.nextStep2.15daypass.future': 'Din 15-Dages Prøveperiode bliver aktivt den {date}',
    'confirmation.nextStep2.freetrial': 'Din prøveperiode er aktiveret og klar til brug.',
    'confirmation.nextStep2.freetrial.future': 'Din prøveperiode starter den {date}. Vi aktiverer den automatisk.',
    'confirmation.nextStep2.punchcard': 'Dit klippekort er klar til brug',
    'confirmation.nextStep3.membership': 'Hent dit medlemskort i Boulders',
    'confirmation.nextStep3.15daypass': 'Besøg centeret for at begynde at bruge ditn 15-Dages Prøveperiode',
    'confirmation.nextStep3.freetrial': 'Når din prøveperiode starter, skal du møde op i hallen og koble dit kort til din konto. Oplys dit telefonnummer til personalet, så hjælper de dig i gang.',
    'confirmation.nextStep3.freetrial.today': 'Mød op i hallen i dag og kobl dit adgangskort til din konto. Oplys dit telefonnummer til personalet, så hjælper de dig i gang.',
    'confirmation.nextStep3.freetrial.future': 'Når din prøveperiode starter, skal du møde op i hallen og koble dit kort til din konto. Oplys dit telefonnummer til personalet, så hjælper de dig i gang.',
    'confirmation.nextStep3.punchcard': 'Besøg centeret for at begynde at bruge dine klip',
    'confirmation.freetrial.changeActivationCta': 'Har du brug for at ændre aktiveringsdato? Klik her.',
    'invite.title': 'Giv dine venner 2 ugers gratis prøveperiode',
    'invite.subtitle': 'Del dit link – når dine venner melder sig ind, får de en 2-ugers gratis prøveperiode.',
    'invite.copyLink': 'Kopiér link',
    'invite.copied': 'Kopieret!',
    'invite.copiedToast': 'Linket er kopieret – del det med dine venner!',
    'invite.copyFailed': 'Kunne ikke kopiere linket. Kopiér det manuelt.',
    'invite.instagramToast': 'Linket er kopieret – sæt det ind i din Instagram-DM eller story!',
    'invite.shareVia': 'eller del via',
    'invite.share.sms': 'Beskeder',
    'invite.share.email': 'E-mail',
    'invite.share.more': 'Mere',
    'invite.shareMessage': 'Hej! {name} her – jeg har lige meldt mig ind hos Boulders. Klatre med mig og få 2 ugers gratis prøveperiode på min konto:',
    'invite.shareSubject': '2 ugers gratis klatring hos Boulders',
    'invite.footnote': 'Dine venner får 2 ugers gratis adgang og lejesko. Intet betalingskort kræves for at starte. Tilbuddet kan kun benyttes af personer, der ikke tidligere har benyttet et prøvepas.',
    'invite.firstclimb.title': 'Del dette med dine venner',
    'invite.firstclimb.subtitle': 'Dagsbilletten på 99 kr inkl. lejesko og kalk er for alle, der ikke har prøvet det før. Send linket til dine venner.',
    'invite.firstclimb.footnote': 'Tilbuddet kan kun bruges én gang pr. person.',
    'invite.firstclimb.shareMessage': 'Klatre med mig hos Boulders! Få din første dag for 99 kr inkl. lejesko og kalk:',
    'confirmation.pending.title': 'Betaling afventer',
    'confirmation.pending.message': 'Din betaling behandles. Vi afventer bekræftelse fra betalingsudbyderen. Dit medlemskab aktiveres, når betalingen er bekræftet. Ordre #',
    'confirmation.pending.stillProcessing': 'Betalingen behandles stadig. Tjek tilbage om et par minutter eller kontakt support, hvis du har gennemført betalingen. Ordre #',
    'footer.terms.title': 'Vilkår og Betingelser', 'footer.terms.all': 'Vilkår og Betingelser', 'footer.terms.membership': 'Vilkår og Betingelser for Medlemskab', 'footer.terms.punchcard': 'Vilkår og Betingelser for Klippekort',
    'footer.policies.title': 'Politikker', 'footer.policies.privacy': 'Privatlivspolitik', 'footer.policies.cookie': 'Cookiepolitik', 'footer.rights': 'Alle rettigheder forbeholdes', 'footer.copyright': '© 2026 Boulders. Alle rettigheder forbeholdes.',
    'cookie.banner.title': 'Vi bruger cookies', 'cookie.banner.description': 'Vi bruger cookies til at forbedre din browsingoplevelse, analysere trafik på sitet og personalisere indhold. Ved at klikke på "Accepter alle" giver du samtykke til vores brug af cookies. Du kan administrere dine præferencer eller læse mere i vores <a href="#" data-action="open-terms" data-terms-type="cookie" class="cookie-banner-link">Cookiepolitik</a>.', 'cookie.banner.accept': 'Accepter alle', 'cookie.banner.reject': 'Afvis alle', 'cookie.banner.settings': 'Tilpas',
    'cookie.settings.title': 'Cookie-indstillinger', 'cookie.settings.description': 'Administrer dine cookie-præferencer. Du kan aktivere eller deaktivere forskellige typer cookies nedenfor. Essentielle cookies kan ikke deaktiveres, da de er nødvendige for, at hjemmesiden fungerer.', 'cookie.settings.save': 'Gem præferencer', 'cookie.settings.button': 'Cookie-indstillinger',
    'cookie.category.essential.title': 'Essentielle Cookies', 'cookie.category.essential.desc': 'Disse cookies er nødvendige for, at hjemmesiden fungerer og kan ikke deaktiveres.',
    'cookie.category.analytics.title': 'Analyse Cookies', 'cookie.category.analytics.desc': 'Disse cookies hjælper os med at forstå, hvordan besøgende interagerer med vores hjemmeside ved at indsamle og rapportere information anonymt.',
    'cookie.category.marketing.title': 'Marketing Cookies', 'cookie.category.marketing.desc': 'Disse cookies bruges til at levere annoncer og spore kampagneeffektivitet.',
    'cookie.category.functional.title': 'Funktionelle Cookies', 'cookie.category.functional.desc': 'Disse cookies muliggør forbedret funktionalitet og personalisering, såsom at huske dine præferencer.',
    'addons.intro': 'Forbedre din klatreoplevelse med vores add-on produkter.',
    'addons.modal.title': 'Tilføj til dit medlemskab',
    'addons.modal.boost.title': 'Boost dit medlemskab',
    'addons.modal.hint': 'Gør klatringen federe med udvalgte tilføjelser. Hentes ved dit næste besøg.',
    'addons.modal.skip': 'Spring over',
    'addons.modal.continue': 'Fortsæt',
    'addons.modal.close': 'Luk',
    'addons.modal.showDescription': 'Vis fuld beskrivelse',
    'addons.modal.collapseDescription': 'Skjul beskrivelse',
    'addons.modal.empty': 'Ingen boost produkter tilgængelige.',
    'addons.skipConfirm.title': 'Spring boost over?',
    'addons.skipConfirm.message': 'Du har ikke valgt ekstraudstyr. Vil du fortsætte uden?',
    'addons.skipConfirm.goBack': 'Tilbage',
    'addons.skipConfirm.skipAnyway': 'Fortsæt uden',
    'terms.tab.membership': 'Medlemskab / 15 Dage', 'terms.tab.punchcard': 'Klippekort',
    'cart.empty': 'Din kurv er tom', 'homeGym.tooltip.title': 'Du får adgang til alle haller.', 'homeGym.tooltip.desc': 'Dette er hallen hvor du henter dit kort.', 'homeGym.label': 'Hjemmehal:',
    'homeGym.tooltip.title.firstclimb': 'Din billet gælder i alle Boulders.',
    'search.noResults': 'Ingen haller fundet der matcher din søgning.',
    'cart.campaignWarning.message': 'Vigtigt: Hvis du går videre til betaling uden at gennemføre købet, kan du blive blokeret fra at købe kampagnen senere.',
    'modal.loading': 'Indlæser...',
    'modal.campaignRejection.title': 'Kampagne ikke tilgængelig',
    'modal.campaignRejection.message': 'Dette tilbud er ikke tilgængeligt for din konto. Dette kan skyldes at du har forladt betalingsvinduet uden at gennenmføre, et eksisterende abonnementer eller kampagnebegrænsning. Kontakt support hvis du tror dette er en fejl. Du kan stadig oprette et almindeligt medlemskab.',
    'modal.campaignRejection.option1': 'Se almindelig medlemskaber',
    'modal.campaignRejection.option2': 'Kontakt support',
    'modal.cartOfferExpired.title': 'Tilbuddet er udløbet',
    'modal.cartOfferExpired.message': 'Tiden til at gennemføre dit køb er udløbet. Du kan stadig forsøge at betale til den viste pris, eller gå tilbage og vælge et andet medlemskab.',
    'modal.cartOfferExpired.continue': 'Fortsæt til betaling',
    'modal.cartOfferExpired.chooseOther': 'Vælg andet tilbud',
    'faq.title': 'Ofte stillede spørgsmål',
    'faq.gyms.openingHours.q': 'Hvad er åbningstiderne?',
    'faq.gyms.openingHours.a': 'Åbningstiderne varierer mellem hallerne. Du kan finde de aktuelle åbningstider på vores hjemmeside eller ved at kontakte den specifikke hal.',
    'faq.gyms.openingHours.intro': 'Alle Boulders har åbent fra morgen til aften 361 dage om året:',
    'faq.gyms.openingHours.tableName': 'Hal',
    'faq.gyms.openingHours.tableOpen': 'Åben',
    'faq.gyms.access.q': 'Hvordan får jeg adgang til hallerne?',
    'faq.gyms.access.a': 'Efter gennemført køb, besøger du din valgte hal og henter dit adgangkort. Kortet er din nøgle til Boulders, som du scanner for at åbne lågerne.',
    'faq.gyms.events.q': 'Er der begivenheder og aktiviteter?',
    'faq.gyms.events.a': 'Ja! Vi afholder regelmæssigt begivenheder, konkurrencer og sociale aktiviteter på tværs af alle vores haller. Tjek vores hjemmeside eller app for at se kommende begivenheder.',
    'faq.gyms.gettingStarted.q': 'Hvordan kommer jeg godt i gang?',
    'faq.gyms.gettingStarted.a': 'Der er mange måder at komme godt i gang på. Vi anbefaler at deltage på et introhold eller et Fundamentals hold. Det er ikke nødvendigt at starte på et hold, men det kan være meget rart — og en rigtig god måde at kickstarte det sociale. Mange starter alene og finder hurtigt fællesskab i vores inkluderende klatrekultur. Du kan også læse vores kom-godt-igang guide.',
    'faq.membership.included.q': 'Hvad er inkluderet i mit medlemskab?',
    'faq.membership.included.a': 'Dit medlemskab inkluderer gratis introhold, ubegrænset adgang til alle Boulders haller, brug af alle klatreområder og faciliteter, samt adgang til begivenheder og aktiviteter og gratis +1 til Late Night Boulders.',
    'faq.membership.blocLife.q': 'Hvad er Bloc Life?',
    'faq.membership.blocLife.a': 'Bloc Life er Boulders\' medlemsfordelsprogram. Når du er medlem, er du automatisk en del af det. Jo længere du er medlem, jo flere fordele får du – rabatter på klatregrej, kaffe, snacks og eksklusiv adgang til nye haller. Fordele og niveauer opdateres automatisk.',
    'faq.membership.terms.q': 'Hvad er vilkårene og betingelserne?',
    'faq.membership.terms.a': 'Medlemskabet er et løbende abonnement med automatisk fornyelse. Der er ingen tilmelding eller opsigelsesgebyrer. Opsigelsesvarsel er resten af måneden plus 1 måned. Du kan læse de fulde vilkår og betingelser ved at klikke på linket i kurven.',
    'faq.membership.bindingPeriod.q': 'Er der bindingsperiode?',
    'faq.membership.bindingPeriod.a': 'Der er ikke bindingsperiode på Boulders\' medlemskaber uden for kampagner. Du kan til enhver tid melde dig ud med varsel på løbende måned plus en måned. Opretter du medlemskab på en prisnedsat kampagne, vil der være 3 måneders binding.',
    'faq.membership.freeze.q': 'Kan jeg fryse mit medlemskab?',
    'faq.membership.freeze.a': 'Ja, du kan fryse dit medlemskab i 1–3 måneder af gangen, op til 3 gange om året for 49 kr pr. gang. Du betaler ikke, mens du er i bero, og genaktivering er gratis. Frysning kan ikke aktiveres i bindingsperiode.',
    'faq.membership.cancellation.q': 'Hvordan opsiger jeg mit medlemskab?',
    'faq.membership.cancellation.a': 'Du kan opsige dit medlemskab når som helst med en kort varsel, som er resten af den aktuelle måned plus 1 måned. Kontakt medlem@boulders.dk eller log ind på din konto for at opsige.',
    'faq.15daypass.howItWorks.q': 'Hvordan virker 15-dages kortet?',
    'faq.15daypass.howItWorks.a': '15-dages kortet giver dig 15 dages ubegrænset adgang til alle Boulders haller fra den dag, du aktiverer det. Det er perfekt til at prøve klatring eller et kortvarigt besøg.',
    'faq.15daypass.validity.q': 'Hvor længe er kortet gyldigt?',
    'faq.15daypass.validity.a': '15-dages kortet er gyldigt i 15 dage fra aktiveringsdatoen. Du har ubegrænset adgang til alle haller, samt fri leje af klatresko, i denne periode.',
    'faq.15daypass.access.q': 'Hvilken adgang får jeg med kortet?',
    'faq.15daypass.access.a': 'Med 15-dages kortet får du ubegrænset adgang til alle Boulders haller, alle klatreområder og faciliteter i 15 dage. kortet kan ikke konverteres til et fuldt medlemskab.',
    'faq.freetrial.changeStartDate.q': 'Kan jeg ændre min startdato?',
    'faq.freetrial.changeStartDate.a': 'Ja, skriv til os på medlem@boulders.dk, så hjælper vi dig.',
    'faq.freetrial.changeStartDateCta': 'Vil du ændre startdatoen? Skriv til medlem@boulders.dk',
    'faq.freetrial.creditCard.q': 'Kræver det et kreditkort?',
    'faq.freetrial.creditCard.a': 'Nej, prøveperioden er helt gratis og kræver ingen betalingsoplysninger.',
    'firstclimb.category.title': 'Din første klatretur',
    'firstclimb.category.description': 'En dagsbillet til 99 kr inkl. lejesko og kalk. Kun for nye klatrere.',
    'firstclimb.cta': 'Køb dagsbillet',
    'firstclimb.banner.title': 'Din første klatretur · 99 kr',
    'firstclimb.banner.perks': 'Lejesko og kalk inkl. · Alle Boulders haller · Gyldig 1 måned',
    'firstclimb.existingCustomer.block': 'Du har allerede brugt dette tilbud.',
    'firstclimb.existingCustomer.title': 'Du har allerede brugt dette tilbud',
    'firstclimb.existingCustomer.body': 'Vores 99 kr dagsbillet kan kun købes én gang pr. konto, og du har allerede brugt din {email}. Du er stadig velkommen til at klatre — vælg et af vores andre produkter.',
    'firstclimb.existingCustomer.cta': 'Se andre adgangsmuligheder',
    'faq.firstclimb.included.q': 'Hvad er inkluderet i dagsbilletten?',
    'faq.firstclimb.included.a': 'Bliv så længe du vil i alle Boulders haller, plus lejesko og kalk. Ingen skjulte gebyrer.',
    'faq.firstclimb.eligibility.q': 'Hvem kan købe dagsbilletten?',
    'faq.firstclimb.eligibility.a': 'Alle kan købe den — men kun én gang pr. person.',
    'faq.firstclimb.validity.q': 'Hvor længe gælder den?',
    'faq.firstclimb.validity.a': 'Din billet skal bruges inden for 1 måned efter køb. Mød op hvilken som helst dag i den periode — du behøver ikke vælge en dato på forhånd.',
    'faq.firstclimb.howToUse.q': 'Hvordan bruger jeg den i hallen?',
    'faq.firstclimb.howToUse.a': 'Mød op i hallen, når det passer dig (inden for en måned). Oplys dit telefonnummer eller email til personalet, så aktiverer de din dagsbillet og udleverer lejesko og kalk.',
    'faq.firstclimb.afterDay.q': 'Hvad sker der efter min første dag?',
    'faq.firstclimb.afterDay.a': 'Bliv hængende! Du kan opgradere til medlemskab, et 15-dages kort eller et klippekort direkte hos personalet — eller online når du er klar.',
    'faq.punchcard.howItWorks.q': 'Hvordan virker klippekortet?',
    'faq.punchcard.howItWorks.a': 'Klippekortet giver dig én (1) indgang pr klip, i alle Boulders haller. Hver gang du besøger en hal, scanner du kortet og bruger ét (1) klip pr. person. Kortet er gyldigt i 12 måneder og kan deles med andre.',
    'faq.punchcard.convert.q': 'Kan jeg konvertere mit klippekort til et medlemskab?',
    'faq.punchcard.convert.a': 'Klippekort kan altid konverteres til et medlemskab ved at sende en mail til medlem@boulders.dk.',
    'faq.punchcard.remainingClips.q': 'Hvad gør jeg med resterende klip på mit klippekort?',
    'faq.punchcard.remainingClips.a': 'Hvis du stadig har klip på dit klippekort, men er klar til medlemskab, kan du få det ombyttet til medlemskab. For ombytning skal du sende en mail til medlem@boulders.dk. Eller giv det som gave til en, der fortjener det.',
    'faq.punchcard.multiple.q': 'Kan jeg have flere klippekort?',
    'faq.punchcard.multiple.a': 'Du kan have ubegrænset antal klippekort tilknyttet din profil.',
    'faq.productChoice.difference.q': 'Hvad er forskellen mellem medlemskab, 15-dages pas og klippekort?',
    'faq.productChoice.difference.a': 'Medlemskab er et løbende abonnement med ubegrænset adgang. 15-dages kortet giver 15 dages ubegrænset adgang – ideelt til at prøve klatring. Klippekortet giver 10 indgange, er gyldigt i 12 måneder og kan deles med andre.',
    'faq.productChoice.membershipBest.q': 'Hvornår vælger jeg medlemskab?',
    'faq.productChoice.membershipBest.a': 'Vælg medlemskab hvis du klatrer mindst én gang om ugen. Du får ubegrænset adgang til alle haller, ingen tilmeldings- eller opsigelsesgebyrer, og du kan opsige når som helst med kort varsel.',
    'faq.productChoice.15daypassBest.q': 'Hvornår skal jeg vælge 15-dages kortet?',
    'faq.productChoice.15daypassBest.a': 'Vælg 15-dages kortet hvis du vil prøve klatring eller kun har brug for adgang i en kort periode. Du får 15 dages ubegrænset adgang til alle haller + lejesko, fra den dag, du aktiverer det.',
    'faq.productChoice.punchcardBest.q': 'Hvornår vælger jeg klippekort?',
    'faq.productChoice.punchcardBest.a': 'Vælg klippekort hvis du klatrer en gang imellem eller vil dele adgang med andre. Du får 10 (medmindre andet er angivet) indgange til alle haller, kortet er gyldigt i 12 måneder, og du kan genopfylde inden for 14 dage efter sidste klip og få 100 kr rabat.',
  },
  'en-GB': {
    'step.homeGym': 'Home Gym', 'step.access': 'Access', 'step.boost': 'Boost', 'step.send': 'Send',
    'category.campaign': 'Membership Campaign', 'category.campaign.desc': 'Limited membership campaign, expires April 8, 2026. Get the offer before it expires. Can only be used by people who have not been members at Boulders within the last 6 months.', 'category.campaign.subtitle': 'Start now and save big! Only 3 months commitment. No hidden fees. <a href="#" data-action="open-terms" data-terms-type="terms" onclick="event.preventDefault();">Terms and conditions</a> apply.', 'category.campaign.endsIn': 'Ends in', 'category.membership': 'Membership', 'category.membership.subtitle': 'Unlimited access at all Boulders + loyalty program + extra member benefits.', 'category.15daypass': '15-Day Trial Pass', 'category.15daypass.subtitle': 'Try Boulders with 15 days of unlimited climbing incl. shoes. Start whenever it suits you.', 'category.punchcard': 'Punch Card', 'category.punchcard.subtitle': 'Climb once in a while or a couple times a month? The punch card is for you.',
    'category.membership.desc': 'Membership is an ongoing subscription with automatic renewal. No signup or cancellation fees. Notice period is the rest of the month + 1 month.',
    'category.15daypass.desc': 'Get 15 days of unlimited access to all gyms. Perfect for trying out climbing or a short-term visit.',
    'category.punchcard.desc': 'Each entry costs 1 punch, and gives access to all gyms and facilities. Refill within 14 days after your last punch and get 100 kr discount at the gym. Can be converted to membership later.',
    'activationDate.label': 'When do you want your trial period to start?',
    'activationDate.now': 'Activate now',
    'activationDate.now.desc': 'Start today',
    'activationDate.later': 'Activate on specific date',
    'activationDate.later.desc': 'Choose a future start date',
    'activationDate.pickLabel': 'Select activation date',
    'activationDate.hint': 'Select a date today or in the future.',
    'activationDate.lastDay': 'Last valid day: {date}',
    'activationDate.change': 'Change',
    'activationDate.changeFailed': 'Unable to change date. Please complete your purchase or start over.',
    'activationConfirm.title': 'Confirm your selection',
    'activationConfirm.subtitle': 'Please confirm when your pass should start before continuing to checkout.',
    'activationConfirm.subtitle.freetrial': 'Confirm when your trial period should start.',
    'activationConfirm.startLabel': 'Starts',
    'activationConfirm.endLabel': 'Valid until',
    'activationConfirm.today': 'Today',
    'activationConfirm.continue': 'Continue to checkout',
    'activationConfirm.continue.freetrial': 'Activate trial period',
    'header.selectedGym': 'Selected Gym:', 'gym.headsUp': 'Home gym selected:', 'access.headsUp': 'Access type selected:',
    'main.subtitle.step1': 'Choose your home gym', 'main.subtitle.step1.secondary': 'This is where you will primarily train − you will have access to all gyms.',
    'main.subtitle.step2': 'Choose your access type', 'main.subtitle.step2.secondary': 'Choose membership if you climb at least once a week.',
    'main.subtitle.step3': 'Would you like fries with that?', 'main.subtitle.step4': 'Send',
    'button.next': 'Next', 'button.back': 'Back', 'button.continue': 'Continue', 'button.skip': 'Skip', 'button.complete': 'Complete', 'button.edit': 'Edit', 'button.select': 'Select', 'button.addToCart': 'Add to cart', 'button.added': 'Added!',
    'button.findNearest': 'Find nearest gym', 'button.searchGyms': 'Search gyms...', 'button.apply': 'Apply', 'button.removeCoupon': 'Remove', 'gym.nearest': 'Nearest',
    'form.email': 'E-mail*', 'form.email.placeholder': 'E-mail', 'form.password': 'Password*', 'form.password.placeholder': 'Password',
    'form.forgotPassword': 'Forgot password?', 'form.login': 'Log in', 'form.createAccount': 'Create account', 'form.loggedInAs': 'Logged in as', 'form.address': 'Address:',
    'form.firstName': 'First name*', 'form.firstName.placeholder': 'First Name', 'form.lastName': 'Last name*', 'form.lastName.placeholder': 'Last name',
    'form.dateOfBirth': 'Date of birth*', 'form.streetAddress': 'Street and house number*', 'form.streetAddress.placeholder': 'Street and house nr',
    'form.postalCode': 'Postal code*', 'form.postalCode.placeholder': 'Zipcode', 'form.city': 'City', 'form.city.placeholder': 'Auto',
    'form.email.create': 'E-mail*', 'form.email.create.placeholder': 'Enter your email', 'form.country': 'Country', 'form.phoneNumber': 'Mobile number*',
    'form.phoneNumber.placeholder': '12345678', 'form.password.create': 'Password*', 'form.password.create.placeholder': 'Create a password',
    'form.confirmPassword': 'Confirm password*', 'form.confirmPassword.placeholder': 'Confirm your password', 'form.saveAccount': 'Save Account',
    'form.buyer': 'Buyer', 'form.createProfile': 'CREATE PROFILE', 'form.parentGuardian': 'Parent/Guardian Information',
    'form.parentFullName': 'First and last name*', 'form.parentFullName.placeholder': 'Enter your full name',
    'form.parentDateOfBirth': 'Date of birth*', 'form.parentStreetAddress': 'Street and house number*', 'form.parentStreetAddress.placeholder': 'Enter street and house number',
    'form.parentPostalCode': 'Postal code*', 'form.parentPostalCode.placeholder': '1234', 'form.parentCity': 'City', 'form.parentCity.placeholder': 'Copenhagen',
    'form.parentEmail': 'E-mail*', 'form.parentEmail.placeholder': 'Enter your email', 'form.parentCountryCode': 'Country',
    'form.parentPhoneNumber': 'Mobile number*', 'form.parentPhoneNumber.placeholder': '12345678', 'form.sameAddress': 'Same address and contact information',
    'form.error.firstName': 'Please enter your first name', 'form.error.lastName': 'Please enter your last name',
    'form.error.email': 'Please enter a valid email address',
    'form.resetPassword': 'RESET PASSWORD', 'form.resetPassword.desc': 'Enter your email address and we\'ll send you instructions to reset your password.',
    'form.resetPassword.success': 'Password reset instructions have been sent to your email.', 'form.sendResetLink': 'SEND RESET LINK',
    'button.cancel': 'Cancel', 'button.close': 'Close',
    'form.authSwitch.login': 'Login', 'form.authSwitch.createAccount': 'Create Account',
    'cart.title': 'Cart', 'cart.completeIn': 'Complete in', 'cart.offerExpiresIn': 'Offer expires in', 'cart.timeLeft': 'Time left', 'cart.timeToComplete': 'Time left to complete:', 'cart.subtotal': 'Subtotal', 'cart.discount': 'Discount code', 'cart.discount.placeholder': 'Discount code', 'cart.discountAmount': 'Discount', 'cart.discountAmountWithPercent': 'Discount ({percent})', 'cart.discount.applied': 'Discount code applied successfully!', 'cart.discount.removed': 'Discount code removed.', 'cart.discount.removeFailed': 'Failed to remove coupon. Please try again.', 'cart.discount.empty': 'Enter a discount code', 'cart.discount.loginRequired': 'Log in or create an account to use a discount code', 'cart.discount.locationRequired': 'Select a gym first', 'cart.discount.selectProduct': 'Select a membership or punch card first', 'cart.discount.notFound': 'Discount code not found. Check the code and try again.', 'cart.discount.invalid': 'Invalid discount code. Check the code and try again.', 'cart.discount.expired': 'This discount code has expired.', 'cart.discount.alreadyUsed': 'This discount code has already been used.', 'cart.discount.notApplicable': 'This discount code does not apply to your order.', 'cart.discount.forbidden': 'This discount code cannot be used on this order.', 'cart.discount.genericFailed': 'Could not apply the discount code. Please try again.', 'cart.discount.noOrder': 'No order found. Refresh the page and try again.', 'cart.discount.methodNotSupported': 'Discount code could not be applied. Please contact support.', 'cart.discount.applying': 'Applying…', 'cart.discount.creatingOrder': 'Creating order…', 'cart.total': 'Total', 'cart.payNow': 'Pay now', 'cart.monthlyFee': 'Monthly payment', 'cart.firstMonth': 'First month', 'cart.validUntil': 'Valid until', 'cart.punch.one': '1 punch', 'cart.punch.label': 'punches',
    'quantity.label': 'Choose quantity',
    'cart.membershipDetails': 'Membership Details', 'cart.membershipNumber': 'Membership Number:', 'cart.membershipActivation': 'Membership activation & auto-renewal setup', 'cart.memberName': 'Member Name:',
    'cart.period': 'Period', 'cart.paymentMethod': 'Choose payment method', 'cart.paymentRedirect': 'You will be redirected to our secure payment provider to complete your payment.',
    'cart.consent.terms': 'I accept the <a href="#" data-action="open-terms" data-terms-type="terms" onclick="event.preventDefault();">Terms and Conditions</a>.*',
    'cart.consent.terms.firstclimb': 'I understand that I will sign the <a href="#" data-action="open-terms" data-terms-type="liability-waiver" onclick="event.preventDefault();">liability waiver</a> when redeeming the ticket.*',
    'liability.waiver.title': 'Liability Disclaimer',
    'cart.consent.privacy': 'I accept the <a href="#" data-action="open-terms" data-terms-type="privacy" onclick="event.preventDefault();">Privacy Policy</a>*.',
    'cart.consent.marketing': 'I want to receive <a href="#" data-action="open-terms" data-terms-type="email-consent" onclick="event.preventDefault();">Marketing Emails</a>.',
    'consent.email.explainer': 'E-mail<br><br>I give consent for Boulders to send me emails and keep me updated with events and initiatives, training inspiration, offers and other services.',
    'cart.consent.sms': 'I want to receive <a href="#" data-action="open-terms" data-terms-type="sms-consent" onclick="event.preventDefault();">SMS Messages.</a>.',
    'consent.sms.explainer': 'SMS<br><br>I give consent for Boulders to send me SMS messages and keep me updated with events and initiatives, training inspiration, offers and other services.',
    'cart.cardPayment': 'Card payment', 'cart.checkout': 'Checkout', 'cart.faqHelp': 'Questions? See FAQ', 'step4.completePurchase': 'Complete your purchase',
    'step4.loginPrompt': 'Log in to your existing account or create a new one.',
    'cart.boundUntil': 'bound until', 'cart.billingPeriodConfirmed': 'Billing period confirmed after checkout.',
    'cart.tier.firstMonths': 'First {months} months', 'cart.tier.afterMonths': 'After {months} months', 'cart.tier.introRate': 'Introductory rate', 'cart.tier.commitment3': 'Only 3 months commitment', 'cart.tier.cancelMonthToMonth': 'Cancel month to month', 'cart.tier.savings': 'SAVE {amount} KR.',
    'cart.payNowTooltip.title': 'How the amount is calculated', 'cart.payNowTooltip.line1': 'Sign up on the 1st-15th: You only pay for the rest of this month.', 'cart.payNowTooltip.line2': 'Sign up on the 16th or later: Rest of this month + full next month.', 'cart.payNowIncludesAddons': 'Includes selected add-ons (+{amount})',
    'offer.membership.title': 'Membership Campaign', 'offer.membership.description': 'Start now and save big! Only 3 months commitment. No hidden fees. <a href="#" data-action="open-terms" data-terms-type="terms" onclick="event.preventDefault();">Terms and conditions</a> apply.',
    'message.noProducts.membership': 'No membership options available at this time.',
    'message.noProducts.punchcard': 'No punch card options available at this time.',
    'message.noProducts.15daypass': 'No 15-Day Trial Pass options available at this time.',
    'confirmation.title': 'SUCCESS!',
    'confirmation.message': 'Your order has been confirmed! You\'ll receive an email with all the details shortly.',
    'confirmation.message.membership': 'Your membership has been confirmed! You\'ll receive an email with all the details shortly.',
    'confirmation.message.15daypass': 'Your 15-Day Trial Period has been confirmed! You\'ll receive an email with all the details shortly.',
    'confirmation.message.punchcard': 'Your punch card has been confirmed! You\'ll receive an email with all the details shortly.',
    'confirmation.message.firstclimb': 'Your first climb is ready! You\'ll receive an email with all the details shortly.',
    'confirmation.nextStep2.firstclimb': 'Your day ticket is ready — drop by the gym whenever it suits you (within one month).',
    'confirmation.nextStep3.firstclimb': 'When you arrive: give your phone number or email, and we\'ll activate the ticket and hand you rental shoes and chalk. Remember you\'ll need to sign the liability waiver.',
    'confirmation.message.generic': 'Your order has been confirmed! You\'ll receive an email with all the details shortly.',
    'confirmation.orderDetails': 'Order Details',
    'confirmation.orderNumber': 'Order Number:',
    'confirmation.date': 'Date:',
    'confirmation.total': 'Total:',
    'confirmation.showReceipt': 'Show detailed receipt',
    'confirmation.receipt.title': 'Order confirmation',
    'confirmation.receipt.order': 'Order',
    'confirmation.receipt.orderOverview': 'Order overview',
    'confirmation.receipt.subtotal': 'Subtotal:',
    'confirmation.receipt.discount': 'Discount:',
    'confirmation.receipt.total': 'TOTAL:',
    'confirmation.receipt.vatInclusive': 'including VAT (25%):',
    'confirmation.receipt.payments': 'Payments',
    'confirmation.receipt.paymentMethod': 'Payment method:',
    'confirmation.receipt.amountPaid': 'Amount paid:',
    'confirmation.receipt.orderNumber': 'Order Number:',
    'confirmation.receipt.sellerInfo': 'Seller information',
    'confirmation.receipt.sellerName': 'Seller name:',
    'confirmation.receipt.address': 'Address:',
    'confirmation.receipt.cvr': 'CVR number:',
    'confirmation.receipt.phone': 'Phone:',
    'confirmation.receipt.email': 'Email:',
    'confirmation.receipt.customerInfo': 'Customer information',
    'confirmation.receipt.customerName': 'Customer name:',
    'confirmation.receipt.valueCardsPurchased': 'Value cards purchased',
    'confirmation.receipt.close': 'CLOSE',
    'confirmation.primaryGym': 'Primary Gym:',
    'confirmation.membershipType': 'Type:',
    'confirmation.monthlyPrice': 'Monthly Price:',
    'confirmation.15daypassDetails': '15-Day Trial Pass Details',
    'confirmation.passType': 'Pass Type:',
    'confirmation.validFrom': 'Valid From:',
    'confirmation.validUntil': 'Valid Until:',
    'confirmation.punchCardDetails': 'Punch Card Details',
    'confirmation.name': 'Name:',
    'confirmation.cardType': 'Card Type:',
    'confirmation.quantity': 'Quantity:',
    'confirmation.nextStepsTitle': 'What happens next?',
    'confirmation.nextStep1': 'Email confirmation sent to your inbox',
    'confirmation.nextStep2.membership': 'Membership activation & auto-renewal setup',
    'confirmation.nextStep2.15daypass': 'Your pass is active and ready to use',
    'confirmation.nextStep2.15daypass.future': 'Your pass becomes active on {date}',
    'confirmation.nextStep2.freetrial': 'Your trial period is activated and ready to use.',
    'confirmation.nextStep2.freetrial.future': 'Your trial period starts on {date}. We will activate it automatically.',
    'confirmation.nextStep2.punchcard': 'Your punch card is ready to use',
    'confirmation.nextStep3.membership': 'Pick up your membership card at the gym',
    'confirmation.nextStep3.15daypass': 'Visit the gym to start using your pass',
    'confirmation.nextStep3.freetrial': 'When your trial starts, visit the gym to connect your access card to your account. Share your phone number with staff and they will help you set everything up.',
    'confirmation.nextStep3.freetrial.today': 'Visit the gym today to connect your access card to your account. Share your phone number with staff and they will help you set everything up.',
    'confirmation.nextStep3.freetrial.future': 'When your trial starts, visit the gym to connect your access card to your account. Share your phone number with staff and they will help you set everything up.',
    'confirmation.nextStep3.punchcard': 'Visit the gym to start using your punches',
    'confirmation.freetrial.changeActivationCta': 'Need to change activation day? Click here.',
    'invite.title': 'Give your friends 2 weeks free',
    'invite.subtitle': 'Share your link — when your friends sign up, they get a free 2-week trial.',
    'invite.copyLink': 'Copy link',
    'invite.copied': 'Copied!',
    'invite.copiedToast': 'Link copied — share it with your friends!',
    'invite.copyFailed': 'Could not copy link. Please copy it manually.',
    'invite.instagramToast': 'Link copied — paste it into your Instagram DM or story!',
    'invite.shareVia': 'or share via',
    'invite.share.sms': 'Messages',
    'invite.share.email': 'Email',
    'invite.share.more': 'More',
    'invite.shareMessage': 'Hey! {name} here — I just joined Boulders. Climb with me and get a free 2-week trial on me:',
    'invite.firstclimb.title': 'Share this with your friends',
    'invite.firstclimb.subtitle': 'Our 99 kr day ticket including rental shoes and chalk is open to anyone who hasn’t tried it yet. Send the link to your friends.',
    'invite.firstclimb.footnote': 'The offer can only be used once per person.',
    'invite.firstclimb.shareMessage': 'Come climb with me at Boulders! Get your first day for 99 kr including rental shoes and chalk:',
    'invite.shareSubject': '2 weeks of free climbing at Boulders',
    'invite.footnote': 'Your friends get 2 weeks free entrance and rental shoes. No credit card required to start. Can only be claimed by people who have not claimed a trial pass previously.',
    'confirmation.pending.title': 'Payment Pending',
    'confirmation.pending.message': 'Your payment is being processed. We\'re waiting for confirmation from the payment provider. Your membership will be activated once payment is confirmed. Order #',
    'confirmation.pending.stillProcessing': 'Payment is still being processed. Please check back in a few minutes or contact support if you\'ve completed payment. Order #',
    'footer.terms.title': 'Terms and Conditions', 'footer.terms.all': 'Terms and Conditions', 'footer.terms.membership': 'Terms and Conditions for Membership', 'footer.terms.punchcard': 'Terms and Conditions for Punch Card',
    'footer.policies.title': 'Policies', 'footer.policies.privacy': 'Privacy Policy', 'footer.policies.cookie': 'Cookie Policy', 'footer.rights': 'All rights reserved', 'footer.copyright': '© 2026 Boulders. All rights reserved.',
    'cookie.banner.title': 'We use cookies', 'cookie.banner.description': 'We use cookies to enhance your browsing experience, analyze site traffic, and personalize content. By clicking "Accept All", you consent to our use of cookies. You can manage your preferences or learn more in our <a href="#" data-action="open-terms" data-terms-type="cookie" class="cookie-banner-link">Cookie Policy</a>.', 'cookie.banner.accept': 'Accept All', 'cookie.banner.reject': 'Reject All', 'cookie.banner.settings': 'Customize',
    'cookie.settings.title': 'Cookie Preferences', 'cookie.settings.description': 'Manage your cookie preferences. You can enable or disable different types of cookies below. Essential cookies cannot be disabled as they are necessary for the website to function.', 'cookie.settings.save': 'Save Preferences', 'cookie.settings.button': 'Cookie Settings',
    'cookie.category.essential.title': 'Essential Cookies', 'cookie.category.essential.desc': 'These cookies are necessary for the website to function and cannot be disabled.',
    'cookie.category.analytics.title': 'Analytics Cookies', 'cookie.category.analytics.desc': 'These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously.',
    'cookie.category.marketing.title': 'Marketing Cookies', 'cookie.category.marketing.desc': 'These cookies are used to deliver advertisements and track campaign effectiveness.',
    'cookie.category.functional.title': 'Functional Cookies', 'cookie.category.functional.desc': 'These cookies enable enhanced functionality and personalization, such as remembering your preferences.',
    'addons.intro': 'Enhance your climbing experience with our add-on products.',
    'addons.modal.title': 'Add to your membership',
    'addons.modal.boost.title': 'Boost your membership',
    'addons.modal.hint': 'Upgrade your climbing sessions with selected additions. Pick it up at your next visit.',
    'addons.modal.skip': 'Skip',
    'addons.modal.continue': 'Continue',
    'addons.modal.close': 'Close',
    'addons.modal.showDescription': 'Show full description',
    'addons.modal.collapseDescription': 'Collapse description',
    'addons.modal.empty': 'No boost products available.',
    'addons.skipConfirm.title': 'Skip boost?',
    'addons.skipConfirm.message': 'You have not selected any extras. Continue without them?',
    'addons.skipConfirm.goBack': 'Back',
    'addons.skipConfirm.skipAnyway': 'Continue without',
    'terms.tab.membership': 'Membership/Trial', 'terms.tab.punchcard': 'Punch Card',
    'cart.empty': 'Your cart is empty', 'homeGym.tooltip.title': 'You get access to all gyms.', 'homeGym.tooltip.desc': 'This is the gym where you pick up your card.', 'homeGym.label': 'Home Gym:',
    'homeGym.tooltip.title.firstclimb': 'Your ticket is valid at any Boulders.',
    'search.noResults': 'No gyms found matching your search.',
    'cart.campaignWarning.message': 'Important: If you proceed to payment without completing the purchase, you may be blocked from purchasing this campaign later.',
    'modal.loading': 'Loading...',
    'modal.campaignRejection.title': 'Campaign Not Available',
    'modal.campaignRejection.message': 'This offer is not available for your account. This may be due to existing subscriptions or campaign eligibility rules. If you believe this is a mistake, contact support. You can still sign up for a regular membership.',
    'modal.campaignRejection.option1': 'Regular Membership',
    'modal.campaignRejection.option2': 'Contact Support',
    'modal.cartOfferExpired.title': 'Offer expired',
    'modal.cartOfferExpired.message': 'The time to complete your purchase has run out. You can still try to pay at the price shown, or go back and choose another membership.',
    'modal.cartOfferExpired.continue': 'Continue to payment',
    'modal.cartOfferExpired.chooseOther': 'Choose another offer',
    'faq.title': 'Frequently Asked Questions',
    'faq.gyms.openingHours.q': 'What are the opening hours?',
    'faq.gyms.openingHours.intro': 'All Boulders are open from morning to evening 361 days a year:',
    'faq.gyms.openingHours.tableName': 'Gym',
    'faq.gyms.openingHours.tableOpen': 'Open',
    'faq.gyms.access.q': 'How do I access the gyms?',
    'faq.gyms.access.a': 'After completed purchase, visit the selected gym, and pick up your access card. The card is your key to Boulders, which you scan when you arrive, to unlock the gates.',
    'faq.gyms.events.q': 'Are there events and activities?',
    'faq.gyms.events.a': 'Yes! We regularly host events, competitions, and social activities across all our gyms. Check our website to see upcoming events.',
    'faq.gyms.gettingStarted.q': 'How do I get started?',
    'faq.gyms.gettingStarted.a': 'There are many ways to get started. We recommend joining an Intro or a Fundamentals class. You don\'t have to start with a class, but it\'s a great way to meet people. Many start on their own and quickly find a community in our inclusive climbing culture. You can also read our getting-started guide.',
    'faq.membership.included.q': 'What is included in my membership?',
    'faq.membership.included.a': 'Your membership includes free intro class, unlimited access to all Boulders gyms, use of all climbing areas and facilities, and access to events and activities.',
    'faq.membership.blocLife.q': 'What is Bloc Life?',
    'faq.membership.blocLife.a': 'Bloc Life is Boulders\' membership loyalty program. When you\'re a member, you\'re automatically part of it. The longer you\'re a member, the more benefits you get – discounts on gear, coffee, snacks, and exclusive access to new gyms. Benefits and tiers update automatically.',
    'faq.membership.terms.q': 'What are the terms and conditions?',
    'faq.membership.terms.a': 'Membership is an ongoing subscription with automatic renewal. There are no signup or cancellation fees. Notice period is the rest of the month plus 1 month. You can read the full terms and conditions by clicking the link in the cart.',
    'faq.membership.bindingPeriod.q': 'Is there a commitment period?',
    'faq.membership.bindingPeriod.a': 'There is no commitment period on Boulders memberships outside of campaigns. You can cancel anytime with notice for the rest of the current month plus one month. If you sign up on a discounted campaign, there is a 3-month commitment period.',
    'faq.membership.freeze.q': 'Can I freeze my membership?',
    'faq.membership.freeze.a': 'Yes. You can freeze your membership for 1–3 months at a time, up to 3 times a year, for 49 kr per freeze. You are not charged while frozen, and reactivation is free. Freezing cannot be activated during a commitment period.',
    'faq.membership.cancellation.q': 'How do I cancel my membership?',
    'faq.membership.cancellation.a': 'You can cancel your membership at any time. The cancellation notice period is the rest of the current month plus 1 month. Contact medlem@boulders.dk or log into your account to cancel.',
    'faq.15daypass.howItWorks.q': 'How does the 15-Day  Trial Pass work?',
    'faq.15daypass.howItWorks.a': 'The 15-Day Trial Pass gives you 15 days of unlimited access to all Boulders gyms from the day you activate it. It\'s perfect for trying out climbing or a short-term visit.',
    'faq.15daypass.validity.q': 'How long is the pass valid?',
    'faq.15daypass.validity.a': 'The 15-Day Trial Pass is valid for 15 days from the activation date. You have unlimited access to all gyms during this period.',
    'faq.15daypass.access.q': 'What access do I get with the pass?',
    'faq.15daypass.access.a': 'With the 15-Day Trial Pass, you get unlimited access to all Boulders gyms, all climbing areas and facilities for 15 days. The pass cannot be converted to a full membership.',
    'faq.freetrial.changeStartDate.q': 'Can I change my start date?',
    'faq.freetrial.changeStartDate.a': 'Yes, write to us at medlem@boulders.dk and we will help you.',
    'faq.freetrial.changeStartDateCta': 'Want to change your start date? Write to medlem@boulders.dk',
    'faq.freetrial.creditCard.q': 'Does it require a credit card?',
    'faq.freetrial.creditCard.a': 'No, the trial period is completely free and requires no payment details.',
    'firstclimb.category.title': 'Your First Climb',
    'firstclimb.category.description': 'A day ticket for 99 kr including rental shoes and chalk. New climbers only.',
    'firstclimb.banner.title': 'Your First Climb · 99 kr',
    'firstclimb.banner.perks': 'Shoes & chalk included · Any Boulders gym · Valid 1 month',
    'firstclimb.cta': 'Buy day ticket',
    'firstclimb.existingCustomer.block': "You've already used this offer.",
    'firstclimb.existingCustomer.title': "You've already used this offer",
    'firstclimb.existingCustomer.body': "Our 99 kr day ticket is a one-per-customer offer, and {email} has already redeemed it. You're still welcome to climb — pick one of our other products.",
    'firstclimb.existingCustomer.cta': 'See other entry options',
    'faq.firstclimb.included.q': "What's included in the day ticket?",
    'faq.firstclimb.included.a': 'Stay as long as you like at any Boulders gym, plus rental shoes and chalk. No hidden fees.',
    'faq.firstclimb.eligibility.q': 'Who can buy the day ticket?',
    'faq.firstclimb.eligibility.a': 'Anyone can buy it — but only once per person.',
    'faq.firstclimb.validity.q': 'How long is it valid?',
    'faq.firstclimb.validity.a': 'Your ticket must be used within 1 month of purchase. Show up any day within that window — no need to pick a date in advance.',
    'faq.firstclimb.howToUse.q': 'How do I use it at the gym?',
    'faq.firstclimb.howToUse.a': 'Drop by whenever it suits you (within one month). Give the staff your phone number or email and they will activate your day ticket and hand you rental shoes and chalk.',
    'faq.firstclimb.afterDay.q': 'What happens after my first day?',
    'faq.firstclimb.afterDay.a': "Stick around! You can upgrade to a membership, a 15-day pass, or a punch card directly with our staff — or online when you're ready.",
    'cart.freePeriod': 'Free period',
    'cart.activateTrial': 'ACTIVATE TRIAL PERIOD',
    'cart.noCreditCardRequired': 'No credit card required',
    'faq.punchcard.howItWorks.q': 'How does the punch card work?',
    'faq.punchcard.howItWorks.a': 'The punch card gives you one (1) entry pr. punch, in all Boulders gyms. Each time you visit a gym, one (1) punch is used. The card is valid for 12 months and can be shared with others.',
    'faq.punchcard.convert.q': 'Can I convert my punch card to a membership?',
    'faq.punchcard.convert.a': 'Yes, you can convert your punch card to a membership. Contact us at medlem@boulders.dk if you want to convert your punch card to a membership.',
    'faq.punchcard.remainingClips.q': 'What do I do with remaining clips on my punch card?',
    'faq.punchcard.remainingClips.a': 'If you still have punches left but are ready for a membership, you can have it converted to a membership. To convert, email medlem@boulders.dk. Or give the remaining clips as a gift to someone who deserves it.',
    'faq.punchcard.multiple.q': 'Can I have multiple punch cards?',
    'faq.punchcard.multiple.a': 'Yes you can have unlimited punch cards on your profile.',
    'faq.productChoice.difference.q': 'What is the difference between membership, 15-Day Trial Pass, and punch card?',
    'faq.productChoice.difference.a': 'Membership is an ongoing subscription with unlimited access. The 15-Day Trial Pass gives you 15 days of unlimited access – ideal for trying out climbing. The punch card gives you typically 10 entries, unless anything else is specified, is valid for 12 months, and can be shared with others.',
    'faq.productChoice.membershipBest.q': 'When should I choose membership?',
    'faq.productChoice.membershipBest.a': 'Choose membership if you plan to climb at least once a week. You get unlimited access to all gyms, no signup or cancellation fees, and you can cancel anytime with short notice.',
    'faq.productChoice.15daypassBest.q': 'When should I choose the 15-Day Trial Pass?',
    'faq.productChoice.15daypassBest.a': 'Choose the 15-Day Trial Pass if you want to try climbing or only need short-term access. You get 15 days of unlimited access to all gyms from the day you activate it. The pass is valid for 15 days from the activation date.',
    'faq.productChoice.punchcardBest.q': 'When should I choose a punch card?',
    'faq.productChoice.punchcardBest.a': 'Choose a punch card if you climb occasionally or want to share access with others. You get 10 entries to all gyms, unless anything else is specified, the card is valid for 12 months, and you can refill within 14 days after your last clip for 100 kr off.',
  },
  'de-DE': {
    'step.homeGym': 'Heimhalle', 'step.access': 'Zugang', 'step.boost': 'Boost', 'step.send': 'Senden',
    'category.campaign': 'Kampagne', 'category.campaign.desc': 'Spezielle Werbeangebote und zeitlich begrenzte Kampagnen. Nutzen Sie diese exklusiven Angebote, solange sie verfügbar sind.', 'category.campaign.subtitle': 'Zeitlich begrenzte Angebote', 'category.campaign.endsIn': 'Endet in', 'category.membership': 'Mitgliedschaft', 'category.membership.subtitle': 'Laufendes Abonnement, unbegrenzter Zugang', 'category.15daypass': '15-Tage-Pass', 'category.15daypass.subtitle': 'Zeitweiliger Zugangspass', 'category.punchcard': 'Stempelkarte', 'category.punchcard.subtitle': '10 Eintritte, teilbare physische Karte',
    'category.membership.desc': 'Mitgliedschaft ist ein laufendes Abonnement mit automatischer Verlängerung. Keine Anmelde- oder Kündigungsgebühren. Kündigungsfrist ist der Rest des Monats + 1 Monat.',
    'category.15daypass.desc': 'Erhalten Sie 15 Tage unbegrenzten Zugang zu allen Hallen. Perfekt zum Ausprobieren des Kletterns oder für einen kurzen Besuch.',
    'category.punchcard.desc': 'Sie können jeweils 1 Art von Stempelkarte kaufen. Jeder Eintritt verwendet einen Stempel auf Ihrer Stempelkarte. Die Karte ist 5 Jahre gültig und beinhaltet keine Mitgliedschaftsvorteile. Füllen Sie innerhalb von 14 Tagen nach Ihrem letzten Stempel nach und erhalten Sie 100 kr Rabatt in der Halle.',
    'activationDate.label': 'Wann möchten Sie Ihren Pass aktivieren?',
    'activationDate.now': 'Sofort aktivieren',
    'activationDate.now.desc': 'Startet heute',
    'activationDate.later': 'An einem bestimmten Datum aktivieren',
    'activationDate.later.desc': 'Wählen Sie ein zukünftiges Startdatum',
    'activationDate.pickLabel': 'Aktivierungsdatum auswählen',
    'activationDate.hint': 'Wählen Sie ein Datum heute oder in der Zukunft.',
    'activationDate.lastDay': 'Letzter gültiger Tag: {date}',
    'activationDate.change': 'Ändern',
    'activationDate.changeFailed': 'Datum konnte nicht geändert werden. Bitte schließen Sie Ihren Kauf ab oder starten Sie neu.',
    'activationConfirm.title': 'Auswahl bestätigen',
    'activationConfirm.subtitle': 'Bitte bestätigen Sie, wann Ihr Pass starten soll, bevor Sie zur Zahlung fortfahren.',
    'activationConfirm.startLabel': 'Start',
    'activationConfirm.endLabel': 'Gültig bis',
    'activationConfirm.today': 'Heute',
    'activationConfirm.continue': 'Weiter zur Kasse',
    'header.selectedGym': 'Ausgewählte Halle:', 'gym.headsUp': 'Heimhalle ausgewählt:', 'access.headsUp': 'Zugangstyp ausgewählt:',
    'main.subtitle.step1': 'Wählen Sie Ihre Heimhalle', 'main.subtitle.step1.secondary': 'Hier trainieren Sie hauptsächlich − Sie haben Zugang zu allen Hallen.',
    'main.subtitle.step2': 'Wählen Sie Ihren Zugangstyp', 'main.subtitle.step2.secondary': 'Wählen Sie Mitgliedschaft, wenn Sie mindestens einmal im Monat klettern.',
    'main.subtitle.step3': 'Möchten Sie Pommes dazu?', 'main.subtitle.step4': 'Senden',
    'button.next': 'Weiter', 'button.back': 'Zurück', 'button.continue': 'Fortsetzen', 'button.skip': 'Überspringen', 'button.complete': 'Fertig', 'button.edit': 'Bearbeiten', 'button.select': 'Auswählen', 'button.addToCart': 'In den Warenkorb', 'button.added': 'Hinzugefügt!',
    'button.findNearest': 'Nächste Halle finden', 'button.searchGyms': 'Hallen suchen...', 'button.apply': 'Anwenden', 'button.removeCoupon': 'Entfernen', 'gym.nearest': 'Nächste',
    'form.email': 'E-Mail*', 'form.email.placeholder': 'E-Mail', 'form.password': 'Passwort*', 'form.password.placeholder': 'Passwort',
    'form.forgotPassword': 'Passwort vergessen?', 'form.login': 'Anmelden', 'form.createAccount': 'Konto erstellen', 'form.loggedInAs': 'Angemeldet als', 'form.address': 'Adresse:',
    'form.firstName': 'Vorname*', 'form.firstName.placeholder': 'Vorname', 'form.lastName': 'Nachname*', 'form.lastName.placeholder': 'Nachname',
    'form.dateOfBirth': 'Geburtsdatum*', 'form.streetAddress': 'Straße und Hausnummer*', 'form.streetAddress.placeholder': 'Straße und Hausnummer',
    'form.postalCode': 'Postleitzahl*', 'form.postalCode.placeholder': 'Postleitzahl', 'form.city': 'Stadt', 'form.city.placeholder': 'Auto',
    'form.email.create': 'E-Mail*', 'form.email.create.placeholder': 'Geben Sie Ihre E-Mail ein', 'form.country': 'Land', 'form.phoneNumber': 'Handynummer*',
    'form.phoneNumber.placeholder': '12345678', 'form.password.create': 'Passwort*', 'form.password.create.placeholder': 'Erstellen Sie ein Passwort',
    'form.confirmPassword': 'Passwort bestätigen*', 'form.confirmPassword.placeholder': 'Bestätigen Sie Ihr Passwort', 'form.saveAccount': 'Profil erstellen',
    'form.buyer': 'Käufer', 'form.createProfile': 'NEUES PROFIL', 'form.parentGuardian': 'Eltern/Erziehungsberechtigte Informationen',
    'form.parentFullName': 'Vorname und Nachname*', 'form.parentFullName.placeholder': 'Geben Sie Ihren vollständigen Namen ein',
    'form.parentDateOfBirth': 'Geburtsdatum*', 'form.parentStreetAddress': 'Straße und Hausnummer*', 'form.parentStreetAddress.placeholder': 'Geben Sie Straße und Hausnummer ein',
    'form.parentPostalCode': 'Postleitzahl*', 'form.parentPostalCode.placeholder': '1234', 'form.parentCity': 'Stadt', 'form.parentCity.placeholder': 'Kopenhagen',
    'form.parentEmail': 'E-Mail*', 'form.parentEmail.placeholder': 'Geben Sie Ihre E-Mail ein', 'form.parentCountryCode': 'Land',
    'form.parentPhoneNumber': 'Handynummer*', 'form.parentPhoneNumber.placeholder': '12345678', 'form.sameAddress': 'Gleiche Adresse und Kontaktinformationen',
    'form.error.firstName': 'Bitte geben Sie Ihren Vornamen ein', 'form.error.lastName': 'Bitte geben Sie Ihren Nachnamen ein',
    'form.error.email': 'Bitte geben Sie eine gültige E-Mail-Adresse ein',
    'form.resetPassword': 'PASSWORT ZURÜCKSETZEN', 'form.resetPassword.desc': 'Geben Sie Ihre E-Mail-Adresse ein und wir senden Ihnen Anweisungen zum Zurücksetzen Ihres Passworts.',
    'form.resetPassword.success': 'Anweisungen zum Zurücksetzen wurden an Ihre E-Mail gesendet.', 'form.sendResetLink': 'ZURÜCKSETZLINK SENDEN',
    'button.cancel': 'Abbrechen', 'button.close': 'Schließen',
    'form.authSwitch.login': 'Anmelden', 'form.authSwitch.createAccount': 'Konto erstellen',
    'firstclimb.category.title': 'Dein erster Kletterbesuch',
    'firstclimb.category.description': 'Eine Tageskarte für 99 kr inkl. Leihschuhe und Chalk. Nur für neue Kletterer.',
    'firstclimb.banner.title': 'Dein erster Kletterbesuch · 99 kr',
    'firstclimb.banner.perks': 'Leihschuhe & Chalk inkl. · Alle Boulders-Hallen · 1 Monat gültig',
    'firstclimb.cta': 'Tageskarte kaufen',
    'firstclimb.existingCustomer.block': 'Du hast dieses Angebot bereits genutzt.',
    'firstclimb.existingCustomer.title': 'Du hast dieses Angebot bereits genutzt',
    'firstclimb.existingCustomer.body': 'Unsere 99 kr Tageskarte gibt es einmal pro Konto, und {email} hat sie schon eingelöst. Du kannst trotzdem klettern — wähle eines unserer anderen Produkte.',
    'firstclimb.existingCustomer.cta': 'Andere Zugangsoptionen ansehen',
    'faq.firstclimb.included.q': 'Was ist in der Tageskarte enthalten?',
    'faq.firstclimb.included.a': 'Bleib so lange du willst in allen Boulders-Hallen, dazu Leihschuhe und Chalk. Keine versteckten Gebühren.',
    'faq.firstclimb.eligibility.q': 'Wer kann die Tageskarte kaufen?',
    'faq.firstclimb.eligibility.a': 'Jeder kann sie kaufen — aber nur einmal pro Person.',
    'faq.firstclimb.validity.q': 'Wie lange ist sie gültig?',
    'faq.firstclimb.validity.a': 'Deine Karte muss innerhalb von 1 Monat nach dem Kauf eingelöst werden. Komm an einem beliebigen Tag in diesem Zeitraum vorbei — du musst kein Datum vorab wählen.',
    'faq.firstclimb.howToUse.q': 'Wie nutze ich sie in der Halle?',
    'faq.firstclimb.howToUse.a': 'Komm einfach innerhalb des Monats vorbei. Gib dem Personal deine Telefonnummer oder E-Mail, dann aktivieren sie deine Tageskarte und händigen dir Leihschuhe und Chalk aus.',
    'faq.firstclimb.afterDay.q': 'Was passiert nach meinem ersten Tag?',
    'faq.firstclimb.afterDay.a': 'Bleib dran! Du kannst direkt vor Ort auf eine Mitgliedschaft, ein 15-Tage-Ticket oder eine Stempelkarte upgraden — oder online, wenn du bereit bist.',
    'cart.title': 'Warenkorb', 'cart.completeIn': 'Abschließen in', 'cart.offerExpiresIn': 'Angebot endet in', 'cart.timeLeft': 'Verbleibende Zeit', 'cart.timeToComplete': 'Verbleibende Zeit zum Abschließen:', 'cart.subtotal': 'Zwischensumme', 'cart.discount': 'Rabattcode', 'cart.discount.placeholder': 'Rabattcode', 'cart.discountAmount': 'Rabatt', 'cart.discountAmountWithPercent': 'Rabatt ({percent})', 'cart.discount.applied': 'Rabattcode angewendet!', 'cart.discount.removed': 'Rabattcode entfernt.', 'cart.discount.removeFailed': 'Rabattcode konnte nicht entfernt werden. Bitte versuchen Sie es erneut.', 'cart.discount.empty': 'Geben Sie einen Rabattcode ein', 'cart.discount.loginRequired': 'Melden Sie sich an oder erstellen Sie ein Konto, um einen Rabattcode zu verwenden', 'cart.discount.locationRequired': 'Wählen Sie zuerst eine Halle', 'cart.discount.selectProduct': 'Wählen Sie zuerst eine Mitgliedschaft oder eine Stempelkarte', 'cart.discount.notFound': 'Rabattcode nicht gefunden. Überprüfen Sie den Code und versuchen Sie es erneut.', 'cart.discount.invalid': 'Ungültiger Rabattcode. Überprüfen Sie den Code und versuchen Sie es erneut.', 'cart.discount.expired': 'Dieser Rabattcode ist abgelaufen.', 'cart.discount.alreadyUsed': 'Dieser Rabattcode wurde bereits verwendet.', 'cart.discount.notApplicable': 'Dieser Rabattcode gilt nicht für diese Bestellung.', 'cart.discount.forbidden': 'Dieser Rabattcode kann für diese Bestellung nicht verwendet werden.', 'cart.discount.genericFailed': 'Rabattcode konnte nicht angewendet werden. Bitte versuchen Sie es erneut.', 'cart.discount.noOrder': 'Keine Bestellung gefunden. Laden Sie die Seite neu und versuchen Sie es erneut.', 'cart.discount.methodNotSupported': 'Rabattcode konnte nicht angewendet werden. Bitte kontaktieren Sie den Support.', 'cart.discount.applying': 'Wird angewendet…', 'cart.discount.creatingOrder': 'Bestellung wird erstellt…', 'cart.total': 'Gesamt', 'cart.payNow': 'Jetzt bezahlen', 'cart.monthlyFee': 'Monatliche Zahlung', 'cart.firstMonth': 'Erster Monat', 'cart.validUntil': 'Gültig bis', 'cart.punch.one': '1 Stempel', 'cart.punch.label': 'Stempel',
    'quantity.label': 'Menge wählen',
    'cart.membershipDetails': 'Mitgliedschaftsdetails', 'cart.membershipNumber': 'Mitgliedsnummer:', 'cart.membershipActivation': 'Mitgliedschaftsaktivierung und automatische Verlängerung', 'cart.memberName': 'Mitgliedsname:',
    'cart.period': 'Periode', 'cart.paymentMethod': 'Zahlungsmethode wählen', 'cart.paymentRedirect': 'Sie werden zu unserem sicheren Zahlungsanbieter weitergeleitet, um Ihre Zahlung abzuschließen.',
    'cart.consent.terms': 'Ich akzeptiere die <a href="#" data-action="open-terms" data-terms-type="terms" onclick="event.preventDefault();">Allgemeinen Geschäftsbedingungen</a>.*',
    'cart.consent.terms.firstclimb': 'Ich verstehe, dass ich beim Einlösen des Tickets den <a href="#" data-action="open-terms" data-terms-type="liability-waiver" onclick="event.preventDefault();">Haftungsausschluss</a> unterschreiben werde.*',
    'liability.waiver.title': 'Haftungsausschluss',
    'cart.consent.privacy': 'Ich akzeptiere die <a href="#" data-action="open-terms" data-terms-type="privacy" onclick="event.preventDefault();">Datenschutzrichtlinie</a>.*',
    'cart.consent.marketing': 'Ich möchte <a href="#" data-action="open-terms" data-terms-type="email-consent" onclick="event.preventDefault();">Marketing-E-Mails</a> erhalten.',
    'consent.email.explainer': 'E-Mail<br><br>Ich gebe meine Einwilligung, dass Boulders mir E-Mails senden und mich über Veranstaltungen und Initiativen, Trainingsinspiration, Angebote und andere Dienstleistungen auf dem Laufenden halten darf.',
    'cart.consent.sms': 'Ich möchte <a href="#" data-action="open-terms" data-terms-type="sms-consent" onclick="event.preventDefault();">SMS-Nachrichten</a> erhalten.',
    'consent.sms.explainer': 'SMS<br><br>Ich gebe meine Einwilligung, dass Boulders mir SMS-Nachrichten senden und mich über Veranstaltungen und Initiativen, Trainingsinspiration, Angebote und andere Dienstleistungen auf dem Laufenden halten darf.',
    'cart.cardPayment': 'Kartenzahlung', 'cart.checkout': 'Zur Kasse', 'step4.completePurchase': 'Ihren Kauf abschließen',
    'step4.loginPrompt': 'Melden Sie sich in Ihrem bestehenden Konto an oder erstellen Sie ein neues.',
    'cart.boundUntil': 'gebunden bis', 'cart.billingPeriodConfirmed': 'Abrechnungszeitraum wird nach dem Kauf bestätigt.',
    'cart.tier.firstMonths': 'Erste {months} Monate', 'cart.tier.afterMonths': 'Nach {months} Monaten', 'cart.tier.introRate': 'Einführungspreis', 'cart.tier.commitment3': 'Nur 3 Monate Bindung', 'cart.tier.cancelMonthToMonth': 'Monatlich kündbar', 'cart.tier.savings': 'SPARE {amount} KR.',
    'cart.payNowTooltip.title': 'So wird der Betrag berechnet', 'cart.payNowTooltip.line1': 'Kauf am 1.-15.: Sie zahlen nur für den Rest dieses Monats.', 'cart.payNowTooltip.line2': 'Kauf am 16. oder später: Rest dieses Monats + der gesamte nächste Monat.', 'cart.payNowIncludesAddons': 'Ausgewählte Add-ons enthalten (+{amount})',
    'offer.membership.title': 'Mitgliedschaftsangebot', 'offer.membership.description': 'Wählen Sie das Mitgliedschaftsangebot, das am besten zu Ihnen passt. Einführungspreise können zeitlich begrenzt sein.',
    'message.noProducts.membership': 'Derzeit sind keine Mitgliedschaftsoptionen verfügbar.',
    'message.noProducts.punchcard': 'Derzeit sind keine Stempelkartenoptionen verfügbar.',
    'message.noProducts.15daypass': 'Derzeit sind keine 15-Tage-Pass-Optionen verfügbar.',
    'footer.terms.title': 'Allgemeine Geschäftsbedingungen', 'footer.terms.all': 'Allgemeine Geschäftsbedingungen', 'footer.terms.membership': 'Allgemeine Geschäftsbedingungen für Mitgliedschaft', 'footer.terms.punchcard': 'Allgemeine Geschäftsbedingungen für Stempelkarte',
    'footer.policies.title': 'Richtlinien', 'footer.policies.privacy': 'Datenschutzrichtlinie', 'footer.policies.cookie': 'Cookie-Richtlinie', 'footer.rights': 'Alle Rechte vorbehalten', 'footer.copyright': '© 2026 Boulders. Alle Rechte vorbehalten.',
    'cookie.banner.title': 'Wir verwenden Cookies', 'cookie.banner.description': 'Wir verwenden Cookies, um Ihr Browsing-Erlebnis zu verbessern, den Website-Verkehr zu analysieren und Inhalte zu personalisieren. Durch Klicken auf "Alle akzeptieren" stimmen Sie unserer Verwendung von Cookies zu. Sie können Ihre Einstellungen verwalten oder mehr in unserer <a href="#" data-action="open-terms" data-terms-type="cookie" class="cookie-banner-link">Cookie-Richtlinie</a> erfahren.', 'cookie.banner.accept': 'Alle akzeptieren', 'cookie.banner.reject': 'Alle ablehnen', 'cookie.banner.settings': 'Anpassen',
    'cookie.settings.title': 'Cookie-Einstellungen', 'cookie.settings.description': 'Verwalten Sie Ihre Cookie-Einstellungen. Sie können unten verschiedene Cookie-Typen aktivieren oder deaktivieren. Essenzielle Cookies können nicht deaktiviert werden, da sie für die Funktionsweise der Website erforderlich sind.', 'cookie.settings.save': 'Einstellungen speichern', 'cookie.settings.button': 'Cookie-Einstellungen',
    'cookie.category.essential.title': 'Essenzielle Cookies', 'cookie.category.essential.desc': 'Diese Cookies sind für die Funktionsweise der Website erforderlich und können nicht deaktiviert werden.',
    'cookie.category.analytics.title': 'Analyse-Cookies', 'cookie.category.analytics.desc': 'Diese Cookies helfen uns zu verstehen, wie Besucher mit unserer Website interagieren, indem sie Informationen anonym sammeln und melden.',
    'cookie.category.marketing.title': 'Marketing-Cookies', 'cookie.category.marketing.desc': 'Diese Cookies werden verwendet, um Werbung zu liefern und die Kampagneneffektivität zu verfolgen.',
    'cookie.category.functional.title': 'Funktionale Cookies', 'cookie.category.functional.desc': 'Diese Cookies ermöglichen erweiterte Funktionalität und Personalisierung, z. B. das Speichern Ihrer Einstellungen.',
    'addons.intro': 'Verbessern Sie Ihr Klettererlebnis mit unseren Add-on-Produkten.',
    'addons.modal.title': 'Zu Ihrer Mitgliedschaft hinzufügen',
    'addons.modal.boost.title': 'Ihre Mitgliedschaft boosten',
    'addons.modal.hint': 'Fügen Sie jetzt Ausrüstung zu einem Sonderpreis hinzu und holen Sie sie bei Ihrem nächsten Besuch ab.',
    'addons.modal.skip': 'Überspringen',
    'addons.modal.continue': 'Weiter',
    'addons.modal.close': 'Schließen',
    'addons.modal.showDescription': 'Vollständige Beschreibung anzeigen',
    'addons.modal.collapseDescription': 'Beschreibung einklappen',
    'addons.modal.empty': 'Keine Boost-Produkte verfügbar.',
    'addons.skipConfirm.title': 'Boost überspringen?',
    'addons.skipConfirm.message': 'Sie haben kein Zusatzprodukt ausgewählt. Ohne fortfahren?',
    'addons.skipConfirm.goBack': 'Zurück',
    'addons.skipConfirm.skipAnyway': 'Ohne fortfahren',
    'terms.tab.membership': 'Mitgliedschaft / 15 Tage', 'terms.tab.punchcard': 'Stempelkarte',
    'cart.empty': 'Ihr Warenkorb ist leer', 'homeGym.tooltip.title': 'Sie erhalten Zugang zu allen Hallen.', 'homeGym.tooltip.desc': 'Dies ist die Halle, in der Sie Ihre Karte abholen.', 'homeGym.label': 'Heimhalle:',
    'homeGym.tooltip.title.firstclimb': 'Dein Ticket gilt in allen Boulders.',
    'search.noResults': 'Keine Hallen gefunden, die Ihrer Suche entsprechen.',
    'cart.campaignWarning.message': 'Wichtig: Wenn Sie zur Zahlung fortfahren, ohne den Kauf abzuschließen, können Sie möglicherweise später daran gehindert werden, diese Kampagne zu kaufen.',
    'modal.loading': 'Lädt...',
    'modal.campaignRejection.title': 'Kampagne nicht verfügbar',
    'modal.campaignRejection.message': 'Dieses Angebot ist für Ihr Konto nicht verfügbar. Dies kann auf bestehende Abonnements oder Kampagnenberechtigungsregeln zurückzuführen sein. Sie können sich für eine reguläre Mitgliedschaft anmelden. Wenn Sie glauben, dass dies ein Fehler ist, kontaktieren Sie den Support.',
    'modal.campaignRejection.option1': 'Reguläre Mitgliedschaft',
    'modal.campaignRejection.option2': 'Support kontaktieren',
    'confirmation.title': 'ERFOLG!',
    'confirmation.message': 'Ihre Bestellung wurde bestätigt! Sie erhalten in Kürze eine E-Mail mit allen Details.',
    'confirmation.message.membership': 'Ihre Mitgliedschaft wurde bestätigt! Sie erhalten in Kürze eine E-Mail mit allen Details.',
    'confirmation.message.15daypass': 'Ihr 15-Tage-Pass wurde bestätigt! Sie erhalten in Kürze eine E-Mail mit allen Details.',
    'confirmation.message.punchcard': 'Ihre Stempelkarte wurde bestätigt! Sie erhalten in Kürze eine E-Mail mit allen Details.',
    'confirmation.message.firstclimb': 'Dein erster Kletterbesuch ist bereit! Du erhältst in Kürze eine E-Mail mit allen Details.',
    'confirmation.nextStep2.firstclimb': 'Deine Tageskarte ist bereit — komm vorbei, wann es dir passt (innerhalb eines Monats).',
    'confirmation.nextStep3.firstclimb': 'Wenn du ankommst: nenne deine Telefonnummer oder E-Mail, dann aktivieren wir die Karte und händigen dir Leihschuhe und Chalk aus. Denk daran, dass du den Haftungsausschluss unterschreiben musst.',
    'confirmation.message.generic': 'Ihre Bestellung wurde bestätigt! Sie erhalten in Kürze eine E-Mail mit allen Details.',
    'confirmation.orderDetails': 'Bestelldetails',
    'confirmation.orderNumber': 'Bestellnummer:',
    'confirmation.date': 'Datum:',
    'confirmation.total': 'Gesamt:',
    'confirmation.showReceipt': 'Detaillierte Quittung anzeigen',
    'confirmation.receipt.title': 'Bestellbestätigung',
    'confirmation.receipt.order': 'Bestellung',
    'confirmation.receipt.orderOverview': 'Bestellübersicht',
    'confirmation.receipt.subtotal': 'Zwischensumme:',
    'confirmation.receipt.discount': 'Rabatt:',
    'confirmation.receipt.total': 'GESAMT:',
    'confirmation.receipt.vatInclusive': 'inkl. MwSt. (25%):',
    'confirmation.receipt.payments': 'Zahlungen',
    'confirmation.receipt.paymentMethod': 'Zahlungsmethode:',
    'confirmation.receipt.amountPaid': 'Bezahlter Betrag:',
    'confirmation.receipt.orderNumber': 'Bestellnummer:',
    'confirmation.receipt.sellerInfo': 'Verkäuferinformationen',
    'confirmation.receipt.sellerName': 'Verkäufername:',
    'confirmation.receipt.address': 'Adresse:',
    'confirmation.receipt.cvr': 'CVR-Nummer:',
    'confirmation.receipt.phone': 'Telefon:',
    'confirmation.receipt.email': 'E-Mail:',
    'confirmation.receipt.customerInfo': 'Kundeninformationen',
    'confirmation.receipt.customerName': 'Kundenname:',
    'confirmation.receipt.valueCardsPurchased': 'Gekaufte Wertkarten',
    'confirmation.receipt.close': 'SCHLIESSEN',
    'confirmation.primaryGym': 'Heimhalle:',
    'confirmation.membershipType': 'Typ:',
    'confirmation.monthlyPrice': 'Monatlicher Preis:',
    'confirmation.15daypassDetails': '15-Tage-Pass Details',
    'confirmation.passType': 'Passtyp:',
    'confirmation.validFrom': 'Gültig von:',
    'confirmation.validUntil': 'Gültig bis:',
    'confirmation.punchCardDetails': 'Stempelkarten-Details',
    'confirmation.name': 'Name:',
    'confirmation.cardType': 'Kartentyp:',
    'confirmation.quantity': 'Menge:',
    'confirmation.nextStepsTitle': 'Was passiert als Nächstes?',
    'confirmation.nextStep1': 'E-Mail-Bestätigung an Ihren Posteingang gesendet',
    'confirmation.nextStep2.membership': 'Mitgliedschaftsaktivierung und automatische Verlängerung',
    'confirmation.nextStep2.15daypass': 'Ihr Pass ist aktiv und einsatzbereit',
    'confirmation.nextStep2.15daypass.future': 'Ihr Pass wird am {date} aktiv',
    'confirmation.nextStep2.punchcard': 'Ihre Stempelkarte ist einsatzbereit',
    'confirmation.nextStep3.membership': 'Holen Sie Ihre Mitgliedskarte in der Halle ab',
    'confirmation.nextStep3.15daypass': 'Besuchen Sie die Halle, um Ihren Pass zu nutzen',
    'confirmation.nextStep3.punchcard': 'Besuchen Sie die Halle, um Ihre Stempel zu nutzen',
    'confirmation.nextStep3.freetrial.today': 'Besuchen Sie die Halle heute, um Ihre Zugangskarte mit Ihrem Konto zu verknüpfen. Geben Sie dem Personal Ihre Telefonnummer, dann helfen sie Ihnen beim Start.',
    'confirmation.nextStep3.freetrial.future': 'Sobald Ihre Probezeit startet, besuchen Sie die Halle, um Ihre Zugangskarte mit Ihrem Konto zu verknüpfen. Geben Sie dem Personal Ihre Telefonnummer, dann helfen sie Ihnen beim Start.',
    'confirmation.freetrial.changeActivationCta': 'Müssen Sie den Aktivierungstag ändern? Klicken Sie hier',
    'invite.title': 'Schenk deinen Freunden 2 Wochen gratis',
    'invite.subtitle': 'Teile deinen Link – wenn sich deine Freunde anmelden, bekommen sie 2 Wochen gratis Probezeit.',
    'invite.copyLink': 'Link kopieren',
    'invite.copied': 'Kopiert!',
    'invite.copiedToast': 'Link kopiert – teile ihn mit deinen Freunden!',
    'invite.copyFailed': 'Link konnte nicht kopiert werden. Bitte kopiere ihn manuell.',
    'invite.instagramToast': 'Link kopiert – füge ihn in deine Instagram-DM oder Story ein!',
    'invite.shareVia': 'oder teilen über',
    'invite.share.sms': 'Nachrichten',
    'invite.share.email': 'E-Mail',
    'invite.share.more': 'Mehr',
    'invite.shareMessage': 'Hey! Hier ist {name} – ich habe mich gerade bei Boulders angemeldet. Klettere mit mir und hol dir 2 Wochen Probezeit auf mich:',
    'invite.firstclimb.title': 'Teile das mit deinen Freunden',
    'invite.firstclimb.subtitle': 'Unsere Tageskarte für 99 kr inkl. Leihschuhe und Chalk ist für alle, die sie noch nicht ausprobiert haben. Schick deinen Freunden den Link.',
    'invite.firstclimb.footnote': 'Das Angebot kann pro Person nur einmal eingelöst werden.',
    'invite.firstclimb.shareMessage': 'Klettere mit mir bei Boulders! Hol dir deinen ersten Tag für 99 kr inkl. Leihschuhe und Chalk:',
    'invite.shareSubject': '2 Wochen gratis Klettern bei Boulders',
    'invite.footnote': 'Deine Freunde bekommen 2 Wochen freien Eintritt inklusive Leihschuhe. Keine Kreditkarte erforderlich. Das Angebot gilt nur für Personen, die noch nie ein Probepass eingelöst haben.',
    'confirmation.pending.title': 'Zahlung ausstehend',
    'confirmation.pending.message': 'Ihre Zahlung wird bearbeitet. Wir warten auf die Bestätigung des Zahlungsanbieters. Ihre Mitgliedschaft wird nach Bestätigung der Zahlung aktiviert. Bestellung #',
    'confirmation.pending.stillProcessing': 'Die Zahlung wird noch bearbeitet. Bitte schauen Sie in einigen Minuten erneut vorbei oder kontaktieren Sie den Support, wenn Sie die Zahlung abgeschlossen haben. Bestellung #',
  },
};

// Get translation for current language
function t(key, fallback = '') {
  const lang = state.language || DEFAULT_LANGUAGE;
  return translations[lang]?.[key] || translations[DEFAULT_LANGUAGE]?.[key] || fallback || key;
}

// Update all translations on the page
function updatePageTranslations() {
  const lang = state.language || DEFAULT_LANGUAGE;
  const langCode = String(lang).split('-')[0];
  
  // Update elements with data-i18n-key attribute
  document.querySelectorAll('[data-i18n-key]').forEach(element => {
    const key = element.getAttribute('data-i18n-key');
    let translation = t(key);
    const activeLandingRoute = state.landingRouteConfig || resolveLandingRouteConfig();
    const isOfferLanding = activeLandingRoute?.componentName === 'LandingFirstMonthFree';
    if (isOfferLanding && key === 'category.campaign') {
      translation = t('offer.membership.title');
    } else if (isOfferLanding && key === 'category.campaign.desc') {
      translation = t('offer.membership.description');
    }

    // Simple placeholder substitution for dynamic translations.
    // Currently supports `{date}` when element provides `data-i18n-date-iso="YYYY-MM-DD"`.
    if (translation && translation.includes('{date}')) {
      const dateIso = element.getAttribute('data-i18n-date-iso');
      if (dateIso && /^\d{4}-\d{2}-\d{2}$/.test(dateIso)) {
        const locale = langCode === 'en' ? 'en-US' : 'da-DK';
        const date = new Date(`${dateIso}T12:00:00`);
        if (!isNaN(date.getTime())) {
          const dateText = new Intl.DateTimeFormat(locale, {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          }).format(date);
          translation = translation.replaceAll('{date}', dateText);
        }
      }
    }
    if (translation && translation !== key) {
      // Handle input placeholders
      if (element.tagName === 'INPUT') {
        if (element.hasAttribute('data-i18n-placeholder') || element.hasAttribute('placeholder') || key.includes('placeholder') || key.includes('searchGyms')) {
          element.placeholder = translation;
        } else {
          element.value = translation; // For input values
        }
      } else if (element.hasAttribute('data-i18n-title')) {
        // Handle title attributes
        element.title = translation;
      } else if (element.hasAttribute('data-i18n-aria-label')) {
        // Handle aria-label attributes
        element.setAttribute('aria-label', translation);
      } else if (element.tagName === 'BUTTON') {
        // Handle buttons - preserve SVG icons if present
        if (element.querySelector('svg')) {
          // Button with icon - update text but keep icon
          const textNodes = Array.from(element.childNodes).filter(node => 
            node.nodeType === Node.TEXT_NODE && node.textContent.trim() !== ''
          );
          textNodes.forEach(node => node.remove());
          const icon = element.querySelector('svg');
          if (icon && icon.previousSibling) {
            // Insert before icon
            element.insertBefore(document.createTextNode(' ' + translation + ' '), icon);
          } else if (icon) {
            // Icon is first child, append text after
            element.appendChild(document.createTextNode(' ' + translation));
          } else {
            element.textContent = translation;
          }
        } else {
          element.textContent = translation;
        }
      } else {
        // Handle HTML content for elements that may contain links (like consent text)
        if (translation.includes('<a') || translation.includes('<span')) {
          element.innerHTML = sanitizeHTML(translation);
        } else {
          element.textContent = translation;
        }
      }
    }
  });
  
  // Update elements with data-i18n-placeholder attribute (separate from data-i18n-key)
  document.querySelectorAll('[data-i18n-placeholder]').forEach(element => {
    const key = element.getAttribute('data-i18n-placeholder');
    const translation = t(key);
    if (translation && translation !== key) {
      element.placeholder = translation;
    }
  });
  
  // Update addon modal translations
  updateAddonModalTranslations();
  applyOfferLandingCategoryCopy();
  
  // Update step labels
  const stepLabels = document.querySelectorAll('.step-label');
  stepLabels.forEach((label, index) => {
    const stepKeys = ['step.homeGym', 'step.access', 'step.boost', 'step.send'];
    if (stepKeys[index]) {
      label.textContent = t(stepKeys[index]);
    }
  });
  
  // Update category titles
  const categoryTitles = {
    'campaign': 'category.campaign',
    'membership': 'category.membership',
    '15daypass': 'category.15daypass',
    'punchcard': 'category.punchcard',
  };
  
  Object.entries(categoryTitles).forEach(([category, key]) => {
    const activeLandingRoute = state.landingRouteConfig || resolveLandingRouteConfig();
    const isOfferLanding = activeLandingRoute?.componentName === 'LandingFirstMonthFree';
    const resolvedTitleKey = (isOfferLanding && category === 'campaign')
      ? 'offer.membership.title'
      : key;
    const resolvedDescKey = (isOfferLanding && category === 'campaign')
      ? 'offer.membership.description'
      : `${key}.desc`;

    const titleEl = document.querySelector(`[data-category="${category}"] .category-title`);
    if (titleEl) {
      titleEl.textContent = t(resolvedTitleKey);
    }
    
    const descEl = document.querySelector(`[data-category="${category}"] .category-description p`);
    if (descEl) {
      const desc = t(resolvedDescKey);
      if (desc) {
        if (desc.includes('<a') || desc.includes('<span')) {
          descEl.innerHTML = sanitizeHTML(desc);
        } else {
          descEl.textContent = desc;
        }
      }
    }
  });
  
  // Update main subtitles
  updateMainSubtitle();
  
  // Update navigation buttons
  updateNavigationButtons();
  
  // Update form labels and placeholders
  updateFormTranslations();
  
  // Update cart labels
  updateCartTranslations();
  
  // Update heads-up displays
  updateHeadsUpTranslations();
  
  // Re-render FAQ with new language
  renderFAQ();
  
  // Update terms tabs if modal is open
  const termsTabs = document.querySelectorAll('.terms-tab[data-i18n-key]');
  termsTabs.forEach(tab => {
    const key = tab.getAttribute('data-i18n-key');
    if (key) {
      tab.textContent = t(key);
    }
  });
  
  console.log('[Translations] Page translations updated for language:', lang);
}

// Update form translations
function updateFormTranslations() {
  // Login form
  const emailLabel = document.querySelector('label[for="loginEmail"]');
  if (emailLabel && emailLabel.hasAttribute('data-i18n-key')) {
    emailLabel.textContent = t('form.email');
  }
  
  const emailInput = document.getElementById('loginEmail');
  if (emailInput && emailInput.hasAttribute('data-i18n-placeholder')) {
    emailInput.placeholder = t('form.email.placeholder');
  }
  
  const passwordLabel = document.querySelector('label[for="loginPassword"]');
  if (passwordLabel && passwordLabel.hasAttribute('data-i18n-key')) {
    passwordLabel.textContent = t('form.password');
  }
  
  const passwordInput = document.getElementById('loginPassword');
  if (passwordInput && passwordInput.hasAttribute('data-i18n-placeholder')) {
    passwordInput.placeholder = t('form.password.placeholder');
  }
  
  const forgotPasswordLink = document.querySelector('[data-action="forgot-password"]');
  if (forgotPasswordLink && forgotPasswordLink.hasAttribute('data-i18n-key')) {
    forgotPasswordLink.textContent = t('form.forgotPassword');
  }
  
  // Registration form fields
  const formFields = [
    { id: 'firstName', labelKey: 'form.firstName', placeholderKey: 'form.firstName.placeholder' },
    { id: 'lastName', labelKey: 'form.lastName', placeholderKey: 'form.lastName.placeholder' },
    { id: 'dateOfBirth', labelKey: 'form.dateOfBirth' },
    { id: 'streetAddress', labelKey: 'form.streetAddress', placeholderKey: 'form.streetAddress.placeholder' },
    { id: 'postalCode', labelKey: 'form.postalCode', placeholderKey: 'form.postalCode.placeholder' },
    { id: 'city', labelKey: 'form.city', placeholderKey: 'form.city.placeholder' },
    { id: 'email', labelKey: 'form.email.create', placeholderKey: 'form.email.create.placeholder' },
    { id: 'phoneNumber', labelKey: 'form.phoneNumber', placeholderKey: 'form.phoneNumber.placeholder' },
    { id: 'password', labelKey: 'form.password.create', placeholderKey: 'form.password.create.placeholder' },
  ];
  
  formFields.forEach(field => {
    const label = document.querySelector(`label[for="${field.id}"]`);
    if (label && label.hasAttribute('data-i18n-key')) {
      label.textContent = t(field.labelKey);
    }
    
    if (field.placeholderKey) {
      const input = document.getElementById(field.id);
      if (input && input.hasAttribute('data-i18n-placeholder')) {
        input.placeholder = t(field.placeholderKey);
      }
    }
  });
  
  // Parent/Guardian form fields
  const parentFields = [
    { id: 'parentFullName', labelKey: 'form.parentFullName', placeholderKey: 'form.parentFullName.placeholder' },
    { id: 'parentDateOfBirth', labelKey: 'form.parentDateOfBirth' },
    { id: 'parentStreetAddress', labelKey: 'form.parentStreetAddress', placeholderKey: 'form.parentStreetAddress.placeholder' },
    { id: 'parentPostalCode', labelKey: 'form.parentPostalCode', placeholderKey: 'form.parentPostalCode.placeholder' },
    { id: 'parentCity', labelKey: 'form.parentCity', placeholderKey: 'form.parentCity.placeholder' },
    { id: 'parentEmail', labelKey: 'form.parentEmail', placeholderKey: 'form.parentEmail.placeholder' },
    { id: 'parentPhoneNumber', labelKey: 'form.parentPhoneNumber', placeholderKey: 'form.parentPhoneNumber.placeholder' },
    { id: 'parentPassword', labelKey: 'form.password.create', placeholderKey: 'form.password.create.placeholder' },
  ];
  
  parentFields.forEach(field => {
    const label = document.querySelector(`label[for="${field.id}"]`);
    if (label && label.hasAttribute('data-i18n-key')) {
      label.textContent = t(field.labelKey);
    }
    
    if (field.placeholderKey) {
      const input = document.getElementById(field.id);
      if (input && input.hasAttribute('data-i18n-placeholder')) {
        input.placeholder = t(field.placeholderKey);
      }
    }
  });
  
  // Error messages
  const errorMessages = document.querySelectorAll('.error-message[data-i18n-key]');
  errorMessages.forEach(error => {
    const key = error.getAttribute('data-i18n-key');
    if (key) {
      error.textContent = t(key);
    }
  });
  
  // Buttons and other elements
  const loginStatusLabel = document.querySelector('.login-status-label');
  if (loginStatusLabel) loginStatusLabel.textContent = t('form.loggedInAs');
  
  const addressLabel = document.querySelector('.profile-detail-label');
  if (addressLabel && (addressLabel.textContent.includes('Address') || addressLabel.textContent.includes('Adresse'))) {
    addressLabel.textContent = t('form.address');
  }
  
  // Save account button
  const saveAccountBtn = document.querySelector('.save-account-btn[data-i18n-key]');
  if (saveAccountBtn) {
    saveAccountBtn.textContent = t('form.saveAccount');
  }
  
  // Login button
  const loginBtn = document.querySelector('.login-btn[data-i18n-key="form.login"]');
  if (loginBtn) {
    loginBtn.textContent = t('form.login').toUpperCase();
  }
  
  // Discount code placeholder is handled by data-i18n-placeholder attribute
  
  // Update apply button
  const applyDiscountBtn = document.querySelector('.apply-discount-btn');
  if (applyDiscountBtn && applyDiscountBtn.hasAttribute('data-i18n-key')) {
    applyDiscountBtn.textContent = t('button.apply');
  }
  const removeDiscountBtn = document.querySelector('.discount-remove-link');
  if (removeDiscountBtn && removeDiscountBtn.hasAttribute('data-i18n-key')) {
    removeDiscountBtn.textContent = t('button.removeCoupon');
  }
  
  // Forgot password modal
  const resetPasswordTitle = document.querySelector('#forgotPasswordModal .info-section-title[data-i18n-key="form.resetPassword"]');
  if (resetPasswordTitle) {
    resetPasswordTitle.textContent = t('form.resetPassword');
  }
  
  const resetPasswordDesc = document.querySelector('.forgot-password-description[data-i18n-key]');
  if (resetPasswordDesc) {
    resetPasswordDesc.textContent = t('form.resetPassword.desc');
  }
  
  const resetPasswordSuccess = document.querySelector('.forgot-password-success-message[data-i18n-key]');
  if (resetPasswordSuccess) {
    resetPasswordSuccess.textContent = t('form.resetPassword.success');
  }
  
  const sendResetLinkBtn = document.querySelector('.login-btn[data-i18n-key="form.sendResetLink"]');
  if (sendResetLinkBtn) {
    sendResetLinkBtn.textContent = t('form.sendResetLink');
  }
  
  const cancelBtn = document.querySelector('.forgot-password-cancel-btn[data-i18n-key]');
  if (cancelBtn) {
    cancelBtn.textContent = t('button.cancel');
  }
  
  const closeBtn = document.querySelector('.login-btn[data-i18n-key="button.close"]');
  if (closeBtn) {
    closeBtn.textContent = t('button.close');
  }
}

// Update cart translations
function updateCartTranslations() {
  // Cart title
  const cartTitle = document.querySelector('.summary-title[data-i18n-key="cart.title"]');
  if (cartTitle) {
    cartTitle.textContent = t('cart.title');
  }
  
  // Discount section
  
  const discountInput = document.querySelector('.discount-input[data-i18n-placeholder]');
  if (discountInput) {
    discountInput.placeholder = t('cart.discount.placeholder');
  }
  
  // Payment redirect text (payment method selection removed)
  const paymentRedirect = document.querySelector('p[data-i18n-key="cart.paymentRedirect"]');
  if (paymentRedirect) {
    paymentRedirect.textContent = t('cart.paymentRedirect');
  }
  
  // Checkout button
  const checkoutBtn = document.querySelector('.checkout-btn[data-i18n-key]');
  if (checkoutBtn) {
    checkoutBtn.textContent = t('cart.checkout');
  }
  
  // Consent checkboxes
  const privacyConsent = document.querySelector('.consent-checkbox .consent-text[data-i18n-key="cart.consent.privacy"]');
  if (privacyConsent) {
    // Handle HTML content with links
    const privacyHtml = t('cart.consent.privacy');
    privacyConsent.innerHTML = sanitizeHTML(privacyHtml);
  }

  const termsConsent = document.querySelector('.consent-checkbox .consent-text[data-i18n-key="cart.consent.terms"]');
  if (termsConsent) {
    // firstclimb (/99kr) requires a liability-waiver acknowledgement instead of
    // the standard T&C consent — the ticket is redeemed in person at the gym.
    const termsKey = isFirstClimbRoute() ? 'cart.consent.terms.firstclimb' : 'cart.consent.terms';
    const termsHtml = t(termsKey);
    termsConsent.innerHTML = sanitizeHTML(termsHtml);
  }

  const marketingConsent = document.querySelector('.consent-checkbox .consent-text[data-i18n-key="cart.consent.marketing"]');
  if (marketingConsent) {
    const marketingHtml = t('cart.consent.marketing');
    marketingConsent.innerHTML = sanitizeHTML(marketingHtml);
  }

  const smsConsent = document.querySelector('.consent-checkbox .consent-text[data-i18n-key="cart.consent.sms"]');
  if (smsConsent) {
    const smsHtml = t('cart.consent.sms');
    smsConsent.innerHTML = sanitizeHTML(smsHtml);
  }
  
  // Consent checkmark toggle is handled via event delegation in setupEventListeners()
  // so it works on initial load even when initLanguageSwitcher() returns early (e.g. no language switcher in DOM).

  // Payment overview labels are handled by data-i18n-key attributes in HTML
  // Cart labels are updated dynamically in updateCartSummary and updatePaymentOverview
  applyFreeFlowCartUi();
}

// Update heads-up displays
function updateHeadsUpTranslations() {
  const gymHeadsUpLabel = document.querySelector('.gym-heads-up-label');
  if (gymHeadsUpLabel) gymHeadsUpLabel.textContent = t('gym.headsUp');
  
  const accessHeadsUpLabel = document.querySelector('.access-heads-up-label');
  if (accessHeadsUpLabel) accessHeadsUpLabel.textContent = t('access.headsUp');
  
  const selectedGymLabel = document.querySelector('.selected-gym-label');
  if (selectedGymLabel) selectedGymLabel.textContent = t('header.selectedGym');
}

// Update addon modal translations
function updateAddonModalTranslations() {
  if (!addonsModal) return;
  
  // Update title - check current title to determine if it's boost or regular modal
  const title = addonsModal.querySelector('.addons-title');
  if (title) {
    // Check if current title matches boost title (in any language) or check state
    const currentTitle = title.textContent.trim();
    // If modal is visible and showing boost products, use boost title
    // Otherwise use regular addon title
    // We'll update based on what's currently displayed
    const grid = addonsModal.querySelector('[data-modal-addons-grid]');
    const hasBoostProducts = grid && Array.from(grid.querySelectorAll('[data-plan-id]')).some(card => {
      const planId = card.dataset.planId;
      return state.boostProducts?.some(p => p.id === planId);
    });
    
    if (hasBoostProducts) {
      title.textContent = t('addons.modal.boost.title');
    } else {
      title.textContent = t('addons.modal.title');
    }
  }
  
  // Update close button aria-label
  const closeBtn = addonsModal.querySelector('.addons-close');
  if (closeBtn) {
    closeBtn.setAttribute('aria-label', t('addons.modal.close'));
  }
  
  // Update hint text
  const hint = addonsModal.querySelector('.addons-hint');
  if (hint) {
    hint.textContent = t('addons.modal.hint');
  }
  
  // Update action button
  updateAddonActionButton();
  
  // Update empty message if present
  const emptyMsg = addonsModal.querySelector('.addons-empty');
  if (emptyMsg) {
    emptyMsg.textContent = t('addons.modal.empty');
  }
}

// Refresh open footer modals when language changes (footer modals follow header language)
function refreshOpenFooterModals() {
  const langCode = (state.language || DEFAULT_LANGUAGE).split('-')[0];
  
  if (DOM.termsModal && DOM.termsModal.style.display === 'flex') {
    if (state.currentModalType === 'terms' && state.currentModalTab) {
      switchTermsTab(state.currentModalTab);
    } else if (state.currentModalType) {
      const content = termsContent[state.currentModalType]?.[langCode] || termsContent[state.currentModalType]?.da || '<p>Content not available.</p>';
      const termsTitles = {
        privacy: t('footer.policies.privacy'),
        cookie: t('footer.policies.cookie'),
        'email-consent': 'E-mail',
        'sms-consent': 'SMS',
      };
      const title = termsTitles[state.currentModalType] || 'Terms and Conditions';
      if (DOM.termsModalTitle) DOM.termsModalTitle.textContent = title;
      state.termsOriginalContent = content;
      if (DOM.termsModalContent) {
        DOM.termsModalContent.innerHTML = sanitizeHTML(content);
        DOM.termsModalContent.scrollTop = 0;
      }
      if (DOM.termsSearchInput) {
        DOM.termsSearchInput.value = '';
        if (DOM.termsSearchClear) DOM.termsSearchClear.style.display = 'none';
      }
    }
  }
  
  if (DOM.dataPolicyModal && DOM.dataPolicyModal.style.display === 'flex') {
    const content = termsContent.privacy?.[langCode] || termsContent.privacy?.da || '<p>Data policy content not available.</p>';
    state.dataPolicyOriginalContent = content;
    if (DOM.dataPolicyModalContent) {
      DOM.dataPolicyModalContent.innerHTML = sanitizeHTML(content);
      DOM.dataPolicyModalContent.scrollTop = 0;
    }
    clearDataPolicySearch();
  }
}

// Change language and reload products
async function changeLanguage(languageCode) {
  if (!SUPPORTED_LANGUAGES[languageCode]) {
    console.warn('[Language] Unsupported language code:', languageCode);
    return;
  }
  
  // Update state and storage
  state.language = languageCode;
  setStoredLanguage(languageCode);
  
  // Update HTML lang attribute
  document.documentElement.lang = languageCode.split('-')[0]; // 'da-DK' -> 'da'
  
  // Update language switcher UI
  updateLanguageSwitcherUI();
  
  // Update all page translations
  updatePageTranslations();
  
  // Refresh open footer modals so they show the new language (based on header switcher)
  refreshOpenFooterModals();
  
  // Reload products if we're on step 2 or later
  if (state.currentStep >= 2 && state.selectedBusinessUnit) {
    console.log('[Language] Reloading products with language:', languageCode);
    
    // Save current UI state before re-rendering
    const savedState = {
      selectedProductId: state.selectedProductId,
      selectedProductType: state.selectedProductType,
      membershipPlanId: state.membershipPlanId,
      valueCardQuantities: new Map(state.valueCardQuantities),
      // Find which cards are currently selected and showing quantity panels
      selectedCards: Array.from(document.querySelectorAll('.plan-card.selected')).map(card => ({
        planId: card.dataset.plan,
        hasQuantityPanel: card.querySelector('.quantity-panel.show') !== null
      }))
    };
    
    // Clear cached products to force fresh fetch
    state.subscriptions = [];
    state.campaignSubscriptions = [];
    state.dayPassSubscriptions = [];
    state.valueCards = [];
    subscriptionDetailHydratedIds.clear();
    subscriptionDetailPromises.clear();
    await loadProductsFromAPI();
    
    // Restore UI state after re-rendering
    if (savedState.selectedProductId) {
      state.selectedProductId = savedState.selectedProductId;
      state.selectedProductType = savedState.selectedProductType;
      state.membershipPlanId = savedState.membershipPlanId;
    }
    
    // Restore quantity selections and UI state
    if (savedState.valueCardQuantities.size > 0) {
      state.valueCardQuantities = savedState.valueCardQuantities;
      
      // Restore selected cards and quantity panels
      savedState.selectedCards.forEach(({ planId, hasQuantityPanel }) => {
        const card = document.querySelector(`.plan-card[data-plan="${planId}"]`);
        if (card) {
          card.classList.add('selected', 'has-quantity');
          
          if (hasQuantityPanel) {
            const quantityPanel = card.querySelector('.quantity-panel');
            if (quantityPanel) {
              quantityPanel.classList.add('show');
              quantityPanel.style.display = 'flex';
              
              // Update the quantity display
              const quantity = state.valueCardQuantities.get(planId) || 1;
              syncPunchCardQuantityUI(card, planId);
              
              // Show the continue button
              const continueBtn = card.querySelector('.continue-btn');
              if (continueBtn) {
                continueBtn.style.display = 'block';
              }
            }
          }
        }
      });
    }
  }
  
  // Reload gyms if we're on step 1
  if (state.currentStep === 1) {
    console.log('[Language] Reloading gyms with language:', languageCode);
    await loadGymsFromAPI();
  }
}

// Update language switcher UI to show active language (trigger label + dropdown item states)
function updateLanguageSwitcherUI() {
  const switcher = document.getElementById('languageSwitcher');
  if (!switcher) return;
  
  const currentLang = state.language || DEFAULT_LANGUAGE;
  const info = SUPPORTED_LANGUAGES[currentLang];
  const triggerFlag = document.getElementById('languageSwitcherTriggerFlag');
  const triggerName = document.getElementById('languageSwitcherTriggerName');
  if (triggerFlag) triggerFlag.textContent = info?.flag ?? '🇩🇰';
  if (triggerName) triggerName.textContent = currentLang === 'en-GB' ? 'EN' : 'DA';
  
  const buttons = switcher.querySelectorAll('.language-btn');
  buttons.forEach(btn => {
    const langCode = btn.dataset.lang;
    if (langCode === currentLang) {
      btn.classList.add('active');
      btn.setAttribute('aria-pressed', 'true');
    } else {
      btn.classList.remove('active');
      btn.setAttribute('aria-pressed', 'false');
    }
  });
}

// Guard so dropdown listeners are only attached once (initLanguageSwitcher is called from DOMContentLoaded and from init())
let languageSwitcherListenersBound = false;

// Initialize language switcher (dropdown: trigger toggles panel, items change language)
function initLanguageSwitcher() {
  const switcher = document.getElementById('languageSwitcher');
  if (!switcher) return;
  
  const trigger = document.getElementById('languageSwitcherTrigger');
  const dropdown = document.getElementById('languageDropdown');
  
  // Set initial language and UI (run every time)
  const currentLang = state.language || DEFAULT_LANGUAGE;
  document.documentElement.lang = currentLang.split('-')[0];
  updateLanguageSwitcherUI();
  updatePageTranslations();
  
  // Attach dropdown open/close and item handlers only once to avoid double-toggle
  if (languageSwitcherListenersBound) return;
  if (!trigger || !dropdown) return;
  languageSwitcherListenersBound = true;
  
  function openDropdown() {
    switcher.classList.add('open');
    dropdown.setAttribute('aria-hidden', 'false');
    trigger.setAttribute('aria-expanded', 'true');
  }
  
  function closeDropdown() {
    switcher.classList.remove('open');
    dropdown.setAttribute('aria-hidden', 'true');
    trigger.setAttribute('aria-expanded', 'false');
  }
  
  function toggleDropdown(e) {
    e.preventDefault();
    e.stopPropagation();
    const isOpen = switcher.classList.contains('open');
    if (isOpen) closeDropdown();
    else openDropdown();
  }
  
  trigger.addEventListener('click', toggleDropdown);
  
  // Click on dropdown item: change language and close
  const buttons = switcher.querySelectorAll('.language-btn');
  buttons.forEach(btn => {
    btn.addEventListener('click', async (e) => {
      e.preventDefault();
      e.stopPropagation();
      const langCode = btn.dataset.lang;
      if (langCode && langCode !== state.language) {
        await changeLanguage(langCode);
      }
      closeDropdown();
    });
  });
  
  // Close on click outside
  document.addEventListener('click', (e) => {
    if (switcher.contains(e.target)) return;
    closeDropdown();
  });
}

// Campaign Rejection Modal Functions
function showCampaignRejectionModal() {
  const modal = document.getElementById('campaignRejectionModal');
  if (modal) {
    modal.style.display = 'flex';
    // Focus the first button for accessibility
    setTimeout(() => {
      const firstButton = document.getElementById('campaignRejectionOption1');
      if (firstButton) {
        firstButton.focus();
      }
    }, 100);
  }
}

function hideCampaignRejectionModal() {
  const modal = document.getElementById('campaignRejectionModal');
  if (modal) {
    modal.style.display = 'none';
  }
}

function handleRejectionOption1() {
  // Hide modal first
  hideCampaignRejectionModal();
  
  // Clear cart when switching from campaign rejection to regular membership
  // This prevents duplicate addons when user adds addon to campaign, gets rejected, then selects regular membership
  console.log('[Campaign Rejection] Clearing cart before switching to regular membership');
  
  // Clear addon selections
  state.addonIds.clear();
  
  // Clear membership plan selection
  state.membershipPlanId = null;
  state.selectedProductId = null;
  state.selectedProductType = null;
  
  // Clear value card quantities
  state.valueCardQuantities.clear();
  
  // Clear cart items
  state.cartItems = [];
  
  // Reset order state
  resetOrderStateForProductChange('campaign-rejection-to-regular-membership');
  
  // Update cart summary to reflect cleared state
  updateCartSummary();
  updateCartTotals();
  
  // Reset addon UI state
  document.querySelectorAll('.addon-card.selected, .plan-card.addon-card.selected').forEach(card => {
    card.classList.remove('selected');
    const button = card.querySelector('[data-action="toggle-addon"]');
    if (button) {
      button.textContent = t('button.addToCart');
      button.setAttribute('data-i18n-key', 'button.addToCart');
    }
  });
  
  // Navigate to step 2
  state.currentStep = 2;
  showStep(2);
  updateStepIndicator();
  updateNavigationButtons();
  updateMainSubtitle();
  
  // Wait for DOM to be ready, then expand membership category
  setTimeout(() => {
    const membershipCategory = document.querySelector('.category-item[data-category="membership"]');
    if (membershipCategory) {
      // Collapse all other categories
      const categoryItems = document.querySelectorAll('.category-item');
      categoryItems.forEach(item => {
        if (item !== membershipCategory) {
          item.classList.remove('expanded', 'selected');
        }
      });
      
      // Expand membership category
      membershipCategory.classList.add('expanded', 'selected');
      
      // Update plan sections visibility
      const singlePlans = document.getElementById('singleChoiceMode');
      const quantityPlans = document.getElementById('quantityMode');
      const singleCategory = document.querySelector('.category-item[data-category="single"]');
      const quantityCategory = document.querySelector('.category-item[data-category="quantity"]');
      
      if (singlePlans) singlePlans.style.display = 'none';
      if (quantityPlans) quantityPlans.style.display = 'none';
      
      // Focus and scroll to top of the category
      setTimeout(() => {
        membershipCategory.setAttribute('tabindex', '-1');
        membershipCategory.focus();
        membershipCategory.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
    
    // Scroll to top
    scrollToTop();
    setTimeout(() => {
      scrollToTop();
    }, 200);
  }, 200);
}

function handleRejectionOption2() {
  // Hide modal
  hideCampaignRejectionModal();
  
  // Open email to support
  const supportEmail = 'medlem@boulders.dk';
  const subject = encodeURIComponent('Campaign Purchase Issue');
  const body = encodeURIComponent('Hello,\n\nI tried to purchase a campaign membership but was told it\'s not available for my account. I believe this may be a mistake.\n\nCould you please help?\n\nThank you!');
  window.location.href = `mailto:${supportEmail}?subject=${subject}&body=${body}`;
}

document.addEventListener('DOMContentLoaded', () => {
  // Initialize language switcher (must be early to set language before API calls)
  initLanguageSwitcher();
  
  // Set up Cookiebot "Cookie Settings" button - reopens Cookiebot consent dialog
  setupCookiebotSettingsButton();
  
  // Check URL parameters for test mode and payment return
  const urlParams = new URLSearchParams(window.location.search);
  const testSuccess = urlParams.get('testSuccess') === 'true';
  const testPaymentFailed = urlParams.get('testPaymentFailed') === 'true';
  const testProductType = urlParams.get('testProductType') || 'membership'; // membership, 15daypass, punch-card
  const testStartDateParam = String(urlParams.get('testStartDate') || '').trim();
  const paymentReturn = urlParams.get('payment');
  const paymentStatus = urlParams.get('status'); // Check for payment status (cancelled, failed, etc.)
  const paymentError = urlParams.get('error'); // Check for payment error (can be 'cancelled' or numeric error code like '205')
  
  if (testSuccess) {
    console.log('[Test Mode] Test success page mode enabled for product type:', testProductType);
    // Store test mode in state
    state.testMode = true;
    state.testProductType = testProductType;
  }
  
  if (testPaymentFailed) {
    console.log('[Test Mode] Test payment failed page mode enabled');
    // Store test mode in state
    state.testMode = true;
  }
  let orderId = urlParams.get('orderId');
  const receiptId = urlParams.get('receiptid') || urlParams.get('receiptId');
  const receiptUuid = urlParams.get('receiptuuid') || urlParams.get('receiptUuid');
  const hasReceiptParam = Boolean(receiptId || receiptUuid);
  let isPaymentReturnFlow = false;
  
  // Fix: Payment provider may append /confirmation to orderId
  // Extract just the numeric part (e.g., "817247/confirmation" -> "817247")
  if (orderId) {
    // Remove any path segments after the order ID
    orderId = orderId.split('/')[0].trim();
    // Ensure it's a valid number
    const numericOrderId = parseInt(orderId, 10);
    if (isNaN(numericOrderId)) {
      console.warn('[Payment Return] Invalid order ID:', orderId);
      orderId = null;
    } else {
      orderId = numericOrderId.toString();
      isPaymentReturnFlow = true;
    }
  }
  
  if (!isPaymentReturnFlow) {
    clearStoredOrderData('page-refresh');
  }
  
  // CRITICAL: Check for payment errors BEFORE init() to prevent step 1 from showing
  if (orderId && (paymentReturn === 'return' || hasReceiptParam)) {
    // We're returning from payment - check for errors first
    console.log('[Payment Return] Detected payment return for order:', orderId);
    
    // Set order ID in state if available
    state.orderId = parseInt(orderId, 10);
    
    // If there's a payment error, go directly to payment failed page
    const hasPaymentError = paymentError || paymentStatus === 'cancelled' || paymentStatus === 'canceled';
    if (hasPaymentError) {
      console.log('[Payment Return] Payment error detected - preventing step 1 from showing');
      // Set step to 5 immediately to prevent step 1 from showing
      state.currentStep = TOTAL_STEPS;
      state.paymentFailed = true;
      state.paymentConfirmed = false;
      state.paymentPending = false;
      
      // CRITICAL: Hide step 1 immediately before init() runs
      // This prevents the flash of step 1
      const step1Panel = document.getElementById('step-1');
      if (step1Panel) {
        step1Panel.style.display = 'none';
        step1Panel.style.visibility = 'hidden';
        step1Panel.style.opacity = '0';
      }
    }
  }
  
  init();
  
  // If in test mode for payment failed, navigate directly to failed page
  if (testPaymentFailed) {
    console.log('[Test Mode] Navigating to payment failed page for testing');
    // Set up minimal test data
    state.orderId = 99999; // Mock order ID
    state.currentStep = TOTAL_STEPS;
    
    // Call the payment failed function directly (for testing)
    showPaymentFailedMessage(null, state.orderId, null);
    return; // Exit early, don't continue with normal flow
  }
  
  // If in test mode, navigate directly to success page
  if (testSuccess) {
    console.log('[Test Mode] Navigating to success page for testing');
    state.currentStep = TOTAL_STEPS;
    
    // Set up test mode data immediately
    const productType = testProductType;
    console.log('[Test Mode] Creating mock order data for testing:', productType);
    
    // Create mock order data for testing
    state.order = {
      number: 'TEST-12345',
      date: new Date(),
      items: [
        { name: productType === 'membership' ? 'Membership' : productType === '15daypass' ? '15-Day Trial Pass' : 'Punch Card', amount: 469 }
      ],
      total: 469,
      memberName: 'Test User',
      membershipNumber: 'TEST-12345',
      membershipType: productType === 'membership' ? 'Medlemskab' : productType === '15daypass' ? '15-Day Trial Pass' : 'Punch Card',
      primaryGym: 'Boulders Aarhus Nord',
      membershipPrice: 469,
    };
    state.orderId = 'TEST-12345';
    
    // Set product type for test mode - ensure state is set correctly
    state.testMode = true;
    state.testProductType = productType;
    state.paymentConfirmed = true; // Set payment confirmed for test mode
    state.paymentFailed = false;
    state.paymentPending = false;
    
    // Set product type for test mode
    if (productType === 'punch-card') {
      state.selectedProductType = 'punch-card';
      // /99kr (firstclimb) is a value-card too — mock the realistic
      // single-use day ticket data rather than a multi-punch klippekort.
      const onFirstClimb = isFirstClimbRoute();
      if (onFirstClimb) {
        // Mirror the real BRP product name so the test URL matches what
        // production renders. Update this string if the BRP product is renamed.
        const firstclimbProductName = 'Velkomstbillet inkl. lejesko og kalk';
        state.order.total = 99;
        state.order.membershipPrice = 99;
        state.order.items = [{ name: firstclimbProductName, amount: 99 }];
        state.fullOrder = {
          valueCardItems: [{
            quantity: 1,
            product: { name: firstclimbProductName, productLabels: [{ name: 'firstclimb' }] },
            valueCard: { number: 'TEST-12345' },
            price: { amount: 9900 } // 99.00 DKK in cents
          }]
        };
      } else {
        state.fullOrder = {
          valueCardItems: [{
            quantity: 2,
            product: { name: 'Klippekort', productLabels: [] },
            valueCard: { number: '12345', numberOfPassages: 10 },
            price: { amount: 46900 } // 469.00 DKK in cents
          }]
        };
      }
    } else if (productType === '15daypass') {
      const validTestStartDate = /^\d{4}-\d{2}-\d{2}$/.test(testStartDateParam) ? testStartDateParam : null;
      state.subscriptionStartDate = validTestStartDate;
      state.selectedProductType = 'membership';
      state.membershipPlanId = '15daypass-123';
      const startDateForMock = validTestStartDate ? new Date(`${validTestStartDate}T12:00:00`) : new Date();
      const endDateForMock = new Date(startDateForMock);
      endDateForMock.setDate(endDateForMock.getDate() + 14);
      // Mock subscription items with 15-Day Trial Pass label and price
      state.fullOrder = {
        subscriptionItems: [{
          product: {
            name: '15-Day Trial Pass',
            productLabels: [{ name: '15-Day Trial Pass' }]
          },
          subscription: {
            startDate: startDateForMock.toISOString(),
            endDate: endDateForMock.toISOString()
          },
          price: { amount: 46900 } // 469.00 DKK in cents
        }]
      };
    } else {
      // Default to membership
      state.selectedProductType = 'membership';
      state.membershipPlanId = 'membership-123';
      // Mock subscription items with price
      state.fullOrder = {
        subscriptionItems: [{
          product: {
            name: 'Medlemskab',
            productLabels: [{ name: 'Public' }]
          },
          price: { amount: 46900 } // 469.00 DKK in cents
        }]
      };
    }
    
    console.log('[Test Mode] Mock data created:', {
      productType: productType,
      selectedProductType: state.selectedProductType,
      membershipPlanId: state.membershipPlanId,
      hasValueCardItems: !!(state.fullOrder?.valueCardItems?.length),
      hasSubscriptionItems: !!(state.fullOrder?.subscriptionItems?.length)
    });
    
    // Show step and render confirmation
    showStep(TOTAL_STEPS);
    updateStepIndicator();
    updateNavigationButtons();
    updateMainSubtitle();
    
    // Render confirmation view after a short delay to ensure DOM is ready
    setTimeout(() => {
      renderConfirmationView();
    }, 100);
    
    return; // Skip payment return flow
  }
  
  // If returning from payment, fetch order data and show confirmation view
  if (orderId && (paymentReturn === 'return' || hasReceiptParam)) {
    // TEST MODE: Force failure for testing (add ?test=fail to URL)
    const testMode = urlParams.get('test');
    if (testMode === 'fail') {
      console.log('[Payment Return] TEST MODE: Simulating payment failure');
      showPaymentFailedMessage(null, parseInt(orderId, 10), null); // Use default calm message
      return;
    }
    
      // CRITICAL: If payment error detected, show failed message immediately
      // This prevents step 1 from flashing before the error is shown
      const hasPaymentError = paymentError || paymentStatus === 'cancelled' || paymentStatus === 'canceled';
      if (hasPaymentError) {
        console.log('[Payment Return] Payment error detected - showing failed message immediately');
        // Ensure step 5 is shown and all other steps are hidden
        state.currentStep = TOTAL_STEPS;
        state.paymentFailed = true;
        
      // Hide all step panels immediately (use direct DOM query since cacheDom might not have run yet)
      const stepPanels = document.querySelectorAll('.step-panel');
      const step5Panel = document.getElementById('step-5');
      stepPanels.forEach((panel) => {
        if (panel.id === 'step-5') {
          // Show step 5
          panel.classList.add('active');
          panel.style.display = 'block';
          panel.style.visibility = 'visible';
          panel.style.opacity = '1';
        } else {
          // Hide all other steps immediately
          panel.classList.remove('active');
          panel.style.display = 'none';
          panel.style.visibility = 'hidden';
          panel.style.opacity = '0';
        }
      });
        
        // Show payment failed message immediately
        showPaymentFailedMessage(null, parseInt(orderId, 10), null);
        // Still load order in background for data, but don't wait
        loadOrderForConfirmation(parseInt(orderId, 10)).catch(err => {
          console.warn('[Payment Return] Could not load order after showing failed message:', err);
        });
        return;
      }
    
    // Always load order first; error params are handled after we verify payment state
    loadOrderForConfirmation(parseInt(orderId, 10));
  }
  
  // Step 6: Validate tokens on app load
  validateTokensOnLoad();
  
  // Restore authenticated state from stored tokens (if available)
  syncAuthenticatedCustomerState();
  
  // Initialize header auth indicator after state sync
  // Use setTimeout to ensure syncAuthenticatedCustomerState completes first
  setTimeout(() => {
    refreshHeaderAuthIndicator();
  }, 100);
  
  // Listen for storage events (cross-tab synchronization)
  // If tokens are saved/cleared in another tab, update the header
  window.addEventListener('storage', (e) => {
    if (e.key === 'boulders_auth_tokens') {
      // Tokens were changed in another tab
      setTimeout(() => {
        refreshHeaderAuthIndicator();
      }, 100);
    }
  });
});


// Re-initialize form scrolling on window resize
let resizeTimer;
window.addEventListener('resize', () => {
  clearTimeout(resizeTimer);
  resizeTimer = setTimeout(() => {
    setupFormFieldScrolling();
  }, 250);
});

function cacheDom() {
  DOM.stepPanels = Array.from(document.querySelectorAll('.step-panel'));
  DOM.stepCircles = Array.from(document.querySelectorAll('.step-circle'));
  DOM.stepConnectors = Array.from(document.querySelectorAll('.step-connector'));
  DOM.prevBtn = document.getElementById('prevBtn');
  DOM.nextBtn = document.getElementById('nextBtn');
  DOM.mainSubtitle = document.getElementById('mainSubtitle');
  DOM.mainTitle = document.querySelector('.main-title');
  DOM.membershipPlans = document.querySelector('[data-component="membership-plans"]');
  DOM.valuePlans = document.querySelector('[data-component="value-plans"]');
  DOM.singlePlanSection = document.getElementById('singlePlans');
  DOM.valuePlanSection = document.getElementById('quantityPlans');
  DOM.singleCarousel = document.getElementById('singleChoiceMode');
  DOM.valueCarousel = document.getElementById('quantityMode');
  DOM.toggleButtons = Array.from(document.querySelectorAll('.toggle-btn'));
  DOM.categoryToggle = document.querySelector('.category-toggle');
  DOM.addonPlans = document.querySelector('[data-component="addon-plans"]');
  DOM.valueCardPunches = document.querySelector('[data-value-card-punches]');
  DOM.valueCardContinueBtn = document.querySelector('[data-action="continue-value-cards"]');
  DOM.valueCardEntryLabel = document.querySelector('[data-entry-label]');
  DOM.cartItems = document.querySelector('[data-component="cart-items"]');
  DOM.cartAddonItems = document.querySelector('[data-component="cart-addon-items"]');
  DOM.paymentOverview = document.querySelector('.payment-overview:not(.payment-overview-total-wrap)');
  DOM.payNow = document.querySelector('[data-summary-field="pay-now"]');
  DOM.monthlyPayment = document.querySelector('[data-summary-field="monthly-payment"]');
  DOM.paymentDiscount = document.querySelector('[data-summary-field="discount-amount"]');
  DOM.paymentPayNowOriginal = document.querySelector('[data-summary-field="pay-now-original"]');
  DOM.paymentBillingPeriod = document.querySelector('[data-summary-field="payment-billing-period"]');
  DOM.paymentBoundUntil = document.querySelector('[data-summary-field="payment-bound-until"]');
  DOM.faqSection = document.getElementById('faqSection');
  DOM.faqQuestions = Array.from(document.querySelectorAll('.faq-question'));
  DOM.checkoutBtn = document.querySelector('[data-action="submit-checkout"]');
  DOM.privacyConsent = document.getElementById('privacyConsent');
  DOM.termsConsent = document.getElementById('termsConsent');
  DOM.discountForm = document.querySelector('.discount-form');
  DOM.discountFormEntry = document.querySelector('.discount-form-entry');
  DOM.discountAppliedChip = document.querySelector('.discount-applied-chip');
  DOM.discountAppliedCode = document.querySelector('.discount-applied-code');
  DOM.discountInput = document.querySelector('.discount-input');
  DOM.applyDiscountBtn = document.querySelector('.apply-discount-btn');
  DOM.removeDiscountBtn = document.querySelector('.discount-remove-link');
  DOM.discountDisplay = document.querySelector('[data-discount-display]');
  DOM.skipAddonsBtn = document.getElementById('skipAddons');
  DOM.backFromAddonsBtn = document.getElementById('backFromAddons');
  DOM.paymentOptions = Array.from(document.querySelectorAll('input[name="paymentMethod"]'));
  DOM.cardPaymentForm = document.getElementById('cardPaymentForm');
  DOM.parentGuardianToggle = document.getElementById('parentGuardian');
  DOM.parentGuardianForm = document.getElementById('parentGuardianForm');
  DOM.parentGuardianReminder = document.querySelector('[data-role="parent-guardian-reminder"]');
  DOM.sameAddressToggle = document.getElementById('sameAddressToggle');
  DOM.loginForm = document.querySelector('.login-form');
  DOM.loginEmail = document.getElementById('loginEmail');
  DOM.loginPassword = document.getElementById('loginPassword');
  DOM.loginButton = DOM.loginForm?.querySelector('.login-btn');
  DOM.loginButtonDefaultText = DOM.loginButton?.textContent?.trim() || 'Log in';
  DOM.loginStatus = document.querySelector('[data-login-status]');
  DOM.loginStatusEmail = document.querySelector('[data-auth-email]');
  DOM.loginStatusName = document.querySelector('[data-auth-name]');
  DOM.loginStatusDob = document.querySelector('[data-auth-dob]');
  DOM.loginStatusAddress = document.querySelector('[data-auth-address]');
  DOM.loginStatusPhone = document.querySelector('[data-auth-phone]');
  DOM.loginStatusNameRow = document.querySelector('[data-auth-name-row]');
  DOM.loginStatusDobRow = document.querySelector('[data-auth-dob-row]');
  DOM.loginStatusEmailRow = document.querySelector('[data-auth-email-row]');
  DOM.loginStatusAddressRow = document.querySelector('[data-auth-address-row]');
  DOM.loginStatusPhoneRow = document.querySelector('[data-auth-phone-row]');
  DOM.loginFormContainer = document.querySelector('[data-login-form-container]');
  // Find form-container that contains login-status (parent of login-status)
  DOM.loginFormContainerWrapper = DOM.loginStatus?.closest('.form-container');
  DOM.authModeToggle = null; // Removed - using button in form instead
  DOM.forgotPasswordLink = document.querySelector('[data-action="forgot-password"]');
  DOM.forgotPasswordModal = document.getElementById('forgotPasswordModal');
  DOM.forgotPasswordForm = document.getElementById('forgotPasswordForm');
  DOM.forgotPasswordEmail = document.getElementById('forgotPasswordEmail');
  DOM.forgotPasswordSuccess = document.getElementById('forgotPasswordSuccess');
  DOM.confirmationItems = document.querySelector('[data-component="confirmation-items"]');
  // Terms modal
  DOM.termsModal = document.getElementById('termsModal');
  DOM.termsModalTitle = document.getElementById('termsModalTitle');
  DOM.termsModalContent = document.getElementById('termsModalContent');
  DOM.termsModalLoading = document.getElementById('termsModalLoading');
  DOM.termsModalClose = document.getElementById('termsModalClose');
  DOM.termsModalTabs = document.getElementById('termsModalTabs');
  DOM.termsModalSearch = document.getElementById('termsModalSearch');
  DOM.termsSearchInput = document.getElementById('termsSearchInput');
  DOM.termsSearchClear = document.getElementById('termsSearchClear');
  
  // Store original content for search
  state.termsOriginalContent = null;
  
  // Data Policy modal
  DOM.dataPolicyModal = document.getElementById('dataPolicyModal');
  DOM.dataPolicyModalContent = document.getElementById('dataPolicyModalContent');
  DOM.dataPolicyModalLoading = document.getElementById('dataPolicyModalLoading');
  DOM.dataPolicyModalClose = document.getElementById('dataPolicyModalClose');
  DOM.dataPolicyModalSearch = document.getElementById('dataPolicyModalSearch');
  DOM.dataPolicySearchInput = document.getElementById('dataPolicySearchInput');
  DOM.dataPolicySearchClear = document.getElementById('dataPolicySearchClear');
  
  // Cookie Declaration modal
  DOM.cookieDeclarationModal = document.getElementById('cookieDeclarationModal');
  DOM.cookieDeclarationModalClose = document.getElementById('cookieDeclarationModalClose');
  
  // Store current modal state
  state.currentModalType = null;
  state.currentModalTab = null;
  // Postal code and city fields for auto-fill
  DOM.postalCode = document.getElementById('postalCode');
  DOM.city = document.getElementById('city');
  DOM.parentPostalCode = document.getElementById('parentPostalCode');
  DOM.parentCity = document.getElementById('parentCity');
  DOM.confirmationFields = {
    orderNumber: document.querySelector('[data-summary-field="order-number"]'),
    orderDate: document.querySelector('[data-summary-field="order-date"]'),
    orderTotal: document.querySelector('[data-summary-field="order-total"]'),
    memberName: document.querySelector('[data-summary-field="member-name"]'),
    membershipType: document.querySelector('[data-summary-field="membership-type"]'),
    primaryGym: document.querySelector('[data-summary-field="primary-gym"]'),
    membershipPrice: document.querySelector('[data-summary-field="membership-price"]'),
  };

  refreshLoginUI();
}

function cacheTemplates() {
  templates.membership = document.getElementById('membership-plan-template');
  templates.valueCard = document.getElementById('value-card-template');
  templates.addon = document.getElementById('addon-card-template');
  templates.cartItem = document.getElementById('cart-item-template');
  templates.confirmationItem = document.getElementById('confirmation-item-template');
}

function setupEventListeners() {
  DOM.nextBtn?.addEventListener('click', nextStep);
  DOM.prevBtn?.addEventListener('click', prevStep);
  DOM.applyDiscountBtn?.addEventListener('click', handleApplyDiscount);
  DOM.removeDiscountBtn?.addEventListener('click', handleRemoveDiscount);
  updateDiscountFormControls();
  DOM.discountInput?.addEventListener('input', handleDiscountInputChange);
  DOM.discountInput?.addEventListener('keypress', (e) => {
    if (e.key === 'Enter' && !DOM.applyDiscountBtn?.disabled) {
      handleApplyDiscount();
    }
  });
  DOM.sameAddressToggle?.addEventListener('change', handleSameAddressToggle);
  DOM.parentGuardianToggle?.addEventListener('change', handleParentGuardianToggle);
  DOM.privacyConsent?.addEventListener('change', () => {
    // Save privacy consent to localStorage with timestamp
    if (DOM.privacyConsent.checked) {
      try {
        const timestamp = new Date().toISOString(); // ISO 8601 format for API
        localStorage.setItem('boulders_privacy_consent', 'true');
        localStorage.setItem('boulders_privacy_consent_timestamp', timestamp);
        console.log('[Privacy Consent] Saved to localStorage with timestamp:', timestamp);
      } catch (e) {
        console.warn('[Privacy Consent] Could not save to localStorage:', e);
      }
    } else {
      try {
        localStorage.removeItem('boulders_privacy_consent');
        localStorage.removeItem('boulders_privacy_consent_timestamp');
        console.log('[Privacy Consent] Removed from localStorage');
      } catch (e) {
        console.warn('[Privacy Consent] Could not remove from localStorage:', e);
      }
    }
    updateCheckoutButton();
  });
  DOM.termsConsent?.addEventListener('change', () => {
    // Save terms consent to localStorage with timestamp
    if (DOM.termsConsent.checked) {
      try {
        const timestamp = new Date().toISOString(); // ISO 8601 format for API
        localStorage.setItem('boulders_terms_consent', 'true');
        localStorage.setItem('boulders_terms_consent_timestamp', timestamp);
        console.log('[Terms Consent] Saved to localStorage with timestamp:', timestamp);
      } catch (e) {
        console.warn('[Terms Consent] Could not save to localStorage:', e);
      }
    } else {
      try {
        localStorage.removeItem('boulders_terms_consent');
        localStorage.removeItem('boulders_terms_consent_timestamp');
        console.log('[Terms Consent] Removed from localStorage');
      } catch (e) {
        console.warn('[Terms Consent] Could not remove from localStorage:', e);
      }
    }
    updateCheckoutButton();
  });

  // Postal code auto-fill event listeners
  setupPostalCodeAutoFill();

  // Gym selection event listeners will be set up dynamically when gyms are loaded

  // Search functionality
  const gymSearch = document.getElementById('gymSearch');
  gymSearch?.addEventListener('input', handleGymSearch);
  
  // Location button
  const findNearestGymBtn = document.getElementById('findNearestGym');
  findNearestGymBtn?.addEventListener('click', (e) => {
    findNearestGym();
  });



  DOM.toggleButtons?.forEach((btn) => {
    btn.addEventListener('click', () => {
      const category = btn.dataset.category ?? 'single';
      handleCategoryToggle(category);
    });
  });

  // New category and plan selection functionality
  setupNewAccessStep();

  if (DOM.paymentOptions.length) {
    DOM.paymentOptions.forEach((option) => {
      option.addEventListener('change', handlePaymentChange);
    });
  }

  document.addEventListener('click', handleGlobalClick);
  document.addEventListener('input', handleGlobalInput);
  const activationDateInput = document.getElementById('activationDateInput');
  const syncActivationDateSelection = () => {
    if (!activationDateInput || !activationDateInput.value) return;
    const pickRadio = document.getElementById('activationDatePick');
    if (pickRadio) pickRadio.checked = true;
    const pickerWrap = document.getElementById('activationDatePickerWrap');
    if (pickerWrap) pickerWrap.hidden = false;
    state.subscriptionStartDate = activationDateInput.value;
    updateActivationDatePeriodHint();
  };
  activationDateInput?.addEventListener('change', syncActivationDateSelection);

  const cardNumber = document.getElementById('cardNumber');
  const expiryDate = document.getElementById('expiryDate');
  const cvv = document.getElementById('cvv');

  cardNumber?.addEventListener('input', formatCardNumber);
  expiryDate?.addEventListener('input', formatExpiryDate);
  cvv?.addEventListener('input', stripNonDigits);
  
  // Setup form field scrolling for mobile
  setupFormFieldScrolling();
  
  // Ensure form fields get focused when clicked
  setupFormFieldFocus();
  
  // Setup save account button validation
  setupSaveAccountButtonValidation();

  if (DOM.loginForm) {
    DOM.loginForm.addEventListener('submit', handleLoginSubmit);
  }
  
  // Forgot password handlers
  if (DOM.forgotPasswordLink) {
    DOM.forgotPasswordLink.addEventListener('click', (e) => {
      e.preventDefault();
      openForgotPasswordModal();
    });
  }
  
  // Close forgot password modal handlers
  document.addEventListener('click', (e) => {
    if (e.target.dataset.action === 'close-forgot-password') {
      closeForgotPasswordModal();
    }
  });
  
  // Close modal when clicking outside
  if (DOM.forgotPasswordModal) {
    DOM.forgotPasswordModal.addEventListener('click', (e) => {
      if (e.target === DOM.forgotPasswordModal) {
        closeForgotPasswordModal();
      }
    });
  }
  
  // Close modal on Escape key (and language dropdown)
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      const languageSwitcher = document.getElementById('languageSwitcher');
      if (languageSwitcher && languageSwitcher.classList.contains('open')) {
        languageSwitcher.classList.remove('open');
        const dropdown = document.getElementById('languageDropdown');
        const trigger = document.getElementById('languageSwitcherTrigger');
        if (dropdown) dropdown.setAttribute('aria-hidden', 'true');
        if (trigger) trigger.setAttribute('aria-expanded', 'false');
        return;
      }
      if (DOM.forgotPasswordModal && DOM.forgotPasswordModal.style.display === 'flex') {
        closeForgotPasswordModal();
      }
      const campaignRejectionModal = document.getElementById('campaignRejectionModal');
      if (campaignRejectionModal && campaignRejectionModal.style.display === 'flex') {
        hideCampaignRejectionModal();
      }
      if (isActivationDateModalOpen()) {
        closeActivationDateModal();
      }
      if (isCheckoutConfirmModalOpen()) {
        closeCheckoutConfirmModal();
      }
    }
  });

  // Close activation modal on backdrop click + trap focus
  const activationDateSection = document.getElementById('activationDateSection');
  if (activationDateSection) {
    activationDateSection.addEventListener('click', (e) => {
      if (e.target === activationDateSection) {
        closeActivationDateModal();
      }
    });
  }

  // Close checkout confirm modal on backdrop click
  const checkoutConfirmModal = document.getElementById('checkoutConfirmModal');
  if (checkoutConfirmModal) {
    checkoutConfirmModal.addEventListener('click', (e) => {
      if (e.target === checkoutConfirmModal) {
        closeCheckoutConfirmModal();
      }
    });
  }

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Tab') return;
    const activationOpen = isActivationDateModalOpen();
    const confirmOpen = isCheckoutConfirmModalOpen();
    if (!activationOpen && !confirmOpen) return;

    const modal = activationOpen
      ? document.querySelector('#activationDateSection .activation-date-modal-card')
      : document.querySelector('#checkoutConfirmModal .checkout-confirm-modal-card');
    if (!modal) return;

    const focusables = Array.from(
      modal.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
    ).filter((el) => !el.hasAttribute('disabled') && el.getAttribute('aria-hidden') !== 'true');

    if (focusables.length === 0) return;

    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;

    if (e.shiftKey) {
      if (active === first || !modal.contains(active)) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (active === last) {
        e.preventDefault();
        first.focus();
      }
    }
  });
  
  // Forgot password form submission
  if (DOM.forgotPasswordForm) {
    DOM.forgotPasswordForm.addEventListener('submit', handleForgotPasswordSubmit);
  }
  
  // Terms modal handlers
  document.addEventListener('click', (e) => {
    // Consent checkbox checkmark (event delegation – works on initial load even if updateCartTranslations ran late)
    const consentCheckmark = e.target.closest('.consent-checkbox .checkmark');
    if (consentCheckmark) {
      const label = consentCheckmark.closest('.consent-checkbox');
      const checkbox = label && label.querySelector('input[type="checkbox"]');
      if (checkbox) {
        e.preventDefault();
        e.stopPropagation();
        checkbox.checked = !checkbox.checked;
        checkbox.dispatchEvent(new Event('change', { bubbles: true }));
      }
      return;
    }

    if (e.target.closest('[data-action="open-cookie-declaration"]')) {
      e.preventDefault();
      openCookieDeclarationModal();
    }
    
    if (e.target.closest('[data-action="open-terms"]')) {
      e.preventDefault();
      const termsType = e.target.closest('[data-action="open-terms"]').dataset.termsType;
      if (termsType === 'privacy') {
        openDataPolicyModal();
      } else if (termsType === 'cookie') {
        openCookieDeclarationModal();
      } else if (termsType === 'email-consent') {
        openTermsModal('email-consent');
      } else if (termsType === 'sms-consent') {
        openTermsModal('sms-consent');
      } else if (termsType === 'terms') {
        openTermsModal('terms');
      } else if (termsType === 'membership' || termsType === 'punchcard') {
        // Open terms modal with tabs and select the appropriate tab
        openTermsModal('terms');
        // Small delay to ensure modal is open before switching tab
        setTimeout(() => {
          switchTermsTab(termsType);
        }, 100);
      } else {
        openTermsModal(termsType);
      }
    }
    
    // Tab switching in terms modal
    if (e.target.closest('.terms-tab')) {
      const tab = e.target.closest('.terms-tab');
      const tabType = tab.dataset.tab;
      switchTermsTab(tabType);
    }
    
    // Search clear button
    if (e.target.closest('#termsSearchClear')) {
      clearTermsSearch();
    }
    if (e.target.closest('#dataPolicySearchClear')) {
      clearDataPolicySearch();
    }
    
    if (e.target === DOM.termsModalClose || e.target.closest('#termsModalClose')) {
      closeTermsModal();
    }
    
    if (e.target === DOM.cookieDeclarationModalClose || e.target.closest('#cookieDeclarationModalClose')) {
      closeCookieDeclarationModal();
    }
    
    if (e.target === DOM.dataPolicyModalClose || e.target.closest('#dataPolicyModalClose')) {
      closeDataPolicyModal();
    }
    
    // Campaign rejection modal handlers
    if (e.target.closest('#campaignRejectionOption1')) {
      handleRejectionOption1();
    }
    
    if (e.target.closest('#campaignRejectionOption2')) {
      handleRejectionOption2();
    }
    
    if (e.target.closest('#campaignRejectionModalClose')) {
      hideCampaignRejectionModal();
    }
    
  });
  
  // Close campaign rejection modal when clicking outside
  const campaignRejectionModal = document.getElementById('campaignRejectionModal');
  if (campaignRejectionModal) {
    campaignRejectionModal.addEventListener('click', (e) => {
      if (e.target === campaignRejectionModal) {
        hideCampaignRejectionModal();
      }
    });
  }
  
  // Search input live update
  if (DOM.termsSearchInput) {
    DOM.termsSearchInput.addEventListener('input', (e) => {
      performTermsSearch(e.target.value);
    });
    DOM.termsSearchInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') e.preventDefault();
    });
  }
  
  // Data policy modal search
  if (DOM.dataPolicySearchInput) {
    DOM.dataPolicySearchInput.addEventListener('input', (e) => {
      performDataPolicySearch(e.target.value);
    });
    DOM.dataPolicySearchInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') e.preventDefault();
    });
  }
  
  // Close terms modal when clicking outside
  if (DOM.termsModal) {
    DOM.termsModal.addEventListener('click', (e) => {
      if (e.target === DOM.termsModal) {
        closeTermsModal();
      }
    });
  }
  
  // Close data policy modal when clicking outside
  if (DOM.dataPolicyModal) {
    DOM.dataPolicyModal.addEventListener('click', (e) => {
      if (e.target === DOM.dataPolicyModal) {
        closeDataPolicyModal();
      }
    });
  }
  
  // Close cookie declaration modal when clicking outside
  if (DOM.cookieDeclarationModal) {
    DOM.cookieDeclarationModal.addEventListener('click', (e) => {
      if (e.target === DOM.cookieDeclarationModal) {
        closeCookieDeclarationModal();
      }
    });
  }
  
  // Close modals on Escape key
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      if (DOM.termsModal && DOM.termsModal.style.display !== 'none') {
        closeTermsModal();
      }
      if (DOM.dataPolicyModal && DOM.dataPolicyModal.style.display !== 'none') {
        closeDataPolicyModal();
      }
      if (DOM.cookieDeclarationModal && DOM.cookieDeclarationModal.style.display !== 'none') {
        closeCookieDeclarationModal();
      }
      const receiptModal = document.getElementById('detailedReceiptModal');
      if (receiptModal && receiptModal.style.display !== 'none') {
        closeDetailedReceipt();
      }
    }
  });
  
  // Close receipt modal when clicking outside
  const receiptModal = document.getElementById('detailedReceiptModal');
  if (receiptModal) {
    receiptModal.addEventListener('click', (e) => {
      if (e.target === receiptModal) {
        closeDetailedReceipt();
      }
    });
  }
}

function setLoginLoadingState(isLoading) {
  if (!DOM.loginButton) return;
  const defaultText = DOM.loginButtonDefaultText || DOM.loginButton.textContent || 'Log in';
  DOM.loginButton.disabled = isLoading;
  DOM.loginButton.classList.toggle('is-loading', isLoading);
  DOM.loginButton.textContent = isLoading ? 'Logging in...' : defaultText;
}

async function handleLoginSubmit(event) {
  event.preventDefault();
  if (state.loginInProgress) {
    return;
  }

  // Check for login cooldown (rate limiting)
  const now = Date.now();
  const cooldownUntil = loginCooldownUntil || (typeof window !== 'undefined' && window.loginCooldownUntil) || 0;
  if (now < cooldownUntil) {
    const secondsLeft = Math.ceil((cooldownUntil - now) / 1000);
    const minutesLeft = Math.floor(secondsLeft / 60);
    const remainingSeconds = secondsLeft % 60;
    const cooldownMessage = minutesLeft > 0
      ? `${minutesLeft} minute${minutesLeft !== 1 ? 's' : ''}${remainingSeconds > 0 ? ` and ${remainingSeconds} second${remainingSeconds !== 1 ? 's' : ''}` : ''}`
      : `${secondsLeft} second${secondsLeft !== 1 ? 's' : ''}`;
    showToast(`Rate limit: Please wait ${cooldownMessage} before trying again.`, 'error');
    return;
  }

  const email = DOM.loginEmail?.value?.trim() || '';
  const password = DOM.loginPassword?.value || '';

  if (!email || !password) {
    showToast('Please enter both email and password.', 'error');
    if (!email) {
      DOM.loginEmail?.closest('.form-group')?.classList.add('error');
    }
    if (!password) {
      DOM.loginPassword?.closest('.form-group')?.classList.add('error');
    }
    return;
  }

  state.loginInProgress = true;
  setLoginLoadingState(true);

  try {
    const response = await authAPI.login(email, password);
    const payload = response?.data ?? response;
    const username = payload?.username || email;
    state.authenticatedEmail = email;

    // /99kr: a successful login means the email has an existing BRP profile.
    // Inspect product history — block only if they've held a product carrying
    // the `Første Gang` label (see FIRSTCLIMB_BLOCKING_LABELS). Empty-profile
    // and never-bought-anything customers can still buy.
    if (isFirstClimbRoute()) {
      let customerIdForCheck = null;
      try {
        await syncAuthenticatedCustomerState(username, email);
        customerIdForCheck = state.customerId
          || getTokenMetadata()?.username
          || getTokenMetadata()?.userName;
      } catch (syncErr) {
        console.warn('[login] customer state sync failed before firstclimb history check:', syncErr);
      }
      let hasBlockingHistory = true;
      try {
        hasBlockingHistory = await customerHasFirstclimbBlockingHistory(customerIdForCheck);
      } catch (historyErr) {
        console.warn('[login] firstclimb history check failed; blocking by default:', historyErr);
        hasBlockingHistory = true;
      }
      if (hasBlockingHistory) {
        try { window.clearTokens && window.clearTokens(); } catch (_) {}
        state.customerId = null;
        state.authenticatedCustomer = null;
        state.authenticatedEmail = null;
        try { refreshLoginUI(); } catch (_) {}
        blockFirstClimbExistingCustomer({ email });
        return;
      }
      // Clean history → carry on with the normal logged-in flow.
    }

    // Sync customer state and fetch profile
    await syncAuthenticatedCustomerState(username, email);
    
    // Always fetch full customer profile to ensure we have complete data
    try {
      const customerId = state.customerId || username;
      if (customerId) {
        const customerData = await authAPI.getCustomer(customerId);
        state.authenticatedCustomer = customerData;
        console.log('[login] Customer profile loaded:', customerData);
        
        // Get display name for toast
        let displayName = email;
        if (customerData?.firstName && customerData?.lastName) {
          displayName = `${customerData.firstName} ${customerData.lastName}`;
        } else if (customerData?.firstName) {
          displayName = customerData.firstName;
        } else if (customerData?.lastName) {
          displayName = customerData.lastName;
        }
        showToast(`Logged in as ${displayName}.`, 'success');
        
        // Refresh UI again to show profile data
        refreshLoginUI();
        
        // Dispatch event to notify React components
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new Event('auth-state-changed'));
        }
      } else {
        showToast(`Logged in as ${email}.`, 'success');
      }
      
      // Dispatch event to notify React components
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event('auth-state-changed'));
      }
    } catch (profileError) {
      console.warn('[login] Could not fetch customer profile:', profileError);
      // Continue even if profile fetch fails - refresh UI with whatever data we have
      showToast(`Logged in as ${email}.`, 'success');
      refreshLoginUI();
      
      // Dispatch event to notify React components
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event('auth-state-changed'));
      }
    }
    
    // Do not auto-create order on login; wait for explicit checkout.
    if (state.currentStep === 4) {
      updatePaymentOverview();
    }
    DOM.loginForm?.reset();
    // Clear cooldown on successful login
    loginCooldownUntil = 0;
    if (typeof window !== 'undefined') {
      window.loginCooldownUntil = 0;
    }
  } catch (error) {
    console.error('[login] Login failed:', error);
    
    // Handle rate limit errors - set cooldown to prevent further attempts
    if (error.message && error.message.includes('Rate limit exceeded')) {
      const retryMs = getRetryDelayFromError(error);
      loginCooldownUntil = Date.now() + retryMs;
      if (typeof window !== 'undefined') {
        window.loginCooldownUntil = loginCooldownUntil;
      }
      const secondsLeft = Math.ceil(retryMs / 1000);
      const minutesLeft = Math.floor(secondsLeft / 60);
      const remainingSeconds = secondsLeft % 60;
      const cooldownMessage = minutesLeft > 0
        ? `${minutesLeft} minute${minutesLeft !== 1 ? 's' : ''}${remainingSeconds > 0 ? ` and ${remainingSeconds} second${remainingSeconds !== 1 ? 's' : ''}` : ''}`
        : `${secondsLeft} second${secondsLeft !== 1 ? 's' : ''}`;
      showToast(`Rate limit exceeded. Please wait ${cooldownMessage} before trying again.`, 'error');
      console.warn(`[login] Rate limited. Cooldown set for ${secondsLeft}s (${cooldownMessage})`);
    } else {
      // Extract and highlight error fields
      const errorFields = extractErrorFields(error);
      errorFields.forEach(({ fieldId }) => {
        if (fieldId) {
          highlightFieldError(fieldId, false);
        }
      });
      
      showToast(getErrorMessage(error, 'Login'), 'error');
    }
  } finally {
    state.loginInProgress = false;
    setLoginLoadingState(false);
  }
}

function openForgotPasswordModal() {
  if (!DOM.forgotPasswordModal) return;
  
  // Pre-fill email if user has entered it in login form
  if (DOM.loginEmail?.value) {
    DOM.forgotPasswordEmail.value = DOM.loginEmail.value;
  }
  
  DOM.forgotPasswordModal.style.display = 'flex';
  DOM.forgotPasswordForm.style.display = 'block';
  DOM.forgotPasswordSuccess.style.display = 'none';
  document.body.classList.add('modal-open');
  
  // Focus on email input
  setTimeout(() => {
    DOM.forgotPasswordEmail?.focus();
  }, 100);
}

function closeForgotPasswordModal() {
  if (!DOM.forgotPasswordModal) return;
  
  DOM.forgotPasswordModal.style.display = 'none';
  document.body.classList.remove('modal-open');
  
  // Reset form
  if (DOM.forgotPasswordForm) {
    DOM.forgotPasswordForm.reset();
  }
}

// Terms Content - Embedded directly to avoid CORS issues
const termsContent = {
  membership: {
    da: `<h2>Vilkår og betingelser for medlemmer og 15 dages klatring i Boulders</h2>
<p><strong>Gælder alle medlemskaber</strong></p>

<h3>Accept</h3>
<p>Ved gennemførsel af indmeldelsesprocessen har du accepteret nedenstående regelsæt. Din accept af aftalen er en bindende aftale mellem medlemmet og Boulders. Din accept heraf fungerer som en underskrift.</p>

<h3>Highlights</h3>
<p>Du skal læse hele regelsættet grundigt igennem, inden du skriver under. Nærlæs særligt nedenstående:</p>
<p><strong>Fra §3:</strong> "Dit medlemskab i Boulders er et løbende abonnement med automatisk fornyelse. Abonnementet starter på købsdatoen og fortsætter indtil det opsiges efter §8."</p>
<p><strong>Fra §8:</strong> "En opsigelse skal ske online på din medlemsprofil eller pr. e-mail til medlem@boulders.dk." … "Opsigelsesperioden for et medlemskab er løbende måned + næste hele afsluttede måned. Et medlemskab kan derfor kun ophøre med effekt sidste dag i en måned."</p>
<p><strong>Fra §10:</strong> "Klatring er en sportsaktivitet, hvor det er påregneligt, at der kan ske skader og uheld. Kunden er derfor indforstået med at benyttelse af Boulders' faciliteter, herunder klatrefaciliteter, foretages på kundens eget ansvar, samt at kunden ikke kan gøre erstatningsansvar gældende på nogen måde overfor Boulders. Kunden erklærer sig således indforstået med, at eventuelle skader på kunden selv eller tredjemand ikke vil blive erstattet af Boulders."</p>

<h3>Indholdsfortegnelse</h3>
<ul>
<li>§1. Generelt</li>
<li>§2. Ændring af medlemsbetingelser og priser</li>
<li>§3. Indmeldelse</li>
<li>§4. Kampagne/tilbud/prisreduceret medlemskab</li>
<li>§5. Medlemskaber og Medlemskort</li>
<li>§6. Betaling</li>
<li>§7. Bero</li>
<li>§8. Opsigelse af medlemskab</li>
<li>§9. Fortrydelsesret</li>
<li>§10. Helbredstilstand og personskade</li>
<li>§11. Force Majeure</li>
<li>§12. Persondataforordningen (GDPR)</li>
<li>§13. Lovvalg og værneting</li>
<li>§14. 15 dages klatring</li>
</ul>

<h3>§1 Generelt</h3>
<p>Følgende regelsæt er gældende for dit medlemskab i Boulders. Gældende regelsæt vil altid kunne findes på Boulders.dk. Vi gør opmærksom på at løbende ændringer af priser, betingelser, åbningstider og regelsæt kan forekomme. Du kan læse mere om ændringer under §2.</p>

<p>Dit medlemskab i Boulders er personligt og må ikke benyttes af andre. Ved misbrug af dit medlemskab eller medlemskort, opsiges dit medlemskab øjeblikkeligt uden refusion for den resterende periode af dit medlemskab.</p>

<p>For at kunne identificere dig som medlem opbevares et billede af dig sammen med dine øvrige personoplysninger. Læs mere om GDPR (Persondataforordningen) i §12. Hvis der sker ændringer i de oplysninger, du har givet ved medlemskabets oprettelse, skal du straks meddele dette til Boulders. Du er som medlem selv ansvarlig for, at Boulders til enhver tid har dine korrekte personoplysninger herunder særligt e-mailadresse.</p>

<p>Ved oprettelsen af et medlemskab accepterer du, ud over dette regelsæt, at modtage meddelelser om væsentlige prisændringer og ændringer i dette regelsæt pr. e-mail. Du accepterer også at modtage andre relevante informationer pr. e-mail.</p>

<p>Henvendelser om medlemskab, herunder spørgsmål til medlemskaber og betingelser skal rettes til medlem@boulders.dk. Kun skriftlige svar herfra kan betragtes som værende fuldgyldige.</p>

<h3>§2 Ændring af medlemsbetingelser og priser</h3>
<p>Boulders forbeholder sig retten til løbende at ændre regelsættet, gebyrer, betingelser, åbningstider og priser, herunder prisen for dit medlemskab. De til enhver tid gældende priser og regelsæt vil altid kunne findes på Boulders.dk.</p>
<p>Ændringer kan forekomme i følgende situationer: En rabataftale, som er tilknyttet dit medlemskab, bortfalder. Boulders foretager investeringer til forbedring af din brugeroplevelse, fx. ansættelse af mere personale eller renovation og modernisering af faciliteter samt udvidelse med flere klatrecentre.</p>
<p>Priserne reguleres som følge af markedsforholdene og samfundsudviklingen, herunder forøgede omkostninger og inflation, indførelsen af nye lovgivningsmæssige krav og lignende situationer. Niveauet af prisstigningen vil blive fastsat forholdsmæssigt ud fra Boulders forøgede omkostninger.</p>
<p>Væsentlige ændringer af priser og regelsæt vil blive varslet pr. e-mail minimum 60 dage inden disse træder i kraft. Såfremt du ikke ønsker at fortsætte dit medlemskab, skal du opsige dette jævnfør §8. Vær opmærksom på opsigelsesfristen!</p>

<h3>§3 Indmeldelse</h3>
<p>Dit medlemskab i Boulders er et løbende abonnement med automatisk fornyelse, der starter på købsdagen og fortsætter indtil det opsiges efter §8.</p>
<p>Se også §4, hvis du har tegnet medlemskab på kampagne/tilbud/prisreduceret.</p>

<h3>§3A Medlemskab når du er under 18 år eller umyndiggjort</h3>
<p>For at tegne eget medlemskab i Boulders skal du være fyldt 18 år.</p>
<p>Hvis du er under 18 år eller umyndig, skal du, for at tegne et medlemskab, enten gøre det online eller møde personligt op i et af Boulders' klatrecentre med din værge eller en af dine forældre. Det skal være en værge/forældre, som betaler medlemskabet. Ved afhentning af medlemskort skal den pågældende værge/forældre underskrive dette regelsæt på vegne af den umyndige.</p>
<p>Du er selv ansvarlig for at oplyse Boulders om, at du er umyndig/U18 og dermed ikke kan indgå aftalen.</p>
<p>Hvis du er værge eller forælder, der indmelder en person under 18 år, indestår og hæfter du for den umyndiges overholdelse af betaling, regelsættet og husets regler. Du kan kun tegne medlemskab i Boulders, hvis din værge/forælder står som betaler af dit medlemskab.</p>

<h3>§4 Kampagne/tilbud/prisreduceret medlemskab</h3>
<p>Medlemskaber købt som kampagne/tilbud/prisreduceret er underlagt en 3 måneders bindingsperiode, fra indgåelse af medlemskabet og tre hele afsluttede måneder fremad.</p>
<p>Medlemskaber købt på kampagne/tilbud/prisreduceret overgår efter endt rabatperiode automatisk til løbende abonnement på normale priser og betingelser, som beskrevet her i regelsættet.</p>
<p>Det er ikke muligt at berosætte medlemskaber i en kampagne/prisreduceret/tilbudsperiode.</p>
<p>Efter udmeldelse, efter §8, er det ikke muligt at genindmelde sig på et kampagne/tilbud/prisreduceret-medlemskab indtil 6 hele måneder efter endt medlemskab. Genindmeldelse skal i den 6 måneders periode være på normale betingelser til gældende normalpriser.</p>

<h3>§5 Medlemskaber og medlemskort</h3>
<p>På Boulders.dk kan du til enhver tid se en oversigt over vores gældende medlemskaber.</p>
<p>Studentermedlemskabet kan kun tegnes af personer med gyldig studieidentifikation. Boulders forbeholder sig retten til at afvise typer af studenteridentifikation eller tilbagekalde brugen af en allerede benyttet studenteridentifikation. Medlemmet er forpligtet til ved henvendelse at fremvise gyldig studenteridentifikation.</p>
<p>I tilfælde af overtrædelse af medlemsbetingelserne, personalets anvisninger eller husets regler, kan Boulders til enhver tid lukke dit medlemskab med øjeblikkelig virkning. Boulders forbeholder sig også retten til at udelukke dig fra medlemskab i Boulders permanent eller på ubestemt tid.</p>
<p>Medlemskortet skal altid medbringes og indlæses, inden du benytter det pågældende klatrecenter. Boulders forbeholder sig retten til at opkrævet et gebyr for glemt medlemskort og retten til at påkræve at du køber et nyt medlemskort efter gentagende at have glemt dit kort.</p>
<p>Hvis du mister eller beskadiger dit medlemskort, skal du straks meddele dette til Boulders, som vil udstede et nyt medlemskort mod et gebyr.</p>

<h3>§5A U16 medlemskaber</h3>
<p>Det er et krav for oprettelse af U16 medlemskab, at personen der benytter medlemskabet er under 16 år.</p>
<p>U16 medlemskaber skal oprettes af forældre/værge, læs mere om medlemskaber til umyndige i §3A.</p>
<p>U16 medlemskaber vil automatisk overgå til almindelig voksenpris- og betingelser, når medlemmer fylder 16 år. Er medlemmer studerende, skal dette oplyses med studenteridentifikation til medlem@boulders.dk inden overgang fra U16 medlemskab.</p>
<p>Gældende priser, medlemstyper og betingelser kan altid findes på boulders.dk</p>

<h3>§6 Betaling</h3>
<p>Du skal ved oprettelsen af dit medlemskab betale for medlemskabet fra købsdatoen tilmed slutningen af indeværende måned + evt. næste hele måned, afhængig af den specifikke indmeldelsesdato.</p>

<h3>§6A Betalingsaftale</h3>
<p>Det er et krav at medlemskabet er tilmeldt automatisk betaling med kreditkortabonnement. Kontingentbetalinger trækkes i starten af måneden, uafhængigt af indmeldelsesdatoen.</p>

<h3>§6B Manglende eller for sen betaling</h3>
<p>Hvis dit medlemskab ikke betales rettidigt, udsendes en rykkerskrivelse til dig eller din værge/forældre. Boulders opkræver rykkergebyr på 100kr efter gældende takster. Betales der ikke inden den angivne forfaldsdato, har Boulders ret til uden varsel at opsige dit medlemskab og opkræve andre udeståender til betaling omgående.</p>
<p>Boulders forbeholder sig ret til selvstændigt eller via inkasso at indkassere det udestående beløb samt et gebyr for rykning og for for sen betaling. Boulders indberetter dårlige betalere til RKI i henhold til RKI's gældende regler. Gebyret fastsættes af Boulders.</p>

<h3>§6C Refundering fra Boulders</h3>
<p>I nogle tilfælde kan det hænde, at du som medlem har penge til gode hos Boulders. Boulders vil som hovedregel godskrive sådanne beløb i dit fremadrettede medlemskab, og du har derfor som udgangspunkt ikke mulighed for at få sådanne beløb udbetalt, før ophør af dit medlemskab.</p>
<p>Hvis dit medlemskab er ophørt og du mener, at du har et beløb til gode, skal du rette henvendelse til medlem@boulders.dk.</p>
<p>Vi gør opmærksom på, at en eventuel refusion kun kan ske til danske konti og indenfor 30 dage fra din henvendelse. Refusion i forbindelse med berosætning eller anden forudbetaling, herunder konvertering af klippekort, er ikke muligt. Medlemskabet vil i disse tilfælde fortsætte indtil alt tilgodehavende er benyttet.</p>

<h3>§7 Bero</h3>
<p>Medlemskaber kan berosættes mod et gebyr på 49kr. Medlemskaber kan berosættes 3 gange pr. kalenderår. Beroperioden skal som minimum være 30 på hinanden følgende kalenderdage og kan maksimalt være 3 måneder.</p>
<p>Bero kan altid ophæves før tid, dog tidligst efter 30 på hinanden følgende kalenderdage. I beroperioden kan denne forlænges op til 3 måneder uden gebyr.</p>
<p>Medlemskaber kan ikke opsiges i beroperioden. Ligeledes kan opsagte medlemskaber ikke berosættes i udmeldelsesperioden.</p>
<p>Eventuelt tilgodehavende ved berosætning, vil blive fratrukket første periode af medlemskabsbetaling efter endt bero. Refusion er ikke muligt.</p>
<p>Det er ikke muligt at berosætte medlemskaber i en kampagne/prisreduceret/tilbudsperiode.</p>
<p>Du kan starte bero på din Boulders online medlemsprofil eller pr. e-mail til medlem@boulders.dk.</p>
<p>Medlemmet beholder sine optjente medlemsfordele og anciennitet under berosætning.</p>

<h3>§8 Opsigelse af medlemskab</h3>
<p>Opsigelsesperioden for et medlemskab er løbende måned + næste hele afsluttede måned. Et medlemskab kan derfor kun ophøre med effekt sidste dag i en måned.</p>
<p>En opsigelse skal ske online på din medlemsprofil eller pr. e-mail til medlem@boulders.dk. Ved opsigelse af dit medlemskab skal du oplyse dit fulde navn og/eller medlemsnummer. En opsigelse er gyldig fra den dag, Boulders modtager den, og du har modtaget en kvittering for gennemførelsen af din opsigelse fra Boulders.</p>
<p>Det påhviler medlemmet at kunne fremvise en tidligere skriftlig bekræftelse fra Boulders på en udmeldelse, hvis der opstår tvivl derom.</p>
<p>Boulders står ikke til ansvar for, om du har brugt dit medlemskab i perioden, du hæfter selv for dit medlemskab, indtil en rettidig opsigelse er gennemført.</p>
<p>Boulders står ikke til ansvar for manglende adgang til medlemmets online profil. Medlemmet skal selv øjeblikkeligt kontakte medlem@boulders.dk, hvis de har problemer med login og udmeldelse online.</p>
<p>Behandler man selv sit medlemskab på sin online medlemsprofil, efter man har kontaktet medlem@boulders.dk, vil din online aktivitet og ændringer være gældende for dit videre medlemskabsforløb og mailen vil ikke være gældende.</p>
<p>Besvarer man ikke eventuelle spørgsmål til det videre forløb af medlemskab, vil sagen ikke blive færdigbehandlet og dit medlemskab fortsætter uden ændringer.</p>
<p>Bemærk at medlemskaber købt som kampagne/tilbud/prisreduceret er underlagt en 3 måneders bindingsperiode, fra indgåelse af medlemskabet og tre hele afsluttede måneder fremad. Læs mere om kampagner i §4.</p>
<p>Ved opsigelse mister medlemmet sine optjente medlemsfordele og anciennitet. Ønsker man at beholde sine fordele, bør berosætning overvejes.</p>

<h3>§9 Fortrydelsesret</h3>
<p>Ved oprettelsen af et medlemskab i Boulders har du 14 dages fortrydelsesret fra den dag, medlemskabet blev oprettet. For at gøre brug af fortrydelsesretten, skal du rette henvendelse til medlem@boulders.dk. Fortrydelsesretten kan ikke benyttes ved gentagne medlemskabsoprettelser. Hvis du benytter dig af din fortrydelsesret, har Boulders ret til at kræve forholdsmæssig betaling i form af normal entré for samtlige antal besøg i den periode, du har benyttet dig af dit medlemskab.</p>

<h3>§10 Helbredstilstand og personskade</h3>
<p>Al færdsel i Boulders klatrecentre og al klatring sker på eget ansvar. Boulders tager ikke ansvar for din brug af Boulders' faciliteter eller udendørsområder. Du er som medlem selv ansvarlig for at være i en helbredstilstand, der tillader deltagelse i aktiviteter hos Boulders.</p>
<p>Klatring er en sportsaktivitet, hvor det er påregneligt, at der kan ske skader og uheld. Kunden er derfor indforstået med at benyttelse af Boulders' faciliteter, herunder klatrefaciliteter, foretages på kundens eget ansvar, samt at kunden ikke kan gøre erstatningsansvar gældende på nogen måde overfor Boulders.</p>
<p>Kunden erklærer sig således indforstået med, at eventuelle skader på kunden selv eller tredjemand ikke vil blive erstattet af Boulders. Det er dit personlige ansvar at inspicere din landingszone inden klatring.</p>
<p>I øvrigt følges dansk erstatningsret på området.</p>

<h3>§10A Værdigenstande</h3>
<p>Boulders bærer ikke noget ansvar for tab som følge af tyveri eller tingsskade.</p>

<h3>§11 Force Majeure</h3>
<p>Såfremt udnyttelse af den til medlemskabet knyttede adgang til Boulders' klatrecentre (og Boulders' faciliteter i øvrigt) umuliggøres eller vanskeliggøres helt eller delvist af forhold, som forårsages af - eller resulterer ud fra - handlinger eller omstændigheder, der er uden for Boulders' kontrol, herunder f.eks. - men ikke begrænset til - epidemier, pandemier, oversvømmelse, frihedsindskrænkninger, nationale nødstilstande, ind greb eller påbud fra myndighedernes side, brand, jordskælv, krig, terrortrusler eller terrorhandlinger, optøjer eller andre civile uroligheder, revolution, embargoer, handelskrige, strejker eller andre arbejdskonflikter, berettiger en sådan umuliggørelse eller vanskeliggørelse dig ikke til at annullere eller på anden måde ændre eller opsige medlemskabet med forkortet varsel. Boulders kan i så fald heller ikke anses for at have misligholdt eller på anden måde overtrådt abonnementsaftalen. Du vil således være forpligtet til at betale for dit medlemskab i overensstemmelse med abonnementsaftalens øvrige vilkår og bestemmelser, som videreføres på hidtidige vilkår.</p>
<p>Såfremt medlemskabet ønskes opsagt, skal du derfor afgive en opsigelse i overensstemmelse med de almindelige vilkår for opsigelse, jf. abonnementsaftalens §8, samt iagttage den heri anførte opsigelsesvarsel, førend opsigelsen kan få virkning. Du vil derfor heller ikke have krav på tilbagebetaling af allerede betalte beløb, ligesom Boulders heller ikke på anden måde kan blive stillet til ansvar og/eller hæfte over for dig på baggrund af en sådan umuliggørelse eller vanskeliggørelse.</p>

<h3>§12 Persondataforordningen (GDPR)</h3>
<p>Når du opretter et medlemskab i Boulders accepterer du, at Boulders indsamler og behandler oplysninger om dig. Hos Boulders indsamler og behandler vi kun de oplysninger, der er nødvendige for at administrere dit medlemskab. Boulders anvender ikke oplysningerne til andre formål.</p>
<p>Boulders opbevarer oplysningerne om dig i op til 1 år efter dit medlemskab er ophørt. Herefter vil alle oplysninger blive destrueret. Når Boulders registrerer oplysninger om dig, har du ret til at:</p>
<ol>
<li>Få indsigt i de oplysninger, som vi behandler om dig.</li>
<li>Gøre indsigelse mod at indsamlingen og behandlingen af dine oplysninger finder sted.</li>
<li>Få oplysninger, der er vildledende eller urigtige, rettet eller slettet.</li>
</ol>
<p>Du har også ret til at klage til Datatilsynet over Boulders' behandling af dine personoplysninger. Henvendelser vedr. vores behandling af dine oplysninger skal ske til medlem@boulders.dk. Boulders er data ansvarlig i forbindelse med vores behandling af de oplysninger du giver os. Vi bruger alene dine persondata i forbindelse med oprettelse af dit medlemskab med løbende betaling.</p>
<p>Kontakt medlem@boulders.dk efter endt medlemskab for at få slettet dine oplysninger omgående. Oplysninger vil kun blive slettet efter udligning af alle eventuelle udeståender.</p>

<h3>§13 Lovvalg og værneting</h3>
<p>Alle køb omfattet af disse medlemsbetingelser er underlagt dansk ret, dog ikke CISG (Den Internationale Købelov), medmindre andet fremgår af ufravigelige regler. Eventuelle uenigheder, som ikke kan afgøres i mindelighed, afgøres af de danske domstole, medmindre andet fremgår af ufravigelige regler.</p>

<h3>§14 15 dages klatring</h3>
<p>15 dages klatring må kun benyttes af personer, der ikke tidligere har været medlem eller tidligere har benyttet et 15 dages kort. Opdages snyd med dette vil indehaveren blive pålagt at betale forholdsmæssig entré for sin brug af 15 dages klatring.</p>
<p>15 dages klatring er personligt og må ikke deles med andre.</p>
<p>Der henvises yderligere til §10 – helbredstilstand og personskade, §10A – Værdigenstande, §12- Persondataforordningen (GDPR) i dette regelsæt, der også er gældende for 15 dages klatring.</p>`,
    en: `<h2>Rules for Members and Trial Pass holders at Boulders</h2>
<p><strong>The following rules apply to all memberships</strong></p>

<h3>Acceptance</h3>
<p>By completing the registration process, you accept the rules outlined below. Your acceptance of this agreement constitutes a binding agreement between you and Boulders. Your acceptance serves as a signature.</p>

<h3>Highlights</h3>
<p>Please read the full set of rules carefully before signing. Pay special attention to the following:</p>
<p><strong>From §3:</strong> "Your membership at Boulders is a recurring subscription with automatic renewal. The subscription starts on the date of purchase and continues until it is terminated in accordance with §8."</p>
<p><strong>From §8:</strong> "Membership cancellations must be done online via your membership profile or by emailing medlem@boulders.dk." ... "The cancellation period for a membership is the current month + the next full calendar month. Therefore, a membership can only terminate effective on the last day of a month."</p>
<p><strong>From §10:</strong> "Climbing is a sports activity where injuries and accidents can occur. The customer agrees that the use of Boulders' facilities, including climbing facilities, is done at their own risk and that no claims for compensation can be made against Boulders. The customer thus agrees that any injuries to themselves or third parties will not be compensated by Boulders."</p>

<h3>Table of Contents</h3>
<ul>
<li>§1. General Provisions</li>
<li>§2. Changes to Membership Terms and Prices</li>
<li>§3. Membership Registration</li>
<li>§4. Promotional/Discounted Memberships</li>
<li>§5. Memberships and Membership Cards</li>
<li>§6. Payment</li>
<li>§7. Membership Freeze</li>
<li>§8. Membership Cancellation</li>
<li>§9. Right of Withdrawal</li>
<li>§10. Health Condition and Personal Injury</li>
<li>§11. Force Majeure</li>
<li>§12. Data Protection (GDPR)</li>
<li>§13. Governing Law and Jurisdiction</li>
<li>§14. 15 Day Trial Pass</li>
</ul>

<h3>§1 General Provisions</h3>
<p>The following rules apply to your membership at Boulders. The applicable rules will always be available on Boulders.dk. Please note that ongoing changes to prices, terms, opening hours, and rules may occur. You can read more about changes under §2.</p>

<p>Your membership at Boulders is personal and may not be used by others. Misuse of your membership or membership card will result in immediate termination of your membership without a refund for the remaining period. To identify you as a member, a photo of you is stored along with your other personal information. Learn more about GDPR (Data Protection Regulation) in §12.</p>

<p>If there are changes to the information you provided when registering for membership, you must notify Boulders immediately. As a member, it is your responsibility to ensure that Boulders always has your correct personal information, especially your email address.</p>

<p>By registering for a membership, you agree to receive notifications about significant price changes and changes to these rules via email. You also agree to receive other relevant information via email.</p>

<p>Inquiries about memberships, including questions regarding memberships and terms, should be directed to medlem@boulders.dk. Only written responses from this email address can be considered valid.</p>

<h3>§2 Changes to Membership Terms and Prices</h3>
<p>Boulders reserves the right to make ongoing changes to the rules, fees, terms, opening hours, and prices, including the price of your membership. The current prices and rules will always be available on Boulders.dk. Changes may occur in the following situations:</p>
<p>A discount agreement linked to your membership expires.</p>
<p>Boulders invests in improving your user experience, e.g., hiring more staff, renovating or modernizing facilities, or expanding with more climbing centers.</p>
<p>Prices are adjusted due to market conditions and societal developments, including increased costs, inflation, the introduction of new legislative requirements, and similar situations.</p>
<p>The level of price increases will be set proportionally based on Boulders' increased costs.</p>
<p>Significant changes to prices and rules will be notified via email at least 60 days before they take effect. If you do not wish to continue your membership, you must cancel it according to §8. Note the cancellation period!</p>

<h3>§3 Membership Registration</h3>
<p>Your membership at Boulders is a recurring subscription with automatic renewal, starting on the date of purchase and continuing until it is canceled in accordance with §8. See also §4 if you have signed up for a promotional/discounted membership.</p>

<h3>§3A Membership for Individuals Under 18 or Those Under Legal Guardianship</h3>
<p>To sign up for a membership at Boulders, you must be at least 18 years old. If you are under 18 or under legal guardianship, you must either register online or visit one of Boulders' climbing centers in person with your guardian or parent. The guardian/parent must be the one paying for the membership. When collecting the membership card, the guardian/parent must sign this rule set on behalf of the individual under guardianship.</p>
<p>It is your responsibility to inform Boulders if you are under legal guardianship or under 18 and thus cannot enter into the agreement.</p>
<p>If you are a guardian or parent registering someone under 18, you vouch for and are responsible for the minor's compliance with payment, the rules, and house policies. Membership at Boulders can only be signed up for if the guardian/parent is listed as the payer for the membership.</p>

<h3>§4 Promotional/Discounted Memberships</h3>
<p>Memberships purchased as part of a promotion/offer/discount are subject to a three-month binding period from the start date of the membership and for three full calendar months thereafter.</p>
<p>It is not possible to freeze memberships during a campaign/discounted/promotional period.</p>
<p>After the discount period ends, the membership automatically transitions into a recurring subscription at the normal rates and terms described in this rule set.</p>
<p>After termination, as per §8, it is not possible to re-register for a promotional/discounted membership until six full months after the end of the membership. Re-registration during this six-month period must be under normal terms at the current normal prices.</p>

<h3>§5 Memberships and Membership Cards</h3>
<p>You can always find an overview of our current memberships on Boulders.dk. The student membership can only be purchased by individuals with valid student identification. Boulders reserves the right to reject certain types of student identification or revoke the use of previously accepted student identification. Members are required to present valid student identification upon request.</p>
<p>In the event of a breach of membership terms, staff instructions, or house rules, Boulders may terminate your membership with immediate effect at any time. Boulders also reserves the right to permanently or indefinitely exclude you from membership at Boulders.</p>
<p>The membership card must always be brought and scanned before using the respective climbing center. Boulders reserves the right to charge a fee for forgotten membership cards and may require you to purchase a new membership card if you repeatedly forget it. If you lose or damage your membership card, you must immediately inform Boulders, who will issue a new card for a fee.</p>

<h3>§5A U16 Memberships</h3>
<p>It is a requirement for obtaining a U16 membership that the individual using the membership is under 16 years of age. U16 memberships must be created by a parent or guardian; see §3A for more details regarding memberships for minors.</p>
<p>U16 memberships will automatically transition to a regular adult membership, with applicable adult pricing and terms, upon the member's 16th birthday. If the member qualifies for a student membership, this must be documented with valid student identification by emailing medlem@boulders.dk prior to the transition from the U16 membership.</p>
<p>Current prices, membership types, and terms can always be found at boulders.dk.</p>

<h3>§6 Payment</h3>
<p>When creating your membership, you will be required to pay for the membership starting from the purchase date until the end of the current month plus the following full month, depending on the specific enrollment date.</p>

<h3>§6A Payment Agreement</h3>
<p>It is a requirement that the membership is registered for automatic payment through a credit card subscription. Membership fees are charged at the beginning of each month, regardless of the enrollment date.</p>

<h3>§6B Non-Payment or Late Payment</h3>
<p>If your membership fee is not paid on time, a reminder notice will be sent to you or your parent/guardian. Boulders will charge a late fee of 100 DKK, in accordance with applicable rates.</p>
<p>If payment is not made by the due date, Boulders reserves the right to terminate your membership without notice and demand immediate payment of any outstanding amounts. Boulders retains the right to collect outstanding amounts independently or through debt collection, including charging fees for reminders and late payments. Boulders may report delinquent payments to the RKI registry in accordance with RKI's applicable rules. The fee is determined by Boulders.</p>

<h3>§6C Refunds from Boulders</h3>
<p>In some cases, you may have a balance in your favor with Boulders. As a general rule, such amounts will be credited toward your ongoing membership, and you will not be able to receive a refund until your membership has ended.</p>
<p>If your membership has ended and you believe you have a balance owed to you, please contact medlem@boulders.dk. Please note that any refund can only be issued to Danish bank accounts and within 30 days of your inquiry.</p>
<p>Refunds related to freezing of memberships or other prepayments, including the conversion of punch cards, are not possible. In such cases, the membership will continue until the balance has been fully utilized.</p>

<h3>§7 Freezing Memberships</h3>
<p>Memberships can be frozen for a fee of 49 DKK. Memberships can be frozen up to 3 times per calendar year. The freezing period must be a minimum of 30 consecutive calendar days and a maximum of 3 months.</p>
<p>A freeze can always be lifted early, but not before 30 consecutive calendar days have passed. During the freezing period, it can be extended up to 3 months without additional fees.</p>
<p>Memberships cannot be canceled during the freezing period, and canceled memberships cannot be frozen during the termination period.</p>
<p>Any credit resulting from the freezing period will be deducted from the first membership fee payment after the freeze ends. Refunds are not possible.</p>
<p>It is not possible to freeze memberships during a campaign/discounted/promotional period.</p>
<p>You can initiate a freeze via your Boulders online membership profile or by emailing medlem@boulders.dk.</p>
<p>Members retain their accrued membership benefits and seniority during the freezing period.</p>

<h3>§8 Termination of Membership</h3>
<p>Termination must be done online through your membership profile or by emailing medlem@boulders.dk. When terminating your membership, you must provide your full name and/or membership number. The termination is valid from the day Boulders receives it, and you have received a confirmation from Boulders acknowledging the termination.</p>
<p>The termination period for a membership is the remainder of the current month plus the following full calendar month. Memberships can therefore only end on the last day of a month.</p>
<p>It is the member's responsibility to retain a written confirmation from Boulders regarding the termination, should any dispute arise.</p>
<p>Boulders is not responsible for whether you have used your membership during the membership period; you remain liable for the membership until a valid termination is completed.</p>
<p>Boulders is not responsible for a lack of access to your online membership profile. You must contact medlem@boulders.dk immediately if you experience issues logging in or canceling online.</p>
<p>If you make changes to your membership online after contacting medlem@boulders.dk, your online activity will take precedence, and the email correspondence will no longer be valid.</p>
<p>Failure to respond to questions regarding the next steps for your membership will result in your case not being finalized, and your membership will continue unchanged.</p>
<p>Memberships purchased as part of a campaign, promotion, or at a discounted rate are subject to a 3-month binding period, effective from the date of membership creation and lasting for three full calendar months. See §4 for more information on campaigns.</p>
<p>Upon termination, members lose their accrued benefits and seniority. If you wish to retain these, freezing your membership may be considered.</p>

<h3>§9 Right of Withdrawal</h3>
<p>When creating a membership with Boulders, you have a 14-day right of withdrawal from the date the membership was created. To exercise your right of withdrawal, contact medlem@boulders.dk. The right of withdrawal cannot be used for repeated membership sign-ups.</p>
<p>If you exercise your right of withdrawal, Boulders is entitled to require proportional payment in the form of normal entry fees for all visits made during the period in which your membership was active.</p>

<h3>§10 Health Conditions and Personal Injury</h3>
<p>All activity in Boulders climbing centers and climbing itself is conducted at your own risk. Boulders assumes no responsibility for your use of Boulders' facilities or outdoor areas. As a member, you are responsible for ensuring that your health condition allows participation in activities at Boulders.</p>
<p>Climbing is a sports activity where injuries and accidents are foreseeable. Customers acknowledge that using Boulders' facilities, including climbing facilities, is at their own risk and that they cannot claim liability or compensation from Boulders in any way. Customers further accept that any injuries to themselves or third parties will not be compensated by Boulders. It is your personal responsibility to inspect your landing zone before climbing. Danish liability law applies to this area.</p>

<h3>§10A Valuables</h3>
<p>Boulders assumes no responsibility for losses due to theft or property damage.</p>

<h3>§11 Force Majeure</h3>
<p>If access to Boulders climbing centers (and Boulders' other facilities) associated with the membership is rendered impossible or significantly hindered, wholly or partially, due to circumstances caused by or resulting from actions or situations beyond Boulders' control—including, but not limited to, epidemics, pandemics, floods, restrictions on freedom, national emergencies, government interventions or orders, fires, earthquakes, wars, terrorist threats or actions, riots or other civil disturbances, revolutions, embargoes, trade wars, strikes or other labor disputes—such hindrance or impossibility does not entitle you to cancel, modify, or terminate your membership with shortened notice.</p>
<p>In such cases, Boulders cannot be considered to have breached or otherwise violated the subscription agreement. You are therefore required to continue payment for your membership under the terms and conditions of the subscription agreement, which remains in effect as per its existing terms.</p>
<p>If you wish to terminate the membership, you must do so in accordance with the standard terms of termination, as outlined in §8 of the subscription agreement, and observe the stipulated notice period before the termination takes effect. Consequently, you are not entitled to a refund of already paid amounts, nor can Boulders be held liable or accountable in any way for such hindrance or impossibility.</p>

<h3>§12 General Data Protection Regulation (GDPR)</h3>
<p>By creating a membership at Boulders, you accept that Boulders collects and processes information about you. Boulders only collects and processes the information necessary to manage your membership and does not use the data for other purposes.</p>
<p>Boulders retains your information for up to 1 year after your membership has ended, after which all data will be destroyed.</p>
<p>When Boulders processes your information, you have the right to:</p>
<ol>
<li>Access the information we process about you.</li>
<li>Object to the collection and processing of your information.</li>
<li>Have misleading or inaccurate information corrected or deleted.</li>
</ol>
<p>You also have the right to file a complaint with the Danish Data Protection Agency regarding Boulders' processing of your personal data. Inquiries about our processing of your information should be directed to medlem@boulders.dk.</p>
<p>Boulders is the data controller responsible for processing the information you provide to us. We use your personal data exclusively for creating your membership with ongoing payment.</p>
<p>Contact medlem@boulders.dk after your membership ends to have your information deleted immediately. Information will only be deleted after settling any outstanding payments.</p>

<h3>§13 Governing Law and Jurisdiction</h3>
<p>All purchases covered by these membership terms are subject to Danish law, excluding the CISG (United Nations Convention on Contracts for the International Sale of Goods), unless otherwise specified by mandatory rules.</p>
<p>Any disputes that cannot be resolved amicably shall be settled by the Danish courts unless otherwise required by mandatory rules.</p>

<h3>§14 15-Day Trial Pass</h3>
<p>The 15-Day Trial Pass may only be used by individuals who have not previously been members or used a 15-day pass. If misuse of this pass is discovered, the holder will be required to pay a proportional entrance fee for the usage of the 15-Day Trial Pass.</p>
<p>The 15-Day Trial Pass is personal and may not be shared with others.</p>
<p>Additionally, references are made to §10 – Health Condition and Personal Injury, §10A – Valuables, and §12 – General Data Protection Regulation (GDPR), which also apply to the 15-Day Trial Pass.</p>`
  },
  punchcard: {
    da: `<h2>Vilkår og betingelser for klippekort</h2>

<h3>§1 Generelt</h3>
<p>Følgende regelsæt er gældende for klippekort hos Boulders.<br>
Ved oprettelse af et klippekort accepterer du at modtage nyhedsmails og anden relevant information pr. e-mail.</p>

<h3>§2 Husets regler og andre bestemmelser</h3>
<p>I tilfælde af overtrædelse af regelsættet, personalets anvisninger eller husets regler, kan Boulders til enhver tid lukke dit klippekort med øjeblikkelig virkning. Boulders forbeholder sig også retten til at udelukke dig fra at klatre hos Boulders på ubestemt tid.<br>
Udleverede plastikkort fungerer som adgangskort og skal altid medbringes og indlæses, inden du benytter det pågældende klatrecenter. Hvis du mister eller beskadiger dit adgangskort, skal du straks meddele dette til Boulders, som vil udstede et nyt adgangskort mod et gebyr.</p>

<h3>§3 Helbredstilstand og personskade</h3>
<p>Al færdsel i Boulders klatrecentre og al klatring sker på eget ansvar. Boulders tager ikke ansvar for din brug af Boulders' faciliteter eller udendørsområder. Du er som klatrer selv ansvarlig for at være i en helbredstilstand, der tillader deltagelse i aktiviteter hos Boulders. Klatring er en sportsaktivitet, hvor det er påregneligt, at der kan ske skader og uheld.<br>
Kunden er derfor indforstået med, at benyttelse af Boulders' faciliteter, herunder klatrefaciliteter, foretages på kundens eget ansvar, samt at kunden ikke kan gøre erstatningsansvar gældende på nogen måde overfor Boulders. Kunden erklærer sig således indforstået med, at eventuelle skader på kunden selv eller tredjemand ikke vil blive erstattet af Boulders. Det er dit personlige ansvar at inspicere din landingszone inden klatring. I øvrigt følges dansk erstatningsret på området.</p>

<h3>§4 Persondataforordningen (GDPR)</h3>
<p>Når du opretter et klippekort i Boulders accepterer du, at Boulders indsamler og behandler oplysninger om dig. Hos Boulders indsamler og behandler vi kun de oplysninger, der er nødvendige for at administrere dit klippekort. Boulders anvender ikke oplysningerne til andre formål. Boulders opbevarer oplysningerne om dig i op til 1 år efter dit klippekort. Herefter vil alle oplysninger blive destrueret. Kontakt medlem@boulders.dk efter klippekortets udløb for at få slette dine oplysninger omgående.</p>

<p>Når Boulders registrerer oplysninger om dig, har du ret til at:</p>
<ul>
<li>Få indsigt i de oplysninger, som vi behandler om dig.</li>
<li>Gøre indsigelse mod at indsamlingen og behandlingen af dine oplysninger finder sted.</li>
<li>Få oplysninger, der er vildledende eller urigtige, rettet eller slettet.</li>
</ul>

<p>Du har også ret til at klage til Datatilsynet over Boulders' behandling af dine personoplysninger.<br>
Henvendelser vedrørende vores behandling af dine oplysninger skal ske til medlem@boulders.dk.<br>
Boulders er dataansvarlig i forbindelse med vores behandling af de oplysninger du giver os.</p>

<h3>§5 Ombytning til Medlemskab</h3>
<p>Det er muligt at ombytte sit ubrugte eller delvist brugte klippekort til et medlemskab. Boulders tager derved højde for antal brugte klip og købsprisen og udregner derved restancen, som bliver fratrukket det ønskede medlemskab. Bemærk at der skal underskrives et regelsæt til medlemskab. Det er ikke muligt at få refunderet restancen ved endt medlemskab. Ønskes ombytning skal du skrive en mail til medlem@boulders.dk. Boulders ombytter ikke i receptionen.</p>

<h3>§6 Handelsbetingelser</h3>
<p>Dit klippekort er gyldigt i 12 måneder fra købsdatoen og betales forud. Du har ikke ret til at få refunderet resterende klip eller på anden måde modtage godtgørelse for ubrugte klip.<br>
Ved køb af et klippekort i Boulders har du 14 dages fortrydelsesret fra den dag, klippekort blev oprettet. For at gøre brug af fortrydelsesretten, skal du rette henvendelse til medlem@boulders.dk. Fortrydelsesretten kan ikke benyttes ved gentagne oprettelser. Hvis du benytter dig af din fortrydelsesret, har Boulders ret til at kræve forholdsmæssig betaling i form af gældende entrépriser for det antal gange klippekort er benyttet og priserne vil reflektere tidspunktet for check-in i relation til peakpriser og off-peakpriser.</p>

<p>Klippekortet er ikke personligt og må gerne benyttes af andre. Enhver person, der benytter et klip fra en klippekortholders kort, skal dog underskrive Boulders' gældende ansvarsfraskrivelse.</p>`,
    en: `<h2>Terms and Conditions for Punch Card</h2>

<h3>§1 General</h3>
<p>The following rules apply to punch cards at Boulders.<br>
By purchasing a punch card, you agree to receive newsletters and other relevant information via email.</p>

<h3>§2 House Rules and Other Provisions</h3>
<p>In case of a breach of the rules, staff instructions, or house rules, Boulders reserves the right to cancel your punch card with immediate effect. Boulders also reserves the right to ban you from climbing at Boulders indefinitely.<br>
Issued plastic cards function as access cards and must always be brought and scanned before entering the climbing center. If you lose or damage your access card, you must immediately inform Boulders, which will issue a new access card for a fee.</p>

<h3>§3 Health Condition and Personal Injury</h3>
<p>All activities at Boulders climbing centers and all climbing are undertaken at your own risk. Boulders assumes no responsibility for your use of its facilities or outdoor areas. You are responsible for being in a health condition that allows participation in activities at Boulders. Climbing is a sport where injuries and accidents are foreseeable.<br>
The customer acknowledges that the use of Boulders' facilities, including climbing facilities, is entirely at their own risk and that no compensation claims can be made against Boulders in any way. The customer also acknowledges that any injuries to themselves or third parties will not be compensated by Boulders. It is your personal responsibility to inspect your landing zone before climbing. Danish liability law applies to the area.</p>

<h3>§4 General Data Protection Regulation (GDPR)</h3>
<p>By purchasing a punch card at Boulders, you accept that Boulders collects and processes information about you. At Boulders, we only collect and process the information necessary to manage your punch card. Boulders does not use the information for other purposes. Boulders stores your information for up to 1 year after your punch card expires, after which all information will be destroyed. Contact medlem@boulders.dk to have your information deleted immediately after your punch card expires.</p>

<p>When Boulders registers information about you, you have the right to:</p>
<ul>
<li>Access the information we process about you.</li>
<li>Object to the collection and processing of your information.</li>
<li>Have misleading or incorrect information corrected or deleted.</li>
</ul>

<p>You also have the right to file a complaint with the Danish Data Protection Agency regarding Boulders' processing of your personal data.<br>
Inquiries regarding our processing of your information should be directed to medlem@boulders.dk.<br>
Boulders is the data controller responsible for processing the information you provide to us.</p>

<h3>§5 Conversion to Membership</h3>
<p>It is possible to convert an unused or partially used punch card into a membership. Boulders will take into account the number of used punches and the purchase price to calculate the remaining balance, which will be deducted from the desired membership. Note that you will need to sign a membership agreement. Any remaining balance from the conversion cannot be refunded upon the termination of the membership.<br>
To request a conversion, you must send an email to medlem@boulders.dk. Boulders does not process conversions at the reception.</p>

<h3>§6 Terms of Sale</h3>
<p>Your punch card is valid for 12 months from the date of purchase and must be paid upfront. You are not entitled to a refund for remaining punches or any other compensation for unused punches.<br>
When purchasing a punch card at Boulders, you have a 14-day right of withdrawal from the date the punch card was created. To exercise your right of withdrawal, you must contact medlem@boulders.dk. The right of withdrawal cannot be exercised for repeated purchases. If you exercise your right of withdrawal, Boulders is entitled to demand proportional payment based on applicable entrance fees for the number of times the punch card has been used. Prices will reflect the check-in time in relation to peak and off-peak pricing.</p>

<p>The punch card is not personal and may be used by others. However, any person using a punch from a cardholder's punch card must sign Boulders' applicable liability waiver.</p>`
  },
  privacy: {
    da: `<h2>Privatlivspolitik</h2>
<p><em>Opdateret August 2024</em></p>

<p>Hos Boulders er det afgørende for os, at du føler dig tryg ved, hvordan vi håndterer dine personoplysninger. Derfor har vi en politik, der sikrer, at de oplysninger, vi indsamler, behandles ansvarligt og med respekt for dit privatliv, i overensstemmelse med gældende lovgivning.</p>

<h3>Generelt</h3>
<p>Denne politik for behandling af personoplysninger ("Persondatapolitik") gælder, når du interagerer med Boulders ("vi", "os", "vores"). Den beskriver, hvordan vi indsamler og behandler dine oplysninger, når du bruger vores hjemmeside boulders.dk ("Hjemmesiden"), benytter vores app ("Appen"), eller i forbindelse med dit kundeforhold og din træning hos os.</p>

<h3>Dataansvar</h3>
<p>Boulders ApS er ansvarlig for behandlingen af de personoplysninger, vi indsamler om dig. Du kan kontakte os på følgende adresse:</p>
<p>Boulders ApS<br>
Graham Bells Vej, 8200 Aarhus N<br>
CVR nr.: 32777651<br>
boulders.dk</p>

<p>Hvis du har spørgsmål om vores behandling af dine personoplysninger, kan du skrive til os på hej@boulders.dk.</p>

<h3>Hvornår indsamler vi personlige oplysninger om dig?</h3>
<p>Vi indsamler dine personoplysninger i forskellige situationer, herunder når du:</p>
<ul>
<li>Tilmelder dig som medlem af Boulders.</li>
<li>Underskriver ansvarsfraskrivelse, ved køb af dagsbillet, indløser fribillet eller lign.</li>
<li>Køber produkter på boulders.goactivebooking.com</li>
<li>Besøger og bruger vores hjemmeside eller app</li>
<li>Kontakter os via e-mail, telefon eller sociale medier</li>
</ul>

<h3>Cookies</h3>
<p>Når du besøger vores hjemmeside, indsamler vi automatisk oplysninger om din brug af hjemmesiden gennem session cookies og lignende teknologier. Cookies er små tekstfiler, der gemmes i din browser og hjælper os med at gøre hjemmesiden mere relevant for dine behov og interesser.</p>

<h3>Sletning af dine personlige oplysninger</h3>
<p>Vi opbevarer dine personlige data så længe, det er nødvendigt for formålet med behandlingen. Når dit medlemskab ophører, gemmer vi dine data i en begrænset periode for at opfylde juridiske krav og vores legitime interesser.</p>`,
    en: `<h2>Privacy Policy</h2>
<p><em>Updated August 2024</em></p>

<p>At Boulders, it is crucial for us that you feel secure about how we handle your personal information. Therefore, we have a policy that ensures the information we collect is processed responsibly and with respect for your privacy, in accordance with applicable legislation.</p>

<h3>General</h3>
<p>This policy for processing personal information ("Data Protection Policy") applies when you interact with Boulders ("we", "us", "our"). It describes how we collect and process your information when you use our website boulders.dk ("Website"), use our app ("App"), or in connection with your customer relationship and training with us.</p>

<h3>Data Controller</h3>
<p>Boulders ApS is responsible for processing the personal information we collect about you. You can contact us at the following address:</p>
<p>Boulders ApS<br>
Graham Bells Vej, 8200 Aarhus N<br>
CVR no.: 32777651<br>
boulders.dk</p>

<p>If you have questions about our processing of your personal information, you can write to us at hej@boulders.dk.</p>

<h3>When do we collect personal information about you?</h3>
<p>We collect your personal information in various situations, including when you:</p>
<ul>
<li>Register as a member of Boulders.</li>
<li>Sign a waiver, purchase a day ticket, redeem a free ticket, etc.</li>
<li>Purchase products on boulders.goactivebooking.com</li>
<li>Visit and use our website or app</li>
<li>Contact us via email, phone, or social media</li>
</ul>

<h3>Cookies</h3>
<p>When you visit our website, we automatically collect information about your use of the website through session cookies and similar technologies. Cookies are small text files stored in your browser that help us make the website more relevant to your needs and interests.</p>

<h3>Deletion of your personal information</h3>
<p>We store your personal data for as long as necessary for the purpose of processing. When your membership ends, we store your data for a limited period to fulfill legal requirements and our legitimate interests.</p>`
  },
  cookie: {
    da: `<h2>Cookie Policy</h2>

<h3>Hvad er en cookie?</h3>
<p>En cookie er en lille datafil, der gemmes på din computer, tablet eller mobiltelefon. Cookies er ikke programmer og kan ikke indeholde skadelige koder eller virus.</p>

<h3>Hjemmesidens brug af cookies</h3>
<p>Cookies er essentielle for, at vores hjemmeside fungerer korrekt. De hjælper os også med at forstå, hvordan du bruger hjemmesiden, så vi kan tilpasse indholdet til dine behov og præferencer. Cookies husker blandt andet dine loginoplysninger, sprogvalg og indstillinger samt målretter annoncer på andre hjemmesider.</p>

<h3>Opbevaringsperiode for cookies</h3>
<p>Cookies opbevares i varierende perioder, afhængigt af deres funktion og formål. Når en cookie udløber, slettes den automatisk. Du kan finde detaljer om levetiden for hver cookie i vores cookiepolitik.</p>

<h3>Afvisning og sletning af cookies</h3>
<p>Du kan til enhver tid afvise eller slette cookies ved at ændre dine browserindstillinger. Bemærk, at dette kan påvirke funktionaliteten af visse tjenester på hjemmesiden. Vejledninger til sletning af cookies varierer afhængigt af den browser og enhed, du bruger.</p>

<h3>Ændring af samtykke</h3>
<p>Hvis du ønsker at ændre dit samtykke, kan du slette cookies fra din browser eller ændre dine indstillinger via knappen i venstre bund, her på hjemmesiden.</p>

<h3>Spørgsmål?</h3>
<p>Hvis du har spørgsmål eller kommentarer vedrørende vores cookiepolitik, er du velkommen til at kontakte os. Selve cookiedeklarationen opdateres regelmæssigt via Cookiebot.</p>`,
    en: `<h2>Cookie Policy</h2>

<h3>What is a cookie?</h3>
<p>A cookie is a small data file stored on your computer, tablet, or mobile phone. Cookies are not programs and cannot contain harmful code or viruses.</p>

<h3>Website's use of cookies</h3>
<p>Cookies are essential for our website to function correctly. They also help us understand how you use the website so we can tailor content to your needs and preferences. Cookies remember, among other things, your login information, language preferences and settings, and target ads on other websites.</p>

<h3>Storage period for cookies</h3>
<p>Cookies are stored for varying periods depending on their function and purpose. When a cookie expires, it is automatically deleted. You can find details about the lifetime of each cookie in our cookie policy.</p>

<h3>Rejection and deletion of cookies</h3>
<p>You can reject or delete cookies at any time by changing your browser settings. Note that this may affect the functionality of certain services on the website. Instructions for deleting cookies vary depending on the browser and device you use.</p>

<h3>Changing consent</h3>
<p>If you wish to change your consent, you can delete cookies from your browser or change your settings via the button in the bottom left, here on the website.</p>

<h3>Questions?</h3>
<p>If you have questions or comments regarding our cookie policy, please feel free to contact us. The cookie declaration itself is regularly updated via Cookiebot.</p>`
  },
  'email-consent': {
    da: `<p>Jeg giver samtykke til, at Boulders må sende mig e-mails og holde mig opdateret med begivenheder og tiltag, inspiration til træningen, tilbud og andre tjenester.</p>`,
    en: `<p>I give consent for Boulders to send me emails and keep me updated with events and initiatives, training inspiration, offers and other services.</p>`
  },
  'sms-consent': {
    da: `<p>Jeg giver samtykke til, at Boulders må sende mig SMS-beskeder og holde mig opdateret med begivenheder og tiltag, inspiration til træningen, tilbud og andre tjenester.</p>`,
    en: `<p>I give consent for Boulders to send me SMS messages and keep me updated with events and initiatives, training inspiration, offers and other services.</p>`
  },
  'liability-waiver': {
    da: `<p><strong>Dette er kun en forhåndsvisning — du accepterer ikke ansvarsfraskrivelsen her.</strong> Den endelige underskrift indsamles, når du ankommer til Boulders og indløser din billet. Her kan du i ro og mag læse, hvad du skal underskrive på dagen.</p>
<p>Før du begynder at klatre, har vi brug for nogle oplysninger om dig samt din digitale underskrift på vores ansvarsfraskrivelse.</p>
<p>Det følgende er en ansvarsfraskrivelse og generel introduktion om adfærd og sikkerhed, som skal underskrives, før du begynder at klatre i Boulders. Vi opbevarer din underskrift, navn, e-mail og telefonnummer i 12 måneder i henhold til gældende GDPR-lovgivning. Det er dit eget ansvar at forny den. Er du i tvivl om status på din underskrift, så kontakt personalet.</p>
<p>Læs venligst alt på de følgende sider, før du accepterer. For at modtage din bekræftelsesmail skal du acceptere at modtage e-mails fra os. Du vil modtage en bekræftelsesmail kort efter, du har underskrevet. Tjek din spam-mappe, hvis du ikke finder mailen i din indbakke. Hvis du stadig ikke modtager bekræftelsesmailen, bedes du udfylde formularen igen og sikre, at den angivne e-mailadresse er korrekt.</p>
<p>Bemærk venligst, at mindreårige/børn under 16 år skal have en forælders eller værges underskrift for at klatre i Boulders. Underskriv med navnet på forælder/værge.</p>
<h2>Helbredstilstand og personskade</h2>
<p>Alle aktiviteter og klatring i Boulders sker på eget ansvar. Boulders er ikke ansvarlig for din brug af faciliteterne eller udendørsområderne. Som bruger er du selv ansvarlig for at være i en helbredstilstand, der tillader deltagelse i aktiviteter i Boulders.</p>
<p>Klatring er en sportsaktivitet, hvor skader og uheld kan forventes. Kunden accepterer derfor, at brugen af Boulders' faciliteter, herunder klatrefaciliteterne, sker på kundens eget ansvar, og at kunden ikke kan holde Boulders erstatningsansvarlig på nogen måde. Kunden accepterer dermed, at eventuelle skader på kunden eller tredjepart ikke erstattes af Boulders.</p>
<p>Det er dit personlige ansvar at inspicere din landingszone, før du klatrer. Er du i tvivl, så rådfør dig med personalet, før du begynder at klatre. Derudover følges dansk erstatningsret på dette område. Hvis du er ansvarlig for en gruppe børn under 16 år og/eller mindreårige, som klatrer, betragtes din underskrift som en gyldig ansvarsfraskrivelse for alle deltagende personer.</p>
<h2>Vær venligst opmærksom på følgende</h2>
<ol>
<li>Klatreelementer, klatresko og klatrevægge kan være glatte.</li>
<li>Måtterne har forskellige fastheder afhængigt af deres alder.</li>
<li>Fald kan være uventede og pludselige.</li>
<li>Fald fra store højder kan være farlige.</li>
<li>Fald fra lave højder kan også være farlige.</li>
<li>At din landingszone er fri for genstande og/eller personer.</li>
<li>Klatr ned i stedet for at hoppe ned for at undgå at lande forkert.</li>
<li>Orienter dig, før du begynder at klatre, og når du bevæger dig rundt i hallen.</li>
<li>Hold personlige ejendele væk fra måtterne.</li>
<li>Du skal bære klatresko, mens du klatrer. Ingen bare fødder, sokker eller andre typer indendørssko!</li>
<li>Pas på dig selv og kend dine grænser.</li>
</ol>`,
    en: `<p><strong>This is a preview only — you are not accepting the liability waiver here.</strong> The signature is collected when you arrive at Boulders and redeem your ticket. This page lets you read what you'll be signing, ahead of time.</p>
<p>Before you start climbing, we need some information about you and your digital signature on our liability disclaimer.</p>
<p>The following is a liability disclaimer and general orientation about conduct and safety, which must be signed before you start climbing in Boulders. We store your signature, name, email, and phone number for 12 months, under applicable GDPR legislation. It is your own responsibility to renew it. If you're in doubt about the status of your signature, then contact the staff.</p>
<p>Please read everything on the following pages before you accept. To receive your confirmation email, you must accept receiving emails from us. You will receive a confirmation email shortly after you have signed. Check your spam folder if you do not find the email in your inbox. If you still do not receive the confirmation email, please fill out the form again and ensure that the email address provided is correct.</p>
<p>Please note that minors/children under 16 years old, must have a parent or guardian's signature to climb at Boulders. Sign with name of parent/guardian.</p>
<h2>Health Condition and Personal Injury</h2>
<p>All activities and climbing in Boulders are done at your own risk. Boulders is not responsible for your use of the facilities or outdoor areas. As a user, you are responsible for being in a health condition that allows participation in activities at Boulders.</p>
<p>Climbing is a sporting activity where injuries and accidents can be expected. The customer, therefore, agrees that the use of Boulders' facilities, including climbing facilities, is at the customer's own risk and that the customer cannot hold Boulders liable for compensation in any way. The customer thus agrees that any injuries to the customer or third parties will not be compensated by Boulders.</p>
<p>It is your personal responsibility to inspect your landing zone before climbing. If in doubt, consult with staff before you start climbing. Additionally, Danish liability law is followed in this area. If you are responsible for a group of children under 16 years of age and/or minors who are climbing, your signature will be considered a valid liability waiver applicable to all participating individuals.</p>
<h2>Please be aware of the following</h2>
<ol>
<li>Climbing elements, climbing shoes, and climbing walls can be slippery.</li>
<li>The mats have different firmness levels depending on their age.</li>
<li>Falls can be unexpected and sudden.</li>
<li>Falls from great heights can be dangerous.</li>
<li>Falls from low heights can also be dangerous.</li>
<li>That your landing zone is free from objects and/or people.</li>
<li>Climb down instead of jumping down to avoid landing incorrectly.</li>
<li>Orient yourself before you start climbing and as you move around the gym.</li>
<li>Keep personal belongings off the mats.</li>
<li>You must wear climbing shoes while climbing. No bare feet, socks, or other types of indoor shoes!</li>
<li>Take care of yourself and know your limits.</li>
</ol>`
  },
  privacy: {
    da: `<h2>Generelt</h2>
<p>Denne politik for behandling af personoplysninger ("Persondatapolitik") gælder, når du interagerer med Boulders ("vi", "os", "vores"). Den beskriver, hvordan vi indsamler og behandler dine oplysninger, når du bruger vores hjemmeside boulders.goactivebooking.com ("Hjemmesiden"), benytter vores app ("Appen"), eller i forbindelse med dit kundeforhold og din træning hos os.</p>

<h2>Dataansvar</h2>
<p>Boulders ApS er ansvarlig for behandlingen af de personoplysninger, vi indsamler om dig. Du kan kontakte os på følgende adresse:</p>
<p><strong>Boulders ApS</strong><br>
Graham Bells Vej, 8200 Aarhus N<br>
CVR nr.: 32777651<br>
boulders.dk</p>
<p>Hvis du har spørgsmål om vores behandling af dine personoplysninger, kan du skrive til os på hej@boulders.dk.</p>

<h2>Hvornår indsamler vi personlige oplysninger om dig?</h2>
<p>Vi indsamler dine personoplysninger i forskellige situationer, herunder når du:</p>
<ul>
<li>Tilmelder dig som medlem af Boulders.</li>
<li>Underskriver ansvarsfraskrivelse, ved køb af dagsbillet, indløser fribillet eller lign.</li>
<li>Køber produkter på boulders.goactivebooking.com.</li>
<li>Besøger og bruger vores hjemmeside, som beskrevet i vores cookiepolitik.</li>
<li>Kontakter os via e-mail ved at udfylde formular eller sender en mail til en @boulders.dk-mailkonto.</li>
<li>Tilmeld dig vores nyhedsbrev.</li>
<li>Bruger Boulders' app.</li>
<li>Booker hold.</li>
<li>Tjekker ind i vores centre med dit medlems- eller adgangs-kort.</li>
</ul>
<p>Vi kan også modtage oplysninger om dig fra sociale medieplatforme som Facebook og Instagram, når du interagerer med vores sider der.</p>

<h2>Hvilke personlige oplysninger indsamler vi om dig?</h2>
<p>Hos Boulders indsamler vi personoplysninger, der er nødvendige for at administrere dit medlemskab eller anden type adgang, og give dig adgang til vores faciliteter og services. De oplysninger, vi typisk indsamler, omfatter:</p>
<ul>
<li>Dit fulde navn, CPR-nummer (hvis oplyst), køn, e-mailadresse, postadresse, telefonnummer, og billede.</li>
<li>Kredit- eller betalingskortoplysninger, bankkontonummer, og registreringsnummer.</li>
<li>Dit medlemsnummer, check-in tider, og de haller, du besøger.</li>
<li>Dine præferencer for produkter og tjenester samt markedsføringspræferencer.</li>
<li>Data fra Boulders' app, herunder check-ins og hold.</li>
</ul>

<h2>Hvordan bruger vi dine personlige oplysninger?</h2>
<p>Vi anvender dine personlige oplysninger til at levere de tjenester og produkter, du har købt, og til at administrere dit medlemskab. Dette inkluderer:</p>
<ul>
<li>Opfyldelse af aftaler, såsom medlemskab og produktkøb.</li>
<li>Verifikation af identitet ved adgangskontrol.</li>
<li>Fakturering og administration af betalinger.</li>
<li>Kommunikation om tjenester og medlemskab.</li>
<li>Tilbud og kampagner baseret på dine præferencer.</li>
<li>Analyse af brugsmønstre for at forbedre vores tjenester.</li>
<li>Forebyggelse af svindel og overholdelse af sikkerhed.</li>
</ul>

<h2>Grundlag for behandling af dine personlige oplysninger</h2>
<p>Boulders behandler, lagrer og opbevarer dine personoplysninger på følgende lovlige grundlag:</p>
<ul>
<li>Behandling er nødvendig for at opfylde din medlemsaftale med Boulders, jf. GDPR art. 6, stk. 1, litra b.</li>
<li>Behandling er nødvendig for at overholde en retlig forpligtelse, f.eks. bogføringsloven, jf. GDPR art. 6, stk. 1, litra c.</li>
<li>Behandling er nødvendig for vores legitime interesse i at kommunikere med medlemmer og forbedre vores tjenester, jf. GDPR art. 6, stk. 1, litra f.</li>
<li>Behandling af billeder eller specifikke oplysninger, såsom cpr.nr., er baseret på dit samtykke, jf. GDPR art. 6, stk. 1, litra a og relevante bestemmelser i databeskyttelsesloven.</li>
</ul>

<h2>Cookies</h2>
<p>Når du besøger vores hjemmeside, indsamler vi automatisk oplysninger om din brug af hjemmesiden gennem session cookies og lignende teknologier. Cookies er små tekstfiler, der gemmes i din browser og hjælper os med at gøre hjemmesiden mere relevant for dine behov og interesser. Du kan vælge at afvise cookies, men dette kan begrænse din adgang til visse funktioner på vores hjemmeside. Læs mere i vores cookiepolitik her.</p>

<h2>Sletning af dine personlige oplysninger</h2>
<p>Vi opbevarer dine personlige data så længe, det er nødvendigt for formålet med behandlingen. Når dit medlemskab ophører, gemmer vi dine data i en begrænset periode for at opfylde juridiske krav og vores legitime interesser. Typisk opbevares data i op til 3 år efter sidste aktivitet, mens oplysninger omfattet af bogføringsloven kan opbevares i 5 år.</p>`,
    en: `<h2>General</h2>
<p>This policy for the processing of personal data ("Privacy Policy") applies when you interact with Boulders ("we", "us", "our"). It describes how we collect and process your information when you use our website boulders.goactivebooking.com ("Website"), use our app ("App"), or in connection with your customer relationship and training with us.</p>

<h2>Data Controller</h2>
<p>Boulders ApS is responsible for the processing of the personal data we collect about you. You can contact us at the following address:</p>
<p><strong>Boulders ApS</strong><br>
Graham Bells Vej, 8200 Aarhus N<br>
CVR no.: 32777651<br>
boulders.dk</p>
<p>If you have questions about our processing of your personal data, you can write to us at hej@boulders.dk.</p>

<h2>When do we collect personal information about you?</h2>
<p>We collect your personal information in various situations, including when you:</p>
<ul>
<li>Sign up as a member of Boulders.</li>
<li>Sign a liability waiver, when purchasing a day pass, redeeming a free pass, or similar.</li>
<li>Purchase products on boulders.goactivebooking.com.</li>
<li>Visit and use our website, as described in our cookie policy.</li>
<li>Contact us via email by filling out a form or sending an email to a @boulders.dk email account.</li>
<li>Subscribe to our newsletter.</li>
<li>Use Boulders' app.</li>
<li>Book classes.</li>
<li>Check in at our centers with your membership or access card.</li>
</ul>
<p>We may also receive information about you from social media platforms such as Facebook and Instagram when you interact with our pages there.</p>

<h2>What personal information do we collect about you?</h2>
<p>At Boulders, we collect personal information that is necessary to manage your membership or other type of access, and to give you access to our facilities and services. The information we typically collect includes:</p>
<ul>
<li>Your full name, CPR number (if provided), gender, email address, postal address, phone number, and photo.</li>
<li>Credit or payment card information, bank account number, and registration number.</li>
<li>Your membership number, check-in times, and the halls you visit.</li>
<li>Your preferences for products and services as well as marketing preferences.</li>
<li>Data from Boulders' app, including check-ins and classes.</li>
</ul>

<h2>How do we use your personal information?</h2>
<p>We use your personal information to deliver the services and products you have purchased, and to manage your membership. This includes:</p>
<ul>
<li>Fulfillment of agreements, such as membership and product purchases.</li>
<li>Identity verification for access control.</li>
<li>Billing and payment administration.</li>
<li>Communication about services and membership.</li>
<li>Offers and campaigns based on your preferences.</li>
<li>Analysis of usage patterns to improve our services.</li>
<li>Fraud prevention and security compliance.</li>
</ul>

<h2>Legal basis for processing your personal information</h2>
<p>Boulders processes, stores, and retains your personal information on the following legal bases:</p>
<ul>
<li>Processing is necessary to fulfill your membership agreement with Boulders, cf. GDPR art. 6, para. 1, lit. b.</li>
<li>Processing is necessary to comply with a legal obligation, e.g., the Accounting Act, cf. GDPR art. 6, para. 1, lit. c.</li>
<li>Processing is necessary for our legitimate interest in communicating with members and improving our services, cf. GDPR art. 6, para. 1, lit. f.</li>
<li>Processing of images or specific information, such as CPR no., is based on your consent, cf. GDPR art. 6, para. 1, lit. a and relevant provisions in the Data Protection Act.</li>
</ul>

<h2>Cookies</h2>
<p>When you visit our website, we automatically collect information about your use of the website through session cookies and similar technologies. Cookies are small text files stored in your browser that help us make the website more relevant to your needs and interests. You can choose to reject cookies, but this may limit your access to certain features on our website. Read more in our cookie policy here.</p>

<h2>Deletion of your personal information</h2>
<p>We retain your personal data for as long as necessary for the purpose of processing. When your membership ends, we store your data for a limited period to comply with legal requirements and our legitimate interests. Typically, data is retained for up to 3 years after last activity, while information covered by the Accounting Act may be retained for 5 years.</p>`
  }
};

// Terms Modal Functions
function openTermsModal(termsType) {
  if (!DOM.termsModal || !DOM.termsModalContent || !DOM.termsModalTitle) return;
  
  state.currentModalType = termsType;
  
  // Language is managed by state.language - use translation function
  const currentLang = state.language || DEFAULT_LANGUAGE;
  
  const termsTitles = {
    membership: t('footer.terms.membership'),
    punchcard: t('footer.terms.punchcard'),
    privacy: t('footer.policies.privacy'),
    cookie: t('footer.policies.cookie'),
    'email-consent': 'E-mail',
    'sms-consent': 'SMS',
    'liability-waiver': t('liability.waiver.title'),
  };

  const isShortConsentModal = termsType === 'sms-consent' || termsType === 'email-consent';
  DOM.termsModal.classList.toggle('terms-modal--short-consent', isShortConsentModal);
  
  // Hide search for short one-paragraph consent modals like SMS and Email consent.
  const showModalSearch = termsType !== 'sms-consent' && termsType !== 'email-consent';
  if (DOM.termsModalSearch) {
    DOM.termsModalSearch.style.display = showModalSearch ? 'block' : 'none';
    if (showModalSearch && DOM.termsSearchInput) {
      const placeholder = currentLang === 'da' || (currentLang && currentLang.startsWith('da'))
        ? DOM.termsSearchInput.dataset.placeholderDa || 'Søg i vilkår...'
        : DOM.termsSearchInput.dataset.placeholderEn || 'Search terms...';
      DOM.termsSearchInput.placeholder = placeholder;
    }
  }
  
  // Handle 'terms' type with tabs
  if (termsType === 'terms') {
    // Show tabs
    if (DOM.termsModalTabs) {
      DOM.termsModalTabs.style.display = 'flex';
    }
    
    // Set title based on language
    const title = currentLang === 'da' ? 'Vilkår og Betingelser' : 'Terms and Conditions';
    DOM.termsModalTitle.textContent = title;
    
    // Show Punch Card tab by default when punch card is in cart; otherwise Membership
    const hasPunchCardInCart = state.selectedProductType === 'punch-card' &&
      state.valueCardQuantities &&
      Array.from(state.valueCardQuantities.values()).some((q) => q > 0);
    const defaultTab = hasPunchCardInCart ? 'punchcard' : 'membership';
    state.currentModalTab = defaultTab;
    switchTermsTab(defaultTab);
    
    // Store original content for search
    const currentTab = state.currentModalTab || 'membership';
    const content = termsContent[currentTab]?.[currentLang] || termsContent[currentTab]?.da || '';
    state.termsOriginalContent = content;
  } else {
    // Hide tabs for other types (search stays visible for all)
    if (DOM.termsModalTabs) {
      DOM.termsModalTabs.style.display = 'none';
    }
    
    // Convert language code (da-DK -> da, en-GB -> en)
    const langCode = currentLang.split('-')[0];
    const title = termsTitles[termsType] || 'Terms and Conditions';
    const content = termsContent[termsType]?.[langCode] || termsContent[termsType]?.da || '<p>Content not available.</p>';
    
    if (!termsContent[termsType]) {
      console.error('Invalid terms type:', termsType);
      return;
    }
    
    // Set title
    DOM.termsModalTitle.textContent = title;
    
    // Set content
    DOM.termsModalContent.innerHTML = sanitizeHTML(content);
    state.termsOriginalContent = content;
  }
  
  // Clear search when opening modal
  if (DOM.termsSearchInput) {
    DOM.termsSearchInput.value = '';
    if (DOM.termsSearchClear) {
      DOM.termsSearchClear.style.display = 'none';
    }
  }
  
  // Show modal
  DOM.termsModal.style.display = 'flex';
  DOM.termsModalContent.style.display = 'block';
  if (DOM.termsModalLoading) {
    DOM.termsModalLoading.style.display = 'none';
  }
  document.body.classList.add('modal-open');
  
  // Scroll to top of modal content
  DOM.termsModalContent.scrollTop = 0;
}

function switchTermsTab(tabType) {
  if (!DOM.termsModalTabs || !DOM.termsModalContent) return;
  
  state.currentModalTab = tabType;
  
  // Clear search when switching tabs
  if (DOM.termsSearchInput) {
    DOM.termsSearchInput.value = '';
    clearTermsSearch();
  }
  
  // Update active tab
  const tabs = DOM.termsModalTabs.querySelectorAll('.terms-tab');
  tabs.forEach(tab => {
    tab.classList.remove('active');
    if (tab.dataset.tab === tabType) {
      tab.classList.add('active');
    }
  });
  
  // Update tab labels based on language
  // Terms tabs are now handled by updatePageTranslations via data-i18n-key
  // This function can be removed or simplified
  tabs.forEach(tab => {
    const key = tab.getAttribute('data-i18n-key');
    if (key) {
      tab.textContent = t(key);
    }
  });
  
  // Load content for selected tab
  // Convert da-DK to da, en-GB to en for content lookup
  const langCode = (state.language || DEFAULT_LANGUAGE).split('-')[0];
  const content = termsContent[tabType]?.[langCode] || termsContent[tabType]?.da || '<p>Content not available.</p>';
  
  if (termsContent[tabType]) {
    DOM.termsModalContent.innerHTML = sanitizeHTML(content);
    state.termsOriginalContent = content;
    DOM.termsModalContent.scrollTop = 0;
  }
}

function performTermsSearch(searchQuery) {
  if (!DOM.termsModalContent || !state.termsOriginalContent) return;
  
  const query = searchQuery.trim().toLowerCase();
  
  // Show/hide clear button
  if (DOM.termsSearchClear) {
    DOM.termsSearchClear.style.display = query ? 'flex' : 'none';
  }
  
  if (!query) {
    // Restore original content
    DOM.termsModalContent.innerHTML = sanitizeHTML(state.termsOriginalContent);
    return;
  }

  // Create a temporary div to parse HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = sanitizeHTML(state.termsOriginalContent);
  
  // Search and highlight function
  function highlightText(node, searchText) {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent;
      const regex = new RegExp(`(${searchText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
      const matches = text.match(regex);
      
      if (matches && matches.length > 0) {
        const parts = text.split(regex);
        const fragment = document.createDocumentFragment();
        
        parts.forEach((part, index) => {
          if (part.toLowerCase() === searchText.toLowerCase()) {
            const highlight = document.createElement('span');
            highlight.className = 'search-highlight';
            highlight.textContent = part;
            fragment.appendChild(highlight);
          } else if (part) {
            fragment.appendChild(document.createTextNode(part));
          }
        });
        
        return fragment;
      }
      return null;
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      // Skip script and style tags
      if (node.tagName === 'SCRIPT' || node.tagName === 'STYLE') {
        return null;
      }
      
      const clone = node.cloneNode(false);
      let hasMatch = false;
      
      Array.from(node.childNodes).forEach(child => {
        const result = highlightText(child, searchText);
        if (result) {
          clone.appendChild(result);
          hasMatch = true;
        } else if (child.nodeType === Node.ELEMENT_NODE) {
          // Check if element contains the search text
          if (child.textContent.toLowerCase().includes(searchText)) {
            clone.appendChild(child.cloneNode(true));
            hasMatch = true;
          }
        } else if (child.nodeType === Node.TEXT_NODE) {
          clone.appendChild(child.cloneNode(true));
        }
      });
      
      return hasMatch ? clone : null;
    }
    return null;
  }
  
  // Perform search
  const highlightedContent = highlightText(tempDiv, query);
  
  if (highlightedContent) {
    DOM.termsModalContent.innerHTML = '';
    DOM.termsModalContent.appendChild(highlightedContent);
  } else {
    // No results found
    DOM.termsModalContent.innerHTML = sanitizeHTML('<div class="search-no-results">No results found for "' + searchQuery + '"</div>');
  }
  
  // Scroll to first match
  const firstHighlight = DOM.termsModalContent.querySelector('.search-highlight');
  if (firstHighlight) {
    firstHighlight.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
}

function clearTermsSearch() {
  if (!DOM.termsSearchInput || !DOM.termsModalContent) return;
  
  DOM.termsSearchInput.value = '';
  if (DOM.termsSearchClear) {
    DOM.termsSearchClear.style.display = 'none';
  }
  
  // Restore original content
  if (state.termsOriginalContent) {
    DOM.termsModalContent.innerHTML = sanitizeHTML(state.termsOriginalContent);
    DOM.termsModalContent.scrollTop = 0;
  }
}

// Data policy modal search (same algorithm as terms search)
function performDataPolicySearch(searchQuery) {
  if (!DOM.dataPolicyModalContent || !state.dataPolicyOriginalContent) return;
  
  const query = searchQuery.trim().toLowerCase();
  
  if (DOM.dataPolicySearchClear) {
    DOM.dataPolicySearchClear.style.display = query ? 'flex' : 'none';
  }
  
  if (!query) {
    DOM.dataPolicyModalContent.innerHTML = sanitizeHTML(state.dataPolicyOriginalContent);
    return;
  }

  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = sanitizeHTML(state.dataPolicyOriginalContent);
  
  function highlightText(node, searchText) {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent;
      const regex = new RegExp(`(${searchText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
      const matches = text.match(regex);
      if (matches && matches.length > 0) {
        const parts = text.split(regex);
        const fragment = document.createDocumentFragment();
        parts.forEach((part, index) => {
          if (part.toLowerCase() === searchText.toLowerCase()) {
            const highlight = document.createElement('span');
            highlight.className = 'search-highlight';
            highlight.textContent = part;
            fragment.appendChild(highlight);
          } else if (part) {
            fragment.appendChild(document.createTextNode(part));
          }
        });
        return fragment;
      }
      return null;
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      if (node.tagName === 'SCRIPT' || node.tagName === 'STYLE') return null;
      const clone = node.cloneNode(false);
      let hasMatch = false;
      Array.from(node.childNodes).forEach(child => {
        const result = highlightText(child, searchText);
        if (result) {
          clone.appendChild(result);
          hasMatch = true;
        } else if (child.nodeType === Node.ELEMENT_NODE) {
          if (child.textContent.toLowerCase().includes(searchText)) {
            clone.appendChild(child.cloneNode(true));
            hasMatch = true;
          }
        } else if (child.nodeType === Node.TEXT_NODE) {
          clone.appendChild(child.cloneNode(true));
        }
      });
      return hasMatch ? clone : null;
    }
    return null;
  }

  const highlightedContent = highlightText(tempDiv, query);
  if (highlightedContent) {
    DOM.dataPolicyModalContent.innerHTML = '';
    DOM.dataPolicyModalContent.appendChild(highlightedContent);
  } else {
    DOM.dataPolicyModalContent.innerHTML = sanitizeHTML('<div class="search-no-results">No results found for "' + searchQuery + '"</div>');
  }
  const firstHighlight = DOM.dataPolicyModalContent.querySelector('.search-highlight');
  if (firstHighlight) {
    firstHighlight.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
}

function clearDataPolicySearch() {
  if (!DOM.dataPolicySearchInput || !DOM.dataPolicyModalContent) return;
  DOM.dataPolicySearchInput.value = '';
  if (DOM.dataPolicySearchClear) DOM.dataPolicySearchClear.style.display = 'none';
  if (state.dataPolicyOriginalContent) {
    DOM.dataPolicyModalContent.innerHTML = sanitizeHTML(state.dataPolicyOriginalContent);
    DOM.dataPolicyModalContent.scrollTop = 0;
  }
}

// Legacy function - language is now managed by state.language
// This function is kept for backward compatibility but does nothing
function updateLanguageSwitcher(modalType, lang) {
  // Language switching is now handled by changeLanguage() and updatePageTranslations()
  // Modal language switchers should use the main language switcher
}

function switchModalLanguage(modalType, lang) {
  // Language is now managed by state.language and changeLanguage()
  // Update translations for the modal
  updatePageTranslations();
  
  if (modalType === 'terms') {
    // Terms tabs are handled by updatePageTranslations via data-i18n-key
    
    // Update title
    const currentTab = state.currentModalTab || 'membership';
    if (state.currentModalType === 'terms') {
      const title = t('footer.terms.title');
      if (DOM.termsModalTitle) {
        DOM.termsModalTitle.textContent = title;
      }
      
      // Update tab labels (handled by updatePageTranslations via data-i18n-key)
      const tabs = DOM.termsModalTabs?.querySelectorAll('.terms-tab[data-i18n-key]');
      tabs?.forEach(tab => {
        const key = tab.getAttribute('data-i18n-key');
        if (key) {
          tab.textContent = t(key);
        }
      });
      
      // Update search placeholder (if needed, add to translations)
      // Reload content for current tab (this will clear search)
      switchTermsTab(currentTab);
      
      // Store original content for search after language switch
      // Convert da-DK to da, en-GB to en for content lookup
      const langCode = lang.split('-')[0];
      const content = termsContent[currentTab]?.[langCode] || termsContent[currentTab]?.da || '';
      state.termsOriginalContent = content;
    } else {
      // For non-tabbed content
      const termsTitles = {
        membership: {
          da: 'Vilkår og Betingelser for Medlemskab',
          en: 'Terms and Conditions for Membership',
        },
        punchcard: {
          da: 'Vilkår og Betingelser for Klippekort',
          en: 'Terms and Conditions for Punch Card',
        },
      };
      
      const title = termsTitles[state.currentModalType]?.[lang] || termsTitles[state.currentModalType]?.da || 'Terms and Conditions';
      const content = termsContent[state.currentModalType]?.[lang] || termsContent[state.currentModalType]?.da || '<p>Content not available.</p>';
      
      if (DOM.termsModalTitle) {
        DOM.termsModalTitle.textContent = title;
      }
      if (DOM.termsModalContent) {
        DOM.termsModalContent.innerHTML = sanitizeHTML(content);
        state.termsOriginalContent = content;
        DOM.termsModalContent.scrollTop = 0;
      }
      
      // Clear search when switching language
      if (DOM.termsSearchInput) {
        DOM.termsSearchInput.value = '';
        if (DOM.termsSearchClear) {
          DOM.termsSearchClear.style.display = 'none';
        }
      }
    }
  }
  // Privacy/data policy modal no longer has its own language switcher; language follows header (see refreshOpenFooterModals in changeLanguage).
}

function openDataPolicyModal() {
  if (!DOM.dataPolicyModal || !DOM.dataPolicyModalContent) return;
  
  state.currentModalType = 'privacy';
  
  updatePageTranslations();
  
  const langCode = (state.language || DEFAULT_LANGUAGE).split('-')[0];
  const content = termsContent.privacy?.[langCode] || termsContent.privacy?.da || '<p>Data policy content not available.</p>';
  
  DOM.dataPolicyModalContent.innerHTML = sanitizeHTML(content);
  state.dataPolicyOriginalContent = content;
  
  // Show and reset search (same as T&C modal)
  if (DOM.dataPolicyModalSearch) DOM.dataPolicyModalSearch.style.display = 'block';
  if (DOM.dataPolicySearchInput) {
    DOM.dataPolicySearchInput.value = '';
    const isDa = (state.language || DEFAULT_LANGUAGE).startsWith('da');
    DOM.dataPolicySearchInput.placeholder = isDa
      ? (DOM.dataPolicySearchInput.dataset?.placeholderDa || 'Søg...')
      : (DOM.dataPolicySearchInput.dataset?.placeholderEn || 'Search...');
  }
  if (DOM.dataPolicySearchClear) DOM.dataPolicySearchClear.style.display = 'none';
  
  DOM.dataPolicyModal.style.display = 'flex';
  DOM.dataPolicyModalContent.style.display = 'block';
  if (DOM.dataPolicyModalLoading) DOM.dataPolicyModalLoading.style.display = 'none';
  document.body.classList.add('modal-open');
  DOM.dataPolicyModalContent.scrollTop = 0;
}

function closeTermsModal() {
  if (!DOM.termsModal) return;
  
  // Clear content
  if (DOM.termsModalContent) {
    DOM.termsModalContent.innerHTML = '';
  }
  
  // Clear search
  if (DOM.termsSearchInput) {
    DOM.termsSearchInput.value = '';
  }
  if (DOM.termsSearchClear) {
    DOM.termsSearchClear.style.display = 'none';
  }
  
  // Reset state
  state.currentModalType = null;
  state.currentModalTab = null;
  state.termsOriginalContent = null;
  
  // Hide modal
  DOM.termsModal.style.display = 'none';
  DOM.termsModal.classList.remove('terms-modal--short-consent');
  document.body.classList.remove('modal-open');
}

function closeDataPolicyModal() {
  if (!DOM.dataPolicyModal) return;
  
  if (DOM.dataPolicyModalContent) DOM.dataPolicyModalContent.innerHTML = '';
  clearDataPolicySearch();
  state.dataPolicyOriginalContent = null;
  state.currentModalType = null;
  DOM.dataPolicyModal.style.display = 'none';
  document.body.classList.remove('modal-open');
}

function openCookieDeclarationModal() {
  if (!DOM.cookieDeclarationModal) return;
  DOM.cookieDeclarationModal.style.display = 'flex';
  document.body.classList.add('modal-open');
}

function closeCookieDeclarationModal() {
  if (!DOM.cookieDeclarationModal) return;
  DOM.cookieDeclarationModal.style.display = 'none';
  document.body.classList.remove('modal-open');
}

async function handleForgotPasswordSubmit(event) {
  event.preventDefault();
  
  if (!DOM.forgotPasswordEmail) return;
  
  const email = DOM.forgotPasswordEmail.value.trim();
  
  if (!email) {
    showToast('Please enter your email address.', 'error');
    DOM.forgotPasswordEmail.closest('.form-group')?.classList.add('error');
    return;
  }
  
  // Validate email format
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    showToast('Please enter a valid email address.', 'error');
    DOM.forgotPasswordEmail.closest('.form-group')?.classList.add('error');
    return;
  }
  
  // Remove error state
  DOM.forgotPasswordEmail.closest('.form-group')?.classList.remove('error');
  
  // Disable form during submission
  const submitButton = DOM.forgotPasswordForm.querySelector('button[type="submit"]');
  const originalButtonText = submitButton?.textContent || 'SEND RESET LINK';
  if (submitButton) {
    submitButton.disabled = true;
    submitButton.textContent = 'Sending...';
  }
  
  try {
    console.log('[Forgot Password] Requesting password reset for:', email);
    await authAPI.resetPassword(email);
    
    // Show success message
    DOM.forgotPasswordForm.style.display = 'none';
    DOM.forgotPasswordSuccess.style.display = 'block';
    
    showToast('Password reset instructions have been sent to your email.', 'success');
    
    console.log('[Forgot Password] Password reset request successful');
  } catch (error) {
    console.error('[Forgot Password] Password reset failed:', error);
    showToast(getErrorMessage(error, 'Password reset'), 'error');
    
    // Re-enable form
    if (submitButton) {
      submitButton.disabled = false;
      submitButton.textContent = originalButtonText;
    }
  }
}

function refreshHeaderAuthIndicator() {
  const headerContent = document.getElementById('headerContent');
  const headerAuthIndicator = document.getElementById('headerAuthIndicator');
  const headerAuthEmail = document.getElementById('headerAuthEmail');
  const headerLogoutBtn = document.getElementById('headerLogoutBtn');
  
  if (!headerAuthIndicator) return;
  
  const authenticated = isUserAuthenticated();
  
  // Always show the header (logo should always be visible)
  if (headerContent) {
    headerContent.style.display = 'block';
  }
  
  if (!authenticated) {
    // Hide auth indicator when not authenticated, but keep header visible
    headerAuthIndicator.style.display = 'none';
    return;
  }
  
  // Get email from various sources
  const metadata = getTokenMetadata();
  const customer = state.authenticatedCustomer;
  const emailDisplay = state.authenticatedEmail || customer?.email || metadata?.email || metadata?.username || 'User';
  
  // Update email display
  if (headerAuthEmail) {
    headerAuthEmail.textContent = emailDisplay;
  }
  
  // Show the indicator
  headerAuthIndicator.style.display = 'flex';
}

function formatCityDisplay(city) {
  if (!city || typeof city !== 'string') return city;
  const trimmed = city.trim();
  if (!trimmed) return trimmed;
  return trimmed
    .split(' ')
    .filter(Boolean)
    .map(word => word
      .split('-')
      .map(part => {
        if (!part) return part;
        const upper = part.toUpperCase();
        if (part.length <= 2 && part === upper) {
          return upper; // Preserve short abbreviations like "C" or "Ø"
        }
        return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
      })
      .join('-'))
    .join(' ');
}

function refreshLoginUI() {
  // Update header auth indicator first
  refreshHeaderAuthIndicator();
  
  if (!DOM.loginStatus && !DOM.loginFormContainer) {
    return;
  }

  const authenticated = isUserAuthenticated();
  const metadata = getTokenMetadata();
  const customer = state.authenticatedCustomer;
  
  // Get form data if available (for recently saved accounts)
  let formData = null;
  try {
    formData = buildCheckoutPayload();
  } catch (e) {
    // Ignore errors if form is not ready
  }
  const formCustomer = formData?.customer;
  
  // Determine display values
  const emailDisplay = state.authenticatedEmail || customer?.email || metadata?.email || metadata?.username || 'Account';
  
  // Get full name from customer data or form data
  let nameDisplay = null;
  if (customer?.firstName && customer?.lastName) {
    nameDisplay = `${customer.firstName} ${customer.lastName}`;
  } else if (customer?.firstName) {
    nameDisplay = customer.firstName;
  } else if (customer?.lastName) {
    nameDisplay = customer.lastName;
  } else if (formCustomer?.firstName && formCustomer?.lastName) {
    nameDisplay = `${formCustomer.firstName} ${formCustomer.lastName}`;
  } else if (formCustomer?.firstName) {
    nameDisplay = formCustomer.firstName;
  } else if (formCustomer?.lastName) {
    nameDisplay = formCustomer.lastName;
  }
  
  // Debug logging
  if (authenticated && !nameDisplay) {
    console.log('[refreshLoginUI] No name found. Customer:', customer);
    console.log('[refreshLoginUI] Form customer:', formCustomer);
    console.log('[refreshLoginUI] Metadata:', metadata);
  }

  if (DOM.loginStatus) {
    DOM.loginStatus.style.display = authenticated ? 'block' : 'none';
  }

  // Ensure checkout button state is correct after auth change (e.g. after creating profile, logout)
  // Always reset any stuck "Processing..." state - covers logout, auth expiry, and interrupted checkout
  if (state.checkoutInProgress) {
    state.checkoutInProgress = false;
    setCheckoutLoadingState(false);
  } else {
    updateCheckoutButton();
  }
  
  // Update name display (order: 1)
  if (DOM.loginStatusName && DOM.loginStatusNameRow) {
    if (nameDisplay) {
      DOM.loginStatusName.textContent = nameDisplay;
      DOM.loginStatusNameRow.style.display = 'flex';
    } else {
      DOM.loginStatusNameRow.style.display = 'none';
    }
  }
  
  // Update date of birth display (order: 2)
  if (DOM.loginStatusDob && DOM.loginStatusDobRow) {
    let dobDisplay = null;
    const dobSource = customer?.dateOfBirth || customer?.birthDate || formCustomer?.dateOfBirth;
    if (dobSource) {
      if (typeof dobSource === 'string') {
        // Format date string if needed (YYYY-MM-DD to DD/MM/YYYY)
        const dateMatch = dobSource.match(/(\d{4})-(\d{2})-(\d{2})/);
        if (dateMatch) {
          dobDisplay = `${dateMatch[3]}/${dateMatch[2]}/${dateMatch[1]}`;
        } else {
          dobDisplay = dobSource;
        }
      } else if (dobSource.year && dobSource.month && dobSource.day) {
        dobDisplay = `${String(dobSource.day).padStart(2, '0')}/${String(dobSource.month).padStart(2, '0')}/${dobSource.year}`;
      }
    }
    if (dobDisplay) {
      DOM.loginStatusDob.textContent = dobDisplay;
      DOM.loginStatusDobRow.style.display = 'flex';
    } else {
      DOM.loginStatusDobRow.style.display = 'none';
    }
  }
  
  // Update email display (order: 3)
  if (DOM.loginStatusEmail && DOM.loginStatusEmailRow) {
    if (emailDisplay && emailDisplay !== 'Account') {
      DOM.loginStatusEmail.textContent = emailDisplay;
      DOM.loginStatusEmailRow.style.display = 'flex';
    } else {
      DOM.loginStatusEmailRow.style.display = 'none';
    }
  }
  
  // Update address display (order: 4)
  if (DOM.loginStatusAddress && DOM.loginStatusAddressRow) {
    let addressParts = [];
    const addressSource =
      customer?.address ||
      customer?.shippingAddress ||
      customer?.streetAddress ||
      customer?.addressLine1 ||
      formCustomer?.address;
    const postalCodeSource =
      customer?.postalCode ||
      customer?.zip ||
      customer?.shippingAddress?.postalCode ||
      formCustomer?.address?.postalCode;
    const citySource =
      customer?.city ||
      customer?.shippingAddress?.city ||
      customer?.addressCity ||
      formCustomer?.address?.city;
    
    if (addressSource) {
      if (typeof addressSource === 'string') {
        addressParts.push(addressSource);
      } else if (addressSource.street) {
        addressParts.push(addressSource.street);
      }
    }
    if (postalCodeSource) {
      addressParts.push(postalCodeSource);
    }
    if (citySource) {
      addressParts.push(formatCityDisplay(citySource));
    }
    const addressDisplay = addressParts.length > 0 ? addressParts.join(', ') : null;
    if (addressDisplay) {
      DOM.loginStatusAddress.textContent = addressDisplay;
      DOM.loginStatusAddressRow.style.display = 'flex';
    } else {
      DOM.loginStatusAddressRow.style.display = 'none';
    }
  }
  
  // Update phone number display (order: 5)
  if (DOM.loginStatusPhone && DOM.loginStatusPhoneRow) {
    let phoneDisplay = null;
    const phoneSource =
      customer?.mobilePhone ||
      customer?.phone ||
      customer?.phoneNumber ||
      formCustomer?.phone;
    const phoneCountryCodeSource =
      customer?.phoneCountryCode ||
      customer?.phoneCountry ||
      formCustomer?.phone?.countryCode;
    
    if (phoneSource) {
      if (typeof phoneSource === 'string') {
        phoneDisplay = phoneSource;
      } else if (phoneSource.number) {
        const countryCode = phoneSource.countryCode || phoneCountryCodeSource || '';
        phoneDisplay = countryCode ? `${countryCode} ${phoneSource.number}` : phoneSource.number;
      }
    } else if (formCustomer?.phoneNumber && formCustomer?.countryCode) {
      phoneDisplay = `${formCustomer.countryCode} ${formCustomer.phoneNumber}`;
    }
    
    if (phoneDisplay) {
      DOM.loginStatusPhone.textContent = phoneDisplay;
      DOM.loginStatusPhoneRow.style.display = 'flex';
    } else {
      DOM.loginStatusPhoneRow.style.display = 'none';
    }
  }
  
  if (DOM.loginFormContainer) {
    DOM.loginFormContainer.style.display = authenticated ? 'none' : '';
  }
  
  // Hide form-container when logged in, but keep login-status visible
  // Move login-status outside form-container when authenticated so it remains visible
  if (DOM.loginFormContainerWrapper && DOM.loginStatus) {
    if (authenticated) {
      // Hide form-container
      DOM.loginFormContainerWrapper.style.display = 'none';
      
      // Move login-status outside form-container to keep it visible
      // Find the parent of form-container (info-section)
      const formContainerParent = DOM.loginFormContainerWrapper.parentElement;
      if (formContainerParent && DOM.loginStatus.parentElement === DOM.loginFormContainerWrapper) {
        // Move login-status to be a sibling of form-container
        formContainerParent.insertBefore(DOM.loginStatus, DOM.loginFormContainerWrapper);
        DOM.loginStatus.style.display = 'block';
      }
    } else {
      // Show form-container when not authenticated
      DOM.loginFormContainerWrapper.style.display = '';
      
      // Move login-status back inside form-container if it was moved out
      if (DOM.loginStatus.parentElement !== DOM.loginFormContainerWrapper) {
        // Insert login-status as first child of form-container
        DOM.loginFormContainerWrapper.insertBefore(DOM.loginStatus, DOM.loginFormContainerWrapper.firstChild);
      }
    }
  }
  
  // Hide auth mode toggle when user is logged in
  if (DOM.authModeToggle) {
    DOM.authModeToggle.style.display = authenticated ? 'none' : '';
  }
  
  // Handle section visibility based on authentication state
  const createSection = document.querySelector('[data-auth-section="create"]');
  const loginSection = document.querySelector('[data-auth-section="login"]');
  
  if (authenticated) {
    // When logged in: hide create section, show login section (for login-status)
    if (createSection) {
      createSection.style.display = 'none';
    }
    if (loginSection) {
      loginSection.style.display = 'block';
    }
  } else {
    // When logged out: hide login section, create section visibility handled by switchAuthMode
    if (loginSection) {
      loginSection.style.display = 'none';
    }
    // Create section will be shown/hidden by switchAuthMode based on active tab
  }
}

async function handleSaveAccount() {
  const saveBtn = document.querySelector('[data-action="save-account"]');
  const messageDiv = document.getElementById('saveAccountMessage');
  
  if (!saveBtn || !messageDiv) {
    console.error('[Save Account] Save button or message div not found');
    return;
  }
  
  // Validate required fields
  const requiredFields = [
    { id: 'firstName', name: 'First name' },
    { id: 'lastName', name: 'Last name' },
    { id: 'dateOfBirth', name: 'Date of birth' },
    { id: 'streetAddress', name: 'Street address' },
    { id: 'postalCode', name: 'Postal code' },
    { id: 'email', name: 'Email' },
    { id: 'phoneNumber', name: 'Phone number' },
    { id: 'password', name: 'Password' },
  ];
  
  const missingFields = [];
  requiredFields.forEach(field => {
    const input = document.getElementById(field.id);
    if (!input || !input.value.trim()) {
      missingFields.push(field.name);
    }
  });
  
  // Validate privacy consent
  const privacyConsent = document.getElementById('privacyConsent');
  if (!privacyConsent || !privacyConsent.checked) {
    showSaveAccountMessage('Please accept the privacy policy to continue.', 'error');
    if (privacyConsent) {
      const formGroup = privacyConsent.closest('.form-group');
      if (formGroup) {
        formGroup.classList.add('error');
        formGroup.style.animation = 'shake 0.5s ease-in-out';
        setTimeout(() => {
          formGroup.style.animation = '';
        }, 500);
      }
    }
    const saveBtn = document.querySelector('[data-action="save-account"]');
    if (saveBtn) {
      saveBtn.disabled = false;
      saveBtn.textContent = 'Save Account';
    }
    return;
  }
  
  // Validate password length
  const passwordInput = document.getElementById('password');
  if (passwordInput && passwordInput.value && passwordInput.value.length < 6) {
    showSaveAccountMessage('Password must be at least 6 characters long.', 'error');
    highlightFieldError('password', true);
    const saveBtn = document.querySelector('[data-action="save-account"]');
    if (saveBtn) {
      saveBtn.disabled = false;
      saveBtn.textContent = 'Save Account';
    }
    return;
  }
  
  // Validate parent password length if parent form is visible
  const parentPasswordInput = document.getElementById('parentPassword');
  const parentForm = document.getElementById('parentGuardianForm');
  if (parentPasswordInput && parentForm && parentForm.style.display !== 'none' && parentPasswordInput.value && parentPasswordInput.value.length < 6) {
    showSaveAccountMessage('Parent/Guardian password must be at least 6 characters long.', 'error');
    highlightFieldError('parentPassword', true);
    const saveBtn = document.querySelector('[data-action="save-account"]');
    if (saveBtn) {
      saveBtn.disabled = false;
      saveBtn.textContent = 'Save Account';
    }
    return;
  }
  
  if (missingFields.length > 0) {
    showSaveAccountMessage(`Please fill in all required fields: ${missingFields.join(', ')}`, 'error');
    // Animate save account button with red flash and highlight missing fields
    const saveBtn = document.querySelector('[data-action="save-account"]');
    if (saveBtn) {
      saveBtn.classList.remove('error-flash');
      // Trigger reflow to restart animation
      void saveBtn.offsetWidth;
      saveBtn.classList.add('error-flash');
      setTimeout(() => {
        saveBtn.classList.remove('error-flash');
      }, 600);
    }
    
    // Highlight missing fields with shake animation
    requiredFields.forEach(field => {
      const input = document.getElementById(field.id);
      if (!input || !input.value.trim()) {
        highlightFieldError(field.id, true); // Animate when triggered from button click
      }
    });
    return;
  }
  
  // Set loading state
  saveBtn.disabled = true;
  saveBtn.textContent = 'Saving...';
  messageDiv.style.display = 'none';
  
  try {
    // Build customer data from form
    const payload = buildCheckoutPayload();
    
    // Also read form fields directly as fallback to ensure we get all values
    const phoneNumberField = document.getElementById('phoneNumber');
    const countryCodeField = document.getElementById('countryCode');
    const dateOfBirthField = document.getElementById('dateOfBirth');
    
    // Get phone number and country code
    const phoneNumber = payload.customer?.phone?.number || phoneNumberField?.value?.trim() || payload.customer?.phone;
    const phoneCountryCode = payload.customer?.phone?.countryCode || countryCodeField?.value?.trim() || '+45';
    
    // Build mobilePhone object if we have phone number
    let mobilePhone = null;
    if (phoneNumber) {
      mobilePhone = {
        countryCode: phoneCountryCode,
        number: phoneNumber
      };
    }
    
    // Get date of birth and convert to birthDate format if needed
    let birthDate = payload.customer?.dateOfBirth || dateOfBirthField?.value?.trim();
    // If dateOfBirth is in YYYY-MM-DD format, keep it (API might accept this)
    // Otherwise, ensure it's in the correct format
    
    // Get address fields
    const streetAddress = payload.customer?.address?.street || document.getElementById('streetAddress')?.value?.trim() || payload.customer?.address;
    const city = payload.customer?.address?.city || document.getElementById('city')?.value?.trim() || payload.customer?.city;
    const postalCode = payload.customer?.address?.postalCode || document.getElementById('postalCode')?.value?.trim() || payload.customer?.postalCode;
    // Country is always Denmark (DK) for this application
    const country = 'DK';
    
    // Build shippingAddress object if we have address data (API expects shippingAddress)
    let shippingAddress = null;
    if (streetAddress || city || postalCode) {
      shippingAddress = {};
      if (streetAddress) shippingAddress.street = streetAddress;
      if (city) shippingAddress.city = city;
      if (postalCode) shippingAddress.postalCode = postalCode;
      shippingAddress.country = country; // Always DK
    }
    
    // Get privacy/terms consent timestamp for acceptedRegistrationTerms
    // Use timestamp from localStorage if available, otherwise use current time
    let acceptedRegistrationTerms = null;
    const privacyConsent = document.getElementById('privacyConsent');
    if (privacyConsent && privacyConsent.checked) {
      try {
        const privacyTimestamp = localStorage.getItem('boulders_privacy_consent_timestamp');
        if (privacyTimestamp) {
          acceptedRegistrationTerms = privacyTimestamp;
        } else {
          // Fallback: use current timestamp if consent is checked but no timestamp stored
          acceptedRegistrationTerms = new Date().toISOString();
        }
        console.log('[Save Account] Using acceptedRegistrationTerms timestamp:', acceptedRegistrationTerms);
      } catch (e) {
        console.warn('[Save Account] Could not get consent timestamp from localStorage, using current time:', e);
        acceptedRegistrationTerms = new Date().toISOString();
      }
    }
    
    const customerData = {
      email: payload.customer?.email || document.getElementById('email')?.value?.trim(),
      firstName: payload.customer?.firstName || document.getElementById('firstName')?.value?.trim(),
      lastName: payload.customer?.lastName || document.getElementById('lastName')?.value?.trim(),
      mobilePhone: mobilePhone, // API expects mobilePhone object with countryCode and number
      birthDate: birthDate, // API expects birthDate (not dateOfBirth)
      shippingAddress: shippingAddress, // API expects shippingAddress object
      address: streetAddress, // Keep for backward compatibility
      city: city, // Keep for backward compatibility
      postalCode: postalCode, // Keep for backward compatibility - ensure it's always included
      country: country, // Always DK
      primaryGym: payload.customer?.primaryGym || state.selectedBusinessUnit,
      password: payload.customer?.password || document.getElementById('password')?.value,
      customerType: 1, // Required by API
      ...(payload.consent?.marketing !== undefined && { allowMassSendEmail: payload.consent.marketing }),
      ...(payload.consent?.sms !== undefined && { allowMassSendSms: payload.consent.sms }),
      // Include registration terms acceptance timestamp (required by API)
      ...(acceptedRegistrationTerms && { acceptedRegistrationTerms }),
    };
    
    // Also include phone and phoneCountryCode for backward compatibility (remove if not needed)
    if (phoneNumber) {
      customerData.phone = phoneNumber;
      customerData.phoneCountryCode = phoneCountryCode;
    }
    
    // Also include dateOfBirth for backward compatibility
    if (birthDate) {
      customerData.dateOfBirth = birthDate;
    }
    
    // Log the payload and customerData for debugging
    console.log('[Save Account] Payload from buildCheckoutPayload:', JSON.stringify(payload, null, 2));
    console.log('[Save Account] Customer data before cleanup:', JSON.stringify(customerData, null, 2));
    
    // Remove undefined/null/empty string values (but keep 0 and false)
    // IMPORTANT: Don't remove postalCode, mobilePhone, birthDate, or shippingAddress even if they seem empty
    // as they might be objects or have nested values
    Object.keys(customerData).forEach(key => {
      const value = customerData[key];
      // Skip removal for complex objects
      if (key === 'mobilePhone' || key === 'shippingAddress') {
        // Only remove if it's null/undefined, not if it's an empty object
        if (value === null || value === undefined) {
          delete customerData[key];
        }
      } else if (value === undefined || value === null || value === '') {
        delete customerData[key];
      }
    });
    
    // Ensure postalCode is included in shippingAddress if it exists
    if (customerData.shippingAddress && postalCode && !customerData.shippingAddress.postalCode) {
      customerData.shippingAddress.postalCode = postalCode;
    }
    
    // Also ensure postalCode is at top level if shippingAddress exists
    if (customerData.shippingAddress && postalCode) {
      customerData.postalCode = postalCode;
    }
    
    console.log('[Save Account] Customer data after cleanup:', JSON.stringify(customerData, null, 2));
    
    // Pre-check: Verify email is not already in use
    const email = customerData.email?.toLowerCase().trim();
    if (email) {
      // Check if user is already logged in with this email
      const authenticatedEmail = state.authenticatedEmail || 
        (typeof window.getTokenMetadata === 'function' && window.getTokenMetadata()?.email);
      if (authenticatedEmail && authenticatedEmail.toLowerCase() === email) {
        showSaveAccountMessage('You are already logged in with this email address.', 'error');
        showToast('You are already logged in with this email.', 'error');
        return;
      }

      console.log('[Save Account] Proceeding with account creation. Duplicate detection will be handled by API; on duplicate we try login and continue.');
    }

    // /99kr defensive existing-customer pre-check. BRP doesn't always reject duplicate
    // emails on the create endpoint, so we attempt login with the typed credentials.
    // If login succeeds, we inspect the customer's product history — only block if
    // they've held a product carrying the `Første Gang` label (see FIRSTCLIMB_BLOCKING_LABELS).
    // Otherwise (abandoned-but-empty profile, never-bought-anything customer) we
    // treat them as eligible: keep the tokens, sync state, skip create-customer.
    if (isFirstClimbRoute()) {
      const candidateEmail = customerData.email;
      const candidatePassword = customerData.password;
      if (candidateEmail && candidatePassword) {
        let preCheckLoggedIn = false;
        try {
          await authAPI.login(candidateEmail, candidatePassword, { saveTokens: true });
          preCheckLoggedIn = true;
        } catch (preCheckErr) {
          // Expected for genuinely new customers. Fall through to normal create.
          console.log('[Save Account] firstclimb pre-check login failed (expected for new customer):', preCheckErr?.message || preCheckErr);
        }
        if (preCheckLoggedIn) {
          let customerIdForCheck = null;
          try {
            await syncAuthenticatedCustomerState(undefined, candidateEmail);
            customerIdForCheck = state.customerId
              || getTokenMetadata()?.username
              || getTokenMetadata()?.userName;
          } catch (syncErr) {
            console.warn('[Save Account] customer state sync failed before history check:', syncErr);
          }

          let hasBlockingHistory = true; // fail-safe default
          try {
            hasBlockingHistory = await customerHasFirstclimbBlockingHistory(customerIdForCheck);
          } catch (historyErr) {
            console.warn('[Save Account] firstclimb history check failed; blocking by default:', historyErr);
            hasBlockingHistory = true;
          }

          if (hasBlockingHistory) {
            try { window.clearTokens && window.clearTokens(); } catch (_) {}
            state.customerId = null;
            state.authenticatedCustomer = null;
            state.authenticatedEmail = null;
            try { refreshLoginUI(); } catch (_) {}
            blockFirstClimbExistingCustomer({ email: candidateEmail });
            return;
          }

          // Clean history → treat the existing-customer login as the sign-in for
          // this purchase. No create-customer POST is needed; the user is ready
          // to proceed to Til Kassen.
          try { refreshLoginUI(); } catch (_) {}
          showSaveAccountMessage('Welcome back! You can proceed to checkout.', 'success');
          showToast('Logged in successfully.', 'success');
          if (state.currentStep === 4) updatePaymentOverview();
          return;
        }
      }
    }

    console.log('[Save Account] Creating customer account...');
    const customer = await authAPI.createCustomer(customerData);
    
    // Log the full customer creation response to check for tokens
    console.log('[Save Account] Customer creation response:', JSON.stringify(customer, null, 2));
    
    // Track this email as used for account creation
    if (email) {
      state.createdEmails.add(email);
      // Also store in localStorage for persistence across sessions
      try {
        const storedEmails = JSON.parse(localStorage.getItem('boulders_created_emails') || '[]');
        if (!storedEmails.includes(email)) {
          storedEmails.push(email);
          localStorage.setItem('boulders_created_emails', JSON.stringify(storedEmails));
        }
      } catch (e) {
        console.warn('[Save Account] Could not store email in localStorage:', e);
      }
    }
    
    // Extract customer ID from response
    const customerId = customer?.data?.id || customer?.id || customer?.customerId || customer?.data?.customerId;
    
    if (customerId) {
      // Store customer ID in state
      state.customerId = customerId;
      
      let hasTokens = false;
      
      // Save tokens if provided in customer creation response
      if (customer?.accessToken && customer?.refreshToken) {
        if (typeof window.saveTokens === 'function') {
          const metadata = {
            username: customer?.username || customerData.email,
            email: customerData.email,
            roles: customer?.roles,
          };
          window.saveTokens(customer.accessToken, customer.refreshToken, undefined, metadata);
          syncAuthenticatedCustomerState(metadata.username, metadata.email);
          hasTokens = true;
          console.log('[Save Account] ✅ Tokens saved from customer creation response');
        }
      } else if (customer?.data?.accessToken && customer?.data?.refreshToken) {
        if (typeof window.saveTokens === 'function') {
          const metadata = {
            username: customer?.data?.username || customerData.email,
            email: customerData.email,
            roles: customer?.data?.roles,
          };
          window.saveTokens(customer.data.accessToken, customer.data.refreshToken, undefined, metadata);
          syncAuthenticatedCustomerState(metadata.username, metadata.email);
          hasTokens = true;
          console.log('[Save Account] ✅ Tokens saved from customer creation response (nested in data)');
        }
      }
      
      // If tokens are not provided in account creation response, automatically log in
      // Get password directly from form field to ensure we have it even if it was removed from customerData
      const password = customerData.password || document.getElementById('password')?.value;
      if (!hasTokens && email && password) {
        try {
          console.log('[Save Account] Tokens not provided, automatically logging in...');
          // Add a small delay to avoid hitting rate limits immediately after account creation
          await new Promise(resolve => setTimeout(resolve, 500));
          const loginResponse = await authAPI.login(email, password);
          const loginPayload = loginResponse?.data ?? loginResponse;
          const username = loginPayload?.username || email;
          
          // Extract and save tokens from login response
          const loginAccessToken = loginPayload?.accessToken || loginPayload?.access_token;
          const loginRefreshToken = loginPayload?.refreshToken || loginPayload?.refresh_token;
          let loginExpiresAt = loginPayload?.expiresAt || loginPayload?.expires_at;
          const loginExpiresIn = loginPayload?.expiresIn || loginPayload?.expires_in;
          if (!loginExpiresAt && loginExpiresIn) {
            const expiresInMs = Number(loginExpiresIn) * 1000;
            loginExpiresAt = Date.now() + (Number.isFinite(expiresInMs) ? expiresInMs : 0);
          }
          
          if (loginAccessToken && loginRefreshToken && typeof window.saveTokens === 'function') {
            const loginMetadata = {
              username: username,
              email: email,
              roles: loginPayload?.roles,
              tokenType: loginPayload?.tokenType || loginPayload?.token_type,
              expiresIn: loginPayload?.expiresIn || loginPayload?.expires_in,
            };
            window.saveTokens(loginAccessToken, loginRefreshToken, loginExpiresAt, loginMetadata);
            console.log('[Save Account] ✅ Tokens saved from login response');
          }
          
          // Sync customer state and fetch profile (syncAuthenticatedCustomerState will fetch and refresh UI)
          await syncAuthenticatedCustomerState(username, email);
          
          hasTokens = true; // Mark as logged in
          console.log('[Save Account] ✅ Successfully logged in after account creation');
        } catch (loginError) {
          console.warn('[Save Account] Auto-login failed:', loginError);
          
          // Handle rate limit errors specifically
          const errorMessage = loginError.message || String(loginError);
          if (errorMessage.includes('429') || errorMessage.includes('Rate limit') || errorMessage.includes('Too many requests')) {
            // Extract retryAfter if available
            let retryAfterSeconds = 60; // Default 1 minute
            const retryAfterMatch = errorMessage.match(/retryAfter["\s:]*(\d+)/i);
            if (retryAfterMatch) {
              retryAfterSeconds = parseInt(retryAfterMatch[1], 10);
            }
            const retryMinutes = Math.ceil(retryAfterSeconds / 60);
            
            console.warn(`[Save Account] Rate limit hit. Will retry login automatically in ${retryMinutes} minute(s)`);
            showSaveAccountMessage(`Account saved successfully! Login temporarily rate-limited. Please log in manually to continue, or wait ${retryMinutes} minute(s) and refresh.`, 'error');
            showToast('Account saved! Login rate-limited. Please log in manually.', 'warning');
          } else {
            // Other login errors
            console.warn('[Save Account] Auto-login failed, user will need to log in manually:', loginError);
            showSaveAccountMessage('Account saved successfully! Please log in to continue.', 'success');
            showToast('Account saved successfully!', 'success');
          }
        }
      }
      
      // Store customer data from form temporarily until profile is fetched
      if (!state.authenticatedCustomer && customerData) {
        state.authenticatedCustomer = {
          firstName: customerData.firstName,
          lastName: customerData.lastName,
          email: customerData.email,
          dateOfBirth: customerData.dateOfBirth,
          address: customerData.address,
          city: customerData.city,
          postalCode: customerData.postalCode,
          phone: customerData.phone,
          phoneCountryCode: customerData.phoneCountryCode,
        };
        console.log('[Save Account] Stored customer data from form:', state.authenticatedCustomer);
      }
      
      // Fetch customer profile if tokens are available (always fetch to get complete data)
      if (hasTokens && customerId) {
        try {
          const customerProfile = await authAPI.getCustomer(customerId);
          state.authenticatedCustomer = customerProfile;
          console.log('[Save Account] Customer profile loaded:', customerProfile);
          // Refresh UI to show all profile fields
          refreshLoginUI();
        } catch (profileError) {
          console.warn('[Save Account] Could not fetch customer profile:', profileError);
          // Keep the form data we stored above and refresh UI
          refreshLoginUI();
        }
      }
      
      // Refresh login UI
      refreshLoginUI();
      
      // Show appropriate message based on whether user is logged in
      if (hasTokens) {
        showSaveAccountMessage('Account saved successfully! You are now logged in. You can proceed to checkout.', 'success');
        showToast('Account saved and logged in successfully!', 'success');
        
        // Do not auto-create order after profile creation; wait for explicit checkout.
        if (state.currentStep === 4) {
          updatePaymentOverview();
        }
      } else {
        // /99kr: login section is hidden, so "Please log in to continue" is a dead
        // end. Auto-attempt login with the password the user just typed so they
        // can proceed straight to checkout. If that login succeeds against an
        // existing customer (rather than the freshly-created one), the surrounding
        // logic still works — the auth state ends up populated either way.
        if (isFirstClimbRoute()) {
          const autoEmail = customerData.email;
          const autoPassword = customerData.password;
          if (autoEmail && autoPassword) {
            try {
              await authAPI.login(autoEmail, autoPassword, { saveTokens: true });
              await syncAuthenticatedCustomerState(undefined, autoEmail);
              try { refreshLoginUI(); } catch (_) {}
              showSaveAccountMessage('Account saved successfully! You are now logged in. You can proceed to checkout.', 'success');
              showToast('Account saved and logged in successfully!', 'success');
              if (state.currentStep === 4) updatePaymentOverview();
            } catch (autoLoginErr) {
              console.warn('[Save Account] firstclimb auto-login after tokenless create failed:', autoLoginErr);
              showSaveAccountMessage('Account saved, but we could not sign you in automatically. Please try again or contact support.', 'error');
              showToast('Account saved, but auto-login failed.', 'error');
            }
          } else {
            showSaveAccountMessage('Account saved successfully! Please log in to continue.', 'success');
            showToast('Account saved successfully!', 'success');
          }
        } else {
          showSaveAccountMessage('Account saved successfully! Please log in to continue.', 'success');
          showToast('Account saved successfully!', 'success');
        }
      }
    } else {
      throw new Error('Customer ID not found in response');
    }
  } catch (error) {
    console.error('[Save Account] Error saving account:', error);
    
    // Handle invalid email error specifically
    if (error.isInvalidEmail) {
      showSaveAccountMessage('Please enter a valid email address.', 'error');
      showToast('Please enter a valid email address.', 'error');
      highlightFieldError('email', true);
    } else if (error.isDuplicateEmail || (error.message && error.message.includes('already exists'))) {
      const emailVal = document.getElementById('email')?.value?.trim()?.toLowerCase() || '';
      const password = customerData?.password || document.getElementById('password')?.value;
      if (emailVal && password) {
        try {
          await authAPI.login(emailVal, password, { saveTokens: true });
          await syncAuthenticatedCustomerState();
          showSaveAccountMessage('Account saved successfully! You are now logged in.', 'success');
          showToast('Account saved successfully!', 'success');
          refreshLoginUI();
        } catch (loginErr) {
          const duplicateMessage = `An account with this email address${emailVal ? ` (${emailVal})` : ''} already exists. Please log in instead.`;
          showSaveAccountMessage(duplicateMessage, 'error');
          showToast('Account already exists. Please log in.', 'error');
          const emailInput = document.getElementById('email');
          if (emailInput) emailInput.closest('.form-group')?.classList.add('error');
          switchAuthMode('login', emailVal);
        }
      } else {
        const duplicateMessage = `An account with this email address${emailVal ? ` (${emailVal})` : ''} already exists. Please log in instead.`;
        showSaveAccountMessage(duplicateMessage, 'error');
        showToast('Account already exists. Please log in.', 'error');
        const emailInput = document.getElementById('email');
        if (emailInput) emailInput.closest('.form-group')?.classList.add('error');
        if (emailVal) switchAuthMode('login', emailVal);
        else switchAuthMode('login');
      }
    } else {
      // Extract and highlight error fields
      const errorFields = extractErrorFields(error);
      errorFields.forEach(({ fieldId }) => {
        if (fieldId) {
          highlightFieldError(fieldId, true);
        }
      });
      
      const errorMessage = getErrorMessage(error, 'Account creation') || error.message || 'Failed to save account. Please try again.';
      showSaveAccountMessage(errorMessage, 'error');
      showToast(errorMessage, 'error');
    }
  } finally {
    // Reset button state
    saveBtn.disabled = false;
    saveBtn.textContent = 'Save Account';
    // Ensure checkout button is not stuck in Processing (user may have switched flows)
    state.checkoutInProgress = false;
    setCheckoutLoadingState(false);
  }
}

function showSaveAccountMessage(message, type = 'success') {
  const messageDiv = document.getElementById('saveAccountMessage');
  if (!messageDiv) return;
  
  messageDiv.textContent = message;
  messageDiv.className = `save-account-message ${type}`;
  messageDiv.style.display = 'block';
  
  // Auto-hide success messages after 5 seconds
  if (type === 'success') {
    setTimeout(() => {
      messageDiv.style.display = 'none';
    }, 5000);
  }
}

function checkSaveAccountButtonState() {
  const saveBtn = document.querySelector('[data-action="save-account"]');
  if (!saveBtn) return;
  
  // Check if we're on the create account section
  const createSection = document.querySelector('[data-auth-section="create"]');
  if (!createSection || createSection.style.display === 'none') {
    saveBtn.classList.remove('valid');
    return;
  }
  
  // Validate required fields
  const requiredFields = [
    'firstName',
    'lastName',
    'dateOfBirth',
    'streetAddress',
    'postalCode',
    'email',
    'phoneNumber',
    'password',
  ];
  
  let allFieldsFilled = true;
  requiredFields.forEach(fieldId => {
    const input = document.getElementById(fieldId);
    if (!input || !input.value.trim()) {
      allFieldsFilled = false;
    }
  });
  
  // Add 'valid' class if all fields are filled
  if (allFieldsFilled) {
    saveBtn.classList.add('valid');
  } else {
    saveBtn.classList.remove('valid');
  }
}

function showLogoutConfirmation() {
  // Create confirmation modal
  const confirmationOverlay = document.createElement('div');
  confirmationOverlay.className = 'logout-confirmation-overlay';
  confirmationOverlay.style.cssText = `
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2000;
    backdrop-filter: blur(4px);
  `;
  
  const confirmationDialog = document.createElement('div');
  confirmationDialog.className = 'logout-confirmation-dialog';
  confirmationDialog.style.cssText = `
    background: rgba(31, 41, 55, 0.95);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 16px;
    padding: 24px;
    max-width: 400px;
    width: 90%;
    text-align: center;
    color: rgba(255, 255, 255, 0.9);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
  `;
  
  // Create dialog content using DOM methods (not innerHTML) to avoid sanitization issues
  const title = document.createElement('h3');
  title.textContent = 'Log out?';
  title.style.cssText = 'margin: 0 0 16px 0; color: #FFFFFF; font-size: 18px; font-weight: 600;';
  
  const message = document.createElement('p');
  message.textContent = "Are you sure you want to log out? You'll need to log in again to continue.";
  message.style.cssText = 'margin: 0 0 24px 0; color: rgba(255, 255, 255, 0.7); line-height: 1.5; font-size: 14px;';
  
  const buttonContainer = document.createElement('div');
  buttonContainer.style.cssText = 'display: flex; gap: 12px; justify-content: center;';
  
  const cancelBtn = document.createElement('button');
  cancelBtn.className = 'logout-confirmation-btn logout-confirmation-cancel';
  cancelBtn.textContent = 'Cancel';
  cancelBtn.style.cssText = `
    padding: 10px 20px;
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 8px;
    background: transparent;
    color: rgba(255, 255, 255, 0.9);
    cursor: pointer;
    font-weight: 500;
    font-size: 14px;
    transition: all 0.2s ease;
  `;
  
  const confirmBtn = document.createElement('button');
  confirmBtn.className = 'logout-confirmation-btn logout-confirmation-confirm';
  confirmBtn.textContent = 'Log out';
  confirmBtn.style.cssText = `
    padding: 10px 20px;
    border: none;
    border-radius: 8px;
    background: rgba(239, 68, 68, 0.2);
    border: 1px solid rgba(239, 68, 68, 0.4);
    color: #F87171;
    cursor: pointer;
    font-weight: 600;
    font-size: 14px;
    transition: all 0.2s ease;
  `;
  
  buttonContainer.appendChild(cancelBtn);
  buttonContainer.appendChild(confirmBtn);
  
  confirmationDialog.appendChild(title);
  confirmationDialog.appendChild(message);
  confirmationDialog.appendChild(buttonContainer);
  
  confirmationOverlay.appendChild(confirmationDialog);
  document.body.appendChild(confirmationOverlay);
  
  // Add hover effects
  
  if (cancelBtn) {
    cancelBtn.addEventListener('mouseenter', () => {
      cancelBtn.style.background = 'rgba(255, 255, 255, 0.1)';
      cancelBtn.style.borderColor = 'rgba(255, 255, 255, 0.3)';
    });
    cancelBtn.addEventListener('mouseleave', () => {
      cancelBtn.style.background = 'transparent';
      cancelBtn.style.borderColor = 'rgba(255, 255, 255, 0.2)';
    });
  }
  
  if (confirmBtn) {
    confirmBtn.addEventListener('mouseenter', () => {
      confirmBtn.style.background = 'rgba(239, 68, 68, 0.3)';
      confirmBtn.style.borderColor = 'rgba(239, 68, 68, 0.6)';
    });
    confirmBtn.addEventListener('mouseleave', () => {
      confirmBtn.style.background = 'rgba(239, 68, 68, 0.2)';
      confirmBtn.style.borderColor = 'rgba(239, 68, 68, 0.4)';
    });
  }
  
  // Add event listeners
  cancelBtn?.addEventListener('click', () => {
    document.body.removeChild(confirmationOverlay);
  });
  
  confirmBtn?.addEventListener('click', () => {
    document.body.removeChild(confirmationOverlay);
    handleLogout();
  });
  
  // Close on overlay click
  confirmationOverlay.addEventListener('click', (e) => {
    if (e.target === confirmationOverlay) {
      document.body.removeChild(confirmationOverlay);
    }
  });
  
  // Close on Escape key
  const handleEscape = (e) => {
    if (e.key === 'Escape') {
      document.body.removeChild(confirmationOverlay);
      document.removeEventListener('keydown', handleEscape);
    }
  };
  document.addEventListener('keydown', handleEscape);
}

function handleLogout() {
  // Clear customer profile data
  state.authenticatedCustomer = null;
  state.authenticatedEmail = null;
  state.customerId = null;
  state.fullOrder = null;
  state.billingPeriod = '';
  state.subscriptionStartDate = null;
  state.discountCode = null;
  state.discountApplied = false;
  state.paymentFailed = false;
  state.paymentPending = false;
  state.paymentConfirmed = false;

  // IMPORTANT: Reset checkout/order context so a new user never reuses
  // a previous user's order/customer snapshot after logout.
  clearStoredOrderData('logout');
  state.selectedProductType = null;
  state.selectedProductId = null;
  state.membershipPlanId = null;
  state.addonIds.clear();
  state.valueCardQuantities.clear();
  state.createdEmails.clear();
  try {
    sessionStorage.removeItem('boulders_checkout_customer');
    sessionStorage.removeItem('boulders_checkout_order');
  } catch (error) {
    console.warn('[checkout] Could not clear checkout session snapshots on logout:', error);
  }
  
  if (typeof window.clearTokens === 'function') {
    window.clearTokens();
  }
  
  // Clear Sentry user context on logout
  if (window.Sentry && window.Sentry.setUser) {
    window.Sentry.setUser(null);
  }
  
  // Refresh UI to show logged out state
  refreshLoginUI();
  
  // Switch to create account mode when logging out
  switchAuthMode('create');

  // If user logs out from success/payment-return context, force app back to front flow.
  // Also strip payment return params so init logic cannot keep user on step 5.
  try {
    const cleanUrl = `${window.location.pathname}${window.location.hash || ''}`;
    window.history.replaceState({}, document.title, cleanUrl);
  } catch (error) {
    console.warn('[logout] Could not clean URL params:', error);
  }

  state.currentStep = 1;
  const stepContent = document.querySelector('.step-content');
  if (stepContent) {
    stepContent.classList.remove('success-page-active');
    stepContent.classList.remove('payment-failed-active');
    stepContent.style.flex = '';
    stepContent.style.minHeight = '';
    stepContent.style.height = '';
  }
  showStep(state.currentStep);
  updateStepIndicator();
  updateNavigationButtons();
  updateMainSubtitle();
  scrollToTop();
  
  showToast('You have been logged out.', 'info');
  
  // Dispatch event to notify React components
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('auth-state-changed'));
  }
}

function renderCatalog() {
  // API-driven catalog: renderProductsFromAPI handles memberships/value cards
  // Add-ons are rendered from subscription additions when available
  renderAddons();
}

function renderMembershipPlans() {
  if (!templates.membership || !DOM.membershipPlans) return;
  if (typeof MEMBERSHIP_PLANS === 'undefined' || !Array.isArray(MEMBERSHIP_PLANS) || MEMBERSHIP_PLANS.length === 0) {
    return;
  }
  DOM.membershipPlans.innerHTML = '';
  DOM.membershipPlans.dataset.centerInitialized = 'false';

  MEMBERSHIP_PLANS.forEach((plan) => {
    const card = templates.membership.content.firstElementChild.cloneNode(true);
    card.dataset.planId = plan.id;

    const nameEl = card.querySelector('[data-element="name"]');
    const priceValueEl = card.querySelector('[data-element="priceValue"]');
    const priceSuffixEl = card.querySelector('[data-element="priceSuffix"]');
    const descriptionEl = card.querySelector('[data-element="description"]');
    const featuresEl = card.querySelector('[data-element="features"]');
    const buttonEl = card.querySelector('[data-action="select-membership"]');

    if (nameEl) nameEl.textContent = plan.name;
    if (priceValueEl) priceValueEl.textContent = formatPriceHalfKrone(roundToHalfKrone(plan.price));
    if (priceSuffixEl) priceSuffixEl.textContent = ` ${plan.priceSuffix}`;
    if (descriptionEl) descriptionEl.textContent = plan.description;
    if (featuresEl) {
      featuresEl.innerHTML = '';
      plan.features.forEach((feature) => {
        const li = document.createElement('li');
        li.textContent = feature;
        featuresEl.appendChild(li);
      });
    }
    if (buttonEl) {
      buttonEl.dataset.planId = plan.id;
      buttonEl.textContent = plan.cta ?? 'Select Plan';
    }

    card.addEventListener('click', (event) => handleMembershipCardClick(event, card));

    DOM.membershipPlans.appendChild(card);
  });
}

function handleGymSelection(item) {
  const gymIdString = item.dataset.gymId;
  const numericId = gymIdString ? gymIdString.replace('gym-', '') : null;
  
  // Remove selected class from all gym items
  document.querySelectorAll('.gym-item').forEach(gymItem => {
    gymItem.classList.remove('selected');
  });
  
  // Add selected class to clicked item
  item.classList.add('selected');
  
  // Step 3: Store the chosen unit in client state so every later request can reference it
  // Extract numeric ID from data attribute (format: "gym-{id}")
  // Note: numericId was already extracted above for early return check
  
  // Step 4: If business unit changed, clear cached reference data to force refresh
  const previousBusinessUnit = state.selectedBusinessUnit;
  if (previousBusinessUnit !== numericId) {
    state.referenceData = {};
    state.referenceDataLoaded = false;
  }
  
  state.selectedGymId = numericId; // Store numeric ID for API requests
  state.selectedBusinessUnit = numericId; // Also store as businessUnit for clarity
  
  // Store gym name for display - try multiple selectors
  let gymNameText = null;
  const gymNameElement = item.querySelector('.gym-name');
  if (gymNameElement) {
    gymNameText = gymNameElement.textContent.trim();
  }
  
  // Fallback: try to get from gymsWithDistances if DOM doesn't have it
  if (!gymNameText && gymsWithDistances && gymsWithDistances.length > 0) {
    const selectedGym = gymsWithDistances.find(gym => 
      String(gym.id) === String(numericId)
    );
    if (selectedGym && selectedGym.name) {
      gymNameText = selectedGym.name;
    }
  }
  
  if (gymNameText && gymNameText.trim() !== '') {
    state.selectedGymName = gymNameText.trim();
    console.log('[Gym Selection] ✅ Stored gym name:', state.selectedGymName, 'for ID:', numericId);
    // Immediately update display if we're already on step 2
    if (state.currentStep === 2) {
      setTimeout(() => updateSelectedGymDisplay(), 50);
    }
  } else {
    console.warn('[Gym Selection] ❌ Could not find gym name. Item:', item);
    console.warn('[Gym Selection] gymNameElement found:', !!gymNameElement);
    console.warn('[Gym Selection] gymsWithDistances length:', gymsWithDistances?.length || 0);
    if (gymsWithDistances && gymsWithDistances.length > 0) {
      console.warn('[Gym Selection] First 3 gyms:', gymsWithDistances.slice(0, 3).map(g => ({ id: String(g.id), name: g.name })));
    }
  }
  
  // Step 4: Load reference data after business unit selection
  // Cache responses in client state and refresh when business unit changes
  if (numericId) {
    loadReferenceData();
  }
  
  // Step 5: Pre-load products when business unit is selected (for faster step 2 loading)
  if (numericId) {
    loadProductsFromAPI();
  }
  
  // Update heads-up display
  updateGymHeadsUp(item);
  
  // Update selected gym display if we're on step 2
  if (state.currentStep === 2) {
    updateSelectedGymDisplay();
  }
  
  // Clear any pending gym navigation timeout to prevent double-clicks
  if (pendingNavigationTimeouts.gym) {
    clearTimeout(pendingNavigationTimeouts.gym);
    pendingNavigationTimeouts.gym = null;
  }
  
  // Only auto-advance if we're on step 1 (gym selection step)
  if (state.currentStep !== 1) {
    return;
  }
  
  // Auto-advance to next step after a short delay
  const timeoutGymId = numericId; // Capture gym ID at timeout creation to prevent stale navigation
  pendingNavigationTimeouts.gym = setTimeout(() => {
    // Read current state ONCE at timeout execution time (atomic read to prevent race conditions)
    const currentStepNow = state.currentStep;
    const selectedGymIdNow = state.selectedGymId;
    // Check if timeout is still valid BEFORE clearing it
    // If prevStep() cleared it, pendingNavigationTimeouts.gym will be null
    const timeoutStillValid = pendingNavigationTimeouts.gym !== null;
    
    // Clear timeout reference immediately to prevent double execution
    pendingNavigationTimeouts.gym = null;
    
    // Only navigate if ALL conditions are met:
    // 1. We're still on step 1 (where gym selection happens)
    // 2. The gym selection hasn't changed (same gym ID)
    // 3. The timeout wasn't cleared (still valid)
    if (timeoutStillValid && currentStepNow === 1 && state.currentStep === 1 && selectedGymIdNow === timeoutGymId) {
      // Final verification: ensure we're still on step 1 and haven't navigated away
      if (state.currentStep !== 1) {
        return; // State changed between check and navigation - abort
      }
      // Navigate to step 2 (next step after gym selection)
      nextStep(1);
    }
  }, 500);
}

// Update gym heads-up display
function updateGymHeadsUp(selectedItem) {
  const headsUp = document.getElementById('gymHeadsUp');
  const gymName = document.getElementById('selectedGymName');
  
  if (selectedItem && headsUp && gymName) {
    const gymNameText = selectedItem.querySelector('.gym-name').textContent;
    gymName.textContent = gymNameText;
    
    // Show heads-up with animation
    headsUp.classList.add('show');
    
    // Auto-hide after 3 seconds
    setTimeout(() => {
      headsUp.classList.remove('show');
    }, 3000);
  }
}

// Update access type heads-up display
function updateAccessHeadsUp(selectedCard) {
  const headsUp = document.getElementById('accessHeadsUp');
  const accessName = document.getElementById('selectedAccessName');
  
  if (selectedCard && headsUp && accessName) {
    const planType = selectedCard.querySelector('.plan-type').textContent;
    const category = selectedCard.closest('.category-item').dataset.category;
    
    // Format the display name based on category and plan type
    let displayName = planType;
    if (category === 'punchcard') {
      displayName = `${planType} Punch Card`;
    } else if (category === 'campaign') {
      displayName = `${planType} Campaign`;
    } else if (category === 'membership') {
      displayName = `${planType} Membership`;
    } else if (category === '15daypass') {
      displayName = `${planType} 15-Day Trial Pass`;
    }
    
    accessName.textContent = displayName;
    
    // Show heads-up with animation
    headsUp.classList.add('show');
    
    // Auto-hide after 3 seconds
    setTimeout(() => {
      headsUp.classList.remove('show');
    }, 3000);
  }
}

// Sync punch card quantity UI
function syncPunchCardQuantityUI(card, planId) {
  const quantity = state.valueCardQuantities.get(planId) || 1;
  const pricePerUnit = parseInt(card.dataset.price) || 1200;
  const total = pricePerUnit * quantity;
  
  // Quantity panel is INSIDE the plan-card (child), not a sibling
  const panel = card.querySelector('.quantity-panel');
  if (!panel) return;
  
  const quantityValue = panel.querySelector('.quantity-value') || panel.querySelector('[data-element="quantityValue"]');
  const quantityTotal = panel.querySelector('[data-element="quantityTotal"]');
  const decrementBtn = panel.querySelector('[data-action="decrement-quantity"]');
  const incrementBtn = panel.querySelector('[data-action="increment-quantity"]');
  
  if (quantityValue) quantityValue.textContent = quantity;
  if (quantityTotal) quantityTotal.textContent = `${total.toLocaleString('da-DK')} kr`;
  
  // Disable buttons based on min/max
  if (decrementBtn) decrementBtn.disabled = quantity <= 1;
  if (incrementBtn) incrementBtn.disabled = quantity >= 5;
  
  // Update cart and value card summary when quantity changes
  updateCartSummary();
  updateValueCardSummary();
}

// Scroll to top function with multiple approaches
function scrollToTop() {
  // Method 1: Direct scroll to top
  document.documentElement.scrollTop = 0;
  document.body.scrollTop = 0;
  
  // Method 2: Smooth scroll with requestAnimationFrame
  requestAnimationFrame(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });
  });
  
  // Method 3: Force scroll after a brief delay
  setTimeout(() => {
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, 50);
}


// Setup form field focus scrolling for mobile
function setupFormFieldScrolling() {
  // Only on mobile
  if (window.innerWidth > 768) return;
  
  const formInputs = document.querySelectorAll('input, select, textarea');
  
  formInputs.forEach((input) => {
    input.addEventListener('focus', function() {
      // Auto-scroll removed - will be revisited later
    });
  });
}

function setupFormFieldFocus() {
  // Ensure form inputs get focused when clicked
  const formInputs = document.querySelectorAll('input, select, textarea');
  
  formInputs.forEach((input) => {
    // Ensure focus on click
    input.addEventListener('click', function(e) {
      // Only focus if not already focused
      if (document.activeElement !== this) {
        this.focus();
      }
    });
    
    // Also handle label clicks - labels with 'for' attribute should work automatically
    // Handle clicking on labels to focus the associated input
    const label = input.closest('.form-group')?.querySelector(`label[for="${input.id}"]`);
    if (label) {
      label.addEventListener('click', function(e) {
        e.preventDefault();
        input.focus();
      });
    }
  });
}

function handleGymSearch(event) {
  const searchTerm = event.target.value.toLowerCase();
  const gymItems = document.querySelectorAll('.gym-item');
  const noResults = document.getElementById('noResults');
  let visibleCount = 0;
  
  gymItems.forEach(item => {
    const gymName = item.querySelector('.gym-name').textContent.toLowerCase();
    const gymAddress = item.querySelector('.gym-address').textContent.toLowerCase();
    
    if (gymName.includes(searchTerm) || gymAddress.includes(searchTerm)) {
      item.classList.remove('hidden');
      visibleCount++;
    } else {
      item.classList.add('hidden');
    }
  });
  
  // Show/hide no results message
  if (visibleCount === 0 && searchTerm.length > 0) {
    noResults.classList.remove('hidden');
  } else {
    noResults.classList.add('hidden');
  }
}

function handleBackToGym() {
  // Clear any pending navigation timeouts when going back
  Object.keys(pendingNavigationTimeouts).forEach(key => {
    if (pendingNavigationTimeouts[key]) {
      clearTimeout(pendingNavigationTimeouts[key]);
      pendingNavigationTimeouts[key] = null;
    }
  });
  
  // Go back to step 1 (gym selection)
  state.currentStep = 1;
  showStep(state.currentStep);
  updateStepIndicator();
  updateNavigationButtons();
  updateMainSubtitle();
  
  // Scroll to top immediately and with delay
  scrollToTop();
  setTimeout(() => {
    scrollToTop();
  }, 200);
  
  // Restore previously selected gym if any
  if (state.selectedGymId) {
    // Convert numeric ID back to data attribute format (gym-{id})
    const gymDataId = `gym-${state.selectedGymId}`;
    const selectedGymItem = document.querySelector(`[data-gym-id="${gymDataId}"]`);
    if (selectedGymItem) {
      // Remove selected class from all items first
      document.querySelectorAll('.gym-item').forEach(item => {
        item.classList.remove('selected');
      });
      // Add selected class to previously selected item
      selectedGymItem.classList.add('selected');
      
      // Show heads-up for previously selected gym
      updateGymHeadsUp(selectedGymItem);
    }
  }
}

function handleMembershipCardClick(event, card) {
  const button = card.querySelector('[data-action="select-membership"]');
  if (!button) return;

  centerPlanCard(card);

  if (event.target.closest('[data-action="select-membership"]')) return;

  if (card.classList.contains('selected')) return;

  triggerPlanButtonGlare(button);
}

function triggerPlanButtonGlare(button) {
  const existingTimeout = buttonGlareTimeouts.get(button);
  if (existingTimeout) {
    clearTimeout(existingTimeout);
  }

  button.classList.add('attention-glare');

  const timeoutId = window.setTimeout(() => {
    button.classList.remove('attention-glare');
    buttonGlareTimeouts.delete(button);
  }, 900);

  buttonGlareTimeouts.set(button, timeoutId);
}

function renderValueCards() {
  if (!templates.valueCard || !DOM.valuePlans) return;
  if (typeof VALUE_CARDS === 'undefined' || !Array.isArray(VALUE_CARDS) || VALUE_CARDS.length === 0) {
    return;
  }
  DOM.valuePlans.innerHTML = '';
  DOM.valuePlans.dataset.centerInitialized = 'false';

  VALUE_CARDS.forEach((plan) => {
    const card = templates.valueCard.content.firstElementChild.cloneNode(true);
    card.dataset.planId = plan.id;

    const nameEl = card.querySelector('[data-element="name"]');
    const priceValueEl = card.querySelector('[data-element="priceValue"]');
    const priceSuffixEl = card.querySelector('[data-element="priceSuffix"]');
    const descriptionEl = card.querySelector('[data-element="description"]');
    const featuresEl = card.querySelector('[data-element="features"]');
    const quantityValueEl = card.querySelector('[data-element="quantityValue"]');
    const quantityTotalEl = card.querySelector('[data-element="quantityTotal"]');
    const selector = card.querySelector('.quantity-selector');
    const decrementBtn = card.querySelector('[data-action="decrement-quantity"]');
    const incrementBtn = card.querySelector('[data-action="increment-quantity"]');

    if (nameEl) nameEl.textContent = plan.name;
    if (priceValueEl) priceValueEl.textContent = formatPriceHalfKrone(roundToHalfKrone(plan.price));
    if (priceSuffixEl) priceSuffixEl.textContent = ` ${plan.priceSuffix}`;
    if (descriptionEl) descriptionEl.textContent = plan.description;
    if (featuresEl) {
      featuresEl.innerHTML = '';
      plan.features.forEach((feature) => {
        const li = document.createElement('li');
        li.textContent = feature;
        featuresEl.appendChild(li);
      });
    }

    if (quantityValueEl) quantityValueEl.textContent = plan.min;
    if (quantityTotalEl) quantityTotalEl.textContent = formatPriceHalfKrone(roundToHalfKrone(plan.min * plan.price));

    if (selector) {
      selector.dataset.planId = plan.id;
      selector.dataset.basePrice = String(plan.price);
      selector.dataset.min = String(plan.min ?? 0);
      selector.dataset.max = String(Number.isFinite(plan.max) ? plan.max : '');
      selector.dataset.current = String(plan.min ?? 0);
    }

    if (decrementBtn) decrementBtn.dataset.planId = plan.id;
    if (incrementBtn) incrementBtn.dataset.planId = plan.id;

    card.addEventListener('click', () => centerPlanCard(card));

    DOM.valuePlans.appendChild(card);

    syncValueCardUI(plan.id);
  });

  enforceValueCardAvailability();
  updateValueCardSummary();
}

function renderAddons() {
  if (!templates.addon || !DOM.addonPlans) return;
  DOM.addonPlans.innerHTML = '';
  DOM.addonPlans.dataset.centerInitialized = 'false';
  const addons = Array.isArray(state.subscriptionAdditions)
    ? state.subscriptionAdditions
    : [];
  if (addons.length === 0) {
    updateAddonSkipButton();
    return;
  }

  addons.forEach((addon, index) => {
    const card = templates.addon.content.firstElementChild.cloneNode(true);
    card.dataset.planId = addon.id;
    const thumbWrapper = card.querySelector('.addon-card-thumb') || card.querySelector('[data-action="open-lightbox"]');

    const nameEl = card.querySelector('[data-element="name"]');
    const originalPriceEl = card.querySelector('[data-element="originalPrice"]');
    const discountedPriceEl = card.querySelector('[data-element="discountedPrice"]');
    const descriptionEl = card.querySelector('[data-element="description"]');
    const featuresEl = card.querySelector('[data-element="features"]');
    const buttonEl = card.querySelector('[data-action="toggle-addon"]');
    const imageEl = card.querySelector('[data-element="image"]');

    const addonData = addon.addition || addon;
    if (nameEl) nameEl.textContent = addonData.name || addon.name || '';
    // Prefer explicit original/list price if provided; otherwise derive from discount percent.
    const priceOriginal = getAddonOriginalPrice(addon);
    // Use helper function for current price
    const priceDiscounted = getAddonPrice(addon);
    if (originalPriceEl) {
      if (typeof priceOriginal === 'number' && priceOriginal > priceDiscounted) {
        originalPriceEl.textContent = formatPriceHalfKrone(roundToHalfKrone(priceOriginal));
        originalPriceEl.style.display = '';
      } else {
        originalPriceEl.style.display = 'none';
      }
    }
    if (discountedPriceEl) {
      discountedPriceEl.textContent = priceDiscounted !== null
        ? formatPriceHalfKrone(roundToHalfKrone(priceDiscounted))
        : '—';
    }
    if (descriptionEl) {
      const description = addonData.description || addon.description || '';
      if (description) {
        // Preserve line breaks from backend - escape HTML and preserve newlines
        const descriptionHtml = description
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/\n/g, '<br>');
        descriptionEl.innerHTML = sanitizeHTML(descriptionHtml);
      } else {
        descriptionEl.textContent = '';
      }
    }
    const descToggleAddonsStep = card.querySelector('.addon-card-description-toggle');
    if (descToggleAddonsStep) {
      const hasDesc = (addonData.description || addon.description || '').trim().length > 0;
      descToggleAddonsStep.style.display = hasDesc ? '' : 'none';
      if (hasDesc) {
        const descId = 'addon-desc-' + (addon.id || index);
        if (descriptionEl) descriptionEl.id = descId;
        descToggleAddonsStep.setAttribute('aria-controls', descId);
        descToggleAddonsStep.setAttribute('aria-label', t('addons.modal.showDescription') || 'Show full description');
        descToggleAddonsStep.title = t('addons.modal.showDescription') || 'Show full description';
      }
      descToggleAddonsStep.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const expanded = card.classList.toggle('expanded');
        descToggleAddonsStep.setAttribute('aria-expanded', String(expanded));
        descToggleAddonsStep.setAttribute('aria-label', expanded ? (t('addons.modal.collapseDescription') || 'Collapse description') : (t('addons.modal.showDescription') || 'Show full description'));
        descToggleAddonsStep.title = expanded ? (t('addons.modal.collapseDescription') || 'Collapse description') : (t('addons.modal.showDescription') || 'Show full description');
      });
    }
    if (featuresEl && Array.isArray(addon.features)) {
      featuresEl.innerHTML = '';
      addon.features.forEach((feature) => {
        const li = document.createElement('li');
        li.textContent = feature;
        featuresEl.appendChild(li);
      });
    }
    if (buttonEl) {
      buttonEl.dataset.addonId = addon.id;
      buttonEl.textContent = addon.cta ?? 'Select Add-on';
    }
    if (thumbWrapper && imageEl && imageEl.src) {
      thumbWrapper.addEventListener('click', (e) => {
        e.stopPropagation();
        openAddonLightbox(imageEl.src, imageEl);
      });
    }

    const mainRow = card.querySelector('.addon-card-main');
    (mainRow || card).addEventListener('click', (e) => {
      if (buttonEl && (e.target === buttonEl || buttonEl.contains(e.target))) return;
      if (thumbWrapper && (e.target === thumbWrapper || thumbWrapper.contains(e.target))) return;
      const descToggleEl = card.querySelector('.addon-card-description-toggle');
      if (descToggleEl && (e.target === descToggleEl || descToggleEl.contains(e.target))) return;
      centerPlanCard(card);
    });

    DOM.addonPlans.appendChild(card);
  });

  updateAddonSkipButton();
}

function handleCategoryToggle(category) {
  // Update category item states
  const categoryItems = document.querySelectorAll('.category-item');
  categoryItems.forEach(item => {
    const isSelected = item.dataset.category === category;
    item.classList.toggle('selected', isSelected);
    
    // Only toggle expanded if this is the selected category
    if (isSelected) {
      const wasExpanded = item.classList.contains('expanded');
      item.classList.toggle('expanded');
      
      // Focus the expanded category item
      if (!wasExpanded && item.classList.contains('expanded')) {
        setTimeout(() => {
          item.setAttribute('tabindex', '-1');
          item.focus();
          item.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
      }
    } else {
      item.classList.remove('expanded');
    }
  });

  // Update plan sections visibility based on expanded state
  const singlePlans = document.getElementById('singleChoiceMode');
  const quantityPlans = document.getElementById('quantityMode');
  
  const singleCategory = document.querySelector('.category-item[data-category="single"]');
  const quantityCategory = document.querySelector('.category-item[data-category="quantity"]');
  
  if (singleCategory && singleCategory.classList.contains('expanded')) {
    if (singlePlans) singlePlans.style.display = 'block';
    if (quantityPlans) quantityPlans.style.display = 'none';
  } else if (quantityCategory && quantityCategory.classList.contains('expanded')) {
    if (singlePlans) singlePlans.style.display = 'none';
    if (quantityPlans) quantityPlans.style.display = 'block';
  } else {
    // If no category is expanded, hide both
    if (singlePlans) singlePlans.style.display = 'none';
    if (quantityPlans) quantityPlans.style.display = 'none';
  }
}

function getProductPriceDKK(product) {
  if (!product) return 0;
  if (product.price?.amount) return product.price.amount / 100;
  if (product.amount) return product.amount / 100;
  if (product.priceWithInterval?.price?.amount) return product.priceWithInterval.price.amount / 100;
  if (typeof product.price === 'number') return product.price;
  return 0;
}

function parsePriceFromCard(card) {
  const priceEl = card?.querySelector('.price-amount');
  if (!priceEl) return 0;
  const text = priceEl.textContent || '';
  const normalized = text.replace(/[^\d,.-]/g, '').replace(/\./g, '').replace(',', '.');
  const value = parseFloat(normalized);
  return Number.isFinite(value) ? value : 0;
}

function trackSelectItemEvent({ product, productId, category, type, card }) {
  if (!window.GTM || !window.GTM.trackSelectItem) return;

  const nameFromCard = card?.querySelector('.plan-type')?.textContent?.trim();
  const productData = {
    id: productId,
    name: product?.name || nameFromCard || 'Unknown Product',
    amount: product ? getProductPriceDKK(product) : parsePriceFromCard(card),
    type: type || 'membership',
    quantity: 1
  };

  try {
    window.GTM.trackSelectItem(productData, category, category);
  } catch (error) {
    console.warn('[GTM] Error tracking select_item:', error);
  }
}

/**
 * Updates the 15-day pass activation date section visibility and state.
 * Shows when a 15-day pass plan is selected; hides otherwise.
 */
function isActivationDateModalOpen() {
  const section = document.getElementById('activationDateSection');
  return !!section && !section.hidden && section.classList.contains('activation-date-modal-open');
}

function isCheckoutConfirmModalOpen() {
  const modal = document.getElementById('checkoutConfirmModal');
  return !!modal && !modal.hidden && modal.classList.contains('checkout-confirm-open');
}

function stopActivationDatePeriodHintAutoRefresh() {
  if (activationDatePeriodHintTimeoutId !== null) {
    clearTimeout(activationDatePeriodHintTimeoutId);
    activationDatePeriodHintTimeoutId = null;
  }
}

function scheduleActivationDatePeriodHintAutoRefresh() {
  stopActivationDatePeriodHintAutoRefresh();
  if (!isActivationDateModalOpen()) return;

  const now = Date.now();
  const msUntilNextMinuteTick = (60 * 1000) - (now % (60 * 1000)) + 50;
  activationDatePeriodHintTimeoutId = setTimeout(() => {
    activationDatePeriodHintTimeoutId = null;
    if (!isActivationDateModalOpen()) return;
    updateActivationDatePeriodHint();
    scheduleActivationDatePeriodHintAutoRefresh();
  }, msUntilNextMinuteTick);
}

function updateActivationDatePeriodHint() {
  const periodEl = document.getElementById('activationDatePeriod');
  if (!periodEl) return;

  const dateInput = document.getElementById('activationDateInput');
  const pickRadio = document.getElementById('activationDatePick');
  const todayStr = getTodayLocalDateString();
  const startStr = (pickRadio?.checked && dateInput?.value) ? dateInput.value : todayStr;
  const normalizedStart = startStr < todayStr ? todayStr : startStr;

  const startDate = new Date(`${normalizedStart}T12:00:00`);
  if (isNaN(startDate.getTime())) {
    const invalidText = t('activationDate.lastDay').replaceAll('{date}', '—');
    if (periodEl.textContent !== invalidText) {
      periodEl.textContent = invalidText;
    }
    return;
  }

  const validUntil = new Date(startDate);
  validUntil.setDate(validUntil.getDate() + 14);
  const nextText = t('activationDate.lastDay').replaceAll('{date}', formatDateDMY(validUntil));
  if (periodEl.textContent !== nextText) {
    periodEl.textContent = nextText;
  }
}

function closeCheckoutConfirmModal({ restoreFocus } = { restoreFocus: true }) {
  const modal = document.getElementById('checkoutConfirmModal');
  if (modal) {
    modal.hidden = true;
    modal.setAttribute('aria-hidden', 'true');
    modal.classList.remove('checkout-confirm-open');
  }
  document.body.classList.remove('modal-open');

  if (restoreFocus && state.checkoutConfirmPreviousFocus && typeof state.checkoutConfirmPreviousFocus.focus === 'function') {
    setTimeout(() => {
      try {
        state.checkoutConfirmPreviousFocus.focus();
      } catch (e) {}
    }, 0);
  }
  state.checkoutConfirmPreviousFocus = null;
}

function openCheckoutConfirmModal() {
  const modal = document.getElementById('checkoutConfirmModal');
  if (!modal) return;

  // Only relevant for 15-day pass style products
  const is15DayPassSelected =
    document.querySelector('[data-category="15daypass"] .plan-card.selected') ||
    document.querySelector('[data-category="freetrial"] .plan-card.selected') ||
    (state.selectedProductId && (state.dayPassSubscriptions || []).some(p => String(p.id) === String(state.selectedProductId)));
  if (!is15DayPassSelected) return;
  
  const isFreeTrialSelected = Boolean(document.querySelector('[data-category="freetrial"] .plan-card.selected')) ||
    state.landingRouteConfig?.componentName === 'LandingFreeTrial';
  const payNowAmount = Number(state?.totals?.payNowAmount ?? state?.totals?.cartTotal ?? NaN);
  const isZeroTotal = Number.isFinite(payNowAmount) && Math.abs(payNowAmount) < 0.0001;
  const isFreeFlow = isFreeTrialSelected || isZeroTotal;

  const subtitleEl = modal.querySelector('.checkout-confirm-subtitle');
  if (subtitleEl) {
    if (isFreeFlow) {
      subtitleEl.setAttribute('data-i18n-key', 'activationConfirm.subtitle.freetrial');
      subtitleEl.textContent = t('activationConfirm.subtitle.freetrial');
    } else {
      subtitleEl.setAttribute('data-i18n-key', 'activationConfirm.subtitle');
      subtitleEl.textContent = t('activationConfirm.subtitle');
    }
  }

  const continueBtn = modal.querySelector('[data-action="checkout-confirm-continue"]');
  if (continueBtn) {
    if (isFreeFlow) {
      continueBtn.setAttribute('data-i18n-key', 'activationConfirm.continue.freetrial');
      continueBtn.textContent = t('activationConfirm.continue.freetrial');
    } else {
      continueBtn.setAttribute('data-i18n-key', 'activationConfirm.continue');
      continueBtn.textContent = t('activationConfirm.continue');
    }
  }

  state.checkoutConfirmAccepted = false;
  state.checkoutConfirmPreviousFocus = document.activeElement;

  // Compute displayed start/end dates (15-day pass)
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const startDate = state.subscriptionStartDate
    ? new Date(state.subscriptionStartDate + 'T12:00:00')
    : today;
  startDate.setHours(0, 0, 0, 0);
  const endDate = new Date(startDate);
  endDate.setDate(endDate.getDate() + 14);

  const startValueEl = document.getElementById('checkoutConfirmStartValue');
  const endValueEl = document.getElementById('checkoutConfirmEndValue');
  if (startValueEl) {
    const startIsToday = !state.subscriptionStartDate || startDate.getTime() === today.getTime();
    startValueEl.textContent = startIsToday ? (t('activationConfirm.today') || 'Today') : formatDateDMY(startDate);
  }
  if (endValueEl) {
    endValueEl.textContent = formatDateDMY(endDate);
  }

  modal.hidden = false;
  modal.setAttribute('aria-hidden', 'false');
  modal.classList.add('checkout-confirm-open');
  document.body.classList.add('modal-open');

  // Focus primary action
  const primary = modal.querySelector('[data-action="checkout-confirm-continue"]');
  if (primary) {
    setTimeout(() => primary.focus(), 50);
  }
}

function closeActivationDateModal({ restoreFocus } = { restoreFocus: true }) {
  stopActivationDatePeriodHintAutoRefresh();
  const section = document.getElementById('activationDateSection');
  const pickerWrap = document.getElementById('activationDatePickerWrap');
  if (section) {
    section.hidden = true;
    section.setAttribute('aria-hidden', 'true');
    section.classList.remove('activation-date-modal-open');
  }
  if (pickerWrap) pickerWrap.hidden = true;
  document.body.classList.remove('modal-open');

  if (restoreFocus && state.activationDateModalPreviousFocus && typeof state.activationDateModalPreviousFocus.focus === 'function') {
    setTimeout(() => {
      try {
        state.activationDateModalPreviousFocus.focus();
      } catch (e) {}
    }, 0);
  }
  state.activationDateModalPreviousFocus = null;
}

function openActivationDateModal() {
  const section = document.getElementById('activationDateSection');
  if (!section) return;

  state.activationDateModalPreviousFocus = document.activeElement;
  section.hidden = false;
  section.setAttribute('aria-hidden', 'false');
  section.classList.add('activation-date-modal-open');
  document.body.classList.add('modal-open');
  updateActivationDatePeriodHint();
  scheduleActivationDatePeriodHintAutoRefresh();

  // Focus first option for accessibility
  const firstRadio = section.querySelector('input[name="activationDate"]');
  if (firstRadio) {
    setTimeout(() => firstRadio.focus(), 50);
  }
}

async function applyActivationDateToExistingOrder() {
  const orderId = state.orderId || state.fullOrder?.id;
  const productId = state.selectedProductId || state.fullOrder?.subscriptionItems?.[0]?.product?.id;
  if (!orderId || !productId) return false;

  // If an existing subscription item exists, delete it before re-adding with new start date
  const subscriptionItemId = state.subscriptionItemId || state.fullOrder?.subscriptionItems?.[0]?.id;
  if (subscriptionItemId) {
    const deleted = await orderAPI.deleteSubscriptionItem(orderId, subscriptionItemId);
    if (!deleted) {
      // If backend refuses the mutation (often due to auth/order lock), fall back to recreating order on next checkout.
      console.warn('[Activation Date] Backend refused subscription item delete; clearing order state so checkout can recreate with new start date.', {
        orderId,
        subscriptionItemId,
      });
      state.order = null;
      state.fullOrder = null;
      state.orderId = null;
      state.subscriptionAttachedOrderId = null;
      state.subscriptionItemId = null;
      state.paymentLink = null;
      state.paymentLinkGenerated = false;
      state.checkoutInProgress = false;
      state.billingPeriod = '';
      state.discountApplied = false;
      state.totals.discountAmount = 0;
      updateCartSummary();
      updatePaymentOverview();
      return false;
    }
    state.subscriptionAttachedOrderId = null;
    state.subscriptionItemId = null;
  }

  const updatedOrder = await orderAPI.addSubscriptionItem(orderId, productId, state.subscriptionStartDate);
  state.fullOrder = updatedOrder;

  const newSubscriptionItem = updatedOrder?.subscriptionItems?.[0];
  if (newSubscriptionItem?.id) {
    state.subscriptionItemId = newSubscriptionItem.id;
  }

  updateCartSummary();
  updatePaymentOverview();
  return true;
}

function updateActivationDateSection(category) {
  const section = document.getElementById('activationDateSection');
  const pickerWrap = document.getElementById('activationDatePickerWrap');
  const dateInput = document.getElementById('activationDateInput');
  const nowRadio = document.getElementById('activationDateNow');

  if (!section || !pickerWrap || !dateInput) return;

  const is15DayPass = category === '15daypass' || category === 'freetrial';
  const dayPassCategoryItem = document.querySelector('[data-category="15daypass"]');
  const freeTrialCategoryItem = document.querySelector('[data-category="freetrial"]');
  const hasSelectedPlan = dayPassCategoryItem?.querySelector('.plan-card.selected') || freeTrialCategoryItem?.querySelector('.plan-card.selected');

  if (is15DayPass && hasSelectedPlan) {
    if (!isActivationDateModalOpen()) {
      openActivationDateModal();
    }

    dateInput.min = getTodayLocalDateString();
    if (nowRadio?.checked) {
      state.subscriptionStartDate = null;
      pickerWrap.hidden = true;
    } else {
      pickerWrap.hidden = false;
      if (dateInput.value) {
        state.subscriptionStartDate = dateInput.value;
      } else {
        dateInput.value = getTodayLocalDateString();
        state.subscriptionStartDate = dateInput.value;
      }
    }
    updateActivationDatePeriodHint();
  } else {
    closeActivationDateModal({ restoreFocus: false });
    state.subscriptionStartDate = null;
    if (pickerWrap) pickerWrap.hidden = true;
  }
}

async function handlePlanSelection(selectedCard) {
  // Remove selected class from all plan cards in the same category
  const category = selectedCard.closest('.category-item').dataset.category;
  const allCardsInCategory = selectedCard.closest('.category-item').querySelectorAll('.plan-card');
  
  allCardsInCategory.forEach(card => {
    card.classList.remove('selected');
  });
  
  // Add selected class to clicked card
  selectedCard.classList.add('selected');
  
  // Brief delay to let user see the selection before modal opens
  // This provides visual feedback that the plan was selected
  
  // Step 5: Store the selected plan and product details
  const planId = selectedCard.dataset.plan;
  const productId = selectedCard.dataset.productId || planId; // Use API product ID if available
  
  // Determine if this is a value card (punch card) by checking if planId starts with "punch-"
  // Value cards can appear in Campaign category too, so we check the planId format, not just category
  const isValueCard = typeof planId === 'string' && planId.startsWith('punch-');
  const isMembership = !isValueCard && (category === 'campaign' || category === 'membership' || category === '15daypass' || category === 'freetrial');
  if (category === 'campaign' && isMembership) {
    await refreshAuthenticatedCustomerForCampaignGuard();
  }
  if (category === 'campaign' && isMembership && customerHasMembershipCampaignBlockRequirement()) {
    showCampaignRejectionModal();
    selectedCard.classList.remove('selected');
    state.membershipPlanId = null;
    state.selectedProductId = null;
    state.selectedProductType = null;
    updateCartSummary();
    updateCheckoutButton();
    return;
  }
  
  const previousProductId = state.selectedProductId;
  const previousPlanId = state.membershipPlanId;
  state.membershipPlanId = planId; // Keep for backward compatibility
  state.selectedProductId = productId; // Store API product ID
  state.selectedProductType = isMembership ? 'membership' : 'punch-card';
  if ((previousProductId && String(previousProductId) !== String(productId)) || (previousPlanId && previousPlanId !== planId)) {
    resetOrderStateForProductChange('plan-selection');
  }
  
  console.log('Selected plan:', planId, 'Product ID:', productId, 'Type:', state.selectedProductType);
  
  // Find the product object to check for Boost label
  let product = null;
  const productIdNum = typeof productId === 'string' ? parseInt(productId) : productId;
  
  if (isMembership) {
    // Check all subscription types
    const allSubscriptions = [
      ...(state.campaignSubscriptions || []),
      ...(state.subscriptions || []),
      ...(state.dayPassSubscriptions || [])
    ];
    product = allSubscriptions.find(p => 
      p.id === productId || 
      p.id === productIdNum ||
      String(p.id) === String(productId)
    );
  } else {
    // Check value cards - include both regular and campaign value cards
    const allValueCards = [
      ...(state.valueCards || []),
      ...(state.campaignValueCards || [])
    ];
    product = allValueCards.find(p => 
      p.id === productId || 
      p.id === productIdNum ||
      String(p.id) === String(productId)
    );
  }
  
  // GTM: Track select_item event
  trackSelectItemEvent({
    product,
    productId,
    category,
    type: isMembership ? 'membership' : 'punch-card',
    card: selectedCard
  });
  
  // Step 5: If membership is selected, fetch add-ons immediately
  if (isMembership && productId) {
    loadSubscriptionAdditions(productId);
  } else {
    // Clear add-ons if punch card is selected
    state.subscriptionAdditions = [];
    state.selectedAddonIds = [];
  }
  
  // Handle value cards (punch cards) - initialize quantity and show quantity panel
  // Value cards can appear in both 'punchcard' and 'campaign' categories
  if (!isMembership && typeof planId === 'string' && planId.startsWith('punch-')) {
    // Initialize quantity to 1 for this specific punch card type
    if (!state.valueCardQuantities.has(planId)) {
      state.valueCardQuantities.set(planId, 1);
    }
    
    // Show quantity panel (inside plan-card-actions)
    selectedCard.classList.add('has-quantity');
    const panel = selectedCard.querySelector('.quantity-panel');
    if (panel) {
      panel.classList.add('show');
      panel.style.display = 'flex';
      const continueBtn = panel.querySelector('.continue-btn');
      if (continueBtn) continueBtn.style.display = '';
      syncPunchCardQuantityUI(selectedCard, planId);
    }
  }
  
  // Update access heads-up display
  updateAccessHeadsUp(selectedCard);

  // Reevaluate Boost visibility based on plan type
  applyConditionalSteps();
  
  // Update cart to reflect selection
  updateCartSummary();
  
  // Check if product has Boost label - if so, show modal instead of auto-advancing
  if (product && hasBoostLabel(product)) {
    loadBoostProducts(product).then(() => {
      // Delay to let user see the selection
      setTimeout(() => {
        showBoostModal({ productsPreloaded: true });
      }, BOOST_MODAL_OPEN_DELAY_MS);
    });
  } else {
    // Auto-advance to next step after a short delay
    setTimeout(() => {
      nextStep();
    }, 500);
  }
}

function setupNewAccessStep() {
  const categoryItems = document.querySelectorAll('.category-item');
  const footerText = document.getElementById('footerText');
  
  if (categoryItems.length === 0) {
    console.warn('No category items found - setupNewAccessStep called too early or categories not in DOM');
    return;
  }
  
  const footerTexts = {
    campaign: 'Special promotional offers and limited-time campaigns. Take advantage of these exclusive deals while they last. By purchasing a campaign offer, you accept <a href="#">terms and Conditions</a>.',
    membership: 'Membership is an ongoing subscription with automatic renewal. No signup or cancellation fees. Notice period is the rest of the month + 1 month. By signing up you accept <a href="#">terms and Conditions</a>.',
    '15daypass': 'Get 15 days of unlimited access to all gyms. Perfect for trying out climbing or a short-term visit. By purchasing a 15-Day Trial Pass, you accept <a href="#">terms and Conditions</a>.',
    punchcard: 'You can buy 1 type of value card at a time. Each entry uses one clip on your value card. Card is valid for 12 months and does not include membership benefits. Refill within 14 days after your last clip and get 100 kr off at the gym. By purchasing a value card, you accept <a href="#">terms and Conditions</a>.'
  };

  let currentCategory = null;
  let selectedPlan = null;

  // Category expansion/collapse
  categoryItems.forEach(category => {
    const isLandingLockedCategory = category.dataset.landingLocked === 'true';
    const header = category.querySelector('.category-header');
    
    if (!header) {
      console.warn('Category header not found for category:', category);
      return;
    }
    
    
    // Remove existing listeners by cloning (simpler than tracking and removing)
    const newHeader = header.cloneNode(true);
    header.parentNode.replaceChild(newHeader, header);
    const freshHeader = category.querySelector('.category-header');
    
    if (!freshHeader) {
      console.error('Failed to get fresh header after clone');
      return;
    }
    
    if (isLandingLockedCategory) {
      category.classList.add('expanded', 'selected');
      freshHeader.style.cursor = 'default';
      return;
    }
    
    freshHeader.style.cursor = 'pointer'; // Ensure it's clickable
    freshHeader.addEventListener('click', (e) => {
      // Allow interactive elements inside the header (e.g. links / data-action triggers)
      // to handle their own clicks without toggling the category.
      if (e.target.closest('a, [data-action], button')) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      console.log('Category header clicked:', category.dataset.category, e);
      const categoryType = category.dataset.category;
      const wasExpanded = category.classList.contains('expanded');

      // Collapse all categories
      categoryItems.forEach(item => {
        item.classList.remove('expanded', 'selected');
      });

      // Expand clicked category if it wasn't already expanded
      if (!wasExpanded) {
        category.classList.add('expanded', 'selected');
        currentCategory = categoryType;
        if (footerText) {
          footerText.innerHTML = sanitizeHTML(footerTexts[categoryType]);
        }
        
        // Focus the expanded category item for accessibility
        setTimeout(() => {
          category.setAttribute('tabindex', '-1');
          category.focus();
          // Scroll to top of expanded category item smoothly
          category.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100); // Small delay to ensure expansion animation has started
      } else {
        currentCategory = null;
        if (footerText) {
          footerText.innerHTML = sanitizeHTML('Select a category above to view available plans.');
        }
      }

      // Clear selected plan when switching categories
      selectedPlan = null;
      state.membershipPlanId = null;
      state.selectedProductId = null;
      state.selectedProductType = null;
      resetOrderStateForProductChange('category-switch');
      
      // Clear all punch card quantities when switching categories (but not when just re-initializing)
      // Only clear if user is actually switching categories, not during language change re-render
      state.valueCardQuantities.clear();
      
      document.querySelectorAll('.plan-card').forEach(card => {
        card.classList.remove('selected', 'has-quantity', 'disabled');
      });
      
      // Hide all quantity panels
      document.querySelectorAll('.quantity-panel').forEach(panel => {
        panel.classList.remove('show');
        panel.style.display = 'none';
      });
      
      // Hide continue buttons when switching categories
      document.querySelectorAll('.continue-btn').forEach(btn => {
        btn.style.display = 'none';
      });
    });
  });

  // Plan selection
  // Re-clone cards before binding to avoid duplicate listeners when setup runs multiple times
  // (e.g. immediate + requestAnimationFrame after render/language updates).
  Array.from(document.querySelectorAll('.plan-card')).forEach((originalCard) => {
    const clonedCard = originalCard.cloneNode(true);
    if (originalCard.parentNode) {
      originalCard.parentNode.replaceChild(clonedCard, originalCard);
    }
    const card = clonedCard;
    card.addEventListener('click', async (e) => {
      // Don't handle clicks inside the quantity panel - its buttons have their own global handlers.
      // This prevents clicks on +/- and Continue from toggling/deselecting the card.
      if (e.target.closest('.quantity-panel')) {
        return;
      }
      
      e.stopPropagation();
      
      const planId = card.dataset.plan;
      const categoryItem = card.closest('.category-item');
      const category = categoryItem.dataset.category;
      
      // Check if this card is already selected
      const isAlreadySelected = card.classList.contains('selected');
      
      // Clear ALL selections across ALL categories first
      document.querySelectorAll('.plan-card').forEach(c => {
        c.classList.remove('selected', 'has-quantity', 'disabled');
      });
      
      // Hide 15-day pass activation date section when clearing selections
      const activationSection = document.getElementById('activationDateSection');
      if (activationSection) {
        activationSection.hidden = true;
        activationSection.setAttribute('aria-hidden', 'true');
      }
      state.subscriptionStartDate = null;
      
      // Clear all punch card quantities when making a new selection
      state.valueCardQuantities.clear();
      
      // Hide all quantity panels
      document.querySelectorAll('.quantity-panel').forEach(panel => {
        panel.classList.remove('show');
        panel.style.display = 'none';
      });
      
      // Hide continue buttons initially
      document.querySelectorAll('.continue-btn').forEach(btn => {
        btn.style.display = 'none';
      });
      
      // Clear state
      state.membershipPlanId = null;
      
      // If clicking the same card that was already selected, deselect it
      if (isAlreadySelected) {
        selectedPlan = null;
        return;
      }
      
      // Select clicked card
      card.classList.add('selected');
      selectedPlan = planId;
      
      // Step 5: Store the selected plan and product details
      const productId = card.dataset.productId || planId; // Use API product ID if available
      const previousProductId = state.selectedProductId;
      const previousPlanId = state.membershipPlanId;
      state.membershipPlanId = planId; // Keep for backward compatibility
      state.selectedProductId = productId; // Store API product ID
      
      // Determine if this is a value card (punch card) by checking if planId starts with "punch-"
      // Value cards can appear in Campaign category too, so we check the planId format, not just category
      const isValueCardProduct = typeof planId === 'string' && planId.startsWith('punch-');
      const isMembership = !isValueCardProduct && (category === 'campaign' || category === 'membership' || category === '15daypass' || category === 'freetrial');
      if (category === 'campaign' && isMembership) {
        await refreshAuthenticatedCustomerForCampaignGuard();
      }
      if (category === 'campaign' && isMembership && customerHasMembershipCampaignBlockRequirement()) {
        showCampaignRejectionModal();
        card.classList.remove('selected');
        selectedPlan = null;
        state.membershipPlanId = null;
        state.selectedProductId = null;
        state.selectedProductType = null;
        updateCartSummary();
        updateCheckoutButton();
        return;
      }
      
      // Determine product type based on planId format, not just category
      if (isMembership) {
        state.selectedProductType = 'membership'; // Subscription products
      } else {
        state.selectedProductType = 'punch-card'; // Value card products
      }

      if ((previousProductId && String(previousProductId) !== String(productId)) || (previousPlanId && previousPlanId !== planId)) {
        resetOrderStateForProductChange('plan-selection');
      }

      // GTM: Track select_item for API-driven plan cards
      // Include both subscriptions and value cards for campaign category
      const productPoolByCategory = {
        campaign: [
          ...(state.campaignSubscriptions || []),
          ...(state.campaignValueCards || [])
        ],
        membership: state.subscriptions || [],
        '15daypass': state.dayPassSubscriptions || [],
        freetrial: state.landingMatchedProducts || [],
        punchcard: state.valueCards || []
      };
      const productPool = productPoolByCategory[category] || [];
      const selectedProduct = productPool.find((item) => String(item.id) === String(productId));
      trackSelectItemEvent({
        product: selectedProduct,
        productId,
        category,
        type: isMembership ? 'membership' : 'punch-card',
        card
      });
      
      // Step 5: If membership is selected, fetch add-ons immediately.
      // Value cards (even in campaign category) should not fetch or keep add-ons.
      if (isMembership && productId) {
        loadSubscriptionAdditions(productId);
      } else {
        // Switching to a value card (punch card) – clear all addon state so cart is \"restarted\"
        // This avoids leftover addons from a previously selected membership.
        state.subscriptionAdditions = [];
        state.selectedAddonIds = [];
        if (state.addonIds && state.addonIds.size > 0) {
          state.addonIds.clear();
          updateAddonSkipButton();
          updateAddonActionButton();
        }
      }
      
      // Handle value cards (punch cards) differently - show quantity selector and allow multi-quantity.
      // Value cards can appear in both 'punchcard' and 'campaign' categories (planId starts with 'punch-')
      if (isValueCardProduct) {
          // Initialize quantity to 1 for this specific punch card type
          if (!state.valueCardQuantities.has(planId)) {
            state.valueCardQuantities.set(planId, 1);
          }

          // Enforce \"one type of value card\" rule:
          // Clear quantities for all other punch card types so only the selected type remains.
          Array.from(state.valueCardQuantities.keys()).forEach((key) => {
            if (key !== planId && typeof key === 'string' && key.startsWith('punch-')) {
              state.valueCardQuantities.delete(key);
            }
          });
          
          // Show quantity panel for the selected card and hide it for others
          // Quantity panel is INSIDE each plan-card (child), not a sibling
          // Find all punch cards across all categories (campaign, punchcard)
          const punchCardCards = document.querySelectorAll('.plan-card[data-plan^="punch-"]');
          punchCardCards.forEach((punchCard) => {
            const punchPanel = punchCard.querySelector('.quantity-panel');
            if (punchCard === card) {
              punchCard.classList.add('has-quantity');
              if (punchPanel) {
                punchPanel.classList.add('show');
                punchPanel.style.display = 'flex'; /* row layout: quantity left, Continue right */
                const continueBtn = punchPanel.querySelector('.continue-btn');
                if (continueBtn) continueBtn.style.display = '';
              }
            } else {
              punchCard.classList.remove('has-quantity', 'selected', 'disabled');
              if (punchPanel) {
                punchPanel.classList.remove('show');
                punchPanel.style.display = 'none';
                const continueBtn = punchPanel.querySelector('.continue-btn');
                if (continueBtn) continueBtn.style.display = 'none';
              }
            }
          });

          // Visually disable all other punch card types so it's clear only one type can be active
          punchCardCards.forEach((punchCard) => {
            if (punchCard !== card) {
              punchCard.classList.add('disabled');
            }
          });

          // Sync UI for the selected punch card (also updates cart and summary)
          syncPunchCardQuantityUI(card, planId);

          // Update access heads-up
          updateAccessHeadsUp(card);
        } else {
          // Membership - update access heads-up
          updateAccessHeadsUp(card);
          
          // Add subtle visual cue on selected card
          card.style.transition = 'all 0.3s ease';
          card.style.transform = 'scale(1.02)';
          card.style.boxShadow = '0 8px 25px rgba(240, 0, 240, 0.3)';
          
          // Update cart to reflect selection
          updateCartSummary();
          
          // 15-day pass style routes: show activation date section, don't auto-advance
          if (category === '15daypass' || category === 'freetrial') {
            state.subscriptionStartDate = null;
            updateActivationDateSection(category);
            card.style.transform = 'scale(1)';
            card.style.boxShadow = '';
            return;
          }
          
          // Check if product has Boost label - if so, show modal instead of auto-advancing
          if (selectedProduct && hasBoostLabel(selectedProduct)) {
            // Reset card animation
            card.style.transform = 'scale(1)';
            card.style.boxShadow = '';
            loadBoostProducts(selectedProduct).then(() => {
              // Delay to let user see the selection
              setTimeout(() => {
                showBoostModal({ productsPreloaded: true });
              }, BOOST_MODAL_OPEN_DELAY_MS);
            });
          } else {
            // Clear any pending membership navigation timeout to prevent double-clicks
            if (pendingNavigationTimeouts.membership) {
              clearTimeout(pendingNavigationTimeouts.membership);
              pendingNavigationTimeouts.membership = null;
            }
            
            // Reset card animation and auto-advance to next step
            pendingNavigationTimeouts.membership = setTimeout(() => {
              // Clear timeout reference before navigation
              pendingNavigationTimeouts.membership = null;
              card.style.transform = 'scale(1)';
              card.style.boxShadow = '';
              // Only navigate if we're still on step 2 (prevent stale state navigation)
              if (state.currentStep === 2) {
                nextStep();
              }
            }, 500);
          }
        }
    });
  });

  // Punch card continue arrows (now within each card)
  document.querySelectorAll('.continue-arrow').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      nextStep();
    });
  });

  // Initialize Lucide icons
  if (typeof lucide !== 'undefined') {
    lucide.createIcons();
  }
}

// Initialize auth mode toggle
function initAuthModeToggle() {
  const loginSection = document.querySelector('[data-auth-section="login"]');
  const createSection = document.querySelector('[data-auth-section="create"]');
  
  // Set initial state - if user is logged in, select login tab, otherwise create account
  const isAuthenticated = isUserAuthenticated();
  
  // Hide toggle buttons if user is already authenticated.
  const switchBtns = document.querySelectorAll('.auth-mode-switch-btn');
  switchBtns.forEach(btn => {
    btn.style.display = isAuthenticated ? 'none' : '';
  });
  
  // On desktop, default to login form; on mobile, also show login form by default
  const isDesktop = window.innerWidth >= 768;
  
  if (!isAuthenticated) {
    // Always show login form by default (both desktop and mobile)
    switchAuthMode('login');
    if (loginSection) {
      loginSection.style.display = 'block';
      // Ensure it's visible
      loginSection.style.visibility = 'visible';
      loginSection.style.opacity = '1';
    }
    if (createSection) {
      createSection.style.display = 'none';
    }
  } else {
    // Authenticated: show login section with status
    if (loginSection) {
      loginSection.style.display = 'block';
    }
    if (createSection) {
      createSection.style.display = 'none';
    }
  }
}

// Switch between auth modes
function switchAuthMode(mode, email = null) {
  const loginSection = document.querySelector('[data-auth-section="login"]');
  const createSection = document.querySelector('[data-auth-section="create"]');
  const switchBtns = document.querySelectorAll('.auth-mode-switch-btn');
  
  // Store current mode
  state.currentAuthMode = mode;
  
  // Update button text and mode based on current section
  switchBtns.forEach(btn => {
    if (mode === 'login') {
      btn.dataset.mode = 'create';
      const textSpan = btn.querySelector('.auth-mode-switch-text');
      if (textSpan) textSpan.textContent = t('form.authSwitch.createAccount');
      // Update icon to account icon
      btn.innerHTML = sanitizeHTML(`
        <span class="auth-mode-switch-text">${t('form.authSwitch.createAccount')}</span>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M18 3a3 3 0 0 0-3 3v12a3 3 0 0 0 3 3 3 3 0 0 0 3-3 3 3 0 0 0-3-3H6a3 3 0 0 0-3 3 3 3 0 0 0 3 3 3 3 0 0 0 3-3V6a3 3 0 0 0-3-3 3 3 0 0 0-3 3 3 3 0 0 0 3 3h12a3 3 0 0 0 3-3 3 3 0 0 0-3-3z"></path>
        </svg>
      `);
    } else {
      btn.dataset.mode = 'login';
      const textSpan = btn.querySelector('.auth-mode-switch-text');
      if (textSpan) textSpan.textContent = t('form.authSwitch.login');
      // Update icon to login icon
      btn.innerHTML = sanitizeHTML(`
        <span class="auth-mode-switch-text">${t('form.authSwitch.login')}</span>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"></path>
          <polyline points="10 17 15 12 10 7"></polyline>
          <line x1="15" y1="12" x2="3" y2="12"></line>
        </svg>
      `);
    }
  });
  
  // Hide buttons if user is authenticated.
  const isAuthenticated = isUserAuthenticated();
  switchBtns.forEach(btn => {
    btn.style.display = isAuthenticated ? 'none' : '';
  });
  
  // Show/hide sections with fade
  if (mode === 'login') {
    createSection.style.display = 'none';
    loginSection.style.display = 'block';
    
    // If email is provided, populate the login email field
    if (email && DOM.loginEmail) {
      DOM.loginEmail.value = email;
      // Focus on password field after a short delay for better UX
      setTimeout(() => {
        if (DOM.loginPassword) {
          DOM.loginPassword.focus();
        }
      }, 100);
    }
  } else {
    loginSection.style.display = 'none';
    createSection.style.display = 'block';
    // Clear any error states when switching to create account mode
    clearErrorStates();
    // Check button state when switching to create account mode
    setTimeout(() => checkSaveAccountButtonState(), 100);
  }
}

function setupSaveAccountButtonValidation() {
  // Check initial state
  setTimeout(() => checkSaveAccountButtonState(), 100);
  
  // Add listeners to all registration form fields
  const registrationForm = document.querySelector('.registration-form');
  if (registrationForm) {
    const fields = registrationForm.querySelectorAll('input, select');
    fields.forEach(field => {
      field.addEventListener('input', checkSaveAccountButtonState);
      field.addEventListener('change', checkSaveAccountButtonState);
    });
  }
}

function handleGlobalClick(event) {
  const actionable = event.target.closest('[data-action]');
  if (!actionable) return;

  const action = actionable.dataset.action;
  
  // Debug: Log edit-gym button clicks
  if (action === 'edit-gym') {
    console.log('[Edit Gym Click] Button clicked:', {
      target: event.target,
      actionable: actionable,
      action: action,
      buttonId: actionable.id,
      buttonElement: document.getElementById('selectedGymLink')
    });
  }

  switch (action) {
    case 'select-plan': {
      // The button lives inside .plan-card, and the card click handler already processes selection.
      // Avoid triggering a second synthetic click here, which can toggle selection twice.
      event.preventDefault();
      event.stopPropagation();
      break;
    }
    case 'select-membership': {
      const planId = actionable.dataset.planId;
      if (planId) selectMembershipPlan(planId);
      break;
    }
    case 'toggle-addon': {
      const addonId = actionable.dataset.addonId;
      if (addonId) toggleAddon(addonId, actionable);
      break;
    }
    case 'increment-quantity': {
      event.stopPropagation();
      const planId = actionable.dataset.planId;
      if (planId && planId.includes('punch')) {
        // Quantity panel is inside the plan-card; find the card that contains it
        const panel = actionable.closest('.quantity-panel');
        const card = panel ? panel.closest('.plan-card') : null;
        if (card && card.classList.contains('plan-card') && card.classList.contains('selected')) {
          const current = state.valueCardQuantities.get(planId) || 1;
          if (current < 5) { // Max 5 punch cards of the same type
            state.valueCardQuantities.set(planId, current + 1);
            syncPunchCardQuantityUI(card, planId);
          }
        }
      } else if (planId) {
        adjustValueCardQuantity(planId, 1);
        centerPlanCard(actionable.closest('.plan-card'));
      }
      break;
    }
    case 'decrement-quantity': {
      event.stopPropagation();
      const planId = actionable.dataset.planId;
      if (planId && planId.includes('punch')) {
        // Quantity panel is inside the plan-card; find the card that contains it
        const panel = actionable.closest('.quantity-panel');
        const card = panel ? panel.closest('.plan-card') : null;
        if (card && card.classList.contains('plan-card') && card.classList.contains('selected')) {
          const current = state.valueCardQuantities.get(planId) || 1;
          if (current > 1) { // Min 1 punch card
            state.valueCardQuantities.set(planId, current - 1);
            syncPunchCardQuantityUI(card, planId);
          }
        }
      } else if (planId) {
        adjustValueCardQuantity(planId, -1);
        centerPlanCard(actionable.closest('.plan-card'));
      }
      break;
    }
    case 'continue-punch-card': {
      event.stopPropagation();
      nextStep();
      break;
    }
    case 'switch-auth-mode': {
      const mode = actionable.dataset.mode;
      // Toggle mode: if currently on login, switch to create, and vice versa
      const currentMode = state.currentAuthMode || (document.querySelector('[data-auth-section="login"]')?.style.display !== 'none' ? 'login' : 'create');
      const newMode = mode || (currentMode === 'login' ? 'create' : 'login');
      switchAuthMode(newMode);
      break;
    }
    case 'submit-checkout': {
      event.preventDefault();
      // For 15-day pass style products: confirm activation choice before proceeding to checkout
      const is15DayPassSelected =
        document.querySelector('[data-category="15daypass"] .plan-card.selected') ||
        document.querySelector('[data-category="freetrial"] .plan-card.selected') ||
        (state.selectedProductId && (state.dayPassSubscriptions || []).some(p => String(p.id) === String(state.selectedProductId)));
      if (is15DayPassSelected && !state.checkoutConfirmAccepted) {
        openCheckoutConfirmModal();
        break;
      }
      handleCheckoutClick();
      break;
    }
    case 'continue-value-cards': {
      event.preventDefault();
      handleValueCardContinue();
      break;
    }
    case 'set-activation-now': {
      state.subscriptionStartDate = null;
      const pickerWrap = document.getElementById('activationDatePickerWrap');
      if (pickerWrap) pickerWrap.hidden = true;
      updateActivationDatePeriodHint();
      break;
    }
    case 'set-activation-date': {
      const pickerWrap = document.getElementById('activationDatePickerWrap');
      const dateInput = document.getElementById('activationDateInput');
      if (pickerWrap) pickerWrap.hidden = false;
      if (dateInput) {
        const todayStr = getTodayLocalDateString();
        dateInput.min = todayStr;
        if (!dateInput.value) {
          dateInput.value = todayStr;
        }
        state.subscriptionStartDate = dateInput.value;
      }
      updateActivationDatePeriodHint();
      break;
    }
    case 'activation-date-continue': {
      event.preventDefault();
      const dateInput = document.getElementById('activationDateInput');
      const pickRadio = document.getElementById('activationDatePick');
      if (pickRadio?.checked && dateInput?.value) {
        state.subscriptionStartDate = dateInput.value;
      } else {
        state.subscriptionStartDate = null;
      }
      closeActivationDateModal();
      if (state.currentStep === 2) {
        nextStep();
      } else {
        // Immediately reflect the newly selected date in UI
        updatePaymentOverview();
        // If the user is editing from checkout confirmation flow, re-open confirmation
        if (state.checkoutConfirmPendingAfterEdit) {
          state.checkoutConfirmPendingAfterEdit = false;
          setTimeout(() => openCheckoutConfirmModal(), 0);
          break;
        }

        // If there's no order yet (common before checkout), only update local state/UI.
        // We'll apply the selected start date when checkout creates the order.
        if (state.orderId || state.fullOrder?.id) {
          // If user changes date from summary step after an order exists, update order + UI in-place
          (async () => {
            try {
              await applyActivationDateToExistingOrder();
            } catch (e) {
              console.error('[Activation Date] Failed to apply activation date change:', e);
              showToast(t('activationDate.changeFailed') || 'Unable to change date. Please complete your purchase or start over.', 'error');
            }
          })();
        }
      }
      break;
    }
    case 'checkout-confirm-edit': {
      event.preventDefault();
      closeCheckoutConfirmModal();
      state.checkoutConfirmPendingAfterEdit = true;
      // Reuse the same change handler so the date input is shown when needed
      handleChangeActivationDate();
      break;
    }
    case 'checkout-confirm-continue': {
      event.preventDefault();
      state.checkoutConfirmAccepted = true;
      closeCheckoutConfirmModal();
      handleCheckoutClick();
      break;
    }
    case 'close-activation-modal': {
      event.preventDefault();
      closeActivationDateModal();
      break;
    }
    case 'edit-cart': {
      event.preventDefault();
      handleEditCart();
      break;
    }
    case 'change-activation-date': {
      event.preventDefault();
      handleChangeActivationDate();
      break;
    }
    case 'show-detailed-receipt': {
      showDetailedReceipt();
      break;
    }
    case 'close-detailed-receipt': {
      closeDetailedReceipt();
      break;
    }
    case 'open-login': {
      showToast('Login flow handled by backend integration.', 'info');
      break;
    }
    case 'logout': {
      event.preventDefault();
      showLogoutConfirmation();
      break;
    }
    case 'save-account': {
      event.preventDefault();
      handleSaveAccount();
      break;
    }
    case 'edit-gym': {
      event.preventDefault();
      handleBackToGym();
      break;
    }
    case 'go-to-step-1': {
      event.preventDefault();
      handleBackToGym();
      break;
    }
    case 'toggle-addons-step': {
      event.preventDefault();
      handleAddonContinue();
      break;
    }
    case 'go-back-step': {
      event.preventDefault();
      prevStep();
      break;
    }
    case 'scroll-to-faq': {
      event.preventDefault();
      scrollToFAQ();
      break;
    }
    case 'invite-copy-link': {
      event.preventDefault();
      handleInviteCopyLink(actionable);
      break;
    }
    case 'invite-share-whatsapp': {
      event.preventDefault();
      handleInviteShare('whatsapp');
      break;
    }
    case 'invite-share-messenger': {
      event.preventDefault();
      handleInviteShare('messenger');
      break;
    }
    case 'invite-share-instagram': {
      event.preventDefault();
      handleInviteShare('instagram');
      break;
    }
    case 'invite-share-sms': {
      event.preventDefault();
      handleInviteShare('sms');
      break;
    }
    case 'invite-share-email': {
      event.preventDefault();
      handleInviteShare('email');
      break;
    }
    case 'invite-share-native': {
      event.preventDefault();
      handleInviteShare('native');
      break;
    }
    default:
      break;
  }
}

// ===========================
// Referral tracking (Phase 1)
// ===========================
// Phase 1 goal: every signup originating from a shared link silently carries
// `recruitedBy=<recruiter customer ID>` to BRP, so attribution data flows from
// day one — even before any reward is wired up. See docs/brp-api3-openapi.yaml
// L7073 for the API field, and the `referralv1` branch context for design.
const REFERRAL_TRACKING_ENABLED = true;
const REFERRAL_STORAGE_KEY = 'boulders_referral_v1';
const REFERRAL_TTL_MS = 30 * 24 * 60 * 60 * 1000;
const REFERRAL_QUERY_PARAM = 'ref';

function isValidReferrerId(value) {
  const n = Number(value);
  return Number.isInteger(n) && n > 0;
}

function captureReferralFromUrl() {
  if (!REFERRAL_TRACKING_ENABLED) return;
  try {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get(REFERRAL_QUERY_PARAM);
    if (!raw) return;
    if (!isValidReferrerId(raw)) {
      devWarn('[Referral] Ignoring invalid ref param:', raw);
      return;
    }
    const ref = Number(raw);
    const entry = {
      ref,
      capturedAt: Date.now(),
      source: normalizePathname(window.location.pathname),
    };
    localStorage.setItem(REFERRAL_STORAGE_KEY, JSON.stringify(entry));
    devLog('[Referral] Captured ref:', ref, 'on', entry.source);
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'referral_link_visited',
        ref,
        landing_path: entry.source,
      });
    } catch (_) { /* GTM optional */ }
  } catch (err) {
    devWarn('[Referral] captureReferralFromUrl failed:', err);
  }
}

function getActiveReferral() {
  if (!REFERRAL_TRACKING_ENABLED) return null;
  try {
    const raw = localStorage.getItem(REFERRAL_STORAGE_KEY);
    if (!raw) return null;
    const entry = JSON.parse(raw);
    if (!entry || !isValidReferrerId(entry.ref) || !entry.capturedAt) return null;
    if (Date.now() - entry.capturedAt > REFERRAL_TTL_MS) {
      localStorage.removeItem(REFERRAL_STORAGE_KEY);
      return null;
    }
    return entry;
  } catch (err) {
    devWarn('[Referral] getActiveReferral failed:', err);
    return null;
  }
}

function clearReferral(reason) {
  try {
    localStorage.removeItem(REFERRAL_STORAGE_KEY);
    if (reason) devLog('[Referral] Cleared:', reason);
  } catch (_) { /* ignore */ }
}

// Returns the recruiter customer ID to send as `recruitedBy` on a subscription
// POST, or null if there is none / the referrer is the subscriber themselves.
function getRecruitedByForPayload() {
  const entry = getActiveReferral();
  if (!entry) return null;
  const subscriberId = state.customerId ? Number(state.customerId) : null;
  if (subscriberId && subscriberId === entry.ref) {
    devWarn('[Referral] Self-referral detected, dropping ref:', entry.ref);
    clearReferral('self-referral');
    return null;
  }
  return entry.ref;
}

// Mirror a successful attribution to our own Google Sheet ledger. This is
// purely a fallback in case BRP doesn't surface `recruitedBy` in the admin
// UI — it gives the team a manual reward queue independent of BRP's UX.
//
// Strict requirements:
//   1. Fire-and-forget — must NEVER block, throw, or delay signup.
//   2. Best-effort — silent failure is acceptable; BRP is the source of truth.
//   3. `keepalive: true` so the request survives navigation off the page.
function logReferralAttributionToSheet(data) {
  if (!REFERRAL_TRACKING_ENABLED) return;
  try {
    const accessToken = typeof window.getAccessToken === 'function' ? window.getAccessToken() : null;
    // Without a token the Pages Function will 401 us anyway, so skip
    // entirely rather than emit a noisy network error.
    if (!accessToken) {
      devLog('[Referral] Skipping sheet log — no access token available');
      return;
    }
    const url = '/api/referrals/log';
    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
      body: JSON.stringify(data),
      keepalive: true,
    })
      .then((resp) => {
        if (!resp.ok) {
          devWarn('[Referral] Sheet log responded', resp.status);
        } else {
          devLog('[Referral] 📒 Mirrored attribution to sheet ledger');
        }
      })
      .catch((err) => {
        devWarn('[Referral] Sheet log failed (non-blocking):', err?.message || err);
      });
  } catch (err) {
    devWarn('[Referral] Sheet log threw (suppressed):', err);
  }
}

// ===========================
// Invite Friends helpers
// ===========================
const INVITE_SHARE_URL_BASE = 'https://join.boulders.dk/freetrial';

// Resolve the current user's customer ID for use as the recruiter ID in the
// shared link. Reads from multiple sources because `state.customerId` is
// populated asynchronously after login (and isn't set at all in test mode),
// so we also consult the authenticated customer profile and the JWT
// metadata as fallbacks.
function getCurrentRecruiterId() {
  const candidates = [
    state?.customerId,
    state?.authenticatedCustomer?.id,
    state?.authenticatedCustomer?.customerId,
  ];
  if (typeof getTokenMetadata === 'function') {
    try {
      const metadata = getTokenMetadata();
      if (metadata) {
        candidates.push(metadata.username, metadata.userName, metadata.customerId);
      }
    } catch (_) { /* ignore */ }
  }
  for (const candidate of candidates) {
    const id = candidate ? Number(candidate) : null;
    if (isValidReferrerId(id)) return id;
  }
  return null;
}

// Personalized share URL: appends ?ref=<recruiter customer ID> when we know
// who the sharer is. Falls back to the plain base URL for anonymous shares
// (e.g. logged-out test mode) so existing behavior is preserved.
function buildInviteShareUrl() {
  // /99kr invites point at the offer landing page itself, with no referral
  // query param — this is an offer share, not a recruiter link.
  if (isFirstClimbRoute()) {
    try {
      return new URL('/99kr', INVITE_SHARE_URL_BASE).toString();
    } catch (_) {
      return `${INVITE_SHARE_URL_BASE.replace(/\/+$/, '')}/99kr`;
    }
  }
  if (!REFERRAL_TRACKING_ENABLED) return INVITE_SHARE_URL_BASE;
  const recruiterId = getCurrentRecruiterId();
  if (!recruiterId) return INVITE_SHARE_URL_BASE;
  return `${INVITE_SHARE_URL_BASE}?${REFERRAL_QUERY_PARAM}=${recruiterId}`;
}

function getInviteFirstName() {
  const fullName = String(state?.order?.memberName || '').trim();
  if (!fullName || fullName === '—') return '';
  const first = fullName.split(/\s+/)[0];
  return first && first !== '—' ? first : '';
}

function getInviteShareMessage() {
  const firstName = getInviteFirstName();
  const templateKey = isFirstClimbRoute() ? 'invite.firstclimb.shareMessage' : 'invite.shareMessage';
  const template = t(templateKey) || 'Hey! Climb with me at Boulders:';
  const withName = firstName
    ? template.replace('{name}', firstName)
    : template.replace(/\s*\{name\}/g, '').replace(/\s*—\s*\{name\}\s*/g, ' ');
  return withName.replace(/\s{2,}/g, ' ').trim();
}

function getInviteShareText() {
  return `${getInviteShareMessage()} ${buildInviteShareUrl()}`;
}

function trackInviteShare(method) {
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'invite_share',
      method,
      share_url: buildInviteShareUrl(),
    });
  } catch (err) {
    console.warn('[Invite] Failed to push GTM event:', err);
  }
}

function isMobileUserAgent() {
  if (typeof navigator === 'undefined') return false;
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent || '');
}

function renderInviteFriends(productType) {
  const section = document.getElementById('inviteFriendsSection');
  if (!section) return;

  // Drive a layout-level flag so CSS can reorder mobile siblings only when the
  // invite card is actually visible (avoids changing layout for other flows).
  const layout = document.querySelector('#step-5 .confirmation-layout');

  const firstclimbVariant = isFirstClimbRoute();
  // Show the invite-friends card for full memberships and for the firstclimb
  // success page. Other product types stay hidden.
  if (productType !== 'membership' && !firstclimbVariant) {
    section.style.display = 'none';
    section.removeAttribute('data-variant');
    if (layout) layout.removeAttribute('data-with-invite');
    return;
  }
  section.style.display = '';
  if (layout) layout.setAttribute('data-with-invite', 'true');

  // Swap the localized copy on the card for the firstclimb success page.
  // Membership flow keeps its existing keys via data-i18n-key. We override the
  // DOM text and the keys so a later language switch picks the right variant.
  const titleEl = section.querySelector('.invite-friends-title');
  const subtitleEl = section.querySelector('.invite-friends-subtitle');
  const footnoteEl = section.querySelector('.invite-friends-footnote');
  if (firstclimbVariant) {
    section.setAttribute('data-variant', 'firstclimb');
    if (titleEl) {
      titleEl.setAttribute('data-i18n-key', 'invite.firstclimb.title');
      titleEl.textContent = t('invite.firstclimb.title');
    }
    if (subtitleEl) {
      subtitleEl.setAttribute('data-i18n-key', 'invite.firstclimb.subtitle');
      subtitleEl.textContent = t('invite.firstclimb.subtitle');
    }
    if (footnoteEl) {
      footnoteEl.setAttribute('data-i18n-key', 'invite.firstclimb.footnote');
      footnoteEl.textContent = t('invite.firstclimb.footnote');
    }
  } else {
    section.removeAttribute('data-variant');
    if (titleEl) {
      titleEl.setAttribute('data-i18n-key', 'invite.title');
      titleEl.textContent = t('invite.title');
    }
    if (subtitleEl) {
      subtitleEl.setAttribute('data-i18n-key', 'invite.subtitle');
      subtitleEl.textContent = t('invite.subtitle');
    }
    if (footnoteEl) {
      footnoteEl.setAttribute('data-i18n-key', 'invite.footnote');
      footnoteEl.textContent = t('invite.footnote');
    }
  }

  // Phase 1: fire a one-time funnel event so we know how many membership
  // signups have a personalized share link to give out. Note: clearing the
  // inbound referral happens at actual attribution time inside
  // addSubscriptionItem(), not here — rendering the success page is not the
  // same as attribution success (see test mode `?testSuccess=true`).
  if (REFERRAL_TRACKING_ENABLED) {
    const recruiterId = getCurrentRecruiterId();
    if (recruiterId) {
      try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'referral_link_generated',
          referrer_id: recruiterId,
        });
      } catch (_) { /* GTM optional */ }
    }
  }

  const card = section.querySelector('.invite-friends-card');
  if (!card) return;

  if (isMobileUserAgent()) {
    card.setAttribute('data-mobile', 'true');
  } else {
    card.removeAttribute('data-mobile');
  }

  if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
    card.setAttribute('data-native-share', 'true');
  } else {
    card.removeAttribute('data-native-share');
  }

  const copyBtn = document.getElementById('inviteCopyBtn');
  if (copyBtn) copyBtn.classList.remove('is-copied');
}

async function handleInviteCopyLink(button) {
  const success = await copyTextToClipboard(buildInviteShareUrl());
  if (!success) {
    showToast(t('invite.copyFailed') || 'Could not copy link. Please copy it manually.', 'error');
    return;
  }

  trackInviteShare('copy_link');
  showToast(t('invite.copiedToast') || 'Link copied — share it with your friends!', 'success');

  if (!button) return;
  button.classList.add('is-copied');
  setTimeout(() => button.classList.remove('is-copied'), 2400);
}

function triggerProtocolHandler(url) {
  // Trigger custom-scheme URLs (mailto:, sms:, fb-messenger:// etc.) via a
  // programmatic anchor click instead of `window.location.href = url`.
  //
  // Browsers treat anchor-click events as direct user-initiated navigation and
  // are far less likely to silently suppress the OS protocol-handler hand-off.
  // Setting window.location.href to a protocol URL can be filtered by popup
  // blockers, especially on Chrome/Edge on Windows, leading to "nothing
  // happens when I click Email" reports. Anchor clicks bypass that heuristic.
  //
  // We deliberately do NOT use target="_blank" here — for protocol URLs some
  // browsers open and immediately close a blank tab, making it look like the
  // click was ignored.
  try {
    const a = document.createElement('a');
    a.href = url;
    a.rel = 'noopener noreferrer';
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } catch (err) {
    console.warn('[Invite] Protocol handler failed, falling back to location.href:', err);
    try { window.location.href = url; } catch (_) { /* give up silently */ }
  }
}

async function copyTextToClipboard(text) {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (_) { /* fall through to fallback */ }

  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'absolute';
    ta.style.left = '-9999px';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return !!ok;
  } catch (err) {
    console.warn('[Invite] Clipboard copy failed:', err);
    return false;
  }
}

async function handleInviteShare(method) {
  const shareUrl = buildInviteShareUrl();
  const message = getInviteShareMessage();
  const shareText = getInviteShareText();
  const encodedText = encodeURIComponent(shareText);
  const encodedUrl = encodeURIComponent(shareUrl);
  const encodedSubject = encodeURIComponent(t('invite.shareSubject') || '2 weeks of free climbing at Boulders');

  switch (method) {
    case 'whatsapp':
      trackInviteShare('whatsapp');
      window.open(`https://wa.me/?text=${encodedText}`, '_blank', 'noopener,noreferrer');
      break;
    case 'messenger':
      trackInviteShare('messenger');
      if (isMobileUserAgent()) {
        triggerProtocolHandler(`fb-messenger://share/?link=${encodedUrl}`);
      } else {
        // Desktop has no public Messenger share dialog without an app_id;
        // fall back to the Facebook share dialog so users can still post the link.
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`, '_blank', 'noopener,noreferrer');
      }
      break;
    case 'instagram':
      trackInviteShare('instagram');
      // Instagram has no public web-share API — we copy the message + URL to
      // the clipboard so the user can paste it manually, then optimistically
      // deep-link to their DM inbox. Toast tells them what to do next.
      //
      // Mobile: `instagram://direct-inbox` is an undocumented deep link that
      //   works on current iOS/Android IG builds. If the app isn't installed
      //   the deep link silently fails, but the link is still on clipboard.
      // Desktop: web DM inbox works for logged-in users; otherwise IG shows
      //   the login page and lands the user in DMs after authentication.
      await copyTextToClipboard(shareText);
      showToast(
        t('invite.instagramToast') || 'Link copied — paste it into your Instagram DM or story!',
        'success'
      );
      if (isMobileUserAgent()) {
        triggerProtocolHandler('instagram://direct-inbox');
      } else {
        window.open('https://www.instagram.com/direct/inbox/', '_blank', 'noopener,noreferrer');
      }
      break;
    case 'sms':
      trackInviteShare('sms');
      triggerProtocolHandler(`sms:?&body=${encodedText}`);
      break;
    case 'email':
      trackInviteShare('email');
      triggerProtocolHandler(`mailto:?subject=${encodedSubject}&body=${encodedText}`);
      break;
    case 'native':
      try {
        if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
          await navigator.share({
            title: t('invite.shareSubject') || '2 weeks of free climbing at Boulders',
            text: message,
            url: shareUrl,
          });
          trackInviteShare('native_share');
        }
      } catch (_) { /* user cancelled or unsupported */ }
      break;
    default:
      break;
  }
}

function handleGlobalInput(event) {
  const field = event.target;
  if (!(field instanceof HTMLElement)) return;
  if (field.classList.contains('quantity-btn')) return;

  // Check save account button state when registration form fields change
  const registrationForm = field.closest('.registration-form');
  if (registrationForm) {
    checkSaveAccountButtonState();
  }

  if (field.closest('.form-group')) {
    field.closest('.form-group').classList.remove('error');
  }

  if (field.dataset.apiField === 'payment.method') {
    updateCheckoutButton();
  }
}

function selectMembershipPlan(planId) {
  state.membershipPlanId = planId;
  state.subscriptionAttachedOrderId = null;
  const selectedPlan = findMembershipPlan(planId);

  if (DOM.membershipPlans) {
    DOM.membershipPlans.querySelectorAll('.plan-card').forEach((card) => {
      const isSelected = card.dataset.planId === planId;
      card.classList.toggle('selected', isSelected);
      const btn = card.querySelector('[data-action="select-membership"]');
      if (btn) {
        const plan = findMembershipPlan(card.dataset.planId ?? '');
        btn.textContent = isSelected
          ? 'Selected'
          : plan?.cta ?? 'Select Plan';
        const timeoutId = buttonGlareTimeouts.get(btn);
        if (timeoutId) {
          clearTimeout(timeoutId);
          buttonGlareTimeouts.delete(btn);
        }
        btn.classList.remove('attention-glare');
      }
      if (isSelected) {
        centerPlanCard(card);
      }
    });
  }

  updateCartSummary();
  updateCheckoutButton();
  if (state.currentStep === 2) {
    setTimeout(() => nextStep(), 300);
  }
  showToast(`${selectedPlan?.name ?? 'Membership'} selected.`, 'success');
  // Do not pre-create order on plan selection; wait for explicit checkout.

  // GTM: Track select_item for legacy membership plan cards
  if (selectedPlan) {
    trackSelectItemEvent({
      product: {
        id: planId,
        name: selectedPlan.name,
        price: selectedPlan.price
      },
      productId: planId,
      category: 'membership',
      type: 'membership'
    });
  }
}

function toggleAddon(addonId, button) {
  if (!addonId) return;
  
  if (state.addonIds.has(addonId)) {
    state.addonIds.delete(addonId);
  } else {
    state.addonIds.add(addonId);
  }

  const card = button?.closest('.addon-card') || button?.closest('.plan-card');
  if (card) {
    const isSelected = state.addonIds.has(addonId);
    card.classList.toggle('selected', isSelected);
    
    // Update button text if it's a button
    if (button && button.tagName === 'BUTTON') {
      button.textContent = isSelected ? t('button.added') : t('button.addToCart');
      button.setAttribute('data-i18n-key', isSelected ? 'button.added' : 'button.addToCart');
    }
    
    centerPlanCard(card);
  }

  updateCartSummary();
  updateAddonSkipButton();
  updateAddonActionButton();
  updateCheckoutButton(); // Re-evaluate checkout button state when addons change
}

function updateAddonActionButton() {
  const actionButton = document.querySelector('.addons-action-btn');
  if (!actionButton) return;
  
  const hasSelectedAddons = state.addonIds.size > 0;
  
  if (hasSelectedAddons) {
    actionButton.textContent = t('addons.modal.continue');
    actionButton.setAttribute('data-i18n-key', 'addons.modal.continue');
    actionButton.classList.remove('addons-skip');
    actionButton.classList.add('addons-continue');
  } else {
    actionButton.textContent = t('addons.modal.skip');
    actionButton.setAttribute('data-i18n-key', 'addons.modal.skip');
    actionButton.classList.remove('addons-continue');
    actionButton.classList.add('addons-skip');
  }
}

function handleAddonAction() {
  const hasSelectedAddons = state.addonIds.size > 0;
  
  if (hasSelectedAddons) {
    // If addons are selected, proceed directly
    proceedAfterAddons();
  } else {
    // If no addons selected, show confirmation dialog
    showSkipConfirmation();
  }
}

function showSkipConfirmation() {
  // Create confirmation modal
  const confirmationOverlay = document.createElement('div');
  confirmationOverlay.className = 'confirmation-overlay';
  confirmationOverlay.style.cssText = `
    position: fixed;
    inset: 0;
    background:
      radial-gradient(120% 90% at 15% 100%, rgba(240, 0, 240, 0.2), transparent 58%),
      radial-gradient(90% 80% at 85% -5%, rgba(59, 130, 246, 0.16), transparent 60%),
      rgba(8, 10, 18, 0.5);
    backdrop-filter: blur(14px) saturate(130%);
    -webkit-backdrop-filter: blur(14px) saturate(130%);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2000;
  `;
  
  const confirmationDialog = document.createElement('div');
  confirmationDialog.className = 'confirmation-dialog';
  confirmationDialog.style.cssText = `
    background:
      linear-gradient(155deg, rgba(255, 255, 255, 0.12), rgba(255, 255, 255, 0.03) 40%, rgba(255, 255, 255, 0.02) 100%),
      rgba(17, 20, 32, 0.66);
    backdrop-filter: blur(24px) saturate(140%);
    -webkit-backdrop-filter: blur(24px) saturate(140%);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 16px;
    padding: 24px;
    max-width: 400px;
    text-align: center;
    color: var(--color-text-secondary);
    box-shadow:
      0 30px 70px rgba(0, 0, 0, 0.45),
      0 0 0 1px rgba(255, 255, 255, 0.18) inset;
  `;
  
  // Create dialog content using DOM methods (not innerHTML) to avoid sanitization issues
  const title = document.createElement('h3');
  title.textContent = t('addons.skipConfirm.title');
  title.style.cssText = 'margin: 0 0 16px 0; color: var(--color-text-secondary); font-size: 18px;';
  
  const message = document.createElement('p');
  message.textContent = t('addons.skipConfirm.message');
  message.style.cssText = 'margin: 0 0 24px 0; color: var(--color-text-muted); line-height: 1.5;';
  
  const buttonContainer = document.createElement('div');
  buttonContainer.style.cssText = 'display: flex; gap: 12px; justify-content: center;';
  
  const cancelBtn = document.createElement('button');
  cancelBtn.className = 'confirmation-btn confirmation-cancel';
  cancelBtn.textContent = t('addons.skipConfirm.goBack');
  cancelBtn.style.cssText = `
    padding: 10px 20px;
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.05);
    color: var(--color-text-secondary);
    cursor: pointer;
    font-weight: 600;
  `;
  
  const skipBtn = document.createElement('button');
  skipBtn.className = 'confirmation-btn confirmation-skip';
  skipBtn.textContent = t('addons.skipConfirm.skipAnyway');
  skipBtn.style.cssText = `
    padding: 10px 20px;
    border: none;
    border-radius: 8px;
    background: var(--color-brand-accent);
    color: var(--color-button-primary);
    cursor: pointer;
    font-weight: 600;
  `;
  
  buttonContainer.appendChild(cancelBtn);
  buttonContainer.appendChild(skipBtn);
  
  confirmationDialog.appendChild(title);
  confirmationDialog.appendChild(message);
  confirmationDialog.appendChild(buttonContainer);
  
  confirmationOverlay.appendChild(confirmationDialog);
  document.body.appendChild(confirmationOverlay);
  
  // Add event listeners
  cancelBtn.addEventListener('click', () => {
    document.body.removeChild(confirmationOverlay);
  });
  
  skipBtn.addEventListener('click', () => {
    document.body.removeChild(confirmationOverlay);
    proceedAfterAddons();
  });
  
  // Close on overlay click
  confirmationOverlay.addEventListener('click', (e) => {
    if (e.target === confirmationOverlay) {
      document.body.removeChild(confirmationOverlay);
    }
  });
}

function adjustValueCardQuantity(planId, delta) {
  const plan = findValueCard(planId);
  if (!plan) return;

  const current = state.valueCardQuantities.get(planId) ?? plan.min ?? 0;
  const max = Number.isFinite(plan.max) ? plan.max : current + delta;
  const min = plan.min ?? 0;
  const next = Math.max(min, Math.min(max, current + delta));

  state.valueCardQuantities.set(planId, next);
  syncValueCardUI(planId);
  enforceValueCardAvailability();
  updateCartSummary();
  updateValueCardSummary();
}

function syncValueCardUI(planId) {
  const plan = findValueCard(planId);
  if (!plan || !DOM.valuePlans) return;

  const card = DOM.valuePlans.querySelector(`[data-plan-id="${planId}"]`);
  if (!card) return;

  const selector = card.querySelector('.quantity-selector');
  const valueEl = card.querySelector('[data-element="quantityValue"]');
  const totalEl = card.querySelector('[data-element="quantityTotal"]');
  const decrementBtn = card.querySelector('[data-action="decrement-quantity"]');
  const incrementBtn = card.querySelector('[data-action="increment-quantity"]');

  const quantity = state.valueCardQuantities.get(planId) ?? plan.min ?? 0;
  const total = plan.price * quantity;

  if (valueEl) valueEl.textContent = quantity;
  if (totalEl) totalEl.textContent = formatPriceHalfKrone(roundToHalfKrone(total));
  if (selector) selector.dataset.current = String(quantity);

  if (decrementBtn) decrementBtn.disabled = quantity <= (plan.min ?? 0);
  if (incrementBtn) {
    if (Number.isFinite(plan.max)) {
      incrementBtn.disabled = quantity >= plan.max;
    } else {
      incrementBtn.disabled = false;
    }
  }

  card.classList.toggle('selected', quantity > (plan.min ?? 0));
}

function enforceValueCardAvailability() {
  if (!DOM.valuePlans) return;
  const activeId = Array.from(state.valueCardQuantities.entries()).find(([, qty]) => qty > 0)?.[0] ?? null;

  DOM.valuePlans.querySelectorAll('.plan-card').forEach((card) => {
    const selector = card.querySelector('.quantity-selector');
    if (!selector) return;
    const planId = card.dataset.planId;
    const decrementBtn = card.querySelector('[data-action="decrement-quantity"]');
    const incrementBtn = card.querySelector('[data-action="increment-quantity"]');

    const disable = Boolean(activeId && planId !== activeId);
    card.classList.toggle('disabled', disable);

    if (disable) {
      decrementBtn?.setAttribute('disabled', 'true');
      incrementBtn?.setAttribute('disabled', 'true');
    } else {
      decrementBtn?.removeAttribute('disabled');
      incrementBtn?.removeAttribute('disabled');
      syncValueCardUI(planId ?? '');
    }
  });
}

function updateValueCardSummary() {
  // This summary is used by the legacy "quantity mode" UI and by newer flows.
  // Treat DOM hooks as optional so modern punch-card UI can call this safely.
  if (!state.valueCardQuantities) return;

  const totalQuantity = Array.from(state.valueCardQuantities.values()).reduce(
    (sum, qty) => sum + qty,
    0,
  );
  // IMPORTANT: We cannot reliably know how many punches each card contains from the API.
  // Some punch cards have different numbers of punches, so instead of assuming a fixed
  // multiplier (e.g. 10 punches per card), we show the number of cards selected.
  const totalCards = totalQuantity;
  const entryLabel = totalCards === 1 ? 'card' : 'cards';

  if (DOM.valueCardPunches) {
    DOM.valueCardPunches.textContent = totalCards.toString();
  }
  if (DOM.valueCardContinueBtn) {
    DOM.valueCardContinueBtn.disabled = totalQuantity <= 0;
    DOM.valueCardContinueBtn.setAttribute('aria-label', `Continue with ${totalCards} ${entryLabel}`);
  }
  if (DOM.valueCardEntryLabel) {
    DOM.valueCardEntryLabel.textContent = entryLabel;
  }
}

function handleValueCardContinue() {
  const hasSelection = Array.from(state.valueCardQuantities.values()).some((qty) => qty > 0);

  if (!hasSelection) {
    showToast('Select a value card quantity before continuing.', 'error');
    return;
  }

  nextStep();
}

function updateAddonSkipButton() {
  if (!DOM.skipAddonsBtn) return;

  const hasAddonsSelected = state.addonIds.size > 0;
  DOM.skipAddonsBtn.textContent = hasAddonsSelected ? 'Continue' : 'Skip';
}

function handleAddonContinue() {
  // Whether skipping or continuing after picking add-ons, we proceed to step 4
  nextStep();
}

function handleEditCart() {
  // Jump back to step 2 (plan selection)
  state.currentStep = 2;
  showStep(state.currentStep);
  updateStepIndicator();
  updateNavigationButtons();
  updateMainSubtitle();
}

async function handleChangeActivationDate() {
  // Open the same activation modal in-place (no step navigation)
  const nowRadio = document.getElementById('activationDateNow');
  const pickRadio = document.getElementById('activationDatePick');
  const pickerWrap = document.getElementById('activationDatePickerWrap');
  const dateInput = document.getElementById('activationDateInput');

  // Ensure the UI reflects current state before opening
  if (state.subscriptionStartDate) {
    if (pickRadio) pickRadio.checked = true;
    if (pickerWrap) pickerWrap.hidden = false;
    if (dateInput) {
      const todayStr = getTodayLocalDateString();
      dateInput.min = todayStr;
      dateInput.value = state.subscriptionStartDate < todayStr ? todayStr : state.subscriptionStartDate;
      state.subscriptionStartDate = dateInput.value;
    }
  } else {
    if (nowRadio) nowRadio.checked = true;
    if (pickerWrap) pickerWrap.hidden = true;
  }

  openActivationDateModal();
}

function handlePaymentChange(event) {
  const selected = event.target;
  state.paymentMethod = selected.value;
  updateCheckoutButton();

  DOM.paymentOptions.forEach((option) => {
    const parent = option.closest('.payment-option');
    if (parent) parent.classList.toggle('selected', option.checked);
  });

  // Card payment form should NEVER be shown - users are redirected to payment provider
  // Hide the form if it exists (it shouldn't be used)
  if (DOM.cardPaymentForm) {
    DOM.cardPaymentForm.style.display = 'none';
  }
}

// Postal code auto-fill functionality
let postalCodeLookupTimers = {
  customer: null,
  parent: null,
};

function setupPostalCodeAutoFill() {
  const referenceAPI = new ReferenceDataAPI();
  
  // Setup for customer postal code field
  if (DOM.postalCode && DOM.city) {
    DOM.postalCode.addEventListener('input', (e) => {
      const postalCode = e.target.value.trim();
      
      // Clear existing timer
      if (postalCodeLookupTimers.customer) {
        clearTimeout(postalCodeLookupTimers.customer);
      }
      
      // Clear city field if postal code is empty and revert to readonly
      if (!postalCode || postalCode.length === 0) {
        if (DOM.city) {
          DOM.city.value = '';
          DOM.city.setAttribute('readonly', 'readonly');
          DOM.city.removeAttribute('placeholder');
          DOM.city.style.opacity = '1';
          DOM.city.style.cursor = 'default';
        }
        return;
      }
      
      // Debounce: wait 500ms after user stops typing
      postalCodeLookupTimers.customer = setTimeout(async () => {
        // Check if postal code is valid format (4 digits for Danish postal codes)
        if (/^\d{4}$/.test(postalCode)) {
          try {
            // Show loading state
            if (DOM.city) {
              DOM.city.value = 'Loading...';
              DOM.city.style.opacity = '0.6';
            }
            
            const result = await referenceAPI.lookupCityByPostalCode(postalCode);
            
            if (result && typeof result === 'object' && result.unavailable) {
              // API endpoint not available - make city field editable
              if (DOM.city) {
                DOM.city.removeAttribute('readonly');
                DOM.city.placeholder = 'Enter city name';
                DOM.city.value = '';
                DOM.city.style.opacity = '1';
                DOM.city.style.cursor = 'text';
                console.log('[PostalCode] API unavailable - city field is now editable');
              }
            } else if (result && typeof result === 'string') {
              // City found - auto-fill and set readonly
              if (DOM.city) {
                DOM.city.value = result;
                DOM.city.setAttribute('readonly', 'readonly');
                DOM.city.removeAttribute('placeholder');
                DOM.city.style.opacity = '1';
                DOM.city.style.cursor = 'default';
                console.log('[PostalCode] Auto-filled city:', result, 'for postal code:', postalCode);
              }
            } else {
              // City not found - make field editable so user can enter city manually
              if (DOM.city) {
                DOM.city.value = '';
                DOM.city.removeAttribute('readonly');
                DOM.city.placeholder = 'Enter city name';
                DOM.city.style.opacity = '1';
                DOM.city.style.cursor = 'text';
                console.log('[PostalCode] No city found for postal code:', postalCode, '- city field is now editable');
              }
            }
          } catch (error) {
            console.error('[PostalCode] Error looking up city:', error);
            if (DOM.city) {
              DOM.city.value = '';
              DOM.city.setAttribute('readonly', 'readonly');
              DOM.city.removeAttribute('placeholder');
              DOM.city.style.opacity = '1';
              DOM.city.style.cursor = 'default';
            }
          }
        } else {
          // Invalid format - clear city field and revert to readonly
          if (DOM.city) {
            DOM.city.value = '';
            DOM.city.setAttribute('readonly', 'readonly');
            DOM.city.removeAttribute('placeholder');
            DOM.city.style.opacity = '1';
            DOM.city.style.cursor = 'default';
          }
        }
      }, 500);
    });
    
    // Also handle blur event for immediate lookup when user leaves field
    DOM.postalCode.addEventListener('blur', async (e) => {
      const postalCode = e.target.value.trim();
      
      // Clear any pending timer
      if (postalCodeLookupTimers.customer) {
        clearTimeout(postalCodeLookupTimers.customer);
        postalCodeLookupTimers.customer = null;
      }
      
      // Only lookup if postal code is valid and city is empty
      if (postalCode && /^\d{4}$/.test(postalCode) && (!DOM.city || !DOM.city.value || DOM.city.value === 'Loading...')) {
        try {
          if (DOM.city) {
            DOM.city.value = 'Loading...';
            DOM.city.style.opacity = '0.6';
          }
          
          const result = await referenceAPI.lookupCityByPostalCode(postalCode);
          
          if (result && typeof result === 'object' && result.unavailable) {
            // API endpoint not available - make city field editable
            if (DOM.city) {
              DOM.city.removeAttribute('readonly');
              DOM.city.placeholder = 'Enter city name';
              DOM.city.value = '';
              DOM.city.style.opacity = '1';
              DOM.city.style.cursor = 'text';
            }
          } else if (result && typeof result === 'string') {
            // City found - auto-fill
            if (DOM.city) {
              DOM.city.value = result;
              DOM.city.style.opacity = '1';
            }
          } else if (DOM.city) {
            DOM.city.value = '';
            DOM.city.style.opacity = '1';
          }
        } catch (error) {
          console.error('[PostalCode] Error looking up city on blur:', error);
          if (DOM.city) {
            DOM.city.value = '';
            DOM.city.style.opacity = '1';
          }
        }
      }
    });
  }
  
  // Setup for parent/guardian postal code field
  if (DOM.parentPostalCode && DOM.parentCity) {
    DOM.parentPostalCode.addEventListener('input', (e) => {
      const postalCode = e.target.value.trim();
      
      // Clear existing timer
      if (postalCodeLookupTimers.parent) {
        clearTimeout(postalCodeLookupTimers.parent);
      }
      
      // Clear city field if postal code is empty and revert to readonly
      if (!postalCode || postalCode.length === 0) {
        if (DOM.parentCity) {
          DOM.parentCity.value = '';
          DOM.parentCity.setAttribute('readonly', 'readonly');
          DOM.parentCity.removeAttribute('placeholder');
          DOM.parentCity.style.opacity = '1';
          DOM.parentCity.style.cursor = 'default';
        }
        return;
      }
      
      // Debounce: wait 500ms after user stops typing
      postalCodeLookupTimers.parent = setTimeout(async () => {
        // Check if postal code is valid format (4 digits for Danish postal codes)
        if (/^\d{4}$/.test(postalCode)) {
          try {
            // Show loading state
            if (DOM.parentCity) {
              DOM.parentCity.value = 'Loading...';
              DOM.parentCity.style.opacity = '0.6';
            }
            
            const result = await referenceAPI.lookupCityByPostalCode(postalCode);
            
            if (result && typeof result === 'object' && result.unavailable) {
              // API endpoint not available - make city field editable
              if (DOM.parentCity) {
                DOM.parentCity.removeAttribute('readonly');
                DOM.parentCity.placeholder = 'Enter city name';
                DOM.parentCity.value = '';
                DOM.parentCity.style.opacity = '1';
                DOM.parentCity.style.cursor = 'text';
                console.log('[PostalCode] API unavailable - parent city field is now editable');
              }
            } else if (result && typeof result === 'string') {
              // City found - auto-fill and set readonly
              if (DOM.parentCity) {
                DOM.parentCity.value = result;
                DOM.parentCity.setAttribute('readonly', 'readonly');
                DOM.parentCity.removeAttribute('placeholder');
                DOM.parentCity.style.opacity = '1';
                DOM.parentCity.style.cursor = 'default';
                console.log('[PostalCode] Auto-filled parent city:', result, 'for postal code:', postalCode);
              }
            } else {
              // City not found - make field editable so user can enter city manually
              if (DOM.parentCity) {
                DOM.parentCity.value = '';
                DOM.parentCity.removeAttribute('readonly');
                DOM.parentCity.placeholder = 'Enter city name';
                DOM.parentCity.style.opacity = '1';
                DOM.parentCity.style.cursor = 'text';
                console.log('[PostalCode] No city found for parent postal code:', postalCode, '- city field is now editable');
              }
            }
          } catch (error) {
            console.error('[PostalCode] Error looking up parent city:', error);
            if (DOM.parentCity) {
              DOM.parentCity.value = '';
              DOM.parentCity.setAttribute('readonly', 'readonly');
              DOM.parentCity.removeAttribute('placeholder');
              DOM.parentCity.style.opacity = '1';
              DOM.parentCity.style.cursor = 'default';
            }
          }
        } else {
          // Invalid format - clear city field and revert to readonly
          if (DOM.parentCity) {
            DOM.parentCity.value = '';
            DOM.parentCity.setAttribute('readonly', 'readonly');
            DOM.parentCity.removeAttribute('placeholder');
            DOM.parentCity.style.opacity = '1';
            DOM.parentCity.style.cursor = 'default';
          }
        }
      }, 500);
    });
    
    // Also handle blur event for immediate lookup when user leaves field
    DOM.parentPostalCode.addEventListener('blur', async (e) => {
      const postalCode = e.target.value.trim();
      
      // Clear any pending timer
      if (postalCodeLookupTimers.parent) {
        clearTimeout(postalCodeLookupTimers.parent);
        postalCodeLookupTimers.parent = null;
      }
      
      // Only lookup if postal code is valid and city is empty
      if (postalCode && /^\d{4}$/.test(postalCode) && (!DOM.parentCity || !DOM.parentCity.value || DOM.parentCity.value === 'Loading...')) {
        try {
          if (DOM.parentCity) {
            DOM.parentCity.value = 'Loading...';
            DOM.parentCity.style.opacity = '0.6';
          }
          
          const result = await referenceAPI.lookupCityByPostalCode(postalCode);
          
          if (result && typeof result === 'object' && result.unavailable) {
            // API endpoint not available - make city field editable
            if (DOM.parentCity) {
              DOM.parentCity.removeAttribute('readonly');
              DOM.parentCity.placeholder = 'Enter city name';
              DOM.parentCity.value = '';
              DOM.parentCity.style.opacity = '1';
              DOM.parentCity.style.cursor = 'text';
            }
          } else if (result && typeof result === 'string') {
            // City found - auto-fill and set readonly
            if (DOM.parentCity) {
              DOM.parentCity.value = result;
              DOM.parentCity.setAttribute('readonly', 'readonly');
              DOM.parentCity.removeAttribute('placeholder');
              DOM.parentCity.style.opacity = '1';
              DOM.parentCity.style.cursor = 'default';
            }
          } else {
            // City not found - make field editable so user can enter city manually
            if (DOM.parentCity) {
              DOM.parentCity.value = '';
              DOM.parentCity.removeAttribute('readonly');
              DOM.parentCity.placeholder = 'Enter city name';
              DOM.parentCity.style.opacity = '1';
              DOM.parentCity.style.cursor = 'text';
            }
          }
        } catch (error) {
          console.error('[PostalCode] Error looking up parent city on blur:', error);
          if (DOM.parentCity) {
            DOM.parentCity.value = '';
            DOM.parentCity.style.opacity = '1';
          }
        }
      }
    });
  }
}

async function handleApplyDiscount() {
  if (!DOM.discountInput) return;
  
  const discountCode = DOM.discountInput.value.trim().toUpperCase();
  
  if (!discountCode) {
    showDiscountError(t('cart.discount.empty'));
    return;
  }
  
  // Prevent duplicate requests
  if (DOM.applyDiscountBtn.disabled) return;
  
  // If order doesn't exist yet, try to create one so we can apply the coupon immediately
  // This allows the price to update right away when Apply is clicked
  // BUT: If we have fullOrder data, we might have an order ID we can use
  const existingOrderId = state.orderId || state.fullOrder?.id;
  
  if (!existingOrderId) {
    // Check if user has items selected (membership or value cards)
    const hasItems = state.membershipPlanId || (state.valueCardQuantities && Array.from(state.valueCardQuantities.values()).some(qty => qty > 0));
    
    if (hasItems) {
      // User has items - create order first, then apply coupon
      console.log('[Discount] No order exists, creating order to apply coupon...');
      beginApplyDiscountLoading(t('cart.discount.creatingOrder'));
      clearDiscountMessage();
      
      try {
        // Check prerequisites before creating order
        if (!state.customerId) {
          throw new Error('Please log in or create an account to apply a discount');
        }
        if (!state.selectedBusinessUnit) {
          throw new Error('Please select a gym location first');
        }
        
        // Create order first
        const ensuredOrderId = await ensureOrderCreated('discount-application');
        if (!ensuredOrderId) {
          // Provide more specific error message
          let errorMsg = 'Failed to create order for coupon application';
          if (!state.customerId) {
            errorMsg = 'Please log in or create an account to apply a discount';
          } else if (!state.selectedBusinessUnit) {
            errorMsg = 'Please select a gym location first';
          }
          throw new Error(errorMsg);
        }
        state.orderId = ensuredOrderId;
        console.log('[Discount] Order created:', state.orderId);
        
        // Now add product to order before applying coupon
        const isMembership = isCheckoutSubscriptionPlan();

        if (isMembership) {
          try {
            await ensureSubscriptionAttached('discount-application');
            console.log('[Discount] Membership ensured on order');
            
            // Refresh fullOrder data after subscription is attached
            try {
              const updatedOrder = await orderAPI.getOrder(state.orderId);
              state.fullOrder = updatedOrder;
            } catch (fetchError) {
              console.warn('[Discount] Could not refresh order data:', fetchError);
            }
          } catch (subError) {
            console.warn('[Discount] Could not attach membership to order:', subError);
            throw subError; // Don't continue if membership attachment fails
          }
        } else if (state.selectedProductType === 'punch-card' && state.valueCardQuantities && state.valueCardQuantities.size > 0) {
          // Add punch cards to order before applying coupon
          console.log('[Discount] Adding punch cards to order before applying coupon...');
          const orderAPI = new OrderAPI();
          
          for (const [planId, quantity] of state.valueCardQuantities.entries()) {
            if (quantity > 0) {
              try {
                // Extract numeric product ID from "punch-43" format
                const numericProductId = typeof planId === 'string' && planId.startsWith('punch-')
                  ? parseInt(planId.replace('punch-', ''), 10)
                  : planId;
                
                // API doesn't accept quantity in payload - call API once per quantity
                for (let i = 0; i < quantity; i++) {
                  await orderAPI.addValueCardItem(state.orderId, numericProductId, 1);
                  console.log(`[Discount] ✅ Value card added: ${planId} (productId: ${numericProductId}) [${i + 1}/${quantity}]`);
                }
              } catch (error) {
                console.error(`[Discount] ❌ Failed to add value card ${planId}:`, error);
                throw new Error(`Failed to add punch card to order: ${error.message}`);
              }
            }
          }
          
          // Refresh fullOrder data after punch cards are added
          try {
            const updatedOrder = await orderAPI.getOrder(state.orderId);
            state.fullOrder = updatedOrder;
            console.log('[Discount] Order refreshed after adding punch cards');
          } catch (fetchError) {
            console.warn('[Discount] Could not refresh order data:', fetchError);
          }
        }
        
        // Continue to apply coupon below (don't return)
        beginApplyDiscountLoading(t('cart.discount.applying'));
      } catch (orderError) {
        console.error('[Discount] Failed to create order for coupon:', orderError);
        // Show specific error message
        const errorMsg = orderError.message || 'Failed to create order. Please try again.';
        
        showDiscountError(resolveDiscountApplyErrorMessage(errorMsg));
        endApplyDiscountLoading();
        return;
      }
    } else {
      showDiscountError(t('cart.discount.selectProduct'));
      return;
    }
  }
  
  // Order exists - apply coupon immediately
  // Use existingOrderId if state.orderId wasn't set but fullOrder has an ID
  let orderIdToUse = state.orderId || existingOrderId;
  
  if (!orderIdToUse) {
    console.error('[Discount] No order ID available for discount application');
    showDiscountError(t('cart.discount.noOrder'));
    endApplyDiscountLoading();
    return;
  }
  
  beginApplyDiscountLoading(t('cart.discount.applying'));
  clearDiscountMessage();
  
  // Ensure state.orderId is set for consistency
  if (!state.orderId && orderIdToUse) {
    state.orderId = orderIdToUse;
  }
  
  try {
    const orderAPI = new OrderAPI();

    // Ensure the order has the selected items before applying coupon
    if (!state.customerId) {
      throw new Error('Please log in or create an account to apply a discount');
    }

    let orderSnapshot = state.fullOrder;
    if (!orderSnapshot || String(orderSnapshot.id) !== String(orderIdToUse)) {
      try {
        orderSnapshot = await orderAPI.getOrder(orderIdToUse);
        state.fullOrder = orderSnapshot;
      } catch (orderFetchError) {
        if (orderFetchError?.status === 403 || orderFetchError?.status === 404) {
          console.warn('[Discount] Existing order is not accessible. Creating a new order.');
          state.orderId = null;
          state.subscriptionAttachedOrderId = null;
          const ensuredOrderId = await ensureOrderCreated('discount-application');
          if (!ensuredOrderId) {
            throw new Error('Failed to create order for coupon application');
          }
          orderIdToUse = ensuredOrderId;
          orderSnapshot = await orderAPI.getOrder(orderIdToUse);
          state.fullOrder = orderSnapshot;
        } else {
          throw orderFetchError;
        }
      }
    }

    const isMembership = isCheckoutSubscriptionPlan();

    if (isMembership) {
      const subscriptionItems = orderSnapshot?.subscriptionItems || [];
      if (!subscriptionItems.length || state.subscriptionAttachedOrderId !== orderIdToUse) {
        await ensureSubscriptionAttached('discount-application');
        orderSnapshot = await orderAPI.getOrder(orderIdToUse);
        state.fullOrder = orderSnapshot;
      }
    } else if (state.selectedProductType === 'punch-card' && state.valueCardQuantities && state.valueCardQuantities.size > 0) {
      const existingValueCardItems = orderSnapshot?.valueCardItems || [];
      for (const [planId, quantity] of state.valueCardQuantities.entries()) {
        if (quantity > 0) {
          const numericProductId = typeof planId === 'string' && planId.startsWith('punch-')
            ? parseInt(planId.replace('punch-', ''), 10)
            : planId;
          const existingCount = existingValueCardItems.filter(item => item.product?.id === numericProductId).length;
          const toAdd = Math.max(0, quantity - existingCount);
          for (let i = 0; i < toAdd; i += 1) {
            await orderAPI.addValueCardItem(orderIdToUse, numericProductId, 1);
          }
        }
      }
      orderSnapshot = await orderAPI.getOrder(orderIdToUse);
      state.fullOrder = orderSnapshot;
    }

    console.log('[Discount] Applying discount code:', discountCode, 'to order:', orderIdToUse);
    const response = await orderAPI.applyDiscountCode(orderIdToUse, discountCode);
    console.log('[Discount] API response received:', response);

    if (!state.totals.subtotal || state.totals.subtotal === 0) {
      updateCartSummary();
    }

    const subtotal = state.totals.subtotal || state.totals.cartTotal || 0;
    let discountAmount = calculateOrderCouponDiscountKr(response, subtotal);
    if (discountAmount <= 0 && response?.discountAmount != null) {
      discountAmount = extractCurrencyOutKr(response.discountAmount);
    }
    discountAmount = capCouponDiscountKr(discountAmount, subtotal);
    const couponDiscount = response?.couponDiscount || response?.price?.couponDiscount;
    
    if (discountAmount > 0) {
      // Success - apply discount
      state.discountCode = discountCode;
      state.discountApplied = true;
      state.totals.discountAmount = roundToHalfKrone(discountAmount);
      
      // CRITICAL: Update fullOrder with discounted prices from API response
      // This ensures payment overview shows the discounted prices
      if (response && !state.fullOrder) {
        state.fullOrder = response;
      } else if (response && state.fullOrder) {
        // Merge updated price data into existing fullOrder
        state.fullOrder.price = response.price || state.fullOrder.price;
        state.fullOrder.couponDiscount = response.couponDiscount || state.fullOrder.couponDiscount;
        if (response.subscriptionItems?.length) {
          state.fullOrder.subscriptionItems = response.subscriptionItems;
        }
        if (response.valueCardItems?.length) {
          state.fullOrder.valueCardItems = response.valueCardItems;
        }
        if (response.articleItems?.length) {
          state.fullOrder.articleItems = response.articleItems;
        }
      }

      const syncedDiscount = getOrderCouponDiscountKr();
      if (syncedDiscount > 0) {
        state.totals.discountAmount = syncedDiscount;
      }
      
      console.log('[Discount] Applying discount:', {
        discountCode,
        discountAmount,
        subtotal,
        finalTotal: subtotal - discountAmount,
        orderPrice: state.fullOrder?.price,
      });
      
      // CRITICAL: Update cart totals using API-based function - this recalculates everything
      // updateCartSummary() will calculate subtotal and cart total, and render the UI
      updateCartSummary();
      
      // Cart total element removed - payment overview shows calculated prices instead
      
      // Force update discount display
      updateDiscountDisplay();
      
      // CRITICAL: Update payment overview to show discounted prices
      updatePaymentOverview();
      
      // Calculate new total for display
      const newTotal = state.totals.cartTotal || (subtotal - discountAmount);
      
      // Update all cart total elements and highlight them
      const allCartTotals = document.querySelectorAll('[data-summary-field="cart-total"], .cart-total .total-amount, .total-amount[data-summary-field="cart-total"], [data-summary-field="order-total"], [data-summary-field="pay-now"], [data-summary-field="monthly-payment"]');
      allCartTotals.forEach(el => {
        // Only update text for cart total elements, payment overview is handled by updatePaymentOverview
        if (el.hasAttribute('data-summary-field') && 
            (el.getAttribute('data-summary-field') === 'cart-total' || 
             el.getAttribute('data-summary-field') === 'order-total')) {
          const expectedTotal = formatCurrencyHalfKrone(state.totals.cartTotal);
          el.textContent = expectedTotal;
        }
        // Highlight the new price
        el.classList.add('price-updated');
        setTimeout(() => {
          el.classList.remove('price-updated');
        }, 2000);
      });
      
      // Double-check the display was updated
      if (DOM.cartTotal) {
        const displayedTotal = DOM.cartTotal.textContent;
        const expectedTotal = formatCurrencyHalfKrone(state.totals.cartTotal);
        console.log('[Discount] Cart total display check:', {
          displayed: displayedTotal,
          expected: expectedTotal,
          match: displayedTotal === expectedTotal,
          subtotal: state.totals.subtotal,
          discountAmount: state.totals.discountAmount,
          cartTotal: state.totals.cartTotal,
        });
        if (displayedTotal !== expectedTotal) {
          console.warn('[Discount] Cart total mismatch, forcing update');
          DOM.cartTotal.textContent = expectedTotal;
          DOM.cartTotal.classList.add('price-updated');
          setTimeout(() => {
            DOM.cartTotal.classList.remove('price-updated');
          }, 2000);
        }
      } else {
        console.error('[Discount] DOM.cartTotal element not found after applying discount!');
      }
      
      // Get updated payment amounts for success message
      const payNowElement = document.querySelector('[data-summary-field="pay-now"]');
      const monthlyPaymentElement = document.querySelector('[data-summary-field="monthly-payment"]');
      const payNowText = payNowElement ? payNowElement.textContent : '';
      const monthlyPaymentText = monthlyPaymentElement ? monthlyPaymentElement.textContent : '';
      
      // Show succinct success message
      showDiscountMessage(t('cart.discount.applied', 'Discount code applied successfully!'), 'success');
      
      // Force a visual update by triggering a reflow
      if (DOM.cartTotal) {
        DOM.cartTotal.offsetHeight; // Trigger reflow
      }
      
      updateDiscountFormControls();
    } else {
      // Check if coupon was actually applied to the order (even if discountAmount is 0)
      // The API might return success but with 0 discount (e.g., for future use coupons)
      const couponDiscount = response?.couponDiscount || response?.price?.couponDiscount;
      if (couponDiscount !== undefined && couponDiscount !== null) {
        // Coupon was applied, even if discount is 0
        state.discountCode = discountCode;
        state.discountApplied = true;
        state.totals.discountAmount = 0; // Set to 0 if that's what the API returned
        
        // Update fullOrder with response to ensure payment overview uses latest data
        if (response && !state.fullOrder) {
          state.fullOrder = response;
        } else if (response && state.fullOrder) {
          state.fullOrder.price = response.price || state.fullOrder.price;
          state.fullOrder.couponDiscount = response.couponDiscount || state.fullOrder.couponDiscount;
        }
        
        updateCartSummary(); // updateCartSummary() already calls renderCartTotal()
        updatePaymentOverview(); // Update payment overview with discounted prices
        
        const newTotal = state.totals.cartTotal || state.totals.subtotal || 0;
        showDiscountMessage(t('cart.discount.applied', 'Discount code applied successfully!'), 'success');
        
        // Highlight the new price in cart total elements and payment overview
        const cartTotalElements = document.querySelectorAll('[data-summary-field="cart-total"], .cart-total .total-amount, .total-amount[data-summary-field="cart-total"], [data-summary-field="order-total"], [data-summary-field="pay-now"], [data-summary-field="monthly-payment"]');
        cartTotalElements.forEach(el => {
          el.classList.add('price-updated');
          setTimeout(() => {
            el.classList.remove('price-updated');
          }, 2000);
        });
        
        updateDiscountFormControls();
      } else {
        throw new Error('Invalid coupon code or no discount applied');
      }
    }
  } catch (error) {
    console.error('[Discount] Error applying coupon:', error);
    
    // Check if this is a stack overflow or recursion error - don't reset state in that case
    const errorMessage = error.message || String(error);
    if (errorMessage.includes('Maximum call stack') || errorMessage.includes('stack size exceeded')) {
      console.error('[Discount] Stack overflow detected - this is a code bug, not a coupon error');
      // Don't reset discount state - coupon might have been applied
      // Just show a generic error
      showDiscountMessage('An error occurred while applying the coupon. Please refresh the page.', 'error');
      endApplyDiscountLoading();
      return; // Exit early to avoid resetting state
    }
    
    resetLocalDiscountState({ preserveInput: true });
    updateCartSummary();
    updateDiscountDisplay();
    
    showDiscountError(resolveDiscountApplyErrorMessage(errorMessage));
  } finally {
    endApplyDiscountLoading();
    updateDiscountFormControls();
  }
}

async function handleRemoveDiscount() {
  if (!state.discountApplied || !state.discountCode) return;
  if (DOM.removeDiscountBtn?.disabled) return;

  const discountCode = state.discountCode;
  const orderId = state.orderId || state.fullOrder?.id;

  if (DOM.removeDiscountBtn) {
    DOM.removeDiscountBtn.disabled = true;
    DOM.removeDiscountBtn.textContent = '...';
  }

  try {
    if (orderId) {
      const updatedOrder = await orderAPI.removeDiscountCode(orderId, discountCode);
      state.fullOrder = updatedOrder;
    }
    resetLocalDiscountState();
    clearDiscountMessage();
    updateCartSummary();
    updateDiscountDisplay();
    updatePaymentOverview();
    showDiscountMessage(t('cart.discount.removed', 'Discount code removed.'), 'info');
  } catch (error) {
    console.error('[Discount] Error removing coupon:', error);
    showDiscountMessage(t('cart.discount.removeFailed'), 'error');
  } finally {
    updateDiscountFormControls();
  }
}

let discountMessageDismissTimer = null;

function dismissDiscountMessage(messageEl) {
  if (!messageEl?.isConnected) return;
  messageEl.classList.add('discount-message-dismiss');
  discountMessageDismissTimer = window.setTimeout(() => {
    messageEl.remove();
    discountMessageDismissTimer = null;
  }, 300);
}

function showDiscountMessage(message, type = 'info') {
  clearDiscountMessage();

  const messageEl = document.createElement('div');
  messageEl.className = `discount-message discount-message-${type}`;
  messageEl.textContent = message;
  if (type === 'error') {
    messageEl.setAttribute('role', 'alert');
    messageEl.setAttribute('aria-live', 'polite');
  }

  if (DOM.discountForm) {
    DOM.discountForm.insertAdjacentElement('afterend', messageEl);
  }

  // Auto-dismiss transient feedback after 5 seconds
  if (type === 'success' || type === 'info' || type === 'error') {
    discountMessageDismissTimer = window.setTimeout(() => {
      dismissDiscountMessage(messageEl);
    }, 5000);
  }
}

function clearDiscountMessage() {
  if (discountMessageDismissTimer) {
    window.clearTimeout(discountMessageDismissTimer);
    discountMessageDismissTimer = null;
  }
  const existingMessage = document.querySelector('.discount-message');
  if (existingMessage) {
    existingMessage.remove();
  }
}

function handleSameAddressToggle(event) {
  if (event.target.checked) {
    copyAddressAndContactInfo();
  } else {
    clearParentFormFields();
  }
}

function handleParentGuardianToggle(event) {
  if (!DOM.parentGuardianForm) return;
  const isChecked = event.target.checked;

  DOM.parentGuardianForm.style.display = isChecked ? 'block' : 'none';
  if (DOM.parentGuardianReminder) {
    DOM.parentGuardianReminder.hidden = !isChecked;
  }

  clearParentFormFields();
}

function copyAddressAndContactInfo() {
  const mappings = [
    ['streetAddress', 'parentStreetAddress'],
    ['postalCode', 'parentPostalCode'],
    ['city', 'parentCity'],
    ['email', 'parentEmail'],
    ['countryCode', 'parentCountryCode'],
    ['phoneNumber', 'parentPhoneNumber'],
  ];

  mappings.forEach(([sourceId, targetId]) => {
    const source = document.getElementById(sourceId);
    const target = document.getElementById(targetId);
    if (!source || !target) return;
    target.value = source.value;
    target.readOnly = true;
    if (target.tagName === 'SELECT') {
      target.value = source.value;
      target.disabled = true;
    }
    target.classList.add('readonly-field');
  });
}

function clearParentFormFields() {
  const parentFields = [
    'parentStreetAddress',
    'parentPostalCode',
    'parentCity',
    'parentEmail',
    'parentCountryCode',
    'parentPhoneNumber',
  ];

  parentFields.forEach((fieldId) => {
    const field = document.getElementById(fieldId);
    if (!field) return;
    field.value = '';
    field.readOnly = false;
    field.disabled = false;
    field.classList.remove('readonly-field');
  });
}

// Helper function to check if cart contains a campaign
function hasCampaignInCart() {
  // Check if membershipPlanId starts with "campaign-"
  if (state.membershipPlanId && String(state.membershipPlanId).startsWith('campaign-')) {
    return true;
  }
  
  // Check if any cart item's productId exists in campaign subscriptions or campaign value cards
  const isCampaignProduct = (id) => {
    const num = typeof id === 'string' ? parseInt(id, 10) : id;
    const inSubs = state.campaignSubscriptions?.some(c => c.id === id || c.id === num || String(c.id) === String(id));
    const inCards = state.campaignValueCards?.some(c => c.id === id || c.id === num || String(c.id) === String(id));
    return inSubs || inCards;
  };
  if (state.cartItems && state.cartItems.length > 0) {
    if (state.cartItems.some(item => item.productId && isCampaignProduct(item.productId))) return true;
  }

  if (state.selectedProductId && isCampaignProduct(state.selectedProductId)) return true;

  return false;
}

function customerHasMembershipCampaignBlockRequirement() {
  if (!isUserAuthenticated()) return false;

  const subscriptions = state.authenticatedCustomer?.subscriptions;
  if (!Array.isArray(subscriptions) || subscriptions.length === 0) return false;

  return subscriptions.some((sub) => {
    const statusCodes = Array.isArray(sub?.statuses)
      ? sub.statuses.map((s) => String(s?.code || '').toUpperCase())
      : [];
    if (statusCodes.includes('TERMINATED')) return false;

    const productLabels =
      sub?.subscriptionProduct?.productLabels ||
      sub?.product?.productLabels ||
      sub?.productLabels ||
      [];

    return Array.isArray(productLabels) && productLabels.some((label) => {
      const name = String(label?.name || '').toLowerCase();
      return name.includes('medlem') || name.includes('member');
    });
  });
}

async function refreshAuthenticatedCustomerForCampaignGuard() {
  if (!isUserAuthenticated()) return;

  if (campaignGuardCustomerRefreshPromise) {
    await campaignGuardCustomerRefreshPromise;
    return;
  }

  campaignGuardCustomerRefreshPromise = (async () => {
    const metadata = getTokenMetadata();
    const resolvedCustomerId = state.customerId || metadata?.username || metadata?.userName;
    if (!resolvedCustomerId) return;

    try {
      const customerData = await authAPI.getCustomer(String(resolvedCustomerId));
      state.customerId = String(resolvedCustomerId);
      state.authenticatedCustomer = customerData;
    } catch (error) {
      console.warn('[Campaign Guard] Could not refresh customer profile before eligibility check:', error);
    }
  })();

  try {
    await campaignGuardCustomerRefreshPromise;
  } finally {
    campaignGuardCustomerRefreshPromise = null;
  }
}

// Show campaign warning banner
function showCampaignWarning() {
  const banner = document.getElementById('campaignWarningBanner');
  if (banner) {
    banner.style.display = 'flex';
  }
}

// Hide campaign warning banner
function hideCampaignWarning() {
  const banner = document.getElementById('campaignWarningBanner');
  if (banner) {
    banner.style.display = 'none';
  }
}

function updateCartSummary() {
  // CRITICAL: Freeze UI during checkout to prevent price changes before redirect
  if (state.checkoutInProgress) {
    console.log('[Cart Summary] ⏸️ Checkout in progress - skipping cart summary update to freeze prices');
    return;
  }
  
  const items = [];
  state.totals.membershipMonthly = 0;

  // Step 5: Use API data for cart items
  // Handle membership selection
  if (state.selectedProductType === 'membership' && state.selectedProductId) {
    // Try to find membership by ID - handle both numeric and string comparisons
    const productIdNum = typeof state.selectedProductId === 'string' 
      ? parseInt(state.selectedProductId) 
      : state.selectedProductId;
    
    // Check all subscription types: campaign, membership, and 15-Day Trial Pass
    const allSubscriptions = [
      ...(state.campaignSubscriptions || []),
      ...(state.subscriptions || []),
      ...(state.dayPassSubscriptions || [])
    ];
    const membership = allSubscriptions.find(p => 
      p.id === state.selectedProductId || 
      p.id === productIdNum ||
      String(p.id) === String(state.selectedProductId)
    );
    
    if (membership) {
      // Extract price from API structure (cents to DKK)
      // The API uses priceWithInterval, not price directly
      // priceWithInterval contains: { interval: { numberOf: 1, unit: "MONTH" }, price: { amount: 46900, currency: "DKK" } }
      const priceInCents = membership.priceWithInterval?.price?.amount || 
                           membership.price?.amount || 
                           membership.amount || 
                           membership.monthlyPrice ||
                           0;
      const price = priceInCents > 0 ? priceInCents / 100 : 0;
      
      // Get externalDescription if available
      const externalDesc = membership.externalDescription || '';
      const displayName = externalDesc 
        ? `${membership.name || 'Membership'} - ${externalDesc}`
        : membership.name || 'Membership';
      
      items.push({
        id: membership.id,
        name: displayName,
        amount: price,
        type: 'membership',
        productId: membership.id, // Store API product ID for order creation
        externalDescription: externalDesc, // Store separately for rendering (renamed from imageBannerText)
        description: membership.externalDescription || membership.description || '',
        productName: membership.name || 'Membership', // Store original name
      });
      state.totals.membershipMonthly = price;
    } else {
      console.warn('Cart: Membership not found', {
        selectedProductId: state.selectedProductId,
        selectedProductType: state.selectedProductType,
        availableSubscriptions: allSubscriptions.map(s => ({ id: s.id, name: s.name }))
      });
    }
  }

  // Handle punch card selection
  if (state.selectedProductType === 'punch-card' && state.selectedProductId) {
    state.valueCardQuantities.forEach((quantity, planId) => {
      if (quantity <= 0) return;
      
      // Find the product by ID - check both the stored productId and planId format
      const productId = state.selectedProductId || planId.replace('punch-', '');
      
      // Check both regular and campaign value cards
      const allValueCards = [
        ...(state.valueCards || []),
        ...(state.campaignValueCards || [])
      ];
      
      const valueCard = allValueCards.find(p => 
        p.id === productId || 
        p.id === parseInt(productId) ||
        planId.includes(String(p.id))
      );
      
      if (valueCard) {
        // Extract price from API structure (cents to DKK)
        const priceInCents = valueCard.price?.amount || valueCard.amount || 0;
        const price = priceInCents / 100;
        const punchesPerCard = resolveClipsPerCardForValueCardProduct(
          valueCard,
          state.fullOrder?.valueCardItems
        );
        const totalPunches = punchesPerCard != null ? quantity * punchesPerCard : null;
        const productName = valueCard.name || 'Punch Card';
        // Name only on left; ×N and total punches shown on the right
        items.push({
          id: valueCard.id,
          name: productName,
          amount: roundToHalfKrone(price * quantity),
          type: 'value-card',
          quantity: quantity,
          productId: valueCard.id, // Store API product ID for order creation
          description: valueCard.externalDescription || valueCard.description || '',
          punchesPerCard: punchesPerCard ?? undefined,
          totalPunches: totalPunches ?? undefined,
        });
      }
    });
  }

  // Handle add-ons (boost products and subscription additions)
  // Store addons separately to append after subscriptions
  const addonItems = [];
  state.addonIds.forEach((addonId) => {
    const addon = findAddon(addonId);
    if (!addon) return;
    const price = getAddonPrice(addon);
    addonItems.push({
      id: addon.id,
      name: addon.name || 'Add-on',
      amount: roundToHalfKrone(price),
      type: 'addon',
      productId: addon.id, // Store API product ID for order creation
      description: addon.externalDescription || addon.description || addon.addition?.description || '',
    });
  });

  // Ensure correct order: subscriptions/value cards first, then addons
  // Items array already has subscriptions and value cards in correct order
  // Now append addons at the end
  items.push(...addonItems);

  // CRITICAL: Explicitly sort items to ensure subscriptions come first
  // This ensures the array order matches the display order
  const sortedItems = [
    ...items.filter(item => item.type === 'membership' || item.type === 'value-card'),
    ...items.filter(item => item.type === 'addon')
  ];

  // GTM: Track add_to_cart event when items are added
  const previousCartItemCount = state.cartItems?.length || 0;
  const newCartItemCount = sortedItems.length;
  
  state.cartItems = sortedItems;
  
  // Check for campaigns and show/hide warning banner
  if (hasCampaignInCart()) {
    showCampaignWarning();
  } else {
    hideCampaignWarning();
  }

  // Calculate subtotal (before discount) - round to half krone
  state.totals.subtotal = roundToHalfKrone(items.reduce((total, item) => total + item.amount, 0));
  
  // Calculate cart total (subtotal - discount) - round to half krone
  state.totals.cartTotal = roundToHalfKrone(Math.max(0, state.totals.subtotal - (state.totals.discountAmount || 0)));

  // Track add_to_cart if items were added (not just updated)
  if (newCartItemCount > previousCartItemCount && window.GTM && window.GTM.trackAddToCart) {
    try {
      // Only track newly added items
      const newItems = items.slice(previousCartItemCount);
      if (newItems.length > 0) {
        window.GTM.trackAddToCart(newItems, state.totals.cartTotal, 'DKK');
      }
    } catch (error) {
      console.warn('[GTM] Error tracking add_to_cart:', error);
    }
  }

  renderCartItems();
  renderCartTotal();
  
  // Update checkout button state when cart changes (addons might have been added/removed)
  updateCheckoutButton();
  
  // If we're on step 4 and have order data, ensure payment overview is updated
  if (state.currentStep === 4 && state.orderId && !state.fullOrder) {
    // Order exists but fullOrder not loaded - fetch it
    orderAPI.getOrder(state.orderId)
      .then(order => {
        state.fullOrder = order;
        updatePaymentOverview();
        console.log('[Cart Summary] Order data fetched and payment overview updated');
      })
      .catch(error => {
        console.warn('[Cart Summary] Could not fetch order data:', error);
      });
  } else if (state.currentStep === 4 && state.fullOrder) {
    // Full order data available - ensure payment overview is updated
    updatePaymentOverview();
  }
}

function updateCartTotals() {
  // CRITICAL: Freeze UI during checkout to prevent price changes before redirect
  if (state.checkoutInProgress) {
    console.log('[Cart Totals] ⏸️ Checkout in progress - skipping cart totals update to freeze prices');
    return;
  }
  
  // Recalculate totals including discount
  const items = [];
  
  // Add membership
  if (state.membershipPlanId) {
    const plan = findMembershipPlan(state.membershipPlanId);
    if (plan) {
    items.push({
      id: plan.id,
      name: plan.name,
      amount: roundToHalfKrone(plan.price),
      type: 'membership',
    });
    }
  }
  
  // Add value cards
  state.valueCardQuantities.forEach((quantity, planId) => {
    if (quantity > 0) {
      const plan = findValueCard(planId);
      if (plan) {
        items.push({
          id: plan.id,
          name: plan.name,
          amount: roundToHalfKrone(plan.price * quantity),
          type: 'value-card',
          quantity,
        });
      }
    }
  });
  
  // Add add-ons (boost products and subscription additions)
  // Store addons separately to append after subscriptions
  const addonItems = [];
  state.addonIds.forEach((addonId) => {
    const addon = findAddon(addonId);
    if (!addon) return;
    const price = getAddonPrice(addon);
    addonItems.push({
      id: addon.id,
      name: addon.name || 'Add-on',
      amount: roundToHalfKrone(price),
      type: 'addon',
    });
  });

  // Ensure correct order: subscriptions/value cards first, then addons
  // Items array already has subscriptions and value cards in correct order
  // Now append addons at the end
  items.push(...addonItems);

  // CRITICAL: Explicitly sort items to ensure subscriptions come first
  // This ensures the array order matches the display order
  const sortedItems = [
    ...items.filter(item => item.type === 'membership' || item.type === 'value-card'),
    ...items.filter(item => item.type === 'addon')
  ];

  state.cartItems = sortedItems;
  
  // Calculate subtotal (before discount) - round to half krone
  state.totals.subtotal = roundToHalfKrone(items.reduce((total, item) => total + item.amount, 0));
  
  // Calculate cart total (subtotal - discount) - round to half krone
  state.totals.cartTotal = roundToHalfKrone(Math.max(0, state.totals.subtotal - (state.totals.discountAmount || 0)));

  renderCartItems();
  renderCartTotal();
}

function renderCartItems() {
  // CRITICAL: Freeze UI during checkout to prevent price changes before redirect
  if (state.checkoutInProgress) {
    console.log('[Cart Items] ⏸️ Checkout in progress - skipping cart items render to freeze prices');
    return;
  }
  
  if (!templates.cartItem || !DOM.cartItems) return;
  
  // Check for campaigns and show/hide warning banner
  if (hasCampaignInCart()) {
    showCampaignWarning();
  } else {
    hideCampaignWarning();
  }

  if (!state.cartItems || !state.cartItems.length) {
    DOM.cartItems.innerHTML = '';
    if (DOM.cartAddonItems) {
      DOM.cartAddonItems.innerHTML = '';
      DOM.cartAddonItems.style.display = 'none';
    }
    // Only show empty message if there's no gym selected either
    if (!state.selectedGymId && !state.selectedBusinessUnit) {
      const empty = document.createElement('div');
      empty.className = 'cart-empty';
      empty.textContent = t('cart.empty');
      DOM.cartItems.appendChild(empty);
    }
    return;
  }
  
  // Clear DOM to ensure clean rendering order
  DOM.cartItems.innerHTML = '';

  // Helper function to create Home Gym info element
  function createHomeGymInfo(selectedGym) {
    const gymInfoContainer = document.createElement('div');
    gymInfoContainer.className = 'home-gym-info';
    
    const gymInfoText = document.createElement('span');
    gymInfoText.className = 'home-gym-text';
    gymInfoText.textContent = `${t('homeGym.label')} ${selectedGym.name}`;
    
    // Create info icon
    const infoIcon = document.createElement('button');
    infoIcon.className = 'home-gym-info-icon';
    infoIcon.setAttribute('type', 'button');
    infoIcon.setAttribute('aria-label', 'Information about home gym');
    infoIcon.setAttribute('aria-expanded', 'false');
    infoIcon.innerHTML = sanitizeHTML(`
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="12" cy="12" r="10"></circle>
        <line x1="12" y1="16" x2="12" y2="12"></line>
        <line x1="12" y1="8" x2="12.01" y2="8"></line>
      </svg>
    `);
    
    // Create wrapper for info icon and tooltip
    const infoWrapper = document.createElement('span');
    infoWrapper.className = 'home-gym-info-wrapper';
    
    // Create tooltip — firstclimb (/99kr) is a single-day ticket valid at any
    // Boulders, so only the headline is needed; skip the supporting paragraph.
    const isFirstClimb = isFirstClimbRoute();
    const tooltipTitle = t(isFirstClimb ? 'homeGym.tooltip.title.firstclimb' : 'homeGym.tooltip.title');
    const tooltipDesc = isFirstClimb ? '' : `<p>${t('homeGym.tooltip.desc')}</p>`;
    const tooltip = document.createElement('div');
    tooltip.className = 'home-gym-tooltip';
    tooltip.innerHTML = sanitizeHTML(`
      <div class="tooltip-content">
        <p><strong>${tooltipTitle}</strong></p>
        ${tooltipDesc}
      </div>
    `);
    
    const closeAllTooltipsExcept = () => {
      document.querySelectorAll('.home-gym-tooltip, .product-info-tooltip').forEach((node) => {
        if (node !== tooltip) node.classList.remove('show');
      });
    };

    const supportsHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    if (supportsHover) {
      infoWrapper.addEventListener('mouseenter', () => {
        closeAllTooltipsExcept();
        tooltip.classList.add('show');
        infoIcon.setAttribute('aria-expanded', 'true');
      });
      infoWrapper.addEventListener('mouseleave', () => {
        tooltip.classList.remove('show');
        infoIcon.setAttribute('aria-expanded', 'false');
      });
      infoWrapper.addEventListener('focusin', () => {
        closeAllTooltipsExcept();
        tooltip.classList.add('show');
        infoIcon.setAttribute('aria-expanded', 'true');
      });
      infoWrapper.addEventListener('focusout', () => {
        tooltip.classList.remove('show');
        infoIcon.setAttribute('aria-expanded', 'false');
      });
    } else {
      // Mobile/touch: toggle on tap
      infoIcon.addEventListener('click', (e) => {
        e.stopPropagation();
        closeAllTooltipsExcept();
        const wasVisible = tooltip.classList.contains('show');
        tooltip.classList.toggle('show');
        infoIcon.setAttribute('aria-expanded', tooltip.classList.contains('show') ? 'true' : 'false');
        
        if (!wasVisible && tooltip.classList.contains('show')) {
          setTimeout(() => {
            const outsideClickHandler = (event) => {
              if (!infoWrapper.contains(event.target)) {
                tooltip.classList.remove('show');
                infoIcon.setAttribute('aria-expanded', 'false');
                document.removeEventListener('click', outsideClickHandler);
              }
            };
            document.addEventListener('click', outsideClickHandler, { once: true });
          }, 0);
        }
      });
    }
    
    infoWrapper.appendChild(infoIcon);
    infoWrapper.appendChild(tooltip);
    gymInfoContainer.appendChild(gymInfoText);
    gymInfoContainer.appendChild(infoWrapper);
    
    return gymInfoContainer;
  }

  function createProductDescriptionInfo(descriptionText) {
    if (!descriptionText || !String(descriptionText).trim()) return null;

    const infoWrapper = document.createElement('span');
    infoWrapper.className = 'product-info-wrapper';

    const infoIcon = document.createElement('button');
    infoIcon.className = 'product-info-icon';
    infoIcon.setAttribute('type', 'button');
    infoIcon.setAttribute('aria-label', 'Show product description');
    infoIcon.innerHTML = sanitizeHTML(`
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="12" cy="12" r="10"></circle>
        <line x1="12" y1="16" x2="12" y2="12"></line>
        <line x1="12" y1="8" x2="12.01" y2="8"></line>
      </svg>
    `);

    const tooltip = document.createElement('div');
    tooltip.className = 'product-info-tooltip';
    const tooltipHtml = String(descriptionText)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/\n/g, '<br>');
    tooltip.innerHTML = sanitizeHTML(`
      <div class="tooltip-content">
        <p>${tooltipHtml}</p>
      </div>
    `);

    const closeAllTooltipsExcept = () => {
      document.querySelectorAll('.product-info-tooltip, .home-gym-tooltip').forEach((node) => {
        if (node !== tooltip) node.classList.remove('show');
      });
    };

    const syncExpandedState = () => {
      infoIcon.setAttribute('aria-expanded', tooltip.classList.contains('show') ? 'true' : 'false');
    };

    const positionTooltip = () => {
      const iconRect = infoIcon.getBoundingClientRect();
      const viewportPadding = 12;
      const tooltipWidth = Math.min(320, window.innerWidth - viewportPadding * 2);
      const desiredLeft = iconRect.left;
      const clampedLeft = Math.max(
        viewportPadding,
        Math.min(desiredLeft, window.innerWidth - tooltipWidth - viewportPadding)
      );
      const top = Math.max(8, iconRect.top - 12);
      tooltip.style.left = `${clampedLeft}px`;
      tooltip.style.top = `${top}px`;
      tooltip.style.setProperty('--tooltip-arrow-left', `${Math.max(10, iconRect.left - clampedLeft + (iconRect.width / 2))}px`);
    };

    const onViewportChange = () => {
      if (tooltip.classList.contains('show')) {
        positionTooltip();
      }
    };

    const bindViewportTracking = () => {
      window.addEventListener('scroll', onViewportChange, { passive: true });
      window.addEventListener('resize', onViewportChange);
    };

    const unbindViewportTracking = () => {
      window.removeEventListener('scroll', onViewportChange);
      window.removeEventListener('resize', onViewportChange);
    };

    const openTooltip = () => {
      closeAllTooltipsExcept();
      positionTooltip();
      tooltip.classList.add('show');
      syncExpandedState();
      bindViewportTracking();
    };

    const closeTooltip = () => {
      tooltip.classList.remove('show');
      syncExpandedState();
      unbindViewportTracking();
    };

    const supportsHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    if (supportsHover) {
      infoWrapper.addEventListener('mouseenter', () => {
        openTooltip();
      });
      infoWrapper.addEventListener('mouseleave', () => {
        closeTooltip();
      });
      infoWrapper.addEventListener('focusin', () => {
        openTooltip();
      });
      infoWrapper.addEventListener('focusout', () => {
        closeTooltip();
      });
    } else {
      infoIcon.addEventListener('click', (e) => {
        e.stopPropagation();
        const wasVisible = tooltip.classList.contains('show');
        if (wasVisible) {
          closeTooltip();
        } else {
          openTooltip();
          setTimeout(() => {
            const outsideClickHandler = (event) => {
              if (!infoWrapper.contains(event.target)) {
                closeTooltip();
                document.removeEventListener('click', outsideClickHandler);
              }
            };
            document.addEventListener('click', outsideClickHandler, { once: true });
          }, 0);
        }
      });
    }

    infoWrapper.appendChild(infoIcon);
    infoWrapper.appendChild(tooltip);
    return infoWrapper;
  }

  // Get selected gym info
  let selectedGym = null;
  if (state.selectedGymId || state.selectedBusinessUnit) {
    const gymId = state.selectedGymId || state.selectedBusinessUnit;
    selectedGym = gymsWithDistances.find(gym => 
      String(gym.id) === String(gymId)
    );
  }

  // Display order: (1) access type first (subscription / punch card / 15 day) with pricing,
  // (2) addon products, (3) total (payment overview is next sibling in DOM).
  // Separate items into two groups: subscriptions/15day pass/value cards first, then addons.
  // All subscriptions (including 15-day passes) have type 'membership', value cards have type 'value-card'.
  const subscriptionItems = state.cartItems.filter(item => 
    item.type === 'membership' || item.type === 'value-card'
  );
  const addonItems = state.cartItems.filter(item => item.type === 'addon');

  // Helper function to render a single cart item
  function renderCartItem(item, isFirstSubscriptionItem = false) {
    const cartItem = templates.cartItem.content.firstElementChild.cloneNode(true);
    const nameEl = cartItem.querySelector('[data-element="name"]');
    const priceEl = cartItem.querySelector('[data-element="price"]');

    if (nameEl) {
      // For membership items with externalDescription, display name and description separately
      if (item.type === 'membership' && item.externalDescription) {
        // Clear any existing content
        nameEl.innerHTML = '';
        
        const nameContainer = document.createElement('div');
        nameContainer.style.display = 'flex';
        nameContainer.style.alignItems = 'center';
        nameContainer.style.gap = '6px';
        
        const productNameSpan = document.createElement('span');
        productNameSpan.textContent = item.productName || item.name;
        productNameSpan.style.fontWeight = '500';
        
        nameContainer.appendChild(productNameSpan);
        const descriptionInfo = createProductDescriptionInfo(item.description || item.externalDescription || '');
        if (descriptionInfo) nameContainer.appendChild(descriptionInfo);
        nameEl.appendChild(nameContainer);
        
        // Add Home Gym info below the name container for first item
        if (isFirstSubscriptionItem && selectedGym) {
          const gymInfo = createHomeGymInfo(selectedGym);
          nameEl.appendChild(gymInfo);
        }
      } else if (item.type === 'value-card') {
        // Punch cards: product name only in main row; Total + price go in a row below
        nameEl.innerHTML = '';
        const productNameSpan = document.createElement('span');
        productNameSpan.textContent = item.name;
        productNameSpan.style.fontWeight = '500';
        const nameRow = document.createElement('div');
        nameRow.style.display = 'flex';
        nameRow.style.alignItems = 'center';
        nameRow.style.gap = '6px';
        nameRow.appendChild(productNameSpan);
        const descriptionInfo = createProductDescriptionInfo(item.description || '');
        if (descriptionInfo) nameRow.appendChild(descriptionInfo);
        nameEl.appendChild(nameRow);
        
        if (isFirstSubscriptionItem && selectedGym) {
          const gymInfo = createHomeGymInfo(selectedGym);
          nameEl.appendChild(gymInfo);
        }
      } else {
        // For addon items and other types, we'll add the plus sign later in the rendering loop
        nameEl.textContent = '';
        const productNameSpan = document.createElement('span');
        productNameSpan.textContent = item.name;
        const nameRow = document.createElement('div');
        nameRow.style.display = 'flex';
        nameRow.style.alignItems = 'center';
        nameRow.style.gap = '6px';
        nameRow.appendChild(productNameSpan);
        const descriptionInfo = createProductDescriptionInfo(item.description || '');
        if (descriptionInfo) nameRow.appendChild(descriptionInfo);
        nameEl.appendChild(nameRow);
        
        // Add Home Gym info below the first item's name
        if (isFirstSubscriptionItem && selectedGym) {
          const gymInfo = createHomeGymInfo(selectedGym);
          nameEl.appendChild(gymInfo);
        }
      }
    }
    
    // Value-card: show ×N and total punches on the right; price moves to total row below
    if (item.type === 'value-card' && priceEl) {
      priceEl.style.display = 'none'; // Price shown in cart-item-total-row below
      const rightWrapper = document.createElement('div');
      rightWrapper.className = 'cart-item-right';
      if (item.quantity > 1) {
        const quantitySpan = document.createElement('span');
        quantitySpan.className = 'cart-item-quantity';
        quantitySpan.textContent = `×${item.quantity}`;
        rightWrapper.appendChild(quantitySpan);
      }
      /* Total punches shown only in cart-item-total-row below, not duplicated on the right */
      priceEl.parentNode.insertBefore(rightWrapper, priceEl);
      rightWrapper.appendChild(priceEl);
    }
    
    // Hide price for membership items (price already shown in Monthly fee section)
    if (priceEl) {
      if (item.type === 'membership') {
        priceEl.style.display = 'none';
      } else if (item.type !== 'value-card') {
        renderCartItemPriceElement(priceEl, item);
      }
    }

    return cartItem;
  }

  // CRITICAL: Clear DOM first to ensure clean rendering
  DOM.cartItems.innerHTML = '';

  // Render subscription/15day pass/value card items ONLY (access type first, with pricing)
  // Payment overview (Månedlig betaling, Betal nu) is next in DOM; then addons; then total
  subscriptionItems.forEach((item, index) => {
    const cartItem = renderCartItem(item, index === 0);
    DOM.cartItems.appendChild(cartItem);
    // Punch-card totals live in payment overview (same breakdown as membership checkout).
  });

  // Render addon items in separate container (below payment overview, above total)
  if (DOM.cartAddonItems) {
    DOM.cartAddonItems.innerHTML = '';
    if (addonItems.length > 0) {
      DOM.cartAddonItems.style.display = 'block';
      addonItems.forEach((item) => {
        const cartItem = renderCartItem(item, false);
        // Add plus sign before addon product names (inline, to the left) with brand accent color
        const nameEl = cartItem.querySelector('[data-element="name"]');
        if (nameEl) {
          const currentText = nameEl.textContent || nameEl.innerText || '';
          const rowEl = nameEl.firstElementChild;
          if (rowEl) {
            rowEl.style.display = 'flex';
            rowEl.style.alignItems = 'center';
            rowEl.style.gap = '4px';
            const firstText = rowEl.querySelector('span');
            if (firstText && !firstText.textContent.trim().startsWith('+')) {
              const plusSpan = document.createElement('span');
              plusSpan.textContent = '+';
              plusSpan.style.color = 'var(--color-brand-accent)';
              plusSpan.style.fontWeight = '600';
              firstText.textContent = firstText.textContent.trim();
              rowEl.insertBefore(plusSpan, firstText);
            }
          }
        }
        DOM.cartAddonItems.appendChild(cartItem);
      });
    } else {
      DOM.cartAddonItems.style.display = 'none';
    }
  }
}

function renderCartAddons() {
  // CRITICAL: Freeze UI during checkout to prevent price changes before redirect
  if (state.checkoutInProgress) {
    console.log('[Cart Addons] ⏸️ Checkout in progress - skipping cart addons render to freeze prices');
    return;
  }
  
  if (!templates.cartItem || !DOM.cartAddons) return;
  DOM.cartAddons.innerHTML = '';

  // Filter to only non-membership items (addons, boost products, etc.)
  const addonItems = state.cartItems.filter(item => item.type !== 'membership');

  if (!addonItems.length) {
    DOM.cartAddons.style.display = 'none';
    return;
  }

  DOM.cartAddons.style.display = 'block';

  addonItems.forEach((item) => {
    const cartItem = templates.cartItem.content.firstElementChild.cloneNode(true);
    const nameEl = cartItem.querySelector('[data-element="name"]');
    const priceEl = cartItem.querySelector('[data-element="price"]');

    if (nameEl) {
      nameEl.textContent = item.name;
    }
    
    if (priceEl) {
      renderCartItemPriceElement(priceEl, item);
    }

    DOM.cartAddons.appendChild(cartItem);
  });
}

function renderCartTotal() {
  // CRITICAL: Freeze UI during checkout to prevent price changes before redirect
  if (state.checkoutInProgress) {
    console.log('[Cart] ⏸️ Checkout in progress - skipping cart total update to freeze prices');
    return;
  }
  
  // Cart total is no longer displayed - payment overview shows calculated prices instead
  // Just update payment overview which shows the correct calculated prices
  
  // Update payment overview (shows "Betales nu" and "Månedlig betaling herefter")
  updatePaymentOverview();
  
  // Update cart total display - use already-calculated cartTotal from state
  const cartTotalEl = document.querySelector('[data-summary-field="cart-total"]') ||
                      document.querySelector('.cart-total .total-amount') ||
                      document.querySelector('.total-amount');
  const cartTotalContainer = document.querySelector('.cart-total');
  
  if (cartTotalEl) {
    // Use already-calculated cartTotal (includes membership + addons - discount)
    // state.totals.cartTotal is calculated in updateCartSummary() as: subtotal - discount
    const total = state.totals.cartTotal || 0;
    cartTotalEl.textContent = formatPriceHalfKrone(roundToHalfKrone(total)) + ' kr.';
    console.log('[Cart] Updated cart total display:', total);
  } else {
    console.warn('[Cart] Cart total element not found in DOM');
  }
  
  // Show/hide cart total container based on whether there are items
  if (cartTotalContainer) {
    // Prices for membership and punch-card flows are shown in payment overview
    const showLegacyCartTotal = state.cartItems?.some(
      (item) => item.type !== 'membership' && item.type !== 'value-card'
    );
    cartTotalContainer.style.display = showLegacyCartTotal ? 'block' : 'none';
  }
  
  // Update discount display if discount is applied
  updateDiscountDisplay();
  
  // Update FAQ based on cart contents
  renderFAQ();
  
  console.log('[Cart] Updated cart display (subtotal:', state.totals.subtotal, 'discount:', state.totals.discountAmount, ')');
}

/**
 * Updates the payment overview section in the cart summary.
 * 
 * Displays:
 * - "Betales nu:" (Pay now) - The initial payment amount with billing period
 * - "Månedlig betaling herefter:" (Monthly payment thereafter) - The recurring monthly fee
 * 
 * Based on OpenAPI 3.0.1 documentation:
 * - Order.price.amount: Total price of the order (CurrencyOut)
 * - SubscriptionItem.initialPaymentPeriod: First payment period (DayRange with start/end)
 * - SubscriptionItem.payRecurring.price.amount: Recurring monthly price after promotional period (CurrencyOut)
 * 
 * @see docs/brp-api3-openapi.yaml - OrderOut, SubscriptionItemOut schemas
 */
function updatePaymentOverview() {
  // CRITICAL: Freeze UI during checkout to prevent price changes before redirect
  // The backend will still process correctly, but UI stays frozen at checkout click
  if (state.checkoutInProgress) {
    console.log('[Payment Overview] ⏸️ Checkout in progress - skipping UI update to freeze cart prices');
    return;
  }
  
  const hasMembership = state.selectedProductType === 'membership' && state.selectedProductId;
  const hasPunchCardCheckout = isPunchCardCheckout();

  ensurePaymentOverviewDomRefs();

  if (!DOM.paymentBillingPeriod) {
    DOM.paymentBillingPeriod = document.querySelector('[data-summary-field="payment-billing-period"]');
  }
  if (!DOM.firstMonthAmount) {
    DOM.firstMonthAmount = document.querySelector('[data-summary-field="first-month"]');
  }

  if (!DOM.paymentOverview || !DOM.payNow) {
    return;
  }

  if (!hasMembership && !hasPunchCardCheckout) {
    DOM.paymentOverview.style.display = 'none';
    const totalWrap = document.querySelector('.payment-overview-total-wrap');
    if (totalWrap) totalWrap.style.display = 'none';
    return;
  }

  if (hasPunchCardCheckout && !hasMembership) {
    updatePunchCardPaymentOverview();
    return;
  }

  if (!DOM.monthlyPayment) {
    return;
  }

  // Show payment overview - don't wait for order data, show prices from product data immediately
  DOM.paymentOverview.style.display = 'block';
  
  console.log('[Payment Overview] ===== UPDATING PAYMENT OVERVIEW =====');
  console.log('[Payment Overview] Order data:', {
    hasFullOrder: !!state.fullOrder,
    orderId: state.fullOrder?.id,
    orderNumber: state.fullOrder?.number,
    orderPrice: state.fullOrder?.price,
    hasSubscriptionItems: !!(state.fullOrder?.subscriptionItems && state.fullOrder.subscriptionItems.length > 0),
    subscriptionItemsCount: state.fullOrder?.subscriptionItems?.length || 0
  });
  
  // Get subscription item from order if available (per OpenAPI: OrderOut.subscriptionItems[0])
  // If not available, we'll use product data instead
  const subscriptionItem = state.fullOrder?.subscriptionItems?.[0];
  const hasOrderData = !!subscriptionItem;
  const activeLandingRoute = state.landingRouteConfig || resolveLandingRouteConfig();
  const isFirstMonthFreeLandingRoute = activeLandingRoute?.componentName === 'LandingFirstMonthFree';
  
  // Check if this is a 15-day pass with shoes (one-time payment product)
  // Check both order data and product data
  const allSubscriptions = [
    ...(state.campaignSubscriptions || []),
    ...(state.subscriptions || []),
    ...(state.dayPassSubscriptions || [])
  ];
  const productIdNum = typeof state.selectedProductId === 'string' 
    ? parseInt(state.selectedProductId) 
    : state.selectedProductId;
  const currentProduct = allSubscriptions.find(p => 
    p.id === state.selectedProductId || 
    p.id === productIdNum ||
    String(p.id) === String(state.selectedProductId)
  );

  // Lazy-load selected subscription detail (once) to avoid rate limiting from bulk detail fetches.
  if (currentProduct?.id != null && !subscriptionDetailHydratedIds.has(String(currentProduct.id))) {
    ensureSubscriptionProductDetail(currentProduct.id).then((wasHydrated) => {
      if (wasHydrated && !state.checkoutInProgress) {
        updatePaymentOverview();
      }
    });
  }
  
  // Check product name from order data first, then fall back to product data
  const productFromOrder = subscriptionItem?.product;
  const productName = productFromOrder?.name || currentProduct?.name || '';
  const currentProductForLabelCheck = currentProduct
    ? { ...currentProduct, productLabels: (currentProduct?.productLabels || currentProduct?.labels || []) }
    : null;
  const has15DayPassDetectedLabel = has15DayPassLabel(currentProductForLabelCheck) || has15DayPassLabel(productFromOrder);
  const is15DayPass =
    state.selectedProductType === '15daypass' ||
    (typeof state.membershipPlanId === 'string' && state.membershipPlanId.startsWith('15daypass-')) ||
    has15DayPassDetectedLabel ||
    (productName && (
      productName.toLowerCase().includes('15-day trial pass') ||
      productName.toLowerCase().includes('15 day pass') ||
      productName.toLowerCase().includes('15 dages')
    ));

  const addMonthsClamped = (date, monthsToAdd) => {
    const d = new Date(date);
    const originalDay = d.getDate();
    d.setDate(1);
    d.setMonth(d.getMonth() + monthsToAdd);
    const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
    d.setDate(Math.min(originalDay, lastDay));
    d.setHours(0, 0, 0, 0);
    return d;
  };

  const endOfMonth = (date) => {
    const d = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    d.setHours(0, 0, 0, 0);
    return d;
  };

  const getProratedAmount = (monthlyAmount, periodStart, periodEnd) => {
    if (!Number.isFinite(monthlyAmount) || monthlyAmount <= 0) return 0;
    const msPerDay = 24 * 60 * 60 * 1000;
    const daysInMonth = new Date(periodEnd.getFullYear(), periodEnd.getMonth() + 1, 0).getDate();
    const billedDays = Math.max(1, Math.floor((periodEnd.getTime() - periodStart.getTime()) / msPerDay) + 1);
    return (monthlyAmount / daysInMonth) * billedDays;
  };

  const getNestedValue = (obj, path) => {
    if (!obj || !path) return undefined;
    return path.split('.').reduce((acc, key) => (acc == null ? undefined : acc[key]), obj);
  };

  const collectWarrantyNodes = (obj, maxDepth = 5) => {
    const matches = [];
    const visited = new WeakSet();
    const walk = (value, keyPath = '', depth = 0) => {
      if (!value || depth > maxDepth) return;
      if (typeof value !== 'object') return;
      if (visited.has(value)) return;
      visited.add(value);

      Object.entries(value).forEach(([key, child]) => {
        const childPath = keyPath ? `${keyPath}.${key}` : key;
        const keyLower = key.toLowerCase();
        const looksWarrantyRelated =
          keyLower.includes('warranty') ||
          keyLower.includes('newproduct') ||
          keyLower.includes('productafter') ||
          keyLower.includes('nextproduct') ||
          keyLower.includes('replacement');

        if (looksWarrantyRelated) {
          matches.push({ path: childPath, value: child });
        }
        walk(child, childPath, depth + 1);
      });
    };

    walk(obj, '', 0);
    return matches;
  };

  const extractWarrantyPriceFromNodeDKK = (nodeValue) => {
    if (!nodeValue) return 0;

    const directCentsCandidates = [
      nodeValue?.newPrice?.amount,
      nodeValue?.price?.amount,
      nodeValue?.priceWithInterval?.price?.amount,
      nodeValue?.amount
    ];

    for (const cents of directCentsCandidates) {
      const numeric = Number(cents);
      if (Number.isFinite(numeric) && numeric > 0) {
        return numeric / 100;
      }
    }

    return 0;
  };

  const getStaticPriceWarrantyFallbackDKK = (sourceProduct) => {
    if (!sourceProduct) return 0;
    const text = [
      sourceProduct?.name,
      sourceProduct?.externalDescription,
      sourceProduct?.description,
      sourceProduct?.imageBanner?.text
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    // Frontend fallback mapping (provided by business):
    // Adult: 469 DKK, Student: 399 DKK, Junior: 279 DKK.
    if (/(student|studie)/i.test(text)) return 399;
    if (/(junior|youth|ungdom)/i.test(text)) return 279;
    if (/(adult|voksen)/i.test(text)) return 469;

    // If this looks like a discounted campaign plan but no segment is present,
    // default to Adult as baseline fallback for post-warranty pricing.
    if (/%/.test(text) || /(month|måneder|maaneder|måned|maaned)/i.test(text)) {
      return 469;
    }

    return 0;
  };

  const getIntroOfferDurationMonths = (sourceProduct) => {
    if (!sourceProduct) return null;
    const text = [
      sourceProduct?.name,
      sourceProduct?.externalDescription,
      sourceProduct?.description,
      sourceProduct?.imageBanner?.text
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    const match = text.match(/(\d+)\s*(?:month|months|måned|måneder|maaned|maaneder)/i);
    if (!match || !match[1]) return null;
    const parsed = parseInt(match[1], 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  };

  const looksLikeIntroOfferPlan = (sourceProduct) => {
    if (!sourceProduct) return false;
    const text = [
      sourceProduct?.name,
      sourceProduct?.externalDescription,
      sourceProduct?.description,
      sourceProduct?.imageBanner?.text
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return /(%|off|rabat|intro|introductory|kampagne|campaign)/i.test(text);
  };

  const resolvePostWarrantyTargetProduct = (sourceProduct) => {
    if (!sourceProduct) return null;

    const allSubscriptionsForLookup = [
      ...(state.campaignSubscriptions || []),
      ...(state.subscriptions || []),
      ...(state.dayPassSubscriptions || [])
    ];

    const relationPaths = [
      'newProductAfterPriceWarranty',
      'newProductAndPriceAfterPriceWarranty',
      'productAfterPriceWarranty',
      'priceWarrantyProduct',
      'newSubscriptionProduct',
      'nextProduct',
      'replacementProduct',
      'priceWarranty.newProduct'
    ];

    const idPaths = [
      'newProductAfterPriceWarrantyId',
      'newProductAndPriceAfterPriceWarrantyId',
      'productAfterPriceWarrantyId',
      'priceWarrantyProductId',
      'newSubscriptionProductId',
      'nextProductId',
      'replacementProductId',
      'priceWarranty.newProductId'
    ];

    let referencedProduct = null;
    for (const relationPath of relationPaths) {
      const relationValue = getNestedValue(sourceProduct, relationPath);
      if (!relationValue) continue;

      if (typeof relationValue === 'object') {
        if (relationValue.priceWithInterval?.price?.amount != null) {
          referencedProduct = relationValue;
          break;
        }
        if (relationValue.id != null) {
          const relationId = String(relationValue.id);
          referencedProduct = allSubscriptionsForLookup.find((candidate) => String(candidate?.id) === relationId) || null;
          if (referencedProduct) break;
        }
      } else {
        const relationId = String(relationValue);
        referencedProduct = allSubscriptionsForLookup.find((candidate) => String(candidate?.id) === relationId) || null;
        if (referencedProduct) break;
      }
    }

    if (referencedProduct) return referencedProduct;

    for (const idPath of idPaths) {
      const relationIdValue = getNestedValue(sourceProduct, idPath);
      if (relationIdValue == null) continue;
      const relationId = String(relationIdValue);
      const matched = allSubscriptionsForLookup.find((candidate) => String(candidate?.id) === relationId);
      if (matched) return matched;
    }

    const dynamicNodes = collectWarrantyNodes(sourceProduct);
    for (const node of dynamicNodes) {
      const value = node.value;
      if (!value) continue;

      if (typeof value === 'object' && value.id != null) {
        const relationId = String(value.id);
        const matched = allSubscriptionsForLookup.find((candidate) => String(candidate?.id) === relationId);
        if (matched) return matched;
      }

      if (typeof value === 'number' || (typeof value === 'string' && /^\d+$/.test(value.trim()))) {
        const relationId = String(value).trim();
        const matched = allSubscriptionsForLookup.find((candidate) => String(candidate?.id) === relationId);
        if (matched) return matched;
      }
    }

    return null;
  };

  const getPostWarrantyMonthlyPriceDKK = (sourceProduct) => {
    if (!sourceProduct) return 0;

    const dynamicNodes = collectWarrantyNodes(sourceProduct);
    for (const node of dynamicNodes) {
      const dynamicPrice = extractWarrantyPriceFromNodeDKK(node.value);
      if (dynamicPrice > 0) {
        return dynamicPrice;
      }
    }

    const targetProduct = resolvePostWarrantyTargetProduct(sourceProduct);
    if (targetProduct) {
      const cents = getProductPriceCents(targetProduct);
      const resolvedPrice = Number.isFinite(cents) && cents > 0 ? cents / 100 : 0;
      if (resolvedPrice > 0) return resolvedPrice;
    }

    return getStaticPriceWarrantyFallbackDKK(sourceProduct);
  };

  const getFallbackMonthlyPriceDKK = () => {
    const postWarrantyPrice =
      getPostWarrantyMonthlyPriceDKK(currentProduct) ||
      getPostWarrantyMonthlyPriceDKK(productFromOrder);
    if (postWarrantyPrice > 0) return postWarrantyPrice;

    const productCents = getProductPriceCents(currentProduct || productFromOrder || {});
    return Number.isFinite(productCents) && productCents > 0 ? productCents / 100 : 0;
  };

  const ensurePayNowLogicTooltip = (payNowRow) => {
    if (!payNowRow) return;
    const label = payNowRow.querySelector('.payment-label');
    if (!label) return;
    const { isFreeFlow } = getFreeFlowCartState();
    if (isFreeFlow) {
      const existingWrapper = payNowRow.querySelector('.paynow-info-wrapper');
      if (existingWrapper) existingWrapper.remove();
      return;
    }

    const tooltipTitle = t('cart.payNowTooltip.title');
    const tooltipBefore15 = t('cart.payNowTooltip.line1');
    const tooltipAfter15 = t('cart.payNowTooltip.line2');

    let wrapper = payNowRow.querySelector('.paynow-info-wrapper');
    let infoIcon;
    let tooltip;
    if (!wrapper) {
      wrapper = document.createElement('span');
      wrapper.className = 'paynow-info-wrapper';

      infoIcon = document.createElement('button');
      infoIcon.className = 'paynow-info-icon';
      infoIcon.setAttribute('type', 'button');
      infoIcon.setAttribute('aria-label', tooltipTitle);
      infoIcon.setAttribute('aria-expanded', 'false');
      infoIcon.innerHTML = sanitizeHTML(`
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="16" x2="12" y2="12"></line>
          <line x1="12" y1="8" x2="12.01" y2="8"></line>
        </svg>
      `);

      tooltip = document.createElement('div');
      tooltip.className = 'paynow-info-tooltip';
      wrapper.appendChild(infoIcon);
      wrapper.appendChild(tooltip);
      label.appendChild(wrapper);

      const closeAllTooltipsExcept = () => {
        document.querySelectorAll('.home-gym-tooltip, .product-info-tooltip, .paynow-info-tooltip').forEach((node) => {
          if (node !== tooltip) node.classList.remove('show');
        });
      };

      const supportsHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
      if (supportsHover) {
        wrapper.addEventListener('mouseenter', () => {
          closeAllTooltipsExcept();
          tooltip.classList.add('show');
          infoIcon.setAttribute('aria-expanded', 'true');
        });
        wrapper.addEventListener('mouseleave', () => {
          tooltip.classList.remove('show');
          infoIcon.setAttribute('aria-expanded', 'false');
        });
        wrapper.addEventListener('focusin', () => {
          closeAllTooltipsExcept();
          tooltip.classList.add('show');
          infoIcon.setAttribute('aria-expanded', 'true');
        });
        wrapper.addEventListener('focusout', () => {
          tooltip.classList.remove('show');
          infoIcon.setAttribute('aria-expanded', 'false');
        });
      } else {
        infoIcon.addEventListener('click', (e) => {
          e.stopPropagation();
          closeAllTooltipsExcept();
          const isOpen = tooltip.classList.contains('show');
          tooltip.classList.toggle('show');
          infoIcon.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
        });
      }
    } else {
      infoIcon = wrapper.querySelector('.paynow-info-icon');
      tooltip = wrapper.querySelector('.paynow-info-tooltip');
      if (!label.contains(wrapper)) {
        label.appendChild(wrapper);
      }
    }

    if (tooltip) {
      tooltip.innerHTML = sanitizeHTML(`
        <div class="tooltip-content">
          <p class="paynow-tooltip-title">${tooltipTitle}</p>
          <ul class="paynow-tooltip-list">
            <li>${tooltipBefore15}</li>
            <li>${tooltipAfter15}</li>
          </ul>
        </div>
      `);
    }
  };
  
  // ============================================================================
  // CALCULATE "BETALES NU" (PAY NOW)
  // ============================================================================
  // CRITICAL: Use the EXACT same price that backend sends to payment window
  // Payment link API reads order.price.amount from backend, so we must use the same value
  // This ensures "Betales nu" matches the price shown in payment window
  // 
  // Per OpenAPI: OrderOut.price (CurrencyOut) - The total price of the order
  // If order data is not available, calculate from product data
  let payNowAmount = 0;
  let billingPeriod = null;
  let isCampaignPayNowPending = false;
  
  if (hasOrderData && state.fullOrder?.price?.amount !== undefined) {
    // CRITICAL: Use API price from order - this is the authoritative source
    // This ensures "Pay now" matches exactly what backend sends to payment window
    // Extract price amount (CurrencyOut can be object with .amount or direct number)
    const orderPriceAmount = state.fullOrder.price.amount;
    const orderPriceDKK = typeof orderPriceAmount === 'object' 
      ? orderPriceAmount.amount / 100 
      : orderPriceAmount / 100;
    
    // CRITICAL: If addons are present, ALWAYS use backend order price
    // The backend order price includes addons (if they were successfully added)
    // Client-side calculations don't account for addons properly
    const hasAddons = state.addonIds && state.addonIds.size > 0;
    if (hasAddons) {
      console.log('[Payment Overview] ✅ Addons present - using backend order price (includes addons):', orderPriceDKK, 'DKK');
      payNowAmount = orderPriceDKK;
      
      // Get billing period from subscription item if available
      if (subscriptionItem?.initialPaymentPeriod?.start) {
        const backendStartDate = new Date(subscriptionItem.initialPaymentPeriod.start);
        const backendEndDate = new Date(subscriptionItem.initialPaymentPeriod.end);
        billingPeriod = {
          start: backendStartDate,
          end: backendEndDate
        };
      }
      
      // Update the pay now amount element
      if (DOM.paymentPayNow) {
        DOM.paymentPayNow.textContent = formatPriceHalfKrone(roundToHalfKrone(payNowAmount)) + ' kr.';
      }
      
      // Skip the verification logic when addons are present - backend price is authoritative
      console.log('[Payment Overview] ✅ Skipping price verification - using backend order price with addons');
    } else {
      // No addons - proceed with normal price verification
      console.log('[Payment Overview] ✅ Using API price from order (fullOrder.price.amount):', orderPriceDKK, 'DKK');
      
      if (is15DayPass) {
      // For 15-day pass: use list price unless the order already has a coupon-adjusted price
      const dayPassPriceInCents =
        currentProduct?.priceWithInterval?.price?.amount ??
        productFromOrder?.priceWithInterval?.price?.amount ??
        currentProduct?.price?.amount ??
        0;
      const orderHasCoupon = extractCouponDiscountKr(state.fullOrder?.couponDiscount) > 0
        || (state.discountApplied && state.totals.discountAmount > 0);
      payNowAmount = orderHasCoupon
        ? orderPriceDKK
        : (dayPassPriceInCents > 0 ? (dayPassPriceInCents / 100) : orderPriceDKK);
      
      // Use selected activation date or today; end = start + 14 days (inclusive 15-day period)
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const startDate = state.subscriptionStartDate
        ? new Date(state.subscriptionStartDate + 'T12:00:00')
        : today;
      startDate.setHours(0, 0, 0, 0);
      const validUntil = new Date(startDate);
      validUntil.setDate(validUntil.getDate() + 14);
      
      billingPeriod = {
        start: startDate,
        end: validUntil,
        is15DayPass: true
      };
      
      console.log('[Payment Overview] ✅ 15-day pass: Full price (one-time payment):', payNowAmount, 'DKK');
    } else {
      // Regular membership: Get initialPaymentPeriod (per OpenAPI: SubscriptionItemOut.initialPaymentPeriod - DayRange)
      const initialPaymentPeriod = subscriptionItem.initialPaymentPeriod;
      const payRecurring = subscriptionItem.payRecurring;
      const recurringPriceAmount = payRecurring?.price?.amount || 0;
      
      // Verify backend pricing is correct using robust verification method
      const productId = subscriptionItem?.product?.id || state.selectedProductId;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const startDateStr = getTodayLocalDateString();
      const expectedPrice = orderAPI._calculateExpectedPartialMonthPrice(productId, startDateStr);
      const dayOfMonth = today.getDate();
      if (initialPaymentPeriod?.start) {
        const backendStartDate = new Date(initialPaymentPeriod.start);
        const backendEndDate = new Date(initialPaymentPeriod.end);
        
        // Use verification method to check if pricing is correct
        const verification = orderAPI._verifySubscriptionPricing(
          state.fullOrder,
          productId,
          expectedPrice,
          today
        );
        
        if (verification.isCorrect || (verification.priceDifference !== null && verification.priceDifference <= 100)) {
          // Backend calculated correctly - use backend's price
          payNowAmount = orderPriceDKK;
          billingPeriod = {
            start: backendStartDate,
            end: backendEndDate
          };
          console.log('[Payment Overview] ✅ Backend pricing accepted:', payNowAmount, 'DKK');
        } else {
          // Backend pricing is incorrect - calculate correct price client-side
          console.warn('[Payment Overview] ⚠️ Backend pricing is incorrect!');
          console.warn('[Payment Overview] ⚠️ Backend shows:', orderPriceDKK, 'DKK');
          console.warn('[Payment Overview] ⚠️ Expected:', expectedPrice?.amountInDKK || 'N/A', 'DKK');
          console.warn('[Payment Overview] ⚠️ Verification:', verification);
          
          if (expectedPrice) {
            // Use calculated correct price
            payNowAmount = expectedPrice.amountInDKK;
            
            // Set billing period to today - end of month
            const currentMonth = today.getMonth();
            const currentYear = today.getFullYear();
            const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0);
            billingPeriod = {
              start: today,
              end: lastDayOfMonth
            };
            console.log('[Payment Overview] ✅ Using client-calculated correct price:', payNowAmount, 'DKK');
            console.warn('[Payment Overview] ⚠️ NOTE: Using calculated price - verify against backend order price');
          } else {
            // Can't calculate expected price - use backend price as fallback
            payNowAmount = orderPriceDKK;
            billingPeriod = {
              start: backendStartDate,
              end: backendEndDate
            };
            console.warn('[Payment Overview] ⚠️ Cannot calculate expected price - using backend price as fallback');
          }
        }
      } else {
        // No initialPaymentPeriod - use order price as-is
        payNowAmount = orderPriceDKK;
        console.log('[Payment Overview] ✅ Pay now from fullOrder.price.amount:', payNowAmount, 'DKK (no initialPaymentPeriod)');
      }
    }
  }
  } else {
    // No order data yet - calculate price from product data
    // This allows prices to be shown before login/account creation
    if (state.selectedProductId && state.subscriptions) {
      const productIdNum = typeof state.selectedProductId === 'string' 
        ? parseInt(state.selectedProductId) 
        : state.selectedProductId;
      
      // Check all subscription types: campaign, membership, and 15-Day Trial Pass
      const allSubscriptions = [
        ...(state.campaignSubscriptions || []),
        ...(state.subscriptions || []),
        ...(state.dayPassSubscriptions || [])
      ];
      const membership = allSubscriptions.find(p => 
        p.id === state.selectedProductId || 
        p.id === productIdNum ||
        String(p.id) === String(state.selectedProductId)
      );
      
      if (membership) {
        const membershipLabels = membership.productLabels || membership.labels || [];
        const membershipForLabelCheck = {
          ...membership,
          productLabels: membershipLabels
        };
        const isMembership15DayPass =
          state.selectedProductType === '15daypass' ||
          (typeof state.membershipPlanId === 'string' && state.membershipPlanId.startsWith('15daypass-')) ||
          has15DayPassLabel(membershipForLabelCheck) ||
          (membership.name && (
            membership.name.toLowerCase().includes('15-day trial pass') ||
            membership.name.toLowerCase().includes('15 day pass') ||
            membership.name.toLowerCase().includes('15 dages')
          ));
        
        if (isMembership15DayPass) {
          // For 15-day pass: always use full price (one-time payment)
          const priceInCents = membership.priceWithInterval?.price?.amount || 0;
          payNowAmount = priceInCents / 100;
          
          // Use selected activation date or today; end = start + 14 days (inclusive 15-day period)
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const startDate = state.subscriptionStartDate
            ? new Date(state.subscriptionStartDate + 'T12:00:00')
            : today;
          startDate.setHours(0, 0, 0, 0);
          const validUntil = new Date(startDate);
          validUntil.setDate(validUntil.getDate() + 14);
          
          billingPeriod = {
            start: startDate,
            end: validUntil,
            is15DayPass: true
          };
          
          console.log('[Payment Overview] ✅ 15-day pass: Full price (one-time payment):', payNowAmount, 'DKK');
        } else {
          // Regular membership: Calculate partial month price client-side
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const startDateStr = getTodayLocalDateString();
          const dayOfMonth = today.getDate();
          if (orderAPI && orderAPI._calculateExpectedPartialMonthPrice) {
          // Try to use the helper function if available
            const expectedPrice = orderAPI._calculateExpectedPartialMonthPrice(membership.id, startDateStr);
            if (expectedPrice) {
              payNowAmount = expectedPrice.amountInDKK;
              
              // Set billing period based on whether next month is included
              const currentMonth = today.getMonth();
              const currentYear = today.getFullYear();
              const dayOfMonth = today.getDate();
              
              if (expectedPrice.includesNextMonth) {
                // Billing period extends to end of next month
                const nextMonth = currentMonth === 11 ? 0 : currentMonth + 1;
                const nextYear = currentMonth === 11 ? currentYear + 1 : currentYear;
                const lastDayOfNextMonth = new Date(nextYear, nextMonth + 1, 0);
                billingPeriod = {
                  start: today,
                  end: lastDayOfNextMonth
                };
                console.log('[Payment Overview] ✅ Pay now calculated (rest of month + full next month):', payNowAmount, 'DKK');
              } else {
                // Billing period is just rest of current month
                const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0);
                billingPeriod = {
                  start: today,
                  end: lastDayOfMonth
                };
                console.log('[Payment Overview] ✅ Pay now calculated (partial month):', payNowAmount, 'DKK');
              }
            } else {
              // Fallback to full month price
              const priceInCents = membership.priceWithInterval?.price?.amount || 0;
              payNowAmount = priceInCents / 100;
              console.log('[Payment Overview] ⚠️ Pay now from product data (full month - fallback):', payNowAmount, 'DKK');
            }
          } else {
            // Calculate partial month price manually
            const priceInCents = membership.priceWithInterval?.price?.amount || 0;
            const monthlyPrice = priceInCents / 100;
            
            // Calculate days until end of month
            const currentMonth = today.getMonth();
            const currentYear = today.getFullYear();
            const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0);
            const daysInMonth = lastDayOfMonth.getDate();
            const dayOfMonth = today.getDate();
            const daysRemaining = lastDayOfMonth.getDate() - today.getDate() + 1;
            
            // CRITICAL: If today is 16th or later, price should be: rest of current month + full next month
            if (dayOfMonth >= 16) {
              // Calculate: (rest of current month) + (full next month)
              const partialCurrentMonthPrice = (monthlyPrice / daysInMonth) * daysRemaining;
              payNowAmount = partialCurrentMonthPrice + monthlyPrice;
              
              // Billing period extends to end of next month
              const nextMonth = currentMonth === 11 ? 0 : currentMonth + 1;
              const nextYear = currentMonth === 11 ? currentYear + 1 : currentYear;
              const lastDayOfNextMonth = new Date(nextYear, nextMonth + 1, 0);
              
              billingPeriod = {
                start: today,
                end: lastDayOfNextMonth
              };
              
              console.log('[Payment Overview] ✅ Pay now calculated (rest of month + full next month, manual calc):', payNowAmount, 'DKK');
            } else {
              // Calculate: just rest of current month (prorated)
              payNowAmount = (monthlyPrice / daysInMonth) * daysRemaining;
              
              billingPeriod = {
                start: today,
                end: lastDayOfMonth
              };
              
              console.log('[Payment Overview] ✅ Pay now calculated (partial month, manual calc):', payNowAmount, 'DKK');
            }
          }
        }
      } else {
        // Fallback: Use cart total
        payNowAmount = state.totals.cartTotal || 0;
        console.log('[Payment Overview] ⚠️ Pay now from cartTotal (fallback):', payNowAmount, 'DKK - product not found');
      }
    } else {
      // Fallback: Use cart total if product data not available
      payNowAmount = state.totals.cartTotal || 0;
      console.log('[Payment Overview] ⚠️ Pay now from cartTotal (fallback):', payNowAmount, 'DKK - product data not available');
    }
  }
  
  // ============================================================================
  // CALCULATE "MÅNEDLIG BETALING HEREFTER" (MONTHLY PAYMENT THEREAFTER)
  // ============================================================================
  // Skip monthly payment calculation for 15-day pass (one-time payment)
  let monthlyPaymentAmount = 0;
  let reducedMonthlyPaymentAmount = 0;
  const withVars = (key, vars = {}) => {
    let text = t(key);
    Object.entries(vars).forEach(([varKey, value]) => {
      text = String(text).replaceAll(`{${varKey}}`, String(value));
    });
    return text;
  };
  
  if (!is15DayPass) {
    const reducedMonthlyCents = getProductPriceCents(currentProduct || productFromOrder || {});
    if (Number.isFinite(reducedMonthlyCents) && reducedMonthlyCents > 0) {
      reducedMonthlyPaymentAmount = reducedMonthlyCents / 100;
    }

    // Per OpenAPI: SubscriptionItemOut.payRecurring.price.amount
    // This is ALWAYS the regular monthly price after any promotional period
    // Even if there's an initialPaymentPeriod, payRecurring shows the price after promotion ends
    
    // CRITICAL: Always prioritize API data from order
    // Per OpenAPI: SubscriptionItemOut.payRecurring.price.amount is the recurring price AFTER promotional period
    // For products with promotions, this shows the regular price after discount period ends
    const postWarrantyMonthlyPrice =
      getPostWarrantyMonthlyPriceDKK(currentProduct) ||
      getPostWarrantyMonthlyPriceDKK(productFromOrder);

    if (postWarrantyMonthlyPrice > 0) {
      monthlyPaymentAmount = postWarrantyMonthlyPrice;
      console.log('[Payment Overview] ✅ Monthly payment from post-warranty target product:', monthlyPaymentAmount, 'DKK');
    } else if (hasOrderData && subscriptionItem.payRecurring?.price?.amount !== undefined) {
      // Use API price from order - this is the authoritative source
      const recurringPriceAmount = subscriptionItem.payRecurring.price.amount;
      monthlyPaymentAmount = typeof recurringPriceAmount === 'object' 
        ? recurringPriceAmount.amount / 100 
        : recurringPriceAmount / 100;
      if (!(monthlyPaymentAmount > 0)) {
        const fallbackMonthly = getFallbackMonthlyPriceDKK();
        if (fallbackMonthly > 0) {
          monthlyPaymentAmount = fallbackMonthly;
        }
      }
      console.log('[Payment Overview] ✅ Monthly payment from API (payRecurring.price.amount):', monthlyPaymentAmount, 'DKK');
    } else if (hasOrderData && subscriptionItem?.product?.priceWithInterval?.price?.amount !== undefined) {
      // Fallback: Use product price from order if payRecurring not available
      const productPriceAmount = subscriptionItem.product.priceWithInterval.price.amount;
      monthlyPaymentAmount = typeof productPriceAmount === 'object' 
        ? productPriceAmount.amount / 100 
        : productPriceAmount / 100;
      console.log('[Payment Overview] ⚠️ Monthly payment from API (product.priceWithInterval - fallback):', monthlyPaymentAmount, 'DKK');
    } else {
      // No order data yet - use product data from API response
      // Check all subscription types: campaign, membership, and 15-Day Trial Pass
      const allSubscriptionsForMonthly = [
        ...(state.campaignSubscriptions || []),
        ...(state.subscriptions || []),
        ...(state.dayPassSubscriptions || [])
      ];
      if (state.selectedProductId && allSubscriptionsForMonthly.length > 0) {
        const productIdNum = typeof state.selectedProductId === 'string' 
          ? parseInt(state.selectedProductId) 
          : state.selectedProductId;
        
        const membership = allSubscriptionsForMonthly.find(p => 
          p.id === state.selectedProductId || 
          p.id === productIdNum ||
          String(p.id) === String(state.selectedProductId)
        );
        
        if (membership?.priceWithInterval?.price?.amount !== undefined) {
          // Use price from API product data (supports both number and {amount})
          const membershipPriceCents = getProductPriceCents(membership);
          monthlyPaymentAmount = membershipPriceCents > 0 ? (membershipPriceCents / 100) : 0;
          console.log('[Payment Overview] Monthly payment from API product data (priceWithInterval):', monthlyPaymentAmount, 'DKK');
        } else {
          monthlyPaymentAmount = state.totals.membershipMonthly || 0;
          console.log('[Payment Overview] ⚠️ Monthly payment from state.totals.membershipMonthly (fallback):', monthlyPaymentAmount, 'DKK');
        }
      } else {
        monthlyPaymentAmount = state.totals.membershipMonthly || 0;
        console.log('[Payment Overview] ⚠️ Monthly payment from state.totals.membershipMonthly (fallback):', monthlyPaymentAmount, 'DKK');
      }
    }
  } else {
    console.log('[Payment Overview] ⏭️ Skipping monthly payment calculation (15-day pass - one-time payment)');
  }

  if (DOM.firstMonthRow) {
    DOM.firstMonthRow.style.display = 'none';
  }
  
  // ============================================================================
  // UPDATE DOM ELEMENTS
  // ============================================================================
  
  // Prepare billing period text
  let billingPeriodText = '';
  if (is15DayPass && billingPeriod?.is15DayPass) {
    // For 15-day pass: show date range (start - end); or "Valid until" when start is today
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const startDate = billingPeriod.start;
    const startStr = startDate ? (startDate.toISOString ? startDate.toISOString().slice(0, 10) : '') : '';
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    const startIsToday = !startDate || startStr === todayStr;
    if (startIsToday) {
      billingPeriodText = `${t('cart.validUntil')} ${formatDateDMY(billingPeriod.end)}`;
    } else {
      billingPeriodText = `${formatDateDMY(billingPeriod.start)} - ${formatDateDMY(billingPeriod.end)}`;
    }
  } else if (billingPeriod) {
    // Regular membership: show billing period dates only
    billingPeriodText = `${formatDateDMY(billingPeriod.start)} - ${formatDateDMY(billingPeriod.end)}`;
  } else if (hasOrderData && subscriptionItem?.boundUntil) {
    // No initialPaymentPeriod, but there's a boundUntil date
    const boundUntilDate = new Date(subscriptionItem.boundUntil);
    billingPeriodText = `${t('cart.boundUntil').charAt(0).toUpperCase() + t('cart.boundUntil').slice(1)} ${formatDateDMY(boundUntilDate)}`;
  }
  
  // Fallback to state.billingPeriod if available
  if (!billingPeriodText && state.billingPeriod) {
    billingPeriodText = state.billingPeriod;
  }

  // First month free flow should show a date range for the free period instead of generic fallback copy.
  if (!billingPeriodText && isFirstMonthFreeLandingRoute) {
    const freeStartDate = state.subscriptionStartDate
      ? new Date(state.subscriptionStartDate + 'T12:00:00')
      : new Date();
    freeStartDate.setHours(0, 0, 0, 0);
    const freePeriodEndDate = endOfMonth(addMonthsClamped(freeStartDate, 1));
    billingPeriodText = `${formatDateDMY(freeStartDate)} - ${formatDateDMY(freePeriodEndDate)}`;
  }
  
  // If still no billing period, show default message
  if (!billingPeriodText) {
    billingPeriodText = t('cart.billingPeriodConfirmed');
  }
  
  const payNowBreakdown = resolvePayNowDiscountBreakdown(payNowAmount);
  payNowAmount = payNowBreakdown.afterDiscount;

  // Round payNowAmount to half krone and store in state for use in cart total calculation
  state.totals.payNowAmount = roundToHalfKrone(payNowAmount);
  
  if (DOM.payNow) {
    const amountText = formatCurrencyHalfKrone(state.totals.payNowAmount);
    DOM.payNow.textContent = isCampaignPayNowPending ? '—' : amountText;
    
    // Add period after "Pay now" label but before amount
    const payNowRow = DOM.payNow.closest('.payment-overview-paynow-row');
    ensurePayNowLogicTooltip(payNowRow);
    if (!isCampaignPayNowPending && payNowRow && billingPeriodText) {
      let periodElement = payNowRow.querySelector('.payment-label-period');
      if (!periodElement) {
        // Create period element if it doesn't exist
        periodElement = document.createElement('span');
        periodElement.className = 'payment-label-period payment-period';
        // Insert after the label, before the amount
        const label = payNowRow.querySelector('.payment-label');
        if (label && label.nextSibling) {
          label.parentNode.insertBefore(periodElement, label.nextSibling);
        } else if (label) {
          label.parentNode.insertBefore(periodElement, DOM.payNow);
        }
      }
      if (is15DayPass) {
        periodElement.innerHTML = `(${billingPeriodText}) <button type="button" class="change-date-link" data-action="change-activation-date">${t('activationDate.change')}</button>`;
      } else {
        periodElement.textContent = `(${billingPeriodText})`;
      }
    } else if (payNowRow) {
      // Hide period element if no billing period text
      const periodElement = payNowRow.querySelector('.payment-label-period');
      if (periodElement) {
        periodElement.textContent = '';
      }
    }
    
    // Verify this matches payment window price (only if order data is available)
    if (hasOrderData && state.fullOrder?.price?.amount !== undefined) {
      const orderPriceForPayment = state.fullOrder.price.amount;
      const orderPriceDKK = typeof orderPriceForPayment === 'object' 
        ? orderPriceForPayment.amount / 100 
        : orderPriceForPayment / 100;
      const pricesMatch = Math.abs(payNowAmount - orderPriceDKK) < 0.01; // Allow small rounding differences
      
      console.log('[Payment Overview] 🔍 "Betales nu" price:', payNowAmount, 'DKK');
      console.log('[Payment Overview] 🔍 Order price (sent to payment window):', orderPriceDKK, 'DKK');
      console.log('[Payment Overview] 🔍 Prices match:', pricesMatch ? '✅ YES' : '❌ NO - MISMATCH!');
      
      if (!pricesMatch) {
        const productId = subscriptionItem?.product?.id || state.selectedProductId;
        console.warn('[Payment Overview] ⚠️ PRICE MISMATCH DETECTED!');
        console.warn('[Payment Overview] ⚠️ UI shows:', payNowAmount, 'DKK');
        console.warn('[Payment Overview] ⚠️ Payment window will show:', orderPriceDKK, 'DKK');
        console.warn('[Payment Overview] ⚠️ Product ID:', productId);
      }
    } else {
      console.log('[Payment Overview] 🔍 "Betales nu" price (from product data):', payNowAmount, 'DKK');
      console.log('[Payment Overview] ℹ️ Order data not available yet - price will be verified when order is created');
    }
  }
  applyFreeFlowCartUi();
  
  // Update "Månedlig betaling herefter" (Monthly payment thereafter)
  // Hide monthly payment for 15-day pass (one-time payment)
  if (DOM.monthlyPayment) {
    if (is15DayPass) {
      // Hide monthly payment section for one-time payment products
      const monthlyPaymentItem = DOM.monthlyPayment.closest('.payment-overview-item');
      if (monthlyPaymentItem) {
        monthlyPaymentItem.style.display = 'none';
        monthlyPaymentItem.classList.remove('payment-overview-item--tiered');
      }
      if (DOM.paymentOverview) {
        DOM.paymentOverview.classList.remove('payment-overview--tiered-offer');
      }
    } else {
      // Show monthly payment for regular memberships
      const monthlyPaymentItem = DOM.monthlyPayment.closest('.payment-overview-item');
      if (monthlyPaymentItem) {
        monthlyPaymentItem.style.display = '';
      }
      if (monthlyPaymentAmount > 0) {
        const roundedMonthly = roundToHalfKrone(monthlyPaymentAmount);
        const roundedReducedMonthly = roundToHalfKrone(reducedMonthlyPaymentAmount);
        const shouldShowReducedAndRegular =
          roundedReducedMonthly > 0 && Math.abs(roundedReducedMonthly - roundedMonthly) > 0.009;

        if (shouldShowReducedAndRegular && looksLikeIntroOfferPlan(currentProduct || productFromOrder)) {
          const introMonths = getIntroOfferDurationMonths(currentProduct || productFromOrder);
          const normalizedMonths = introMonths || 1;
          const introTitle = withVars('cart.tier.firstMonths', { months: normalizedMonths });
          const afterTitle = withVars('cart.tier.afterMonths', { months: normalizedMonths });
          const introSubtitleKey = normalizedMonths >= 6 ? 'cart.tier.commitment3' : 'cart.tier.introRate';
          const reducedText = formatCurrencyHalfKrone(roundedReducedMonthly);
          const regularText = formatCurrencyHalfKrone(roundedMonthly);
          const savingsTotal = introMonths
            ? Math.max(0, roundToHalfKrone((roundedMonthly - roundedReducedMonthly) * introMonths))
            : 0;
          const savingsDisplay = savingsTotal > 0
            ? withVars('cart.tier.savings', { amount: Math.round(savingsTotal) })
            : '';
          if (monthlyPaymentItem) {
            monthlyPaymentItem.classList.add('payment-overview-item--tiered');
          }
          if (DOM.paymentOverview) {
            DOM.paymentOverview.classList.add('payment-overview--tiered-offer');
          }
          DOM.monthlyPayment.innerHTML = sanitizeHTML(
            `<span class="payment-tiered-pricing">
              <span class="payment-tiered-pricing-row">
                <span class="payment-tiered-pricing-left">
                  <span class="payment-tiered-pricing-title">${introTitle}${savingsDisplay ? `<span class="payment-tiered-pricing-chip">${savingsDisplay}</span>` : ''}</span>
                  <span class="payment-tiered-pricing-subtitle">${t(introSubtitleKey)}</span>
                </span>
                <span class="payment-tiered-pricing-values">
                  <span class="payment-tiered-pricing-current">${reducedText}/md</span>
                  <span class="payment-tiered-pricing-old">${regularText}/md</span>
                </span>
              </span>
              <span class="payment-tiered-pricing-row payment-tiered-pricing-row--after">
                <span class="payment-tiered-pricing-left">
                  <span class="payment-tiered-pricing-title">${afterTitle}</span>
                  <span class="payment-tiered-pricing-subtitle">${t('cart.tier.cancelMonthToMonth')}</span>
                </span>
                <span class="payment-tiered-pricing-values">
                  <span class="payment-tiered-pricing-current">${regularText}/md</span>
                </span>
              </span>
            </span>`
          );
        } else if (shouldShowReducedAndRegular) {
          const reducedText = formatCurrencyHalfKrone(roundedReducedMonthly);
          const regularText = formatCurrencyHalfKrone(roundedMonthly);
          if (monthlyPaymentItem) {
            monthlyPaymentItem.classList.remove('payment-overview-item--tiered');
          }
          if (DOM.paymentOverview) {
            DOM.paymentOverview.classList.remove('payment-overview--tiered-offer');
          }
          DOM.monthlyPayment.innerHTML = sanitizeHTML(
            `<span style="text-decoration: line-through; opacity: 0.65; margin-right: 8px;">${reducedText}/md</span><span>${regularText}/md</span>`
          );
        } else {
          if (monthlyPaymentItem) {
            monthlyPaymentItem.classList.remove('payment-overview-item--tiered');
          }
          if (DOM.paymentOverview) {
            DOM.paymentOverview.classList.remove('payment-overview--tiered-offer');
          }
          DOM.monthlyPayment.textContent = `${formatCurrencyHalfKrone(roundedMonthly)}/md`;
        }
      } else {
        if (monthlyPaymentItem) {
          monthlyPaymentItem.classList.remove('payment-overview-item--tiered');
        }
        if (DOM.paymentOverview) {
          DOM.paymentOverview.classList.remove('payment-overview--tiered-offer');
        }
        DOM.monthlyPayment.textContent = '—';
      }
    }
  }

  applyPaymentOverviewDiscountRows(payNowBreakdown);
  
  // Display boundUntil date separately if available (for memberships with promotional periods)
  if (DOM.paymentBoundUntil) {
    if (hasOrderData && subscriptionItem?.boundUntil && !is15DayPass) {
      const boundUntilDate = new Date(subscriptionItem.boundUntil);
      const boundUntilText = `${t('cart.boundUntil').charAt(0).toUpperCase() + t('cart.boundUntil').slice(1)} ${formatDateDMY(boundUntilDate)}`;
      DOM.paymentBoundUntil.textContent = boundUntilText;
      DOM.paymentBoundUntil.style.display = 'block';
    } else {
      DOM.paymentBoundUntil.style.display = 'none';
    }
  }
  
  // Calculate final payable amount (single "Pay now", including add-ons)
  const totalEl = document.querySelector('[data-summary-field="total"]');
  const totalContainer = document.querySelector('.payment-overview-total');
  const payNowIncludesEl = document.querySelector('[data-summary-field="pay-now-includes"]');
  
  if (totalEl && totalContainer) {
    // CRITICAL: Calculate addon total from state.addonIds (source of truth)
    // Don't rely on state.cartItems which might not be updated yet (e.g., after login)
    const hasAddons = state.addonIds && state.addonIds.size > 0;
    let addonTotal = 0;
    
    if (hasAddons) {
      // Calculate addon total directly from addonIds
      state.addonIds.forEach((addonId) => {
        const addon = findAddon(addonId);
        if (addon) {
          const price = getAddonPrice(addon);
          addonTotal += roundToHalfKrone(price);
        }
      });
    }
    
    // CRITICAL: Check if payNowAmount already includes addons
    // Only trust order price if order actually has addons in it (articleItems or valueCardItems)
    // After login, order might be created without addons yet, so we need to check
    const orderHasAddons = hasOrderData && state.fullOrder && (
      (state.fullOrder.articleItems && state.fullOrder.articleItems.length > 0) ||
      (state.fullOrder.valueCardItems && state.fullOrder.valueCardItems.length > 0)
    );
    
    // Check if order price includes addons: only if order actually has addons AND we have addons in state
    const payNowIncludesAddons = hasAddons && hasOrderData && orderHasAddons && state.fullOrder?.price?.amount !== undefined;
    
    // Total calculation:
    // - If payNowAmount already includes addons (from backend order that has addons), use it as-is
    // - Otherwise, add addonTotal to payNowAmount (order doesn't have addons yet, or no order data)
    // payNowAmount already reflects coupon via resolvePayNowDiscountBreakdown
    let total = payNowIncludesAddons ? payNowAmount : payNowAmount + addonTotal;

    const roundedTotal = roundToHalfKrone(total);
    if (DOM.payNow) {
      DOM.payNow.textContent = formatPriceHalfKrone(roundedTotal) + ' kr.';
    }
    state.totals.payNowAmount = roundedTotal;

    // Keep legacy total field updated for compatibility, but hidden in UI.
    totalEl.textContent = formatPriceHalfKrone(roundedTotal) + ' kr.';

    // Show helper text that clarifies add-ons are included in the single pay-now amount.
    if (payNowIncludesEl) {
      if (addonTotal > 0) {
        const addonAmountText = formatPriceHalfKrone(roundToHalfKrone(addonTotal)) + ' kr.';
        const template = t('cart.payNowIncludesAddons');
        payNowIncludesEl.textContent = String(template).replace('{amount}', addonAmountText);
        payNowIncludesEl.style.display = 'block';
      } else {
        payNowIncludesEl.style.display = 'none';
        payNowIncludesEl.textContent = '';
      }
    }

    // Hide total container/wrapper to keep one prominent payable price in UI.
    const totalWrap = totalContainer.closest('.payment-overview-total-wrap');
    totalContainer.style.display = 'none';
    if (totalWrap) totalWrap.style.display = 'none';
    if (payNowIncludesAddons) {
      console.log('[Payment Overview] Pay now displayed:', roundedTotal, '(already includes addons from backend order)');
    } else {
      console.log('[Payment Overview] Pay now displayed:', roundedTotal, '(base:', payNowAmount, '+ addons:', addonTotal, ')');
    }
  }
  
  // Calculate total for logging (same logic as display - use state.addonIds as source of truth)
  const hasAddonsForLog = state.addonIds && state.addonIds.size > 0;
  let addonTotalForLog = 0;
  if (hasAddonsForLog) {
    state.addonIds.forEach((addonId) => {
      const addon = findAddon(addonId);
      if (addon) {
        const price = getAddonPrice(addon);
        addonTotalForLog += roundToHalfKrone(price);
      }
    });
  }
  // Check if order actually has addons (same logic as display)
  const orderHasAddonsForLog = hasOrderData && state.fullOrder && (
    (state.fullOrder.articleItems && state.fullOrder.articleItems.length > 0) ||
    (state.fullOrder.valueCardItems && state.fullOrder.valueCardItems.length > 0)
  );
  const payNowIncludesAddonsForLog = hasAddonsForLog && hasOrderData && orderHasAddonsForLog && state.fullOrder?.price?.amount !== undefined;
  const calculatedTotal = payNowIncludesAddonsForLog ? payNowAmount : payNowAmount + addonTotalForLog;
  
  console.log('[Payment Overview] ✅ Updated:', {
    payNow: payNowAmount,
    monthlyPayment: monthlyPaymentAmount,
    billingPeriod: DOM.paymentBillingPeriod?.textContent || 'N/A',
    total: totalEl ? calculatedTotal : 'N/A',
    payNowIncludesAddons: payNowIncludesAddonsForLog
  });
}

function updateDiscountDisplay() {
  // CRITICAL: Freeze UI during checkout to prevent price changes before redirect
  if (state.checkoutInProgress) {
    console.log('[Discount Display] ⏸️ Checkout in progress - skipping discount display update to freeze prices');
    return;
  }
  
  // Find or create discount display element
  let discountDisplay = DOM.discountDisplay || document.querySelector('[data-discount-display]') || document.querySelector('.discount-display');
  
  // Show discount display if discount is applied OR if discount code is stored (pending application)
  // BUT: Don't show pending message if we're currently applying a discount (button is disabled)
  const isApplyingDiscount = isApplyDiscountButtonLoading();
  const shouldShowPending = state.discountCode && !state.discountApplied && !isApplyingDiscount;
  
  updateDiscountFormControls();

  // Payment overview already shows Rabat + Betal nu — skip the duplicate breakdown box.
  if (state.discountApplied) {
    if (discountDisplay) discountDisplay.style.display = 'none';
    return;
  }

  if (shouldShowPending) {
    // Ensure subtotal is calculated - but don't call updateCartTotals() to avoid recursion
    // Instead, just recalculate subtotal if needed
    if (!state.totals.subtotal || state.totals.subtotal === 0) {
      const items = [];
      if (state.membershipPlanId) {
        const plan = findMembershipPlan(state.membershipPlanId);
        if (plan) items.push({ amount: plan.price });
      }
      state.valueCardQuantities.forEach((quantity, planId) => {
        if (quantity > 0) {
          const plan = findValueCard(planId);
          if (plan) items.push({ amount: plan.price * quantity });
        }
      });
      state.addonIds.forEach((addonId) => {
        const addon = findAddon(addonId);
        if (addon) {
          const price = getAddonPrice(addon);
          items.push({ amount: price });
        }
      });
      state.totals.subtotal = roundToHalfKrone(items.reduce((total, item) => total + item.amount, 0));
    }
    
    if (!discountDisplay) {
      // Create discount display element
      discountDisplay = document.createElement('div');
      discountDisplay.className = 'discount-display';

      // Prefer placing under the discount form
      if (DOM.discountForm) {
        DOM.discountForm.insertAdjacentElement('afterend', discountDisplay);
      } else {
        // Fallback: insert before cart total
        const cartTotalEl = document.querySelector('.cart-total');
        if (cartTotalEl) {
          cartTotalEl.insertAdjacentElement('beforebegin', discountDisplay);
        } else {
          // Fallback: try to find cart total by data attribute
          const cartTotalByAttr = document.querySelector('[data-summary-field="cart-total"]');
          if (cartTotalByAttr && cartTotalByAttr.parentElement) {
            cartTotalByAttr.parentElement.insertBefore(discountDisplay, cartTotalByAttr);
          }
        }
      }
      DOM.discountDisplay = discountDisplay;
    }
    
    if (state.discountCode && !state.discountApplied) {
      // Discount code entered but not yet applied (pending) - show subtotal only
      discountDisplay.innerHTML = sanitizeHTML(`
        <div class="discount-row">
          <span class="discount-label">Subtotal:</span>
          <span class="discount-value">${formatCurrencyHalfKrone(state.totals.subtotal)}</span>
        </div>
        <div class="discount-row discount-pending" style="opacity: 0.7; font-style: italic;">
          <span class="discount-label">Discount code (${state.discountCode}):</span>
          <span class="discount-value">Will be applied at checkout</span>
        </div>
        <div class="discount-row discount-total" style="margin-top: 8px; padding-top: 8px; border-top: 1px solid rgba(255,255,255,0.1);">
          <span class="discount-label" style="font-weight: bold;">Total:</span>
          <span class="discount-value" style="font-weight: bold;">${formatCurrencyHalfKrone(state.totals.cartTotal)}</span>
        </div>
      `);
    }
    discountDisplay.style.display = 'block';
  } else if (discountDisplay) {
    discountDisplay.style.display = 'none';
  }
}

function persistOrderSnapshot(orderId) {
  if (!orderId) return;
  try {
    sessionStorage.setItem('boulders_checkout_order', JSON.stringify({
      orderId,
      membershipPlanId: state.membershipPlanId,
      cartItems: state.cartItems || [],
      totals: state.totals,
      selectedBusinessUnit: state.selectedBusinessUnit,
      selectedProductType: state.selectedProductType, // Store product type for restoration
      selectedProductId: state.selectedProductId, // Store product ID for restoration
      subscriptionStartDate: state.subscriptionStartDate, // 15-day pass activation date
      paymentMethod: state.paymentMethod,
    }));
  } catch (e) {
    console.warn('[checkout] Could not save order to sessionStorage:', e);
  }
}

function clearStoredOrderData(reason = 'manual') {
  console.log(`[checkout] Clearing stored order data (${reason})`);
  state.order = null;
  state.orderId = null;
  state.subscriptionAttachedOrderId = null;
  state.paymentLink = null;
  state.paymentLinkGenerated = false;
  state.checkoutInProgress = false;
  updateFAQVisibility(); // Show FAQ again when order data is cleared
  state.cartItems = [];
  state.totals = {
    cartTotal: 0,
    membershipMonthly: 0,
  };

  try {
    sessionStorage.removeItem('boulders_checkout_order');
  } catch (error) {
    console.warn('[checkout] Could not clear order session data:', error);
  }
}

function resetOrderStateForProductChange(reason = 'product-change') {
  const hadOrderData = Boolean(state.orderId || state.fullOrder || state.paymentLink || state.paymentLinkGenerated);
  const hadDiscount = Boolean(state.discountApplied || state.discountCode);

  // Always clear cart when switching product/plan (subscription ↔ 15-day ↔ punch card ↔ different subscription)
  // So addons and other cart state never carry over to a new product
  console.log(`[checkout] Resetting order state (${reason})`);
  state.addonIds.clear();
  state.cartItems = [];
  state.valueCardQuantities.clear();

  // Deselect addon cards in UI so they match cleared addonIds
  document.querySelectorAll('.addon-card.selected, .plan-card.addon-card.selected').forEach((card) => {
    card.classList.remove('selected');
    const button = card.querySelector('[data-action="toggle-addon"]');
    if (button) {
      button.textContent = t('button.addToCart');
      button.setAttribute('data-i18n-key', 'button.addToCart');
    }
  });

  updateCartSummary();

  // Clear order/discount state only when we had order or discount
  if (!hadOrderData && !hadDiscount) {
    return;
  }

  state.order = null;
  state.fullOrder = null;
  state.orderId = null;
  state.subscriptionAttachedOrderId = null;
  state.subscriptionStartDate = null;
  state.paymentLink = null;
  state.paymentLinkGenerated = false;
  state.checkoutInProgress = false;
  state.billingPeriod = '';
  resetLocalDiscountState();
  state.totals.payNowAmount = 0;

  // Re-enable logout button and reset checkout button if they were disabled during checkout
  setCheckoutLoadingState(false);

  clearDiscountMessage();
  updateDiscountDisplay();

  try {
    sessionStorage.removeItem('boulders_checkout_order');
  } catch (error) {
    console.warn('[checkout] Could not clear order session data:', error);
  }
}

/** True when cart selection is a subscription product (membership, campaign, 15-day pass, free trial), not punch-cards. */
function isCheckoutSubscriptionPlan() {
  const planId = state.membershipPlanId;
  if (!planId) return false;
  if (state.selectedProductType === 'punch-card') return false;
  if (typeof planId === 'string' && planId.startsWith('punch-')) return false;
  return (
    state.selectedProductType === 'membership' ||
    state.selectedProductType === '15daypass' ||
    (typeof planId === 'string' &&
      (planId.startsWith('membership-') ||
        planId.startsWith('campaign-') ||
        planId.startsWith('15daypass-') ||
        planId.startsWith('freetrial-')))
  );
}

/**
 * Best-effort parse of amount still owed on an order (amount/currency shape varies by endpoint).
 */
function extractOrderLeftToPayAmount(order) {
  if (!order) return null;
  const raw =
    order.leftToPay?.amount ??
    order.leftToPay ??
    order.price?.leftToPay?.amount ??
    order.price?.leftToPay ??
    null;
  if (raw === null || raw === undefined) return null;
  const n = typeof raw === 'number' ? raw : Number(raw);
  return Number.isFinite(n) ? n : null;
}

/**
 * BRP normally creates the membership after payment is registered (leftToPay cleared), often via PSP webhook.
 * Clearing preliminary alone is not always enough (see docs/backend-issues/archive/MEMBERSHIP_CREATION_ROOT_CAUSE.md).
 * Free/zero flows should still open the Join payment URL when the API returns one so completion can register.
 */
async function finalizePreliminaryZeroBalanceOrder(orderId, context = 'checkout-free-flow') {
  if (!orderId) return null;
  try {
    let order = await orderAPI.getOrder(orderId);
    const leftAmt = extractOrderLeftToPayAmount(order);

    const isPreliminary = order.preliminary === true;
    if (!isPreliminary) {
      state.fullOrder = order;
      return order;
    }

    if (leftAmt !== null && leftAmt > 0) {
      console.warn(`[checkout] ${context}: preliminary order still has balance — not finalizing client-side`, {
        leftAmt,
      });
      state.fullOrder = order;
      return order;
    }

    const updateData = {
      preliminary: false,
      businessUnit: state.selectedBusinessUnit || order.businessUnit?.id || order.businessUnit,
    };
    console.log(`[checkout] ${context}: finalizing preliminary order (zero or unknown balance)`, {
      orderId,
      updateData,
      leftAmt,
    });
    await orderAPI.updateOrder(orderId, updateData);
    await new Promise((resolve) => setTimeout(resolve, 500));
    order = await orderAPI.getOrder(orderId);
    state.fullOrder = order;
    console.log(`[checkout] ${context}: finalize complete`, {
      preliminary: order.preliminary,
      leftToPay: order.leftToPay,
    });
    return order;
  } catch (error) {
    console.error(`[checkout] ${context}: finalize preliminary order failed`, error);
    return null;
  }
}

async function ensureOrderCreated(context = 'auto') {
  if (state.orderId) {
    console.log(`[checkout] Reusing existing order ${state.orderId} (${context})`);
    persistOrderSnapshot(state.orderId);
    return state.orderId;
  }

  if (orderCreationPromise) {
    console.log(`[checkout] Awaiting existing order creation (${context})`);
    return orderCreationPromise;
  }

  if (!state.customerId) {
    console.warn(`[checkout] Cannot create order (${context}) - customerId missing`);
    return null;
  }

  if (!state.selectedBusinessUnit) {
    console.warn(`[checkout] Cannot create order (${context}) - business unit missing`);
    return null;
  }

  const orderData = {
    customer: Number(state.customerId),
    businessUnit: state.selectedBusinessUnit,
  };

  orderCreationPromise = (async () => {
    console.log(`[checkout] Creating order (context: ${context})...`);
    const order = await orderAPI.createOrder(orderData);
    state.orderId = order.id || order.orderId;
    state.subscriptionAttachedOrderId = null;
    
    // Store full order object for payment overview
    // Note: This order might not have subscriptionItems yet, but we'll update it later
    state.fullOrder = order;
    
    // Update payment overview if we're on step 4
    if (state.currentStep === 4) {
      updatePaymentOverview();
    }
    
    persistOrderSnapshot(state.orderId);
    console.log(`[checkout] Order ready: ${state.orderId} (${context})`);
    return state.orderId;
  })();

  try {
    return await orderCreationPromise;
  } finally {
    orderCreationPromise = null;
  }
}

async function ensureSubscriptionAttached(context = 'auto') {
  // Subscription plans (membership, campaign, 15-day trial, freetrial landing) — not punch cards.
  const isMembership = isCheckoutSubscriptionPlan();

  if (!state.membershipPlanId || !isMembership) {
    if (state.membershipPlanId && !isMembership) {
      console.log(`[checkout] Skipping subscription attach (${context}) - not a subscription plan`);
      console.log(`[checkout] membershipPlanId: ${state.membershipPlanId}, selectedProductType: ${state.selectedProductType}`);
    } else {
      console.warn(`[checkout] Cannot attach subscription (${context}) - no membership selected`);
    }
    return null;
  }

  const orderId = await ensureOrderCreated(`${context}-subscription`);
  if (!orderId) {
    console.warn(`[checkout] Cannot attach subscription (${context}) - order missing`);
    return null;
  }

  if (state.subscriptionAttachedOrderId === orderId) {
    console.log(`[checkout] Subscription already attached to order ${orderId} (${context})`);
    return orderId;
  }

  if (subscriptionAttachPromise) {
    console.log(`[checkout] Awaiting existing subscription attach (${context})`);
    return subscriptionAttachPromise;
  }

  subscriptionAttachPromise = (async () => {
    console.log(`[checkout] Attaching membership ${state.membershipPlanId} to order ${orderId} (${context})...`);
    
    // 15-day pass: use selected activation date; others use today
    const is15DayPass =
      typeof state.membershipPlanId === 'string' &&
      (state.membershipPlanId.startsWith('15daypass-') || state.membershipPlanId.startsWith('freetrial-'));
    const startDate = is15DayPass ? (state.subscriptionStartDate || getTodayLocalDateString()) : undefined;
    
    // Add subscription item - this may return updated order if startDate was fixed by re-adding
    const subscriptionResponse = await orderAPI.addSubscriptionItem(orderId, state.membershipPlanId, startDate);
    state.subscriptionAttachedOrderId = orderId;
    
    // CRITICAL: Use the order from addSubscriptionItem response if available (has correct price after re-add)
    // Otherwise fetch updated order with subscriptionItems for payment overview
    let updatedOrder = subscriptionResponse;
    if (!updatedOrder || !updatedOrder.subscriptionItems) {
      try {
        updatedOrder = await orderAPI.getOrder(orderId);
      } catch (error) {
        console.warn(`[checkout] Could not fetch order for payment overview:`, error);
        return orderId;
      }
    }
    
    // Store full order for payment overview - this now has the correct price
    state.fullOrder = updatedOrder;
    state.order = updatedOrder; // Also update state.order for backward compatibility
    
    // CRITICAL: Store subscription item ID for linking addons via additionTo
    const subscriptionItem = updatedOrder?.subscriptionItems?.[0];
    if (subscriptionItem?.id) {
      state.subscriptionItemId = subscriptionItem.id;
      console.log('[ensureSubscriptionAttached] ✅ Stored subscription item ID:', state.subscriptionItemId);
    } else {
      console.warn('[ensureSubscriptionAttached] ⚠️ No subscription item ID found - addons may not be linked correctly');
    }
    
    // Log the order price to verify it matches what will be sent to payment window
    const orderPriceDKK = updatedOrder?.price?.amount ? updatedOrder.price.amount / 100 : 0;
    const initialPeriodStart = subscriptionItem?.initialPaymentPeriod?.start;
    console.log('[ensureSubscriptionAttached] ✅ Order data after subscription add:', {
      orderId,
      orderPrice: orderPriceDKK,
      initialPeriodStart,
      hasSubscriptionItems: !!subscriptionItem,
      productId: subscriptionItem?.product?.id,
      subscriptionItemId: subscriptionItem?.id,
    });
    
    updatePaymentOverview();
    console.log(`[checkout] Order updated with subscriptionItems for payment overview`);
    
    persistOrderSnapshot(orderId);
    console.log(`[checkout] Membership attached to order ${orderId} (${context})`);
    return orderId;
  })();

  try {
    return await subscriptionAttachPromise;
  } catch (error) {
    console.error(`[checkout] Failed to attach subscription (${context}):`, error);
    throw error;
  } finally {
    subscriptionAttachPromise = null;
  }
}

async function autoEnsureOrderIfReady(context = 'auto') {
  if (!isUserAuthenticated()) {
    return;
  }

  const isMembership = isCheckoutSubscriptionPlan();

  if (!state.membershipPlanId || !isMembership) {
    if (state.membershipPlanId && !isMembership) {
      console.log(`[checkout] Skipping auto ensure order (${context}) - not a subscription plan`);
    }
    return;
  }
  if (!state.selectedBusinessUnit) {
    return;
  }
  try {
    await ensureOrderCreated(`${context}-order`);
    await ensureSubscriptionAttached(`${context}-subscription`);
  } catch (error) {
    console.warn(`[checkout] Auto ensure order failed (${context}):`, error);
  }
}

function isRateLimitError(error) {
  if (!error) return false;
  if (error.status === 429) return true;
  const message = typeof error.message === 'string' ? error.message : '';
  return message.includes('429') || message.toLowerCase().includes('too many requests');
}

function getRetryDelayFromError(error, defaultMs = 120000) {
  // Default to 2 minutes (120 seconds) if we can't extract retryAfter
  const message = typeof error?.message === 'string' ? error.message : '';
  
  // Try to extract retryAfter from JSON in error message
  try {
    const jsonMatch = message.match(/\{[\s\S]*"retryAfter"[\s\S]*\}/);
    if (jsonMatch) {
      const jsonData = JSON.parse(jsonMatch[0]);
      if (jsonData.retryAfter) {
        const seconds = parseInt(jsonData.retryAfter, 10);
        if (!isNaN(seconds) && seconds > 0) {
          // Use the actual retryAfter time from API (no cap)
          // Convert seconds to milliseconds
          return Math.max(seconds * 1000, 1000);
        }
      }
    }
  } catch (e) {
    // JSON parse failed, try regex fallback
  }
  
  // Fallback: regex extraction
  const match = message.match(/"retryAfter":\s*(\d+)/i);
  if (match) {
    const seconds = parseInt(match[1], 10);
    if (!isNaN(seconds) && seconds > 0) {
      // Use the actual retryAfter time from API (no cap)
      return Math.max(seconds * 1000, 1000);
    }
  }
  
  return defaultMs;
}

function handleCheckoutClick() {
  handleCheckout();
}

async function handleCheckout() {
  // Prevent multiple simultaneous checkout attempts
  if (state.checkoutInProgress) {
    console.log('[checkout] Checkout already in progress, ignoring duplicate request');
    return;
  }
  
  // Validation (existing)
  const validationResult = validateForm(true);
  if (!validationResult.isValid) {
    console.log('[checkout] Validation failed:', validationResult);
    showToast(validationResult.message || 'Please review the highlighted fields.', 'error');
    // Animate checkout button with red flash
    if (DOM.checkoutBtn) {
      DOM.checkoutBtn.classList.remove('error-flash');
      // Trigger reflow to restart animation
      void DOM.checkoutBtn.offsetWidth;
      DOM.checkoutBtn.classList.add('error-flash');
      setTimeout(() => {
        DOM.checkoutBtn.classList.remove('error-flash');
      }, 600);
    }
    return;
  }

  // Allow checkout if either membership OR punch cards are selected
  const hasMembership = !!state.membershipPlanId;
  const hasPunchCards = state.valueCardQuantities && 
    Array.from(state.valueCardQuantities.values()).some(qty => qty > 0);
  const hasAddons = state.addonIds && state.addonIds.size > 0;

  if (!hasMembership && !hasPunchCards && !hasAddons) {
    showToast('Select a membership, punch card, or add-on to continue.', 'error');
    // Animate checkout button with red flash
    if (DOM.checkoutBtn) {
      DOM.checkoutBtn.classList.remove('error-flash');
      void DOM.checkoutBtn.offsetWidth;
      DOM.checkoutBtn.classList.add('error-flash');
      setTimeout(() => {
        DOM.checkoutBtn.classList.remove('error-flash');
      }, 600);
    }
    return;
  }

  // Guardrail: block campaign membership checkout for users who meet
  // "Bloker oprettelse for brugere der har ABO med produktmærke Medlem".
  if (hasCampaignInCart()) {
    await refreshAuthenticatedCustomerForCampaignGuard();
  }
  if (hasCampaignInCart() && customerHasMembershipCampaignBlockRequirement()) {
    showCampaignRejectionModal();
    return;
  }
  
  // Mark checkout as in progress to prevent state resets
  state.checkoutInProgress = true;
  
  // Hide FAQ section when checkout starts
  updateFAQVisibility();

  // GTM: Track begin_checkout event
  if (window.GTM && window.GTM.trackBeginCheckout && state.cartItems && state.cartItems.length > 0) {
    try {
      window.GTM.trackBeginCheckout(state.cartItems, state.totals.cartTotal, 'DKK');
    } catch (error) {
      console.warn('[GTM] Error tracking begin_checkout:', error);
    }
  }

  // Payment method UI removed - use card by default
  if (!state.paymentMethod) {
    state.paymentMethod = 'card';
  }

  // Set loading state
  setCheckoutLoadingState(true);

  try {
    const payload = buildCheckoutPayload();
    state.forms = payload;
    const customerEmail = payload.customer?.email || state.authenticatedEmail || null;

    // Step 1: Create or authenticate customer
    let customer = null;
    let customerId = state.customerId ?? null;
    
    // Check if user is already logged in
    const accessToken = typeof window.getAccessToken === 'function' ? window.getAccessToken() : null;
    if (accessToken && !customerId) {
      const metadata = getTokenMetadata();
      const metadataId = metadata?.username || metadata?.userName;
      if (metadataId) {
        customerId = String(metadataId);
        state.customerId = customerId;
      }
    }
    
    if (!accessToken) {
      // Create new customer
      try {
        console.log('[checkout] Creating customer...');
        console.log('[checkout] Full payload:', JSON.stringify(payload, null, 2));
        console.log('[checkout] Customer payload:', JSON.stringify(payload.customer, null, 2));
        
        // Build customer data matching API expectations
        // The API expects fields at the top level, not nested under "customer"
        
        // Get phone number and country code
        const phoneNumber = payload.customer?.phone?.number || payload.customer?.phone;
        const phoneCountryCode = payload.customer?.phone?.countryCode || '+45';
        
        // Build mobilePhone object if we have phone number (API expects mobilePhone)
        let mobilePhone = null;
        if (phoneNumber) {
          mobilePhone = {
            countryCode: phoneCountryCode,
            number: phoneNumber
          };
        }
        
        // Get date of birth (API expects birthDate)
        const birthDate = payload.customer?.dateOfBirth;
        
        // Get address fields
        const streetAddress = payload.customer?.address?.street || payload.customer?.address;
        const city = payload.customer?.address?.city || payload.customer?.city;
        const postalCode = payload.customer?.address?.postalCode || payload.customer?.postalCode;
        // Country is always Denmark (DK) for this application
        const country = 'DK';
        
        // Build shippingAddress object if we have address data (API expects shippingAddress)
        let shippingAddress = null;
        if (streetAddress || city || postalCode) {
          shippingAddress = {};
          if (streetAddress) shippingAddress.street = streetAddress;
          if (city) shippingAddress.city = city;
          if (postalCode) shippingAddress.postalCode = postalCode;
          shippingAddress.country = country; // Always DK
        }
        
        // Get privacy/terms consent timestamp for acceptedRegistrationTerms
        // Use timestamp from localStorage if available, otherwise use current time
        let acceptedRegistrationTerms = null;
        if (payload.consent?.privacy || payload.consent?.terms) {
          try {
            const privacyTimestamp = localStorage.getItem('boulders_privacy_consent_timestamp');
            const termsTimestamp = localStorage.getItem('boulders_terms_consent_timestamp');
            // Use the earliest timestamp if both are present, otherwise use whichever is available
            if (privacyTimestamp && termsTimestamp) {
              acceptedRegistrationTerms = privacyTimestamp < termsTimestamp ? privacyTimestamp : termsTimestamp;
            } else if (privacyTimestamp) {
              acceptedRegistrationTerms = privacyTimestamp;
            } else if (termsTimestamp) {
              acceptedRegistrationTerms = termsTimestamp;
            } else {
              // Fallback: use current timestamp if consent is checked but no timestamp stored
              acceptedRegistrationTerms = new Date().toISOString();
            }
            console.log('[checkout] Using acceptedRegistrationTerms timestamp:', acceptedRegistrationTerms);
          } catch (e) {
            console.warn('[checkout] Could not get consent timestamp from localStorage, using current time:', e);
            acceptedRegistrationTerms = new Date().toISOString();
          }
        }
        
        const customerData = {
          email: payload.customer?.email,
          firstName: payload.customer?.firstName,
          lastName: payload.customer?.lastName,
          mobilePhone: mobilePhone, // API expects mobilePhone object with countryCode and number
          birthDate: birthDate, // API expects birthDate (not dateOfBirth)
          shippingAddress: shippingAddress, // API expects shippingAddress object
          address: streetAddress, // Keep for backward compatibility
          city: city, // Keep for backward compatibility
          postalCode: postalCode, // Keep for backward compatibility - ensure it's always included
          country: country, // Country from form or default to Denmark
          primaryGym: payload.customer?.primaryGym,
          password: payload.customer?.password,
          customerType: 1, // Required by API - numeric ID (typically 1 = Individual customer type)
          // Include marketing email consent - API field: allowMassSendEmail
          ...(payload.consent?.marketing !== undefined && { allowMassSendEmail: payload.consent.marketing }),
          // Include SMS consent - API field: allowMassSendSms
          ...(payload.consent?.sms !== undefined && { allowMassSendSms: payload.consent.sms }),
          // Include registration terms acceptance timestamp (required by API)
          ...(acceptedRegistrationTerms && { acceptedRegistrationTerms }),
        };
        
        // Also include phone and phoneCountryCode for backward compatibility
        if (phoneNumber) {
          customerData.phone = phoneNumber;
          customerData.phoneCountryCode = phoneCountryCode;
        }
        
        // Also include dateOfBirth for backward compatibility
        if (birthDate) {
          customerData.dateOfBirth = birthDate;
        }
        
        console.log('[checkout] Customer data before cleanup:', JSON.stringify(customerData, null, 2));
        if (customerData.allowMassSendEmail !== undefined) {
          console.log('[checkout] Marketing consent (allowMassSendEmail):', customerData.allowMassSendEmail);
        }
        if (customerData.allowMassSendSms !== undefined) {
          console.log('[checkout] SMS consent (allowMassSendSms):', customerData.allowMassSendSms);
        }
        
        // Remove undefined/null values (but keep empty strings for now to debug)
        Object.keys(customerData).forEach(key => {
          if (customerData[key] === undefined || customerData[key] === null) {
            delete customerData[key];
          }
        });
        
        console.log('[checkout] Customer data prepared:', JSON.stringify(customerData, null, 2));
        // Pre-check: Don't create account if user is already logged in with this email
        const email = customerData.email?.toLowerCase().trim();
        if (email) {
          const authenticatedEmail = state.authenticatedEmail || 
            (typeof window.getTokenMetadata === 'function' && window.getTokenMetadata()?.email);
          if (authenticatedEmail && authenticatedEmail.toLowerCase() === email) {
            console.log('[checkout] User already logged in with this email, skipping account creation');
            const metadata = getTokenMetadata();
            customerId = metadata?.username || metadata?.userName || state.customerId;
            if (customerId) {
              state.customerId = String(customerId);
            }
            // Skip account creation and proceed
          } else {
            console.log('[checkout] Proceeding with account creation. Duplicate detection will be handled by API; on duplicate we try login and continue.');
            customer = await authAPI.createCustomer(customerData);
            
            // Track this email as used
            state.createdEmails.add(email);
            try {
              const storedEmails = JSON.parse(localStorage.getItem('boulders_created_emails') || '[]');
              if (!storedEmails.includes(email)) {
                storedEmails.push(email);
                localStorage.setItem('boulders_created_emails', JSON.stringify(storedEmails));
              }
            } catch (e) {
              console.warn('[checkout] Could not store email in localStorage:', e);
            }
          }
        } else {
          customer = await authAPI.createCustomer(customerData);
        }
        
        // Extract customer ID from response - API returns {success: true, data: {id: ...}}
        if (customer) {
          customerId = customer?.data?.id || customer?.id || customer?.customerId || customer?.data?.customerId;
        }
        // Store customer ID in state for later use (e.g., in order summary)
        state.customerId = customerId;
        // Store customer data in sessionStorage for payment return
        try {
          sessionStorage.setItem('boulders_checkout_customer', JSON.stringify({
            id: customerId,
            firstName: payload.customer?.firstName,
            lastName: payload.customer?.lastName,
            email: payload.customer?.email,
            primaryGym: payload.customer?.primaryGym,
          }));
        } catch (e) {
          console.warn('[checkout] Could not save customer to sessionStorage:', e);
        }
        console.log('[checkout] Customer response:', customer);
        console.log('[checkout] Extracted customer ID:', customerId);
        
        // Save tokens if provided in customer creation response
        let hasTokens = false;
        if (customer?.accessToken && customer?.refreshToken) {
          if (typeof window.saveTokens === 'function') {
            const metadata = {
              username: customer?.username || customerEmail,
              email: customerEmail,
              roles: customer?.roles,
            };
            window.saveTokens(customer.accessToken, customer.refreshToken, undefined, metadata);
            syncAuthenticatedCustomerState(metadata.username, metadata.email);
            hasTokens = true;
            console.log('[checkout] ✅ Tokens saved from customer creation response');
            
            // Dispatch event to notify React components
            if (typeof window !== 'undefined') {
              window.dispatchEvent(new Event('auth-state-changed'));
            }
          }
        } else if (customer?.data?.accessToken && customer?.data?.refreshToken) {
          // Check if tokens are nested in data object
          if (typeof window.saveTokens === 'function') {
            const metadata = {
              username: customer?.data?.username || customerEmail,
              email: customerEmail,
              roles: customer?.data?.roles,
            };
            window.saveTokens(customer.data.accessToken, customer.data.refreshToken, undefined, metadata);
            syncAuthenticatedCustomerState(metadata.username, metadata.email);
            hasTokens = true;
            console.log('[checkout] ✅ Tokens saved from customer creation response (nested in data)');
            
            // Dispatch event to notify React components
            if (typeof window !== 'undefined') {
              window.dispatchEvent(new Event('auth-state-changed'));
            }
          }
        }
        
        // Check if we already have valid tokens before attempting login
        const existingAccessToken = typeof window.getAccessToken === 'function' ? window.getAccessToken() : null;
        const existingRefreshToken = typeof window.getRefreshToken === 'function' ? window.getRefreshToken() : null;
        const isTokenExpired = typeof window.isTokenExpired === 'function' ? window.isTokenExpired() : false;
        const hasValidTokens = existingAccessToken && existingRefreshToken && !isTokenExpired;
        
        // If no tokens from customer creation, try to use existing tokens or login
        if (!hasTokens && payload.customer?.email && payload.customer?.password) {
          if (hasValidTokens) {
            console.log('[checkout] Using existing valid authentication tokens (skipping login to avoid rate limits)');
            // Tokens already exist and are valid, no need to login again
          } else if (existingAccessToken && existingRefreshToken && isTokenExpired) {
            console.log('[checkout] Existing tokens expired, attempting to refresh...');
            try {
              await authAPI.refreshToken();
              console.log('[checkout] ✅ Token refreshed successfully');
            } catch (refreshError) {
              console.warn('[checkout] ⚠️ Token refresh failed, will attempt login:', refreshError);
              // Fall through to login attempt
            }
          }
          
          // Only attempt login if we don't have valid tokens
          if (!hasValidTokens && (!existingAccessToken || !existingRefreshToken || isTokenExpired)) {
            console.log('[checkout] No tokens available, attempting login...');
            try {
              const loginResponse = await authAPI.login(payload.customer.email, payload.customer.password);
              
              // Save tokens from login response (handle nested data structure)
              const loginPayload = loginResponse?.data ?? loginResponse;
              const loginAccessToken = loginPayload?.accessToken || loginPayload?.access_token;
              const loginRefreshToken = loginPayload?.refreshToken || loginPayload?.refresh_token;
              let loginExpiresAt = loginPayload?.expiresAt || loginPayload?.expires_at;
              const loginExpiresIn = loginPayload?.expiresIn || loginPayload?.expires_in;
              if (!loginExpiresAt && loginExpiresIn) {
                const expiresInMs = Number(loginExpiresIn) * 1000;
                loginExpiresAt = Date.now() + (Number.isFinite(expiresInMs) ? expiresInMs : 0);
              }
              
              if (loginAccessToken && loginRefreshToken && typeof window.saveTokens === 'function') {
                const loginMetadata = {
                  username: loginPayload?.username || payload.customer?.email,
                  email: loginPayload?.email || payload.customer?.email,
                  roles: loginPayload?.roles,
                  tokenType: loginPayload?.tokenType || loginPayload?.token_type,
                  expiresIn: loginPayload?.expiresIn || loginPayload?.expires_in,
                };
                window.saveTokens(loginAccessToken, loginRefreshToken, loginExpiresAt, loginMetadata);
                syncAuthenticatedCustomerState(loginMetadata.username, loginMetadata.email);
                console.log('[checkout] ✅ Login successful, tokens saved from login response');
              } else {
                console.warn('[checkout] ⚠️ Login succeeded but no tokens found in response');
              }
            } catch (loginError) {
              // Handle rate limit errors specifically
              const errorMessage = loginError.message || String(loginError);
              if (errorMessage.includes('429') || errorMessage.includes('Rate limit') || errorMessage.includes('Too many requests')) {
                // Extract retryAfter from error message (it's in seconds)
                let retryAfterSeconds = 60; // Default 1 minute (60 seconds) - more reasonable
                const retryAfterMatch = errorMessage.match(/retryAfter["\s:]*(\d+)/i);
                if (retryAfterMatch) {
                  retryAfterSeconds = parseInt(retryAfterMatch[1], 10);
                  // Cap at 2 minutes (120 seconds) for better UX - API might say 15 minutes but that's too long
                  retryAfterSeconds = Math.min(retryAfterSeconds, 120);
                }
                
                const retryMinutes = Math.ceil(retryAfterSeconds / 60);
                const retrySeconds = retryAfterSeconds % 60;
                const retryMessage = retryMinutes > 0 
                  ? `${retryMinutes} minute${retryMinutes !== 1 ? 's' : ''}${retrySeconds > 0 ? ` and ${retrySeconds} second${retrySeconds !== 1 ? 's' : ''}` : ''}`
                  : `${retryAfterSeconds} second${retryAfterSeconds !== 1 ? 's' : ''}`;
                
                console.error(`[checkout] ⚠️ Rate limit exceeded. Please wait ${retryMessage} before trying again.`);
                showToast(`Rate limit exceeded. Please wait ${retryMessage} before trying again.`, 'error');
                
                // If we have existing tokens, try to use them
                if (existingAccessToken && existingRefreshToken) {
                  console.log('[checkout] Attempting to use existing tokens despite rate limit...');
                  // Tokens might still be valid, continue with checkout
                } else {
                  setCheckoutLoadingState(false);
                  state.checkoutInProgress = false;
                  throw new Error(`Rate limit exceeded. Please wait ${retryMessage} before trying again.`);
                }
              } else {
                console.warn('[checkout] ⚠️ Login after customer creation failed:', loginError);
                // If we have existing tokens, try to use them
                if (existingAccessToken && existingRefreshToken) {
                  console.log('[checkout] Login failed but existing tokens available, continuing with checkout...');
                } else {
                  console.warn('[checkout] Payment link generation might fail without authentication token');
                }
              }
            }
          }
        }
        
        console.log('[checkout] Customer created:', customerId);
        try {
          await ensureOrderCreated('profile-create');
          await ensureSubscriptionAttached('profile-create');
        } catch (orderError) {
          console.warn('[checkout] Could not auto-attach membership after profile creation:', orderError);
        }
      } catch (error) {
        console.error('[checkout] Customer creation failed:', error);
        let duplicateResolvedWithLogin = false;

        // Handle duplicate email error: try login with provided credentials and continue on success
        if (error.isDuplicateEmail || (error.message && error.message.includes('already exists'))) {
          const emailVal = (payload.customer?.email?.trim() || '').toLowerCase();
          const password = payload.customer?.password || customerData?.password;
          if (emailVal && password) {
            try {
              await authAPI.login(emailVal, password, { saveTokens: true });
              await syncAuthenticatedCustomerState();
              customerId = state.customerId || getTokenMetadata()?.username || getTokenMetadata()?.userName;
              if (customerId) state.customerId = String(customerId);
              try {
                sessionStorage.setItem('boulders_checkout_customer', JSON.stringify({
                  id: customerId,
                  firstName: payload.customer?.firstName,
                  lastName: payload.customer?.lastName,
                  email: payload.customer?.email,
                  primaryGym: payload.customer?.primaryGym,
                }));
              } catch (e) {
                console.warn('[checkout] Could not save customer to sessionStorage:', e);
              }
              await ensureOrderCreated('profile-create');
              await ensureSubscriptionAttached('profile-create');
              duplicateResolvedWithLogin = true;
            } catch (loginErr) {
              const duplicateMessage = `An account with this email address${emailVal ? ` (${emailVal})` : ''} already exists. Please log in instead.`;
              showToast(duplicateMessage, 'error');
              const emailInput = document.getElementById('email');
              if (emailInput) emailInput.closest('.form-group')?.classList.add('error');
              if (emailVal) switchAuthMode('login', emailVal);
              else switchAuthMode('login');
              setCheckoutLoadingState(false);
              state.checkoutInProgress = false;
              return;
            }
          } else {
            const duplicateMessage = `An account with this email address${emailVal ? ` (${emailVal})` : ''} already exists. Please log in instead.`;
            showToast(duplicateMessage, 'error');
            const emailInput = document.getElementById('email');
            if (emailInput) emailInput.closest('.form-group')?.classList.add('error');
            if (emailVal) switchAuthMode('login', emailVal);
            else switchAuthMode('login');
            setCheckoutLoadingState(false);
            state.checkoutInProgress = false;
            return;
          }
        }

        if (!duplicateResolvedWithLogin) {
          // Extract and highlight error fields
          const errorFields = extractErrorFields(error);
          errorFields.forEach(({ fieldId }) => {
            if (fieldId) {
              highlightFieldError(fieldId, false);
            }
          });
          showToast(getErrorMessage(error, 'Customer creation'), 'error');
          setCheckoutLoadingState(false);
          state.checkoutInProgress = false;
          return;
        }
      }
    } else {
      // User is logged in, get customer ID from token or state
      // For now, we'll proceed with order creation
      console.log('[checkout] User is authenticated');
    }

    // Step 2: Ensure order exists (create if needed)
    try {
      console.log('[checkout] Ensuring order exists before adding items...');
      const ensuredOrderId = await ensureOrderCreated('checkout-flow');
      if (!ensuredOrderId) {
        throw new Error('Order ID missing after ensureOrderCreated');
      }
      state.orderId = ensuredOrderId;
      
      persistOrderSnapshot(state.orderId);
    } catch (error) {
      console.error('[checkout] Order creation failed:', error);
      showToast(getErrorMessage(error, 'Order creation'), 'error');
      setCheckoutLoadingState(false);
      state.checkoutInProgress = false;
      return;
    }

    // Step 3: Add items to order and generate payment link
    // Backend requirement: Generate Payment Link Card immediately after subscription is added to cart
    // Note: We do not use Assently or any separate e-signature step. Checkout goes straight to payment.
    let paymentLink = null;
    
    try {
      console.log('[checkout] Adding items to order...');

      const isMembership = isCheckoutSubscriptionPlan();

      // Add membership/subscription FIRST (only if it's actually a membership)
      if (isMembership) {
	        try {
	          await ensureSubscriptionAttached('checkout-flow');
	          console.log('[checkout] Membership ensured on order');
	          
	          // Ensure payment overview is updated after subscription is attached
	          // ensureSubscriptionAttached already fetches order and calls updatePaymentOverview(),
	          // but let's make sure it's called here too
	          if (state.order) {
	            updatePaymentOverview();
	          }
          
          // CRITICAL: Apply coupon BEFORE generating payment link
          // This ensures the payment portal shows the discounted price
          const discountCodeToApply = (DOM.discountInput && DOM.discountInput.value.trim()) 
            ? DOM.discountInput.value.trim().toUpperCase()
            : state.discountCode;
          
          // Apply discount if we have a code and an order, even if already marked as applied
          // We need to ensure it's actually on the order before generating payment link
          if (discountCodeToApply && state.orderId) {
            try {
              // First, check if discount is already on the order
              console.log('[checkout] ===== CHECKING/APPLYING COUPON BEFORE PAYMENT LINK =====');
              console.log('[checkout] Coupon code:', discountCodeToApply);
              console.log('[checkout] Order ID:', state.orderId);
              console.log('[checkout] Current state - discountApplied:', state.discountApplied, 'discountAmount:', state.totals.discountAmount);
              
              // Check current order state first
              let orderCheck = null;
              try {
                orderCheck = await orderAPI.getOrder(state.orderId);
                
                // Store full order object for payment overview
                state.fullOrder = orderCheck;
                updatePaymentOverview();
                
                const existingCouponDiscount = orderCheck?.couponDiscount || orderCheck?.price?.couponDiscount;
                if (existingCouponDiscount) {
                  console.log('[checkout] ✅ Discount already exists on order, verifying amount...');
                  const subtotal = state.totals.subtotal || state.totals.cartTotal || 0;
                  const existingDiscountAmount = roundToHalfKrone(
                    calculateOrderCouponDiscountKr(orderCheck, subtotal)
                  );
                  const orderTotalFromAPI = extractCurrencyOutKr(
                    orderCheck?.price?.amount ?? orderCheck?.price?.total ?? orderCheck?.price?.leftToPay
                  );

                  if (existingDiscountAmount > 0 || orderTotalFromAPI > 0) {
                    console.log('[checkout] Using existing discount amount from order:', existingDiscountAmount);
                    console.log('[checkout] Order total from API:', orderTotalFromAPI);
                    state.discountCode = discountCodeToApply;
                    state.discountApplied = true;
                    state.totals.discountAmount = existingDiscountAmount;
                    // Update cart total to match order total from API if available
                    if (orderTotalFromAPI > 0) {
                      state.totals.cartTotal = orderTotalFromAPI;
                      console.log('[checkout] Updated cart total to match order total from API:', orderTotalFromAPI);
                    }
                    updateCartSummary();
                    // Skip applying discount again - it's already on the order
                    // Continue to payment link generation
                    console.log('[checkout] ✅ Discount verified on order, proceeding to payment link');
                  }
                }
              } catch (checkError) {
                console.warn('[checkout] Could not check order before applying discount:', checkError);
              }
              
              // Only apply discount if it's not already on the order
              if (!orderCheck || !(orderCheck?.couponDiscount || orderCheck?.price?.couponDiscount)) {
                console.log('[checkout] Discount not found on order, applying now...');
                const discountResponse = await orderAPI.applyDiscountCode(state.orderId, discountCodeToApply);
                console.log('[checkout] Coupon API response:', JSON.stringify(discountResponse, null, 2));

                const subtotal = state.totals.subtotal || state.totals.cartTotal || 0;
                let discountAmount = calculateOrderCouponDiscountKr(discountResponse, subtotal);
                if (discountAmount <= 0 && discountResponse?.discountAmount != null) {
                  discountAmount = extractCurrencyOutKr(discountResponse.discountAmount);
                }
                discountAmount = roundToHalfKrone(capCouponDiscountKr(discountAmount, subtotal));

              if (discountAmount > 0) {
                state.discountCode = discountCodeToApply;
                state.discountApplied = true;
                state.totals.discountAmount = roundToHalfKrone(discountAmount);
                updateCartSummary(); // Use API-based cart update function
                console.log('[checkout] ✅ Coupon applied before payment link:', discountCodeToApply, 'Amount:', discountAmount);

                // CRITICAL: Ensure coupon impacts the /api/orders representation used by payment link generation
                // If /api/orders doesn't reflect the coupon, payment window will still show full price.
                try {
                  const orderAfterCoupon = await orderAPI.getOrder(state.orderId);
                  const orderAfterAmountRaw = orderAfterCoupon?.price?.amount;
                  const orderAfterAmountCents = typeof orderAfterAmountRaw === 'object'
                    ? (orderAfterAmountRaw?.amount ?? null)
                    : orderAfterAmountRaw;
                  const orderAfterAmountDKK = (typeof orderAfterAmountCents === 'number')
                    ? (orderAfterAmountCents / 100)
                    : null;
                  const hasCouponOnApiOrders = !!(orderAfterCoupon?.couponDiscount || orderAfterCoupon?.price?.couponDiscount);
                  const expectedTotalDKK = Math.max(0, (state.totals.subtotal || state.totals.cartTotal || 0) - discountAmount);

                  console.log('[checkout] Post-coupon /api/orders verification:', {
                    hasCouponOnApiOrders,
                    orderPriceAmount: orderAfterAmountRaw,
                    orderPriceDKK: orderAfterAmountDKK,
                    expectedTotalDKK,
                  });

                  const priceMatches = typeof orderAfterAmountDKK === 'number' && Math.abs(orderAfterAmountDKK - expectedTotalDKK) < 1;
                  if (!hasCouponOnApiOrders && !priceMatches) {
                    console.warn('[checkout] ⚠️ Coupon not reflected in /api/orders - applying via /api/orders coupon endpoint...');
                    await orderAPI.applyDiscountCodeApiOrders(state.orderId, discountCodeToApply);
                    const orderAfterAlt = await orderAPI.getOrder(state.orderId);
                    state.fullOrder = orderAfterAlt;
                    updatePaymentOverview();
                    console.log('[checkout] ✅ /api/orders coupon apply done. Updated order price:', orderAfterAlt?.price?.amount);
                  }
                } catch (apiOrdersCouponError) {
                  console.warn('[checkout] ⚠️ Could not verify/apply coupon via /api/orders endpoint:', apiOrdersCouponError);
                }
                
                // CRITICAL: Fetch updated order multiple times to ensure backend has processed the coupon
                // Payment link generation reads order total from the order, so we need to ensure it's updated
                let orderUpdated = false;
                let attempts = 0;
                const maxAttempts = 5;
                
                  // Ensure subtotal is calculated before checking order
                  if (!state.totals.subtotal || state.totals.subtotal === 0) {
                    updateCartSummary(); // Use API-based cart update function
                  }
                  
                  while (!orderUpdated && attempts < maxAttempts) {
                  attempts++;
                  try {
                    console.log(`[checkout] Fetching updated order (attempt ${attempts}/${maxAttempts}) to verify discount...`);
                    const updatedOrder = await orderAPI.getOrder(state.orderId);
                    
                    console.log('[checkout] Full order response:', JSON.stringify(updatedOrder, null, 2));
                    
                    // Store full order object for payment overview
                    state.fullOrder = updatedOrder;
                    updatePaymentOverview();
                    
                    // Check if order has the discount applied
                    const orderCouponDiscount = updatedOrder?.couponDiscount || updatedOrder?.price?.couponDiscount;
                    
                    // Try multiple ways to get order total
                    let orderTotal = updatedOrder?.price?.total?.amount || updatedOrder?.price?.total;
                    if (!orderTotal) orderTotal = updatedOrder?.price?.leftToPay?.amount || updatedOrder?.price?.leftToPay;
                    if (!orderTotal) orderTotal = updatedOrder?.total?.amount || updatedOrder?.total;
                    if (!orderTotal) orderTotal = updatedOrder?.price?.amount;
                    
                    // Convert to DKK if in cents (or if it's an object with amount property)
                    let orderTotalDKK = 0;
                    if (orderTotal) {
                      if (typeof orderTotal === 'object' && orderTotal.amount) {
                        orderTotalDKK = orderTotal.amount;
                      } else if (typeof orderTotal === 'number') {
                        orderTotalDKK = orderTotal;
                      }
                      // Convert from cents if needed
                      if (orderTotalDKK > 10000) {
                        orderTotalDKK = orderTotalDKK / 100;
                      }
                    }
                    
                    const subtotal = state.totals.subtotal || state.totals.cartTotal || 0;
                    const expectedTotal = Math.max(0, subtotal - discountAmount);
                    
                    console.log('[checkout] Order state:', {
                      orderCouponDiscount,
                      orderTotalRaw: orderTotal,
                      orderTotalDKK,
                      expectedTotal,
                      subtotal,
                      discountAmount,
                      hasCouponDiscount: !!orderCouponDiscount,
                    });
                    
                    // Verify discount is reflected - check if couponDiscount exists OR order total matches expected
                    if (orderCouponDiscount) {
                      // Coupon discount exists in order
                      orderUpdated = true;
                      console.log('[checkout] ✅ Order has couponDiscount, proceeding to payment link generation');
                    } else if (orderTotalDKK > 0 && Math.abs(orderTotalDKK - expectedTotal) < 1) {
                      // Order total matches expected discounted total
                      orderUpdated = true;
                      console.log('[checkout] ✅ Order total matches expected discounted total, proceeding to payment link generation');
                    } else if (orderTotalDKK === 0 && expectedTotal === 0) {
                      // Both are zero (100% discount)
                      orderUpdated = true;
                      console.log('[checkout] ✅ Order total is zero (100% discount), proceeding to payment link generation');
                    } else {
                      console.log('[checkout] Order not yet updated, waiting... (orderTotal:', orderTotalDKK, 'expected:', expectedTotal, ')');
                      await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1 second between attempts
                    }
                  } catch (fetchError) {
                    console.warn(`[checkout] Could not fetch updated order (attempt ${attempts}):`, fetchError);
                    if (attempts < maxAttempts) {
                      await new Promise(resolve => setTimeout(resolve, 1000));
                    }
                  }
                }
                
                if (!orderUpdated) {
                  console.warn('[checkout] ⚠️ Order may not be fully updated with discount, but proceeding with payment link generation');
                }
              } // End of if (discountAmount > 0)
            } // End of if (!orderCheck || !(orderCheck?.couponDiscount || orderCheck?.price?.couponDiscount))
            } catch (couponError) {
              console.warn('[checkout] Failed to apply coupon before payment link:', couponError);
              // Don't block checkout if coupon fails, but log warning
              console.warn('[checkout] Payment link may not reflect discount if coupon application failed');
            }
          } else if (discountCodeToApply && state.discountApplied) {
            // Discount code is stored and marked as applied, but verify it's actually on the order
            try {
              console.log('[checkout] Verifying discount is on order before payment link generation...');
              const orderCheck = await orderAPI.getOrder(state.orderId);
              const orderCouponDiscount = orderCheck?.couponDiscount || orderCheck?.price?.couponDiscount;
              
              if (!orderCouponDiscount) {
                console.warn('[checkout] Discount code marked as applied but not found on order, re-applying...');
                // Re-apply the discount
                const discountResponse = await orderAPI.applyDiscountCode(state.orderId, discountCodeToApply);
                console.log('[checkout] Re-applied discount, response:', JSON.stringify(discountResponse, null, 2));
              } else {
                console.log('[checkout] ✅ Discount confirmed on order before payment link generation');
              }
            } catch (verifyError) {
              console.warn('[checkout] Could not verify discount on order:', verifyError);
            }
          }
          
          // CRITICAL: Add add-ons/articles BEFORE generating payment link
          // This ensures the order total includes addons when payment link is generated
          // Payment link API reads order.price.amount, so addons must be added first
          // CRITICAL: Link addons to subscription using additionTo field
          if (state.addonIds && state.addonIds.size > 0) {
            console.log('[checkout] ===== ADDING ADDONS BEFORE PAYMENT LINK =====');
            console.log('[checkout] Adding', state.addonIds.size, 'addon(s) to order before payment link generation');
            
            // CRITICAL: Verify subscription item ID exists in current order before using it
            let validSubscriptionItemId = state.subscriptionItemId;
            if (!validSubscriptionItemId || validSubscriptionItemId === null) {
              console.warn('[checkout] ⚠️ Subscription item ID is missing, fetching order to get current subscription item ID...');
              try {
                const currentOrder = await orderAPI.getOrder(state.orderId);
                const subscriptionItem = currentOrder?.subscriptionItems?.[0];
                if (subscriptionItem?.id) {
                  validSubscriptionItemId = subscriptionItem.id;
                  state.subscriptionItemId = validSubscriptionItemId;
                  console.log('[checkout] ✅ Retrieved subscription item ID from order:', validSubscriptionItemId);
                } else {
                  console.error('[checkout] ❌ No subscription item found in order - cannot link addons');
                  throw new Error('No subscription item found in order - cannot add addons with additionTo link');
                }
              } catch (error) {
                console.error('[checkout] ❌ Failed to fetch order to verify subscription item ID:', error);
                throw new Error(`Cannot verify subscription item ID: ${error.message}`);
              }
            } else {
              // Verify the subscription item ID actually exists in the order
              try {
                const currentOrder = await orderAPI.getOrder(state.orderId);
                const subscriptionItemExists = currentOrder?.subscriptionItems?.some(
                  item => item.id === validSubscriptionItemId
                );
                if (!subscriptionItemExists) {
                  console.warn('[checkout] ⚠️ Stored subscription item ID not found in order, fetching current ID...');
                  const subscriptionItem = currentOrder?.subscriptionItems?.[0];
                  if (subscriptionItem?.id) {
                    validSubscriptionItemId = subscriptionItem.id;
                    state.subscriptionItemId = validSubscriptionItemId;
                    console.log('[checkout] ✅ Updated subscription item ID to:', validSubscriptionItemId);
                  } else {
                    console.error('[checkout] ❌ No subscription item found in order - cannot link addons');
                    throw new Error('No subscription item found in order - cannot add addons with additionTo link');
                  }
                } else {
                  console.log('[checkout] ✅ Verified subscription item ID exists in order:', validSubscriptionItemId);
                }
              } catch (error) {
                console.error('[checkout] ❌ Failed to verify subscription item ID:', error);
                // Don't throw - try to continue with stored ID, but log the warning
                console.warn('[checkout] ⚠️ Continuing with stored subscription item ID, but it may be invalid');
              }
            }
            
            console.log('[checkout] Subscription item ID for linking:', validSubscriptionItemId);
            
            const addonIdsArray = Array.from(state.addonIds);
            const addedAddonIds = [];
            
            for (const addonId of addonIdsArray) {
              try {
                // Determine if addon is a value card or article
                // CRITICAL: Check if product ID exists in valueCards array (most reliable method)
                // Value cards are loaded separately via /api/products/valuecards endpoint
                const numericId = typeof addonId === 'string' ? parseInt(addonId, 10) : addonId;
                const addon = findAddon(addonId);
                
                // Primary method: Check if product ID exists in valueCards (both regular and campaign)
                // This is the most reliable since value cards come from a different API endpoint
                const allValueCards = [
                  ...(state.valueCards || []),
                  ...(state.campaignValueCards || [])
                ];
                const isInValueCards = allValueCards.some(vc => {
                  const vcId = typeof vc.id === 'string' ? parseInt(vc.id, 10) : vc.id;
                  return vcId === numericId || String(vc.id) === String(addonId) || vc.id === addonId;
                });
                
                // Also check allRawProducts (includes value cards before filtering)
                const isInRawValueCards = state.allRawProducts && state.allRawProducts.some(p => {
                  const pId = typeof p.id === 'string' ? parseInt(p.id, 10) : p.id;
                  return (pId === numericId || String(p.id) === String(addonId) || p.id === addonId) &&
                         (p.productType === 'VALUE_CARD' || p.productType === 'VALUECARD' || 
                          // Check if it came from valueCards endpoint by checking if it's not a subscription
                          (!p.priceWithInterval && !p.subscriptionInterval));
                });
                
                // Secondary method: Check productType field if available
                const hasValueCardType = addon && (
                  addon.productType === 'VALUE_CARD' ||
                  addon.productType === 'VALUECARD' ||
                  (addon.product && (addon.product.productType === 'VALUE_CARD' || addon.product.productType === 'VALUECARD'))
                );
                
                // Tertiary method: Check if it's from valueCards API endpoint (has value card specific fields)
                const hasValueCardFields = addon && (
                  addon.valueCardType !== undefined ||
                  addon.validUntil !== undefined ||
                  (addon.product && addon.product.valueCardType !== undefined)
                );
                
                const isValueCard = isInValueCards || isInRawValueCards || hasValueCardType || hasValueCardFields;
                
                console.log(`[checkout] Addon ${addonId} type detection:`, {
                  addonId,
                  numericId,
                  isInValueCards,
                  isInRawValueCards,
                  hasValueCardType,
                  hasValueCardFields,
                  productType: addon?.productType || addon?.product?.productType,
                  isValueCard,
                  valueCardsCount: state.valueCards?.length || 0,
                  valueCardIds: state.valueCards?.map(vc => vc.id) || [],
                  allRawProductsCount: state.allRawProducts?.length || 0
                });
                
                if (isValueCard) {
                  // Add as value card with additionTo link. Pass amount (cents) so API FIELD_MANDATORY is satisfied.
                  let amountCents = getAddonPriceInCents(addon);
                  if (amountCents <= 0) {
                    const allVc = [...(state.valueCards || []), ...(state.campaignValueCards || [])];
                    const vcProduct = allVc.find(p => p.id === numericId || String(p.id) === String(addonId));
                    const priceObj = vcProduct?.price;
                    if (priceObj != null) {
                      amountCents = typeof priceObj === 'object' && 'amount' in priceObj ? Math.round(Number(priceObj.amount)) : Math.round(Number(priceObj));
                    }
                    if (vcProduct?.amount != null) amountCents = Math.round(Number(vcProduct.amount));
                  }
                  console.log(`[checkout] Adding addon ${addonId} as value card (linked to subscription item ${validSubscriptionItemId}), amount: ${amountCents} cents`);
                  await orderAPI.addValueCardItem(state.orderId, numericId, 1, validSubscriptionItemId, amountCents > 0 ? amountCents : null);
                } else {
                  // Add as article with additionTo link
                  console.log(`[checkout] Adding addon ${addonId} as article (linked to subscription item ${validSubscriptionItemId})`);
                  await orderAPI.addArticleItem(state.orderId, numericId, validSubscriptionItemId);
                }
                addedAddonIds.push(addonId);
                console.log(`[checkout] ✅ Add-on added: ${addonId}`);
              } catch (error) {
                console.error(`[checkout] ❌ Failed to add add-on ${addonId}:`, error);
                
                // Check if error is due to invalid additionTo (ID_NOT_FOUND)
                const errorMessage = error.message || JSON.stringify(error);
                const isInvalidAdditionTo = errorMessage.includes('ID_NOT_FOUND') || 
                                           errorMessage.includes('additionTo') ||
                                           (error.payload && typeof error.payload === 'string' && error.payload.includes('ID_NOT_FOUND'));
                
                if (isInvalidAdditionTo) {
                  console.error(`[checkout] ❌ CRITICAL: Invalid subscription item ID for addon ${addonId}`);
                  console.error(`[checkout] ❌ This means the subscription item doesn't exist in the order`);
                  console.error(`[checkout] ❌ Cannot proceed - payment window would show incorrect total`);
                  throw new Error(`Failed to link addon ${addonId} to subscription: subscription item not found in order. Please try selecting your membership again.`);
                }
                
                // Check if order is locked (403 Forbidden)
                const isOrderLocked = error.status === 403 || errorMessage.includes('locked') || errorMessage.includes('Forbidden');
                if (isOrderLocked) {
                  console.error(`[checkout] ❌ CRITICAL: Order is locked (403 Forbidden) - cannot add addon ${addonId}`);
                  console.error(`[checkout] ❌ Cannot proceed - payment window would show incorrect total`);
                  throw new Error(`Order is locked and cannot be modified. Please start a new checkout.`);
                }
                
                // For other errors, still throw to prevent incorrect payment
                console.error(`[checkout] ❌ BLOCKING PAYMENT - Addon ${addonId} failed to add`);
                throw new Error(`Failed to add addon ${addonId}: ${error.message || 'Unknown error'}`);
              }
            }
            
            // Refresh order and verify addons are actually in the order
            let addonsVerified = false;
            let attempts = 0;
            const maxAttempts = 5;
            
            while (!addonsVerified && attempts < maxAttempts && addedAddonIds.length > 0) {
              attempts++;
              try {
                console.log(`[checkout] Verifying addons in order (attempt ${attempts}/${maxAttempts})...`);
                await new Promise(resolve => setTimeout(resolve, 1000)); // Wait for backend to process
                
                const updatedOrder = await orderAPI.getOrder(state.orderId);
                state.fullOrder = updatedOrder;
                
                // Check both article items and value card items (addons can be either type)
                const articleItems = updatedOrder?.articleItems || [];
                const valueCardItems = updatedOrder?.valueCardItems || [];
                const articleProductIds = articleItems.map(item => item.product?.id).filter(Boolean);
                const valueCardProductIds = valueCardItems.map(item => item.product?.id).filter(Boolean);
                const allProductIds = [...articleProductIds, ...valueCardProductIds];
                
                // Check if all added addons are in the order (either as articles or value cards)
                const allAddonsPresent = addedAddonIds.every(id => 
                  allProductIds.includes(Number(id)) || allProductIds.includes(String(id))
                );
                
                if (allAddonsPresent) {
                  addonsVerified = true;
                  const orderTotalDKK = updatedOrder?.price?.amount 
                    ? (typeof updatedOrder.price.amount === 'object' 
                        ? updatedOrder.price.amount.amount / 100 
                        : updatedOrder.price.amount / 100)
                    : 0;
                  console.log('[checkout] ✅ All addons verified in order');
                  console.log('[checkout] ✅ Order total with addons:', orderTotalDKK, 'DKK');
                  console.log('[checkout] ✅ Article items in order:', articleItems.length);
                  console.log('[checkout] ✅ Value card items in order:', valueCardItems.length);
                  
                  // CRITICAL: Update state.fullOrder with the order that includes addons
                  // This ensures payment overview and payment link use the correct price
                  state.fullOrder = updatedOrder;
                  console.log('[checkout] ✅ Updated state.fullOrder with order containing addons');
                } else {
                  console.warn(`[checkout] ⚠️ Addons not yet in order (attempt ${attempts}/${maxAttempts})`);
                  console.warn('[checkout] Expected addon IDs:', addedAddonIds);
                  console.warn('[checkout] Article product IDs in order:', articleProductIds);
                  console.warn('[checkout] Value card product IDs in order:', valueCardProductIds);
                }
              } catch (refreshError) {
                console.warn(`[checkout] Could not verify addons (attempt ${attempts}):`, refreshError);
              }
            }
            
            if (!addonsVerified && addedAddonIds.length > 0) {
              console.error('[checkout] ❌ CRITICAL: Could not verify addons are in order after', maxAttempts, 'attempts');
              console.error('[checkout] ❌ Payment window will show incorrect total without addons!');
              console.error('[checkout] ❌ Expected addons:', addedAddonIds);
              
              // CRITICAL: If addons failed to add, we must prevent payment link generation
              // The payment window will show incorrect price if addons aren't in the order
              const errorMessage = `Failed to add addons to order. Payment cannot proceed with incorrect total. Please try again or contact support.`;
              console.error('[checkout] ❌ BLOCKING PAYMENT LINK GENERATION - Addons not in order');
              throw new Error(errorMessage);
            }
          }
          
          // CRITICAL: Generate Payment Link Card immediately after subscription is added AND coupon is applied AND addons are added
          // The order should now have the discount applied, so payment link will show discounted price
          // Backend requirement: "Generate Payment Link Card" request must be made when subscription is added to cart
          // This is what triggers the payment flow according to backend team
          console.log('[checkout] ===== GENERATE PAYMENT LINK CARD =====');
          console.log('[checkout] Generating Payment Link Card (backend requirement)');
          console.log('[checkout] Order ID:', state.orderId);
          console.log('[checkout] Payment Method:', state.paymentMethod);
          console.log('[checkout] Business Unit:', state.selectedBusinessUnit);
          
          const baseUrl = getReturnUrlBase();
          const returnUrl = `${baseUrl}${window.location.pathname}?payment=return&orderId=${state.orderId}`;
          console.log('[checkout] Return URL:', returnUrl);
          
          // API Documentation: POST /api/payment/generate-link
          // Payload: { orderId, paymentMethodId, businessUnit, returnUrl }
          if (!state.orderId) {
            throw new Error('Order ID is required to generate payment link');
          }
          
          // CRITICAL: Final verification and fix of order price before payment link generation
          // The payment link API reads order.price.amount from backend, so we MUST ensure it's correct
          // This is our last chance to fix backend pricing bugs before payment window shows wrong price
          try {
            console.log('[checkout] ===== FINAL ORDER PRICE VERIFICATION BEFORE PAYMENT LINK =====');
            // Wait a bit to ensure backend has fully processed everything (including addons)
            await new Promise(resolve => setTimeout(resolve, 2000)); // Increased wait time for addons to be processed
            
            let orderBeforePayment = await orderAPI.getOrder(state.orderId);
            console.log('[checkout] Initial order fetch:', {
              orderId: orderBeforePayment?.id,
              orderPrice: orderBeforePayment?.price?.amount,
              orderPriceDKK: orderBeforePayment?.price?.amount ? orderBeforePayment.price.amount / 100 : null,
              hasSubscriptionItems: !!orderBeforePayment?.subscriptionItems?.length,
              hasArticleItems: !!orderBeforePayment?.articleItems?.length,
              hasValueCardItems: !!orderBeforePayment?.valueCardItems?.length,
              articleItemsCount: orderBeforePayment?.articleItems?.length || 0,
              valueCardItemsCount: orderBeforePayment?.valueCardItems?.length || 0,
              articleItems: orderBeforePayment?.articleItems?.map(item => ({
                id: item.id,
                productId: item.product?.id,
                productName: item.product?.name,
                price: item.price?.amount,
                additionTo: item.addition?.parentItemId
              })) || [],
              valueCardItems: orderBeforePayment?.valueCardItems?.map(item => ({
                id: item.id,
                productId: item.product?.id,
                productName: item.product?.name,
                price: item.price?.amount,
                additionTo: item.addition?.parentItemId
              })) || []
            });
            
            // Verify addons are actually in the order (check both articles and value cards)
            if (state.addonIds && state.addonIds.size > 0) {
              const addonIdsArray = Array.from(state.addonIds);
              const articleItems = orderBeforePayment?.articleItems || [];
              const valueCardItems = orderBeforePayment?.valueCardItems || [];
              const articleProductIds = articleItems.map(item => item.product?.id).filter(Boolean);
              const valueCardProductIds = valueCardItems.map(item => item.product?.id).filter(Boolean);
              const allProductIds = [...articleProductIds, ...valueCardProductIds];
              
              console.log('[checkout] Addon verification:', {
                expectedAddonIds: addonIdsArray,
                articleProductIdsInOrder: articleProductIds,
                valueCardProductIdsInOrder: valueCardProductIds,
                allProductIdsInOrder: allProductIds,
                allAddonsInOrder: addonIdsArray.every(id => allProductIds.includes(Number(id)) || allProductIds.includes(String(id)))
              });
              
              if (!addonIdsArray.every(id => allProductIds.includes(Number(id)) || allProductIds.includes(String(id)))) {
                console.error('[checkout] ❌ CRITICAL: Not all addons are in the order!');
                console.error('[checkout] ❌ Expected addons:', addonIdsArray);
                console.error('[checkout] ❌ Article addons in order:', articleProductIds);
                console.error('[checkout] ❌ Value card addons in order:', valueCardProductIds);
                console.error('[checkout] ❌ Payment window will show incorrect total without addons!');
              } else {
                console.log('[checkout] ✅ All addons are present in the order');
              }
            }
            
            // Verify pricing is correct for subscription items
            const subscriptionItem = orderBeforePayment?.subscriptionItems?.[0];
            if (subscriptionItem) {
              const product = subscriptionItem?.product;
              const productId = product?.id || state.selectedProductId;
              const productName = product?.name || '';
              const productLabels = product?.productLabels || product?.labels || [];
              const has15DayPassLabel = Array.isArray(productLabels) && productLabels.some(
                label => label?.name && label.name.toLowerCase() === '15-Day Trial Pass'
              );
              const is15DayPass =
                state.selectedProductType === '15daypass' ||
                has15DayPassLabel ||
                (productName && (
                  productName.toLowerCase().includes('15-Day Trial Pass') ||
                  productName.toLowerCase().includes('15 dages')
                )) ||
                (state.membershipPlanId && String(state.membershipPlanId).startsWith('15daypass-'));
              if (is15DayPass) {
                console.log('[checkout] ✅ Skipping partial-month price verification for 15-Day Trial Pass');
              } else {
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const startDateStr = getTodayLocalDateString();
                const expectedPrice = orderAPI._calculateExpectedPartialMonthPrice(productId, startDateStr);
              
              // Use robust verification method
                const verification = orderAPI._verifySubscriptionPricing(
                  orderBeforePayment,
                  productId,
                  expectedPrice,
                  today
                );
              
                console.log('[checkout] Price verification result:', {
                  isCorrect: verification.isCorrect,
                  startDateCorrect: verification.startDateCorrect,
                  priceCorrect: verification.priceCorrect,
                  daysUntilStart: verification.daysUntilStart,
                  orderPriceDKK: verification.orderPriceDKK,
                  expectedPriceDKK: verification.expectedPriceDKK,
                  productId: productId
                });
              
              // If pricing is incorrect, try to fix it (only if we have permission)
                if (!verification.isCorrect && subscriptionItem.id && (!verification.priceDifference || verification.priceDifference > 100)) {
                  console.warn('[checkout] ⚠️ ORDER PRICE IS INCORRECT BEFORE PAYMENT LINK GENERATION!');
                  console.warn('[checkout] ⚠️ Backend shows:', verification.orderPriceDKK, 'DKK');
                  console.warn('[checkout] ⚠️ Expected:', verification.expectedPriceDKK || 'N/A', 'DKK');
                  console.warn('[checkout] ⚠️ Attempting to fix by deleting and re-adding subscription...');
                  console.warn('[checkout] ⚠️ NOTE: This may fail with 403 if order is in a state that prevents modification');
                
                // Try to fix with multiple strategies
                // Build URL for subscription endpoint
                let subscriptionUrl;
                if (orderAPI.useProxy) {
                  subscriptionUrl = `${orderAPI.baseUrl}?path=/api/ver3/orders/${state.orderId}/items/subscriptions`;
                } else {
                  subscriptionUrl = `https://boulders.brpsystems.com/apiserver/api/ver3/orders/${state.orderId}/items/subscriptions`;
                }
                
                  const fixedOrder = await orderAPI._fixBackendPricingBug(
                    state.orderId,
                    subscriptionUrl,
                    {
                      'Accept-Language': getAcceptLanguageHeader(),
                      'Content-Type': 'application/json',
                      ...(typeof window.getAccessToken === 'function' && window.getAccessToken() 
                        ? { 'Authorization': `Bearer ${window.getAccessToken()}` } 
                        : {}),
                    },
                    subscriptionItem.id,
                    {
                      subscriptionProduct: productId,
                      startDate: startDateStr,
                      ...(state.customerId ? { subscriber: Number(state.customerId) } : {}),
                      ...(getSubscriberBirthDate() ? { birthDate: getSubscriberBirthDate() } : {}),
                    },
                    productId,
                    expectedPrice,
                    typeof window.getAccessToken === 'function' ? window.getAccessToken() : null,
                    today
                  );
                
                  if (fixedOrder) {
                    // Verify the fix worked
                    const fixedVerification = orderAPI._verifySubscriptionPricing(
                      fixedOrder,
                      productId,
                      expectedPrice,
                      today
                    );
                    
                    if (fixedVerification.isCorrect) {
                      console.log('[checkout] ✅ Successfully fixed order price before payment link generation!');
                      orderBeforePayment = fixedOrder;
                      
                      // CRITICAL: Update subscription item ID after pricing fix
                      // The pricing fix deletes and re-adds the subscription item, creating a NEW ID
                      // We must update state.subscriptionItemId with the new ID, otherwise addons will fail with ID_NOT_FOUND
                      const newSubscriptionItem = fixedOrder?.subscriptionItems?.[0];
                      if (newSubscriptionItem?.id) {
                        const oldSubscriptionItemId = state.subscriptionItemId;
                        state.subscriptionItemId = newSubscriptionItem.id;
                        console.log('[checkout] ✅ Updated subscription item ID after pricing fix:', {
                          oldId: oldSubscriptionItemId,
                          newId: state.subscriptionItemId
                        });
                      } else {
                        console.warn('[checkout] ⚠️ Fixed order does not have subscription item - cannot update subscriptionItemId');
                      }
                      
                      // Update state.fullOrder with the fixed order
                      state.fullOrder = fixedOrder;
                    } else {
                      console.error('[checkout] ❌ Fix attempt failed - price still incorrect');
                      console.error('[checkout] ❌ Pricing mismatch detected');
                      console.error('[checkout] ❌ UI shows calculated price:', verification.expectedPriceDKK, 'DKK');
                      console.error('[checkout] ❌ Backend order price:', verification.orderPriceDKK, 'DKK');
                    }
                    } else {
                    console.error('[checkout] ❌ All fix strategies failed - cannot modify order (likely 403 Forbidden)');
                    console.error('[checkout] ❌ Pricing mismatch - cannot fix order (order may be locked)');
                    console.error('[checkout] ❌ UI shows calculated price:', verification.expectedPriceDKK, 'DKK');
                    console.error('[checkout] ❌ Backend order price:', verification.orderPriceDKK, 'DKK');
                    console.error('[checkout] ❌ Product ID:', productId);
                  }
                } else if (verification.isCorrect || (verification.priceDifference !== null && verification.priceDifference <= 100)) {
                  console.log('[checkout] ✅ Order price is acceptable - no fix needed');
                }
              }
            }
            
            // CRITICAL: Refresh order one final time before payment link to ensure we have latest price with addons
            // This is especially important if addons were just added
            if (state.addonIds && state.addonIds.size > 0) {
              try {
                console.log('[checkout] 🔍 Final order refresh before payment link generation...');
                const finalOrderCheck = await orderAPI.getOrder(state.orderId);
                state.fullOrder = finalOrderCheck;
                orderBeforePayment = finalOrderCheck;
                
                const finalOrderPriceDKK = finalOrderCheck?.price?.amount 
                  ? (typeof finalOrderCheck.price.amount === 'object' 
                      ? finalOrderCheck.price.amount.amount / 100 
                      : finalOrderCheck.price.amount / 100)
                  : 0;
                
                // Verify addons are in the final order
                const finalArticleItems = finalOrderCheck?.articleItems || [];
                const finalValueCardItems = finalOrderCheck?.valueCardItems || [];
                const finalArticleProductIds = finalArticleItems.map(item => item.product?.id).filter(Boolean);
                const finalValueCardProductIds = finalValueCardItems.map(item => item.product?.id).filter(Boolean);
                const finalAllProductIds = [...finalArticleProductIds, ...finalValueCardProductIds];
                const finalAddonIdsArray = Array.from(state.addonIds);
                const allAddonsInFinalOrder = finalAddonIdsArray.every(id => 
                  finalAllProductIds.includes(Number(id)) || finalAllProductIds.includes(String(id))
                );
                
                if (allAddonsInFinalOrder) {
                  console.log('[checkout] ✅ Final order verification: All addons present');
                  console.log('[checkout] ✅ Final order price with addons:', finalOrderPriceDKK, 'DKK');
                } else {
                  console.error('[checkout] ❌ CRITICAL: Addons missing in final order check!');
                  console.error('[checkout] ❌ Expected addons:', finalAddonIdsArray);
                  console.error('[checkout] ❌ Found product IDs:', finalAllProductIds);
                  throw new Error('Addons are not present in the final order - cannot proceed with payment');
                }
              } catch (finalCheckError) {
                console.error('[checkout] ❌ Error during final order check:', finalCheckError);
                throw finalCheckError;
              }
            }
            
            // Store full order object for payment overview
            state.fullOrder = orderBeforePayment;
            
            // CRITICAL: Log the exact price that will be sent to payment window
            const finalOrderPrice = orderBeforePayment?.price?.amount || 0;
            const finalOrderPriceDKK = typeof finalOrderPrice === 'object' 
              ? finalOrderPrice.amount / 100 
              : finalOrderPrice / 100;
            
            console.log('[checkout] 🔍 FINAL PRICE THAT WILL BE SENT TO PAYMENT WINDOW:', finalOrderPrice, '(in cents) =', finalOrderPriceDKK, 'DKK');
            console.log('[checkout] This is order.price.amount from backend - payment link API will use this exact value');
            
            // Update payment overview with final order data
            updatePaymentOverview();
            
            // Verify coupon discount is present
            const orderCouponDiscount = orderBeforePayment?.couponDiscount || orderBeforePayment?.price?.couponDiscount;
            if (orderCouponDiscount) {
              console.log('[checkout] ✅ Order has couponDiscount, payment link should reflect discount');
            } else {
              console.warn('[checkout] ⚠️ Order does not have couponDiscount - payment link may not reflect discount');
            }
            
            // Log comprehensive price information for debugging
            console.log('[checkout] Final order price summary:', {
              orderId: orderBeforePayment?.id,
              orderPriceAmount: finalOrderPrice,
              orderPriceDKK: finalOrderPriceDKK,
              hasPrice: !!orderBeforePayment?.price,
              priceKeys: orderBeforePayment?.price ? Object.keys(orderBeforePayment.price) : [],
              couponDiscount: orderCouponDiscount,
              subscriptionItemPrice: subscriptionItem?.price?.amount,
              recurringPrice: subscriptionItem?.payRecurring?.price?.amount,
              initialPaymentPeriod: subscriptionItem?.initialPaymentPeriod,
            });
          } catch (orderCheckError) {
            console.error('[checkout] ❌ Error during final order verification:', orderCheckError);
            console.warn('[checkout] ⚠️ Continuing with payment link generation - price may be incorrect');
            // Try to get order anyway for payment overview
            try {
              const fallbackOrder = await orderAPI.getOrder(state.orderId);
              state.fullOrder = fallbackOrder;
              updatePaymentOverview();
            } catch (fallbackError) {
              console.error('[checkout] Could not fetch order for payment overview:', fallbackError);
            }
          }
          
          const paymentData = await paymentAPI.generatePaymentLink({
            orderId: state.orderId,
            paymentMethod: state.paymentMethod,
            businessUnit: state.selectedBusinessUnit,
            returnUrl,
            receiptEmail: customerEmail,
          });
          
          // Extract payment link from response - API returns {success: true, data: {paymentLink: ...}}
          // Log full response for debugging
          console.log('[checkout] Full payment link API response:', JSON.stringify(paymentData, null, 2));
          
          // CRITICAL: Log the price that was sent to payment window
          // Check if payment response contains price information
          const paymentPrice = paymentData?.data?.amount || paymentData?.data?.price || paymentData?.amount || paymentData?.price;
          const currentPayNowDisplay = DOM.payNow?.textContent || 'N/A';
          console.log('[checkout] 🔍 Payment link generated');
          console.log('[checkout] 🔍 Price in payment response:', paymentPrice);
          console.log('[checkout] 🔍 "Betales nu" display:', currentPayNowDisplay);
          console.log('[checkout] 🔍 Order price (state.fullOrder.price.amount):', state.fullOrder?.price?.amount || 'N/A');
          
          paymentLink = paymentData?.data?.paymentLink || 
                        paymentData?.data?.link || 
                        paymentData?.data?.url ||
                        paymentData?.paymentLink || 
                        paymentData?.link || 
                        paymentData?.url;
          
          // Additional checks for nested structures
          if (!paymentLink && paymentData?.data) {
            // Try to find any URL-like field in the data object
            const dataKeys = Object.keys(paymentData.data);
            console.log('[checkout] Available keys in paymentData.data:', dataKeys);
            for (const key of dataKeys) {
              const value = paymentData.data[key];
              if (typeof value === 'string' && (value.startsWith('http://') || value.startsWith('https://'))) {
                paymentLink = value;
                console.log('[checkout] Found URL in paymentData.data.' + key + ':', paymentLink);
                break;
              }
            }
          }
          
          state.paymentLink = paymentLink;
          state.paymentLinkGenerated = true;
          
          console.log('[checkout] Payment link extracted:', paymentLink);
          console.log('[checkout] Payment link type:', typeof paymentLink);
          console.log('[checkout] Payment link is valid URL:', paymentLink ? (paymentLink.startsWith('http://') || paymentLink.startsWith('https://')) : 'null/undefined');
          
          if (!paymentLink) {
            console.error('[checkout] ⚠️ Payment link is null/undefined!');
            console.error('[checkout] Payment API response structure:', {
              hasData: !!paymentData?.data,
              dataKeys: paymentData?.data ? Object.keys(paymentData.data) : [],
              topLevelKeys: Object.keys(paymentData || {}),
            });
          }
        } catch (error) {
          console.error('[checkout] Failed to add membership or generate payment link:', error);

          // Report critical payment errors to Sentry
          captureException(error, {
            tags: {
              flow: 'checkout',
              error_type: 'payment_link_generation',
            },
            extra: {
              orderId: state.fullOrder?.id,
              subscriptionItems: state.fullOrder?.subscriptionItems,
            },
          });

          // Check if this is a PRODUCT_NOT_ALLOWED error (campaign eligibility restriction)
          const isProductNotAllowed = error.isProductNotAllowed || 
                                      (error.message && error.message.includes('PRODUCT_NOT_ALLOWED'));
          
          if (isProductNotAllowed) {
            // Show modal with options instead of toast
            showCampaignRejectionModal();
            return; // Stop checkout flow without throwing error
          }
          
          // Check if this is a payment link generation error due to backend pricing bug
          const isPaymentLinkError = error.message && error.message.includes('Generate Payment Link Card failed');
          const is403Error = error.message && error.message.includes('403');
          
          if (isPaymentLinkError && is403Error) {
            // Check if order has incorrect pricing
            const subscriptionItem = state.fullOrder?.subscriptionItems?.[0];
            if (subscriptionItem) {
              const productId = subscriptionItem?.product?.id;
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              const startDateStr = getTodayLocalDateString();
              const expectedPrice = orderAPI._calculateExpectedPartialMonthPrice(productId, startDateStr);
              const verification = orderAPI._verifySubscriptionPricing(state.fullOrder, productId, expectedPrice, today);
              
              if (!verification.isCorrect && (!verification.priceDifference || verification.priceDifference > 100)) {
                console.error('[checkout] ❌ PAYMENT LINK GENERATION FAILED DUE TO PRICING MISMATCH');
                console.error('[checkout] ❌ Order has incorrect pricing - backend ignored startDate parameter');
                console.error('[checkout] ❌ This is a backend issue that needs to be fixed on backend side');
                console.error('[checkout] ❌ Product ID:', productId);
                console.error('[checkout] ❌ Backend price:', verification.orderPriceDKK, 'DKK');
                console.error('[checkout] ❌ Expected price:', verification.expectedPriceDKK || 'N/A', 'DKK');
                
                // Show user-friendly error message
                showToast(
                  'Unable to process payment due to a system issue. Please try again in a few moments or contact support.',
                  'error'
                );
                
                // Reset checkout state to allow retry
                state.checkoutInProgress = false;
                if (typeof setCheckoutLoadingState === 'function') {
                  setCheckoutLoadingState(false);
                }
                
                throw new Error(`Payment link generation failed due to backend pricing bug. Order price: ${verification.orderPriceDKK} DKK, Expected: ${verification.expectedPriceDKK || 'N/A'} DKK. Product ID: ${productId}. Backend ignored startDate parameter.`);
              }
            }
          }
          
          throw new Error('Failed to add membership to order or generate payment link');
        }
      }
      
      // Add value cards (punch cards) - add FIRST if no membership, or AFTER payment link if membership exists
      // CRITICAL: Check if punch cards are already in the order (e.g., from discount flow) to avoid duplicates
      let valueCardAddFailed = false;
      let valueCardError = null;
      
      if (state.valueCardQuantities && state.valueCardQuantities.size > 0) {
        // Check existing valueCardItems in order to avoid duplicates
        const existingValueCardItems = state.fullOrder?.valueCardItems || [];
        const existingProductIds = new Set(existingValueCardItems.map(item => item.product?.id));
        
        for (const [planId, quantity] of state.valueCardQuantities.entries()) {
          if (quantity > 0) {
            try {
              // Extract numeric product ID from "punch-43" format
              const numericProductId = typeof planId === 'string' && planId.startsWith('punch-')
                ? parseInt(planId.replace('punch-', ''), 10)
                : planId;
              
              // Check if this product is already in the order
              const existingCount = existingValueCardItems.filter(item => item.product?.id === numericProductId).length;
              const neededCount = quantity;
              
              if (existingCount >= neededCount) {
                console.log(`[checkout] ⚠️ Value card ${planId} (productId: ${numericProductId}) already in order (${existingCount} items, need ${neededCount}) - skipping`);
                continue;
              }
              
              // Only add the difference
              const toAdd = neededCount - existingCount;
              console.log(`[checkout] Adding ${toAdd} value card(s) for ${planId} (${existingCount} already in order, need ${neededCount})`);
              
              // API doesn't accept quantity in payload - call API once per quantity needed
              for (let i = 0; i < toAdd; i++) {
                await orderAPI.addValueCardItem(state.orderId, numericProductId, 1);
                console.log(`[checkout] ✅ Value card added: ${planId} (productId: ${numericProductId}) [${existingCount + i + 1}/${neededCount}]`);
              }
            } catch (error) {
              valueCardAddFailed = true;
              valueCardError = error;
              console.error(`[checkout] ❌ Failed to add value card ${planId}:`, error);
              console.error(`[checkout] Error details:`, {
                message: error.message,
                is403: error.message.includes('403') || error.message.includes('Forbidden'),
                is401: error.message.includes('401') || error.message.includes('Unauthorized'),
                orderId: state.orderId,
                productId: numericProductId,
                quantity,
                isMembership
              });
              
              // Don't throw immediately - continue to payment link generation
              // We'll handle the error after payment link is generated
              if (isMembership) {
                console.warn(`[checkout] Continuing despite value card error - payment link already generated`);
              } else {
                console.warn(`[checkout] ⚠️ Value card add failed, but continuing to generate payment link`);
              }
            }
          }
        }
      }
      
      // CRITICAL: Generate payment link if it hasn't been generated yet
      // This handles cases where user only selected punch cards or addons (no membership)
      if (!paymentLink && state.orderId) {
        console.log('[checkout] ===== GENERATE PAYMENT LINK (no membership) =====');
        console.log('[checkout] No membership selected, generating payment link for punch cards/addons only');
        console.log('[checkout] Order ID:', state.orderId);
        console.log('[checkout] Payment Method:', state.paymentMethod);
        console.log('[checkout] Business Unit:', state.selectedBusinessUnit);
        console.log('[checkout] Product Type:', state.selectedProductType);
        console.log('[checkout] Membership Plan ID:', state.membershipPlanId);
        console.log('[checkout] Is Membership:', isMembership);
        console.log('[checkout] Has Value Cards:', state.valueCardQuantities?.size > 0);
        console.log('[checkout] Has Addons:', state.addonIds?.size > 0);
        
        // CRITICAL: Add addons BEFORE generating payment link for non-membership orders
        // This ensures the order total includes addons when payment link is generated
        // Note: For non-membership orders, additionTo may not be available
        if (state.addonIds && state.addonIds.size > 0) {
          console.log('[checkout] Adding', state.addonIds.size, 'addon(s) to order before payment link generation (non-membership)');
          for (const addonId of state.addonIds) {
            try {
              // Determine if addon is a value card or article
              const addon = findAddon(addonId);
              const numericId = typeof addonId === 'string' ? parseInt(addonId, 10) : addonId;
              
              // Check multiple sources to identify product type (consistent with membership checkout)
              const isInValueCards = state.valueCards && state.valueCards.some(vc => {
                const vcId = typeof vc.id === 'string' ? parseInt(vc.id, 10) : vc.id;
                return vcId === numericId || String(vc.id) === String(addonId) || vc.id === addonId;
              });
              
              // Also check allRawProducts (includes value cards before filtering)
              const isInRawValueCards = state.allRawProducts && state.allRawProducts.some(p => {
                const pId = typeof p.id === 'string' ? parseInt(p.id, 10) : p.id;
                return (pId === numericId || String(p.id) === String(addonId) || p.id === addonId) &&
                       (p.productType === 'VALUE_CARD' || p.productType === 'VALUECARD' || 
                        (!p.priceWithInterval && !p.subscriptionInterval));
              });
              
              const hasValueCardType = addon && (
                addon.productType === 'VALUE_CARD' ||
                addon.productType === 'VALUECARD' ||
                (addon.product && (addon.product.productType === 'VALUE_CARD' || addon.product.productType === 'VALUECARD'))
              );
              const hasValueCardFields = addon && (
                addon.valueCardType !== undefined ||
                addon.validUntil !== undefined ||
                (addon.product && addon.product.valueCardType !== undefined)
              );
              
              const isValueCard = isInValueCards || isInRawValueCards || hasValueCardType || hasValueCardFields;
              
              if (isValueCard) {
                await orderAPI.addValueCardItem(state.orderId, numericId, 1, null);
              } else {
                await orderAPI.addArticleItem(state.orderId, numericId, null);
              }
              console.log(`[checkout] ✅ Add-on added: ${addonId}`);
            } catch (error) {
              console.error(`[checkout] ❌ Failed to add add-on ${addonId}:`, error);
              console.warn(`[checkout] ⚠️ Continuing despite add-on error - payment link may not include this addon`);
            }
          }
          
          // Refresh order to get updated total with addons
          try {
            console.log('[checkout] Refreshing order to get updated total with addons...');
            const updatedOrder = await orderAPI.getOrder(state.orderId);
            state.fullOrder = updatedOrder;
            console.log('[checkout] ✅ Order refreshed with addons, new total:', updatedOrder?.price?.amount ? (typeof updatedOrder.price.amount === 'object' ? updatedOrder.price.amount.amount / 100 : updatedOrder.price.amount / 100) : 'N/A', 'DKK');
          } catch (refreshError) {
            console.warn('[checkout] Could not refresh order after adding addons:', refreshError);
          }
        }
        
        try {
          const baseUrl = getReturnUrlBase();
          const returnUrl = `${baseUrl}${window.location.pathname}?payment=return&orderId=${state.orderId}`;
          
          if (!state.orderId) {
            throw new Error('Order ID is required to generate payment link');
          }
          
          const paymentData = await paymentAPI.generatePaymentLink({
            orderId: state.orderId,
            paymentMethod: state.paymentMethod,
            businessUnit: state.selectedBusinessUnit,
            returnUrl,
            receiptEmail: customerEmail,
          });
          
          console.log('[checkout] Full payment link API response:', JSON.stringify(paymentData, null, 2));
          
          paymentLink = paymentData?.data?.paymentLink || 
                        paymentData?.data?.link || 
                        paymentData?.data?.url ||
                        paymentData?.paymentLink || 
                        paymentData?.link || 
                        paymentData?.url;
          
          // Additional checks for nested structures
          if (!paymentLink && paymentData?.data) {
            const dataKeys = Object.keys(paymentData.data);
            console.log('[checkout] Available keys in paymentData.data:', dataKeys);
            for (const key of dataKeys) {
              const value = paymentData.data[key];
              if (typeof value === 'string' && (value.startsWith('http://') || value.startsWith('https://'))) {
                paymentLink = value;
                console.log('[checkout] Found URL in paymentData.data.' + key + ':', paymentLink);
                break;
              }
            }
          }
          
          state.paymentLink = paymentLink;
          state.paymentLinkGenerated = true;
          
          console.log('[checkout] Payment link extracted:', paymentLink);
          console.log('[checkout] Payment link type:', typeof paymentLink);
          console.log('[checkout] Payment link is valid URL:', paymentLink ? (paymentLink.startsWith('http://') || paymentLink.startsWith('https://')) : 'null/undefined');
          
          if (!paymentLink) {
            console.error('[checkout] ⚠️ Payment link is null/undefined!');
            console.error('[checkout] Payment API response structure:', {
              hasData: !!paymentData?.data,
              dataKeys: paymentData?.data ? Object.keys(paymentData.data) : [],
              topLevelKeys: Object.keys(paymentData || {}),
            });
          }
        } catch (error) {
          console.error('[checkout] Failed to generate payment link for non-membership order:', error);
          throw new Error('Failed to generate payment link');
        }
      }
      
      console.log('[checkout] All items added to order');
      
      // If value card add failed for punch-card-only order, show error but don't block checkout
      if (valueCardAddFailed && !isMembership && valueCardError) {
        console.error(`[checkout] ⚠️ Value card add failed, but payment link was generated`);
        console.error(`[checkout] Error:`, valueCardError.message);
        // Show error toast but don't block checkout
        const errorMsg = valueCardError.message.includes('403') || valueCardError.message.includes('Forbidden')
          ? 'Could not add punch card to order (permission error). Payment link generated anyway.'
          : `Warning: Could not add punch card to order. Payment link generated anyway.`;
        showToast(errorMsg, 'warning');
      }
      
      // Note: Coupon should already be applied BEFORE payment link generation (see above)
      // This is a fallback in case the first attempt failed
      // Skip if coupon was already successfully applied
      if (state.discountApplied) {
        console.log('[checkout] Coupon already applied, skipping duplicate application');
      } else {
        // Fallback: Apply coupon if it wasn't applied before payment link generation
        const discountCodeToApply = (DOM.discountInput && DOM.discountInput.value.trim()) 
          ? DOM.discountInput.value.trim().toUpperCase()
          : state.discountCode;
        
        if (discountCodeToApply && state.orderId) {
          try {
            console.log('[checkout] ===== APPLYING COUPON DURING CHECKOUT =====');
            console.log('[checkout] Coupon code:', discountCodeToApply);
            console.log('[checkout] Order ID:', state.orderId);
            
            const discountResponse = await orderAPI.applyDiscountCode(state.orderId, discountCodeToApply);
            
            console.log('[checkout] Coupon API response:', JSON.stringify(discountResponse, null, 2));
            
            const subtotal = state.totals.subtotal || state.totals.cartTotal || 0;
            let discountAmount = calculateOrderCouponDiscountKr(discountResponse, subtotal);
            if (discountAmount <= 0 && discountResponse?.discountAmount != null) {
              discountAmount = extractCurrencyOutKr(discountResponse.discountAmount);
            }

            if (!discountAmount) {
              try {
                const updatedOrder = await orderAPI.getOrder(state.orderId);
                state.fullOrder = updatedOrder;
                discountAmount = calculateOrderCouponDiscountKr(updatedOrder, subtotal);
              } catch (fetchError) {
                console.warn('[checkout] Could not fetch updated order:', fetchError);
              }
            }

            discountAmount = roundToHalfKrone(capCouponDiscountKr(discountAmount, subtotal));
            console.log('[checkout] Final discountAmount:', discountAmount, '(subtotal:', subtotal, ')');

            if (discountAmount > 0) {
              // Success - apply discount
              state.discountCode = discountCodeToApply;
              state.discountApplied = true;
              state.totals.discountAmount = roundToHalfKrone(discountAmount);
              
              console.log('[checkout] Updating cart totals with discount:', discountAmount);
              console.log('[checkout] Subtotal:', state.totals.subtotal, 'Discount:', discountAmount, 'Final Total:', state.totals.subtotal - discountAmount);
              updateCartSummary(); // Use API-based cart update function
              
              // Update UI to show coupon is applied
              if (DOM.discountInput) {
                DOM.discountInput.value = discountCodeToApply;
              }
              updateDiscountFormControls();

              // Show success message
              showDiscountMessage(`Coupon "${discountCodeToApply}" applied! Discount: ${formatCurrencyHalfKrone(discountAmount)}`, 'success');
              
              console.log('[checkout] ✅ Coupon applied successfully:', discountCodeToApply, 'Amount:', discountAmount);
            } else {
              console.warn('[checkout] ⚠️ Coupon applied but no discount amount found');
              console.warn('[checkout] Response structure:', Object.keys(discountResponse || {}));
              console.warn('[checkout] Full response:', JSON.stringify(discountResponse, null, 2));
              showDiscountMessage('Coupon applied but discount amount could not be determined. Please check the order total.', 'error');
            }
          } catch (error) {
            console.error('[checkout] ❌ Failed to apply coupon during checkout:', error);
            console.error('[checkout] Error details:', {
              message: error.message,
              stack: error.stack,
            });
            
            // Parse error message to extract error code
            let errorMessage = 'Coupon could not be applied. Checkout will continue.';
            const errorText = error.message || '';
            
            if (errorText.includes('COUPON_NOT_APPLICABLE')) {
              errorMessage = 'This coupon is not applicable to your order. Checkout will continue without discount.';
            } else if (errorText.includes('COUPON_NOT_FOUND') || errorText.includes('404')) {
              errorMessage = 'Coupon code not found. Checkout will continue without discount.';
            } else if (errorText.includes('COUPON_EXPIRED')) {
              errorMessage = 'This coupon has expired. Checkout will continue without discount.';
            } else if (errorText.includes('403') || errorText.includes('Forbidden')) {
              errorMessage = 'This coupon cannot be applied. Checkout will continue without discount.';
            }
            
            // Don't block checkout if coupon fails - just log the warning
            // Show a warning message but don't prevent checkout
            if (DOM.discountInput) {
              showDiscountMessage(errorMessage, 'error');
            }
          }
        } else {
          console.log('[checkout] Coupon application skipped:', {
            discountCodeToApply,
            discountApplied: state.discountApplied,
            orderId: state.orderId,
          });
        }
      }
      
      const { isFreeFlow: checkoutSkipsPaymentRedirect } = getFreeFlowCartState();
      if (!paymentLink && state.membershipPlanId && !checkoutSkipsPaymentRedirect) {
        throw new Error('Payment link was not generated after adding subscription');
      }
    } catch (error) {
      console.error('[checkout] Failed to add items or generate payment link:', error);
      
      // Check if this is a PRODUCT_NOT_ALLOWED error (campaign eligibility restriction)
      const isProductNotAllowed = error.isProductNotAllowed || 
                                  (error.message && error.message.includes('PRODUCT_NOT_ALLOWED'));
      
      if (isProductNotAllowed) {
        // Show modal with options instead of toast
        showCampaignRejectionModal();
      } else {
        // Show error message for actual errors
        showToast(getErrorMessage(error, 'Adding items'), 'error');
      }
      
      setCheckoutLoadingState(false);
      return;
    }

    // Step 5: Update order summary with real data
    // IMPORTANT: Preserve full order object (state.fullOrder) for payment overview
    // buildOrderSummary creates a summary object for confirmation view, not the full API order
    const summaryOrder = state.orderId ? { id: state.orderId, orderId: state.orderId } : null;
    state.order = buildOrderSummary(payload, summaryOrder, customer);
    
    // If we don't have full order data yet, try to fetch it before redirecting
    // This ensures payment overview shows correct prices
    if (!state.fullOrder || !state.fullOrder.subscriptionItems) {
      if (state.orderId) {
        try {
          const fullOrder = await orderAPI.getOrder(state.orderId);
          state.fullOrder = fullOrder;
          updatePaymentOverview();
          console.log('[checkout] Full order data fetched and stored for payment overview');
        } catch (error) {
          console.warn('[checkout] Could not fetch full order data:', error);
        }
      }
    }
    
    // Step 6: Redirect to payment or show confirmation
    console.log('[checkout] ===== PAYMENT REDIRECT CHECK =====');
    console.log('[checkout] paymentLink value:', paymentLink);
    console.log('[checkout] paymentLink type:', typeof paymentLink);
    console.log('[checkout] paymentLink truthy?', !!paymentLink);
    console.log('[checkout] state.paymentLink:', state.paymentLink);

    const isAssentlyPaymentUrl = (url) => typeof url === 'string' && /assently\.com/i.test(url);
    const effectivePaymentLink = paymentLink || state.paymentLink;
    const hasTrustedPaymentRedirect =
      !!effectivePaymentLink &&
      (effectivePaymentLink.startsWith('http://') || effectivePaymentLink.startsWith('https://')) &&
      !isAssentlyPaymentUrl(effectivePaymentLink);

    const { isFreeFlow, isFreeTrialSelected } = getFreeFlowCartState();

    // Free-trial subscriptions: BRP gates membership creation on PSP-registered payment (leftToPay cleared),
    // so a 0 DKK trial must still follow the Join payment URL.
    // Coupon-driven zero totals are different — the payment link reflects the pre-coupon amount and would
    // show the user a full-price payment page, so we finalize client-side instead.
    if (isFreeTrialSelected && hasTrustedPaymentRedirect) {
      console.log(
        '[checkout] Free-trial flow: redirecting via payment link so backend/PSP can register completion',
      );
      showToast('Redirecting to secure payment...', 'info');
      setTimeout(() => {
        try {
          window.location.replace(effectivePaymentLink);
        } catch (error) {
          console.error('[checkout] ❌ Free-trial redirect failed:', error);
          window.location.href = effectivePaymentLink;
        }
      }, 500);
      return;
    }

    if (isFreeFlow) {
      console.log(
        '[checkout] Free flow without payment URL — finalizing preliminary order client-side and showing confirmation',
      );
      if (state.orderId) {
        await finalizePreliminaryZeroBalanceOrder(state.orderId, 'checkout-free-flow');
      }
      // Mark as confirmed so success-page guards do not trigger intermediate navigation artifacts.
      state.paymentConfirmed = true;
      state.checkoutInProgress = false;
      updateFAQVisibility();
      setCheckoutLoadingState(false);
      state.currentStep = TOTAL_STEPS;
      showStep(TOTAL_STEPS);
      updateStepIndicator();
      updateNavigationButtons();
      updateMainSubtitle();
      renderConfirmationView();
      return;
    }

    if (effectivePaymentLink && isAssentlyPaymentUrl(effectivePaymentLink)) {
      console.error('[checkout] ❌ Refusing to redirect to Assently – API returned signature URL instead of payment link');
      showToast('Unable to open payment. Please try again or contact support.', 'error');
      state.checkoutInProgress = false;
      setCheckoutLoadingState(false);
    } else if (hasTrustedPaymentRedirect) {
      // Redirect to payment provider (never to Assently)
      console.log('[checkout] ✅ Valid payment link found, redirecting to payment provider...');
      console.log('[checkout] Payment link URL:', effectivePaymentLink);
      showToast('Redirecting to secure payment...', 'info');

      // Use replace instead of href to avoid adding to browser history
      // This prevents the back button from going back to the checkout page
      setTimeout(() => {
        try {
          persistOrderSnapshot(state.orderId);
          console.log('[checkout] Executing window.location.replace with:', effectivePaymentLink);
          window.location.replace(effectivePaymentLink);
        } catch (error) {
          console.error('[checkout] ❌ Redirect failed with replace:', error);
          // Fallback to href if replace fails
          try {
            console.log('[checkout] Falling back to window.location.href');
            window.location.href = effectivePaymentLink;
          } catch (hrefError) {
            console.error('[checkout] ❌ Redirect failed with href:', hrefError);
            showToast('Failed to redirect to payment. Please contact support.', 'error');
            state.checkoutInProgress = false;
            setCheckoutLoadingState(false);
          }
        }
      }, 500);
    } else {
      // No payment link or invalid URL
      console.error('[checkout] ❌ Payment link not available or invalid!');
      console.error('[checkout] paymentLink:', paymentLink);
      console.error('[checkout] This means the API did not return a valid payment URL');
      console.error('[checkout] The payment provider might be embedded or the API response structure changed');

      // Check if payment link is in state (maybe it was set elsewhere) – but never use Assently URL
      const stateLink = state.paymentLink;
      if (
        stateLink &&
        (stateLink.startsWith('http://') || stateLink.startsWith('https://')) &&
        !isAssentlyPaymentUrl(stateLink)
      ) {
        console.log('[checkout] Found payment link in state, using that instead');
        showToast('Redirecting to secure payment...', 'info');
        setTimeout(() => {
          persistOrderSnapshot(state.orderId);
          window.location.replace(stateLink);
        }, 500);
      } else {
        // No valid payment link - this is an error
        console.error('[checkout] ❌ CRITICAL: No valid payment link available!');
        console.error('[checkout] Order was created but payment cannot be processed');
        showToast('Payment link not available. Please contact support with order ID: ' + state.orderId, 'error');
        state.checkoutInProgress = false;
        setCheckoutLoadingState(false);
        
        // Still show confirmation but warn user
        if (state.currentStep < TOTAL_STEPS) {
          nextStep();
        } else {
          renderConfirmationView();
        }
      }
    }

  } catch (error) {
    // Catch-all for unexpected errors
    console.error('[checkout] Unexpected error:', error);
    showToast(getErrorMessage(error, 'Checkout'), 'error');
    state.checkoutInProgress = false; // Reset on error
    updateFAQVisibility(); // Show FAQ again if checkout fails
    setCheckoutLoadingState(false);
  }
}

function buildCheckoutPayload() {
  const payload = {};
  const fields = document.querySelectorAll('[data-api-field]');

  fields.forEach((field) => {
    const path = field.dataset.apiField;
    if (!path) return;
    const value = field.type === 'checkbox' ? field.checked : field.value;
    setByPath(payload, path, value);
  });

  const valueCards = Array.from(state.valueCardQuantities.entries())
    .filter(([, qty]) => qty > 0)
    .map(([planId, quantity]) => ({ planId, quantity }));

  payload.purchase = {
    ...(payload.purchase || {}),
    membershipPlanId: state.membershipPlanId,
    valueCards,
    addons: Array.from(state.addonIds),
    totalAmount: state.totals.cartTotal,
    ...(state.discountCode ? { discountCode: state.discountCode } : {}),
  };

  payload.payment = {
    ...(payload.payment || {}),
    method: state.paymentMethod,
  };

  return payload;
}

function buildOrderSummary(payload, order = null, customer = null) {
  const now = new Date();
  
  // Try to find membership from API subscriptions first, then fall back to static plans
  let membership = null;
  const membershipPlanId = state.membershipPlanId ?? '';
  // Check both membership subscriptions and 15-Day Trial Pass subscriptions
  const allSubscriptions = [...(state.subscriptions || []), ...(state.dayPassSubscriptions || [])];
  if (membershipPlanId && allSubscriptions.length > 0) {
    // Extract numeric ID from 'campaign-XXX', 'membership-134', or '15daypass-XXX' format
    const numericId = membershipPlanId.replace(/^(campaign|membership|15daypass)-/, '');
    const productId = parseInt(numericId, 10);
    
    // Find membership in API subscriptions (check both arrays)
    membership = allSubscriptions.find(sub => 
      sub.id === productId || 
      String(sub.id) === numericId ||
      sub.id === membershipPlanId
    );
    
    // If found, convert to format expected by the rest of the code
    if (membership) {
      membership = {
        id: membershipPlanId,
        name: membership.name || membership.productNumber || 'Membership',
        price: (membership.priceWithInterval?.price?.amount || membership.price?.amount || 0) / 100,
      };
    }
  }
  
  // Fall back to static plans if not found in API data
  if (!membership) {
    membership = findMembershipPlan(membershipPlanId);
  }

  // Use real data if available, otherwise use TBD placeholders
  // IMPORTANT: Use order.number if available (this is the display number shown in emails/receipts)
  // Fall back to order.id if number doesn't exist (for backwards compatibility)
  const orderNumber = order?.number || order?.id || order?.orderId || state.orderId || 'TBD-ORDER-ID';
  const orderId = order?.id || order?.orderId || state.orderId || 'TBD-ORDER-ID';
  // Extract membership ID from customer response - check both direct and nested data structure
  const membershipId = customer?.data?.id || 
                       customer?.data?.customerId || 
                       customer?.data?.membershipId ||
                       customer?.id || 
                       customer?.customerId || 
                       customer?.membershipId || 
                       state.customerId || // Use customer ID from checkout if available
                       'TBD-MEMBERSHIP-ID';

  // Build member name from customer data - check multiple sources
  let memberName = '';
  if (customer?.fullName) {
    memberName = customer.fullName;
  } else if (customer?.firstName && customer?.lastName) {
    memberName = `${customer.firstName} ${customer.lastName}`;
  } else if (payload.customer?.firstName && payload.customer?.lastName) {
    memberName = `${payload.customer.firstName} ${payload.customer.lastName}`;
  }
  
  // Build primary gym label - check multiple sources
  const primaryGymValue = customer?.primaryGym || customer?.primary_gym || payload.customer?.primaryGym;
  
  // Determine product type for the order summary
  let productType = 'membership'; // default
  if (state.selectedProductType === 'punch-card') {
    productType = 'punch-card';
  } else if (state.membershipPlanId && String(state.membershipPlanId).startsWith('15daypass-')) {
    productType = '15daypass';
  } else if (membershipPlanId) {
    // Check if it's a 15-Day Trial Pass by checking product labels
    const allSubscriptions = [...(state.subscriptions || []), ...(state.dayPassSubscriptions || [])];
    const numericId = membershipPlanId.replace(/^(campaign|membership|15daypass)-/, '');
    const productId = parseInt(numericId, 10);
    const foundProduct = allSubscriptions.find(sub => 
      sub.id === productId || 
      String(sub.id) === numericId
    );
    if (has15DayPassLabel(foundProduct)) {
      productType = '15daypass';
    }
  }
  
  return {
    number: orderNumber,
    date: order?.createdAt ? new Date(order.createdAt) : (order?.created ? new Date(order.created) : now),
    items: [...state.cartItems],
    total: order?.total || order?.totalAmount || state.totals.cartTotal,
    memberName: memberName || '—',
    membershipNumber: membershipId,
    membershipType: membership?.name ?? '—',
    primaryGym: resolveGymLabel(primaryGymValue),
    membershipPrice: state.totals.membershipMonthly,
    productType: productType, // Store product type in order summary
  };
}

function resolveGymLabel(value) {
  const mapping = {
    'boulders-copenhagen': 'Boulders Copenhagen',
    'boulders-aarhus': 'Boulders Aarhus',
    'boulders-odense': 'Boulders Odense',
  };
  return value ? mapping[value] ?? value : '—';
}

// Diagnostic helper: Export diagnostic data
window.exportPaymentDiagnostics = function() {
  const diagnostics = {
    timestamp: new Date().toISOString(),
    orderId: state.orderId,
    customerId: state.customerId,
    sessionStorage: {
      customer: sessionStorage.getItem('boulders_checkout_customer'),
      order: sessionStorage.getItem('boulders_checkout_order'),
    },
    state: {
      selectedBusinessUnit: state.selectedBusinessUnit,
      membershipPlanId: state.membershipPlanId,
      order: state.order,
    },
    url: window.location.href,
  };
  
  const blob = new Blob([JSON.stringify(diagnostics, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `payment-diagnostics-${Date.now()}.json`;
  a.click();
  URL.revokeObjectURL(url);
  
  console.log('[Diagnostics] Exported diagnostic data:', diagnostics);
  return diagnostics;
};

/**
 * Comprehensive order price verification function for debugging.
 * Call this from console to verify order pricing is correct.
 * 
 * Usage: verifyOrderPrice(orderId)
 * 
 * @param {number} orderId - Order ID to verify (optional, uses state.orderId if not provided)
 * @returns {Promise<Object>} Verification result with detailed information
 */
window.verifyOrderPrice = async function(orderId = null) {
  const orderIdToCheck = orderId || state.orderId;
  if (!orderIdToCheck) {
    console.error('No order ID provided and state.orderId is not set');
    return null;
  }
  
  console.log('===== COMPREHENSIVE ORDER PRICE VERIFICATION =====');
  console.log('Order ID:', orderIdToCheck);
  
  try {
    const order = await orderAPI.getOrder(orderIdToCheck);
    const subscriptionItem = order?.subscriptionItems?.[0];
    
    if (!subscriptionItem) {
      console.warn('No subscription item found in order');
      return { order, hasSubscription: false };
    }
    
    const productId = subscriptionItem?.product?.id;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const startDateStr = getTodayLocalDateString();
    const expectedPrice = orderAPI._calculateExpectedPartialMonthPrice(productId, startDateStr);
    const verification = orderAPI._verifySubscriptionPricing(order, productId, expectedPrice, today);
    
    const orderPriceAmount = order?.price?.amount || 0;
    const orderPriceInCents = typeof orderPriceAmount === 'object' ? orderPriceAmount.amount : orderPriceAmount;
    const orderPriceDKK = orderPriceInCents / 100;
    
    console.log('Order Price:', orderPriceDKK, 'DKK');
    console.log('Expected Price:', expectedPrice?.amountInDKK || 'N/A', 'DKK');
    console.log('Verification Result:', verification);
    console.log('Is Correct:', verification.isCorrect ? '✅ YES' : '❌ NO');
    
    if (!verification.isCorrect) {
      console.error('⚠️ ORDER PRICE IS INCORRECT!');
      console.error('Backend shows:', orderPriceDKK, 'DKK');
      console.error('Should be:', expectedPrice?.amountInDKK || 'N/A', 'DKK');
      console.error('Difference:', expectedPrice ? (orderPriceDKK - expectedPrice.amountInDKK).toFixed(2) : 'N/A', 'DKK');
    }
    
    return {
      order,
      subscriptionItem,
      productId,
      orderPriceDKK,
      expectedPriceDKK: expectedPrice?.amountInDKK || null,
      verification,
      isCorrect: verification.isCorrect
    };
  } catch (error) {
    console.error('Error verifying order price:', error);
    return { error: error.message };
  }
};

// Debug helper: Check payment overview state
window.checkPaymentOverview = function() {
  console.log('=== PAYMENT OVERVIEW DEBUG ===');
  console.log('State:', {
    orderId: state.orderId,
    hasFullOrder: !!state.fullOrder,
    fullOrderPrice: state.fullOrder?.price,
    fullOrderSubscriptionItems: state.fullOrder?.subscriptionItems?.length || 0,
    hasOrder: !!state.order,
    cartTotal: state.totals.cartTotal,
    membershipMonthly: state.totals.membershipMonthly,
    selectedProductType: state.selectedProductType,
    selectedProductId: state.selectedProductId
  });
  
  if (state.fullOrder) {
    console.log('Full Order:', JSON.stringify(state.fullOrder, null, 2));
  }
  
  if (state.orderId && !state.fullOrder) {
    console.log('⚠️ Order ID exists but fullOrder is missing - fetching...');
    orderAPI.getOrder(state.orderId)
      .then(order => {
        state.fullOrder = order;
        updatePaymentOverview();
        console.log('✅ Order fetched and payment overview updated');
      })
      .catch(error => {
        console.error('❌ Could not fetch order:', error);
      });
  } else if (state.fullOrder) {
    console.log('✅ Full order exists, updating payment overview...');
    updatePaymentOverview();
  }
  
  return {
    orderId: state.orderId,
    hasFullOrder: !!state.fullOrder,
    payNow: state.fullOrder?.price?.amount ? (typeof state.fullOrder.price.amount === 'object' ? state.fullOrder.price.amount.amount / 100 : state.fullOrder.price.amount / 100) : state.totals.cartTotal,
    monthlyPayment: state.fullOrder?.subscriptionItems?.[0]?.payRecurring?.price?.amount ? state.fullOrder.subscriptionItems[0].payRecurring.price.amount / 100 : state.totals.membershipMonthly
  };
};

// Diagnostic helper: Get current order status
window.getOrderDiagnostics = async function(orderId) {
  if (!orderId) {
    orderId = state.orderId;
  }
  if (!orderId) {
    console.error('[Diagnostics] No order ID available');
    return null;
  }
  
  try {
    const order = await orderAPI.getOrder(orderId);
    const diagnostics = {
      timestamp: new Date().toISOString(),
      orderId: orderId,
      order: {
        id: order.id,
        number: order.number,
        status: order.orderStatus?.name || order.status,
        statusId: order.orderStatus?.id,
        preliminary: order.preliminary,
        leftToPay: order.leftToPay,
        totalCost: order.totalCost,
        customer: order.customer,
        subscriptionItems: order.subscriptionItems,
        created: order.created,
        lastModified: order.lastModified,
      },
      subscriptionStatus: order.subscriptionItems?.map(item => ({
        id: item.subscription?.id,
        name: item.subscription?.name,
        users: item.subscription?.users,
        payer: item.subscription?.payer,
      })),
    };
    
    console.log('[Diagnostics] Order diagnostics:', diagnostics);
    return diagnostics;
  } catch (error) {
    console.error('[Diagnostics] Failed to get order diagnostics:', error);
    return { error: error.message };
  }
};

const PURCHASE_TRACKED_KEY_PREFIX = 'boulders_purchase_tracked_';

function getPurchaseTrackedStorageKey(orderId) {
  return `${PURCHASE_TRACKED_KEY_PREFIX}${orderId}`;
}

function isPurchaseAlreadyTracked(orderId) {
  try {
    return sessionStorage.getItem(getPurchaseTrackedStorageKey(orderId)) === '1';
  } catch (_) {
    return false;
  }
}

function markPurchaseTracked(orderId) {
  try {
    sessionStorage.setItem(getPurchaseTrackedStorageKey(orderId), '1');
  } catch (_) { /* ignore */ }
}

function getOrderTotalKrForTracking(order, storedOrder = null) {
  const fromOrderPrice = extractCurrencyOutKr(order?.price?.amount ?? order?.price);
  if (fromOrderPrice > 0) return fromOrderPrice;

  const storedTotal = storedOrder?.totals?.cartTotal ?? state.totals?.cartTotal;
  if (storedTotal > 0) return roundToHalfKrone(storedTotal);

  const legacy = order?.total ?? order?.totalAmount ?? order?.data?.total;
  if (typeof legacy === 'number' && legacy > 0) {
    return legacy > 10000 ? roundToHalfKrone(legacy / 100) : roundToHalfKrone(legacy);
  }

  return 0;
}

function buildPurchaseItemsFromOrder(order) {
  if (!order) return [];
  const items = [];

  const pushItem = ({ id, name, amountKr, type, quantity = 1 }) => {
    if (!id && !name) return;
    items.push({
      id: id || name,
      productId: id,
      name: name || 'Product',
      amount: roundToHalfKrone(amountKr),
      type,
      quantity,
    });
  };

  for (const item of order.subscriptionItems || []) {
    const product = item.product || {};
    const qty = item.quantity || 1;
    const lineKr = extractCurrencyOutKr(item.price?.amount ?? item.price);
    pushItem({
      id: product.id || item.id,
      name: product.name || 'Membership',
      amountKr: lineKr,
      type: 'membership',
      quantity: qty,
    });
  }

  for (const item of order.valueCardItems || []) {
    const product = item.product || {};
    const qty = item.quantity || 1;
    const lineKr = extractCurrencyOutKr(item.price?.amount ?? item.price);
    pushItem({
      id: product.id || item.id,
      name: product.name || 'Punch Card',
      amountKr: lineKr,
      type: 'value-card',
      quantity: qty,
    });
  }

  for (const item of order.articleItems || []) {
    const product = item.product || {};
    const qty = item.quantity || 1;
    const lineKr = extractCurrencyOutKr(item.price?.amount ?? item.price);
    pushItem({
      id: product.id || item.id,
      name: product.name || 'Add-on',
      amountKr: lineKr,
      type: 'addon',
      quantity: qty,
    });
  }

  for (const item of order.entryItems || []) {
    const product = item.product || {};
    const qty = item.quantity || 1;
    const lineKr = extractCurrencyOutKr(item.price?.amount ?? item.price);
    pushItem({
      id: product.id || item.id,
      name: product.name || 'Day ticket',
      amountKr: lineKr,
      type: 'entry',
      quantity: qty,
    });
  }

  return items;
}

function resolvePurchaseItems(order, storedOrder = null) {
  const fromCart = (state.cartItems && state.cartItems.length > 0)
    ? state.cartItems
    : (storedOrder?.cartItems || []);
  if (fromCart.length > 0) return fromCart;
  return buildPurchaseItemsFromOrder(order);
}

function sumPurchaseItemsKr(items) {
  return roundToHalfKrone(
    items.reduce((sum, item) => {
      const unit = typeof item.amount === 'number'
        ? item.amount
        : (typeof item.price === 'number' ? item.price : 0);
      return sum + unit * (item.quantity || 1);
    }, 0)
  );
}

function resolvePurchaseTrackingMetadata(order, storedOrder = null) {
  const businessUnit = order?.businessUnit || {};
  const gymId = businessUnit.id || storedOrder?.selectedBusinessUnit || state.selectedBusinessUnit || null;
  const gymName = businessUnit.name || businessUnit.label || null;
  const paymentType = storedOrder?.paymentMethod || state.paymentMethod || null;
  const landingPath = normalizePathname(window.location.pathname);

  const metadata = {};
  if (gymId != null) metadata.gym_id = String(gymId);
  if (gymName) metadata.gym_name = gymName;
  if (paymentType) metadata.payment_type = String(paymentType);
  if (landingPath) metadata.landing_path = landingPath;
  return metadata;
}

function trackConfirmedPurchase({ order, orderId, storedOrder = null } = {}) {
  if (!orderId) {
    devWarn('[GTM] purchase not tracked: missing orderId');
    return false;
  }

  if (isPurchaseAlreadyTracked(orderId)) {
    devLog('[GTM] purchase already tracked for order', orderId);
    return false;
  }

  if (!window.GTM?.trackPurchase) {
    devWarn('[GTM] purchase not tracked: GTM utilities not loaded');
    return false;
  }

  const purchaseItems = resolvePurchaseItems(order, storedOrder);
  if (!purchaseItems.length) {
    devWarn('[GTM] purchase not tracked: no items for order', orderId);
    return false;
  }

  let purchaseValue = getOrderTotalKrForTracking(order, storedOrder);
  if (purchaseValue <= 0) {
    purchaseValue = sumPurchaseItemsKr(purchaseItems);
  }

  const transactionId = order?.number ?? order?.id ?? orderId;
  const metadata = resolvePurchaseTrackingMetadata(order, storedOrder);

  try {
    window.GTM.trackPurchase(
      transactionId,
      purchaseItems,
      purchaseValue,
      0,
      0,
      'DKK',
      metadata
    );
    markPurchaseTracked(orderId);
    devLog('[GTM] purchase tracked:', { transactionId, purchaseValue, itemCount: purchaseItems.length });
    return true;
  } catch (error) {
    devWarn('[GTM] Error tracking purchase:', error);
    return false;
  }
}

function readStoredCheckoutOrder() {
  try {
    const orderData = sessionStorage.getItem('boulders_checkout_order');
    return orderData ? JSON.parse(orderData) : null;
  } catch (_) {
    return null;
  }
}

// Load order data when returning from payment
async function loadOrderForConfirmation(orderId) {
  // CRITICAL: Check for URL error parameters FIRST (before resetting flags)
  // If there's an error code in URL, payment definitely failed
  const urlParams = new URLSearchParams(window.location.search);
  const paymentError = urlParams.get('error');
  const paymentStatus = urlParams.get('status');
  const receiptId = urlParams.get('receiptid') || urlParams.get('receiptId');
  const receiptUuid = urlParams.get('receiptuuid') || urlParams.get('receiptUuid');
  
  const hasPaymentErrorParam = paymentError || paymentStatus === 'cancelled' || paymentStatus === 'canceled';
  const hasReceiptParam = Boolean(receiptId || receiptUuid);
  // Note: We no longer extract specific failure reasons since we show a single friendly message
  // But we still check for payment error params to determine if payment failed
  
  // CRITICAL: Reset payment status flags at start (only if no URL error)
  state.paymentFailed = false;
  state.paymentPending = false;
  state.paymentConfirmed = false;
  
  try {
    // Fix: Ensure orderId is numeric (handle cases where it might have path segments)
    if (typeof orderId === 'string') {
      // Remove any path segments (e.g., "817247/confirmation" -> "817247")
      orderId = orderId.split('/')[0].trim();
      orderId = parseInt(orderId, 10);
      if (isNaN(orderId)) {
        throw new Error(`Invalid order ID: ${orderId}`);
      }
    }
    
    const startTime = Date.now();
    console.log(`[Payment Return] [${new Date().toISOString()}] Fetching order data for:`, orderId);
    
    // Restore checkout data from sessionStorage
    let storedCustomer = null;
    let storedOrder = null;
    try {
      const customerData = sessionStorage.getItem('boulders_checkout_customer');
      const orderData = sessionStorage.getItem('boulders_checkout_order');
      if (customerData) {
        storedCustomer = JSON.parse(customerData);
        state.customerId = storedCustomer.id;
        console.log('[Payment Return] Restored customer from sessionStorage:', storedCustomer);
      }
      if (orderData) {
        storedOrder = JSON.parse(orderData);
        if (storedOrder.membershipPlanId) state.membershipPlanId = storedOrder.membershipPlanId;
        if (storedOrder.subscriptionStartDate != null) state.subscriptionStartDate = storedOrder.subscriptionStartDate;
        if (storedOrder.cartItems) state.cartItems = storedOrder.cartItems;
        if (storedOrder.totals) state.totals = storedOrder.totals;
        if (storedOrder.selectedBusinessUnit) state.selectedBusinessUnit = storedOrder.selectedBusinessUnit;
        console.log('[Payment Return] Restored order data from sessionStorage:', storedOrder);
      }
      
      // Load subscriptions from API if we have a business unit, so membership lookup works
      const hasAllProducts = (state.subscriptions?.length || 0) > 0 && (state.valueCards?.length || 0) > 0;
      if (state.selectedBusinessUnit && !hasAllProducts) {
        console.log('[Payment Return] Loading products from API for membership lookup...');
        await loadProductsFromAPI();
      }
    } catch (e) {
      console.warn('[Payment Return] Could not restore data from sessionStorage:', e);
    }
    
    // Fetch order from API
    let order;
    try {
      order = await orderAPI.getOrder(orderId);
      console.log('[Payment Return] Order fetched:', order);
    } catch (fetchError) {
      // CRITICAL: This catch block MUST handle ALL errors to prevent success page from showing
      console.error('[Payment Return] ===== INNER CATCH BLOCK REACHED =====');
      console.error('[Payment Return] Inner catch - fetchError:', fetchError);
      console.error('[Payment Return] Inner catch - fetchError.message:', fetchError?.message);
      console.error('[Payment Return] Inner catch - fetchError.status:', fetchError?.status);
      
      // ALWAYS show payment failed for ANY error when returning from payment
      // This is safer than showing success page when payment actually failed
      try {
        const errorMessage = String(fetchError?.message || fetchError || '');
        const errorStatus = fetchError?.status || (errorMessage.match(/401|404/) ? parseInt(errorMessage.match(/(401|404)/)?.[0]) : null);
        const isUnauthorized = errorStatus === 401 || errorMessage.includes('401') || errorMessage.includes('Unauthorized') || errorMessage.includes('Get order failed: 401');
        const isNotFound = errorStatus === 404 || errorMessage.includes('404') || errorMessage.includes('Not Found') || errorMessage.includes('Get order failed: 404');
        
        console.log('[Payment Return] Inner catch - Error analysis:', {
          errorMessage: errorMessage.substring(0, 200),
          errorStatus,
          fetchErrorStatus: fetchError?.status,
          isUnauthorized,
          isNotFound,
          errorString: String(fetchError).substring(0, 200)
        });
        
        if (hasReceiptParam && !hasPaymentErrorParam) {
          console.warn('[Payment Return] ⚠️ Order fetch failed, but receipt present - showing success using stored data');

          const summaryOrder = {
            id: orderId,
            orderId,
            number: orderId,
            created: new Date().toISOString(),
            total: storedOrder?.totals?.cartTotal ?? state.totals?.cartTotal ?? 0,
            totalAmount: storedOrder?.totals?.cartTotal ?? state.totals?.cartTotal ?? 0,
          };

          const payload = buildCheckoutPayload();
          const summaryCustomer = storedCustomer || null;

          state.orderId = orderId;
          state.order = buildOrderSummary(payload, summaryOrder, summaryCustomer);
          state.paymentConfirmed = true;
          state.paymentFailed = false;
          state.paymentPending = false;

          state.currentStep = TOTAL_STEPS;
          showStep(TOTAL_STEPS);
          updateStepIndicator();
          updateNavigationButtons();
          updateMainSubtitle();
          trackConfirmedPurchase({ order: summaryOrder, orderId, storedOrder });
          renderConfirmationView();
          return;
        }

        if (isUnauthorized && !hasPaymentErrorParam) {
          console.warn('[Payment Return] ⚠️ Order fetch unauthorized without error param - showing pending instead of failed');
          showPaymentPendingMessage(null, orderId);
          return;
        }

        console.warn('[Payment Return] ⚠️ INNER CATCH: Calling showPaymentFailedMessage');
        showPaymentFailedMessage(null, orderId, null);
        console.warn('[Payment Return] ✅ showPaymentFailedMessage called from inner catch - returning early');
        return; // CRITICAL: Always return to prevent success page
      } catch (innerError) {
        // Even if showPaymentFailedMessage fails, don't show success page
        console.error('[Payment Return] ❌ Error in inner catch handler:', innerError);
        // At minimum, navigate to step 5 and show error state
        state.paymentFailed = true;
        state.currentStep = TOTAL_STEPS;
        showStep(TOTAL_STEPS);
        return;
      }
    }
    
    // CRITICAL: Check payment status IMMEDIATELY after fetch
    // If payment is not confirmed, show pending/failed right away (don't show success first)
    const initialLeftToPay = order.leftToPay?.amount ?? order.leftToPay ?? null;
    const initialOrderStatus = order.orderStatus?.name || order.status;
    const initialIsPaid = order.orderStatus?.name === 'Betalet' || order.orderStatus?.id === 2;
    
    console.log('[Payment Return] Initial payment check:', {
      leftToPay: initialLeftToPay,
      orderStatus: initialOrderStatus,
      isPaid: initialIsPaid,
      willShowPending: initialLeftToPay !== null && initialLeftToPay > 0 && !initialIsPaid
    });
    
    // CRITICAL: Check payment status BEFORE showing any page
    // If payment is clearly not confirmed (leftToPay > 0 and not paid), show pending immediately
    if (hasPaymentErrorParam && !initialIsPaid) {
      console.warn('[Payment Return] ⚠️ Payment error param present and payment not confirmed - showing failure');
      showPaymentFailedMessage(order, orderId, null);
      return;
    }

    if (initialLeftToPay !== null && initialLeftToPay > 0 && !initialIsPaid) {
      console.warn('[Payment Return] ⚠️ Payment not confirmed on initial fetch - showing pending immediately');
      showPaymentPendingMessage(order, orderId);
      return; // Don't continue with success page logic
    }
    
    // Payment is confirmed - navigate to step 5 and show success
    state.currentStep = TOTAL_STEPS;
    showStep(TOTAL_STEPS);
    updateStepIndicator();
    updateNavigationButtons();
    updateMainSubtitle();
    
    // DETAILED LOGGING: Check order status and structure to diagnose membership creation issue
    const diagnosticTime = Date.now();
    console.log('[Payment Return] ===== ORDER DIAGNOSTICS =====');
    console.log(`[Payment Return] Diagnostic timestamp: ${new Date().toISOString()}`);
    console.log(`[Payment Return] Time since payment return: ${((diagnosticTime - startTime) / 1000).toFixed(2)}s`);
    console.log('[Payment Return] Full order object:', JSON.stringify(order, null, 2));
    console.log('[Payment Return] Order status:', order.status);
    console.log('[Payment Return] Order paymentStatus:', order.paymentStatus || order.payment?.status);
    console.log('[Payment Return] Order items:', order.items);
    console.log('[Payment Return] Order customer:', order.customer);
    
    // Check if subscription item exists in order
    const hasSubscription = order.items?.some(item => 
      item.type === 'subscription' || 
      item.productType === 'subscription' ||
      item.subscriptionProduct ||
      item.subscription
    );
    console.log('[Payment Return] Has subscription item in order:', hasSubscription);
    
    // Check customer memberships/subscriptions
    if (order.customer) {
      console.log('[Payment Return] Customer memberships:', order.customer.memberships);
      console.log('[Payment Return] Customer subscriptions:', order.customer.subscriptions);
      console.log('[Payment Return] Customer activeMemberships:', order.customer.activeMemberships);
    }
    
    // Check order status - API uses orderStatus object, not status field
    const orderStatusName = order.orderStatus?.name || order.status;
    const orderStatusId = order.orderStatus?.id;
    const isPreliminary = order.preliminary === true;
    const leftToPay = order.leftToPay?.amount || 0;
    
    console.log('[Payment Return] Order status name:', orderStatusName);
    console.log('[Payment Return] Order status ID:', orderStatusId);
    console.log('[Payment Return] Order is preliminary:', isPreliminary);
    console.log('[Payment Return] Left to pay:', leftToPay);
    
    // Warn if order is still pending or preliminary
    if (orderStatusName === 'Oprettet' || orderStatusName === 'Created' || isPreliminary || leftToPay > 0) {
      console.warn('[Payment Return] ⚠️ Order still in "Created" status or preliminary - membership may not be created yet');
      console.warn('[Payment Return] Order needs to be finalized/completed for membership to be created in BRP');
      console.warn('[Payment Return] This might indicate:');
      console.warn('[Payment Return]   1. Payment webhook hasn\'t arrived yet (wait a few seconds)');
      console.warn('[Payment Return]   2. Payment webhook failed');
      console.warn('[Payment Return]   3. Order needs to be manually finalized');
      
      // Try to finalize the order if it's preliminary and payment was successful
      // Note: We're returning from payment, so payment should be successful
      // The order needs to be finalized (preliminary: false) for membership to be created in BRP
      //
      // Idempotency guard: setting `preliminary: false` is suspected to trigger
      // a BRP-side order-confirmation email on every successful call, so if the
      // user reloads the return URL (or it's opened twice) we don't want to
      // attempt the finalize a second time. Track the attempt in sessionStorage
      // keyed on orderId — survives reloads but clears with the tab.
      const finalizeGuardKey = `boulders_finalize_attempted_${orderId}`;
      let alreadyAttemptedFinalize = false;
      try { alreadyAttemptedFinalize = sessionStorage.getItem(finalizeGuardKey) === '1'; } catch (_) {}

      if (isPreliminary && alreadyAttemptedFinalize) {
        console.log('[Payment Return] Skipping client-side finalize — already attempted in this session for order', orderId);
      } else if (isPreliminary) {
        console.log('[Payment Return] ⚠️ Order is preliminary - attempting to finalize...');
        console.log('[Payment Return] This is required for membership to be created in BRP');

        try {
          // Mark the attempt BEFORE calling so a mid-flight reload can't double-fire.
          try { sessionStorage.setItem(finalizeGuardKey, '1'); } catch (_) {}

          // Option 1: Try to set preliminary to false
          // The API might require additional fields or a specific endpoint
          const updateData = {
            preliminary: false, // Finalize the order
            businessUnit: state.selectedBusinessUnit || order.businessUnit?.id,
          };

          console.log('[Payment Return] Updating order with:', JSON.stringify(updateData, null, 2));
          const updatedOrder = await orderAPI.updateOrder(orderId, updateData);
          console.log('[Payment Return] Order update response:', updatedOrder);
          
          // Re-fetch order to verify status changed
          await new Promise(resolve => setTimeout(resolve, 500)); // Small delay for backend processing
          const refreshedOrder = await orderAPI.getOrder(orderId);
          console.log('[Payment Return] Refreshed order status:', refreshedOrder.orderStatus);
          console.log('[Payment Return] Refreshed order preliminary:', refreshedOrder.preliminary);
          console.log('[Payment Return] Refreshed order leftToPay:', refreshedOrder.leftToPay);
          
          if (!refreshedOrder.preliminary && refreshedOrder.leftToPay?.amount === 0) {
            console.log('[Payment Return] ✅ Order finalized successfully! Membership should now be created in BRP');
            // Use refreshed order for summary
            Object.assign(order, refreshedOrder);
          } else if (!refreshedOrder.preliminary) {
            console.log('[Payment Return] ✅ Order preliminary set to false, but leftToPay still > 0');
            console.log('[Payment Return] ⚠️ Payment has not been registered yet - membership will NOT be created until payment is confirmed');
            console.log('[Payment Return] This might indicate:');
            console.warn('[Payment Return]   1. Payment webhook from payment provider hasn\'t arrived yet');
            console.warn('[Payment Return]   2. Payment provider needs to send webhook to backend');
            console.warn('[Payment Return]   3. Backend needs to process payment webhook to register payment');
            console.warn('[Payment Return]   4. Once payment is registered (leftToPay = 0), membership will be created');
            
            // Try polling for payment registration (wait up to 10 seconds)
            console.log('[Payment Return] Polling for payment registration...');
            const pollStartTime = Date.now();
            let paymentRegistered = false;
            for (let attempt = 0; attempt < 5; attempt++) {
              await new Promise(resolve => setTimeout(resolve, 2000)); // Wait 2 seconds between attempts
              
              const pollTime = Date.now();
              const polledOrder = await orderAPI.getOrder(orderId);
              const newLeftToPay = polledOrder.leftToPay?.amount || 0;
              const timeSinceStart = ((pollTime - pollStartTime) / 1000).toFixed(1);
              
              console.log(`[Payment Return] [${new Date().toISOString()}] Poll attempt ${attempt + 1}/5 (${timeSinceStart}s): leftToPay = ${newLeftToPay}`);
              console.log(`[Payment Return] Poll ${attempt + 1} order status:`, polledOrder.orderStatus?.name, '| preliminary:', polledOrder.preliminary);
              
              if (newLeftToPay === 0) {
                console.log('[Payment Return] ✅ Payment registered! Membership should now be created in BRP');
                Object.assign(order, polledOrder);
                paymentRegistered = true;
                break;
              }
            }
            
            if (!paymentRegistered) {
              console.warn('[Payment Return] ⚠️ Payment still not registered after polling');
              console.warn('[Payment Return] The payment webhook may be delayed or failed');
              console.warn('[Payment Return] Membership will be created automatically once payment is registered');
            }
            
            // Use refreshed order for summary
            Object.assign(order, refreshedOrder);
          } else {
            console.warn('[Payment Return] ⚠️ Order update didn\'t change preliminary status');
            console.warn('[Payment Return] This might indicate:');
            console.warn('[Payment Return]   1. API doesn\'t allow client to finalize orders');
            console.warn('[Payment Return]   2. Backend webhook needs to finalize the order');
            console.warn('[Payment Return]   3. Additional fields required in update payload');
          }
        } catch (error) {
          console.error('[Payment Return] Failed to finalize order:', error);
          console.warn('[Payment Return] This might be expected if:');
          console.warn('[Payment Return]   1. Backend handles finalization via payment webhook');
          console.warn('[Payment Return]   2. Client is not authorized to finalize orders');
          console.warn('[Payment Return]   3. Order finalization requires different endpoint/fields');
          // Don't throw - continue with showing confirmation even if finalization fails
        }
      } else if (leftToPay > 0) {
        console.warn('[Payment Return] ⚠️ Order is not preliminary but still has leftToPay > 0');
        console.warn('[Payment Return] Payment may not have been registered yet');
      }
    }
    console.log('[Payment Return] =============================');
    
    // Extract customer data - try order response first, then stored data
    let customer = order?.customer || order?.data?.customer || null;
    let customerId = customer?.id || order?.customerId || state.customerId;
    
    // If no customer in order response, use stored customer data
    if (!customer && storedCustomer) {
      customer = storedCustomer;
      customerId = storedCustomer.id;
    }
    
    // If we have customer ID but no customer object, try to fetch customer data
    if (customerId && !customer) {
      try {
        // Note: We don't have a getCustomer API method, so we'll use stored data
        // If needed, we could add authAPI.getCustomer(customerId) later
        console.log('[Payment Return] Customer ID available but no customer object, using stored data');
      } catch (e) {
        console.warn('[Payment Return] Could not fetch customer data:', e);
      }
    }
    
    // Store customer ID if available
    if (customerId) {
      state.customerId = customerId;
    }
    
    // Build payload - use stored customer data if available
    const payload = buildCheckoutPayload();
    
    // Ensure customer data is in payload - prioritize stored customer data
    if (storedCustomer) {
      payload.customer = {
        firstName: storedCustomer.firstName,
        lastName: storedCustomer.lastName,
        email: storedCustomer.email,
        primaryGym: storedCustomer.primaryGym,
      };
    } else if (customer) {
      payload.customer = {
        firstName: customer.firstName || customer.first_name,
        lastName: customer.lastName || customer.last_name,
        email: customer.email,
        primaryGym: customer.primaryGym || customer.primary_gym,
      };
    }
    
    // Also ensure customer object has firstName/lastName for buildOrderSummary
    // Get primaryGym from business unit if not in customer data
    let primaryGym = storedCustomer?.primaryGym || customer?.primaryGym || customer?.primary_gym;
    if (!primaryGym && state.selectedBusinessUnit) {
      // Try to get gym name from business unit - check if businessUnits are loaded
      // Business units are stored in DOM or we need to fetch them
      // For now, use the business unit ID as fallback, or try to get from order
      if (order?.businessUnit) {
        primaryGym = order.businessUnit.name || order.businessUnit.label || order.businessUnit.id;
      } else {
        // Use business unit ID as fallback
        primaryGym = state.selectedBusinessUnit;
      }
    }
    
    if (!customer && storedCustomer) {
      customer = { ...storedCustomer, primaryGym: primaryGym || storedCustomer.primaryGym };
    } else if (customer && storedCustomer) {
      // Merge stored customer data into customer object if missing
      customer.firstName = customer.firstName || customer.first_name || storedCustomer.firstName;
      customer.lastName = customer.lastName || customer.last_name || storedCustomer.lastName;
      customer.email = customer.email || storedCustomer.email;
      customer.primaryGym = primaryGym || customer.primaryGym || customer.primary_gym || storedCustomer.primaryGym;
    } else if (customer) {
      customer.primaryGym = primaryGym || customer.primaryGym || customer.primary_gym;
    }
    
    const orderTotal = getOrderTotalKrForTracking(order, storedOrder);
    
    // Store diagnostic data for easy access (after customer is extracted)
    window.lastPaymentDiagnostics = {
      timestamp: new Date().toISOString(),
      orderId: orderId,
      order: order,
      customer: customer || storedCustomer,
      state: {
        customerId: state.customerId,
        membershipPlanId: state.membershipPlanId,
        selectedBusinessUnit: state.selectedBusinessUnit,
      },
    };
    console.log('[Payment Return] Diagnostic data stored in window.lastPaymentDiagnostics');
    
    // CRITICAL: Check payment status AFTER all processing (including polling)
    // This ensures we check the final state of the order after any updates
    const finalOrder = order; // Use the order object which may have been updated during polling
    const isPaymentConfirmed = finalOrder.leftToPay?.amount === 0 || finalOrder.leftToPay === 0;
    const isOrderPaid = finalOrder.orderStatus?.name === 'Betalet' || finalOrder.orderStatus?.id === 2; // Assuming 2 is "Paid" status
    
    console.log('[Payment Return] Final payment check:', {
      leftToPay: finalOrder.leftToPay?.amount || finalOrder.leftToPay,
      orderStatus: finalOrder.orderStatus?.name,
      isPaymentConfirmed,
      isOrderPaid,
      willShowSuccess: isPaymentConfirmed || isOrderPaid
    });
    
    if (!isPaymentConfirmed && !isOrderPaid) {
      console.warn('[Payment Return] ⚠️ Payment not confirmed - showing pending message instead of success page');
      console.warn('[Payment Return] leftToPay:', finalOrder.leftToPay?.amount || finalOrder.leftToPay);
      console.warn('[Payment Return] orderStatus:', finalOrder.orderStatus?.name);
      
      // Show payment pending message instead of success page
      showPaymentPendingMessage(finalOrder, orderId);
      return; // Don't show success page yet
    }
    
    // CRITICAL: Mark payment as confirmed to allow success page rendering
    state.paymentConfirmed = true;
    state.paymentFailed = false;
    state.paymentPending = false;
    
    // Store full order object for payment overview (before building summary)
    state.fullOrder = order;
    
    // Log order number fields to verify correct number is used
    console.log('[Payment Return] Order number fields:', {
      'order.id': order.id,
      'order.number': order.number,
      'order.orderId': order.orderId,
      'state.orderId': state.orderId,
      'Will use for display': order.number || order.id || order.orderId || state.orderId
    });
    
    // Build order summary with fetched data (for confirmation view)
    state.order = buildOrderSummary(payload, { ...order, total: orderTotal, totalAmount: orderTotal }, customer || storedCustomer);
    console.log('[Payment Return] Order summary built:', state.order);
    console.log('[Payment Return] Order number in summary:', state.order.number);
    
    // Update payment overview with order data
    updatePaymentOverview();
    
    trackConfirmedPurchase({ order, orderId, storedOrder });

    // Only render confirmation view if payment is confirmed
    renderConfirmationView();
  } catch (error) {
    console.error('[Payment Return] ===== CATCH BLOCK REACHED =====');
    console.error('[Payment Return] Failed to load order data:', error);
    
    // CRITICAL: Check if error is 401 (Unauthorized) - this usually means payment was cancelled/failed
    // 401 = Unauthorized (payment not completed, order not accessible)
    // 404 = Not Found (order doesn't exist)
    // Use multiple detection methods to be absolutely sure we catch 401 errors
    const errorString = String(error || '');
    const errorMessage = String(error?.message || errorString || '');
    const errorStatus = error?.status || (errorMessage.match(/401|404/) ? parseInt(errorMessage.match(/(401|404)/)?.[0]) : null);
    
    // Multiple ways to detect 401/404 errors - check EVERYTHING
    const has401InMessage = errorMessage.includes('401') || errorString.includes('401') || String(error).includes('401');
    const has404InMessage = errorMessage.includes('404') || errorString.includes('404') || String(error).includes('404');
    const isUnauthorized = errorStatus === 401 || has401InMessage || errorMessage.includes('Unauthorized') || errorString.includes('Unauthorized');
    const isNotFound = errorStatus === 404 || has404InMessage || errorMessage.includes('Not Found') || errorString.includes('Not Found');
    
    console.log('[Payment Return] Error detection:', {
      errorString: errorString.substring(0, 200),
      errorMessage: errorMessage.substring(0, 200),
      errorStatus,
      has401InMessage,
      has404InMessage,
      isUnauthorized,
      isNotFound,
      errorKeys: Object.keys(error || {})
    });
    
    // ALWAYS show payment failed for 401/404 errors (payment cancelled/failed)
    // Also show failed for ANY error when returning from payment (safer than showing success)
    if (isUnauthorized || isNotFound || has401InMessage || has404InMessage) {
      console.warn('[Payment Return] ⚠️ DETECTED 401/404 ERROR - Calling showPaymentFailedMessage');
      
      // CRITICAL: Always call showPaymentFailedMessage for 401/404 errors
      console.warn('[Payment Return] About to call showPaymentFailedMessage');
      showPaymentFailedMessage(null, orderId, null);
      console.warn('[Payment Return] showPaymentFailedMessage called - returning early');
      return; // Don't show success page
    }
    
    // For any other error when returning from payment, also show failed (safer than showing success)
    // This handles cases where error doesn't match 401/404 patterns but still indicates failure
    console.warn('[Payment Return] ⚠️ Unexpected error during payment return - showing payment failed as fallback');
    showPaymentFailedMessage(null, orderId, null);
    console.warn('[Payment Return] showPaymentFailedMessage (fallback) called - returning early');
    return; // Don't show success page or pending message
  }
}

async function showPaymentFailedMessage(order, orderId, reason = null) {
  // reason parameter is kept for backward compatibility but no longer used
  // All payment failures now show the same friendly message
  console.log('[Payment Failed] Called with:', { orderId, currentStep: state.currentStep });
  
  // CRITICAL: Restore cart and state from sessionStorage BEFORE showing failure page
  // This ensures cart is populated when user clicks "Try Payment Again"
  try {
    const orderData = sessionStorage.getItem('boulders_checkout_order');
    const customerData = sessionStorage.getItem('boulders_checkout_customer');
    
    if (orderData) {
      const storedOrder = JSON.parse(orderData);
      console.log('[Payment Failed] Restoring cart and state from sessionStorage:', storedOrder);
      
      // Restore cart items and state
      if (storedOrder.cartItems) {
        state.cartItems = storedOrder.cartItems;
        console.log('[Payment Failed] ✅ Restored cart items:', state.cartItems.length, 'items');
        
        // CRITICAL: For punch cards, rebuild valueCardQuantities from cart items
        // This is needed because valueCardQuantities is a Map and doesn't serialize to JSON
        const punchCardItems = storedOrder.cartItems.filter(item => item.type === 'value-card');
        if (punchCardItems.length > 0) {
          console.log('[Payment Failed] Found punch card items, rebuilding valueCardQuantities');
          state.valueCardQuantities.clear();
          punchCardItems.forEach(item => {
            // Use membershipPlanId format (punch-{id} or adult-punch/junior-punch)
            const planId = storedOrder.membershipPlanId || 
                          (item.productId ? `punch-${item.productId}` : `punch-${item.id}`);
            const quantity = item.quantity || 1;
            state.valueCardQuantities.set(planId, quantity);
            console.log('[Payment Failed] ✅ Rebuilt valueCardQuantities:', planId, '=', quantity);
          });
        }
      }
      if (storedOrder.totals) {
        state.totals = { ...state.totals, ...storedOrder.totals };
        console.log('[Payment Failed] ✅ Restored totals:', state.totals);
      }
      
      // Restore selectedProductType and selectedProductId if available
      if (storedOrder.selectedProductType) {
        state.selectedProductType = storedOrder.selectedProductType;
        console.log('[Payment Failed] ✅ Restored selectedProductType:', state.selectedProductType);
      }
      if (storedOrder.selectedProductId) {
        state.selectedProductId = storedOrder.selectedProductId;
        console.log('[Payment Failed] ✅ Restored selectedProductId:', state.selectedProductId);
      }
      
      if (storedOrder.membershipPlanId) {
        state.membershipPlanId = storedOrder.membershipPlanId;
        console.log('[Payment Failed] ✅ Restored membershipPlanId:', state.membershipPlanId);
        
        // CRITICAL: Derive selectedProductId and selectedProductType from membershipPlanId if not already set
        // Handle all formats: campaign-, membership-, 15daypass-, punch-
        if (!state.selectedProductType || !state.selectedProductId) {
          if (typeof storedOrder.membershipPlanId === 'string') {
            if (storedOrder.membershipPlanId.startsWith('campaign-')) {
              const productId = storedOrder.membershipPlanId.replace('campaign-', '');
              state.selectedProductId = parseInt(productId, 10) || productId;
              state.selectedProductType = 'membership';
              console.log('[Payment Failed] ✅ Derived selectedProductId from campaign:', state.selectedProductId);
            } else if (storedOrder.membershipPlanId.startsWith('membership-')) {
              const productId = storedOrder.membershipPlanId.replace('membership-', '');
              state.selectedProductId = parseInt(productId, 10) || productId;
              state.selectedProductType = 'membership';
              console.log('[Payment Failed] ✅ Derived selectedProductId from membership:', state.selectedProductId);
            } else if (storedOrder.membershipPlanId.startsWith('15daypass-')) {
              const productId = storedOrder.membershipPlanId.replace('15daypass-', '');
              state.selectedProductId = parseInt(productId, 10) || productId;
              state.selectedProductType = 'membership';
              console.log('[Payment Failed] ✅ Derived selectedProductId from 15daypass:', state.selectedProductId);
            } else if (storedOrder.membershipPlanId.startsWith('punch-') || 
                       storedOrder.membershipPlanId === 'adult-punch' || 
                       storedOrder.membershipPlanId === 'junior-punch') {
              // For punch cards, extract productId from membershipPlanId
              if (storedOrder.membershipPlanId.startsWith('punch-')) {
                const productId = storedOrder.membershipPlanId.replace('punch-', '');
                state.selectedProductId = parseInt(productId, 10) || productId;
              } else {
                // For adult-punch/junior-punch, we need to find the productId from cart items
                const punchCardItem = storedOrder.cartItems?.find(item => item.type === 'value-card');
                if (punchCardItem) {
                  state.selectedProductId = punchCardItem.productId || punchCardItem.id;
                }
              }
              state.selectedProductType = 'punch-card';
              console.log('[Payment Failed] ✅ Derived selectedProductId from punch card:', state.selectedProductId);
            }
          }
        }
      }
      if (storedOrder.subscriptionStartDate != null) {
        state.subscriptionStartDate = storedOrder.subscriptionStartDate;
      }
      if (storedOrder.selectedBusinessUnit) {
        state.selectedBusinessUnit = storedOrder.selectedBusinessUnit;
        console.log('[Payment Failed] ✅ Restored selectedBusinessUnit:', state.selectedBusinessUnit);
      }
      if (storedOrder.orderId) {
        state.orderId = storedOrder.orderId;
        console.log('[Payment Failed] ✅ Restored orderId:', state.orderId);
      }
    }
    
    // CRITICAL: Restore customer profile data to show full profile info
    if (customerData) {
      try {
        const storedCustomer = JSON.parse(customerData);
        state.customerId = storedCustomer.id;
        console.log('[Payment Failed] Restored customer ID from sessionStorage:', storedCustomer.id);
        
        // Fetch full customer profile to display all fields (if we have a customer ID)
        // Try to fetch even if authentication status is unclear, as we might have valid tokens
        if (storedCustomer.id) {
          try {
            const customerProfile = await authAPI.getCustomer(storedCustomer.id);
            state.authenticatedCustomer = customerProfile;
            // Also set authenticatedEmail for refreshLoginUI
            if (customerProfile?.email) {
              state.authenticatedEmail = customerProfile.email;
            }
            console.log('[Payment Failed] ✅ Loaded full customer profile:', customerProfile);
            // Refresh UI after loading full profile
            refreshLoginUI();
          } catch (profileError) {
            console.warn('[Payment Failed] Could not fetch full customer profile, using stored data:', profileError);
            // Fallback: Use stored customer data if available
            if (storedCustomer.firstName || storedCustomer.lastName || storedCustomer.email) {
              state.authenticatedCustomer = {
                id: storedCustomer.id,
                firstName: storedCustomer.firstName,
                lastName: storedCustomer.lastName,
                email: storedCustomer.email,
                // Note: Full profile might not have all fields, but we'll try to use what we have
              };
              // Also set authenticatedEmail for refreshLoginUI
              if (storedCustomer.email) {
                state.authenticatedEmail = storedCustomer.email;
              }
            }
            // Refresh UI with fallback data
            refreshLoginUI();
          }
        } else {
          // No customer ID - refresh UI with what we have
          refreshLoginUI();
        }
      } catch (e) {
        console.warn('[Payment Failed] Could not parse customer data from sessionStorage:', e);
        // Still refresh UI even if customer data parse failed
        refreshLoginUI();
      }
    } else {
      // No customer data in sessionStorage - refresh UI with current state
      refreshLoginUI();
    }
  } catch (e) {
    console.warn('[Payment Failed] Could not restore cart from sessionStorage:', e);
    // Still refresh UI even if restore failed
    refreshLoginUI();
  }
  
  // CRITICAL: Mark payment as failed FIRST to prevent success page from rendering
  state.paymentFailed = true;
  state.paymentConfirmed = false;
  state.paymentPending = false;
  
  // CRITICAL: Clear order data to prevent success page from rendering
  // Don't set state.order - this prevents renderConfirmationView() from being called
  if (state.order) {
    console.log('[Payment Failed] Clearing state.order to prevent success page');
    state.order = null;
  }
  
  // CRITICAL: Navigate to step 5 and IMMEDIATELY modify HTML before it's visible
  console.log('[Payment Failed] Navigating to confirmation page...');
  state.currentStep = TOTAL_STEPS;
  
  // Show step 5 panel immediately - hide all other steps first to prevent flash
  const step5Panel = document.getElementById('step-5');
  if (step5Panel) {
    // CRITICAL: Hide all other panels FIRST to prevent any flash of step 1
    DOM.stepPanels.forEach((panel, index) => {
      if (index + 1 === TOTAL_STEPS) {
        // This is step 5 - show it
        panel.classList.add('active');
        panel.style.display = 'block';
        panel.style.visibility = 'visible';
        panel.style.opacity = '1';
      } else {
        // Hide all other steps immediately
        panel.classList.remove('active');
        panel.style.display = 'none';
        panel.style.visibility = 'hidden';
        panel.style.opacity = '0';
      }
    });
    
    // Mark step 5 panel as payment failed for CSS targeting
    step5Panel.setAttribute('data-payment-failed', 'true');
    
    // Add class to step-content to adjust layout when payment fails
    const stepContent = document.querySelector('.step-content');
    if (stepContent) {
      stepContent.classList.remove('success-page-active'); // Remove success page class if present
      stepContent.classList.add('payment-failed-active');
      stepContent.style.flex = 'none';
      stepContent.style.minHeight = 'auto';
      stepContent.style.height = 'auto';
    }
    
    // IMMEDIATELY modify the HTML BEFORE it's visible to user
    const confirmationHeader = step5Panel.querySelector('.confirmation-header');
    if (!confirmationHeader) {
      console.error('[Payment Failed] Confirmation header not found');
      return;
    }
    
    // Hide success elements
    const successTitle = step5Panel.querySelector('.success-title');
    const successMessage = step5Panel.querySelector('.success-message');
    const successBadge = step5Panel.querySelector('.success-badge');
    
    if (successTitle) {
      successTitle.style.display = 'none';
    }
    if (successMessage) {
      successMessage.style.display = 'none';
    }
    if (successBadge) {
      successBadge.style.display = 'none';
    }
    
    // Get or create payment failed elements
    let paymentFailedBadge = confirmationHeader.querySelector('.payment-failed-badge');
    let paymentFailedTitle = confirmationHeader.querySelector('.payment-failed-title');
    let quickStatus = confirmationHeader.querySelector('.quick-status');
    let reassuranceBar = confirmationHeader.querySelector('.reassurance-bar');
    let failureActions = confirmationHeader.querySelector('.failure-actions');
    
    const displayOrderId = orderId || order?.number || order?.id || 'N/A';
    
    // Use a single friendly message for all payment failures
    const statusText = 'Payment Error';
    const statusDetail = 'Something went wrong with your payment. Please try again.';
    
    // Create payment failed badge
    if (!paymentFailedBadge) {
      paymentFailedBadge = document.createElement('div');
      paymentFailedBadge.className = 'payment-failed-badge';
      confirmationHeader.insertBefore(paymentFailedBadge, confirmationHeader.firstChild);
    }
    paymentFailedBadge.innerHTML = `
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
        <line x1="12" y1="9" x2="12" y2="13"/>
        <line x1="12" y1="17" x2="12.01" y2="17"/>
      </svg>
    `;
    
    // Create payment failed title
    if (!paymentFailedTitle) {
      paymentFailedTitle = document.createElement('h2');
      paymentFailedTitle.className = 'payment-failed-title';
      confirmationHeader.insertBefore(paymentFailedTitle, quickStatus || reassuranceBar || failureActions || null);
    }
    paymentFailedTitle.textContent = 'Payment Couldn\'t Be Completed';
    
    // Create quick status
    if (!quickStatus) {
      quickStatus = document.createElement('div');
      quickStatus.className = 'quick-status';
      confirmationHeader.insertBefore(quickStatus, reassuranceBar || failureActions || null);
    }
    quickStatus.innerHTML = `
      <span class="status-badge">${statusText}</span>
      <p class="status-detail">${statusDetail}</p>
    `;
    
    // Create reassurance bar with separate containers
    if (!reassuranceBar) {
      reassuranceBar = document.createElement('div');
      reassuranceBar.className = 'reassurance-bar';
      confirmationHeader.insertBefore(reassuranceBar, failureActions || null);
    }
    reassuranceBar.innerHTML = `
      <div class="reassurance-item">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="20 6 9 17 4 12"/>
        </svg>
        <span>Nothing charged</span>
      </div>
      <div class="reassurance-item">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="20 6 9 17 4 12"/>
        </svg>
        <span>Order saved</span>
      </div>
      <div class="reassurance-item">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="20 6 9 17 4 12"/>
        </svg>
        <span>Profile saved</span>
      </div>
    `;
    
    // Create failure actions
    if (!failureActions) {
      failureActions = document.createElement('div');
      failureActions.className = 'failure-actions';
      confirmationHeader.appendChild(failureActions);
    }
    failureActions.innerHTML = `
      <div class="action-grid">
        <div class="action-option primary-option">
          <div class="option-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/>
            </svg>
          </div>
          <h4>Try Again</h4>
          <p>Retry payment with saved order details</p>
        </div>
        

        <div class="action-option" id="contact-support-option">
          <div class="option-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
            </svg>
          </div>
          <h4>Get Help</h4>
          <p>Email support • Order #<span id="failed-order-id">${displayOrderId}</span></p>
        </div>
      </div>

      <button id="retry-payment-btn" class="retry-btn">
        <span>Try Payment Again</span>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <line x1="5" y1="12" x2="19" y2="12"/>
          <polyline points="12 5 19 12 12 19"/>
        </svg>
      </button>
    `;
    
    // Add event listeners for action buttons
    setTimeout(() => {
      const retryBtn = document.getElementById('retry-payment-btn');
      const supportOption = document.getElementById('contact-support-option');
      const primaryOption = document.querySelector('.action-option.primary-option');
      
      // Shared retry payment handler function
      const handleRetryPayment = async () => {
          console.log('[Payment Failed] User clicked "Try Payment Again" - navigating to payment step');
          
          // CRITICAL: Restore cart and state from sessionStorage BEFORE navigating
          // This ensures cart is populated when showStep(4) runs
          try {
            const orderData = sessionStorage.getItem('boulders_checkout_order');
            const customerData = sessionStorage.getItem('boulders_checkout_customer');
            
            if (orderData) {
              const storedOrder = JSON.parse(orderData);
              console.log('[Payment Retry] Restoring cart and state from sessionStorage:', storedOrder);
              
              // Always restore cart items (force restore, don't check if empty)
              if (storedOrder.cartItems) {
                state.cartItems = storedOrder.cartItems;
                console.log('[Payment Retry] ✅ Restored cart items:', state.cartItems.length, 'items');
                
                // CRITICAL: For punch cards, rebuild valueCardQuantities from cart items
                // This is needed because valueCardQuantities is a Map and doesn't serialize to JSON
                const punchCardItems = storedOrder.cartItems.filter(item => item.type === 'value-card');
                if (punchCardItems.length > 0) {
                  console.log('[Payment Retry] Found punch card items, rebuilding valueCardQuantities');
                  state.valueCardQuantities.clear();
                  punchCardItems.forEach(item => {
                    // Use membershipPlanId format (punch-{id} or adult-punch/junior-punch)
                    // If membershipPlanId exists, use it; otherwise construct from productId
                    const planId = storedOrder.membershipPlanId || 
                                  (item.productId ? `punch-${item.productId}` : `punch-${item.id}`);
                    const quantity = item.quantity || 1;
                    state.valueCardQuantities.set(planId, quantity);
                    console.log('[Payment Retry] ✅ Rebuilt valueCardQuantities:', planId, '=', quantity);
                  });
                }
              }
              if (storedOrder.totals) {
                state.totals = { ...state.totals, ...storedOrder.totals };
                console.log('[Payment Retry] ✅ Restored totals');
              }
              
              // Restore selectedProductType and selectedProductId if available
              if (storedOrder.selectedProductType) {
                state.selectedProductType = storedOrder.selectedProductType;
                console.log('[Payment Retry] ✅ Restored selectedProductType:', state.selectedProductType);
              }
              if (storedOrder.selectedProductId) {
                state.selectedProductId = storedOrder.selectedProductId;
                console.log('[Payment Retry] ✅ Restored selectedProductId:', state.selectedProductId);
              }
              
              if (storedOrder.membershipPlanId) {
                state.membershipPlanId = storedOrder.membershipPlanId;
                console.log('[Payment Retry] ✅ Restored membershipPlanId:', state.membershipPlanId);
                
                // CRITICAL: Derive selectedProductId and selectedProductType from membershipPlanId if not already set
                // Handle all formats: campaign-, membership-, 15daypass-, punch-
                if (!state.selectedProductType || !state.selectedProductId) {
                  if (typeof storedOrder.membershipPlanId === 'string') {
                    if (storedOrder.membershipPlanId.startsWith('campaign-')) {
                      const productId = storedOrder.membershipPlanId.replace('campaign-', '');
                      state.selectedProductId = parseInt(productId, 10) || productId;
                      state.selectedProductType = 'membership';
                      console.log('[Payment Retry] ✅ Derived selectedProductId from campaign:', state.selectedProductId);
                    } else if (storedOrder.membershipPlanId.startsWith('membership-')) {
                      const productId = storedOrder.membershipPlanId.replace('membership-', '');
                      state.selectedProductId = parseInt(productId, 10) || productId;
                      state.selectedProductType = 'membership';
                      console.log('[Payment Retry] ✅ Derived selectedProductId from membership:', state.selectedProductId);
                    } else if (storedOrder.membershipPlanId.startsWith('15daypass-')) {
                      const productId = storedOrder.membershipPlanId.replace('15daypass-', '');
                      state.selectedProductId = parseInt(productId, 10) || productId;
                      state.selectedProductType = 'membership';
                      console.log('[Payment Retry] ✅ Derived selectedProductId from 15daypass:', state.selectedProductId);
                    } else if (storedOrder.membershipPlanId.startsWith('punch-') || 
                               storedOrder.membershipPlanId === 'adult-punch' || 
                               storedOrder.membershipPlanId === 'junior-punch') {
                      // For punch cards, extract productId from membershipPlanId
                      if (storedOrder.membershipPlanId.startsWith('punch-')) {
                        const productId = storedOrder.membershipPlanId.replace('punch-', '');
                        state.selectedProductId = parseInt(productId, 10) || productId;
                      } else {
                        // For adult-punch/junior-punch, we need to find the productId from cart items
                        const punchCardItem = storedOrder.cartItems?.find(item => item.type === 'value-card');
                        if (punchCardItem) {
                          state.selectedProductId = punchCardItem.productId || punchCardItem.id;
                        }
                      }
                      state.selectedProductType = 'punch-card';
                      console.log('[Payment Retry] ✅ Derived selectedProductId from punch card:', state.selectedProductId);
                    }
                  }
                }
              }
              if (storedOrder.selectedBusinessUnit) {
                state.selectedBusinessUnit = storedOrder.selectedBusinessUnit;
                console.log('[Payment Retry] ✅ Restored selectedBusinessUnit');
              }
              if (storedOrder.subscriptionStartDate != null) {
                state.subscriptionStartDate = storedOrder.subscriptionStartDate;
              }
              if (storedOrder.orderId) {
                state.orderId = storedOrder.orderId;
                console.log('[Payment Retry] ✅ Restored orderId');
              }
            }
            
            // CRITICAL: Verify cart was restored before proceeding
            if (!state.cartItems || state.cartItems.length === 0) {
              console.error('[Payment Retry] ⚠️ WARNING: Cart is still empty after restoration attempt!');
              console.error('[Payment Retry] Stored order data:', orderData ? 'exists' : 'missing');
            } else {
              console.log('[Payment Retry] ✅ Cart successfully restored with', state.cartItems.length, 'items');
            }
            
            // CRITICAL: Restore customer profile data to show full profile info
            if (customerData) {
              try {
                const storedCustomer = JSON.parse(customerData);
                state.customerId = storedCustomer.id;
                console.log('[Payment Retry] Restored customer ID from sessionStorage:', storedCustomer.id);
                
                // Fetch full customer profile to display all fields (if we have a customer ID)
                // Try to fetch even if authentication status is unclear, as we might have valid tokens
                if (storedCustomer.id) {
                  try {
                    const customerProfile = await authAPI.getCustomer(storedCustomer.id);
                    state.authenticatedCustomer = customerProfile;
                    // Also set authenticatedEmail for refreshLoginUI
                    if (customerProfile?.email) {
                      state.authenticatedEmail = customerProfile.email;
                    }
                    console.log('[Payment Retry] ✅ Loaded full customer profile:', customerProfile);
                    // Refresh UI after loading full profile
                    refreshLoginUI();
                  } catch (profileError) {
                    console.warn('[Payment Retry] Could not fetch full customer profile, using stored data:', profileError);
                    // Fallback: Use stored customer data if available
                    if (storedCustomer.firstName || storedCustomer.lastName || storedCustomer.email) {
                      state.authenticatedCustomer = {
                        id: storedCustomer.id,
                        firstName: storedCustomer.firstName,
                        lastName: storedCustomer.lastName,
                        email: storedCustomer.email,
                        // Note: Full profile might not have all fields, but we'll try to use what we have
                      };
                      // Also set authenticatedEmail for refreshLoginUI
                      if (storedCustomer.email) {
                        state.authenticatedEmail = storedCustomer.email;
                      }
                    }
                    // Refresh UI with fallback data
                    refreshLoginUI();
                  }
                } else {
                  // No customer ID - refresh UI with what we have
                  refreshLoginUI();
                }
              } catch (e) {
                console.warn('[Payment Retry] Could not parse customer data from sessionStorage:', e);
                // Still refresh UI even if customer data parse failed
                refreshLoginUI();
              }
            } else {
              // No customer data in sessionStorage - refresh UI with current state
              refreshLoginUI();
            }
          } catch (e) {
            console.warn('[Payment Retry] Could not restore cart from sessionStorage:', e);
            // Still refresh UI even if restore failed
            refreshLoginUI();
          }
          
          // Reset payment failed state and navigate to step 4
          state.paymentFailed = false;
          // Clean up payment-failed-active class and inline styles before navigating
          const stepContent = document.querySelector('.step-content');
          if (stepContent) {
            stepContent.classList.remove('payment-failed-active');
            stepContent.style.flex = '';
            stepContent.style.minHeight = '';
            stepContent.style.height = '';
          }
          
          state.currentStep = 4;
          showStep(4);
          updateStepIndicator();
          updateNavigationButtons();
          updateMainSubtitle();
          
          // CRITICAL: Update cart immediately after restoration to ensure it's displayed
          // Then update again after DOM is ready to ensure proper rendering
          console.log('[Payment Retry] Cart items after restoration:', state.cartItems?.length || 0, 'items');
          updateCartSummary();
          
          // CRITICAL: Update cart and payment overview after DOM is ready
          // Use setTimeout to ensure showStep(4) has finished rendering
          setTimeout(() => {
            // Refresh login UI to ensure profile information is displayed
            refreshLoginUI();
            
            // Update cart again to ensure it's displayed (in case showStep(4) cleared it)
            console.log('[Payment Retry] Updating cart after showStep(4) - cart items:', state.cartItems?.length || 0, 'items');
            updateCartSummary();
            
            // If order exists, fetch full order data for payment overview
            if (state.orderId) {
              console.log('[Payment Retry] Fetching order data for payment overview (orderId:', state.orderId, ')');
              orderAPI.getOrder(state.orderId)
                .then(order => {
                  state.fullOrder = order;
                  updatePaymentOverview();
                  console.log('[Payment Retry] ✅ Order data fetched, payment overview updated');
                })
                .catch(error => {
                  console.warn('[Payment Retry] Could not fetch order data for payment overview:', error);
                  // Still update payment overview with available data
                  updatePaymentOverview();
                });
            } else {
              // Update payment overview with current state data
              updatePaymentOverview();
            }
          }, 200); // Increased timeout to ensure showStep(4) has fully completed
          
          scrollToTop();
      };
      
      // Attach handler to retry button
      if (retryBtn) {
        retryBtn.addEventListener('click', handleRetryPayment);
      }
      
      // Attach handler to primary option (Try Again card)
      if (primaryOption) {
        primaryOption.addEventListener('click', handleRetryPayment);
        // Add cursor pointer style for better UX
        primaryOption.style.cursor = 'pointer';
      }
      
      if (supportOption) {
        supportOption.addEventListener('click', () => {
          console.log('[Payment Failed] User clicked "Get Help"');
          // Open support email
          const supportEmail = 'medlem@boulders.dk';
          const subject = encodeURIComponent(`Payment Issue - Order #${displayOrderId}`);
          const body = encodeURIComponent(`Hello,\n\nHej, jeg kunne ikke gennemføre købet af ordrenr. #${displayOrderId}.\n\nKan i hjælpe med at løse det?\n\nMange tak!`);
          window.location.href = `mailto:${supportEmail}?subject=${subject}&body=${body}`;
        });
      }
    }, 100);
    
    console.log('[Payment Failed] ✅ New payment failed UI structure created');
    
    // CRITICAL: Hide order details, membership details, and "What happens next?" sections
    // These should only be shown when payment is successful
    // Use multiple methods to ensure they stay hidden
    const confirmationLayout = step5Panel.querySelector('.confirmation-layout');
    const confirmationLeft = step5Panel.querySelector('.confirmation-left');
    const confirmationRight = step5Panel.querySelector('.confirmation-right');
    
    const hideConfirmationSections = () => {
      if (confirmationLayout) {
        confirmationLayout.style.display = 'none';
        confirmationLayout.style.visibility = 'hidden';
        confirmationLayout.setAttribute('data-payment-failed', 'true');
      }
      if (confirmationLeft) {
        confirmationLeft.style.display = 'none';
        confirmationLeft.style.visibility = 'hidden';
        confirmationLeft.setAttribute('data-payment-failed', 'true');
      }
      if (confirmationRight) {
        confirmationRight.style.display = 'none';
        confirmationRight.style.visibility = 'hidden';
        confirmationRight.setAttribute('data-payment-failed', 'true');
      }
    };
    
    // Hide immediately
    hideConfirmationSections();
    console.log('[Payment Failed] ✅ Hidden confirmation sections');
    
    // Re-hide after a short delay in case something tries to show them
    setTimeout(() => {
      hideConfirmationSections();
      console.log('[Payment Failed] ✅ Re-checked and ensured confirmation sections are hidden');
    }, 100);
    
    // Also set up a MutationObserver to keep them hidden
    if (confirmationLayout && typeof MutationObserver !== 'undefined') {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
            const target = mutation.target;
            if (target.getAttribute('data-payment-failed') === 'true' && target.style.display !== 'none') {
              target.style.display = 'none';
              target.style.visibility = 'hidden';
              console.log('[Payment Failed] 🔒 Prevented confirmation section from being shown');
            }
          }
        });
      });
      
      [confirmationLayout, confirmationLeft, confirmationRight].forEach((el) => {
        if (el) {
          observer.observe(el, { attributes: true, attributeFilter: ['style'] });
        }
      });
    }
    
    console.log('[Payment Failed] ✅ HTML modified BEFORE showing panel');
  }
  
  updateStepIndicator();
  updateNavigationButtons();
  updateMainSubtitle();
  
  console.log('[Payment Failed] ✅ Payment failed page displayed');
}

function showPaymentPendingMessage(order, orderId) {
  // CRITICAL: Mark payment as pending to prevent success page from rendering
  state.paymentPending = true;
  state.paymentConfirmed = false;
  state.paymentFailed = false;
  
  // CRITICAL: Ensure we're on step 5 (confirmation page) before modifying elements
  // If we're not on the confirmation page yet, navigate to it first
  if (state.currentStep !== TOTAL_STEPS) {
    console.log('[Payment Pending] Navigating to confirmation page first...');
    state.currentStep = TOTAL_STEPS;
    showStep(TOTAL_STEPS);
    updateStepIndicator();
    updateNavigationButtons();
    updateMainSubtitle();
  }
  
  // Update the confirmation page to show payment pending instead of success
  const successTitle = document.querySelector('.success-title');
  const successMessage = document.querySelector('.success-message');
  const successBadge = document.querySelector('.success-badge');
  const displayOrderId = orderId || order?.number || order?.id || 'N/A';
  
  if (successTitle) {
    successTitle.removeAttribute('data-i18n-key');
    successTitle.textContent = t('confirmation.pending.title');
    successTitle.style.color = '#f59e0b'; // Orange/amber color
  }
  
  if (successMessage) {
    successMessage.removeAttribute('data-i18n-key');
    successMessage.textContent = t('confirmation.pending.message') + displayOrderId;
    successMessage.style.color = '#6b7280'; // Gray color
  }
  
  if (successBadge) {
    // Change checkmark to a clock/spinner icon
    successBadge.innerHTML = sanitizeHTML(`
      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="color: #f59e0b;">
        <circle cx="12" cy="12" r="10"></circle>
        <polyline points="12 6 12 12 16 14"></polyline>
      </svg>
    `);
  }
  
  // CRITICAL: Hide order details, membership details, and "What happens next?" sections
  // These should only be shown when payment is confirmed
  const step5Panel = document.getElementById('step-5');
  if (step5Panel) {
    // Mark step 5 panel as payment pending for CSS targeting
    step5Panel.setAttribute('data-payment-pending', 'true');
    const confirmationLayout = step5Panel.querySelector('.confirmation-layout');
    const confirmationLeft = step5Panel.querySelector('.confirmation-left');
    const confirmationRight = step5Panel.querySelector('.confirmation-right');
    
    const hideConfirmationSections = () => {
      if (confirmationLayout) {
        confirmationLayout.style.display = 'none';
        confirmationLayout.style.visibility = 'hidden';
        confirmationLayout.setAttribute('data-payment-pending', 'true');
      }
      if (confirmationLeft) {
        confirmationLeft.style.display = 'none';
        confirmationLeft.style.visibility = 'hidden';
        confirmationLeft.setAttribute('data-payment-pending', 'true');
      }
      if (confirmationRight) {
        confirmationRight.style.display = 'none';
        confirmationRight.style.visibility = 'hidden';
        confirmationRight.setAttribute('data-payment-pending', 'true');
      }
    };
    
    // Hide immediately
    hideConfirmationSections();
    console.log('[Payment Pending] ✅ Hidden confirmation sections');
    
    // Re-hide after a short delay in case something tries to show them
    setTimeout(() => {
      hideConfirmationSections();
      console.log('[Payment Pending] ✅ Re-checked and ensured confirmation sections are hidden');
    }, 100);
  }
  
  // Show a message that the page will auto-refresh
  console.log('[Payment Pending] Showing payment pending message. Page will check payment status automatically.');
  
  // Only start polling if we have an orderId
  if (!orderId) {
    console.warn('[Payment Pending] No orderId provided, cannot poll for payment status');
    return;
  }
  
  // Poll for payment confirmation every 5 seconds (up to 2 minutes)
  let pollCount = 0;
  const maxPolls = 24; // 24 * 5 seconds = 2 minutes
  
  const pollInterval = setInterval(async () => {
    pollCount++;
    console.log(`[Payment Pending] Checking payment status (attempt ${pollCount}/${maxPolls})...`);
    
    try {
      const updatedOrder = await orderAPI.getOrder(orderId);
      const isPaymentConfirmed = updatedOrder.leftToPay?.amount === 0 || updatedOrder.leftToPay === 0;
      const isOrderPaid = updatedOrder.orderStatus?.name === 'Betalet' || updatedOrder.orderStatus?.id === 2;
      
      if (isPaymentConfirmed || isOrderPaid) {
        console.log('[Payment Pending] ✅ Payment confirmed! Reloading page to show success...');
        clearInterval(pollInterval);
        const storedOrder = readStoredCheckoutOrder();
        trackConfirmedPurchase({ order: updatedOrder, orderId, storedOrder });
        // Reload the page to show the success message
        window.location.reload();
      } else if (pollCount >= maxPolls) {
        console.warn('[Payment Pending] ⚠️ Payment still not confirmed after 2 minutes. Stopping auto-poll.');
        clearInterval(pollInterval);
        if (successMessage) {
          const displayOrderId = orderId || updatedOrder?.number || updatedOrder?.id || 'N/A';
          successMessage.textContent = t('confirmation.pending.stillProcessing') + displayOrderId;
        }
      }
    } catch (error) {
      console.error('[Payment Pending] Error checking payment status:', error);
      if (pollCount >= maxPolls) {
        clearInterval(pollInterval);
      }
    }
  }, 5000); // Check every 5 seconds
}

/**
 * Determine product type from order data
 * Returns: 'membership', '15daypass', or 'punch-card'
 */
function determineProductTypeFromOrder() {
  // Priority 1: Check test mode product type (if in test mode)
  if (state.testMode && state.testProductType) {
    console.log('[Product Type] Using test mode product type:', state.testProductType);
    return state.testProductType;
  }
  
  // Priority 2: Check full order data first (from API)
  if (state.fullOrder) {
    // ✅ Check subscription items FIRST (subscriptions take priority)
    if (state.fullOrder.subscriptionItems && state.fullOrder.subscriptionItems.length > 0) {
      const subscriptionItem = state.fullOrder.subscriptionItems[0];
      const product = subscriptionItem?.product;
      
      // Check product labels to determine if it's a 15-Day Trial Pass
      if (has15DayPassLabel(product)) {
        console.log('[Product Type] Detected 15daypass from productLabels');
        return '15daypass';
      }
      
      // Check product name for 15-Day Trial Pass
      if (product?.name) {
        const nameLower = product.name.toLowerCase();
        if (
          nameLower.includes('15 day') ||
          nameLower.includes('15-day') ||
          nameLower.includes('15 dage') ||
          nameLower.includes('15 dages') ||
          nameLower.includes('15dag')
        ) {
          console.log('[Product Type] Detected 15daypass from product name');
          return '15daypass';
        }
      }
      
      // Default to membership if it's a subscription item
      console.log('[Product Type] Detected membership from subscriptionItems');
      return 'membership';
    }
    
    // ✅ Only check value cards if NO subscription items exist
    if (state.fullOrder.valueCardItems && state.fullOrder.valueCardItems.length > 0) {
      console.log('[Product Type] Detected punch-card from valueCardItems');
      return 'punch-card';
    }
  }
  
  // Priority 3: Fall back to state.selectedProductType
  if (state.selectedProductType === 'punch-card') {
    console.log('[Product Type] Detected punch-card from selectedProductType');
    return 'punch-card';
  }
  
  // Priority 3b: Use stored order summary product type if available
  if (state.order?.productType) {
    const summaryType = String(state.order.productType).toLowerCase();
    if (summaryType === '15daypass' || summaryType === 'punch-card' || summaryType === 'membership') {
      console.log('[Product Type] Using product type from state.order.productType:', summaryType);
      return summaryType;
    }
  }
  
  // Priority 4: Check membershipPlanId format
  if (state.membershipPlanId) {
    if (String(state.membershipPlanId).startsWith('15daypass-')) {
      console.log('[Product Type] Detected 15daypass from membershipPlanId');
      return '15daypass';
    }
    if (String(state.membershipPlanId).startsWith('punch-')) {
      console.log('[Product Type] Detected punch-card from membershipPlanId');
      return 'punch-card';
    }
    // Default to membership for campaign, membership, etc.
    console.log('[Product Type] Detected membership from membershipPlanId');
    return 'membership';
  }
  
  // Priority 5: Check cart items
  if (state.cartItems && state.cartItems.length > 0) {
    const firstItem = state.cartItems[0];
    if (firstItem.type === 'value-card' || firstItem.type === 'punch-card') {
      console.log('[Product Type] Detected punch-card from cartItems');
      return 'punch-card';
    }
    if (firstItem.name && (firstItem.name.toLowerCase().includes('15 day') || firstItem.name.toLowerCase().includes('15 dage'))) {
      console.log('[Product Type] Detected 15daypass from cartItems');
      return '15daypass';
    }
  }
  
  // Priority 6: Check order summary naming signals
  const summaryMembershipType = String(state.order?.membershipType || '').toLowerCase();
  if (summaryMembershipType.includes('15 day') || summaryMembershipType.includes('15-day') || summaryMembershipType.includes('15 dage') || summaryMembershipType.includes('15 dages')) {
    console.log('[Product Type] Detected 15daypass from state.order membershipType');
    return '15daypass';
  }
  
  // Default to membership
  console.log('[Product Type] Defaulting to membership');
  return 'membership';
}

function renderConfirmationView() {
  // CRITICAL: Don't render success page if payment failed or is pending (unless in test mode)
  if (!state.testMode && state.paymentFailed === true) {
    console.warn('[Confirmation] Payment failed - not rendering success page');
    return;
  }
  
  if (!state.testMode && state.paymentPending === true) {
    console.warn('[Confirmation] Payment pending - not rendering success page');
    return;
  }
  
  if (!state.order) {
    console.warn('[Confirmation] No order data available to render');
    return;
  }
  
  // CRITICAL: Only render if payment is confirmed OR we're in test mode
  if (!state.testMode && state.paymentConfirmed !== true) {
    console.warn('[Confirmation] Payment not confirmed - not rendering success page');
    return;
  }
  
  console.log('[Confirmation] ✅ Rendering success page - payment confirmed' + (state.testMode ? ' (test mode)' : ''));

  // CRITICAL: Show confirmation sections (order details, membership details, next steps) when payment succeeds
  const step5Panel = document.getElementById('step-5');
  if (step5Panel) {
    // Remove payment failed/pending attributes to allow CSS to show sections
    step5Panel.removeAttribute('data-payment-failed');
    step5Panel.removeAttribute('data-payment-pending');
    
    // Clean up payment-failed-active class and inline styles from step-content
    // Add success-page-active class for reliable CSS targeting
    const stepContent = document.querySelector('.step-content');
    if (stepContent) {
      stepContent.classList.remove('payment-failed-active');
      stepContent.classList.add('success-page-active'); // Add class for reliable CSS targeting
      stepContent.style.flex = '';
      stepContent.style.minHeight = '';
      stepContent.style.height = '';
    }
    
    const confirmationLayout = step5Panel.querySelector('.confirmation-layout');
    const confirmationLeft = step5Panel.querySelector('.confirmation-left');
    const confirmationRight = step5Panel.querySelector('.confirmation-right');
    
    // Remove data attributes and show sections
    const showConfirmationSections = () => {
      if (confirmationLayout) {
        confirmationLayout.style.display = '';
        confirmationLayout.style.visibility = '';
        confirmationLayout.removeAttribute('data-payment-failed');
        confirmationLayout.removeAttribute('data-payment-pending');
      }
      if (confirmationLeft) {
        confirmationLeft.style.display = '';
        confirmationLeft.style.visibility = '';
        confirmationLeft.removeAttribute('data-payment-failed');
        confirmationLeft.removeAttribute('data-payment-pending');
      }
      if (confirmationRight) {
        confirmationRight.style.display = '';
        confirmationRight.style.visibility = '';
        confirmationRight.removeAttribute('data-payment-failed');
        confirmationRight.removeAttribute('data-payment-pending');
      }
    };
    
    showConfirmationSections();
    console.log('[Confirmation] ✅ Showing confirmation sections - payment confirmed');
  }

  // Determine product type
  const productType = determineProductTypeFromOrder();
  console.log('[Confirmation] Product type determined:', productType);
  console.log('[Confirmation] State for debugging:', {
    selectedProductType: state.selectedProductType,
    membershipPlanId: state.membershipPlanId,
    hasFullOrder: !!state.fullOrder,
    hasValueCardItems: !!(state.fullOrder?.valueCardItems?.length),
    hasSubscriptionItems: !!(state.fullOrder?.subscriptionItems?.length),
    productLabels: state.fullOrder?.subscriptionItems?.[0]?.product?.productLabels
  });

  // Update success message based on product type (use translation keys so language switch works)
  const isFirstClimbFlow = isFirstClimbRoute();
  const successMessage = document.querySelector('.success-message');
  if (successMessage) {
    const messageKey = isFirstClimbFlow ? 'confirmation.message.firstclimb'
      : productType === 'membership' ? 'confirmation.message.membership'
      : productType === '15daypass' ? 'confirmation.message.15daypass'
      : productType === 'punch-card' ? 'confirmation.message.punchcard'
      : 'confirmation.message.generic';
    successMessage.setAttribute('data-i18n-key', messageKey);
    successMessage.textContent = t(messageKey);
  }

  // Update "What happens next?" steps based on product type (use translation keys)
  const nextStep1 = document.getElementById('nextStep1');
  const nextStep2 = document.getElementById('nextStep2');
  const nextStep3 = document.getElementById('nextStep3');
  const freetrialActivationChangeCta = document.getElementById('freetrialActivationChangeCta');
  const freetrialActivationChangeLink = document.getElementById('freetrialActivationChangeLink');
  const isFreeTrialFlow = state.landingRouteConfig?.componentName === 'LandingFreeTrial';

  if (freetrialActivationChangeCta && freetrialActivationChangeLink) {
    const supportEmail = 'medlem@boulders.dm';
    const supportSubject = encodeURIComponent('Change of activation date - Free Trial');
    const supportBody = encodeURIComponent([
      'Hi Boulders team,',
      '',
      'I would like to request a change of activation date for my free trial.',
      '',
      `Order number: ${state.order?.number || state.orderId || '[insert order number]'}`,
      `Current activation date: ${state.subscriptionStartDate || '[insert activation date]'}`,
      'Requested new activation date: [insert new date]',
      '',
      'Name: [insert your full name]',
      'Phone: [insert your phone number]',
      '',
      'Thanks in advance!'
    ].join('\n'));
    freetrialActivationChangeLink.href = `mailto:${supportEmail}?subject=${supportSubject}&body=${supportBody}`;
    freetrialActivationChangeLink.setAttribute('data-i18n-key', 'confirmation.freetrial.changeActivationCta');
    freetrialActivationChangeLink.textContent = t('confirmation.freetrial.changeActivationCta');
    freetrialActivationChangeCta.style.display = isFreeTrialFlow ? '' : 'none';
  }
  
  if (nextStep1) {
    nextStep1.setAttribute('data-i18n-key', 'confirmation.nextStep1');
    nextStep1.textContent = t('confirmation.nextStep1');
  }

  // Render the invite-friends section (membership only; UA-aware buttons + copy state reset)
  renderInviteFriends(productType);
  
  if (nextStep2 && nextStep3) {
    const step2Key = isFirstClimbFlow ? 'confirmation.nextStep2.firstclimb'
      : productType === 'membership' ? 'confirmation.nextStep2.membership'
      : productType === '15daypass' ? (isFreeTrialFlow ? 'confirmation.nextStep2.freetrial' : 'confirmation.nextStep2.15daypass')
      : productType === 'punch-card' ? 'confirmation.nextStep2.punchcard'
      : 'confirmation.nextStep2.membership';
    const step3Key = isFirstClimbFlow ? 'confirmation.nextStep3.firstclimb'
      : productType === 'membership' ? 'confirmation.nextStep3.membership'
      : productType === '15daypass' ? (isFreeTrialFlow ? 'confirmation.nextStep3.freetrial.today' : 'confirmation.nextStep3.15daypass')
      : productType === 'punch-card' ? 'confirmation.nextStep3.punchcard'
      : 'confirmation.nextStep3.membership';
    nextStep2.setAttribute('data-i18n-key', step2Key);
    nextStep2.textContent = t(step2Key);
    nextStep3.setAttribute('data-i18n-key', step3Key);
    nextStep3.textContent = t(step3Key);
  }

  // Hide all sections first - use !important to override any CSS
  const membershipSection = document.getElementById('confirmationMembershipSection');
  const dayPassSection = document.getElementById('confirmation15DayPassSection');
  const punchCardSection = document.getElementById('confirmationPunchCardSection');
  
  if (membershipSection) {
    membershipSection.style.display = 'none';
    membershipSection.style.setProperty('display', 'none', 'important');
  }
  if (dayPassSection) {
    dayPassSection.style.display = 'none';
    dayPassSection.style.setProperty('display', 'none', 'important');
  }
  if (punchCardSection) {
    punchCardSection.style.display = 'none';
    punchCardSection.style.setProperty('display', 'none', 'important');
  }

  // Show appropriate section based on product type - only ONE section should be visible
  if (productType === 'membership' && membershipSection) {
    console.log('[Confirmation] Showing membership section');
    membershipSection.style.display = 'block';
    membershipSection.style.setProperty('display', 'block', 'important');
  } else if (productType === '15daypass' && dayPassSection) {
    console.log('[Confirmation] Showing 15-Day Trial Pass section');
    dayPassSection.style.display = 'block';
    dayPassSection.style.setProperty('display', 'block', 'important');
  } else if (productType === 'punch-card' && punchCardSection) {
    // /99kr is technically a value-card/punch-card under the hood, but the
    // "Punch Card Details" panel (name / card type / quantity / valid until)
    // doesn't carry any user-meaningful info for a single-use day ticket —
    // the "What happens next?" block already covers everything. Hide it.
    if (isFirstClimbFlow) {
      console.log('[Confirmation] firstclimb flow — hiding punch card details section');
    } else {
      console.log('[Confirmation] Showing punch card section');
      punchCardSection.style.display = 'block';
      punchCardSection.style.setProperty('display', 'block', 'important');
    }
  } else {
    console.warn('[Confirmation] Unknown product type or section not found:', productType);
  }
  
  const { orderNumber, orderDate, orderTotal, memberName, membershipType, primaryGym, membershipPrice } = DOM.confirmationFields;

  // Prefer API order data, but fall back to summary order so success page is never blank.
  const apiOrder = state.fullOrder || state.order || null;
  
  // Order number - from API only
  if (orderNumber) {
    const number = apiOrder?.number || apiOrder?.id || '—';
    orderNumber.textContent = number;
  }
  
  // Order date - from API only
  if (orderDate) {
    if (apiOrder?.createdAt || apiOrder?.created || apiOrder?.date) {
      const rawDate = apiOrder.createdAt || apiOrder.created || apiOrder.date;
      const date = new Date(rawDate);
      orderDate.textContent = new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }).format(date);
    } else {
      orderDate.textContent = '—';
    }
  }
  
  // Order total - from API only
  if (orderTotal) {
    if (apiOrder?.price?.amount !== undefined && apiOrder?.price?.amount !== null) {
      const amount = apiOrder.price.amount;
      const total = typeof amount === 'object' ? amount.amount / 100 : amount / 100;
      orderTotal.textContent = formatCurrencyHalfKrone(total);
    } else if (apiOrder?.total !== undefined && apiOrder?.total !== null) {
      const total = typeof apiOrder.total === 'object' ? apiOrder.total.amount / 100 : apiOrder.total / 100;
      orderTotal.textContent = formatCurrencyHalfKrone(total);
    } else {
      orderTotal.textContent = '—';
    }
  }
  
  // Member name - from API only
  if (memberName) {
    let name = '—';
    if (apiOrder?.customer) {
      const customer = apiOrder.customer;
      name = customer.fullName || 
            (customer.firstName && customer.lastName ? `${customer.firstName} ${customer.lastName}` : null) ||
            customer.name ||
            '—';
    } else if (apiOrder?.memberName) {
      name = apiOrder.memberName;
    }
    memberName.textContent = name;
    // Also update in other sections
    const dayPassMemberName = document.querySelector('#confirmation15DayPassSection [data-summary-field="member-name"]');
    const punchCardMemberName = document.querySelector('#confirmationPunchCardSection [data-summary-field="member-name"]');
    if (dayPassMemberName) dayPassMemberName.textContent = name;
    if (punchCardMemberName) punchCardMemberName.textContent = name;
  }
  
  if (membershipType) {
    const type = apiOrder?.subscriptionItems?.[0]?.product?.name || apiOrder?.membershipType || '—';
    membershipType.textContent = type;
  }
  
  if (primaryGym) {
    let gym = '—';
    if (apiOrder?.customer?.primaryGym) {
      gym = resolveGymLabel(apiOrder.customer.primaryGym);
    } else if (apiOrder?.businessUnit?.name) {
      gym = apiOrder.businessUnit.name;
    } else if (apiOrder?.primaryGym) {
      gym = apiOrder.primaryGym;
    }
    primaryGym.textContent = gym;
    // Also update in other sections
    const dayPassGym = document.querySelector('#confirmation15DayPassSection [data-summary-field="primary-gym"]');
    if (dayPassGym) dayPassGym.textContent = gym;
  }
  
  if (membershipPrice) {
    let price = null;
    if (apiOrder?.subscriptionItems?.[0]?.payRecurring?.price?.amount) {
      const amount = apiOrder.subscriptionItems[0].payRecurring.price.amount;
      price = typeof amount === 'object' ? amount.amount / 100 : amount / 100;
    } else if (apiOrder?.subscriptionItems?.[0]?.price?.amount) {
      const amount = apiOrder.subscriptionItems[0].price.amount;
      price = typeof amount === 'object' ? amount.amount / 100 : amount / 100;
    }
    
    if (price !== null) {
      const formatted = formatCurrencyHalfKrone(roundToHalfKrone(price));
      membershipPrice.textContent = productType === '15daypass' ? formatted : `${formatted}/month`;
    } else {
      membershipPrice.textContent = '—';
    }
  }
  
  // 15-Day Trial Pass specific fields - from API only
  if (productType === '15daypass') {
    const passStartDate = document.querySelector('#confirmation15DayPassSection [data-summary-field="pass-start-date"]');
    const passEndDate = document.querySelector('#confirmation15DayPassSection [data-summary-field="pass-end-date"]');
    
    const subscriptionItem = apiOrder?.subscriptionItems?.[0];
    const rawStart =
      subscriptionItem?.subscription?.startDate ||
      subscriptionItem?.initialPaymentPeriod?.start ||
      (state.subscriptionStartDate ? `${state.subscriptionStartDate}T12:00:00` : null);
    const rawEnd =
      subscriptionItem?.subscription?.endDate ||
      subscriptionItem?.initialPaymentPeriod?.end ||
      null;

    const parsedStart = rawStart ? new Date(rawStart) : null;
    let parsedEnd = rawEnd ? new Date(rawEnd) : null;
    if (parsedStart && !parsedEnd) {
      // Fallback for 15-day pass: end = start + 14 days (inclusive 15-day period)
      const computedEnd = new Date(parsedStart);
      computedEnd.setDate(computedEnd.getDate() + 14);
      parsedEnd = computedEnd;
    }

    const langCode = (state.language || DEFAULT_LANGUAGE).split('-')[0];
    const locale = langCode === 'en' ? 'en-US' : 'da-DK';
    const formatLongDate = (date) => new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(date);

    if (passStartDate) {
      passStartDate.textContent = (parsedStart && !isNaN(parsedStart.getTime()))
        ? formatLongDate(parsedStart)
        : '—';
    }

    if (passEndDate) {
      passEndDate.textContent = (parsedEnd && !isNaN(parsedEnd.getTime()))
        ? formatLongDate(parsedEnd)
        : '—';
    }

    // Update "what happens next" step 2 to reflect future activation
    if (nextStep2 && parsedStart && !isNaN(parsedStart.getTime())) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const startDay = new Date(parsedStart);
      startDay.setHours(0, 0, 0, 0);

      if (startDay.getTime() > today.getTime()) {
        const dateIso = `${startDay.getFullYear()}-${String(startDay.getMonth() + 1).padStart(2, '0')}-${String(startDay.getDate()).padStart(2, '0')}`;
        const key = state.landingRouteConfig?.componentName === 'LandingFreeTrial'
          ? 'confirmation.nextStep2.freetrial.future'
          : 'confirmation.nextStep2.15daypass.future';
        nextStep2.setAttribute('data-i18n-key', key);
        nextStep2.setAttribute('data-i18n-date-iso', dateIso);
        // Render immediately (also allows updatePageTranslations() to re-render on language switch)
        nextStep2.textContent = t(key).replaceAll('{date}', formatLongDate(startDay));

        if (state.landingRouteConfig?.componentName === 'LandingFreeTrial') {
          nextStep3.setAttribute('data-i18n-key', 'confirmation.nextStep3.freetrial.future');
          nextStep3.textContent = t('confirmation.nextStep3.freetrial.future');
        }
      } else {
        // Pass is active now (or today) - restore normal i18n-managed message
        nextStep2.removeAttribute('data-i18n-date-iso');
        const activeNowKey = state.landingRouteConfig?.componentName === 'LandingFreeTrial'
          ? 'confirmation.nextStep2.freetrial'
          : 'confirmation.nextStep2.15daypass';
        nextStep2.setAttribute('data-i18n-key', activeNowKey);
        nextStep2.textContent = t(activeNowKey);

        if (state.landingRouteConfig?.componentName === 'LandingFreeTrial') {
          nextStep3.setAttribute('data-i18n-key', 'confirmation.nextStep3.freetrial.today');
          nextStep3.textContent = t('confirmation.nextStep3.freetrial.today');
        }
      }
    }
  }
  
// Helper function to create purchase item element if template not available
function createPurchaseItemElement() {
  const item = document.createElement('div');
  item.className = 'purchase-item';
  item.setAttribute('data-confirmation-item', '');
  item.innerHTML = sanitizeHTML(`
    <span class="item-name" data-element="name"></span>
    <span class="item-price" data-element="price"></span>
  `);
  return item;
}

  // Punch Card specific fields - from API only
  // Show aggregate info for all punch cards if multiple, or single card details
  if (productType === 'punch-card') {
    const punchCardType = document.querySelector('#confirmationPunchCardSection [data-summary-field="punch-card-type"]');
    const punchCardQuantity = document.querySelector('#confirmationPunchCardSection [data-summary-field="punch-card-quantity"]');
    const punchCardExpiry = document.querySelector('#confirmationPunchCardSection [data-summary-field="punch-card-expiry"]');
    
    // Use API valueCardItems data - aggregate if multiple cards
    const valueCardItems = apiOrder?.valueCardItems || [];
    const totalQuantity = valueCardItems.reduce((sum, item) => sum + (item.quantity || 1), 0);
    
    if (punchCardType) {
      // Show product name from first card, or indicate multiple types if different
      // Remove card number from name (e.g., "Klippekort: 10 Klip (400117054549)" -> "Klippekort: 10 Klip")
      if (valueCardItems.length > 0) {
        let firstCardName = valueCardItems[0]?.product?.name || 'Klippekort';
        // Remove card number in parentheses (e.g., " (400117054549)")
        firstCardName = firstCardName.replace(/\s*\(\d+\)\s*$/, '');
        
        if (valueCardItems.length === 1) {
          punchCardType.textContent = firstCardName;
        } else {
          // Multiple cards - show first card name with count
          punchCardType.textContent = `${firstCardName} (${valueCardItems.length} kort)`;
        }
      } else {
        punchCardType.textContent = '—';
      }
    }
    
    if (punchCardQuantity) {
      punchCardQuantity.textContent = totalQuantity > 0 ? totalQuantity.toString() : '—';
    }
    
    if (punchCardExpiry) {
      // Get expiry from first card (all should have same expiry)
      // Per OpenAPI: ValueCardItemOut has validUntil field, and ValueCardOut also has validUntil
      let expiryDate = null;
      const firstCard = valueCardItems[0];
      
      // Priority 1: Check valueCardItem.validUntil (direct field on the item)
      if (firstCard?.validUntil) {
        expiryDate = new Date(firstCard.validUntil);
      }
      // Priority 2: Check valueCard.validUntil (nested in valueCard object)
      else if (firstCard?.valueCard?.validUntil) {
        expiryDate = new Date(firstCard.valueCard.validUntil);
      }
      
      // Display expiry date from API, or show '—' if not available
      if (expiryDate && !isNaN(expiryDate.getTime())) {
        punchCardExpiry.textContent = new Intl.DateTimeFormat('da-DK', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        }).format(expiryDate);
      } else {
        punchCardExpiry.textContent = '—';
      }
    }
  }

  // Populate product details from full order (subscriptionItems/valueCardItems) or fallback to state.order.items
  if (DOM.confirmationItems) {
    DOM.confirmationItems.innerHTML = '';
    let hasItems = false;
    
    console.log('[Confirmation] Populating product details:', {
      hasTemplate: !!templates.confirmationItem,
      hasFullOrder: !!state.fullOrder,
      hasSubscriptionItems: !!(state.fullOrder?.subscriptionItems?.length),
      hasValueCardItems: !!(state.fullOrder?.valueCardItems?.length),
      hasOrderItems: !!(state.order?.items?.length),
      hasCartItems: !!(state.cartItems?.length),
      selectedProductId: state.selectedProductId,
      membershipPlanId: state.membershipPlanId
    });
    
    // Priority 1: Use fullOrder data (from API) for detailed product information
    if (state.fullOrder) {
      // Add subscription items (memberships, 15-day passes)
      if (state.fullOrder.subscriptionItems && state.fullOrder.subscriptionItems.length > 0) {
        state.fullOrder.subscriptionItems.forEach(item => {
          const node = templates.confirmationItem 
            ? templates.confirmationItem.content.firstElementChild.cloneNode(true)
            : createPurchaseItemElement();
          const nameEl = node.querySelector('[data-element="name"]') || node.querySelector('.item-name');
          const priceEl = node.querySelector('[data-element="price"]') || node.querySelector('.item-price');
          
          if (nameEl) {
            const productName = item.product?.name || 'Medlemskab';
            nameEl.textContent = productName;
          }
          
          if (priceEl) {
            // Per SubscriptionItemOut: price.amount is total price for all quantity
            const itemTotal = item.price?.amount ? (typeof item.price.amount === 'object' ? item.price.amount.amount / 100 : item.price.amount / 100) : 0;
            priceEl.textContent = formatCurrencyHalfKrone(roundToHalfKrone(itemTotal));
          }
          
          DOM.confirmationItems.appendChild(node);
          hasItems = true;
        });
      }
      
      // Add value card items (punch cards)
      if (state.fullOrder.valueCardItems && state.fullOrder.valueCardItems.length > 0) {
        state.fullOrder.valueCardItems.forEach(item => {
          const node = templates.confirmationItem
            ? templates.confirmationItem.content.firstElementChild.cloneNode(true)
            : createPurchaseItemElement();
          const nameEl = node.querySelector('[data-element="name"]') || node.querySelector('.item-name');
          const priceEl = node.querySelector('[data-element="price"]') || node.querySelector('.item-price');

          if (nameEl) {
            let productName = item.product?.name || 'Klippekort';
            // Remove card number from product name (e.g., "Klippekort: 10 Klip (400117054549)" -> "Klippekort: 10 Klip")
            productName = productName.replace(/\s*\(\d+\)\s*$/, '');
            nameEl.textContent = productName;
          }
          
          if (priceEl) {
            // Per ValueCardItemOut: price.amount is total price for all quantity
            const itemTotal = item.price?.amount ? (typeof item.price.amount === 'object' ? item.price.amount.amount / 100 : item.price.amount / 100) : 0;
            priceEl.textContent = formatCurrencyHalfKrone(roundToHalfKrone(itemTotal));
          }
          
          DOM.confirmationItems.appendChild(node);
          hasItems = true;
        });
      }
      
      // Add article items (boost products/add-ons)
      if (state.fullOrder.articleItems && state.fullOrder.articleItems.length > 0) {
        state.fullOrder.articleItems.forEach(item => {
          const node = templates.confirmationItem 
            ? templates.confirmationItem.content.firstElementChild.cloneNode(true)
            : createPurchaseItemElement();
          const nameEl = node.querySelector('[data-element="name"]') || node.querySelector('.item-name');
          const priceEl = node.querySelector('[data-element="price"]') || node.querySelector('.item-price');
          
          if (nameEl) {
            const productName = item.product?.name || item.name || 'Add-on';
            nameEl.textContent = productName;
          }
          
          if (priceEl) {
            // Per ProductItemOut: price.amount is total price
            const itemTotal = item.price?.amount ? (typeof item.price.amount === 'object' ? item.price.amount.amount / 100 : item.price.amount / 100) : 0;
            priceEl.textContent = formatCurrencyHalfKrone(roundToHalfKrone(itemTotal));
          }
          
          DOM.confirmationItems.appendChild(node);
          hasItems = true;
        });
      }
    }
    
    // Priority 2: Fallback to state.order.items if fullOrder not available or has no items
    if (!hasItems && state.order?.items && state.order.items.length > 0) {
      state.order.items.forEach((item) => {
        const node = templates.confirmationItem 
          ? templates.confirmationItem.content.firstElementChild.cloneNode(true)
          : createPurchaseItemElement();
        const nameEl = node.querySelector('[data-element="name"]') || node.querySelector('.item-name');
        const priceEl = node.querySelector('[data-element="price"]') || node.querySelector('.item-price');
        if (nameEl) nameEl.textContent = item.name;
        if (priceEl) priceEl.textContent = formatCurrencyHalfKrone(roundToHalfKrone(item.amount));
        DOM.confirmationItems.appendChild(node);
        hasItems = true;
      });
    }
    
    // Priority 3: Fallback to state.cartItems if order.items is empty
    if (!hasItems && state.cartItems && state.cartItems.length > 0) {
      state.cartItems.forEach((item) => {
        const node = templates.confirmationItem 
          ? templates.confirmationItem.content.firstElementChild.cloneNode(true)
          : createPurchaseItemElement();
        const nameEl = node.querySelector('[data-element="name"]') || node.querySelector('.item-name');
        const priceEl = node.querySelector('[data-element="price"]') || node.querySelector('.item-price');
        if (nameEl) {
          // Remove quantity suffix if present (e.g., " ×2")
          const itemName = item.name?.replace(/\s×\d+$/, '') || item.name || 'Item';
          nameEl.textContent = itemName;
        }
        if (priceEl) {
          const itemPrice = item.price || item.amount || 0;
          priceEl.textContent = formatCurrencyHalfKrone(roundToHalfKrone(itemPrice));
        }
        DOM.confirmationItems.appendChild(node);
        hasItems = true;
      });
    }
    
    // Priority 4: Build from selected product if nothing else is available
    if (!hasItems && (state.selectedProductId || state.membershipPlanId)) {
      const node = templates.confirmationItem 
        ? templates.confirmationItem.content.firstElementChild.cloneNode(true)
        : createPurchaseItemElement();
      const nameEl = node.querySelector('[data-element="name"]') || node.querySelector('.item-name');
      const priceEl = node.querySelector('[data-element="price"]') || node.querySelector('.item-price');
      
      if (nameEl) {
        // Try to find product name from available product lists
        const allSubscriptions = [...(state.subscriptions || []), ...(state.dayPassSubscriptions || []), ...(state.campaignSubscriptions || [])];
        const allValueCards = state.valueCards || [];
        const allProducts = [...allSubscriptions, ...allValueCards];
        
        const productId = state.selectedProductId || state.membershipPlanId;
        const numericId = String(productId).replace(/^(campaign|membership|15daypass|punch)-/, '');
        const productIdNum = parseInt(numericId, 10);
        
        const foundProduct = allProducts.find(p => 
          p.id === productId || 
          p.id === productIdNum ||
          String(p.id) === numericId
        );
        
        const productName = foundProduct?.name || 
                           (state.selectedProductType === 'punch-card' ? 'Klippekort' : 'Medlemskab');
        nameEl.textContent = productName;
      }
      
      if (priceEl) {
        const itemPrice = state.totals?.membershipMonthly || state.totals?.cartTotal || state.order?.total || 0;
        priceEl.textContent = formatCurrencyHalfKrone(roundToHalfKrone(itemPrice));
      }
      
      DOM.confirmationItems.appendChild(node);
      hasItems = true;
    }
    
    // Log if no items were found
    if (!hasItems) {
      console.warn('[Confirmation] No product details available to display:', {
        hasFullOrder: !!state.fullOrder,
        hasSubscriptionItems: !!(state.fullOrder?.subscriptionItems?.length),
        hasValueCardItems: !!(state.fullOrder?.valueCardItems?.length),
        hasOrderItems: !!(state.order?.items?.length),
        hasCartItems: !!(state.cartItems?.length),
        selectedProductId: state.selectedProductId,
        membershipPlanId: state.membershipPlanId
      });
    }
  }
}

async function showDetailedReceipt() {
  if (!state.fullOrder && !state.order) {
    console.warn('[Receipt] No order data available');
    return;
  }
  
  const modal = document.getElementById('detailedReceiptModal');
  if (!modal) return;
  
  const order = state.fullOrder || state.order;
  
  // Fetch business unit details for seller information
  let businessUnit = null;
  if (order.businessUnit?.id || state.selectedBusinessUnit) {
    const businessUnitId = order.businessUnit?.id || state.selectedBusinessUnit;
    try {
      // Try to get from cached gymsWithDistances first (already loaded)
      if (gymsWithDistances && Array.isArray(gymsWithDistances)) {
        businessUnit = gymsWithDistances.find(bu => bu.id === businessUnitId || bu.id === parseInt(businessUnitId, 10));
      }
      
      // If not found, try state.gyms (from loadGymsFromAPI)
      if (!businessUnit && state.gyms && Array.isArray(state.gyms)) {
        businessUnit = state.gyms.find(bu => bu.id === businessUnitId || bu.id === parseInt(businessUnitId, 10));
      }
      
      // If still not found, fetch from API
      if (!businessUnit) {
        const businessUnitsAPI = new BusinessUnitsAPI();
        const allBusinessUnits = await businessUnitsAPI.getBusinessUnits();
        businessUnit = allBusinessUnits.find(bu => bu.id === businessUnitId || bu.id === parseInt(businessUnitId, 10));
      }
    } catch (error) {
      console.warn('[Receipt] Could not fetch business unit details:', error);
    }
  }
  
  // Populate receipt header
  const receiptOrderNumber = document.getElementById('receiptOrderNumber');
  const receiptDate = document.getElementById('receiptDate');
  if (receiptOrderNumber) {
    receiptOrderNumber.textContent = order.number || order.id || '—';
  }
  if (receiptDate) {
    const orderDate = order.createdAt ? new Date(order.createdAt) : (order.created ? new Date(order.created) : (order.date || new Date()));
    receiptDate.textContent = new Intl.DateTimeFormat('da-DK', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(orderDate);
  }
  
  // Calculate totals - use API data structure per OrderOut schema
  const totalAmount = order.price?.amount || order.total || order.totalAmount || 0;
  const totalDKK = typeof totalAmount === 'object' ? (totalAmount.amount || totalAmount) / 100 : totalAmount / 100;
  
  // Use vatSums from API (per OrderOut.vatSums) instead of calculating
  let vatAmount = 0;
  if (order.vatSums && Array.isArray(order.vatSums) && order.vatSums.length > 0) {
    // Sum all VAT amounts from vatSums array
    vatAmount = order.vatSums.reduce((sum, vat) => {
      const vatValue = vat.amount ? (typeof vat.amount === 'object' ? vat.amount.amount / 100 : vat.amount / 100) : 0;
      return sum + vatValue;
    }, 0);
  } else {
    // Fallback: calculate 25% VAT if vatSums not available
    vatAmount = totalDKK * 0.25;
  }
  
  const subtotal = totalDKK - vatAmount;
  const discount = extractCouponDiscountKr(order.couponDiscount);
  
  // Populate order overview
  const receiptSubtotal = document.getElementById('receiptSubtotal');
  const receiptDiscount = document.getElementById('receiptDiscount');
  const receiptTotal = document.getElementById('receiptTotal');
  const receiptVat = document.getElementById('receiptVat');
  
  if (receiptSubtotal) receiptSubtotal.textContent = formatCurrencyHalfKrone(subtotal);
  if (receiptDiscount) receiptDiscount.textContent = discount > 0 ? `-${formatCurrencyHalfKrone(discount)}` : '0,00 kr';
  if (receiptTotal) receiptTotal.textContent = formatCurrencyHalfKrone(totalDKK);
  if (receiptVat) receiptVat.textContent = formatCurrencyHalfKrone(vatAmount);
  
  // Populate payment details
  const receiptPaymentMethod = document.getElementById('receiptPaymentMethod');
  const receiptAmountPaid = document.getElementById('receiptAmountPaid');
  const receiptOrderNumberPayment = document.getElementById('receiptOrderNumberPayment');

  if (receiptPaymentMethod) {
    const paymentMethod = order.paymentMethod || order.payment?.method || 'Kortbetaling';
    receiptPaymentMethod.textContent = paymentMethod;
  }
  if (receiptAmountPaid) receiptAmountPaid.textContent = formatCurrencyHalfKrone(totalDKK);
  if (receiptOrderNumberPayment) {
    receiptOrderNumberPayment.textContent = order.number || order.id || '—';
  }
  
  // Populate customer information - fetch full customer details if we only have a reference
  const receiptCustomerName = document.getElementById('receiptCustomerName');
  const receiptCustomerAddress = document.getElementById('receiptCustomerAddress');
  const receiptCustomerPhone = document.getElementById('receiptCustomerPhone');
  const receiptCustomerEmail = document.getElementById('receiptCustomerEmail');
  
  // Get customer - prefer full customer object, fallback to order.customer reference
  let customer = order.customer || state.customer || state.authenticatedCustomer;
  
  // If we only have customer ID (reference), fetch full customer details
  if (customer?.id && !customer.email && !customer.shippingAddress && !customer.billingAddress && !customer.mobilePhone) {
    try {
      const fullCustomer = await authAPI.getCustomer(customer.id);
      customer = fullCustomer;
      console.log('[Receipt] Fetched full customer details for receipt');
    } catch (error) {
      console.warn('[Receipt] Could not fetch full customer details:', error);
      // Continue with reference customer
    }
  }
  
  if (receiptCustomerName) {
    const name = customer?.fullName || 
                (customer?.firstName && customer?.lastName ? `${customer.firstName} ${customer.lastName}` : null) || 
                state.order?.memberName || 
                '—';
    receiptCustomerName.textContent = name;
  }
  
  if (receiptCustomerAddress) {
    // Use shippingAddress or billingAddress per CustomerOut schema (AddressOut)
    const addressObj = customer?.shippingAddress || customer?.billingAddress;
    if (addressObj) {
      const street = addressObj.street || '';
      const postalCode = addressObj.postalCode || '';
      const city = addressObj.city || '';
      const country = addressObj.country?.name || '';
      const address = `${street} ${postalCode}${city ? ', ' + city : ''}${country ? ' ' + country : ''}`.trim();
      receiptCustomerAddress.textContent = address || '—';
    } else {
      // Fallback for old structure
      const address = customer?.address ? 
        `${customer.address.street || ''} ${customer.address.streetNumber || ''} ${customer.address.postalCode || ''}, ${customer.address.city || ''}`.trim() :
        (customer?.addressLine1 ? `${customer.addressLine1} ${customer.postalCode || ''}, ${customer.city || ''}`.trim() : '—');
      receiptCustomerAddress.textContent = address || '—';
    }
  }
  
  if (receiptCustomerPhone) {
    // Use mobilePhone per CustomerOut schema (PhoneNumberOut)
    if (customer?.mobilePhone) {
      const countryCode = customer.mobilePhone.countryCode ? `+${customer.mobilePhone.countryCode}` : '';
      const number = customer.mobilePhone.number || '';
      receiptCustomerPhone.textContent = countryCode && number ? `${countryCode} ${number}` : number || '—';
    } else {
      // Fallback
      receiptCustomerPhone.textContent = customer?.phone || customer?.phoneNumber || '—';
    }
  }
  
  if (receiptCustomerEmail) {
    receiptCustomerEmail.textContent = customer?.email || '—';
  }
  
  // Populate seller information from business unit
  const receiptSellerName = document.getElementById('receiptSellerName');
  const receiptSellerAddress = document.getElementById('receiptSellerAddress');
  const receiptSellerCVR = document.getElementById('receiptSellerCVR');
  const receiptSellerPhone = document.getElementById('receiptSellerPhone');
  const receiptSellerEmail = document.getElementById('receiptSellerEmail');
  
  if (businessUnit) {
    // Per BusinessUnitOut schema: companyNameForInvoice, address, company
    if (receiptSellerName) {
      receiptSellerName.textContent = businessUnit.companyNameForInvoice || businessUnit.company?.name || businessUnit.name || 'Boulders ApS';
    }
    
    if (receiptSellerAddress && businessUnit.address) {
      // Per AddressOut schema: street, city, postalCode, country
      const street = businessUnit.address.street || '';
      const postalCode = businessUnit.address.postalCode || '';
      const city = businessUnit.address.city || '';
      const country = businessUnit.address.country?.name || '';
      const address = `${street} ${postalCode}${city ? ', ' + city : ''}${country ? ' ' + country : ''}`.trim();
      receiptSellerAddress.textContent = address || '—';
    } else if (receiptSellerAddress) {
      receiptSellerAddress.textContent = '—';
    }
    
    // CVR, Phone, Email might not be in BusinessUnitOut schema - check if available
    if (receiptSellerCVR) {
      // CVR might be in company object or business unit settings - not in schema, so use fallback
      receiptSellerCVR.textContent = businessUnit.cvr || businessUnit.company?.cvr || '32777651'; // Fallback to known value
    }
    
    if (receiptSellerPhone) {
      receiptSellerPhone.textContent = businessUnit.phone || businessUnit.contactPhone || '+45 72100019'; // Fallback
    }
    
    if (receiptSellerEmail) {
      receiptSellerEmail.textContent = businessUnit.email || businessUnit.contactEmail || 'medlem@boulders.dk'; // Fallback
    }
  } else {
    // Fallback to hardcoded values if business unit not available
    if (receiptSellerName) receiptSellerName.textContent = 'Boulders ApS';
    if (receiptSellerAddress) receiptSellerAddress.textContent = 'Graham Bells Vej 18A, 8200 Aarhus DK';
    if (receiptSellerCVR) receiptSellerCVR.textContent = '32777651';
    if (receiptSellerPhone) receiptSellerPhone.textContent = '+45 72100019';
    if (receiptSellerEmail) receiptSellerEmail.textContent = 'medlem@boulders.dk';
  }
  
  // Populate purchased items
  const receiptItems = document.getElementById('receiptItems');
  if (receiptItems) {
    receiptItems.innerHTML = '';
    
    // Add header row
    const headerRow = document.createElement('div');
    headerRow.className = 'receipt-item receipt-item-header';
    headerRow.innerHTML = sanitizeHTML(`
      <div>Navn</div>
      <div>Antal</div>
      <div>TOTALT</div>
    `);
    receiptItems.appendChild(headerRow);
    
    // Add items from order - per OrderOut schema
    if (order.valueCardItems && order.valueCardItems.length > 0) {
      // Per ValueCardItemOut schema: price is total for all quantity, quantity is amount of cards
      order.valueCardItems.forEach(item => {
        const itemRow = document.createElement('div');
        itemRow.className = 'receipt-item';
        let itemName = item.product?.name || 'Klippekort';
        // Remove card number from product name (e.g., "Klippekort: 10 Klip (400117054549)" -> "Klippekort: 10 Klip")
        itemName = itemName.replace(/\s*\(\d+\)\s*$/, '');
        const itemQuantity = item.quantity || 1;
        // Per ValueCardItemOut: price.amount is total price for all quantity (not per unit)
        const itemTotal = item.price?.amount ? (typeof item.price.amount === 'object' ? item.price.amount.amount / 100 : item.price.amount / 100) : 0;
        
        itemRow.innerHTML = sanitizeHTML(`
          <div>${itemName}</div>
          <div>${itemQuantity}</div>
          <div>${formatCurrencyHalfKrone(itemTotal)}</div>
        `);
        receiptItems.appendChild(itemRow);
      });
    } else if (order.subscriptionItems && order.subscriptionItems.length > 0) {
      // Per SubscriptionItemOut schema: price is total for all quantity, quantity is amount of subscriptions
      order.subscriptionItems.forEach(item => {
        const itemRow = document.createElement('div');
        itemRow.className = 'receipt-item';
        const itemName = item.product?.name || 'Medlemskab';
        const itemQuantity = item.quantity || 1;
        // Per SubscriptionItemOut: price.amount is total price for all quantity
        const itemTotal = item.price?.amount ? (typeof item.price.amount === 'object' ? item.price.amount.amount / 100 : item.price.amount / 100) : 0;

        itemRow.innerHTML = sanitizeHTML(`
          <div>${itemName}</div>
          <div>${itemQuantity}</div>
          <div>${formatCurrencyHalfKrone(itemTotal)}</div>
        `);
        receiptItems.appendChild(itemRow);
      });
    } else if (state.order?.items && state.order.items.length > 0) {
      // Fallback to state.order.items
      state.order.items.forEach(item => {
        const itemRow = document.createElement('div');
        itemRow.className = 'receipt-item';
        itemRow.innerHTML = sanitizeHTML(`
          <div>${item.name || 'Item'}</div>
          <div>${item.quantity || 1}</div>
          <div>${formatCurrencyHalfKrone(item.amount || 0)}</div>
        `);
        receiptItems.appendChild(itemRow);
      });
    }
  }
  
  // Move modal to body to escape any parent stacking contexts
  if (modal.parentElement !== document.body) {
    document.body.appendChild(modal);
  }
  
  // Prevent background scrolling - save current scroll position
  const scrollY = window.scrollY;
  document.body.style.position = 'fixed';
  document.body.style.top = `-${scrollY}px`;
  document.body.style.width = '100%';
  document.body.style.overflow = 'hidden';
  
  // Show modal
  modal.style.display = 'flex';
}

function closeDetailedReceipt() {
  const modal = document.getElementById('detailedReceiptModal');
  if (modal) {
    modal.style.display = 'none';
    
    // Restore background scrolling - restore scroll position
    const scrollY = document.body.style.top;
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    document.body.style.overflow = '';
    
    // Restore scroll position
    if (scrollY) {
      window.scrollTo(0, parseInt(scrollY || '0') * -1);
    }
  }
}


function validateForm(animate = false) {
  let isValid = true;
  const errors = [];
  clearErrorStates();
  const skipPersonalValidation = isUserAuthenticated();

  if (!skipPersonalValidation) {
    REQUIRED_FIELDS.forEach((fieldId) => {
      const field = document.getElementById(fieldId);
      if (field && !field.value.trim()) {
        isValid = false;
        errors.push(`Missing required field: ${fieldId}`);
        highlightFieldError(fieldId, animate);
      }
    });
  }

  if (!skipPersonalValidation && DOM.parentGuardianForm && DOM.parentGuardianForm.style.display !== 'none') {
    PARENT_REQUIRED_FIELDS.forEach((fieldId) => {
      const field = document.getElementById(fieldId);
      if (field && !field.value.trim()) {
        isValid = false;
        errors.push(`Missing parent/guardian field: ${fieldId}`);
        highlightFieldError(fieldId, animate);
      }
    });
  }

  // Re-cache consent elements in case they weren't found initially
  if (!DOM.privacyConsent) {
    DOM.privacyConsent = document.getElementById('privacyConsent');
  }
  if (!DOM.termsConsent) {
    DOM.termsConsent = document.getElementById('termsConsent');
  }

  // Privacy consent is only required for new users (not logged-in users)
  // Logged-in users already accepted privacy policy when creating their account
  if (!skipPersonalValidation && !DOM.privacyConsent?.checked) {
    isValid = false;
    errors.push('Privacy consent not accepted');
    if (animate) {
      showToast('Please accept the privacy policy.', 'error');
      // Animate privacy consent checkbox
      const privacyConsent = DOM.privacyConsent;
      if (privacyConsent) {
        const formGroup = privacyConsent.closest('.form-group') || privacyConsent.closest('.consents-section');
        if (formGroup) {
          formGroup.classList.add('error');
          formGroup.style.animation = 'shake 0.5s ease-in-out';
          setTimeout(() => {
            formGroup.style.animation = '';
          }, 500);
        }
      }
    }
  }

  if (!DOM.termsConsent?.checked) {
    isValid = false;
    errors.push('Terms consent not accepted');
    if (animate) {
      showToast('Please accept the terms and conditions.', 'error');
      // Animate terms consent checkbox
      const termsConsent = DOM.termsConsent;
      if (termsConsent) {
        const formGroup = termsConsent.closest('.form-group') || termsConsent.closest('.consents-section');
        if (formGroup) {
          formGroup.classList.add('error');
          formGroup.style.animation = 'shake 0.5s ease-in-out';
          setTimeout(() => {
            formGroup.style.animation = '';
          }, 500);
        }
      }
    }
  }

  // Payment method UI removed - always use card; ensure default for API
  if (!state.paymentMethod) {
    state.paymentMethod = 'card';
  }

  // Card fields validation REMOVED - users are redirected to payment provider
  // Payment details are entered on the payment provider's secure page, not on our site
  // No need to validate card fields here since they won't be filled on this page

  // Return detailed validation result for better debugging
  if (!isValid) {
    console.log('[validateForm] Validation errors:', {
      skipPersonalValidation,
      privacyConsentFound: !!DOM.privacyConsent,
      privacyConsentChecked: DOM.privacyConsent?.checked,
      termsConsentFound: !!DOM.termsConsent,
      termsConsentChecked: DOM.termsConsent?.checked,
      errors
    });
  }

  return {
    isValid,
    errors,
    message: errors.length > 0 ? errors[0] : 'Please review the highlighted fields.'
  };
}

function clearErrorStates() {
  [...REQUIRED_FIELDS, ...PARENT_REQUIRED_FIELDS, ...CARD_FIELDS].forEach((fieldId) => {
    const field = document.getElementById(fieldId);
    const formGroup = field?.closest('.form-group');
    if (formGroup) {
      formGroup.classList.remove('error');
      formGroup.style.animation = ''; // Clear any animations
    }
  });
  
  // Also clear animations from consent and payment sections
  const consentsSection = document.querySelector('.consents-section');
  if (consentsSection) {
    consentsSection.classList.remove('error');
    consentsSection.style.animation = '';
  }
  
  const paymentMethods = document.querySelector('.payment-methods');
  if (paymentMethods) {
    paymentMethods.style.animation = '';
  }
}

function getFreeFlowCartState() {
  const isFreeTrialSelected = Boolean(document.querySelector('[data-category="freetrial"] .plan-card.selected')) ||
    state.landingRouteConfig?.componentName === 'LandingFreeTrial';
  const payNowAmount = Number(state?.totals?.payNowAmount ?? state?.totals?.cartTotal ?? NaN);
  const isZeroTotal = Number.isFinite(payNowAmount) && Math.abs(payNowAmount) < 0.0001;
  const isFreeFlow = isFreeTrialSelected || isZeroTotal;
  return { isFreeTrialSelected, isZeroTotal, isFreeFlow };
}

// Single source of truth for free trial cart UI.
// Do NOT set these labels/states from translation updates or other cart refresh paths.
function applyFreeFlowCartUi() {
  const { isFreeTrialSelected, isZeroTotal, isFreeFlow } = getFreeFlowCartState();

  const payNowRow = DOM.payNow ? DOM.payNow.closest('.payment-overview-paynow-row') : null;
  if (payNowRow) {
    const payNowLabel = payNowRow.querySelector('.payment-label');
    if (payNowLabel) {
      const payNowInfoWrapper = payNowLabel.querySelector('.paynow-info-wrapper');
      payNowLabel.textContent = isFreeTrialSelected
        ? `${t('cart.freePeriod') || 'Gratis periode'}:`
        : `${t('cart.payNow') || 'Pay now'}:`;
      if (payNowInfoWrapper) {
        if (isFreeTrialSelected) {
          payNowInfoWrapper.remove();
        } else {
          payNowLabel.appendChild(payNowInfoWrapper);
        }
      }
    }
  }

  if (DOM.checkoutBtn) {
    DOM.checkoutBtn.textContent = isFreeTrialSelected ? (t('cart.activateTrial') || 'AKTIVÉR PRØVEPERIODE') : t('cart.checkout');
  }

  const paymentRedirectText = document.querySelector('p[data-i18n-key="cart.paymentRedirect"]');
  if (paymentRedirectText) {
    paymentRedirectText.style.display = isFreeFlow ? 'none' : '';
  }

  const discountSection = document.querySelector('.discount-section');
  if (discountSection) {
    discountSection.style.display = isFreeTrialSelected ? 'none' : '';
  }

  return { isFreeTrialSelected, isZeroTotal, isFreeFlow };
}

function updateCheckoutButton() {
  if (!DOM.checkoutBtn) return;
  
  // Re-cache DOM elements in case they weren't found initially (e.g., when logged in and create form is hidden)
  if (!DOM.privacyConsent) {
    DOM.privacyConsent = document.getElementById('privacyConsent');
  }
  if (!DOM.termsConsent) {
    DOM.termsConsent = document.getElementById('termsConsent');
  }
  
  const isAuthenticated = isUserAuthenticated();
  
  // For logged-in users, privacy consent is assumed (they already have an account)
  // For new users, check if privacy consent checkbox is checked
  const privacyAccepted = isAuthenticated 
    ? true 
    : (DOM.privacyConsent?.checked ?? false);
  
  const termsAccepted = DOM.termsConsent?.checked ?? false;
  const hasMembership = Boolean(state.membershipPlanId);
  const hasPunchCards = state.valueCardQuantities && 
    Array.from(state.valueCardQuantities.values()).some(qty => qty > 0);
  const hasAddons = state.addonIds && state.addonIds.size > 0;

  // Payment method UI removed - always use card; ensure default is set for API
  if (!state.paymentMethod) {
    state.paymentMethod = 'card';
  }

  // Enable checkout if: privacy + terms accepted, AND (membership OR punch cards OR addons)
  // Payment method no longer required from UI - card is used by default
  const hasProduct = hasMembership || hasPunchCards || hasAddons;
  DOM.checkoutBtn.disabled = !(privacyAccepted && termsAccepted && hasProduct);
  const { isFreeTrialSelected, isZeroTotal, isFreeFlow } = applyFreeFlowCartUi();
  
  const consentsSection = document.querySelector('.consents-section');
  if (consentsSection) {
    consentsSection.style.marginTop = isFreeFlow ? '16px' : '';
  }

  const shouldShowFreeTrialHint = isFreeTrialSelected && isZeroTotal;
  let freeTrialHint = document.getElementById('freeTrialCheckoutHint');
  if (!freeTrialHint) {
    freeTrialHint = document.createElement('div');
    freeTrialHint.id = 'freeTrialCheckoutHint';
    freeTrialHint.style.display = 'none';
    freeTrialHint.style.marginTop = '8px';
    freeTrialHint.style.marginTop = '16px';
    freeTrialHint.style.textAlign = 'center';
    freeTrialHint.style.fontSize = '0.8125rem';
    freeTrialHint.style.color = 'rgba(255, 255, 255, 0.65)';
    freeTrialHint.style.lineHeight = '1.3';
    const hintWrap = document.createElement('span');
    hintWrap.style.display = 'inline-flex';
    hintWrap.style.alignItems = 'center';
    hintWrap.style.gap = '6px';
    const hintIcon = document.createElement('span');
    hintIcon.setAttribute('aria-hidden', 'true');
    hintIcon.style.color = '#22c55e';
    hintIcon.textContent = '✓';
    const hintText = document.createElement('span');
    hintText.textContent = t('cart.noCreditCardRequired') || 'Intet kreditkort kræves';
    hintText.className = 'free-trial-checkout-hint-text';
    hintWrap.appendChild(hintIcon);
    hintWrap.appendChild(hintText);
    freeTrialHint.appendChild(hintWrap);
    DOM.checkoutBtn.insertAdjacentElement('afterend', freeTrialHint);
  }
  const freeTrialHintTextEl = freeTrialHint.querySelector('.free-trial-checkout-hint-text');
  if (freeTrialHintTextEl) {
    freeTrialHintTextEl.textContent = t('cart.noCreditCardRequired') || 'Intet kreditkort kræves';
  }
  freeTrialHint.style.display = shouldShowFreeTrialHint ? 'block' : 'none';
  
  console.log('[Checkout Button] State:', {
    isAuthenticated,
    privacyAccepted,
    termsAccepted,
    hasMembership,
    hasPunchCards,
    hasAddons,
    hasProduct,
    isFreeTrialSelected,
    isZeroTotal,
    isFreeFlow,
    shouldShowFreeTrialHint,
    disabled: DOM.checkoutBtn.disabled,
    privacyConsentElement: DOM.privacyConsent ? 'found' : 'not found',
    termsConsentElement: DOM.termsConsent ? 'found' : 'not found'
  });
}

// Helper function to manage loading state during checkout
function setCheckoutLoadingState(isLoading) {
  if (!DOM.checkoutBtn) return;
  
  DOM.checkoutBtn.disabled = isLoading;
  
  if (isLoading) {
    DOM.checkoutBtn.textContent = 'Processing...';
    DOM.checkoutBtn.classList.add('loading');
    // Disable logout to prevent user logging out while redirecting to payment
    const headerLogoutBtn = document.getElementById('headerLogoutBtn');
    if (headerLogoutBtn) headerLogoutBtn.disabled = true;
  } else {
    DOM.checkoutBtn.textContent = 'Checkout';
    DOM.checkoutBtn.classList.remove('loading');
    // Re-enable logout when checkout flow ends
    const headerLogoutBtn = document.getElementById('headerLogoutBtn');
    if (headerLogoutBtn) headerLogoutBtn.disabled = false;
    // Re-validate button state based on form
    updateCheckoutButton();
  }
}

function nextStep(fromStep) {
  // Use provided fromStep if available (prevents race conditions), otherwise use state.currentStep
  const currentStep = fromStep !== undefined ? fromStep : state.currentStep;
  
  if (currentStep >= TOTAL_STEPS) {
    return;
  }
  
  // CRITICAL: When going from step 1 (gym selection), always go to step 2 (membership selection)
  // Never skip to any other step - this prevents navigation bugs
  if (currentStep === 1) {
    // /99kr (LandingFirstClimb): single-product flow. Skip step 2 entirely —
    // auto-select the firstclimb value card and jump straight to the form/checkout step.
    if (isFirstClimbRoute()) {
      advanceFirstClimbFromGym();
      return;
    }
    state.currentStep = 2;
    showStep(2);
    updateStepIndicator();
    updateNavigationButtons();
    updateMainSubtitle();

    // Step 5: Load products when step 2 (access type selection) is shown
    if (state.selectedBusinessUnit) {
      // Only load if we don't already have products loaded
      const hasAnyProducts = (state.subscriptions?.length || 0) > 0 ||
                             (state.dayPassSubscriptions?.length || 0) > 0 ||
                             (state.valueCards?.length || 0) > 0;
      if (!hasAnyProducts) {
        loadProductsFromAPI();
      }
      // Update selected gym display
      setTimeout(() => {
        updateSelectedGymDisplay();
      }, 100);
    }

    // Scroll to top when navigating steps
    scrollToTop();
    setTimeout(() => {
      scrollToTop();
    }, 200);

    return; // Early return - don't continue with normal navigation logic
  }
  
  // advance to next visible panel (skip any hidden ones)
  let target = currentStep + 1;
  // Add-ons step (step 3) is disabled - always skip it
  if (target === 3) {
    target = 4; // Skip directly to step 4
  }
  // Ensure membership goes to Boost (step 3) right after Access (step 2) - DISABLED
  // if (state.currentStep === 2 && isMembershipSelected()) {
  //   target = 3;
  // }
  
  // If we're going from step 2 to step 4 (skipping step 3), don't check for hidden panels
  // Step 4 should always be shown when coming from step 2
  if (currentStep === 2 && target === 4) {
    // Don't enter the while loop - step 4 should be shown
  } else {
    while (target <= TOTAL_STEPS) {
      const panel = DOM.stepPanels[target - 1];
      if (!panel) break; // Panel doesn't exist
      // Skip step 3 check since step 3 is always hidden
      if (target === 3) {
        target = 4;
        continue;
      }
      // Check if panel is explicitly hidden via inline style
      // Don't check computed style to avoid skipping step 4 when it has CSS display:none
      const hidden = panel.style && panel.style.display === 'none';
      if (!hidden) break;
      target += 1;
      // Skip step 3 if we land on it
      if (target === 3) {
        target = 4;
      }
      // CRITICAL: Never skip to step 5 (success page) unless explicitly navigating from step 4
      // This prevents accidental navigation to success page
      if (target >= TOTAL_STEPS && currentStep !== 4) {
        console.warn('[Navigation] Prevented navigation to success page from step', currentStep);
        target = Math.min(currentStep + 1, 4); // Cap at step 4
        break;
      }
    }
  }
  state.currentStep = Math.min(target, TOTAL_STEPS);
  showStep(state.currentStep);
  updateStepIndicator();
  updateNavigationButtons();
  updateMainSubtitle();
  
  // Scroll to top when navigating steps
  scrollToTop();
  setTimeout(() => {
    scrollToTop();
  }, 200);

  // Step 5: Load products when step 2 (access type selection) is shown
  if (state.currentStep === 2 && state.selectedBusinessUnit) {
    // Only load if we don't already have products loaded
    const hasAnyProducts = (state.subscriptions?.length || 0) > 0 || 
                           (state.dayPassSubscriptions?.length || 0) > 0 || 
                           (state.valueCards?.length || 0) > 0;
    if (!hasAnyProducts) {
      loadProductsFromAPI();
    }
    // Update selected gym display when step 2 is shown
    updateSelectedGymDisplay();
  }

  // Update cart when step 4 (Send/Info) is shown
  if (state.currentStep === 4) {
    updateCartSummary();
    // If user is logged in, ensure login tab is selected
    if (isUserAuthenticated()) {
      switchAuthMode('login');
      
      // Do not pre-create order on step display; wait for explicit checkout.
    }
    
    // If order exists, fetch full order data for payment overview
    if (state.orderId && !state.fullOrder) {
      console.log('[Payment Overview] Step 4 shown - fetching order data for payment overview (orderId:', state.orderId, ')');
      orderAPI.getOrder(state.orderId)
        .then(order => {
          state.fullOrder = order;
          updatePaymentOverview();
          console.log('[Payment Overview] ✅ Order data fetched on step 4, payment overview updated');
          console.log('[Payment Overview] Order price:', order.price?.amount, 'SubscriptionItems:', order.subscriptionItems?.length);
        })
        .catch(error => {
          console.warn('[Payment Overview] Could not fetch order data on step 4:', error);
        });
    } else if (state.fullOrder) {
      // Full order data already available - just update payment overview
      updatePaymentOverview();
    }
  }

  // Scroll to top on mobile only
  if (window.innerWidth <= 768) {
    scrollToTop();
    setTimeout(() => {
      scrollToTop();
    }, 200);
  }

  // CRITICAL: Only show success page (step 5) if there's actually a completed order
  // Never navigate to success page unless purchase is actually successful
  // EXCEPTION: Allow test mode via URL parameter ?testSuccess=true
  if (state.currentStep === TOTAL_STEPS) {
    // Check URL parameters for test mode
    const urlParams = new URLSearchParams(window.location.search);
    const testMode = urlParams.get('testSuccess') === 'true';
    const testProductType = urlParams.get('testProductType') || 'membership'; // membership, 15daypass, punch-card
    
    // CRITICAL: Check if payment is actually confirmed before showing success page
    // Don't show success page if payment failed (401 error) or payment is pending
    // Test mode bypasses this check
    if (!testMode) {
      const isPaymentFailed = state.paymentFailed === true;
      const isPaymentPending = state.paymentPending === true;
      
      if (isPaymentFailed || isPaymentPending) {
        console.log('[Navigation] Payment failed or pending - not rendering success page');
        // Don't render success page - the failed/pending message functions handle the UI
        return;
      }
    }
    
    // Only render confirmation if we have order data AND (payment is confirmed OR we're in test mode)
    // This prevents showing success page when user hasn't completed purchase (unless in test mode)
    if ((state.order && state.orderId && (state.paymentConfirmed !== false || testMode)) || testMode) {
      if (testMode) {
        // Use test product type from state if available, otherwise from URL
        const productType = state.testProductType || testProductType;
        console.log('[Test Mode] Creating mock order data for testing:', productType);
        
        // Create mock order data for testing
        state.order = {
          number: 'TEST-12345',
          date: new Date(),
          items: [
            { name: productType === 'membership' ? 'Membership' : productType === '15daypass' ? '15-Day Trial Pass' : 'Punch Card', amount: 469 }
          ],
          total: 469,
          memberName: 'Test User',
          membershipNumber: 'TEST-12345',
          membershipType: productType === 'membership' ? 'Medlemskab' : productType === '15daypass' ? '15-Day Trial Pass' : 'Punch Card',
          primaryGym: 'Boulders Aarhus Nord',
          membershipPrice: 469,
        };
        state.orderId = 'TEST-12345';
        
        // Set product type for test mode - ensure state is set correctly
        state.testMode = true;
        state.testProductType = productType;
        state.paymentConfirmed = true; // Set payment confirmed for test mode
        state.paymentFailed = false;
        state.paymentPending = false;
        
        // Set product type for test mode
        if (productType === 'punch-card') {
          state.selectedProductType = 'punch-card';
          // Mock value card items with price
          state.fullOrder = {
            valueCardItems: [{
              quantity: 2,
              product: { name: 'Klippekort', productLabels: [] },
              valueCard: { number: '12345', numberOfPassages: 10 },
              price: { amount: 46900 } // 469.00 DKK in cents
            }]
          };
        } else if (productType === '15daypass') {
          state.selectedProductType = 'membership';
          state.membershipPlanId = '15daypass-123';
          // Mock subscription items with 15-Day Trial Pass label and price
          state.fullOrder = {
            subscriptionItems: [{
              product: {
                name: '15-Day Trial Pass',
                productLabels: [{ name: '15-Day Trial Pass' }]
              },
              price: { amount: 46900 } // 469.00 DKK in cents
            }]
          };
        } else {
          // Default to membership
          state.selectedProductType = 'membership';
          state.membershipPlanId = 'membership-123';
          // Mock subscription items with price
          state.fullOrder = {
            subscriptionItems: [{
              product: {
                name: 'Medlemskab',
                productLabels: [{ name: 'Public' }]
              },
              price: { amount: 46900 } // 469.00 DKK in cents
            }]
          };
        }
        
        console.log('[Test Mode] Mock data created:', {
          productType: productType,
          selectedProductType: state.selectedProductType,
          membershipPlanId: state.membershipPlanId,
          hasValueCardItems: !!(state.fullOrder?.valueCardItems?.length),
          hasSubscriptionItems: !!(state.fullOrder?.subscriptionItems?.length)
        });
      }
      renderConfirmationView();
    } else {
      // If we somehow ended up on step 5 without an order, go back to step 1
      console.warn('[Navigation] Attempted to show success page without order data or payment not confirmed. Not rendering success page.');
      console.warn('[Navigation] To test success page, add ?testSuccess=true&testProductType=membership|15daypass|punch-card to URL');
      // Don't redirect - let the payment failed/pending handlers show the appropriate message
    }
  }
}

function prevStep() {
  if (state.currentStep <= 1) return;
  
  // Clear any pending navigation timeouts when going back
  // This prevents stale timeouts from navigating after user changes selection
  Object.keys(pendingNavigationTimeouts).forEach(key => {
    if (pendingNavigationTimeouts[key]) {
      clearTimeout(pendingNavigationTimeouts[key]);
      pendingNavigationTimeouts[key] = null;
    }
  });
  
  // go back to previous visible panel (skip any hidden ones)
  let target = state.currentStep - 1;
  // Add-ons step (step 3) is disabled - always skip it
  if (target === 3) {
    target = 2; // Go back to step 2 instead
  }
  while (target >= 1) {
    const panel = DOM.stepPanels[target - 1];
    const hidden = panel && panel.style && panel.style.display === 'none';
    if (!hidden) break;
    target -= 1;
    // Skip step 3 if we land on it
    if (target === 3) {
      target = 2;
    }
  }
  state.currentStep = Math.max(target, 1);
  showStep(state.currentStep);
  updateStepIndicator();
  updateNavigationButtons();
  updateMainSubtitle();

  // Scroll to top when navigating steps
  scrollToTop();
  setTimeout(() => {
    scrollToTop();
  }, 200);
  
  // Show access heads-up if going back to step 2 and a plan is selected
  if (state.currentStep === 2 && state.membershipPlanId) {
    const selectedCard = document.querySelector(`[data-plan="${state.membershipPlanId}"]`);
    if (selectedCard) {
        // Ensure the card is selected visually
        // Clear all selections first
        document.querySelectorAll('.plan-card').forEach(c => {
          c.classList.remove('selected', 'has-quantity');
          // Hide quantity selector for all cards
          const selector = c.querySelector('.quantity-selector');
          if (selector) {
            selector.style.display = 'none';
          }
        });
        
        // Select the previously selected card
        selectedCard.classList.add('selected');
        
        // If it's a value card with quantity, restore quantity panel and disabled state.
        // Quantity panel is INSIDE the plan-card (child), not a sibling.
        if (state.valueCardQuantities.has(state.membershipPlanId)) {
          selectedCard.classList.add('has-quantity');
          const panel = selectedCard.querySelector('.quantity-panel');
          if (panel) {
            panel.classList.add('show');
            panel.style.display = 'flex'; /* row layout: quantity left, Continue right */
            const continueBtn = panel.querySelector('.continue-btn');
            if (continueBtn) continueBtn.style.display = '';
            const qtySelector = panel.querySelector('.quantity-selector');
            if (qtySelector) qtySelector.style.display = 'flex';
            syncPunchCardQuantityUI(selectedCard, state.membershipPlanId);
          }
          // Grey out the other punch card type (same category)
          const category = selectedCard.closest('.category-item').dataset.category;
          if (category === 'punchcard') {
            const otherPunchCard = document.querySelector(`[data-plan="${state.membershipPlanId === 'adult-punch' ? 'junior-punch' : 'adult-punch'}"]`);
            if (otherPunchCard) otherPunchCard.classList.add('disabled');
          }
        }
        
        // Show heads-up for previously selected access type
        updateAccessHeadsUp(selectedCard);
        
        // 15-day pass: show activation date section if restoring selection
        const category = selectedCard.closest('.category-item')?.dataset?.category;
        if (category === '15daypass') {
          updateActivationDateSection('15daypass');
          // Restore activation date UI state
          const nowRadio = document.getElementById('activationDateNow');
          const pickRadio = document.getElementById('activationDatePick');
          const dateInput = document.getElementById('activationDateInput');
          const pickerWrap = document.getElementById('activationDatePickerWrap');
          if (state.subscriptionStartDate && pickRadio && dateInput && pickerWrap) {
            pickRadio.checked = true;
            dateInput.value = state.subscriptionStartDate;
            pickerWrap.hidden = false;
          } else if (nowRadio && pickerWrap) {
            nowRadio.checked = true;
            pickerWrap.hidden = true;
          }
        }
      }
    }
}

function showStep(stepNumber) {
  
  DOM.stepPanels.forEach((panel, index) => {
    const isActive = index + 1 === stepNumber;
    panel.classList.toggle('active', isActive);
    // Ensure display style is set correctly
    if (isActive) {
      panel.style.display = 'block';
      panel.style.visibility = 'visible';
      panel.style.opacity = '1';
    } else {
      // Only hide if not already hidden by other logic (like step 3)
      if (panel.id !== 'step-3') {
        panel.style.display = 'none';
      }
    }
  });
  
  // Scroll to top when showing a step
  scrollToTop();
  setTimeout(() => {
    scrollToTop();
  }, 100);
  
  // Remove flex and margin from step-content when step 5 (success page) is active
  const stepContent = document.querySelector('.step-content');
  if (stepContent) {
    if (stepNumber === TOTAL_STEPS) {
      stepContent.style.marginTop = '0';
      stepContent.style.flex = 'none'; // Prevent container from expanding
    } else {
      stepContent.style.marginTop = ''; // Reset to CSS default (50px)
      stepContent.style.flex = ''; // Reset to CSS default (flex: 1)
      // Ensure success/failure layout classes never leak into regular steps
      stepContent.classList.remove('success-page-active');
      stepContent.classList.remove('payment-failed-active');
      stepContent.style.minHeight = '';
      stepContent.style.height = '';
    }
  }
  
  // Update FAQ visibility based on current step
  updateFAQVisibility();
  
  // Update selected gym display when showing step 2
  if (stepNumber === 2) {
    // Use setTimeout to ensure DOM is ready
    setTimeout(() => {
      updateSelectedGymDisplay();
      
      // CRITICAL: Restore selectedBusinessUnit from sessionStorage if missing
      if (!state.selectedBusinessUnit) {
        try {
          const orderData = sessionStorage.getItem('boulders_checkout_order');
          if (orderData) {
            const storedOrder = JSON.parse(orderData);
            if (storedOrder.selectedBusinessUnit) {
              state.selectedBusinessUnit = storedOrder.selectedBusinessUnit;
              console.log('[showStep] Step 2 - Restored selectedBusinessUnit from sessionStorage:', state.selectedBusinessUnit);
            }
          }
        } catch (e) {
          console.warn('[showStep] Could not restore selectedBusinessUnit:', e);
        }
      }
      
      // CRITICAL: Load products if business unit is selected but products aren't loaded
      if (state.selectedBusinessUnit) {
        const hasAnyProducts = (state.subscriptions?.length || 0) > 0 || 
                               (state.dayPassSubscriptions?.length || 0) > 0 || 
                               (state.valueCards?.length || 0) > 0;
        if (!hasAnyProducts) {
          console.log('[showStep] Step 2 shown - loading products for business unit:', state.selectedBusinessUnit);
          loadProductsFromAPI()
            .then(() => {
              console.log('[showStep] ✅ Products loaded');
            })
            .catch(error => {
              console.error('[showStep] ❌ Failed to load products:', error);
            });
        } else {
          // Products already loaded, just render them
          console.log('[showStep] Products already loaded, rendering...');
          renderProductsFromAPI();
        }
      } else {
        console.warn('[showStep] Step 2 shown but no business unit selected - cannot load products');
      }
    }, 100);
  }
  
  // Update cart and payment overview when showing step 4 (payment step)
  if (stepNumber === 4) {
    // Use setTimeout to ensure DOM is ready
    setTimeout(() => {
      // CRITICAL: Restore cart from sessionStorage if empty OR if we're retrying payment
      // Check both conditions: empty cart OR payment retry scenario (orderId exists but cart might be empty)
      const needsRestore = (!state.cartItems || state.cartItems.length === 0) && state.orderId;
      if (needsRestore) {
        try {
          const orderData = sessionStorage.getItem('boulders_checkout_order');
          if (orderData) {
            const storedOrder = JSON.parse(orderData);
            if (storedOrder.cartItems) {
              state.cartItems = storedOrder.cartItems;
              console.log('[showStep] Step 4 - Restored cart items from sessionStorage:', state.cartItems.length, 'items');
              
              // CRITICAL: For punch cards, rebuild valueCardQuantities from cart items
              // This is needed because valueCardQuantities is a Map and doesn't serialize to JSON
              const punchCardItems = storedOrder.cartItems.filter(item => item.type === 'value-card');
              if (punchCardItems.length > 0) {
                console.log('[showStep] Step 4 - Found punch card items, rebuilding valueCardQuantities');
                state.valueCardQuantities.clear();
                punchCardItems.forEach(item => {
                  // Use membershipPlanId format (punch-{id} or adult-punch/junior-punch)
                  const planId = storedOrder.membershipPlanId || 
                                (item.productId ? `punch-${item.productId}` : `punch-${item.id}`);
                  const quantity = item.quantity || 1;
                  state.valueCardQuantities.set(planId, quantity);
                  console.log('[showStep] Step 4 - Rebuilt valueCardQuantities:', planId, '=', quantity);
                });
              }
            }
            if (storedOrder.totals) {
              state.totals = { ...state.totals, ...storedOrder.totals };
            }
            
            // Restore selectedProductType and selectedProductId if available
            if (storedOrder.selectedProductType) {
              state.selectedProductType = storedOrder.selectedProductType;
              console.log('[showStep] Step 4 - Restored selectedProductType:', state.selectedProductType);
            }
            if (storedOrder.selectedProductId) {
              state.selectedProductId = storedOrder.selectedProductId;
              console.log('[showStep] Step 4 - Restored selectedProductId:', state.selectedProductId);
            }
            
            if (storedOrder.membershipPlanId && !state.membershipPlanId) {
              state.membershipPlanId = storedOrder.membershipPlanId;
              
              // CRITICAL: Derive selectedProductId and selectedProductType from membershipPlanId if not already set
              // Handle all formats: campaign-, membership-, 15daypass-, punch-
              if (!state.selectedProductType || !state.selectedProductId) {
                if (typeof storedOrder.membershipPlanId === 'string') {
                  if (storedOrder.membershipPlanId.startsWith('campaign-')) {
                    const productId = storedOrder.membershipPlanId.replace('campaign-', '');
                    state.selectedProductId = parseInt(productId, 10) || productId;
                    state.selectedProductType = 'membership';
                    console.log('[showStep] Step 4 - Derived selectedProductId from campaign:', state.selectedProductId);
                  } else if (storedOrder.membershipPlanId.startsWith('membership-')) {
                    const productId = storedOrder.membershipPlanId.replace('membership-', '');
                    state.selectedProductId = parseInt(productId, 10) || productId;
                    state.selectedProductType = 'membership';
                    console.log('[showStep] Step 4 - Derived selectedProductId from membership:', state.selectedProductId);
                  } else if (storedOrder.membershipPlanId.startsWith('15daypass-')) {
                    const productId = storedOrder.membershipPlanId.replace('15daypass-', '');
                    state.selectedProductId = parseInt(productId, 10) || productId;
                    state.selectedProductType = 'membership';
                    console.log('[showStep] Step 4 - Derived selectedProductId from 15daypass:', state.selectedProductId);
                  } else if (storedOrder.membershipPlanId.startsWith('punch-') || 
                             storedOrder.membershipPlanId === 'adult-punch' || 
                             storedOrder.membershipPlanId === 'junior-punch') {
                    // For punch cards, extract productId from membershipPlanId
                    if (storedOrder.membershipPlanId.startsWith('punch-')) {
                      const productId = storedOrder.membershipPlanId.replace('punch-', '');
                      state.selectedProductId = parseInt(productId, 10) || productId;
                    } else {
                      // For adult-punch/junior-punch, we need to find the productId from cart items
                      const punchCardItem = storedOrder.cartItems?.find(item => item.type === 'value-card');
                      if (punchCardItem) {
                        state.selectedProductId = punchCardItem.productId || punchCardItem.id;
                      }
                    }
                    state.selectedProductType = 'punch-card';
                    console.log('[showStep] Step 4 - Derived selectedProductId from punch card:', state.selectedProductId);
                  }
                }
              }
            }
            if (storedOrder.selectedBusinessUnit && !state.selectedBusinessUnit) {
              state.selectedBusinessUnit = storedOrder.selectedBusinessUnit;
            }
            if (storedOrder.subscriptionStartDate != null) {
              state.subscriptionStartDate = storedOrder.subscriptionStartDate;
            }
          }
        } catch (e) {
          console.warn('[showStep] Could not restore cart from sessionStorage:', e);
        }
      }
      
      updateCartSummary();
      // If order exists, update payment overview
      if (state.orderId && state.fullOrder) {
        updatePaymentOverview();
      }
    }, 100);
  } else {
    // When leaving step 2, reset main content margin
    const mainContent = document.getElementById('mainContent');
    if (mainContent) {
      mainContent.style.marginTop = '';
    }
  }
  
  // If showing step 4, ensure country selectors are populated
  if (stepNumber === 4) {
    // Ensure countries are loaded (in case they weren't loaded on init)
    const countryCodeSelect = document.getElementById('countryCode');
    const parentCountryCodeSelect = document.getElementById('parentCountryCode');
    if (countryCodeSelect && countryCodeSelect.options.length <= 1) {
      // Only one option (the default +45), so countries haven't been loaded yet
      const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      if (isDevelopment) {
        console.log('[Step 4] Country selectors not populated, loading countries...');
      }
      loadCountriesFromAPI();
    }
  }
  
  // If showing step 4 and user is logged in, ensure login tab is selected
  if (stepNumber === 4 && isUserAuthenticated()) {
    switchAuthMode('login');
  } else if (stepNumber === 4 && !isUserAuthenticated()) {
    // Update switch button visibility when showing step 4
    const switchBtns = document.querySelectorAll('.auth-mode-switch-btn');
    switchBtns.forEach(btn => {
      btn.style.display = '';
    });
  }
  
  // Default to create account form when showing step 4 (if not authenticated)
  if (stepNumber === 4 && !isUserAuthenticated()) {
    // Initialize auth mode toggle immediately
    initAuthModeToggle();
    
    // Ensure create account form is visible
    setTimeout(() => {
      const loginSection = document.querySelector('[data-auth-section="login"]');
      const createSection = document.querySelector('[data-auth-section="create"]');
      
      if (loginSection && createSection) {
        // Activate create account mode
        switchAuthMode('create');
        
        // Ensure sections are properly displayed
        createSection.style.display = 'block';
        createSection.style.visibility = 'visible';
        createSection.style.opacity = '1';
        loginSection.style.display = 'none';
      }
    }, 50);
  }
  
  // If showing step 4 and order data is available, update payment overview
  if (stepNumber === 4 && state.orderId && !state.fullOrder) {
    // Order ID exists but full order data not loaded - fetch it
    orderAPI.getOrder(state.orderId)
      .then(order => {
        state.fullOrder = order;
        updatePaymentOverview();
        console.log('[Payment Overview] Order data fetched and payment overview updated on step 4');
      })
      .catch(error => {
        console.warn('[Payment Overview] Could not fetch order data on step 4:', error);
      });
  } else if (stepNumber === 4 && state.fullOrder) {
    // Full order data already available - just update payment overview
    updatePaymentOverview();
  }
}

function updateStepIndicator() {
  const stepIndicator = document.querySelector('.step-indicator');
  if (!stepIndicator) return;

  if (state.currentStep === TOTAL_STEPS) {
    stepIndicator.classList.add('hidden');
  } else {
    stepIndicator.classList.remove('hidden');
  }
  
  // Add class when on step 2 to remove top margin (selected gym display is visible)
  if (state.currentStep === 2) {
    stepIndicator.classList.add('step-2-active');
  } else {
    stepIndicator.classList.remove('step-2-active');
  }

  // Map state.currentStep to indicator step index
  // Default flow: Step 1 → 0 (Home Gym), Step 2 → 1 (Access), Step 4 → 2 (Send)
  // /99kr flow: Access is hidden, so Step 1 → 0 (Home Gym), Step 4 → 1 (Send)
  // Step 3 is always skipped (Boost is hidden); Step 5 hides the indicator (handled above).
  const firstClimbFlow = isFirstClimbRoute();
  let visibleCurrentIndex = -1;
  if (state.currentStep === 1) {
    visibleCurrentIndex = 0;
  } else if (state.currentStep === 2 && !firstClimbFlow) {
    visibleCurrentIndex = 1;
  } else if (state.currentStep === 4) {
    visibleCurrentIndex = firstClimbFlow ? 1 : 2;
  } else {
    // For step 5 or any other step, hide indicator (already handled above)
    return;
  }

  console.log('[Step Indicator] Updating:', {
    currentStep: state.currentStep,
    visibleCurrentIndex: visibleCurrentIndex
  });

  // Work only with visible indicator steps (Boost is hidden by applyConditionalSteps)
  const indicatorSteps = Array.from(document.querySelectorAll('.step'))
    .filter((s) => !s.classList.contains('hidden'));
  const indicatorCircles = indicatorSteps.map((s) => s.querySelector('.step-circle')).filter(Boolean);
  const indicatorConnectors = Array.from(document.querySelectorAll('.step-connector'))
    .filter((c) => !c.classList.contains('hidden'));

  indicatorCircles.forEach((circle, index) => {
    const isCompleted = index < visibleCurrentIndex;
    const isActive = index === visibleCurrentIndex;
    circle.classList.toggle('completed', isCompleted);
    circle.classList.toggle('active', isActive);
    circle.classList.toggle('inactive', !isActive && !isCompleted);

    if (isCompleted) {
      circle.innerHTML = sanitizeHTML(
        '<svg class="checkmark" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6L9 17l-5-5"/></svg>');
    } else {
      circle.textContent = String(index + 1);
    }

    const step = circle.closest('.step');
    if (step) {
      step.classList.toggle('completed', isCompleted);
      step.classList.toggle('active', isActive);
      step.classList.toggle('inactive', !isActive && !isCompleted);
    }
  });

  indicatorConnectors.forEach((connector, index) => {
    const wasCompleted = connector.classList.contains('completed');
    const isNowCompleted = index < visibleCurrentIndex;
    
    connector.classList.toggle('completed', isNowCompleted);
    
    // Trigger animation when connector becomes completed
    if (!wasCompleted && isNowCompleted) {
      connector.classList.add('animating');
      setTimeout(() => {
        connector.classList.remove('animating');
      }, 600);
    }
  });
  
  // Update selected gym display (show only on step 2)
  updateSelectedGymDisplay();
}

function updateSelectedGymDisplay() {
  const selectedGymDisplay = document.getElementById('selectedGymDisplay');
  const selectedGymNameDisplay = document.getElementById('selectedGymNameDisplay');
  const mainContent = document.getElementById('mainContent');
  
  if (!selectedGymDisplay || !selectedGymNameDisplay) {
    console.warn('[Selected Gym Display] Elements not found', {
      display: !!selectedGymDisplay,
      nameDisplay: !!selectedGymNameDisplay
    });
    return;
  }
  
  // Always hide first, then show only if we have a valid name
  selectedGymDisplay.style.display = 'none';
  selectedGymNameDisplay.textContent = '-'; // Reset to default
  
  // Reset main content margin when hiding display (for all steps except step 2)
  if (mainContent && state.currentStep !== 2) {
    mainContent.style.marginTop = '';
  }
  
  // Show only on step 2 if a gym is selected
  if (state.currentStep === 2 && state.selectedBusinessUnit) {
    let gymName = null;
    
    console.log('[Selected Gym Display] Looking for gym:', {
      currentStep: state.currentStep,
      selectedBusinessUnit: state.selectedBusinessUnit,
      storedGymName: state.selectedGymName,
      gymsWithDistancesLength: gymsWithDistances?.length || 0
    });
    
    // Priority 1: Use stored gym name from state (most reliable)
    if (state.selectedGymName && state.selectedGymName.trim() !== '') {
      gymName = state.selectedGymName.trim();
      console.log('[Selected Gym Display] Using stored name:', gymName);
    }
    // Priority 2: Try to get from DOM element (if step 1 is still visible)
    else {
      const gymDataId = `gym-${state.selectedBusinessUnit}`;
      const selectedGymItem = document.querySelector(`[data-gym-id="${gymDataId}"]`);
      console.log('[Selected Gym Display] Looking for DOM element:', gymDataId, 'Found:', !!selectedGymItem);
      if (selectedGymItem) {
        const gymNameElement = selectedGymItem.querySelector('.gym-name');
        if (gymNameElement) {
          gymName = gymNameElement.textContent.trim();
          if (gymName) {
            // Store it for future use
            state.selectedGymName = gymName;
            console.log('[Selected Gym Display] Found in DOM:', gymName);
          }
        }
      }
    }
    
    // Priority 3: Fallback to gymsWithDistances array
    if (!gymName && gymsWithDistances && gymsWithDistances.length > 0) {
      console.log('[Selected Gym Display] Searching in gymsWithDistances array...');
      const selectedGym = gymsWithDistances.find(gym => 
        String(gym.id) === String(state.selectedBusinessUnit)
      );
      console.log('[Selected Gym Display] Found gym in array:', selectedGym);
      if (selectedGym && selectedGym.name && selectedGym.name.trim() !== '') {
        gymName = selectedGym.name.trim();
        // Store it for future use
        state.selectedGymName = gymName;
        console.log('[Selected Gym Display] Found in array:', gymName);
      }
    }
    
    // Only show if we have a valid gym name
    if (gymName && gymName.trim() !== '' && gymName !== '-') {
      selectedGymNameDisplay.textContent = gymName;
      selectedGymDisplay.style.display = 'flex';
      
      // Ensure button is clickable - add direct event listener as backup
      const selectedGymLink = document.getElementById('selectedGymLink');
      if (selectedGymLink) {
        // Remove any existing listeners to avoid duplicates
        const newButton = selectedGymLink.cloneNode(true);
        selectedGymLink.parentNode.replaceChild(newButton, selectedGymLink);
        
        // Add direct click handler
        newButton.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          console.log('[Edit Gym] Direct click handler triggered');
          handleBackToGym();
        });
        
        console.log('[Selected Gym Display] Button event listener attached');
      }
      
      // Adjust main content margin to account for selected gym display height
      // Use setTimeout to ensure height is calculated after display is shown
      setTimeout(() => {
        const mainContent = document.getElementById('mainContent');
        if (mainContent && selectedGymDisplay.offsetHeight > 0) {
          const displayHeight = selectedGymDisplay.offsetHeight;
          mainContent.style.marginTop = `-${displayHeight}px`;
        }
      }, 10);
      
      console.log('[Selected Gym Display] ✅ Display updated with:', gymName);
    } else {
      // Hide if we can't find the gym name
      selectedGymDisplay.style.display = 'none';
      
      // Reset main content margin when display is hidden
      if (mainContent) {
        mainContent.style.marginTop = '';
      }
      
      console.warn('[Selected Gym Display] ❌ Could not find gym name for ID:', state.selectedBusinessUnit, {
        storedName: state.selectedGymName,
        gymsArrayLength: gymsWithDistances?.length || 0,
        gymsArray: gymsWithDistances?.slice(0, 3).map(g => ({ id: String(g.id), name: g.name })) || []
      });
    }
  } else {
    // Not on step 2 - ensure margin is reset
    if (mainContent) {
      mainContent.style.marginTop = '';
    }
  }
}

function updateNavigationButtons() {
  if (DOM.prevBtn) {
    DOM.prevBtn.disabled = state.currentStep === 1;
  }
  if (DOM.nextBtn) {
    DOM.nextBtn.disabled = state.currentStep === TOTAL_STEPS;
    DOM.nextBtn.textContent = state.currentStep === TOTAL_STEPS ? 'Complete' : 'Next';
  }
  
}
function updateMainSubtitle() {
  if (!DOM.mainSubtitle || !DOM.mainTitle) return;

  const subtitles = {
    1: t('main.subtitle.step1'),
    2: t('main.subtitle.step2'),
    3: t('main.subtitle.step3'),
    4: t('main.subtitle.step4'),
    5: 'Welcome to Boulders!', // Step 5 is success page
  };

  DOM.mainSubtitle.textContent = subtitles[state.currentStep] ?? t('main.subtitle.step2');
  
  // Update secondary subtitle for current step
  const secondarySubtitle = document.querySelector('.secondary-subtitle');
  if (secondarySubtitle) {
    if (state.currentStep === 1) {
      secondarySubtitle.textContent = t('main.subtitle.step1.secondary');
    } else if (state.currentStep === 2) {
      secondarySubtitle.textContent = t('main.subtitle.step2.secondary');
    }
  }
  
  DOM.mainTitle.textContent = state.currentStep === TOTAL_STEPS ? 'WELCOME TO BOULDERS' : 'JOIN BOULDERS';
}

function refreshCarousels() {
  document.querySelectorAll('.plan-section').forEach((section) => {
    setupCarousel(section);
  });
}

function scrollCardIntoCenter(carousel, card, behavior = 'smooth') {
  if (!carousel || !card) return;

  const canScroll = Math.ceil(carousel.scrollWidth) > Math.ceil(carousel.clientWidth);
  if (!canScroll) return;

  const cardOffset = card.offsetLeft;
  const cardWidth = card.offsetWidth;
  const desiredLeft = cardOffset - (carousel.clientWidth / 2 - cardWidth / 2);
  const maxScroll = Math.max(carousel.scrollWidth - carousel.clientWidth, 0);
  const target = Math.min(Math.max(desiredLeft, 0), maxScroll);

  carousel.scrollTo({ left: target, behavior });
}

function centerPlanCard(card, behavior = 'smooth') {
  if (!card) return;
  const carousel = card.closest('[data-scroll-container]');
  if (!carousel) return;

  scrollCardIntoCenter(carousel, card, behavior);
  carousel.dataset.centerInitialized = 'true';
}

function initializeCarouselCenter(carousel) {
  if (!carousel || carousel.dataset.centerInitialized === 'true') return;

  const initialId = carousel.dataset.initialPlanId;
  let targetCard = initialId ? carousel.querySelector(`[data-plan-id="${initialId}"]`) : null;
  if (!targetCard) {
    targetCard = carousel.querySelector('.plan-card.selected');
  }
  if (!targetCard) {
    targetCard = carousel.querySelector('.plan-card');
  }

  if (targetCard) {
    scrollCardIntoCenter(carousel, targetCard, 'auto');
    carousel.dataset.centerInitialized = 'true';
  }
}

function setupCarousel(section) {
  const carousel = section.querySelector('[data-scroll-container]');
  const indicator = section.querySelector('.scroll-indicator');
  if (!carousel || !indicator) return;

  const updateIndicator = () => {
    const canScroll = Math.ceil(carousel.scrollWidth) > Math.ceil(carousel.clientWidth);
    indicator.classList.toggle('hidden', !canScroll);
    if (!canScroll) return;
    indicator.classList.toggle('at-start', carousel.scrollLeft <= 0);
    indicator.classList.toggle(
      'at-end',
      Math.ceil(carousel.scrollLeft + carousel.clientWidth) >= Math.ceil(carousel.scrollWidth),
    );
  };

  const adjustEdgePadding = () => {
    const firstCard = carousel.querySelector('.plan-card');
    if (!firstCard) {
      carousel.style.removeProperty('--carousel-edge-padding');
      carousel.style.removeProperty('--carousel-scroll-padding');
      return;
    }

    const cardWidth = firstCard.getBoundingClientRect().width;
    const containerWidth = carousel.getBoundingClientRect().width;

    if (!cardWidth || !containerWidth) {
      window.requestAnimationFrame(adjustEdgePadding);
      return;
    }

    const hasHorizontalOverflow = Math.ceil(carousel.scrollWidth) > Math.ceil(carousel.clientWidth);
    if (!hasHorizontalOverflow) {
      carousel.style.removeProperty('--carousel-edge-padding');
      carousel.style.removeProperty('--carousel-scroll-padding');
      return;
    }

    const diff = containerWidth - cardWidth;
    const edgePadding = diff > 0 ? diff / 2 : 12;
    const scrollPadding = diff > 0 ? diff / 2 : 12;

    carousel.style.setProperty('--carousel-edge-padding', `${edgePadding}px`);
    carousel.style.setProperty('--carousel-scroll-padding', `${scrollPadding}px`);
  };

  const updateCarouselState = () => {
    adjustEdgePadding();
    updateIndicator();
    const selectedCard = carousel.querySelector('.plan-card.selected');
    if (selectedCard) {
      scrollCardIntoCenter(carousel, selectedCard, 'auto');
    }
  };

  updateCarouselState();
  initializeCarouselCenter(carousel);

  let scrollHandler = carouselScrollHandlers.get(carousel);
  if (scrollHandler) {
    carousel.removeEventListener('scroll', scrollHandler);
  }
  scrollHandler = () => updateIndicator();
  carousel.addEventListener('scroll', scrollHandler, { passive: true });
  carouselScrollHandlers.set(carousel, scrollHandler);

  if (typeof ResizeObserver === 'function') {
    const fallbackHandler = carouselResizeFallbacks.get(carousel);
    if (fallbackHandler) {
      window.removeEventListener('resize', fallbackHandler);
      carouselResizeFallbacks.delete(carousel);
    }

    let resizeObserver = carouselResizeObservers.get(carousel);
    if (resizeObserver) {
      resizeObserver.disconnect();
    }
    resizeObserver = new ResizeObserver(updateCarouselState);
    resizeObserver.observe(carousel);
    const firstCard = carousel.querySelector('.plan-card');
    if (firstCard) {
      resizeObserver.observe(firstCard);
    }
    carouselResizeObservers.set(carousel, resizeObserver);
  } else {
    let fallbackHandler = carouselResizeFallbacks.get(carousel);
    if (fallbackHandler) {
      window.removeEventListener('resize', fallbackHandler);
    }
    fallbackHandler = () => updateCarouselState();
    window.addEventListener('resize', fallbackHandler);
    carouselResizeFallbacks.set(carousel, fallbackHandler);
  }
}

function setByPath(target, path, value) {
  const segments = path.split('.');
  let current = target;
  segments.forEach((segment, index) => {
    if (index === segments.length - 1) {
      current[segment] = value;
    } else {
      current[segment] = current[segment] ?? {};
      current = current[segment];
    }
  });
}

function findMembershipPlan(id) {
  const planId = String(id ?? '');
  const pools = [
    state.subscriptions,
    state.dayPassSubscriptions,
    state.campaignSubscriptions,
  ];
  for (const list of pools) {
    if (!Array.isArray(list)) continue;
    const match = list.find((plan) => String(plan.id) === planId);
    if (match) return match;
  }
  return null;
}

function findValueCard(id) {
  const rawId = String(id ?? '');
  const normalizedId = rawId.startsWith('punch-') ? rawId.replace('punch-', '') : rawId;
  
  // Check both regular and campaign value cards
  const allValueCards = [
    ...(Array.isArray(state.valueCards) ? state.valueCards : []),
    ...(Array.isArray(state.campaignValueCards) ? state.campaignValueCards : [])
  ];
  
  return allValueCards.find((plan) => String(plan.id) === normalizedId) ?? null;
}

/** @returns {number|null} */
function parsePositiveInt(value) {
  if (value == null || value === '') return null;
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) return Math.round(value);
  if (typeof value === 'string' && /^\d+$/.test(value.trim())) {
    const n = parseInt(value.trim(), 10);
    return n > 0 ? n : null;
  }
  return null;
}

/**
 * Clip/punch count from value card product JSON (list or detail GET).
 * Tries common BRP / extension field names; ignores non-numeric text fields.
 * @param {Object} product
 * @returns {number|null}
 */
function getClipsPerCardFromApiProduct(product) {
  if (!product || typeof product !== 'object') return null;

  const pick = (...vals) => {
    for (const v of vals) {
      const n = parsePositiveInt(v);
      if (n != null) return n;
    }
    return null;
  };

  const fromFlat = pick(
    product.units,
    product.numberOfUnits,
    product.initialUnits,
    product.totalUnits,
    product.totalunits,
    product.clipCount,
    product.clips,
    product.numberOfClips,
    product.entryCount,
    product.number_of_units,
    product.total_units,
    product.initial_units
  );
  if (fromFlat != null) return fromFlat;

  const nested = product.valueCard;
  if (nested && typeof nested === 'object') {
    const type = String(nested.type || '').toUpperCase();
    if (type === 'UNIT') {
      const u = parsePositiveInt(nested.unitsLeft);
      if (u != null) return u;
    }
  }

  return null;
}

/**
 * Clips per card from order line items (backend-issued value card snapshot).
 * @param {number|string} productId
 * @param {Array} valueCardItems
 * @returns {number|null}
 */
function getClipsPerCardFromOrderValueCardItems(productId, valueCardItems) {
  if (productId == null || !Array.isArray(valueCardItems) || valueCardItems.length === 0) {
    return null;
  }
  const pid = String(productId);
  for (const item of valueCardItems) {
    if (item?.product?.id == null || String(item.product.id) !== pid) continue;

    for (const key of ['units', 'numberOfUnits', 'totalUnits', 'initialUnits']) {
      const n = parsePositiveInt(item[key]);
      if (n != null) return n;
    }

    const vc = item.valueCard;
    if (vc && typeof vc === 'object' && String(vc.type || '').toUpperCase() === 'UNIT') {
      const u = parsePositiveInt(vc.unitsLeft);
      if (u != null) return u;
    }
  }
  return null;
}

/**
 * Resolve clips per card: order lines (if present), then product API fields, then name/description parse.
 * @param {Object} product
 * @param {Array|undefined} orderValueCardItems
 * @returns {number|null}
 */
function resolveClipsPerCardForValueCardProduct(product, orderValueCardItems) {
  if (!product) return null;
  const fromOrder = getClipsPerCardFromOrderValueCardItems(product.id, orderValueCardItems);
  if (fromOrder != null) return fromOrder;
  const fromApi = getClipsPerCardFromApiProduct(product);
  if (fromApi != null) return fromApi;
  return getPunchesPerCard(product);
}

/**
 * Merge GET /products/valuecards/{id} into list entries when list payload lacks unit counts.
 * @param {Array<Array<Object>>} cardArrays
 */
async function mergeValueCardProductDetails(cardArrays) {
  const flat = cardArrays.flat().filter(Boolean);
  const seen = new Set();
  await Promise.all(
    flat.map(async (vc) => {
      if (!vc?.id || seen.has(vc.id)) return;
      seen.add(vc.id);
      if (getClipsPerCardFromApiProduct(vc) != null) return;
      try {
        const detail = await businessUnitsAPI.getValueCardById(vc.id, state.selectedBusinessUnit);
        if (detail && typeof detail === 'object') Object.assign(vc, detail);
      } catch (e) {
        console.warn('[Products] Value card detail fetch failed:', vc.id, e);
      }
    })
  );
}

/**
 * Fetch and merge subscription detail only for the selected product.
 * This avoids hammering the API with detail calls for every product in local dev.
 * @param {string|number} productId
 * @returns {Promise<boolean>} true when fresh detail was merged
 */
async function ensureSubscriptionProductDetail(productId) {
  if (productId == null) return false;
  const normalizedId = String(productId);
  if (subscriptionDetailHydratedIds.has(normalizedId)) return false;
  if (subscriptionDetailPromises.has(normalizedId)) {
    return subscriptionDetailPromises.get(normalizedId);
  }

  const promise = (async () => {
    try {
      const detail = await businessUnitsAPI.getSubscriptionById(productId, state.selectedBusinessUnit);
      if (!detail || typeof detail !== 'object') return false;

      const allSubs = [
        ...(state.campaignSubscriptions || []),
        ...(state.subscriptions || []),
        ...(state.dayPassSubscriptions || [])
      ];
      allSubs.forEach((sub) => {
        if (String(sub?.id) === normalizedId) {
          Object.assign(sub, detail);
        }
      });

      subscriptionDetailHydratedIds.add(normalizedId);
      return true;
    } catch (e) {
      console.warn('[Products] Selected subscription detail fetch failed:', productId, e);
      return false;
    } finally {
      subscriptionDetailPromises.delete(normalizedId);
    }
  })();

  subscriptionDetailPromises.set(normalizedId, promise);
  return promise;
}

/**
 * Fallback: parse clip count from product name/description when API does not send numeric fields.
 * Supports e.g. "10 Klip", "Klippekort: 10 Klip + 2 Extra", "10 clips", "10 Entries (+2 Free)".
 * @param {Object} product - Value card product (name, description, etc.)
 * @returns {number|null} Punches per card, or null if unknown
 */
function getPunchesPerCard(product) {
  if (!product) return null;
  const name = (product.name || '').trim();
  const desc = (product.description || product.externalDescription || '').trim();
  const text = `${name} ${desc}`;
  if (!text) return null;
  // Main count: "10 Klip", "10 clips", "10 Entries", "10 punches" (English product names)
  const mainMatch = text.match(/(\d+)\s*(?:klip|clips?|entr(?:y|ies)|punches?)\b/i);
  const main = mainMatch ? parseInt(mainMatch[1], 10) : null;
  // Optional extra: "+ 2 Extra", "+ 2 ekstra", "(+2 Free)"
  const extraMatch = text.match(/\+\s*(\d+)\s*(?:extra|ekstra|free)?/i);
  const extra = extraMatch ? parseInt(extraMatch[1], 10) : 0;
  if (main != null && !Number.isNaN(main)) {
    return main + (Number.isNaN(extra) ? 0 : extra);
  }
  // Fallback: first number in text (e.g. "Klippekort 5" → 5)
  const anyNum = text.match(/\b(\d+)\b/);
  return anyNum ? parseInt(anyNum[1], 10) : null;
}

function findAddon(id) {
  // First check subscription additions (traditional addons)
  if (Array.isArray(state.subscriptionAdditions)) {
    const addon = state.subscriptionAdditions.find((addon) => String(addon.id) === String(id));
    if (addon) return addon;
  }
  
  // Then check boost products
  if (Array.isArray(state.boostProducts)) {
    const boostProduct = state.boostProducts.find((product) => String(product.id) === String(id));
    if (boostProduct) return boostProduct;
  }
  
  return null;
}

// Extract price from addon (handles both subscription additions and boost products)
function getAddonPrice(addon) {
  if (!addon) return 0;
  const source = addon.addition || addon;
  
  // Subscription additions typically have price.discounted structure
  if (addon.price && typeof addon.price.discounted === 'number') {
    return addon.price.discounted;
  }
  if (source.price && typeof source.price.discounted === 'number') {
    return source.price.discounted;
  }
  
  // Boost products use API structure similar to subscriptions
  // Try priceWithInterval.price.amount (cents)
  if (source.priceWithInterval?.price?.amount) {
    return source.priceWithInterval.price.amount / 100; // Convert cents to DKK
  }
  if (addon.priceWithInterval?.price?.amount) {
    return addon.priceWithInterval.price.amount / 100; // Convert cents to DKK
  }
  
  // Try price.amount (cents)
  if (source.price?.amount) {
    return source.price.amount / 100; // Convert cents to DKK
  }
  if (addon.price?.amount) {
    return addon.price.amount / 100; // Convert cents to DKK
  }
  
  // Try direct amount (cents)
  if (source.amount) {
    return source.amount / 100; // Convert cents to DKK
  }
  if (addon.amount) {
    return addon.amount / 100; // Convert cents to DKK
  }
  
  // Try direct price (already in DKK)
  if (typeof source.price === 'number') {
    return source.price;
  }
  if (typeof addon.price === 'number') {
    return addon.price;
  }
  
  return 0;
}

function getAddonDiscountPercent(addon) {
  if (!addon) return null;
  const source = addon.addition || addon;
  const discount = addon.discount || source.discount;
  const candidates = [
    addon.discountPercent,
    addon.discountPercentage,
    addon.rabatPercent,
    addon.rabatProcent,
    source.discountPercent,
    source.discountPercentage,
    source.rabatPercent,
    source.rabatProcent,
    discount?.percent,
    discount?.percentage,
    discount?.rate,
    discount?.value,
  ];

  for (const candidate of candidates) {
    const percent = Number(candidate);
    if (Number.isFinite(percent) && percent > 0 && percent < 100) {
      return percent;
    }
  }
  return null;
}

function getAddonMediaBannerText(addon) {
  if (!addon) return '';
  const source = addon.addition || addon;
  const directCandidates = [
    addon.imageBanner?.text,
    source.imageBanner?.text,
  ];
  for (const candidate of directCandidates) {
    if (typeof candidate === 'string' && candidate.trim()) return candidate.trim();
  }
  return extractMediaBannerText(source.media) || extractMediaBannerText(addon.media) || '';
}

function extractMediaBannerText(media) {
  if (!media) return '';
  const mediaItems = Array.isArray(media) ? media : [media];
  for (const item of mediaItems) {
    if (typeof item === 'string' && item.trim()) return item.trim();
    if (!item || typeof item !== 'object') continue;

    const directTextCandidates = [
      item.text,
      item.bannerText,
      item.priceText,
      item.label,
      item.title,
      item.value,
      item.imageBanner?.text,
      item.content?.text,
      item.data?.text,
    ];
    for (const candidate of directTextCandidates) {
      if (typeof candidate === 'string' && candidate.trim()) return candidate.trim();
    }
  }
  return '';
}

function getAddonOriginalPrice(addon) {
  if (!addon) return null;
  const source = addon.addition || addon;
  const discounted = getAddonPrice(addon);

  // Workaround: Use media/image-banner text as explicit original price when provided.
  const bannerText = getAddonMediaBannerText(source);
  if (typeof bannerText === 'string' && bannerText.trim()) {
    const match = bannerText.match(/(\d{1,6}(?:[.,]\d{1,2})?)/);
    if (match) {
      const parsed = Number(match[1].replace(',', '.'));
      if (Number.isFinite(parsed) && parsed > discounted) {
        return parsed;
      }
    }
  }

  const centsCandidates = [
    addon.originalAmount,
    source.originalAmount,
    addon.price?.originalAmount,
    source.price?.originalAmount,
    addon.price?.original?.amount,
    source.price?.original?.amount,
    addon.priceWithInterval?.price?.originalAmount,
    source.priceWithInterval?.price?.originalAmount,
    addon.priceWithInterval?.originalPrice?.amount,
    source.priceWithInterval?.originalPrice?.amount,
    addon.priceWithInterval?.price?.amountBeforeDiscount,
    source.priceWithInterval?.price?.amountBeforeDiscount,
  ];
  for (const cents of centsCandidates) {
    const value = Number(cents);
    if (Number.isFinite(value) && value > 0) {
      return value / 100;
    }
  }

  const dkkCandidates = [
    addon.originalPrice,
    source.originalPrice,
    addon.standardPrice,
    source.standardPrice,
    addon.listPrice,
    source.listPrice,
    addon.price?.original,
    source.price?.original,
    addon.priceBeforeDiscount,
    source.priceBeforeDiscount,
  ];
  for (const dkk of dkkCandidates) {
    const value = Number(dkk);
    if (Number.isFinite(value) && value > 0) {
      return value;
    }
  }

  const discountPercent = getAddonDiscountPercent(addon);
  if (discountPercent && discounted > 0) {
    const derived = discounted / (1 - (discountPercent / 100));
    if (Number.isFinite(derived) && derived > discounted) {
      return derived;
    }
  }

  return null;
}

function getBoostProductOriginalPrice(product) {
  if (!product) return null;

  const discounted = getAddonPrice(product);
  if (!(discounted > 0)) return null;
  
  // Workaround: Use media banner text as explicit original price when provided.
  // Example inputs: "199 kr", "199,00", "199.00 kr."
  const bannerText = getAddonMediaBannerText(product);
  if (typeof bannerText === 'string' && bannerText.trim()) {
    const match = bannerText.match(/(\d{1,6}(?:[.,]\d{1,2})?)/);
    if (match) {
      const parsed = Number(match[1].replace(',', '.'));
      if (Number.isFinite(parsed) && parsed > discounted) {
        return parsed;
      }
    }
  }

  // Many boost products are regular catalog products where priceWithInterval.price.amount
  // is the discounted/current value and price.amount can still hold standard/list price.
  const centsCandidates = [
    product.price?.amount,
    product.originalAmount,
    product.originalPrice?.amount,
    product.price?.originalAmount,
    product.price?.original?.amount,
    product.priceWithInterval?.price?.originalAmount,
    product.priceWithInterval?.originalPrice?.amount,
    product.priceWithInterval?.price?.amountBeforeDiscount,
    product.priceBeforeDiscount?.amount,
  ];
  for (const cents of centsCandidates) {
    const value = Number(cents);
    if (Number.isFinite(value) && value > 0) {
      const dkk = value / 100;
      if (dkk > discounted) return dkk;
    }
  }

  const dkkCandidates = [
    product.originalPrice,
    product.standardPrice,
    product.listPrice,
    product.price?.original,
    product.priceBeforeDiscount,
  ];
  for (const dkk of dkkCandidates) {
    const value = Number(dkk);
    if (Number.isFinite(value) && value > discounted) {
      return value;
    }
  }

  const percent = getBoostProductDiscountPercent(product);
  if (Number.isFinite(percent) && percent > 0 && percent < 100 && discounted > 0) {
    // BRP price rows use discount% on the base/original price:
    // discounted = original * (1 - discount%)
    // => original = discounted / (1 - discount%)
    const derived = discounted / (1 - (percent / 100));
    if (Number.isFinite(derived) && derived > discounted) return derived;
  }

  return null;
}

function getBoostProductDiscountPercent(product) {
  if (!product) return null;

  const numericCandidates = [
    product.discountPercent,
    product.discountPercentage,
    product.rabatPercent,
    product.rabatProcent,
    product.discount?.percent,
    product.discount?.percentage,
    product.discount?.rate,
    product.discount?.value,
  ];
  for (const candidate of numericCandidates) {
    const percent = Number(candidate);
    if (Number.isFinite(percent) && percent > 0 && percent < 100) {
      return percent;
    }
  }

  // Fallback: look for "15%" style hints in product text/labels.
  const textCandidates = [
    product.externalProductInfo,
    product.description,
    product.name,
    ...(Array.isArray(product.productLabels)
      ? product.productLabels.map((label) => label?.name).filter(Boolean)
      : []),
  ]
    .filter(Boolean)
    .map((v) => String(v));

  for (const text of textCandidates) {
    const match = text.match(/(\d{1,2}(?:[.,]\d+)?)\s*%/);
    if (!match) continue;
    const percent = Number(String(match[1]).replace(',', '.'));
    if (Number.isFinite(percent) && percent > 0 && percent < 100) {
      return percent;
    }
  }

  return null;
}

function getPlanCardOriginalPrice(product, discountedPriceDKK) {
  if (!product) return null;
  const discounted = Number(discountedPriceDKK);
  if (!Number.isFinite(discounted) || discounted <= 0) return null;

  // Prefer explicit API original-price fields for plan cards.
  // Campaign/membership payloads often carry discounted value in priceWithInterval
  // while the standard monthly price remains in price.amount.
  const centsCandidates = [
    product.priceWithInterval?.price?.amountBeforeDiscount,
    product.priceWithInterval?.price?.originalAmount,
    product.priceWithInterval?.originalPrice?.amount,
    product.originalAmount,
    product.originalPrice?.amount,
    product.price?.originalAmount,
    product.price?.original?.amount,
    product.price?.amount,
  ];
  for (const cents of centsCandidates) {
    const value = Number(cents);
    if (!Number.isFinite(value) || value <= 0) continue;
    const dkk = value / 100;
    if (dkk > discounted) return dkk;
  }

  const dkkCandidates = [
    product.originalPrice,
    product.standardPrice,
    product.listPrice,
    product.priceBeforeDiscount,
  ];
  for (const dkk of dkkCandidates) {
    const value = Number(dkk);
    if (Number.isFinite(value) && value > discounted) {
      return value;
    }
  }

  const staticFallback = getPlanCardOriginalPriceFallbackDKK(product);
  if (staticFallback > discounted) {
    return staticFallback;
  }

  const percent = getBoostProductDiscountPercent(product);
  if (Number.isFinite(percent) && percent > 0 && percent < 100) {
    const derived = discounted / (1 - (percent / 100));
    if (Number.isFinite(derived) && derived > discounted) return derived;
  }

  return null;
}

function getPlanCardOriginalPriceFallbackDKK(product) {
  if (!product) return 0;
  const text = [
    product?.name,
    product?.externalDescription,
    product?.description,
    product?.imageBanner?.text,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  if (/(student|studie)/i.test(text)) return 399;
  if (/(junior|youth|ungdom)/i.test(text)) return 279;
  if (/(adult|voksen)/i.test(text)) return 469;
  if (/%/.test(text) || /(month|måneder|maaneder|måned|maaned)/i.test(text)) return 469;

  return 0;
}

// Addon price in cents (for API payloads that require amount)
function getAddonPriceInCents(addon) {
  if (!addon) return 0;
  const source = addon.addition || addon;
  if (source.priceWithInterval?.price?.amount != null) return Math.round(Number(source.priceWithInterval.price.amount));
  if (source.price?.amount != null) return Math.round(Number(source.price.amount));
  if (source.amount != null) return Math.round(Number(source.amount));
  if (addon.priceWithInterval?.price?.amount != null) return Math.round(Number(addon.priceWithInterval.price.amount));
  if (addon.price?.amount != null) return Math.round(Number(addon.price.amount));
  if (addon.amount != null) return Math.round(Number(addon.amount));
  // Subscription additions often have price.discounted in DKK
  if (addon.price && typeof addon.price.discounted === 'number') return Math.round(addon.price.discounted * 100);
  if (source.price && typeof source.price.discounted === 'number') return Math.round(source.price.discounted * 100);
  const dkk = getAddonPrice(addon);
  return Math.round(dkk * 100);
}

// Privacy and Terms Consent Persistence
function restoreConsentCheckboxes() {
  try {
    // Restore privacy consent - use cached DOM element if available, otherwise query
    const privacyConsent = DOM.privacyConsent || document.getElementById('privacyConsent');
    if (privacyConsent) {
      const savedPrivacyConsent = localStorage.getItem('boulders_privacy_consent');
      if (savedPrivacyConsent === 'true') {
        privacyConsent.checked = true;
        // Ensure timestamp exists - if consent is restored but timestamp is missing, create one
        // This handles edge cases where localStorage was partially cleared
        if (!localStorage.getItem('boulders_privacy_consent_timestamp')) {
          const timestamp = new Date().toISOString();
          localStorage.setItem('boulders_privacy_consent_timestamp', timestamp);
          console.log('[Privacy Consent] Restored from localStorage, created new timestamp:', timestamp);
        } else {
          console.log('[Privacy Consent] Restored from localStorage with existing timestamp');
        }
      }
    } else {
      console.warn('[Privacy Consent] Checkbox not found in DOM');
    }
    
    // Restore terms consent - use cached DOM element if available, otherwise query
    const termsConsent = DOM.termsConsent || document.getElementById('termsConsent');
    if (termsConsent) {
      const savedTermsConsent = localStorage.getItem('boulders_terms_consent');
      if (savedTermsConsent === 'true') {
        termsConsent.checked = true;
        // Ensure timestamp exists - if consent is restored but timestamp is missing, create one
        // This handles edge cases where localStorage was partially cleared
        if (!localStorage.getItem('boulders_terms_consent_timestamp')) {
          const timestamp = new Date().toISOString();
          localStorage.setItem('boulders_terms_consent_timestamp', timestamp);
          console.log('[Terms Consent] Restored from localStorage, created new timestamp:', timestamp);
        } else {
          console.log('[Terms Consent] Restored from localStorage with existing timestamp');
        }
      }
    } else {
      console.warn('[Terms Consent] Checkbox not found in DOM');
    }
  } catch (e) {
    console.warn('[Consent] Could not restore consent checkboxes:', e);
  }
}

// Cookiebot integration - footer "Cookie Settings" button reopens Cookiebot consent dialog
function setupCookiebotSettingsButton() {
  const settingsButton = document.getElementById('cookieSettingsButton');
  if (!settingsButton) return;

  settingsButton.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (typeof Cookiebot !== 'undefined' && typeof Cookiebot.renew === 'function') {
      Cookiebot.renew();
    } else {
      console.warn('[Cookiebot] Cookiebot.renew() not available - Cookiebot may not be loaded yet');
    }
  });
}

// Static opening hours fallback when API does not return openingHours (e.g. /api/reference/business-units).
// Keys match gym names from API or partial match; values shown in "Open" column.
const OPENING_HOURS_FALLBACK = {
  'Boulders Aarhus City': '08 - 23',
  'Boulders Aarhus Nord': '10 - 22',
  'Boulders Aarhus Syd': '10 - 22',
  'Boulders Odense': '10 - 22',
  'Boulders KBH Sydhavn': '08 - 23',
  'Boulders KBH Valby': '09 - 22',
  'Boulders KBH Hvidovre': '10 - 22',
  'Boulders KBH Amager': '10 - 22',
  'Boulders Aarhus Aaby': '08 - 23',
  'Boulders KBH Vanløse': '08 - 23',
  'Boulders Aalborg': '10 - 22',
  // Partial keys for API name variants
  'Aarhus City': '08 - 23',
  'Aarhus Nord': '10 - 22',
  'Aarhus Syd': '10 - 22',
  'Odense': '10 - 22',
  'Sydhavn': '08 - 23',
  'Valby': '09 - 22',
  'Hvidovre': '10 - 22',
  'Amager': '10 - 22',
  'Aaby': '08 - 23',
  'Vanløse': '08 - 23',
  'Aalborg': '10 - 22'
};

function escapeHtml(str) {
  if (str == null) return '';
  const s = String(str);
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/**
 * Get opening hours string for a gym: from API (openingHours / opening_hours) or static fallback.
 * @param {{ name: string, openingHours?: string, opening_hours?: string }} gym
 * @returns {string}
 */
function getGymOpeningHours(gym) {
  const fromApi = gym.openingHours || gym.opening_hours || gym.openingHoursText;
  if (fromApi && String(fromApi).trim()) return String(fromApi).trim();
  const name = (gym.name || '').trim();
  if (OPENING_HOURS_FALLBACK[name]) return OPENING_HOURS_FALLBACK[name];
  for (const [key, hours] of Object.entries(OPENING_HOURS_FALLBACK)) {
    if (name.includes(key) || key.includes(name)) return hours;
  }
  return '';
}

/**
 * Build HTML for the FAQ "What are the opening hours?" answer: intro + table from API gyms or fallback.
 * Uses gymsWithDistances when available; otherwise static list from OPENING_HOURS_FALLBACK.
 * @returns {string}
 */
function getOpeningHoursAnswerHtml() {
  const intro = t('faq.gyms.openingHours.intro');
  const tableName = t('faq.gyms.openingHours.tableName');
  const tableOpen = t('faq.gyms.openingHours.tableOpen');
  const fullNameKeys = ['Boulders Aarhus City', 'Boulders Aarhus Nord', 'Boulders Aarhus Syd', 'Boulders Odense', 'Boulders KBH Sydhavn', 'Boulders KBH Valby', 'Boulders KBH Hvidovre', 'Boulders KBH Amager', 'Boulders Aarhus Aaby', 'Boulders KBH Vanløse', 'Boulders Aalborg'];
  const gyms = Array.isArray(gymsWithDistances) && gymsWithDistances.length > 0
    ? gymsWithDistances
    : fullNameKeys.map(name => ({ name, _fallback: true }));
  const rows = gyms.map(gym => {
    const name = gym.name || gym;
    const hours = (typeof gym === 'object' && !gym._fallback ? getGymOpeningHours(gym) : OPENING_HOURS_FALLBACK[name]) || '—';
    return `<tr><td class="faq-oh-name">${escapeHtml(name)}</td><td class="faq-oh-hours">${escapeHtml(hours)}</td></tr>`;
  }).join('');
  return `<p class="faq-opening-hours-intro">${escapeHtml(intro)}</p><table class="faq-opening-hours-table" aria-label="${escapeHtml(tableOpen)}"><thead><tr><th scope="col">${escapeHtml(tableName)}</th><th scope="col">${escapeHtml(tableOpen)}</th></tr></thead><tbody>${rows}</tbody></table>`;
}

// FAQ Data Structure
const FAQ_DATA = {
  gyms: [
    { q: 'faq.gyms.openingHours.q', a: 'faq.gyms.openingHours.a' },
    { q: 'faq.gyms.access.q', a: 'faq.gyms.access.a' },
    { q: 'faq.gyms.events.q', a: 'faq.gyms.events.a' },
    { q: 'faq.gyms.gettingStarted.q', a: 'faq.gyms.gettingStarted.a' }
  ],
  productChoice: [
    { q: 'faq.productChoice.difference.q', a: 'faq.productChoice.difference.a' },
    { q: 'faq.productChoice.membershipBest.q', a: 'faq.productChoice.membershipBest.a' },
    { q: 'faq.productChoice.15daypassBest.q', a: 'faq.productChoice.15daypassBest.a' },
    { q: 'faq.productChoice.punchcardBest.q', a: 'faq.productChoice.punchcardBest.a' }
  ],
  membership: [
    { q: 'faq.membership.included.q', a: 'faq.membership.included.a' },
    { q: 'faq.membership.blocLife.q', a: 'faq.membership.blocLife.a' },
    { q: 'faq.membership.terms.q', a: 'faq.membership.terms.a' },
    { q: 'faq.membership.bindingPeriod.q', a: 'faq.membership.bindingPeriod.a' },
    { q: 'faq.membership.freeze.q', a: 'faq.membership.freeze.a' },
    { q: 'faq.membership.cancellation.q', a: 'faq.membership.cancellation.a' }
  ],
  '15daypass': [
    { q: 'faq.15daypass.howItWorks.q', a: 'faq.15daypass.howItWorks.a' },
    { q: 'faq.15daypass.validity.q', a: 'faq.15daypass.validity.a' },
    { q: 'faq.15daypass.access.q', a: 'faq.15daypass.access.a' }
  ],
  'punch-card': [
    { q: 'faq.punchcard.howItWorks.q', a: 'faq.punchcard.howItWorks.a' },
    { q: 'faq.punchcard.convert.q', a: 'faq.punchcard.convert.a' },
    { q: 'faq.punchcard.remainingClips.q', a: 'faq.punchcard.remainingClips.a' },
    { q: 'faq.punchcard.multiple.q', a: 'faq.punchcard.multiple.a' }
  ],
  firstclimb: [
    { q: 'faq.firstclimb.included.q', a: 'faq.firstclimb.included.a' },
    { q: 'faq.firstclimb.eligibility.q', a: 'faq.firstclimb.eligibility.a' },
    { q: 'faq.firstclimb.validity.q', a: 'faq.firstclimb.validity.a' },
    { q: 'faq.firstclimb.howToUse.q', a: 'faq.firstclimb.howToUse.a' },
    { q: 'faq.firstclimb.afterDay.q', a: 'faq.firstclimb.afterDay.a' }
  ]
};

const FREE_TRIAL_EXTRA_FAQS = [
  { q: 'faq.freetrial.changeStartDate.q', a: 'faq.freetrial.changeStartDate.a' },
  { q: 'faq.freetrial.creditCard.q', a: 'faq.freetrial.creditCard.a' },
];

/**
 * Determine which FAQ categories should be shown based on current step and cart.
 * Step 1: gyms only. Step 2–3: product choice (help choose product). Step 4: contextual to cart.
 * @returns {string[]} Array of FAQ category keys to display
 */
function getActiveFAQs() {
  const step = state.currentStep;

  // firstclimb landing route: dedicated FAQ on every visible step.
  if (state.landingRouteConfig?.componentName === 'LandingFirstClimb') {
    return ['firstclimb'];
  }

  // Step 1: gym info only
  if (step === 1) {
    return ['gyms'];
  }

  // Step 2 or 3: help users choose the right product (differences between membership, 15-day pass, punch card)
  if (step === 2 || step === 3) {
    return ['productChoice'];
  }

  // Step 4 (cart): only FAQs for the selected product type (no gyms)
  if (step === 4) {
    const productType = determineProductTypeFromOrder();
    if (productType === '15daypass') return ['15daypass'];
    if (productType === 'membership') return ['membership'];
    if (productType === 'punch-card') return ['punch-card'];
    return [];
  }

  return [];
}

/**
 * Render FAQ HTML based on active categories
 */
function renderFAQ() {
  if (!DOM.faqSection) {
    DOM.faqSection = document.getElementById('faqSection');
    if (!DOM.faqSection) return;
  }
  
  const faqList = DOM.faqSection.querySelector('.faq-list');
  if (!faqList) return;
  
  // Get active FAQ categories
  const activeCategories = getActiveFAQs();
  const isFreeTrialFaqFlow = state.landingRouteConfig?.componentName === 'LandingFreeTrial';
  
  // Clear existing FAQ items
  faqList.innerHTML = '';
  
  // Render FAQs for each active category
  activeCategories.forEach(categoryKey => {
    const categoryFAQs = FAQ_DATA[categoryKey];
    if (!categoryFAQs) return;

    const faqsToRender = (categoryKey === '15daypass' && isFreeTrialFaqFlow)
      ? [...categoryFAQs, ...FREE_TRIAL_EXTRA_FAQS]
      : categoryFAQs;

    faqsToRender.forEach(faq => {
      const faqItem = document.createElement('div');
      faqItem.className = 'faq-item';
      faqItem.setAttribute('aria-expanded', 'false');
      const isOpeningHours = categoryKey === 'gyms' && faq.a === 'faq.gyms.openingHours.a';
      const answerContent = isOpeningHours ? getOpeningHoursAnswerHtml() : t(faq.a);
      const answerTag = isOpeningHours ? 'div' : 'p';
      const answerClass = isOpeningHours ? 'faq-answer-text faq-opening-hours' : 'faq-answer-text';
      if (isOpeningHours) faqItem.classList.add('faq-item--with-table');
      faqItem.innerHTML = `
        <button class="faq-question" type="button" aria-expanded="false">
          <span class="faq-question-text">${t(faq.q)}</span>
          <svg class="faq-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M6 9l6 6 6-6"/>
          </svg>
        </button>
        <div class="faq-answer">
          <${answerTag} class="${answerClass}">${answerContent}</${answerTag}>
        </div>
      `;
      
      faqList.appendChild(faqItem);
    });
  });
  
  // Reinitialize accordion functionality for new FAQ items
  initFAQ();
}

// Update FAQ section visibility
function updateFAQVisibility() {
  if (!DOM.faqSection) {
    // Retry to find FAQ section if not cached yet
    DOM.faqSection = document.getElementById('faqSection');
    if (!DOM.faqSection) return;
  }
  
  // Hide FAQ if checkout is in progress or on step 5 (success page)
  const shouldHide = state.checkoutInProgress || state.currentStep === TOTAL_STEPS;
  
  if (shouldHide) {
    DOM.faqSection.style.display = 'none';
    DOM.faqSection.setAttribute('data-checkout', 'true');
    DOM.faqSection.setAttribute('data-step-5', state.currentStep === TOTAL_STEPS ? 'true' : 'false');
  } else {
    // Show FAQ on steps 1-4 when checkout is not in progress
    DOM.faqSection.style.display = 'block';
    DOM.faqSection.removeAttribute('data-checkout');
    DOM.faqSection.removeAttribute('data-step-5');
    // Render FAQ when showing
    renderFAQ();
  }
}

/**
 * Scroll to FAQ section smoothly
 */
function scrollToFAQ() {
  const faqSection = document.getElementById('faqSection');
  if (!faqSection) return;
  
  // Force FAQ to be visible if hidden
  if (state.checkoutInProgress) {
    // Temporarily show FAQ even during checkout
    faqSection.style.display = 'block';
    faqSection.removeAttribute('data-checkout');
  } else {
    updateFAQVisibility();
  }
  
  // Scroll to FAQ with smooth behavior
  faqSection.scrollIntoView({ 
    behavior: 'smooth', 
    block: 'start' 
  });
  
  // Optional: Highlight FAQ section briefly
  faqSection.style.transition = 'background-color 0.3s ease';
  faqSection.style.backgroundColor = 'rgba(244, 1, 245, 0.1)';
  setTimeout(() => {
    faqSection.style.backgroundColor = '';
  }, 2000);
}

// Initialize FAQ accordion functionality
function initFAQ() {
  if (!DOM.faqQuestions || DOM.faqQuestions.length === 0) {
    // Retry after DOM is fully loaded
    setTimeout(() => {
      DOM.faqQuestions = Array.from(document.querySelectorAll('.faq-question'));
      if (DOM.faqQuestions.length > 0) {
        setupFAQAccordion();
      }
    }, 100);
    return;
  }
  
  setupFAQAccordion();
}

function setupFAQAccordion() {
  // Get fresh references to FAQ questions
  const faqQuestions = Array.from(document.querySelectorAll('.faq-question'));
  if (faqQuestions.length === 0) return;
  
  faqQuestions.forEach((questionBtn) => {
    // Check if already has listener (avoid duplicates)
    if (questionBtn.dataset.faqInitialized === 'true') return;
    questionBtn.dataset.faqInitialized = 'true';
    
    questionBtn.addEventListener('click', () => {
      const faqItem = questionBtn.closest('.faq-item');
      const isExpanded = questionBtn.getAttribute('aria-expanded') === 'true';
      
      // Close all other FAQ items
      faqQuestions.forEach((otherBtn) => {
        if (otherBtn !== questionBtn) {
          const otherItem = otherBtn.closest('.faq-item');
          otherBtn.setAttribute('aria-expanded', 'false');
          if (otherItem) {
            otherItem.setAttribute('aria-expanded', 'false');
          }
        }
      });
      
      // Toggle current item
      questionBtn.setAttribute('aria-expanded', !isExpanded);
      if (faqItem) {
        faqItem.setAttribute('aria-expanded', !isExpanded);
      }
    });
  });
  
  // Update DOM reference
  DOM.faqQuestions = faqQuestions;
}
